// src/Pages/CommercialDashboard/CommercialDashboard.jsx
import React, { useState, useMemo, useEffect, useRef } from "react";
// Le calcul d'échéance et d'état d'un mois vit dans un seul fichier, partagé avec
// l'espace apprenant et aligné sur le cron qui envoie les rappels J-7/J-3/J-1.
import { dateEcheanceMois, statutMoisEcran as getMoisStatut } from "../../utils/planMensuel";
import { chargerFilieresCatalogue } from "../../utils/catalogueOffres";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ModalAlignementTarif from "../../Components/ModalAlignementTarif";
import "../../chartConfig";
import { Line, Doughnut } from "react-chartjs-2";
import MessagerieTab from "../../Components/MessagerieTab";
import CloudinaryUpload from "../../Components/CloudinaryUpload";
import DashboardColorPicker from "../../Components/DashboardColorPicker";
import ChampPhotoClasse from "../../Components/ChampPhotoClasse";
import ChampTarifMigration from "../../Components/ChampTarifMigration";
import PastilleGroupe from "../../Components/PastilleGroupe";
import PhotoClasseEntete from "../../Components/PhotoClasseEntete";
import NotificationBell from "../../Components/NotificationBell";
import BarreFiltres, { RangeePastilles, RangeeChamps, champFiltre } from "../../Components/BarreFiltres";
import { useNotifPoller } from "../../hooks/useNotifPoller";
import { clearAllSessionStorage } from "../../utils/session";
import { exporterExcelStyle } from "../../utils/exporterExcel";
import { genererPDF, fmtDatePDF } from "../../utils/genererPDF";
import { useProspectChatsUnread } from "../../hooks/useProspectChatsUnread";
import { useFirebaseChat } from "../../hooks/useFirebaseChat";
import { useNotifications } from "../../hooks/useNotifications";
import ProspectChatPanel from "../../Components/ProspectChatPanel";
import NotificationsTab from "../../Components/NotificationsTab";
import { useLanguage } from "../../contexts/LanguageContext";
import { useConfirm, useAlerte } from "../../contexts/ConfirmContext";
import FooterSaaS from "../../Components/FooterSaaS";
import { assombrirCouleur } from "../../utils/couleurs";
import { lancerImport, ouvrirImport, annulerImport, compterResultats, listerImports, lignesDeLImport, peutEtreAnnule, peutEtreRelance, FENETRE_ANNULATION_HEURES,
  supprimerImport, peutEtreEfface, marquerImportRepris, STATUT_IMPORT } from "../../utils/importEnMasse";
import { useLivePermissions } from "../../utils/permissions";
import { openElearningSSO } from "../../utils/ssoElearning";
import { useTabState } from "../../hooks/useTabState";
import MonProfilModal, { BoutonMonProfil } from "../../Components/MonProfilModal";
import { libelleMoisSolde } from "../../utils/moisDuVersement";
import { PUBLICS_CIBLES, libellePublicCible, emojiPublicCible } from "../../utils/publicCible";
import PlanningSeances, { usePlanningSeances } from "../../Components/PlanningSeances";
import ChatGroupe from "../../Components/ChatGroupe";
import ModifierEmailApprenant from "../../Components/ModifierEmailApprenant";
import IdentifiantConnexion from "../../Components/IdentifiantConnexion";
import GroupesLibres from "../../Components/GroupesLibres";
import BadgeCategorieCoach from "../../Components/BadgeCategorieCoach";
import { NOTE_COULEUR as NOTE_COUL_CAT, CATEGORIES_COACH, CATEGORIE_SANS_NOTE, categorieCoach, noteSur10 } from "../../utils/categorieCoach";
import { useGroupesChatUnreadTotal, useGroupesLibresNonLus } from "../../hooks/useGroupeChat";

import { uploadFichier, maxMoPour } from "../../utils/uploadFichier";
import { fetchUpload } from "../../utils/fetchUpload";
import VisionneuseImage from "../../Components/VisionneuseImage";
import { estClassePrivee } from "../../utils/classePrivee";
import { libelleTypeCours } from "../../utils/typeCours";
import { BoutonMigrationCanal, BoutonDemandeTransfert } from "../../Components/DemandeTransfert";
import FileDemandesTransfert, { useCompteDemandesTransfert } from "../../Components/DemandeTransfert";
import { AncienNiveau } from "../../Components/NiveauApprenant";
import EvolutionNiveauTests, { texteEvolutionNiveau } from "../../Components/EvolutionNiveauTests";
import ArchiveApprenantsCommerciale from "../../Components/ArchiveApprenantsCommerciale";
import { libelleCanal } from "../../utils/canal";
import ModaleAnimee from "../../Components/ModaleAnimee";
const API_URL      = process.env.REACT_APP_API_URL      || "http://localhost:5001";
const ELEARNING_URL = process.env.REACT_APP_ELEARNING_URL || "http://localhost:3002";
const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

// Une offre présentiel/centre est enregistrée sur le dossier avec le nom du centre en
// préfixe (ex: "BET Angré - Cours en groupe", cf. Frontend/CourseDetail.jsx:759), mais le
// catalogue de prix (GET /api/admin/catalogue-offres) ne stocke que le libellé brut ("Cours
// en groupe") - une correspondance stricte échouait donc systématiquement pour toute offre
// présentiel/centre, affichant "Montant inconnu" (ex: bouton "Renvoyer le lien").
/**
 * La grille des formules privées qui s'applique à une offre.
 *
 * Les formules vivent désormais SUR L'OFFRE : le SuperAdmin peut en ajouter autant qu'il
 * veut au-delà de Classique et Intensive, et deux offres d'une même adresse peuvent
 * différer. Les écrans de la commerciale listaient ces deux packs en dur : une troisième
 * formule créée par le SuperAdmin n'y serait jamais apparue, et le dossier serait parti
 * avec un pack que personne n'avait choisi.
 *
 * Repli sur la grille globale quand l'offre ne définit rien - le comportement d'avant.
 */
const grillePriveDeLOffre = (catalogueOffres, label, grilleGlobale) => {
  const o = matchOffreCatalogue(catalogueOffres || [], label);
  if (o?.formules_actif !== true) return grilleGlobale;
  const liste = (o.formules || []).filter(f => f.label);
  if (!liste.length) return grilleGlobale;
  return Object.fromEntries(liste.map(f => [f.id || f.label, {
    label:     f.label,
    frequence: f.frequence || "",
    cabinet:   Number(f.tarif) || 0,
    en_ligne:  Number(f.tarif) || 0,
    domicile:  Number(f.tarif_domicile) || Number(f.tarif) || 0,
  }]));
};

const matchOffreCatalogue = (catalogueOffres, label) => {
  if (!label) return undefined;
  return catalogueOffres.find(o => o.label === label)
    || catalogueOffres.find(o => o.label && label.endsWith(o.label));
};

const SUIVI_CHECKLIST_ITEMS = [
  { id:"bienvenue", label:"Email de bienvenue envoyé",        icon:"✉️" },
  { id:"acces",     label:"Lien espace apprenant transmis",   icon:"🔗" },
  { id:"kit",       label:"Lien matériel de cours envoyé",    icon:"📚" },
  { id:"planning",  label:"Planning des séances envoyé",      icon:"📅" },
];
const pctSuiviChecklist = (cl) => {
  const keys = SUIVI_CHECKLIST_ITEMS.map(i => i.id);
  const vals = keys.map(k => (cl||{})[k] || false);
  return Math.round((vals.filter(Boolean).length / keys.length) * 100);
};
// "bienvenue"/"acces" reflètent l'envoi réellement automatique (compte apprenant créé dès
// l'ajout au groupe, cf. creerAccesApprenantCore côté backend) - mêmes items/logique que
// l'onglet Suivi démarrage d'Onboarding, portés ici pour le présentiel.
const SUIVI_ITEMS_AUTO = ["bienvenue", "acces"];
const suiviChecklistEffectif = (a) => ({
  ...(a.suivi_checklist || {}),
  bienvenue: !!a.apprenant_id,
  acces:     !!a.apprenant_id,
});
const SUIVI_PAGE_SIZE = 10;
const paginateSuivi = (items, page) => items.slice((page-1)*SUIVI_PAGE_SIZE, page*SUIVI_PAGE_SIZE);
const SuiviPagination = ({ page, setPage, total, color }) => {
  const nb = Math.max(1, Math.ceil(total / SUIVI_PAGE_SIZE));
  if (nb <= 1) return null;
  const pages = [];
  for (let i = 1; i <= nb; i++) {
    if (i === 1 || i === nb || Math.abs(i - page) <= 1) pages.push(i);
    else if (pages[pages.length-1] !== "…") pages.push("…");
  }
  return (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginTop:16 }}>
      <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
        style={{ padding:"5px 12px",borderRadius:7,border:"1px solid #e2e8f0",background:page===1?"#f8fafc":"#fff",color:page===1?"#cbd5e1":"#374151",fontWeight:700,fontSize:12,cursor:page===1?"default":"pointer" }}>‹</button>
      {pages.map((p,i)=>
        p==="…"
          ? <span key={`e${i}`} style={{ fontSize:12,color:"#9ca3af",padding:"0 2px" }}>…</span>
          : <button key={p} onClick={()=>setPage(p)}
              style={{ width:32,height:32,borderRadius:7,border:`1.5px solid ${page===p?color:"#e2e8f0"}`,background:page===p?color:"#fff",color:page===p?"#fff":"#374151",fontWeight:700,fontSize:12,cursor:"pointer" }}>{p}</button>
      )}
      <button onClick={()=>setPage(p=>Math.min(nb,p+1))} disabled={page===nb}
        style={{ padding:"5px 12px",borderRadius:7,border:"1px solid #e2e8f0",background:page===nb?"#f8fafc":"#fff",color:page===nb?"#cbd5e1":"#374151",fontWeight:700,fontSize:12,cursor:page===nb?"default":"pointer" }}>›</button>
      <span style={{ fontSize:11,color:"#9ca3af",marginLeft:4 }}>{page} / {nb}</span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   CONSTANTES (charte BET)
═══════════════════════════════════════════════════════ */
const BET_COLOR    = (() => { try { return JSON.parse(localStorage.getItem("admin_profil")||"{}").couleur||"#0891b2"; } catch { return "#0891b2"; } })();
const BET_DARK     = BET_COLOR;
const BET_LIGHT    = BET_COLOR + "18";
const BET_GRADIENT = `linear-gradient(135deg, #0f172a 0%, ${assombrirCouleur(BET_COLOR)} 100%)`;

// Normalise un élément d'horaire : objet {jour,debut,fin} OU chaîne déjà formatée
const fmtHoraire = (h) => {
  /* LES SECONDES DE POSTGRES N'ONT RIEN À FAIRE DANS UN CRÉNEAU. Un `time` revient en
     `08:00:00` ; affiché tel quel, « 08:00:00–09:00:00 » met six caractères de bruit par
     ligne sur des listes qui en comptent des dizaines, pour une information qu'aucune
     disponibilité déclarée au quart d'heure ne porte.

     La coupe se fait par expression régulière sur le TEXTE FINAL, ce qui couvre les deux
     formes reçues : l'objet `{ jour, debut, fin }` comme la chaîne déjà composée. Passer
     par un objet `Date` réinterpréterait l'heure dans le fuseau du navigateur et pourrait
     la décaler d'une heure. */
  const sansSecondes = (v) => String(v ?? "").replace(/(\d{1,2}:\d{2}):\d{2}/g, "$1");
  if (!h) return "";
  if (typeof h === "string") return sansSecondes(h);
  if (typeof h === "object") return sansSecondes(`${h.jour || ""} ${h.debut || ""}${h.fin ? "–" + h.fin : ""}`.trim());
  return sansSecondes(h);
};

// ── Plan mensuel helpers ─────────────────────────────────────────────────────
const MOIS_FR = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const getMoisLabel = (periode) => { const [y,m] = periode.split("-"); return `${MOIS_FR[parseInt(m,10)-1]} ${y}`; };
// Jour d'échéance ancré sur le vrai jour de départ (ex: démarré le 20 → dû le 20 chaque
// mois), pas toujours le 1er - clampé au dernier jour du mois si celui-ci est plus court
// (ex: ancre au 31, un mois de 30 jours ou février tombe sur son propre dernier jour).
const genererMoisPaiements = (dateDebut, dateFin, mensualite) => {
  if (!dateDebut || !mensualite) return [];
  const mois = [];
  // L'itération interne reste calée au 1er (uniquement pour incrémenter les mois sans bug
  // de débordement de jour, ex: 31 janvier + 1 mois → 3 mars) - le vrai jour d'échéance est
  // stocké une fois pour tout le plan (plan.jour_echeance) et appliqué via dateEcheanceMois().
  const curr = new Date(dateDebut); curr.setDate(1); curr.setHours(0,0,0,0);
  // Plan ouvert (pas de date de fin) : rattrape aussi tous les mois déjà écoulés jusqu'au
  // mois courant inclus (ex: import d'un dossier existant depuis 4 mois) - sinon un seul
  // mois est généré et les versements déjà réalisés n'ont nulle part où s'appliquer ; le
  // cron générerait ensuite les mois suivants comme "en_attente" alors qu'ils sont réglés.
  const limite = dateFin
    ? (() => { const d = new Date(dateFin); d.setDate(1); d.setHours(0,0,0,0); return d; })()
    : (() => { const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d; })();
  const MAX  = 60; // sécurité
  let i = 0;
  do {
    const periode = `${curr.getFullYear()}-${String(curr.getMonth()+1).padStart(2,"0")}`;
    mois.push({ id:`m_${periode}`, periode, label:getMoisLabel(periode), montant:mensualite, statut:"en_attente", tranches:[] });
    curr.setMonth(curr.getMonth()+1);
    i++;
  } while (curr <= limite && i < MAX);
  return mois;
};
// Applique les versements déjà importés (montants réels déjà reçus, colonne "Paiements")
// aux premiers mois générés, dans l'ordre chronologique - sans ça, le mois 1 restait
// affiché "en attente" alors que l'argent avait bien été perçu (le versement n'existait
// que dans la liste "Versements enregistrés", jamais rapproché du plan mensuel).
const appliquerVersementsAuPlan = (moisListe, paiements) => {
  const mois = (moisListe || []).map(m => ({ ...m, tranches: [...(m.tranches || [])] }));
  const versements = [...(paiements || [])].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  let idx = 0;
  for (const pmt of versements) {
    let restant = Number(pmt.montant) || 0;
    while (restant > 0 && idx < mois.length) {
      const m = mois[idx];
      const dejaPaye = (m.tranches || []).reduce((s, t) => s + (t.montant || 0), 0);
      const capacite = Math.max(0, m.montant - dejaPaye);
      if (capacite <= 0) { idx++; continue; }
      const part = Math.min(restant, capacite);
      m.tranches.push({ id: `t_imp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, montant: part, statut: "paye", date_paiement: pmt.date || null, mode: "Mobile Money", ref: pmt.reference || "", notes: "Import en masse" });
      restant -= part;
      if (part === capacite) idx++;
    }
  }
  return mois.map(m => {
    const totalPaye = (m.tranches || []).reduce((s, t) => s + (t.montant || 0), 0);
    return { ...m, statut: totalPaye >= m.montant ? "paye" : totalPaye > 0 ? "partiel" : "en_attente" };
  });
};

const NIVEAUX    = ["A1","A2","B1","B2","C1","C2"];
const TYPE_COURS = [
  { id:"presentiel", label:"Présentiel 🏢" },
  { id:"en_ligne",   label:"En ligne 🌐" },
  { id:"hybride",    label:"Hybride ⚡" },
];

/* ═══════════════════════════════════════════════════════
   COMPOSANTS RÉUTILISABLES
═══════════════════════════════════════════════════════ */
const StatCard = ({ label, value, color, icon, sub, onClick }) => (
  <div onClick={onClick} style={{ background:"#fff", padding:16, borderRadius:12, boxShadow:"0 1px 6px rgba(0,0,0,0.06)", display:"flex", alignItems:"center", gap:12, cursor:onClick?"pointer":"default", transition:"transform .15s", border:"1px solid #f1f5f9" }}>
    <div style={{ width:46, height:46, borderRadius:10, background:color+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>{icon}</div>
    <div style={{ minWidth:0 }}>
      <div style={{ fontSize:11, color:"#9ca3af" }}>{label}</div>
      <div style={{ fontSize:21, fontWeight:800, color, lineHeight:1.2 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:"#9ca3af", marginTop:1 }}>{sub}</div>}
    </div>
  </div>
);

const Badge = ({ label, color, bg }) => (
  <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:99, fontSize:10, fontWeight:700, color, background:bg, whiteSpace:"nowrap" }}>{label}</span>
);

const Modal = ({ title, onClose, children, wide, size, headerExtra }) => (
  <div style={modalOverlay}>
    <div style={{ ...modalBox, width: size === "xxl" ? "90vw" : size === "xl" ? "88vw" : wide ? "70vw" : 520, maxWidth: size === "xxl" ? "90vw" : size === "xl" ? "88vw" : wide ? "70vw" : "92vw", maxHeight:"90vh", overflowY:"auto" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18, gap:10 }}>
        <h3 style={{ margin:0, fontSize:16, flex:1 }}>{title}</h3>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {headerExtra}
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#6b7280" }}>✕</button>
        </div>
      </div>
      {children}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════
   HELPERS STATUTS
═══════════════════════════════════════════════════════ */
const STATUT_PAIEMENT = {
  en_attente: { label:"En attente", color:"#f59e0b", bg:"#fef3c7" },
  partiel:    { label:"Partiel",    color:"#0891b2", bg:"#e0f2fe" },
  // handleSavePaiement écrit "paye" (sans accent) - la clé doit matcher exactement.
  paye:       { label:"Reçu",       color:"#22c55e", bg:"#dcfce7" },
  remboursé:  { label:"Remboursé",  color:"#8b5cf6", bg:"#ede9fe" },
};
const STATUT_TEST = {
  nouveau:    { label:"Nouveau",    color:"#0891b2", bg:"#e0f2fe" },
  contacté:   { label:"Contacté",  color:"#f59e0b", bg:"#fef3c7" },
  converti:   { label:"Converti",  color:"#22c55e", bg:"#dcfce7" },
  archivé:    { label:"Archivé",   color:"#6b7280", bg:"#f3f4f6" },
};

const CECRL_COLORS = {
  A1:{ color:"#6b7280", bg:"#f3f4f6" },
  A2:{ color:"#d97706", bg:"#fef3c7" },
  B1:{ color:"#1a4fd6", bg:"#dbeafe" },
  B2:{ color:"#7c3aed", bg:"#ede9fe" },
  C1:{ color:"#059669", bg:"#dcfce7" },
  C2:{ color:"#dc2626", bg:"#fee2e2" },
};
const MOIS_LABELS_DISPO = ["","Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const JOURS_ORDRE_DISPO = ["Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi","Dimanche"];
// Mois en cours + mois suivant (avec bascule d'année en décembre) - un coach peut renseigner
// ses disponibilités du mois prochain en avance, donc on prépare toujours les deux périodes.
const getPeriodesDispo = () => {
  const now = new Date();
  const courant = { mois: now.getMonth() + 1, annee: now.getFullYear() };
  const suivant = courant.mois === 12 ? { mois: 1, annee: courant.annee + 1 } : { mois: courant.mois + 1, annee: courant.annee };
  return { courant, suivant };
};
// Regroupe les créneaux bruts (coach_disponibilites) par jour, dans l'ordre de la semaine -
// utilisé partout où un sélecteur de coach doit montrer ses vraies disponibilités du mois.
const formatDispoSlots = (slots) => {
  if (!slots || slots.length === 0) return [];
  const parJour = {};
  slots.forEach(s => { (parJour[s.jour] = parJour[s.jour] || []).push(s); });
  return JOURS_ORDRE_DISPO
    .filter(j => parJour[j]?.length)
    .map(j => ({
      jour: j,
      creneaux: parJour[j].sort((a,b) => (a.debut||"").localeCompare(b.debut||"")).map(s => `${(s.debut||"").slice(0,5)}–${(s.fin||"").slice(0,5)}`),
    }));
};
// Bloc compact affiché sur une carte coach - disponibilités réelles du mois sélectionné.
const CoachDispoBlock = ({ coachId, coachDispoMap, mois, annee }) => {
  const slots = coachDispoMap[`${coachId}_${mois}_${annee}`];
  if (slots === undefined) {
    return <div style={{ fontSize:10, color:"#9ca3af", fontStyle:"italic", marginTop:4 }}>Chargement des disponibilités…</div>;
  }
  const parJour = formatDispoSlots(slots);
  if (parJour.length === 0) {
    return (
      <div style={{ fontSize:10, color:"#b45309", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:6, padding:"3px 7px", marginTop:4, display:"inline-block" }}>
        ⚠️ Disponibilités non renseignées pour {MOIS_LABELS_DISPO[mois]}
      </div>
    );
  }
  return (
    <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:4, alignItems:"flex-start" }}>
      {/* UN CRÉNEAU PAR LIGNE, et non une énumération séparée par des virgules.
          « Lundi 08:00–09:00, 09:00–10:00, 10:00–11:00, 11:00–12:00 » se lit comme une seule
          longue chaîne : pour savoir si le coach est libre à 10 h, il faut parcourir la phrase.
          Empilés, les créneaux se comparent d'un coup d'œil, et le jour ne se répète pas. */}
      {parJour.map(({ jour, creneaux }) => (
        <div key={jour} title={`${jour} · ${MOIS_LABELS_DISPO[mois]}`}
          style={{ fontSize:9.5, fontWeight:600, color:"#166534", background:"#f0fdf4",
            border:"1px solid #bbf7d0", borderRadius:5, padding:"3px 7px" }}>
          <div style={{ fontWeight:800, marginBottom:2 }}>📅 {jour}</div>
          {creneaux.map((c, i) => (
            <div key={i} style={{ whiteSpace:"nowrap" }}>🕐 {c}</div>
          ))}
        </div>
      ))}
    </div>
  );
};
// Sélecteur mois en cours / mois suivant - ne dépend que des deux périodes déjà préchargées.
const DispoMoisToggle = ({ moisSel, setMoisSel, periodes }) => (
  <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:8 }}>
    <span style={{ fontSize:11, color:"#6b7280", fontWeight:600 }}>📅 Disponibilités :</span>
    {["courant","suivant"].map(v => (
      <button key={v} type="button" onClick={() => setMoisSel(v)}
        style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:99, cursor:"pointer", border:`1.5px solid ${moisSel===v?"#16a34a":"#e2e8f0"}`, background:moisSel===v?"#dcfce7":"#fff", color:moisSel===v?"#166534":"#6b7280" }}>
        {v==="courant" ? `Ce mois (${MOIS_LABELS_DISPO[periodes.courant.mois]})` : `Mois prochain (${MOIS_LABELS_DISPO[periodes.suivant.mois]})`}
      </button>
    ))}
  </div>
);
const STATUT_CLASSE = {
  actif:    { label:"Actif",       color:"#16a34a", bg:"#dcfce7" },
  suspendu: { label:"Suspendu",    color:"#d97706", bg:"#fef3c7" },
  terminé:  { label:"Terminé",     color:"#6b7280", bg:"#f3f4f6" },
  cloture:  { label:"🔒 Clôturé",  color:"#6b7280", bg:"#f3f4f6" },
};

/* ═══════════════════════════════════════════════════════
   DONNÉES MOCK
═══════════════════════════════════════════════════════ */
const OFFRE_LIST = ["Anglais Pro B2", "Business English", "Certification TOEIC", "Anglais Enfant", "Formation Entreprise"];

const isOffreCoachingPrive = (offre = "") => {
  const l = offre.toLowerCase();
  return l.includes("privé") || l.includes("prive") || l.includes("particulier") || l.includes("coaching privé") || l.includes("coaching prive");
};
// const PROFIL_LIST = ["Particulier", "Entreprise", "Étudiant"];

const INIT_INSCRIPTIONS = [
];

/* ── Tests de niveau reçus ── */
const INIT_TESTS = [];

/* ═══════════════════════════════════════════════════════
   PROFIL ENFANT SECTION  (sous-composant isolé pour respecter les Rules of Hooks)
═══════════════════════════════════════════════════════ */
const PROFILS_LIST = [
  { id:"adulte",   label:"Adulte",   emoji:"👤", bg:"#eff6ff", color:"#1d4ed8" },
  { id:"etudiant", label:"Étudiant", emoji:"🎒", bg:"#f0fdf4", color:"#15803d" },
  { id:"enfant",   label:"Enfant",   emoji:"👶", bg:"#fefce8", color:"#854d0e" },
];


/* ── L'OBJECTIF DU CLIENT, affiché et modifiable partout où le dossier se consulte.
   Le champ existait en base et dans l'API depuis toujours ; rien ne le remplissait.
   C'est pourtant lui que le conseiller pédagogique lit pour choisir le coach, et que le
   coach lira pour savoir ce qu'on attend de lui. ── */
function ObjectifClientBloc({ a, authHeaders, setAssignations, setApprenantModal }) {
  const [edite, setEdite]   = React.useState(false);
  const [texte, setTexte]   = React.useState(a?.objectif_client || "");
  const [occupe, setOccupe] = React.useState(false);

  const enregistrer = async () => {
    const valeur = texte.trim();
    setOccupe(true);
    try {
      const r = await fetch(`${API_URL}/api/parcours/assignations/${a.id}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ objectif_client: valeur || null }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Enregistrement impossible");
      // On recopie localement : le parent ne se recharge qu'à la fermeture du modal, et
      // sans cela on écrirait un objectif sans le voir apparaître.
      setAssignations(prev => prev.map(x => x.id === a.id ? { ...x, objectif_client: valeur || null } : x));
      setApprenantModal(prev => prev?.id === a.id ? { ...prev, objectif_client: valeur || null } : prev);
      setEdite(false);
      toast.success(valeur ? "Objectif enregistré ✓" : "Objectif effacé");
    } catch (e) {
      toast.error(e.message || "Enregistrement impossible");
    } finally { setOccupe(false); }
  };

  const vide = !(a?.objectif_client || "").trim();
  return (
    <div style={{ background: vide ? "#fffbeb" : "#f5f3ff", borderRadius:12, padding:"14px 18px",
      border:`1.5px solid ${vide ? "#fde68a" : "#ddd6fe"}` }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom: edite ? 10 : 6 }}>
        <span style={{ fontSize:15 }}>🎯</span>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:".05em", textTransform:"uppercase",
          color: vide ? "#92400e" : "#6d28d9" }}>
          Objectif du client
        </div>
        {!edite && (
          <button onClick={() => { setTexte(a?.objectif_client || ""); setEdite(true); }}
            style={{ marginLeft:"auto", fontSize:11, padding:"3px 11px", borderRadius:7,
              border:"1px solid #e2e8f0", background:"#fff", color:"#475569", fontWeight:700, cursor:"pointer" }}>
            {vide ? "+ Renseigner" : "✏️ Modifier"}
          </button>
        )}
      </div>

      {edite ? (
        <>
          <textarea value={texte} onChange={e => setTexte(e.target.value)} autoFocus rows={3}
            placeholder="Ex. Préparer un entretien d'embauche en anglais chez Orange · IELTS 7.0 avant mars"
            style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:"1.5px solid #ddd6fe",
              fontSize:13, fontFamily:"inherit", boxSizing:"border-box", resize:"vertical" }} />
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:8 }}>
            <button onClick={() => setEdite(false)} disabled={occupe}
              style={{ padding:"7px 14px", borderRadius:8, border:"1px solid #e2e8f0", background:"#fff",
                color:"#374151", fontSize:12, fontWeight:600, cursor:"pointer" }}>Annuler</button>
            <button onClick={enregistrer} disabled={occupe}
              style={{ padding:"7px 16px", borderRadius:8, border:"none", background: occupe ? "#cbd5e1" : "#7c3aed",
                color:"#fff", fontSize:12, fontWeight:700, cursor: occupe ? "wait" : "pointer" }}>
              {occupe ? "…" : "Enregistrer"}
            </button>
          </div>
        </>
      ) : vide ? (
        <div style={{ fontSize:12.5, color:"#92400e", lineHeight:1.6 }}>
          Non renseigné. C'est ce texte que le conseiller pédagogique lit pour choisir le coach
          — sans lui, l'affectation se fait à l'aveugle.
        </div>
      ) : (
        <div style={{ fontSize:13.5, color:"#0f172a", lineHeight:1.65, whiteSpace:"pre-wrap" }}>
          {a.objectif_client}
        </div>
      )}
    </div>
  );
}

function ProfilEnfantSection({ a, setAssignations, setApprenantModal }) {
  const inputStyle = { padding:9, borderRadius:6, border:"1px solid #d1d5db", fontSize:13, width:"100%", boxSizing:"border-box" };
  const [localProfil, setLocalProfil] = useState({
    profil_type:      a.profil_type      || "adulte",
    nom_parent:       a.nom_parent       || "",
    telephone_parent: a.telephone_parent || "",
    email_parent:     a.email_parent     || "",
  });
  const [savingProfil, setSavingProfil] = useState(false);
  const [editProfil,   setEditProfil]   = useState(false);

  const saveProfil = async () => {
    setSavingProfil(true);
    try {
      const r = await fetch(`${API_URL}/api/parcours/assignations/${a.id}`, {
        method:"PATCH", headers: authHeaders(),
        body: JSON.stringify({
          profil_type:      localProfil.profil_type,
          nom_parent:       localProfil.profil_type === "enfant" ? (localProfil.nom_parent||null) : null,
          telephone_parent: localProfil.profil_type === "enfant" ? (localProfil.telephone_parent||null) : null,
          email_parent:     localProfil.profil_type === "enfant" ? (localProfil.email_parent||null) : null,
        }),
      });
      if (r.ok) {
        setAssignations(prev => prev.map(x => x.id===a.id ? { ...x, ...localProfil } : x));
        setApprenantModal(prev => ({ ...prev, ...localProfil }));
        toast.success("Profil mis à jour");
        setEditProfil(false);
      } else { toast.error("Erreur lors de la sauvegarde"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setSavingProfil(false); }
  };

  const isEnfant = localProfil.profil_type === "enfant";
  const currentProfil = PROFILS_LIST.find(p => p.id === localProfil.profil_type) || PROFILS_LIST[0];

  return (
    <div style={{ borderRadius:12, border:`1.5px solid ${isEnfant?"#fde68a":"#e2e8f0"}`, background:isEnfant?"#fffbeb":"#f8fafc", overflow:"hidden" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 16px", borderBottom:`1px solid ${isEnfant?"#fde68a":"#e2e8f0"}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:18 }}>{currentProfil.emoji}</span>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".05em" }}>Profil apprenant</div>
            <div style={{ fontSize:13, fontWeight:800, color:currentProfil.color }}>{currentProfil.label}</div>
          </div>
        </div>
        {!editProfil
          ? <button onClick={()=>setEditProfil(true)} style={{ fontSize:11, padding:"4px 10px", borderRadius:6, border:"1px solid #e2e8f0", background:"#fff", color:"#374151", cursor:"pointer", fontWeight:600 }}>✏️ Modifier</button>
          : <div style={{ display:"flex", gap:6 }}>
              <button onClick={()=>setEditProfil(false)} style={{ fontSize:11, padding:"4px 10px", borderRadius:6, border:"1px solid #e2e8f0", background:"#fff", color:"#374151", cursor:"pointer" }}>Annuler</button>
              <button onClick={saveProfil} disabled={savingProfil} style={{ fontSize:11, padding:"4px 12px", borderRadius:6, border:"none", background:BET_COLOR, color:"#fff", cursor:"pointer", fontWeight:700 }}>{savingProfil?"⏳":"💾 Sauvegarder"}</button>
            </div>
        }
      </div>

      <div style={{ padding:"12px 16px" }}>
        {editProfil && (
          <div style={{ display:"flex", gap:8, marginBottom:12 }}>
            {PROFILS_LIST.map(p => (
              <button key={p.id} onClick={()=>setLocalProfil(prev=>({...prev, profil_type:p.id}))}
                style={{ flex:1, padding:"8px", borderRadius:9, border:`2px solid ${localProfil.profil_type===p.id?p.color:"#e2e8f0"}`, background:localProfil.profil_type===p.id?p.bg:"#fff", cursor:"pointer", fontWeight:localProfil.profil_type===p.id?800:500, color:localProfil.profil_type===p.id?p.color:"#374151", fontSize:12, textAlign:"center" }}>
                {p.emoji} {p.label}
              </button>
            ))}
          </div>
        )}

        {/* Infos parent */}
        {isEnfant && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {editProfil ? (
              <>
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Nom du parent / tuteur *</label>
                  <input value={localProfil.nom_parent} onChange={e=>setLocalProfil(p=>({...p,nom_parent:e.target.value}))} style={{ ...inputStyle, margin:0 }} placeholder="Prénom Nom" />
                </div>
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Téléphone parent</label>
                  <input value={localProfil.telephone_parent} onChange={e=>setLocalProfil(p=>({...p,telephone_parent:e.target.value}))} style={{ ...inputStyle, margin:0 }} placeholder="+225 07 …" />
                </div>
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Email parent</label>
                  <input value={localProfil.email_parent} onChange={e=>setLocalProfil(p=>({...p,email_parent:e.target.value}))} style={{ ...inputStyle, margin:0 }} placeholder="parent@email.ci" type="email" />
                </div>
              </>
            ) : (
              <>
                <div style={{ gridColumn:"1/-1", background:"#fff", borderRadius:8, padding:"10px 14px", border:"1px solid #fde68a" }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#a16207", textTransform:"uppercase", letterSpacing:".05em", marginBottom:2 }}>Parent / Tuteur</div>
                  <div style={{ fontSize:14, fontWeight:800, color:"#0f172a" }}>{localProfil.nom_parent || <span style={{ color:"#94a3b8", fontStyle:"italic" }}>Non renseigné</span>}</div>
                </div>
                <div style={{ background:"#fff", borderRadius:8, padding:"10px 14px", border:"1px solid #fde68a" }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#a16207", textTransform:"uppercase", letterSpacing:".05em", marginBottom:2 }}>Téléphone parent</div>
                  <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>{localProfil.telephone_parent ? <a href={`tel:${localProfil.telephone_parent}`} style={{ color:BET_COLOR, textDecoration:"none" }}>{localProfil.telephone_parent}</a> : <span style={{ color:"#94a3b8", fontStyle:"italic" }}>-</span>}</div>
                </div>
                <div style={{ background:"#fff", borderRadius:8, padding:"10px 14px", border:"1px solid #fde68a" }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#a16207", textTransform:"uppercase", letterSpacing:".05em", marginBottom:2 }}>Email parent</div>
                  <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>{localProfil.email_parent ? <a href={`mailto:${localProfil.email_parent}`} style={{ color:BET_COLOR, textDecoration:"none" }}>{localProfil.email_parent}</a> : <span style={{ color:"#94a3b8", fontStyle:"italic" }}>-</span>}</div>
                </div>
              </>
            )}
          </div>
        )}

        {!isEnfant && !editProfil && (
          <div style={{ fontSize:12, color:"#94a3b8", fontStyle:"italic" }}>
            {localProfil.profil_type === "etudiant" ? "Étudiant - aucune coordination parent requise." : "Adulte autonome."}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Composant : configuration du plan mensuel ──────────────────────────── */
const SetupPlanMensuel = ({ mensAuto, debutAuto, finAuto, classeNom, offreNom, onGenerer, negociee = null, tarifCatalogue = null }) => {
  const [open,       setOpen]       = React.useState(false);
  const [mensualite, setMensualite] = React.useState(mensAuto || 0);
  const [dateDebut,  setDateDebut]  = React.useState(debutAuto || "");
  const [dateFin,    setDateFin]    = React.useState(finAuto   || "");
  const [loading,    setLoading]    = React.useState(false);

  const inputSt = { width:"100%", padding:"7px 10px", borderRadius:7, border:"1px solid #e2e8f0", fontSize:12, boxSizing:"border-box" };

  return (
    <div style={{ borderRadius:10, border:"1.5px dashed #0891b2", background:"#e0f2fe", overflow:"hidden" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 16px" }}>
        <div>
          <div style={{ fontSize:12, fontWeight:800, color:"#0369a1" }}>📅 Plan de paiement mensuel</div>
          {/* Dire d'où vient le montant proposé, sinon il sera « corrigé » par réflexe
              vers le tarif public - et la négociation perdue une seconde fois. */}
          {negociee && (
            <div style={{ fontSize:11, color:"#9a3412", background:"#ffedd5", borderRadius:6, padding:"3px 8px", marginTop:4, display:"inline-block" }}>
              🤝 Tarif négocié à l'inscription{negociee.par ? ` par ${negociee.par}` : ""}
              {negociee.le ? ` le ${new Date(negociee.le).toLocaleDateString("fr-FR")}` : ""}
              {tarifCatalogue && Number(mensAuto) !== Number(tarifCatalogue)
                ? ` · catalogue : ${Number(tarifCatalogue).toLocaleString("fr-FR")} F`
                : ""}
            </div>
          )}
          <div style={{ fontSize:11, color:"#0369a1", marginTop:2 }}>
            {offreNom && <span>{offreNom}</span>}
            {classeNom && <span> · {classeNom}</span>}
            {mensAuto > 0 && !open && <span> · {Number(mensAuto).toLocaleString("fr-FR")} F/mois</span>}
          </div>
        </div>
        <button
          onClick={() => setOpen(o => !o)}
          style={{ flexShrink:0, padding:"7px 14px", borderRadius:8, border:"none", cursor:"pointer", fontWeight:700, fontSize:12, background:"#0891b2", color:"#fff" }}
        >{open ? "Annuler" : "Configurer ▸"}</button>
      </div>
      {open && (
        <div style={{ padding:"0 16px 14px", display:"flex", flexDirection:"column", gap:10, borderTop:"1px solid #bae6fd" }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginTop:12 }}>
            <div>
              <div style={{ fontSize:10, fontWeight:700, color:"#0369a1", marginBottom:4 }}>Mensualité (FCFA) *</div>
              <input type="number" style={inputSt} value={mensualite} onChange={e=>setMensualite(Number(e.target.value))} min={0} placeholder="Ex: 80000" />
            </div>
            <div>
              <div style={{ fontSize:10, fontWeight:700, color:"#0369a1", marginBottom:4 }}>Date de début *</div>
              <input type="date" style={inputSt} value={dateDebut} onChange={e=>setDateDebut(e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize:10, fontWeight:700, color:"#0369a1", marginBottom:4 }}>Date de fin <span style={{ fontWeight:400 }}>(optionnel)</span></div>
              <input type="date" style={inputSt} value={dateFin} onChange={e=>setDateFin(e.target.value)} />
            </div>
          </div>
          {dateFin && dateDebut && dateFin > dateDebut && (
            <div style={{ fontSize:11, color:"#0369a1", background:"#fff", borderRadius:6, padding:"6px 10px", border:"1px solid #bae6fd" }}>
              {(() => { const n = genererMoisPaiements(dateDebut, dateFin, mensualite||0).length; return `${n} mois générés · Total : ${Number((mensualite||0)*n).toLocaleString("fr-FR")} F`; })()}
            </div>
          )}
          {!dateFin && <div style={{ fontSize:11, color:"#0369a1" }}>Sans date de fin → plan ouvert (mois générés un à un)</div>}
          <button
            disabled={!mensualite || !dateDebut || loading}
            onClick={async () => { if (loading) return; setLoading(true); await onGenerer({ mensualite, dateDebut, dateFin }); setLoading(false); setOpen(false); }}
            style={{ padding:"9px 0", borderRadius:8, border:"none", cursor: (!mensualite||!dateDebut||loading)?"not-allowed":"pointer", fontWeight:800, fontSize:13, background: (!mensualite||!dateDebut)?"#94a3b8":"#0891b2", color:"#fff" }}
          >{loading ? "Génération…" : "✓ Générer le plan mensuel"}</button>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   PAGE PRINCIPALE
═══════════════════════════════════════════════════════ */
export default function CommercialDashboard() {
  const navigate = useNavigate();
  const profilInitial = JSON.parse(localStorage.getItem("admin_profil") || "null");
  const { profil, peut } = useLivePermissions(profilInitial);
  const [msgSubTab, setMsgSubTab] = useState("prive");
  const prenom = profil?.prenom || profil?.first_name || "";
  const nom    = profil?.nom    || profil?.last_name  || "";
  const nomComplet = [prenom, nom].filter(Boolean).join(" ") || profil?.email || "Commercial";
  // Le surnom devient le titre du dashboard ; nomComplet reste affiche juste en dessous
  // et garde sa valeur partout ailleurs (listes, exports), ou l'etat civil fait foi.
  const [surnomPerso, setSurnomPerso] = useState(() => {
    try { return JSON.parse(localStorage.getItem("admin_profil") || "{}").surnom || ""; } catch { return ""; }
  });
  const [monProfilOuvert, setMonProfilOuvert] = useState(false);
  const nomAffiche = (surnomPerso || "").trim() || nomComplet;
  const initiales = [prenom[0], nom[0]].filter(Boolean).join("").toUpperCase() || "CM";
  const permBtnStyle = (style, allowed) => allowed ? style : { ...style, opacity:0.45, cursor:"not-allowed", filter:"grayscale(70%)" };
  const permBtnTitle = (label, allowed) => allowed ? label : "🔒 Vous n'avez pas la permission pour cette action";

  const [accesElearning, setAccesElearning] = useState(profil?.acces_elearning === true);

  const { lang, toggleLang } = useLanguage();
  const confirm = useConfirm();
  const handleLogout = async () => {
    if (!(await confirm('Vous allez être déconnecté(e) de votre session.', { titre: 'Se déconnecter ?', confirmLabel: 'Se déconnecter', danger: false }))) return;
    clearAllSessionStorage();
    navigate("/bet-staff-access", { replace: true });
  };

  const [activeTab, setActiveTab]           = useTabState("dashboard");
  const [avatarUrl,  setAvatarUrl]          = useState(profil?.avatar_url || null);
  // Cliquer sur la photo la montre en grand ; le bouton 📷 en coin garde le remplacement.
  const [photoProfilOuverte, setPhotoProfilOuverte] = useState(false);

  const handleAvatarSuccess = async (file) => {
    setAvatarUrl(file.url);
    const token = localStorage.getItem("admin_token");
    try {
      const r = await fetch(`${API_URL}/api/admin/me`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ avatar_url: file.url }),
      });
      if (r.ok) {
        const stored = (() => { try { return JSON.parse(localStorage.getItem("admin_profil")||"{}"); } catch { return {}; } })();
        localStorage.setItem("admin_profil", JSON.stringify({ ...stored, avatar_url: file.url }));
        toast.success("Photo de profil mise à jour !");
      } else { toast.error("Erreur lors de la sauvegarde."); }
    } catch { toast.error("Erreur réseau."); }
  };

  // ── Refresh couleur depuis le serveur (si SuperAdmin l'a modifiée) ───
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) return;
    fetch(`${API_URL}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.profil) return;
        if (sessionStorage.getItem("_bet_color_guard")) { sessionStorage.removeItem("_bet_color_guard"); return; }
        const freshColor = data.profil.couleur || null;
        const stored = (() => { try { return JSON.parse(localStorage.getItem("admin_profil")||"{}"); } catch { return {}; } })();
        const _mp = { ...stored, ...data.profil }; if (!data.profil.couleur && stored.couleur) _mp.couleur = stored.couleur; localStorage.setItem("admin_profil", JSON.stringify(_mp));
        if (data.profil.acces_elearning !== undefined) setAccesElearning(data.profil.acces_elearning === true);
        if (freshColor && freshColor !== BET_COLOR) window.location.reload();
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  // ── Détection profil assistante (b2c / b2b / les_deux) + centre ───
  const [assistanteProfil,    setAssistanteProfil]    = useState(null); // null = chargement
  const [assistanteTypeCours, setAssistanteTypeCours] = useState(null);
  const [assistanteCentreNom, setAssistanteCentreNom] = useState(null);
  const [assistanteCentreId,  setAssistanteCentreId]  = useState(null);
  const [assistanteId,        setAssistanteId]        = useState(null);
  // Filières = offres réelles du catalogue. Écrites en dur jusqu'ici, elles ne
  // correspondaient à aucune offre : toute classe créée à la main produisait des
  // inscriptions « hors catalogue », exclues des statistiques sans alerte visible.
  const [filieresCatalogue, setFilieresCatalogue] = useState([]);
  useEffect(() => {
    chargerFilieresCatalogue(API_URL, localStorage.getItem("admin_token")).then(setFilieresCatalogue);
  }, []);

  // ── Suivi démarrage (commerciale centre / présentiel uniquement) ──
  const [suiviApprenants,     setSuiviApprenants]      = useState([]);
  const [detailSuiviApprenant, setDetailSuiviApprenant] = useState(null);
  const [suiviFiltreStatut,  setSuiviFiltreStatut]  = useState("tous"); // tous | termine | non_termine
  const [suiviFiltreDateDebut, setSuiviFiltreDateDebut] = useState("");
  const [suiviFiltreDateFin,   setSuiviFiltreDateFin]   = useState("");
  const [pageSuivi,          setPageSuivi]          = useState(1);
  const [suiviLoading,        setSuiviLoading]         = useState(false);
  const isCommercialeCentre = assistanteTypeCours === "presentiel" || assistanteTypeCours === "les_deux";

  useEffect(() => {
    if (activeTab !== "suivi" || !isCommercialeCentre || !assistanteCentreId) return;
    setSuiviLoading(true);
    fetch(`${API_URL}/api/groupes/apprenants?centre_id=${encodeURIComponent(assistanteCentreId)}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : { apprenants: [] })
      .then(d => setSuiviApprenants(d.apprenants || []))
      .catch(() => {})
      .finally(() => setSuiviLoading(false));
  }, [activeTab, isCommercialeCentre, assistanteCentreId]);

  const toggleSuiviCheck = async (gaId, itemId) => {
    const cible = suiviApprenants.find(a => a.id === gaId);
    if (!cible) return;
    const nvlChecklist = { ...(cible.suivi_checklist||{}), [itemId]: !((cible.suivi_checklist||{})[itemId]) };
    setSuiviApprenants(prev => prev.map(a => a.id === gaId ? { ...a, suivi_checklist: nvlChecklist } : a));
    try {
      await fetch(`${API_URL}/api/groupes/apprenants/${gaId}/suivi`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify({ checklist: nvlChecklist }),
      });
    } catch { toast.error("Erreur lors de la mise à jour du suivi."); }
  };

  const enregistrerSuiviNotes = async (gaId, notes) => {
    setSuiviApprenants(prev => prev.map(a => a.id === gaId ? { ...a, suivi_notes: notes } : a));
    try {
      await fetch(`${API_URL}/api/groupes/apprenants/${gaId}/suivi`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify({ notes }),
      });
    } catch { toast.error("Erreur lors de l'enregistrement des notes."); }
  };

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token || !profil?.email) { setAssistanteProfil("b2c"); return; }
    fetch(`${API_URL}/api/parcours/assistantes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const myAss = (d?.assistantes || []).find(a => a.email === profil.email);
        const p = myAss?.profil || "b2c";
        setAssistanteProfil(p);
        setAssistanteTypeCours(myAss?.type_cours || null);
        setAssistanteCentreNom(myAss?.centre_nom || null);
        setAssistanteCentreId(myAss?.centre_id   || null);
        setAssistanteId(myAss?.id                || null);
        // Si B2B pur, démarrer sur les demandes reçues (formulaires entreprise du site web)
        if (p === "b2b") setActiveTab("assignations");
      })
      .catch(() => setAssistanteProfil("b2c"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasB2C = !assistanteProfil || assistanteProfil === "b2c" || assistanteProfil === "les_deux";
  const hasB2B = assistanteProfil === "b2b" || assistanteProfil === "les_deux";

  // ── Données B2B (chargées à la demande) ─────────────────
  const [b2bEntreprises, setB2bEntreprises] = useState([]);
  const [b2bProspects,   setB2bProspects]   = useState([]);

  /* ══ LE TABLEAU DE BORD CORPORATE ══
     Les huit indicateurs et la file des leads. Chargés ensemble : ils décrivent la même
     période et se contrediraient si l'un se rafraîchissait sans l'autre. */
  const [corpIndic,      setCorpIndic]      = useState(null);
  const [corpLeads,      setCorpLeads]      = useState([]);
  const [corpLoading,    setCorpLoading]    = useState(false);
  /* 30 jours par défaut : un tableau de bord sans borne additionne toute l'histoire de
     l'entreprise et cesse de dire ce qui se passe maintenant. */
  const [corpJours,      setCorpJours]      = useState(30);
  const [corpFiltre,     setCorpFiltre]     = useState("");
  const [b2bDocuments,   setB2bDocuments]   = useState([]);
  const [b2bFactures,    setB2bFactures]    = useState([]);
  const [b2bLoaded,      setB2bLoaded]      = useState(false);
  const [b2bLoading,     setB2bLoading]     = useState(false);
  const [b2bPackages,    setB2bPackages]    = useState([]);
  const [b2bCompteRH,    setB2bCompteRH]    = useState(null);

  useEffect(() => {
    if (!hasB2B) return;
    fetch(`${API_URL}/api/packages-commerciaux`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : { packages: [] })
      .then(d => setB2bPackages(d.packages || []))
      .catch(() => {});
  }, [hasB2B]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadB2B = async () => {
    if (b2bLoaded || b2bLoading) return;
    setB2bLoading(true);
    try {
      const [rE, rP, rD, rF] = await Promise.all([
        fetch(`${API_URL}/api/corporate/entreprises`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/corporate/prospects`,   { headers: authHeaders() }),
        fetch(`${API_URL}/api/corporate/documents`,   { headers: authHeaders() }),
        fetch(`${API_URL}/api/corporate/factures`,    { headers: authHeaders() }),
      ]);
      const [dE, dP, dD, dF] = await Promise.all([rE.json(), rP.json(), rD.json(), rF.json()]);
      setB2bEntreprises(dE.entreprises || []);
      setB2bProspects(dP.prospects || []);
      setB2bDocuments(dD.documents || []);
      setB2bFactures(dF.factures || []);
      setB2bLoaded(true);
    } catch { toast.error("Erreur chargement données Corporate"); }
    finally { setB2bLoading(false); }
  };

  useEffect(() => {
    if (hasB2B && activeTab === "tdb_corporate") chargerCorporate();
    if (hasB2B && ["entreprises","pipeline_b2b","documents_b2b","facturation_b2b"].includes(activeTab)) {
      loadB2B();
    }
  }, [activeTab, hasB2B]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helpers B2B
  const fmtB2B = (n) => n != null ? Number(n).toLocaleString("fr-FR") + " FCFA" : "-";
  const fmtDateB2B = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "-";

  // Modales B2B
  const [b2bEntrepriseForm, setB2bEntrepriseForm] = useState(null); // null=fermé, {}=nouveau, {id,...}=édition
  const [b2bProspectForm,   setB2bProspectForm]   = useState(null);
  const [b2bDocumentForm,   setB2bDocumentForm]   = useState(null);
  const [b2bFactureForm,    setB2bFactureForm]    = useState(null);
  const [b2bSavingId,       setB2bSavingId]       = useState(null);
  const [b2bDeletingId,     setB2bDeletingId]     = useState(null);
  const [b2bHistoriqueItem, setB2bHistoriqueItem] = useState(null);

  const saveB2BEntreprise = async (form) => {
    if (b2bSavingId) return;
    setB2bSavingId("entreprise");
    try {
      const isEdit = !!form.id;
      const url = isEdit ? `${API_URL}/api/corporate/entreprises/${form.id}` : `${API_URL}/api/corporate/entreprises`;
      const res = await fetch(url, { method: isEdit ? "PATCH" : "POST", headers: authHeaders(), body: JSON.stringify(form) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      toast.success(isEdit ? "Entreprise mise à jour ✓" : "Entreprise créée ✓");
      setB2bEntrepriseForm(null); setB2bLoaded(false); loadB2B();
      if (d.compteRH) setB2bCompteRH(d.compteRH);
    } catch (err) { toast.error(err.message); }
    finally { setB2bSavingId(null); }
  };

  const saveB2BProspect = async (form) => {
    if (b2bSavingId) return;
    setB2bSavingId("prospect");
    try {
      const isEdit = !!form.id;
      const url = isEdit ? `${API_URL}/api/corporate/prospects/${form.id}` : `${API_URL}/api/corporate/prospects`;
      const res = await fetch(url, { method: isEdit ? "PATCH" : "POST", headers: authHeaders(), body: JSON.stringify(form) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      toast.success(isEdit ? "Prospect mis à jour ✓" : "Prospect créé ✓");
      setB2bProspectForm(null); setB2bLoaded(false); loadB2B();
    } catch (err) { toast.error(err.message); }
    finally { setB2bSavingId(null); }
  };

  const saveB2BDocument = async (form) => {
    if (b2bSavingId) return;
    setB2bSavingId("document");
    try {
      const res = await fetch(`${API_URL}/api/corporate/documents`, { method: "POST", headers: authHeaders(), body: JSON.stringify(form) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      toast.success("Document enregistré ✓");
      setB2bDocumentForm(null); setB2bLoaded(false); loadB2B();
    } catch (err) { toast.error(err.message); }
    finally { setB2bSavingId(null); }
  };

  const saveB2BFacture = async (form) => {
    if (b2bSavingId) return;
    setB2bSavingId("facture");
    try {
      const res = await fetch(`${API_URL}/api/corporate/factures`, { method: "POST", headers: authHeaders(), body: JSON.stringify(form) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      toast.success("Facture créée ✓");
      setB2bFactureForm(null); setB2bLoaded(false); loadB2B();
    } catch (err) { toast.error(err.message); }
    finally { setB2bSavingId(null); }
  };

  const patchB2BStatut = async (table, id, updates) => {
    await fetch(`${API_URL}/api/corporate/${table}/${id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(updates) });
    setB2bLoaded(false); loadB2B();
  };

  // Notifications polling
  useNotifPoller({ userId: profil?.id, sources: ["tests", "contacts", "assignations"] });

  const [inscriptions, setInscriptions]     = useState(INIT_INSCRIPTIONS);
  const [tests, setTests]                   = useState(INIT_TESTS);
  const [testsLoading, setTestsLoading]     = useState(false);
  const [testsNonAssignes, setTestsNonAssignes] = useState([]);
  /* TOUS les tests, sans le filtre `commercial_id`. `tests` ne garde que les tests du pipeline
     de prospection - ceux que la conseillère a fait passer elle-même. Or le test qui rouvre le
     dossier d'un apprenant déjà inscrit est passé par l'AGENT PLACEMENT TEST lors d'un
     transfert : il arrive sans `commercial_id`, tombait donc dans `testsNonAssignes`, et la
     fiche de l'apprenant restait bloquée sur son ancien test. L'historique de niveau d'un
     apprenant ne dépend pas de qui lui a fait passer le test. */
  const [tousTests, setTousTests]           = useState([]);
  const [testPage, setTestPage]             = useState(1);
  const TESTS_PER_PAGE = 10;
  const [clientMessages, setClientMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const myCommercialId = profil?.id || null;
  const { unreadMap: prospectUnreadMap } = useProspectChatsUnread(myCommercialId);

  const [centreName, setCentreName] = useState("");

  useEffect(() => {
    const scope = profil?.scope || [];
    if (!scope.length || scope.includes("national")) return;
    fetch(`${API_URL}/api/centres`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d?.centres) return;
        const matched = d.centres.filter(c => scope.includes(c.id));
        if (matched.length > 0) setCentreName(matched.map(c => c.nom).join(" · "));
      })
      .catch(() => {});
  }, []);

  // ── Classes / Groupes ────────────────────────────────────
  const [classes,          setClasses]          = useState([]);
  const [classesLoading,   setClassesLoading]   = useState(false);
  const [coachsList,       setCoachsList]        = useState([]);
  // Disponibilités réelles des coachs (table coach_disponibilites, renseignée par le coach
  // CHAQUE MOIS depuis son espace - jamais un planning hebdomadaire fixe). Clé = `${coachId}_${mois}_${annee}`.
  // On précharge le mois en cours ET le mois suivant pour permettre à l'assistante de basculer
  // sans attendre un nouveau fetch (un coach peut renseigner son mois suivant en avance).
  const [coachDispoMap,     setCoachDispoMap]     = useState({});
  const fetchCoachsDispos = async (coachIds) => {
    if (!coachIds.length) return;
    const { courant, suivant } = getPeriodesDispo();
    const periodes = [courant, suivant];
    try {
      const results = await Promise.all(
        coachIds.flatMap(id => periodes.map(({ mois, annee }) =>
          fetch(`${API_URL}/api/coachs/${id}/disponibilites?mois=${mois}&annee=${annee}`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : { disponibilites: [] })
            .then(d => ({ id, mois, annee, slots: d.disponibilites || [] }))
            .catch(() => ({ id, mois, annee, slots: [] }))
        ))
      );
      setCoachDispoMap(prev => {
        const next = { ...prev };
        results.forEach(({ id, mois, annee, slots }) => { next[`${id}_${mois}_${annee}`] = slots; });
        return next;
      });
    } catch {}
  };
  const [showCreateClasse, setShowCreateClasse]  = useState(false);
  const [classeForm,       setClasseForm]        = useState({
    nom:"", niveau:"B1", filiere:"Anglais Pro", type_cours:"", coach_id:"",
    centre_id:"", date_debut:"", date_fin:"", capacite_max:20, horaire:[], photo_url:null,
    /* Vide = non renseigné, et c'est un état admis : le champ n'est pas obligatoire. */
    public_cible:"",
  });
  const [classeSaving,     setClasseSaving]      = useState(false);

  const fetchClasses = async () => {
    setClassesLoading(true);
    try {
      const params = new URLSearchParams();
      // Filtrer par centre si l'assistante a un centre attitré
      if (assistanteCentreId) params.set("centre_id", assistanteCentreId);
      // Indispensable pour que le backend ne renvoie QUE les classes créées par cette
      // assistante (ou contenant ses apprenants) - sans ce paramètre, GET /api/groupes ne
      // filtre le rôle "commercial" nulle part et renvoyait TOUTES les classes du système,
      // présentiel et en ligne confondues, à n'importe quelle assistante.
      if (profil?.email) params.set("commercial_email", profil.email);
      const r = await fetch(`${API_URL}/api/groupes?${params}`, { headers: authHeaders() });
      if (r.ok) { const d = await r.json(); const liste = d.groupes || []; setClasses(liste); return liste; }
    } catch { toast.error("Erreur chargement classes"); }
    finally { setClassesLoading(false); }
    return null;
  };

  const fetchCoachs = async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API_URL}/api/coachs`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/admin/utilisateurs?role=coach`, { headers: authHeaders() }),
        /* LE CLASSEMENT - note moyenne, tendance et historique. Cet écran ne le chargeait
           pas : au moment d'assigner un coach à une classe, l'assistante voyait sa
           disponibilité et ses niveaux, mais rien sur la qualité de son travail. C'est
           pourtant la moitié de la décision. */
        fetch(`${API_URL}/api/coachs/classement`, { headers: authHeaders() }),
      ]);
      const d1 = r1.ok ? await r1.json() : { coachs: [] };
      const d2 = r2.ok ? await r2.json() : { utilisateurs: [] };

      // Table coachs (profil complet) - actif=null traité comme actif
      const coachsTable = (d1.coachs || []).filter(c => c.actif !== false);
      const coachsTableIds = new Set(coachsTable.map(c => c.id));

      // Utilisateurs role=coach non encore dans la table coachs - actif=null traité comme actif
      const coachsUsers = (d2.utilisateurs || [])
        .filter(u => u.actif !== false && !coachsTableIds.has(u.id))
        .map(u => ({
          id:                u.id,
          nom:               u.nom,
          prenom:            u.prenom,
          // coach_info stocke "filiere" (pas "grade"), "specialite" au singulier (pas
          // "specialites"), et "niveaux" en chaîne "B1,B2,C1" (pas un tableau) - le mapping
          // précédent cherchait les mauvais noms de champs, donc niveau/spécialité/contrat
          // n'apparaissaient jamais sur les cartes du sélecteur de coach, malgré des données
          // bien renseignées côté formulaire d'inscription du coach.
          grade:             u.coach_info?.filiere           || "Coach",
          photo_url:         u.avatar_url                    || null,
          biographie:        u.coach_info?.bio               || null,
          specialites:       u.coach_info?.specialite ? [u.coach_info.specialite] : [],
          niveaux_enseignes: u.coach_info?.niveaux ? u.coach_info.niveaux.split(",").map(s=>s.trim()).filter(Boolean) : [],
          experience_annees: u.coach_info?.experience_annees || null,
          type_contrat:      u.coach_info?.type_contrat      || null,
          telephone:         u.telephone                     || null,
          email:             u.email                         || null,
          actif:             true,
        }));

      /* Les notes sont RECOLLÉES sur les deux sources : un coach peut venir de `coachs`
         ou de `utilisateurs`, et le classement ne connaît que son identifiant. */
      const d3 = r3.ok ? await r3.json() : { classement: [] };
      const notes = {};
      (d3.classement || []).forEach(c => {
        notes[String(c.id)] = {
          moyenne:        c.moyenne ?? null,
          tendance:       c.tendance || "stable",
          historique:     Array.isArray(c.historique) ? c.historique : [],
          nb_evaluations: c.nb_avec_note ?? c.nb_evaluations ?? 0,
        };
      });

      const merged = [...coachsTable, ...coachsUsers].map(c => ({ ...c, ...(notes[String(c.id)] || {}) }));
      setCoachsList(merged);
      fetchCoachsDispos(merged.map(c => c.id));
    } catch (e) { console.error("fetchCoachs:", e); }
  };

  useEffect(() => {
    if (activeTab === "classes") { fetchClasses(); fetchCoachs(); }
  }, [activeTab]); // eslint-disable-line


  // Les classes sont chargées dès que le profil est connu, sans attendre l'onglet « Mes
  // classes ».
  //
  // Une commerciale EN LIGNE ne crée pas ses classes : elle en hérite par ses apprenants.
  // Les charger seulement à l'ouverture de cet onglet avait deux conséquences - le chat de
  // groupe annonçait « Aucune classe à afficher » à qui allait directement dans Messages, et
  // le badge de non-lus, calculé sur cette liste, restait à zéro sur tous les autres onglets.
  // Un compteur d'onglet ne peut pas dépendre de l'onglet qu'on regarde.
  useEffect(() => {
    if (profil?.email) fetchClasses();
  }, [profil?.email, assistanteCentreId]); // eslint-disable-line

  const [classeCoachSearch,      setClasseCoachSearch]      = useState("");
  // Filtre par catégorie de contrat. "" = toutes ; "sans_note" = jamais évalué.
  const [classeCoachCategorie,   setClasseCoachCategorie]   = useState("");
  const [classeCoachDispoMois,   setClasseCoachDispoMois]   = useState("courant"); // "courant" | "suivant"
  const [showCreateClasseInFin,    setShowCreateClasseInFin]    = useState(false);
  const [showCreateClasseInAssign, setShowCreateClasseInAssign] = useState(false);
  const [showCreateClasseInTransfert, setShowCreateClasseInTransfert] = useState(false);
  const [classesPage,            setClassesPage]            = useState(1);
  const [classesDateDebut,       setClassesDateDebut]       = useState("");
  const [classesDateFin,         setClassesDateFin]         = useState("");
  const [classesNiveauFil,       setClassesNiveauFil]       = useState("");
  const [classesStatutFil,       setClassesStatutFil]       = useState("");
  const [classeApprenantModal,   setClasseApprenantModal]   = useState(null);
  const [classeApprenantTab,     setClasseApprenantTab]     = useState("infos");
  const [classeAppPres,          setClasseAppPres]          = useState([]);
  const [classeAppPresLoading,   setClasseAppPresLoading]   = useState(false);
  // Présences dans apprenantModal (Mes apprenants)
  const [commAppPres,            setCommAppPres]            = useState([]);
  const [commAppPresLoading,     setCommAppPresLoading]     = useState(false);
  // Notes/évaluations de classe (données par le coach) dans apprenantModal
  const [commAppEvals,           setCommAppEvals]           = useState([]);
  const [commAppEvalsLoading,    setCommAppEvalsLoading]    = useState(false);
  // Historique de parcours (changements de classe et autres événements à venir)
  const [commAppHistorique,        setCommAppHistorique]        = useState([]);
  const [commAppHistoriqueLoading, setCommAppHistoriqueLoading] = useState(false);
  const [commAppHistoriqueOuvert,  setCommAppHistoriqueOuvert]  = useState(null); // id de la ligne dépliée
  const CLASSES_PER_PAGE = 10;

  const saveClasse = async () => {
    if (classeSaving) return;
    if (!classeForm.nom.trim()) { toast.error("Nom de la classe requis"); return; }
    setClasseSaving(true);
    try {
      const { _id, coach_id_original, ...rest } = classeForm;
      // Ne JAMAIS retomber silencieusement sur "en_ligne" : une assistante avec plusieurs
      // scopes (assistanteTypeCours === "les_deux") ou sans type_cours résolu doit choisir
      // explicitement présentiel/en ligne - sinon la classe créée finit avec un type_cours
      // qui ne correspond à aucune de ses classes réelles et devient invisible dans le
      // sélecteur du modal d'assignation (filtré strictement par type_cours).
      const typeCoursResolu = rest.type_cours || (assistanteTypeCours && assistanteTypeCours !== "les_deux" ? assistanteTypeCours : "");
      if (!typeCoursResolu) {
        toast.error("Choisissez le type de cours (présentiel / en ligne) pour cette classe");
        setClasseSaving(false);
        return;
      }
      const payload = {
        ...rest,
        type_cours:   typeCoursResolu,
        centre_id:    rest.centre_id    || assistanteCentreId  || null,
        coach_id:     rest.coach_id     || null,
        capacite_max: Number(rest.capacite_max) || 20,
      };
      const isEdit = !!_id;
      const r = await fetch(isEdit ? `${API_URL}/api/groupes/${_id}` : `${API_URL}/api/groupes`, {
        method: isEdit ? "PATCH" : "POST", headers: authHeaders(), body: JSON.stringify(payload),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || "Erreur"); }
      await r.json();
      toast.success(isEdit ? "Classe mise à jour ✓" : "Classe créée ✓");
      setShowCreateClasse(false);
      setClasseForm({ nom:"", niveau:"B1", filiere:"Anglais Pro", type_cours:"", coach_id:"", centre_id:"", date_debut:"", date_fin:"", capacite_max:20, horaire:[], photo_url:null, public_cible:"" });
      setClasseCoachSearch("");
      setShowCreateClasseInFin(false);
      // Une classe fraîchement créée peut tomber hors des filtres actifs de "Mes classes"
      // (niveau/statut/plage de dates laissés d'une recherche précédente) ou sur une page
      // de pagination différente de la page 1 - dans les deux cas elle reste invisible
      // même après un fetch réussi. On repart d'un état "vue complète" à chaque création.
      if (!isEdit) {
        setClassesNiveauFil("");
        setClassesStatutFil("");
        setClassesDateDebut("");
        setClassesDateFin("");
        setClassesPage(1);
      }
      // Attendre le rechargement AVANT de rouvrir le modal d'assignation - sinon il se
      // rouvre avec l'état `classes` périmé (avant création) et la classe qu'on vient de
      // créer semble "manquante" tant que le fetch (non attendu) n'a pas fini derrière.
      await fetchClasses();
      if (showCreateClasseInAssign && !isEdit) {
        setShowCreateClasseInAssign(false);
        setShowAssignCentreModal(true);
      }
      setShowCreateClasseInAssign(false);
      // Créée depuis le modal de transfert (AssignationCentreModal, même flux que
      // l'affectation initiale) - pas de présélection à faire ici : fetchClasses()
      // rafraîchit la liste passée en props, la nouvelle classe apparaît directement
      // dans la liste cliquable, comme pour toute autre création de classe.
      setShowCreateClasseInTransfert(false);
    } catch (e) { toast.error(e.message); }
    finally { setClasseSaving(false); }
  };

  /* ══════════════════════════════════════════════════════
     IMPORT EN MASSE DES CLASSES (Excel) - présentiel uniquement.
     Réutilise POST /api/groupes ligne par ligne (même endpoint que
     "Créer une classe"), type_cours et centre_id imposés par le
     compte connecté - jamais laissés au fichier pour éviter qu'une
     classe importée finisse invisible dans le sélecteur (filtré
     strictement par type_cours, cf. saveClasse ci-dessus).
  ══════════════════════════════════════════════════════ */
  const importClassesFileRef = useRef(null);
  const [showImportClassesUpload,  setShowImportClassesUpload]  = useState(false);
  const [importClassesPreviewRows, setImportClassesPreviewRows] = useState([]);
  const [showImportClassesPreview, setShowImportClassesPreview] = useState(false);
  // Communs aux 3 imports : nom du fichier (pour l'historique) et identifiant du
  // dernier lot, qui rend le bouton "Annuler cet import" possible depuis le rapport.
  const [importFichierNom, setImportFichierNom] = useState("");
  const [dernierImportId,  setDernierImportId]  = useState(null);
  // Lot ouvert dès la lecture d'un fichier "Import global" : les classes y sont déjà
  // créées et estampillées, les apprenants doivent rejoindre LE MÊME lot pour que
  // l'annulation défasse l'ensemble d'un seul coup.
  const [importGlobalJobId, setImportGlobalJobId] = useState(null);
  const [annulationEnCours, setAnnulationEnCours] = useState(false);
  // Progression unifiee des 3 imports : alimente le modal bloquant ci-dessous.
  const [importProgress, setImportProgress] = useState(null); // { label, pct, traites, total }
  // Drapeau d'interruption : une ref, pas un state - le rappel doitArreter est capturé
  // dans une closure, où un state serait lu périmé et l'arrêt jamais pris en compte.
  const arretImportRef = useRef(false);
  const [arretDemande, setArretDemande] = useState(false);
  const ACCENT_IMPORT = "#7c3aed";

  /* Rapport d'import : défaire le lot qui vient d'être créé, sans script ni
     intervention en base. Le serveur supprime exactement ce que CE lot a produit
     (filtre sur import_id), donc aucune saisie manuelle ne peut être touchée. */

  /* Borne de génération de l'échéancier.
     Un plan ouvert s'arrête au mois courant : les versements réglés D'AVANCE (mois
     futurs) n'ont alors aucun mois où s'imputer et disparaissent silencieusement du
     plan - mesuré à 50 000 à 80 000 FCFA par apprenant sur un échéancier de 2 à 3
     versements. Pire, le cron ajoutera plus tard ces mois en "en_attente" et
     déclenchera des relances J-7/J-3/J-1 pour des sommes DÉJÀ payées.
     On étend donc la génération jusqu'au dernier versement connu, sans toucher à
     date_fin_formation : le plan reste ouvert et le cron continue de le prolonger. */
  const borneEcheancier = (dateDebut, dateFin, paiements) => {
    const dates = (paiements || []).map((p) => p.date).filter(Boolean).sort();
    const dernierVersement = dates[dates.length - 1] || null;
    const socle = dateFin || new Date().toISOString().slice(0, 10);
    if (!dernierVersement) return dateFin || null;
    return dernierVersement > socle ? dernierVersement : (dateFin || null);
  };

  /* ══ HISTORIQUE DES IMPORTS ══
     L'annulation ne vivait que dans le rapport de fin : la fenêtre fermée, plus aucun
     moyen de défaire un lot depuis l'interface (il a fallu un script de suppression
     sur mesure pour nettoyer un import de test). Cet écran la rend accessible à tout
     moment, et permet aussi de relancer un lot interrompu - l'idempotence ne recrée
     que les lignes manquantes. Le serveur cloisonne : chacune ne voit que ses lots. */
  const [showHistoriqueImports, setShowHistoriqueImports] = useState(false);
  const [historiqueImports, setHistoriqueImports] = useState([]);
  const [historiqueChargement, setHistoriqueChargement] = useState(false);
  const [relanceEnCours, setRelanceEnCours] = useState(null);
  // Détail d'un lot passé. Le serveur conserve le résultat ligne par ligne dans
  // imports.resultats : c'est ce qui permet de rouvrir un import annulé et de lire
  // pourquoi ses lignes ont échoué, longtemps après que le rapport a été fermé.
  const [detailImport, setDetailImport] = useState(null);

  const ouvrirDetailImport = async (job) => {
    setDetailImport({ job, lignes: null, erreur: null });
    try {
      const r = await fetch(`${API_URL}/api/imports/${job.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Import introuvable");
      setDetailImport({ job: d.import || job, lignes: d.import?.resultats || [], erreur: null });
    } catch (err) {
      setDetailImport({ job, lignes: [], erreur: err.message });
    }
  };

  const chargerHistoriqueImports = async () => {
    setHistoriqueChargement(true);
    try {
      setHistoriqueImports(await listerImports({
        apiUrl: API_URL, token: localStorage.getItem("admin_token"), limit: 30,
      }));
    } catch (err) {
      toast.error("Historique indisponible : " + err.message);
    } finally {
      setHistoriqueChargement(false);
    }
  };

  const ouvrirHistoriqueImports = () => { setDetailImport(null); setShowHistoriqueImports(true); chargerHistoriqueImports(); };

  const annulerDepuisHistorique = async (job) => {
    const ok = await confirm(
      `Tout ce que l'import « ${job.fichier_nom || job.type} » a créé sera supprimé : dossiers, `
      + "versements, classes et comptes d'accès. Cette action est définitive.",
      { titre: "Annuler cet import ?", confirmLabel: "Annuler l'import", danger: true },
    );
    if (!ok) return;
    setRelanceEnCours(job.id);
    try {
      const s = await annulerImport({ apiUrl: API_URL, token: localStorage.getItem("admin_token"), importId: job.id });
      toast.success(`Import annulé - ${s.assignations_parcours || 0} dossier(s), ${s.paiements_parcours || 0} versement(s), `
        + `${s.groupes || 0} classe(s), ${s.comptes || 0} compte(s) supprimé(s)`);
      await chargerHistoriqueImports();
      fetchClasses();
    } catch (err) {
      toast.error("Annulation impossible : " + err.message);
    } finally {
      setRelanceEnCours(null);
    }
  };

  const relancerDepuisHistorique = async (job) => {
    setRelanceEnCours(job.id);
    try {
      const complet = await lignesDeLImport({
        apiUrl: API_URL, token: localStorage.getItem("admin_token"), importId: job.id,
      });
      const ok = await confirm(
        `${complet.lignes_source.length} ligne(s) vont être rejouées. Celles déjà créées lors du `
        + "premier passage seront automatiquement ignorées - seules les manquantes seront créées.",
        { titre: "Relancer cet import ?", confirmLabel: "Relancer", danger: false },
      );
      if (!ok) { setRelanceEnCours(null); return; }

      setShowHistoriqueImports(false);
      arretImportRef.current = false;
      setArretDemande(false);
      setImportProgress({ label: "Reprise de l'import", pct: 0, traites: 0, total: complet.lignes_source.length });

      const { importId, resultats, interrompu } = await lancerImport({
        apiUrl: API_URL, token: localStorage.getItem("admin_token"),
        type: complet.type, fichierNom: complet.fichier_nom, contexte: complet.contexte || {},
        lignes: complet.lignes_source,
        doitArreter: () => arretImportRef.current,
        onProgress: (pct, info) => setImportProgress({
          label: "Reprise de l'import", pct, traites: info.traites, total: info.total,
        }),
      });

      const { ok: nbOk, erreur, ignore } = compterResultats(resultats);
      setDernierImportId(importId);
      // L'ancien lot est desormais repris : sans ce marquage il resterait
      // "interrompu" pour toujours (il ne peut pas etre efface, il a des donnees).
      if (!interrompu) {
        await marquerImportRepris({
          apiUrl: API_URL, token: localStorage.getItem("admin_token"), importId: job.id,
        });
      }
      toast.success(`Reprise terminée - ${nbOk} créée(s)`
        + (ignore ? `, ${ignore} déjà présente(s)` : "")
        + (erreur ? `, ${erreur} en erreur` : "")
        + (interrompu ? " (interrompue)" : "") + " ✓");
      fetchClasses();
    } catch (err) {
      toast.error("Relance impossible : " + err.message);
    } finally {
      setImportProgress(null);
      setRelanceEnCours(null);
    }
  };

  /* Compteur d'alerte sur le bouton Historique : un lot annulé signifie que des
     données créées ont été supprimées - la commerciale doit le savoir, surtout si
     l'annulation vient d'une autre personne (rôle à vue totale). Le badge s'éteint
     quand elle efface l'entrée du journal : c'est un accusé de réception, pas une
     alerte permanente qu'on finirait par ne plus voir. */
  // Deux niveaux distincts : un lot interrompu ATTEND une action (le relancer pour
  // créer les lignes manquantes) ; un lot annulé CONSTATE une suppression. Le premier
  // est le plus actionnable, d'où sa place en tête.
  const nbImportsInterrompus = historiqueImports.filter(j => j.statut === "interrompu").length;
  const nbImportsAnnules     = historiqueImports.filter(j => j.statut === "annule").length;

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const liste = await listerImports({
          apiUrl: API_URL, token: localStorage.getItem("admin_token"), limit: 30,
        });
        if (!annule) setHistoriqueImports(liste);
      } catch { /* l'historique reste consultable a la demande, pas d'alerte au chargement */ }
    })();
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const supprimerDepuisHistorique = async (job) => {
    const ok = await confirm(
      "L'entrée disparaîtra de l'historique. Les données ne sont pas touchées - ce lot "
      + (job.statut === "annule" ? "a déjà été annulé." : "n'a rien créé."),
      { titre: "Effacer cette ligne de l'historique ?", confirmLabel: "Effacer", danger: true },
    );
    if (!ok) return;
    setRelanceEnCours(job.id);
    try {
      await supprimerImport({ apiUrl: API_URL, token: localStorage.getItem("admin_token"), importId: job.id });
      toast.success("Entrée effacée de l'historique");
      await chargerHistoriqueImports();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRelanceEnCours(null);
    }
  };

  const rendreModalHistoriqueImports = () => !showHistoriqueImports ? null : (
    <Modal title="📥 Historique des imports" onClose={() => { setShowHistoriqueImports(false); setDetailImport(null); }} size="xl">
      <p style={{ margin:"-8px 0 16px", fontSize:12, color:"#6b7280" }}>
        Vos imports récents - cliquez une ligne pour voir son détail ligne par ligne.
        « Relancer » rejoue un lot incomplet sans recréer ce qui existe déjà ; « Annuler »
        supprime tout ce que le lot a créé, dans les {FENETRE_ANNULATION_HEURES} h qui suivent l'import.
      </p>
      {detailImport ? (
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, flexWrap:"wrap" }}>
            <button onClick={() => setDetailImport(null)}
              style={{ padding:"5px 12px", background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:7,
                fontSize:11, fontWeight:700, color:"#334155", cursor:"pointer" }}>← Retour</button>
            <strong style={{ fontSize:13, color:"#0f172a" }}>{detailImport.job.fichier_nom || detailImport.job.type}</strong>
            <span style={{ fontSize:11, color:"#94a3b8" }}>
              {new Date(detailImport.job.created_at).toLocaleString("fr-FR")}
            </span>
          </div>
          {detailImport.erreur ? (
            <p style={{ padding:"24px 0", textAlign:"center", color:"#dc2626", fontSize:13 }}>{detailImport.erreur}</p>
          ) : !detailImport.lignes ? (
            <p style={{ padding:"24px 0", textAlign:"center", color:"#94a3b8", fontSize:13 }}>Chargement du détail…</p>
          ) : !detailImport.lignes.length ? (
            <p style={{ padding:"24px 0", textAlign:"center", color:"#94a3b8", fontSize:13 }}>
              Aucun détail conservé pour cet import.
            </p>
          ) : (
            <div style={{ overflowX:"auto", maxHeight:420, overflowY:"auto", borderRadius:10, border:"1.5px solid #e2e8f0" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead style={{ position:"sticky", top:0, zIndex:2 }}>
                  <tr style={{ background:"#f8fafc" }}>
                    {["Ligne","Statut","Détail"].map(h => (
                      <th key={h} style={{ padding:"9px 10px", textAlign:"left", fontWeight:700, fontSize:11,
                        color:"#64748b", borderBottom:"2px solid #e2e8f0", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detailImport.lignes.map((l, i) => {
                    const cfg = l.statut === "ok"     ? { c:"#16a34a", bg:"#dcfce7", t:"✓ Créé" }
                              : l.statut === "ignore" ? { c:"#92400e", bg:"#fef3c7", t:"= Ignoré" }
                              :                         { c:"#dc2626", bg:"#fee2e2", t:"✗ Erreur" };
                    return (
                      <tr key={i} style={{ borderBottom:"1px solid #f1f5f9", background: l.statut === "erreur" ? "#fffbfb" : "#fff" }}>
                        <td style={{ padding:"7px 10px", fontWeight:600, color:"#0f172a" }}>{l.nom || "-"}</td>
                        <td style={{ padding:"7px 10px", whiteSpace:"nowrap" }}>
                          <span style={{ background:cfg.bg, color:cfg.c, borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>{cfg.t}</span>
                        </td>
                        <td style={{ padding:"7px 10px", color: l.statut === "erreur" ? "#dc2626" : "#64748b" }}>{l.detail || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : historiqueChargement ? (
        <p style={{ padding:"24px 0", textAlign:"center", color:"#94a3b8", fontSize:13 }}>Chargement…</p>
      ) : !historiqueImports.length ? (
        <p style={{ padding:"24px 0", textAlign:"center", color:"#94a3b8", fontSize:13 }}>
          Aucun import pour l'instant.
        </p>
      ) : (
        <div style={{ overflowX:"auto", maxHeight:460, overflowY:"auto", borderRadius:10, border:"1.5px solid #e2e8f0" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead style={{ position:"sticky", top:0, zIndex:2 }}>
              <tr style={{ background:"#f8fafc" }}>
                {["Date","Fichier","Type","Résultat","Statut","Actions"].map(h => (
                  <th key={h} style={{ padding:"9px 10px", textAlign:"left", fontWeight:700, fontSize:11,
                    color:"#64748b", borderBottom:"2px solid #e2e8f0", whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {historiqueImports.map(job => {
                const st = STATUT_IMPORT[job.statut] || { label: job.statut, couleur:"#64748b", fond:"#f1f5f9" };
                const occupe = relanceEnCours === job.id;
                return (
                  <tr key={job.id} onClick={() => ouvrirDetailImport(job)}
                    title="Voir le détail ligne par ligne de cet import"
                    style={{ borderBottom:"1px solid #f1f5f9", cursor:"pointer",
                    background: job.statut === "annule" ? "#fafafa" : "#fff" }}>
                    <td style={{ padding:"8px 10px", whiteSpace:"nowrap", color:"#475569" }}>
                      {new Date(job.created_at).toLocaleString("fr-FR", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" })}
                    </td>
                    <td style={{ padding:"8px 10px", fontWeight:600, maxWidth:220, overflow:"hidden",
                      textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{job.fichier_nom || "-"}</td>
                    <td style={{ padding:"8px 10px", color:"#64748b", whiteSpace:"nowrap" }}>{job.type}</td>
                    <td style={{ padding:"8px 10px", whiteSpace:"nowrap", fontVariantNumeric:"tabular-nums" }}>
                      <span style={{ color:"#16a34a", fontWeight:700 }}>{job.nb_ok}</span>
                      <span style={{ color:"#94a3b8" }}> / {job.nb_lignes}</span>
                      {job.nb_erreur > 0 && <span style={{ color:"#dc2626", marginLeft:6 }}>· {job.nb_erreur} err.</span>}
                      {job.nb_ignore > 0 && <span style={{ color:"#92400e", marginLeft:6 }}>· {job.nb_ignore} ign.</span>}
                    </td>
                    <td style={{ padding:"8px 10px", whiteSpace:"nowrap" }}>
                      <span style={{ background:st.fond, color:st.couleur, borderRadius:20, padding:"2px 9px",
                        fontSize:10, fontWeight:700 }}>{st.label}</span>
                    </td>
                    <td style={{ padding:"8px 10px", whiteSpace:"nowrap" }}>
                      <div style={{ display:"flex", gap:6 }} onClick={(e) => e.stopPropagation()}>
                        {peutEtreRelance(job) && (
                          <button onClick={() => relancerDepuisHistorique(job)} disabled={occupe || !!importProgress}
                            style={{ padding:"5px 10px", background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:7,
                              fontSize:11, fontWeight:600, color:"#334155",
                              cursor:(occupe || importProgress) ? "not-allowed" : "pointer" }}>
                            {occupe ? "…" : "↻ Relancer"}
                          </button>
                        )}
                        {peutEtreAnnule(job) && (
                          <button onClick={() => annulerDepuisHistorique(job)} disabled={occupe || !!importProgress}
                            style={{ padding:"5px 10px", background:"#fff", border:"1.5px solid #fecaca", borderRadius:7,
                              fontSize:11, fontWeight:600, color:"#dc2626",
                              cursor:(occupe || importProgress) ? "not-allowed" : "pointer" }}>
                            {occupe ? "…" : "↩︎ Annuler"}
                          </button>
                        )}
                        {job.statut !== "annule" && !peutEtreAnnule(job) && (
                          <span title={`Passé ${FENETRE_ANNULATION_HEURES} h, l'annulation passe par un administrateur - les apprenants de ce lot ont depuis été assignés, ont payé ou ont été transférés.`}
                            style={{ fontSize:10, color:"#94a3b8", fontWeight:600, alignSelf:"center" }}>
                            🔒 Annulation close
                          </span>
                        )}
                        {peutEtreEfface(job) && (
                          <button onClick={() => supprimerDepuisHistorique(job)} disabled={occupe || !!importProgress}
                            title="Effacer cette ligne de l'historique (les données ne sont pas touchées)"
                            style={{ padding:"5px 10px", background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:7,
                              fontSize:11, fontWeight:600, color:"#64748b",
                              cursor:(occupe || importProgress) ? "not-allowed" : "pointer" }}>
                            🗑
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );

  /* Modal de progression - s'affiche dès la confirmation et reste jusqu'à la fin.
     Volontairement SANS bouton de fermeture : l'import tourne dans la page, la
     fermer en cours de route couperait le lot en deux (situation déjà vécue, où
     l'import a continué 25 minutes dans un onglet abandonné, invisible). */
  const rendreModalProgressionImport = () => !importProgress ? null : (
    <ModaleAnimee ouvert
      style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.62)", display:"flex",
      alignItems:"center", justifyContent:"center", zIndex:10050, padding:16 }}
      panneauStyle={{ background:"#fff", borderRadius:16, padding:"28px 32px", width:440, maxWidth:"92vw",
        boxShadow:"0 24px 60px rgba(0,0,0,0.28)", textAlign:"center" }}>
        <div style={{ fontSize:34, marginBottom:10 }}>📥</div>
        <h3 style={{ margin:"0 0 4px", fontSize:17, color:"#0f172a" }}>{importProgress.label}</h3>
        <p style={{ margin:"0 0 20px", fontSize:12.5, color:"#64748b" }}>
          Création en cours dans la base - merci de patienter.
        </p>

        <div style={{ fontSize:40, fontWeight:800, color: ACCENT_IMPORT, lineHeight:1, marginBottom:14,
          fontVariantNumeric:"tabular-nums" }}>
          {importProgress.pct}%
        </div>

        <div style={{ height:10, background:"#e2e8f0", borderRadius:99, overflow:"hidden", marginBottom:10 }}>
          <div style={{ height:"100%", width:`${importProgress.pct}%`, background: ACCENT_IMPORT,
            borderRadius:99, transition:"width 0.35s ease" }} />
        </div>

        <p style={{ margin:"0 0 18px", fontSize:12, color:"#475569", fontVariantNumeric:"tabular-nums" }}>
          {importProgress.traites} / {importProgress.total} ligne(s) traitée(s)
        </p>


        <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:10, padding:"10px 14px",
          fontSize:11.5, color:"#92400e", lineHeight:1.55 }}>
          ⚠️ Ne fermez pas cette page et ne quittez pas l'onglet tant que l'import n'est pas terminé.
        </div>

        {/* Interruption propre : le moteur s'arrête ENTRE deux paquets, jamais au milieu
            d'une ligne. Les lignes déjà créées restent en base - le rapport les liste, et
            le bouton "Annuler cet import" permet ensuite de tout défaire d'un coup. */}
        <button
          onClick={() => { arretImportRef.current = true; setArretDemande(true); }}
          disabled={arretDemande}
          style={{ marginTop:16, padding:"8px 16px", background:"#fff", border:"1.5px solid #e2e8f0",
            borderRadius:8, fontSize:12, fontWeight:600, color: arretDemande ? "#94a3b8" : "#dc2626",
            cursor: arretDemande ? "not-allowed" : "pointer" }}>
          {arretDemande ? "⏳ Arrêt après le paquet en cours…" : "✕ Interrompre l'import"}
        </button>
    </ModaleAnimee>
  );

  const rendreBoutonAnnulerImport = (onFini) => !dernierImportId ? <span /> : (
    <button
      onClick={async () => {
        const ok = await confirm(
          "Tout ce que cet import a créé sera supprimé : dossiers, versements, classes et comptes d'accès. Cette action est définitive.",
          { titre: "Annuler cet import ?", confirmLabel: "Annuler l'import", danger: true },
        );
        if (!ok) return;
        setAnnulationEnCours(true);
        try {
          const s = await annulerImport({
            apiUrl: API_URL, token: localStorage.getItem("admin_token"), importId: dernierImportId,
          });
          toast.success(`Import annulé - ${s.assignations_parcours || 0} dossier(s), ${s.paiements_parcours || 0} versement(s), `
            + `${s.groupes || 0} classe(s), ${s.comptes || 0} compte(s) supprimé(s)`);
          setDernierImportId(null);
          onFini?.();
        } catch (err) {
          toast.error("Annulation impossible : " + err.message);
        } finally {
          setAnnulationEnCours(false);
        }
      }}
      disabled={annulationEnCours}
      style={{ padding:"9px 18px", background:"#fff", border:"1.5px solid #fecaca", borderRadius:8,
        fontSize:12, color:"#dc2626", cursor: annulationEnCours ? "not-allowed" : "pointer", fontWeight:700 }}>
      {annulationEnCours ? "⏳ Annulation…" : "↩︎ Annuler cet import"}
    </button>
  );


  const [importClassesConfirming,  setImportClassesConfirming]  = useState(false);
  const [importClassesProgress,    setImportClassesProgress]    = useState(0); // 0-100, pendant confirmerImportClasses
  const [importClassesResultats,   setImportClassesResultats]   = useState([]);
  const [showImportClassesResultat, setShowImportClassesResultat] = useState(false);

  // ── Import global (combiné) - classes + apprenants en un seul fichier ────
  // Garde les 2 imports séparés existants tels quels (bouton + logique inchangés) : celui-ci
  // s'ajoute en complément pour le cas où classes et apprenants sont connus en même temps.
  // Principe : crée d'abord les classes manquantes (mêmes règles que "Importer des classes"),
  // puis réutilise à l'identique le pipeline "Importer des apprenants" (mêmes validations,
  // paiements, plan mensuel, lots) une fois les classe_id résolus - aucune duplication de
  // cette logique déjà existante.
  const importGlobalFileRef = useRef(null);
  const [showImportGlobalUpload,   setShowImportGlobalUpload]   = useState(false);
  const [importGlobalProcessing,   setImportGlobalProcessing]   = useState(false);

  // Parseur partagé (classes de groupe + coaching privé) - un horaire par cellule Excel,
  // créneaux séparés par ";" ou ",", format "Lun 08:00-10:00". Jamais bloquant : un créneau
  // mal formé est signalé en avertissement mais n'empêche pas l'import de la ligne.
  const JOURS_VALIDES_IMPORT = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];
  const parseHorairesImport = (str) => {
    const horaire = [];
    const erreurs = [];
    if (!str) return { horaire, erreurs };
    str.split(/[;,]/).map(s => s.trim()).filter(Boolean).forEach(slot => {
      const m = slot.match(/^([A-Za-zÀ-ÿ]{3,})\.?\s+(\d{1,2}:\d{2})\s*[-–àa]+\s*(\d{1,2}:\d{2})$/i);
      if (!m) { erreurs.push(`Créneau "${slot}" non reconnu (format attendu : Lun 08:00-10:00)`); return; }
      const jourNorm = m[1].slice(0, 3);
      const jour = JOURS_VALIDES_IMPORT.find(j => j.toLowerCase() === jourNorm.toLowerCase());
      if (!jour) { erreurs.push(`Jour "${m[1]}" non reconnu dans "${slot}"`); return; }
      horaire.push({ jour, debut: m[2], fin: m[3] });
    });
    return { horaire, erreurs };
  };

  // Parseur partagé (coaching privé + apprenants) - nombre de versements ILLIMITÉ par
  // apprenant (relation déjà réelle : ça peut être 1 comme 20 versements déjà encaissés
  // avant la migration), une cellule = "montant@date@référence" séparés par ";", date et
  // référence optionnelles. Jamais bloquant : un versement mal formé est signalé en
  // avertissement, sans empêcher l'import du reste de la ligne.
  const parsePaiementsImport = (str) => {
    const paiements = [];
    const erreurs = [];
    if (!str) return { paiements, erreurs };
    str.split(";").map(s => s.trim()).filter(Boolean).forEach(entree => {
      const parts = entree.split("@").map(p => p.trim());
      const montant = Number(parts[0]);
      if (!Number.isFinite(montant) || montant <= 0) { erreurs.push(`Versement "${entree}" non reconnu (montant attendu en premier, ex: 80000@2026-09-01@TP-001)`); return; }
      paiements.push({ montant, date: parts[1] || "", reference: parts[2] || "" });
    });
    return { paiements, erreurs };
  };

  const telechargerModeleImportClasses = () => {
    const headers = ["Nom classe*", "Filière", "Niveau", "Date de début (AAAA-MM-JJ)", "Date de fin (AAAA-MM-JJ)", "Capacité max", "Coach (email)*", "Horaires (ex: Lun 08:00-10:00; Mer 14:00-16:00)"];
    const exemple = ["Anglais B1 Soir 3", "Anglais Pro", "B1", "2026-09-01", "", "20", "coach@bet-coaching.ci", "Lun 18:00-20:00; Mer 18:00-20:00"];
    const ws = XLSX.utils.aoa_to_sheet([headers, exemple]);
    ws["!cols"] = headers.map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Import classes");
    XLSX.writeFile(wb, "modele_import_classes_bet.xlsx");
    toast.success("Modèle téléchargé ✓");
  };

  const lireEtPrevisualiserClasses = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setImportFichierNom(file.name);
    try {
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf, { type: "array" });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (!rows.length) { toast.error("Fichier vide ou non reconnu"); return; }

      const norm = (s) => String(s || "").toLowerCase().trim()
        .replace(/[éèêë]/g, "e").replace(/[àâä]/g, "a").replace(/[ùûü]/g, "u").replace(/[îï]/g, "i").replace(/[ôö]/g, "o")
        .replace(/[^a-z0-9]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
      const NIVEAUX_VALIDES = ["A1", "A2", "B1", "B2", "C1", "C2"];
      const nomsExistants = new Set(classes.map(c => (c.nom || "").toLowerCase().trim()));
      const nomsVusDansBatch = new Set();
      const coachsParEmail = new Map(coachsList.filter(c => c.email).map(c => [c.email.toLowerCase().trim(), c]));

      const lignes = rows.map((row, idx) => {
        const r = {};
        Object.keys(row).forEach(k => { r[norm(k)] = String(row[k] || "").trim(); });

        const nom      = r.nom_classe || r.nom_groupe || r.nom || "";
        const filiere  = r.filiere || "";
        const niveauBrut = (r.niveau || "").toUpperCase();
        const niveau   = NIVEAUX_VALIDES.includes(niveauBrut) ? niveauBrut : "";
        const dateDebut = r.date_de_debut_aaaa_mm_jj || r.date_de_debut || r.date_debut || "";
        const dateFin   = r.date_de_fin_aaaa_mm_jj || r.date_de_fin || r.date_fin || "";
        const capaciteBrut = r.capacite_max || r.capacite || "";
        const capacite_max = capaciteBrut ? Number(capaciteBrut) : 20;
        const coachEmailBrut = r.coach_email || r.coach || "";
        const coachTrouve = coachEmailBrut ? coachsParEmail.get(coachEmailBrut.toLowerCase().trim()) : null;
        const horairesBrut = r.horaires_ex_lun_08_00_10_00_mer_14_00_16_00 || r.horaires || r.horaire || "";
        const { horaire, erreurs: erreursHoraires } = parseHorairesImport(horairesBrut);

        const erreurs = [];
        const avertissements = [...erreursHoraires];
        if (!nom) erreurs.push("Nom de la classe manquant");
        else {
          const nomLower = nom.toLowerCase();
          if (nomsExistants.has(nomLower)) erreurs.push("Une classe avec ce nom existe déjà");
          if (nomsVusDansBatch.has(nomLower)) erreurs.push("Nom en doublon dans le fichier");
          nomsVusDansBatch.add(nomLower);
        }
        if (niveauBrut && !niveau) avertissements.push(`Niveau "${niveauBrut}" non reconnu (A1 à C2) - laissé vide`);
        if (!filiere) avertissements.push("Filière non renseignée");
        if (capaciteBrut && (!Number.isFinite(capacite_max) || capacite_max <= 0)) avertissements.push("Capacité invalide - 20 par défaut appliqué");
        if (!coachEmailBrut) erreurs.push("Coach manquant - obligatoire pour une classe déjà existante");
        else if (!coachTrouve) erreurs.push(`Coach "${coachEmailBrut}" introuvable`);

        const ligneVide = Object.values(row).every(v => String(v || "").trim() === "");

        return {
          _idx: idx + 2, nom, filiere, niveau, date_debut: dateDebut, date_fin: dateFin, horaire,
          capacite_max: (Number.isFinite(capacite_max) && capacite_max > 0) ? capacite_max : 20,
          coach_id: coachTrouve?.id || null, coach_nom: coachTrouve ? `${coachTrouve.prenom||""} ${coachTrouve.nom||""}`.trim() : "",
          _erreurs: erreurs, _avertissements: avertissements, _ligneVide: ligneVide,
          _statut: erreurs.length > 0 ? "erreur" : avertissements.length > 0 ? "avertissement" : "ok",
        };
      }).filter(l => !l._ligneVide);

      if (!lignes.length) { toast.error("Aucune ligne exploitable dans le fichier"); return; }
      setImportClassesPreviewRows(lignes);
      setShowImportClassesUpload(false);
      setShowImportClassesPreview(true);
    } catch (err) { toast.error("Erreur lecture fichier : " + err.message); }
  };

  const confirmerImportClasses = async () => {
    const lignesValides = importClassesPreviewRows.filter(l => l._statut !== "erreur");
    if (!lignesValides.length) return;
    const typeCoursImport = (assistanteTypeCours && assistanteTypeCours !== "les_deux") ? assistanteTypeCours : "presentiel";
    if (typeCoursImport !== "en_ligne" && !assistanteCentreId) {
      toast.error("Centre introuvable pour votre compte - import annulé pour éviter des classes invisibles.");
      return;
    }
    setImportClassesConfirming(true);
    setImportClassesProgress(0);
    arretImportRef.current = false;
    setArretDemande(false);
    setImportProgress({ label: "Import des classes", pct: 0, traites: 0, total: lignesValides.length });

    try {
      const { importId, resultats, interrompu } = await lancerImport({
        apiUrl: API_URL,
        token: localStorage.getItem("admin_token"),
        type: "classe",
        fichierNom: importFichierNom || "Import classes",
        contexte: { type_cours: typeCoursImport, centre_id: assistanteCentreId || null },
        lignes: lignesValides,
        doitArreter: () => arretImportRef.current,
        onProgress: (pct, info) => {
          setImportClassesProgress(pct);
          setImportProgress({ label: "Import des classes", pct, traites: info.traites, total: info.total });
        },
      });

      const { ok, erreur, ignore } = compterResultats(resultats);
      setDernierImportId(importId);
      setImportClassesResultats(resultats);
      setShowImportClassesPreview(false);
      setImportClassesPreviewRows([]);
      setShowImportClassesResultat(true);
      if (interrompu) {
        toast("Import interrompu - " + ok + " ligne(s) créée(s) avant l'arrêt. "
          + "Le rapport les liste, et « Annuler cet import » permet de tout défaire.",
          { icon: "⏹️", duration: 7000 });
      }
      if (ok > 0) {
        toast.success(`${ok} classe(s) créée(s)`
          + (erreur ? ` · ${erreur} erreur(s)` : "")
          + (ignore ? ` · ${ignore} déjà présente(s)` : "") + " ✓");
        fetchClasses();
      } else if (ignore > 0) {
        toast(`${ignore} classe(s) déjà importée(s) - rien à créer`);
      } else {
        toast.error("Aucune classe créée");
      }
    } catch (err) {
      toast.error("Import interrompu : " + err.message);
    } finally {
      setImportClassesConfirming(false);
      setImportProgress(null);
    }
  };

  const telechargerModeleImportGlobal = () => {
    const headers = [
      "Nom classe*", "Filière classe", "Niveau classe", "Date début classe (AAAA-MM-JJ)", "Date fin classe (AAAA-MM-JJ)",
      "Capacité max classe", "Coach classe (email)*", "Horaires classe (ex: Lun 08:00-10:00; Mer 14:00-16:00)",
      "Nom apprenant*", "Prénom apprenant", "Email apprenant", "Téléphone apprenant", "Niveau apprenant",
      "Date de début formation (AAAA-MM-JJ)", "Profil (adulte/enfant)", "Nom du parent (si enfant)", "Téléphone du parent (si enfant)", "Email du parent (si enfant)",
      "Paiements", "Notes",
    ];
    // 2 lignes pour la même classe : seule la 1ère porte les colonnes "classe" (les
    // suivantes peuvent les laisser vides, la classe n'est créée/lue qu'une fois par nom).
    const exemple1 = ["Anglais B1 Soir 3", "Anglais Pro", "B1", "2026-09-01", "", "20", "coach@bet-coaching.ci", "Lun 18:00-20:00; Mer 18:00-20:00",
      "Kouassi", "Awa", "awa.kouassi@example.com", "0700000000", "B1", "2026-09-01", "adulte", "", "", "", "80000@2026-09-01@TP-001", ""];
    const exemple2 = ["Anglais B1 Soir 3", "", "", "", "", "", "", "",
      "Bamba", "Moussa", "moussa.bamba@example.com", "0700000001", "B1", "2026-09-01", "adulte", "", "", "", "80000@2026-09-01@TP-002", ""];
    const ws = XLSX.utils.aoa_to_sheet([headers, exemple1, exemple2]);
    ws["!cols"] = headers.map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Import global");
    XLSX.writeFile(wb, "modele_import_global_bet.xlsx");
    toast.success("Modèle téléchargé ✓");
  };

  // Lit le fichier combiné, crée d'abord les classes manquantes (une fois par nom distinct),
  // puis construit les lignes apprenants avec classe_id déjà résolu et les dépose dans le
  // pipeline "Importer des apprenants" existant (même aperçu, même bouton de validation,
  // mêmes paiements/plan mensuel) - rien de la logique déjà en place n'est dupliqué ici.
  const lireEtPrevisualiserGlobal = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setImportFichierNom(file.name);
    setImportGlobalProcessing(true);
    try {
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf, { type: "array" });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (!rows.length) { toast.error("Fichier vide ou non reconnu"); return; }

      const norm = (s) => String(s || "").toLowerCase().trim()
        .replace(/[éèêë]/g, "e").replace(/[àâä]/g, "a").replace(/[ùûü]/g, "u").replace(/[îï]/g, "i").replace(/[ôö]/g, "o")
        .replace(/[^a-z0-9]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
      const NIVEAUX_VALIDES = ["A1", "A2", "B1", "B2", "C1", "C2"];
      const coachsParEmail = new Map(coachsList.filter(c => c.email).map(c => [c.email.toLowerCase().trim(), c]));

      const rowsNorm = rows.map(row => {
        const r = {};
        Object.keys(row).forEach(k => { r[norm(k)] = String(row[k] || "").trim(); });
        return r;
      }).filter(r => Object.values(r).some(v => v !== ""));
      if (!rowsNorm.length) { toast.error("Aucune ligne exploitable dans le fichier"); return; }

      // 1. Regroupement par classe - la 1ère ligne rencontrée pour un nom donné porte les
      //    métadonnées de la classe (les lignes suivantes du même groupe peuvent les laisser vides).
      const groupes = new Map(); // clé normalisée -> { nom, filiere, niveau, ..., rows:[] }
      rowsNorm.forEach(r => {
        const classeNom = r.nom_classe || "";
        const cle = normNomImport(classeNom);
        if (!groupes.has(cle)) {
          groupes.set(cle, {
            nom: classeNom,
            filiere: r.filiere_classe || "",
            niveauBrut: (r.niveau_classe || "").toUpperCase(),
            date_debut: r.date_debut_classe_aaaa_mm_jj || r.date_debut_classe || "",
            date_fin: r.date_fin_classe_aaaa_mm_jj || r.date_fin_classe || "",
            capaciteBrut: r.capacite_max_classe || "",
            coachEmailBrut: r.coach_classe_email || r.coach_classe || "",
            horairesBrut: r.horaires_classe_ex_lun_08_00_10_00_mer_14_00_16_00 || r.horaires_classe || "",
            rows: [],
          });
        }
        groupes.get(cle).rows.push(r);
      });

      // 2. Créer les classes manquantes (celles dont le nom ne correspond à aucune classe
      //    ouverte existante) - mêmes règles/erreurs que "Importer des classes".
      const classesFraiches = (await fetchClasses()) || classes;
      const classesOuvertes = classesFraiches.filter(c => c.statut !== "terminé" && c.statut !== "cloture");
      let classesParNom = new Map(classesOuvertes.map(c => [normNomImport(c.nom), c]));
      const typeCoursImport = (assistanteTypeCours && assistanteTypeCours !== "les_deux") ? assistanteTypeCours : "presentiel";
      const erreurCreationParClasse = new Map(); // cle -> message d'erreur si la création a échoué/été refusée
      let nbClassesCreees = 0;
      const classesACreer = [];
      let jobImportGlobal = null;

      for (const [cle, g] of groupes) {
        if (!g.nom || classesParNom.has(cle)) continue; // pas de nom, ou classe déjà existante → rien à créer
        const coachTrouve = g.coachEmailBrut ? coachsParEmail.get(g.coachEmailBrut.toLowerCase().trim()) : null;
        if (!g.coachEmailBrut) { erreurCreationParClasse.set(cle, "Classe non créée - coach manquant"); continue; }
        if (!coachTrouve) { erreurCreationParClasse.set(cle, `Classe non créée - coach "${g.coachEmailBrut}" introuvable`); continue; }
        if (typeCoursImport !== "en_ligne" && !assistanteCentreId) { erreurCreationParClasse.set(cle, "Classe non créée - centre introuvable pour votre compte"); continue; }
        const niveau = NIVEAUX_VALIDES.includes(g.niveauBrut) ? g.niveauBrut : "";
        const capaciteBrutNum = g.capaciteBrut ? Number(g.capaciteBrut) : 20;
        const capacite_max = (Number.isFinite(capaciteBrutNum) && capaciteBrutNum > 0) ? capaciteBrutNum : 20;
        const { horaire } = parseHorairesImport(g.horairesBrut);
        classesACreer.push({ cle, ligne: {
          nom: g.nom, filiere: g.filiere || null, niveau: niveau || null,
          date_debut: g.date_debut || null, date_fin: g.date_fin || null,
          capacite_max, horaire, coach_id: coachTrouve.id,
        }});
      }

      // Création des classes PAR LE MOTEUR, dans le même lot que les apprenants qui
      // suivront. C'est ce qui leur donne un import_id - donc ce qui les rend annulables
      // avec le reste. Créées hors moteur (ancien appel direct à POST /api/groupes),
      // elles n'étaient estampillées nulle part et survivaient à l'annulation, laissant
      // des classes vides derrière elle.
      if (classesACreer.length) {
        jobImportGlobal = await ouvrirImport({
          apiUrl: API_URL, token: localStorage.getItem("admin_token"),
          type: "apprenant", fichierNom: file.name, nbLignes: rowsNorm.length,
          contexte: {
            source: "import_masse_apprenants_assistante_commerciale",
            assistante_id: assistanteId || null,
            centre_id: assistanteCentreId || null,
            type_cours: typeCoursImport,
          },
        });
        const { resultats: resClasses } = await lancerImport({
          apiUrl: API_URL, token: localStorage.getItem("admin_token"),
          importIdExistant: jobImportGlobal, typeLot: "classe", cloturer: false,
          contexte: { type_cours: typeCoursImport, centre_id: assistanteCentreId || null },
          lignes: classesACreer.map(c => c.ligne),
        });
        const parNom = new Map((resClasses || []).map(r => [r.nom, r]));
        classesACreer.forEach(({ cle, ligne }) => {
          const r = parNom.get(ligne.nom);
          if (!r || r.statut === "erreur") erreurCreationParClasse.set(cle, `Classe non créée - ${r?.detail || "erreur inconnue"}`);
          else nbClassesCreees++;
        });
      }

      // 3. Recharger les classes (celles qu'on vient de créer doivent maintenant matcher)
      //    puis construire les lignes apprenants exactement comme "Importer des apprenants".
      const classesApresCreation = (await fetchClasses()) || classesFraiches;
      const classesOuvertesApres = classesApresCreation.filter(c => c.statut !== "terminé" && c.statut !== "cloture");
      classesParNom = new Map(classesOuvertesApres.map(c => [normNomImport(c.nom), c]));
      const compteParClasse = new Map();

      const lignesApprenants = [];
      let idx = 1;
      for (const [cle, g] of groupes) {
        const classeTrouvee = g.nom ? classesParNom.get(cle) : null;
        for (const r of g.rows) {
          idx++;
          const nom        = r.nom_apprenant || r.nom || "";
          const prenom     = r.prenom_apprenant || r.prenom || "";
          const email      = r.email_apprenant || r.email || "";
          const telephone  = r.telephone_apprenant || r.telephone || "";
          const niveauBrut = (r.niveau_apprenant || r.niveau || "").toUpperCase();
          const niveau     = NIVEAUX_VALIDES.includes(niveauBrut) ? niveauBrut : "";
          const dateDebut  = r.date_de_debut_formation_aaaa_mm_jj || r.date_de_debut_formation || "";
          const profilBrut = (r.profil_adulte_enfant || r.profil || "adulte").toLowerCase();
          const profilType = profilBrut === "enfant" ? "enfant" : "adulte";
          const nomParent  = r.nom_du_parent_si_enfant || r.nom_parent || "";
          const telParent  = r.telephone_du_parent_si_enfant || r.telephone_parent || "";
        const emailParent = r.email_du_parent_si_enfant || r.email_parent || "";
          const notes      = r.notes || "";

          const erreurs = [];
          const avertissements = [];
          const paiementsBrut = r.paiements || r.versements || "";
          const { paiements, erreurs: erreursPaiements } = parsePaiementsImport(paiementsBrut);
          avertissements.push(...erreursPaiements);
          const montant_paye = paiements.length ? paiements.reduce((s, p) => s + p.montant, 0) : null;

          if (!nom) erreurs.push("Nom de l'apprenant manquant");
          if (g.nom && !classeTrouvee) erreurs.push(erreurCreationParClasse.get(cle) || `Classe "${g.nom}" introuvable`);
          if (!g.nom) avertissements.push("Aucune classe précisée - sera assignée après l'import (choix groupé)");
          if (!paiements.length) avertissements.push("Aucun paiement renseigné - aucun plan mensuel ne sera créé pour cet apprenant");
          if (niveauBrut && !niveau) avertissements.push(`Niveau "${niveauBrut}" non reconnu (A1 à C2) - laissé vide`);
          if (!email) avertissements.push("Email non renseigné - un identifiant technique sera généré");
          if (profilType === "enfant" && (!nomParent || !telParent)) erreurs.push("Nom et téléphone du parent obligatoires pour un profil enfant");
        // Non bloquant : sans email du parent, l'enfant reçoit un identifiant technique
        // (<nom>@betacademy.app) et la famille ne reçoit jamais ses accès.
        if (profilType === "enfant" && !emailParent) avertissements.push("Email du parent non renseigné - la famille ne recevra pas les accès de l'enfant");

          if (classeTrouvee) {
            const n = (compteParClasse.get(classeTrouvee.id) || 0) + 1;
            compteParClasse.set(classeTrouvee.id, n);
            const dejaInscrits = classeTrouvee.nb_apprenants || 0;
            if (classeTrouvee.capacite_max && (dejaInscrits + n) > classeTrouvee.capacite_max) {
              avertissements.push(`Classe "${classeTrouvee.nom}" bientôt/déjà à capacité (${dejaInscrits}/${classeTrouvee.capacite_max}) - inscription quand même, ce sont des places déjà réelles`);
            }
          }

          lignesApprenants.push({
            _idx: idx, nom, prenom, email, telephone, niveau, notes, date_debut: dateDebut,
            classe_saisie: g.nom, classe_id: classeTrouvee?.id || null, classe_nom: classeTrouvee?.nom || null,
            profil_type: profilType, nom_parent: nomParent, telephone_parent: telParent, email_parent: emailParent,
            paiements, montant_paye: Number.isFinite(montant_paye) ? montant_paye : null,
            _erreurs: erreurs, _avertissements: avertissements, _ligneVide: false,
            _statut: erreurs.length > 0 ? "erreur" : avertissements.length > 0 ? "avertissement" : "ok",
          });
        }
      }

      if (nbClassesCreees > 0) toast.success(`${nbClassesCreees} classe(s) créée(s) ✓`);
      if (erreurCreationParClasse.size > 0) toast.error(`${erreurCreationParClasse.size} classe(s) n'ont pas pu être créées - voir le détail par ligne`);

      // Réutilise à l'identique la suite du pipeline "Importer des apprenants" déjà existant.
      setImportGlobalJobId(jobImportGlobal);
      setImportApprenantsPreviewRows(lignesApprenants);
      setShowImportGlobalUpload(false);
      setShowImportApprenantsPreview(true);
    } catch (err) {
      toast.error("Erreur lecture fichier : " + err.message);
    } finally {
      setImportGlobalProcessing(false);
    }
  };

  /* ══════════════════════════════════════════════════════
     IMPORT COACHING PRIVÉ (Excel) - présentiel/domicile uniquement.
     Pour des relations déjà réelles (apprenant + coach déjà ensemble
     dans la vraie vie) : crée directement dossier + classe + coach +
     apprenant dans leur état final, en contournant le circuit normal
     de validation/paiement - réutilise les 3 mêmes endpoints que le
     flux manuel de finalisation (handleAssignCentre ci-dessus), sans
     déclencher les notifications de transmission/validation qui n'ont
     pas lieu d'être ici.
  ══════════════════════════════════════════════════════ */
  const importPriveFileRef = useRef(null);
  const [showImportPriveUpload,  setShowImportPriveUpload]  = useState(false);
  const [importPrivePreviewRows, setImportPrivePreviewRows] = useState([]);
  const [showImportPrivePreview, setShowImportPrivePreview] = useState(false);
  const [importPriveConfirming,  setImportPriveConfirming]  = useState(false);
  const [importPriveProgress,    setImportPriveProgress]    = useState(0); // 0-100, pendant confirmerImportPrive
  const [importPriveResultats,   setImportPriveResultats]   = useState([]);
  const [showImportPriveResultat, setShowImportPriveResultat] = useState(false);

  const telechargerModeleImportPrive = () => {
    const headers = [
      "Nom apprenant*", "Prénom apprenant", "Email apprenant", "Téléphone apprenant", "Niveau",
      "Filière / Formation", "Lieu (cabinet/domicile)", "Adresse (si domicile)", "Coach (email)*",
      "Date de début (AAAA-MM-JJ)", "Date de fin (AAAA-MM-JJ)", "Pack (classique/intensive)",
      "Profil (adulte/enfant)", "Nom du parent (si enfant)", "Téléphone du parent (si enfant)", "Email du parent (si enfant)",
      "Paiements", "Objectif client", "Notes", "Disponibilités (ex: Lun 08:00-10:00; Mer 14:00-16:00)",
    ];
    const exemple = [
      "Kouassi", "Awa", "awa.kouassi@example.com", "0700000000", "B1", "Anglais Pro", "cabinet", "",
      "coach@bet-coaching.ci", "2026-09-01", "", "classique", "adulte", "", "", "",
      "80000@2026-09-01@TP-001; 70000@2026-10-01@TP-002",
      "", "", "Lun 18:00-20:00; Mer 18:00-20:00",
    ];
    // 2e ligne : profil enfant - email/téléphone de l'apprenant laissés vides (ce sont ceux
    // du parent qui font foi), nom ET téléphone du parent obligatoires.
    const exempleEnfant = [
      "Bamba", "Yannick", "", "", "A1", "Anglais Kids", "cabinet", "",
      "coach@bet-coaching.ci", "2026-09-01", "", "classique", "enfant", "Bamba Bernard", "0500000000", "parent.bamba@example.com",
      "50000@2026-09-01@TP-003",
      "", "", "Sam 10:00-12:00",
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, exemple, exempleEnfant]);
    ws["!cols"] = headers.map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Import coaching privé");
    XLSX.writeFile(wb, "modele_import_coaching_prive_bet.xlsx");
    toast.success("Modèle téléchargé ✓");
  };

  const lireEtPrevisualiserPrive = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setImportFichierNom(file.name);
    try {
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf, { type: "array" });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (!rows.length) { toast.error("Fichier vide ou non reconnu"); return; }

      const norm = (s) => String(s || "").toLowerCase().trim()
        .replace(/[éèêë]/g, "e").replace(/[àâä]/g, "a").replace(/[ùûü]/g, "u").replace(/[îï]/g, "i").replace(/[ôö]/g, "o")
        .replace(/[^a-z0-9]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
      const NIVEAUX_VALIDES = ["A1", "A2", "B1", "B2", "C1", "C2"];
      const coachsParEmail = new Map(coachsList.filter(c => c.email).map(c => [c.email.toLowerCase().trim(), c]));

      const lignes = rows.map((row, idx) => {
        const r = {};
        Object.keys(row).forEach(k => { r[norm(k)] = String(row[k] || "").trim(); });

        const nom        = r.nom_apprenant || r.nom || "";
        const prenom     = r.prenom_apprenant || r.prenom || "";
        const email      = r.email_apprenant || r.email || "";
        const telephone  = r.telephone_apprenant || r.telephone || "";
        const niveauBrut = (r.niveau || "").toUpperCase();
        const niveau     = NIVEAUX_VALIDES.includes(niveauBrut) ? niveauBrut : "";
        const filiere    = r.filiere_formation || r.filiere || r.formation || "";
        const lieuBrut   = (r.lieu_cabinet_domicile || r.lieu || "cabinet").toLowerCase();
        const lieu       = lieuBrut === "domicile" ? "domicile" : "cabinet";
        const adresse    = r.adresse_si_domicile || r.adresse || "";
        const dateDebut  = r.date_de_debut_aaaa_mm_jj || r.date_de_debut || r.date_debut || "";
        const dateFin    = r.date_de_fin_aaaa_mm_jj || r.date_de_fin || r.date_fin || "";
        const packBrut   = (r.pack_classique_intensive || r.pack || "classique").toLowerCase();
        const pack       = packBrut === "intensive" ? "intensive" : "classique";
        const profilBrut = (r.profil_adulte_enfant || r.profil || "adulte").toLowerCase();
        const profilType = profilBrut === "enfant" ? "enfant" : "adulte";
        const nomParent  = r.nom_du_parent_si_enfant || r.nom_parent || "";
        const telParent  = r.telephone_du_parent_si_enfant || r.telephone_parent || "";
        const emailParent = r.email_du_parent_si_enfant || r.email_parent || "";
        const objectif   = r.objectif_client || r.objectif || "";
        const notes      = r.notes || "";
        const coachEmailBrut = r.coach_email || r.coach || "";
        const coachTrouve = coachEmailBrut ? coachsParEmail.get(coachEmailBrut.toLowerCase().trim()) : null;
        const disposBrut = r.disponibilites_ex_lun_08_00_10_00_mer_14_00_16_00 || r.disponibilites || r.horaires || "";
        const { horaire, erreurs: erreursHoraires } = parseHorairesImport(disposBrut);

        const erreurs = [];
        const avertissements = [...erreursHoraires];

        // Nombre de versements illimité - colonne "montant@date@référence" séparés par ";".
        const paiementsBrut = r.paiements || r.versements || "";
        const { paiements, erreurs: erreursPaiements } = parsePaiementsImport(paiementsBrut);
        avertissements.push(...erreursPaiements);
        const montant_paye = paiements.length ? paiements.reduce((s, p) => s + p.montant, 0) : null;

        if (!nom) erreurs.push("Nom de l'apprenant manquant");
        if (!coachEmailBrut) erreurs.push("Coach manquant - obligatoire pour du coaching privé");
        else if (!coachTrouve) erreurs.push(`Coach "${coachEmailBrut}" introuvable`);
        if (niveauBrut && !niveau) avertissements.push(`Niveau "${niveauBrut}" non reconnu (A1 à C2) - laissé vide`);
        // Un enfant n'a pas d'email propre (ce sont les coordonnées du parent qui font foi) :
        // l'avertissement ne concerne donc que les profils adultes.
        if (!email && profilType !== "enfant") avertissements.push("Email non renseigné - un identifiant technique sera généré");
        if (profilType === "enfant" && (!nomParent || !telParent)) erreurs.push("Nom et téléphone du parent obligatoires pour un profil enfant");
        // Non bloquant : sans email du parent, l'enfant reçoit un identifiant technique
        // (<nom>@betacademy.app) et la famille ne reçoit jamais ses accès.
        if (profilType === "enfant" && !emailParent) avertissements.push("Email du parent non renseigné - la famille ne recevra pas les accès de l'enfant");
        if (lieuBrut === "domicile" && !adresse) avertissements.push("Adresse non renseignée pour un coaching à domicile");

        // Une ligne n'est ignorée que si TOUTE la ligne Excel est vide - sinon elle doit
        // rester visible (même invalide) pour que l'utilisateur voie ce qui a été lu, au
        // lieu de la faire disparaître silencieusement de l'aperçu (ex: mauvais modèle
        // importé par erreur - colonnes reconnues partiellement, nom/coach absents).
        const ligneVide = Object.values(row).every(v => String(v || "").trim() === "");

        return {
          _idx: idx + 2, nom, prenom, email, telephone, niveau, filiere, lieu, adresse, date_debut: dateDebut, date_fin: dateFin, horaire,
          pack, profil_type: profilType, nom_parent: nomParent, telephone_parent: telParent, email_parent: emailParent,
          paiements, montant_paye: Number.isFinite(montant_paye) ? montant_paye : null, objectif, notes,
          coach_id: coachTrouve?.id || null, coach_nom: coachTrouve ? `${coachTrouve.prenom||""} ${coachTrouve.nom||""}`.trim() : "",
          _erreurs: erreurs, _avertissements: avertissements, _ligneVide: ligneVide,
          _statut: erreurs.length > 0 ? "erreur" : avertissements.length > 0 ? "avertissement" : "ok",
        };
      }).filter(l => !l._ligneVide);

      if (!lignes.length) { toast.error("Aucune ligne exploitable dans le fichier"); return; }
      setImportPrivePreviewRows(lignes);
      setShowImportPriveUpload(false);
      setShowImportPrivePreview(true);
    } catch (err) { toast.error("Erreur lecture fichier : " + err.message); }
  };

  const confirmerImportPrive = async () => {
    const lignesValides = importPrivePreviewRows.filter(l => l._statut !== "erreur");
    if (!lignesValides.length) return;
    if (!assistanteCentreId) {
      toast.error("Centre introuvable pour votre compte - import annulé pour éviter des classes invisibles.");
      return;
    }
    setImportPriveConfirming(true);
    setImportPriveProgress(0);
    arretImportRef.current = false;
    setArretDemande(false);
    setImportProgress({ label: "Import des coachings privés", pct: 0, traites: 0, total: lignesValides.length });

    // Le plan mensuel dérive de la grille tarifaire, qui vit côté dashboard : il est
    // donc calculé ici et voyage avec la ligne, plutôt que d'être recalculé côté serveur.
    const lignes = lignesValides.map((l) => {
      if (!l.date_debut) return l;
      const lieuKey = l.lieu === "domicile" ? "domicile" : "cabinet";
      const mensualite = coachingPriveConfig[l.pack]?.[lieuKey] || 0;
      if (mensualite <= 0) return l;
      return {
        ...l,
        plan_paiement: {
          type: "mensuel", mensualite,
          date_debut_formation: l.date_debut, date_fin_formation: l.date_fin || null,
          mois_paiements: appliquerVersementsAuPlan(
            genererMoisPaiements(l.date_debut, borneEcheancier(l.date_debut, l.date_fin || null, l.paiements), mensualite),
            l.paiements || [],
          ),
          jour_echeance: new Date(l.date_debut).getDate(), actif: true,
        },
      };
    });

    try {
      const { importId, resultats, interrompu } = await lancerImport({
        apiUrl: API_URL,
        token: localStorage.getItem("admin_token"),
        type: "prive_presentiel",
        fichierNom: importFichierNom || "Import coaching privé",
        contexte: {
          source: "import_masse_assistante_commerciale",
          assistante_id: assistanteId || null,
          centre_id: assistanteCentreId || null,
          type_cours: "presentiel",
        },
        lignes,
        doitArreter: () => arretImportRef.current,
        onProgress: (pct, info) => {
          setImportPriveProgress(pct);
          setImportProgress({ label: "Import des coachings privés", pct, traites: info.traites, total: info.total });
        },
      });

      const { ok, erreur, ignore } = compterResultats(resultats);
      setDernierImportId(importId);
      setImportPriveResultats(resultats);
      setShowImportPrivePreview(false);
      setImportPrivePreviewRows([]);
      setShowImportPriveResultat(true);
      if (interrompu) {
        toast("Import interrompu - " + ok + " ligne(s) créée(s) avant l'arrêt. "
          + "Le rapport les liste, et « Annuler cet import » permet de tout défaire.",
          { icon: "⏹️", duration: 7000 });
      }
      if (ok > 0) {
        toast.success(`${ok} coaching(s) privé(s) créé(s)`
          + (erreur ? ` · ${erreur} erreur(s)` : "")
          + (ignore ? ` · ${ignore} déjà présent(s)` : "") + " ✓");
        fetchClasses();
      } else if (ignore > 0) {
        toast(`${ignore} ligne(s) déjà importée(s) - rien à créer`);
      } else {
        toast.error("Aucun coaching privé créé");
      }
    } catch (err) {
      toast.error("Import interrompu : " + err.message);
    } finally {
      setImportPriveConfirming(false);
      setImportProgress(null);
    }
  };

  /* ══════════════════════════════════════════════════════
     IMPORT APPRENANTS (Excel) - dans des classes DÉJÀ EXISTANTES.
     Onglet Assignation, bouton "Importer". Colonne "Classe" optionnelle :
     - renseignée + trouvée  → dossier créé et apprenant inscrit tout de suite.
     - vide                  → dossier créé seul, puis un modal groupé (après
                                l'import) fait choisir UNE classe pour tout ce lot.
     Ni coach ni classe ne sont créés ici : on rejoint une classe existante.
  ══════════════════════════════════════════════════════ */
  const importApprenantsFileRef = useRef(null);
  const [showImportApprenantsUpload,   setShowImportApprenantsUpload]   = useState(false);
  const [importApprenantsPreviewRows,  setImportApprenantsPreviewRows]  = useState([]);
  const [showImportApprenantsPreview,  setShowImportApprenantsPreview]  = useState(false);
  const [importApprenantsConfirming,   setImportApprenantsConfirming]   = useState(false);
  const [importApprenantsResultats,    setImportApprenantsResultats]    = useState([]);
  const [showImportApprenantsResultat, setShowImportApprenantsResultat] = useState(false);
  const [importApprenantsEnAttente,    setImportApprenantsEnAttente]    = useState([]); // lignes sans classe, en attente du choix groupé
  const [showImportApprenantsChoixClasse, setShowImportApprenantsChoixClasse] = useState(false);
  const [importApprenantsClasseChoisieId, setImportApprenantsClasseChoisieId] = useState("");
  const [importApprenantsProgress, setImportApprenantsProgress] = useState(0); // 0-100, pendant confirmerImportApprenants / confirmerChoixClasseGroupe

  /* Un import en masse tourne DANS la page, pas sur le serveur : chaque ligne enchaîne
     plusieurs appels (dossier, versements, classe, apprenant). Fermer l'onglet ou
     recharger tue la boucle en plein milieu - le lot est alors à moitié importé, sans
     aucun signal. Le navigateur demande donc confirmation tant qu'un import tourne
     (les modales, elles, refusent déjà de se fermer - voir leurs onClose). */
  const importEnCours = importClassesConfirming || importPriveConfirming || importApprenantsConfirming || importGlobalProcessing;
  useEffect(() => {
    if (!importEnCours) return;
    const avertirAvantFermeture = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", avertirAvantFermeture);
    return () => window.removeEventListener("beforeunload", avertirAvantFermeture);
  }, [importEnCours]);

  const TAILLE_LOT_IMPORT_APPRENANTS = 10; // lignes traitées en parallèle par lot, pour tenir 1000+ lignes sans tout sérialiser

  const normNomImport = (s) => String(s || "").toLowerCase().trim().replace(/\s+/g, " ");

  const telechargerModeleImportApprenants = () => {
    const headers = [
      "Nom apprenant*", "Prénom apprenant", "Email apprenant", "Téléphone apprenant", "Niveau",
      "Classe (nom exact, laisser vide pour choisir après import)", "Date de début (AAAA-MM-JJ)",
      "Profil (adulte/enfant)", "Nom du parent (si enfant)", "Téléphone du parent (si enfant)", "Email du parent (si enfant)",
      "Paiements", "Notes",
    ];
    const exemple = [
      "Kouassi", "Awa", "awa.kouassi@example.com", "0700000000", "B1", "Anglais B1 Soir Test", "2026-09-01",
      "adulte", "", "", "",
      "80000@2026-09-01@TP-001; 70000@2026-10-01@TP-002",
      "",
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, exemple]);
    ws["!cols"] = headers.map(() => ({ wch: 24 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Import apprenants");
    XLSX.writeFile(wb, "modele_import_apprenants_bet.xlsx");
    toast.success("Modèle téléchargé ✓");
  };

  const lireEtPrevisualiserApprenants = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setImportFichierNom(file.name);
    try {
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf, { type: "array" });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (!rows.length) { toast.error("Fichier vide ou non reconnu"); return; }

      const norm = (s) => String(s || "").toLowerCase().trim()
        .replace(/[éèêë]/g, "e").replace(/[àâä]/g, "a").replace(/[ùûü]/g, "u").replace(/[îï]/g, "i").replace(/[ôö]/g, "o")
        .replace(/[^a-z0-9]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
      const NIVEAUX_VALIDES = ["A1", "A2", "B1", "B2", "C1", "C2"];
      // Classes déjà existantes et encore ouvertes du centre de l'assistante - aucune
      // classe n'est créée par cet import, on ne fait que rejoindre l'existant. On
      // recharge toujours une liste fraîche ici (plutôt que de se fier au `classes` déjà
      // en mémoire) : si l'assistante importe depuis l'onglet Assignation sans être
      // jamais passée par "Mes classes", cet état pouvait être vide et faisait échouer
      // TOUTE résolution de classe, même pour des noms exacts existant bien en base.
      const classesFraiches  = (await fetchClasses()) || classes;
      const classesOuvertes  = classesFraiches.filter(c => c.statut !== "terminé" && c.statut !== "cloture");
      const classesParNom    = new Map(classesOuvertes.map(c => [normNomImport(c.nom), c]));
      const compteParClasse  = new Map(); // classeId -> nb de lignes de CE fichier qui la visent

      const lignes = rows.map((row, idx) => {
        const r = {};
        Object.keys(row).forEach(k => { r[norm(k)] = String(row[k] || "").trim(); });

        const nom        = r.nom_apprenant || r.nom || "";
        const prenom     = r.prenom_apprenant || r.prenom || "";
        const email      = r.email_apprenant || r.email || "";
        const telephone  = r.telephone_apprenant || r.telephone || "";
        const niveauBrut = (r.niveau || "").toUpperCase();
        const niveau     = NIVEAUX_VALIDES.includes(niveauBrut) ? niveauBrut : "";
        const classeBrut = r.classe_nom_exact_laisser_vide_pour_choisir_apres_import || r.classe || r.nom_classe || r.classe_nom || "";
        const classeTrouvee = classeBrut ? classesParNom.get(normNomImport(classeBrut)) : null;
        const dateDebut  = r.date_de_debut_aaaa_mm_jj || r.date_de_debut || r.date_debut || "";
        const profilBrut = (r.profil_adulte_enfant || r.profil || "adulte").toLowerCase();
        const profilType = profilBrut === "enfant" ? "enfant" : "adulte";
        const nomParent   = r.nom_du_parent_si_enfant || r.nom_parent || "";
        const telParent   = r.telephone_du_parent_si_enfant || r.telephone_parent || "";
        const emailParent = r.email_du_parent_si_enfant || r.email_parent || "";
        const notes       = r.notes || "";

        // Nombre de versements illimité par apprenant (relation déjà réelle : ça peut être
        // 1 comme 20 versements déjà encaissés avant la migration) - colonne unique
        // "montant@date@référence" séparés par ";".
        const erreurs = [];
        const avertissements = [];
        const paiementsBrut = r.paiements || r.versements || "";
        const { paiements, erreurs: erreursPaiements } = parsePaiementsImport(paiementsBrut);
        avertissements.push(...erreursPaiements);
        const montant_paye = paiements.length ? paiements.reduce((s, p) => s + p.montant, 0) : null;

        if (!nom) erreurs.push("Nom de l'apprenant manquant");
        if (classeBrut && !classeTrouvee) erreurs.push(`Classe "${classeBrut}" introuvable ou clôturée`);
        if (!classeBrut) avertissements.push("Aucune classe précisée - sera assignée après l'import (choix groupé)");
        // Un apprenant n'est normalement jamais assigné à une classe sans avoir déjà payé -
        // sans versement, aucun plan mensuel ne peut être créé (la mensualité se dérive du
        // montant du 1er versement). Import quand même autorisé, mais signalé.
        if (!paiements.length) avertissements.push("Aucun paiement renseigné - aucun plan mensuel ne sera créé pour cet apprenant");
        if (niveauBrut && !niveau) avertissements.push(`Niveau "${niveauBrut}" non reconnu (A1 à C2) - laissé vide`);
        if (!email) avertissements.push("Email non renseigné - un identifiant technique sera généré");
        if (profilType === "enfant" && (!nomParent || !telParent)) erreurs.push("Nom et téléphone du parent obligatoires pour un profil enfant");
        // Non bloquant : sans email du parent, l'enfant reçoit un identifiant technique
        // (<nom>@betacademy.app) et la famille ne reçoit jamais ses accès.
        if (profilType === "enfant" && !emailParent) avertissements.push("Email du parent non renseigné - la famille ne recevra pas les accès de l'enfant");

        if (classeTrouvee) {
          const n = (compteParClasse.get(classeTrouvee.id) || 0) + 1;
          compteParClasse.set(classeTrouvee.id, n);
          const dejaInscrits = classeTrouvee.nb_apprenants || 0;
          if (classeTrouvee.capacite_max && (dejaInscrits + n) > classeTrouvee.capacite_max) {
            avertissements.push(`Classe "${classeTrouvee.nom}" bientôt/déjà à capacité (${dejaInscrits}/${classeTrouvee.capacite_max}) - inscription quand même, ce sont des places déjà réelles`);
          }
        }

        const ligneVide = Object.values(row).every(v => String(v || "").trim() === "");

        return {
          _idx: idx + 2, nom, prenom, email, telephone, niveau, notes, date_debut: dateDebut,
          classe_saisie: classeBrut, classe_id: classeTrouvee?.id || null, classe_nom: classeTrouvee?.nom || null,
          profil_type: profilType, nom_parent: nomParent, telephone_parent: telParent, email_parent: emailParent,
          paiements, montant_paye: Number.isFinite(montant_paye) ? montant_paye : null,
          _erreurs: erreurs, _avertissements: avertissements, _ligneVide: ligneVide,
          _statut: erreurs.length > 0 ? "erreur" : avertissements.length > 0 ? "avertissement" : "ok",
        };
      }).filter(l => !l._ligneVide);

      if (!lignes.length) { toast.error("Aucune ligne exploitable dans le fichier"); return; }
      setImportApprenantsPreviewRows(lignes);
      setShowImportApprenantsUpload(false);
      setShowImportApprenantsPreview(true);
    } catch (err) { toast.error("Erreur lecture fichier : " + err.message); }
  };

  // Corrige en direct la classe saisie sur une ligne de l'aperçu (sans réuploader le
  // fichier) - re-résout immédiatement contre les classes réelles du centre.
  const corrigerClasseLigneApprenant = (idx, nouvelleValeur) => {
    const classesOuvertes = classes.filter(c => c.statut !== "terminé" && c.statut !== "cloture");
    const classesParNom   = new Map(classesOuvertes.map(c => [normNomImport(c.nom), c]));
    setImportApprenantsPreviewRows(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const classeBrut = nouvelleValeur.trim();
      const classeTrouvee = classeBrut ? classesParNom.get(normNomImport(classeBrut)) : null;
      const erreurs = l._erreurs.filter(e2 => !e2.startsWith("Classe "));
      const avertissements = l._avertissements.filter(w => !w.startsWith("Aucune classe précisée"));
      if (classeBrut && !classeTrouvee) erreurs.push(`Classe "${classeBrut}" introuvable ou clôturée`);
      if (!classeBrut) avertissements.push("Aucune classe précisée - sera assignée après l'import (choix groupé)");
      return {
        ...l, classe_saisie: classeBrut, classe_id: classeTrouvee?.id || null, classe_nom: classeTrouvee?.nom || null,
        _erreurs: erreurs, _avertissements: avertissements,
        _statut: erreurs.length > 0 ? "erreur" : avertissements.length > 0 ? "avertissement" : "ok",
      };
    }));
  };

  const confirmerImportApprenants = async () => {
    const lignesValides = importApprenantsPreviewRows.filter(l => l._statut !== "erreur");
    if (!lignesValides.length) return;
    setImportApprenantsConfirming(true);
    setImportApprenantsProgress(0);
    arretImportRef.current = false;
    setArretDemande(false);
    setImportProgress({ label: "Import des apprenants", pct: 0, traites: 0, total: lignesValides.length });

    // Plan mensuel récurrent, dès qu'une date de début et au moins un versement sont
    // connus. La mensualité vient du PREMIER versement - c'est ce que l'apprenant paie
    // réellement chaque mois - là où le coaching privé la tire de la grille tarifaire.
    // Sans ce plan, l'onglet Paiement de la fiche reste vide malgré des versements bien
    // enregistrés : c'est autour du plan que la fiche affiche l'échéancier.
    const lignes = lignesValides.map((l) => {
      if (!l.date_debut || !l.paiements?.length) return l;
      const mensualite = l.paiements[0].montant;
      if (!(mensualite > 0)) return l;
      return {
        ...l,
        plan_paiement: {
          type: "mensuel", mensualite,
          date_debut_formation: l.date_debut, date_fin_formation: null,
          mois_paiements: appliquerVersementsAuPlan(
            genererMoisPaiements(l.date_debut, borneEcheancier(l.date_debut, null, l.paiements), mensualite),
            l.paiements,
          ),
          jour_echeance: new Date(l.date_debut).getDate(), actif: true,
        },
      };
    });

    try {
      const { importId, resultats, interrompu } = await lancerImport({
        apiUrl: API_URL,
        token: localStorage.getItem("admin_token"),
        type: "apprenant",
        importIdExistant: importGlobalJobId,
        fichierNom: importFichierNom || "Import apprenants",
        contexte: {
          source: "import_masse_apprenants_assistante_commerciale",
          assistante_id: assistanteId || null,
          centre_id: assistanteCentreId || null,
          type_cours: assistanteTypeCours === "en_ligne" ? "en_ligne" : "presentiel",
        },
        lignes,
        doitArreter: () => arretImportRef.current,
        onProgress: (pct, info) => {
          setImportApprenantsProgress(pct);
          setImportProgress({ label: "Import des apprenants", pct, traites: info.traites, total: info.total });
        },
      });

      const { ok, erreur, ignore } = compterResultats(resultats);
      setDernierImportId(importId);
      setImportGlobalJobId(null); // lot consommé - l'import suivant ouvrira le sien
      setImportApprenantsResultats(resultats);
      setShowImportApprenantsPreview(false);
      setImportApprenantsPreviewRows([]);
      setShowImportApprenantsResultat(true);

      // Lignes sans classe : elles restent à affecter en une fois, comme avant.
      const sansClasse = lignesValides.filter(l => !l.classe_id);
      if (sansClasse.length) {
        const parNom = new Map(resultats.map(r => [r.nom, r]));
        setImportApprenantsEnAttente(sansClasse.filter(l => {
          const r = parNom.get([l.prenom, l.nom].filter(Boolean).join(" "));
          return r && r.statut === "ok";
        }).map(l => ({
          nom: [l.prenom, l.nom].filter(Boolean).join(" "), nomSeul: l.nom, prenom: l.prenom,
          email: l.email, telephone: l.telephone, niveau: l.niveau,
          profil_type: l.profil_type, date_debut: l.date_debut,
        })));
      }

      if (interrompu) {
        toast("Import interrompu - " + ok + " ligne(s) créée(s) avant l'arrêt. "
          + "Le rapport les liste, et « Annuler cet import » permet de tout défaire.",
          { icon: "⏹️", duration: 7000 });
      }
      if (ok > 0) {
        toast.success(`${ok} apprenant(s) importé(s)`
          + (erreur ? ` · ${erreur} erreur(s)` : "")
          + (ignore ? ` · ${ignore} déjà présent(s)` : "") + " ✓");
        fetchClasses();
      } else if (ignore > 0) {
        toast(`${ignore} apprenant(s) déjà importé(s) - rien à créer`);
      } else {
        toast.error("Aucun apprenant importé");
      }
    } catch (err) {
      toast.error("Import interrompu : " + err.message);
    } finally {
      setImportApprenantsConfirming(false);
      setImportProgress(null);
    }
  };

  // Applique la classe choisie dans le modal groupé à toutes les lignes mises de côté
  // (celles sans "Classe" renseignée dans le fichier) - un seul choix pour tout le lot.
  const confirmerChoixClasseGroupe = async () => {
    const classeCible = classes.find(c => String(c.id) === String(importApprenantsClasseChoisieId));
    if (!classeCible) { toast.error("Choisissez une classe"); return; }
    setImportApprenantsConfirming(true);
    setImportApprenantsProgress(0);
    const resultatsMaj = [...importApprenantsResultats];
    let traites = 0;
    const total = importApprenantsEnAttente.length;

    const traiterLigne = async (l) => {
      try {
        await fetch(`${API_URL}/api/parcours/assignations/${l.assignationId}`, {
          method: "PATCH", headers: authHeaders(),
          body: JSON.stringify({
            /* On FUSIONNE au lieu de remplacer : le PATCH écrase `suivi_demarrage` en
               entier côté serveur, et une ligne d'import rapprochée d'un prospect web
               existant effaçait sa formule de groupe, son tarif et ses disponibilités. */
            suivi_demarrage: { ...(assignations.find(x => x.id === l.assignationId)?.suivi_demarrage || {}), classe_id: classeCible.id, classe_nom: classeCible.nom, coach_id: classeCible.coach_id || null, coach_nom: classeCible.coach_nom || null, date_assignation: new Date().toISOString().slice(0, 10) },
            routing_statut: "centre_assigne", statut: "converti",
          }),
        });
        const rApp = await fetch(`${API_URL}/api/groupes/${classeCible.id}/apprenants`, {
          method: "POST", headers: authHeaders(),
          body: JSON.stringify({
            nom_apprenant: l.nomSeul, prenom_apprenant: l.prenom || "",
            email_apprenant: l.profil_type === "enfant" ? "" : (l.email || ""),
            telephone: l.profil_type === "enfant" ? "" : (l.telephone || ""),
            niveau: l.niveau || classeCible.niveau || "",
            note: JSON.stringify({ programme: classeCible.filiere || "Formation", date_debut: l.date_debut || classeCible.date_debut || null, assignation_id: l.assignationId }),
          }),
        });
        const idx = resultatsMaj.findIndex(r => r.nom === l.nom && r.detail.startsWith("À assigner après import"));
        if (rApp.ok) {
          if (idx >= 0) resultatsMaj[idx] = { ...resultatsMaj[idx], detail: `Inscrit dans "${classeCible.nom}"` };
        } else {
          const d = await rApp.json().catch(() => ({}));
          if (idx >= 0) resultatsMaj[idx] = { ...resultatsMaj[idx], statut: "erreur", detail: "Apprenant : " + (d.error || rApp.statusText) };
        }
      } catch (err) {
        const idx = resultatsMaj.findIndex(r => r.nom === l.nom && r.detail.startsWith("À assigner après import"));
        if (idx >= 0) resultatsMaj[idx] = { ...resultatsMaj[idx], statut: "erreur", detail: err.message };
      } finally {
        traites++;
        setImportApprenantsProgress(Math.round((traites / total) * 100));
      }
    };

    for (let i = 0; i < importApprenantsEnAttente.length; i += TAILLE_LOT_IMPORT_APPRENANTS) {
      const lot = importApprenantsEnAttente.slice(i, i + TAILLE_LOT_IMPORT_APPRENANTS);
      await Promise.all(lot.map(traiterLigne));
    }

    setImportApprenantsConfirming(false);
    setImportApprenantsProgress(0);
    setImportApprenantsResultats(resultatsMaj);
    setImportApprenantsEnAttente([]);
    setImportApprenantsClasseChoisieId("");
    setShowImportApprenantsChoixClasse(false);
    setShowImportApprenantsResultat(true);
    fetchClasses();
    toast.success(`Lot assigné à "${classeCible.nom}" ✓`);
  };

  // Filtrage coaches - partagé par le modal "classes" et le modal de finalisation
  // Tri : coachs compatibles avec le niveau de la classe en premier
  const filteredCoachs = (() => {
    const q = classeCoachSearch.toLowerCase();
    const niveauClasse = classeForm.niveau || "";
    return coachsList
      .filter(c => {
        /* Le filtre par CATÉGORIE s'applique avant la recherche textuelle : « montre-moi
           les Corporate » est une question de sélection, pas de recherche par nom. Un
           coach jamais évalué n'appartient à aucune catégorie - il a la sienne, pour
           qu'on puisse justement le repérer plutôt que de le confondre avec un mauvais. */
        if (classeCoachCategorie) {
          const cle = c.moyenne != null ? categorieCoach(Number(c.moyenne)).cle : CATEGORIE_SANS_NOTE.cle;
          if (cle !== classeCoachCategorie) return false;
        }
        if (!q) return true;
        const name = [c.prenom, c.nom].filter(Boolean).join(" ").toLowerCase();
        return name.includes(q) || (c.grade||"").toLowerCase().includes(q) ||
          (c.specialites||[]).join(" ").toLowerCase().includes(q) ||
          (c.niveaux_enseignes||[]).join(" ").toLowerCase().includes(q);
      })
      .sort((a, b) => {
        if (!niveauClasse) return 0;
        const aMatch = (a.niveaux_enseignes||[]).includes(niveauClasse);
        const bMatch = (b.niveaux_enseignes||[]).includes(niveauClasse);
        if (aMatch && !bMatch) return -1;
        if (!aMatch && bMatch) return 1;
        return 0;
      });
  })();

  const exportClassesExcel = () => {
    exporterExcelStyle({
      nomFichier: `classes_${new Date().toISOString().slice(0,10)}`,
      titre: '🏫 Classes',
      sousTitre: `${classes.length} classe${classes.length>1?'s':''} · Généré le ${new Date().toLocaleDateString('fr-FR')}`,
      couleurEntete: '6366F1',
      colonnes: [
        { label: 'Classe', width: 28, bold: true }, { label: 'Filière', width: 20 },
        { label: 'Niveau', width: 8, align: 'center' }, { label: 'Type de cours', width: 14 },
        { label: 'Coach', width: 24 }, { label: 'Apprenants', width: 12, align: 'center' },
        { label: 'Capacité max', width: 14, align: 'center' }, { label: 'Taux remplissage', width: 16, align: 'center' },
        { label: 'Date début', width: 14 }, { label: 'Date fin', width: 14 },
        { label: 'Statut', width: 12 }, { label: 'Centre', width: 20 },
      ],
      lignes: classes.map(cl => [
        cl.nom || "",
        cl.filiere || "",
        cl.niveau || "",
        libelleTypeCours(cl),
        cl.coach_nom || "Non assigné",
        cl.nb_apprenants || 0,
        cl.capacite_max || 20,
        cl.capacite_max ? `${Math.round(((cl.nb_apprenants||0)/cl.capacite_max)*100)}%` : "-",
        cl.date_debut ? new Date(cl.date_debut).toLocaleDateString("fr-FR") : "",
        cl.date_fin ? new Date(cl.date_fin).toLocaleDateString("fr-FR") : "",
        cl.statut || "",
        cl.centre_nom || assistanteCentreNom || "",
      ]),
    });
    toast.success("Export Excel téléchargé ✓");
  };

  // ── Détail classe (modal à onglets) ────────────────────────
  const [selectedClasse,      setSelectedClasse]      = useState(null);
  const [classeDetailTab,     setClasseDetailTab]     = useState("infos");
  // Mouvements d'apprenants de la classe (arrivées / départs par transfert).
  const [classeHistorique,        setClasseHistorique]        = useState([]);
  const [classeHistoriqueLoading, setClasseHistoriqueLoading] = useState(false);
  const [classeDetailData,    setClasseDetailData]    = useState(null);
  const [classeDetailLoading, setClasseDetailLoading] = useState(false);
  const [planActionLoading, setPlanActionLoading] = useState(false);
  // Le rattrapage d'un tarif non appliqué aux mois déjà écrits - voir ModalAlignementTarif.
  const [alignTarif, setAlignTarif] = useState(null);
  const [classePresences,     setClassePresences]     = useState(null);
  const [classeSessions,        setClasseSessions]        = useState({ live: [], cours: [] });
  const [classeSessionsLoading, setClasseSessionsLoading] = useState(false);
  const [sessFiltrePeriode,     setSessFiltrePeriode]     = useState("tout");
  const [sessFiltreDateDebut,   setSessFiltreDateDebut]   = useState("");
  const [sessFiltreDateFin,     setSessFiltreDateFin]     = useState("");
  const [sessFiltreType,        setSessFiltreType]        = useState("all");
  const [sessStatutFil,         setSessStatutFil]         = useState("all");
  const [sessPage,              setSessPage]              = useState(0);
  const SESS_PAR_PAGE = 10;
  const [presDateDebut,         setPresDateDebut]         = useState("");
  const [presDateFin,           setPresDateFin]           = useState("");
  const [presPage,              setPresPage]              = useState(0);
  const PRES_PAR_PAGE = 10;
  const fetchClasseSessions = async (classeId) => {
    setClasseSessionsLoading(true);
    try {
      const [rLive, rCours] = await Promise.all([
        fetch(`${API_URL}/api/coachs/sessions-live/upcoming?groupe_id=${classeId}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/groupes/${classeId}/cours`, { headers: authHeaders() }),
      ]);
      const live  = rLive.ok  ? (await rLive.json()).sessions  || [] : [];
      const cours = rCours.ok ? (await rCours.json()).cours    || [] : [];
      setClasseSessions({ live, cours });
    } catch { setClasseSessions({ live: [], cours: [] }); }
    finally { setClasseSessionsLoading(false); }
  };

  const openClasseDetail = async (cl) => {
    setSelectedClasse(cl);
    setClasseDetailTab("infos");
    setClasseHistorique([]);
    setClasseDetailData(null);
    setClassePresences(null);
    setClasseSessions({ live: [], cours: [] });
    setSessFiltrePeriode("tout");
    setSessFiltreDateDebut("");
    setSessFiltreDateFin("");
    setSessFiltreType("all");
    setSessStatutFil("all");
    setSessPage(0);
    setPresDateDebut("");
    setPresDateFin("");
    setPresPage(0);
    setClasseDetailLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API_URL}/api/groupes/${cl.id}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/groupes/${cl.id}/presences`, { headers: authHeaders() }),
      ]);
      if (r1.ok) { const d = await r1.json(); setClasseDetailData(d); }
      if (r2.ok) { const d = await r2.json(); setClassePresences(d); }
    } catch { toast.error("Erreur chargement détail classe"); }
    finally { setClasseDetailLoading(false); }
    // Charger les sessions en arrière-plan
    fetchClasseSessions(cl.id);
  };

  /* removeApprenant supprimée avec son bouton : retirer un apprenant effaçait sa ligne
     et, avec elle, tout l'historique de son parcours. Le retrait passe désormais par
     « Changer de groupe », qui conserve la trace du mouvement. */

  const offreParNiveau = (level) => {
    const map = { A1:"Cours débutant / Alphabétisation", A2:"Anglais Essentiel A2", B1:"Anglais Pro B1", B2:"Anglais Pro B2", C1:"Business English", C2:"Anglais Expert C2" };
    return map[level] || "Formation Personnalisée";
  };

  const mapTest = (r, i) => {
    const parts = (r.fullname || "").trim().split(" ");
    const prenom = parts[0] || "";
    const nom    = parts.slice(1).join(" ") || "";
    const scorePct = r.points_total > 0
      ? Math.round((r.points_earned / r.points_total) * 100)
      : (r.score || 0);
    return {
      id:               r.id || i + 1,
      assignation_id:   r.assignation_id || null,
      nom:              nom || prenom,
      prenom:           nom ? prenom : "",
      telephone:        r.phone || "",
      email:            r.email || "",
      profil:           r.profile || "Particulier",
      niveau:           r.level || "-",
      score:            scorePct,
      points_earned:    r.points_earned || 0,
      points_total:     r.points_total  || 0,
      correct_answers:  r.correct_answers,
      total_questions:  r.total_questions,
      time_taken_seconds: r.time_taken_seconds || 0,
      date:             r.submitted_at ? r.submitted_at.split("T")[0] : "",
      commercial_id:    r.commercial_id || null,
      source:           r.source            || "online",
      format_test:      r.format_test       || "mixte",
      correction_statut: r.correction_statut || "auto",
      notes:            r.notes_oral        || "",
      answers_text:     r.answers_details   || [],
      statut:           r.statut || "nouveau",
      offreRecommandee: offreParNiveau(r.level),
      by_category:      r.by_category   || {},
      by_cefr:          r.by_cefr       || {},
      answers_details:  r.answers_details || [],
      audio_answers:    r.audio_answers  || {},
      speaking_notes:   r.speaking_notes || {},
    };
  };

  const fetchTests = async () => {
    setTestsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/level-test/all`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const { results } = await res.json();
      if (Array.isArray(results)) {
        const all = results.map(mapTest);
        // Mes clients = ceux qui m'ont choisi
        const miens = myCommercialId
          ? all.filter(t => t.commercial_id === myCommercialId)
          : all;
        // Non assignés (peut-être à revendiquer)
        const nonAssignes = all.filter(t => !t.commercial_id);
        setTests(miens);
        setTousTests(all);
        setTestsNonAssignes(nonAssignes);
      }
    } catch (e) {
      console.error("Chargement tests:", e);
    } finally {
      setTestsLoading(false);
    }
  };

  useEffect(() => { fetchTests(); }, []); // eslint-disable-line

  // ── Inscriptions depuis Supabase ──────────────────────
  const fetchInscriptions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/inscriptions/all`, { headers: authHeaders() });
      if (!res.ok) return; // silencieux - l'état local reste intact
      const { adultes = [], enfants = [] } = await res.json();
      const mapped = [
        ...adultes.map(r => ({
          id: r.id, type: "adulte", client: r.nom_complet||"", contact: r.nom_complet||"",
          email: r.email||"", telephone: r.telephone||"", offre: r.offre_titre||"",
          niveauTest: r.niveau_detecte||"", montant: 0,
          dateDebut: r.date_debut_formation||"", date: r.created_at?.slice(0,10)||"",
          statut: r.statut||"confirmée", apprenantConverti: false, documents: [],
        })),
        ...enfants.map(r => ({
          id: r.id, type: "enfant",
          client: `${r.prenom_enfant||""} ${r.nom_enfant||""}`.trim(),
          contact: r.nom_parent||"", email: r.email_parent||"", telephone: r.telephone_parent||"",
          offre: r.tranche_age ? `Enfants ${r.tranche_age}` : "Formation Enfant",
          niveauTest: "", montant: 0, dateDebut: "", date: r.created_at?.slice(0,10)||"",
          statut: r.statut||"confirmée", apprenantConverti: false, documents: [],
        })),
      ];
      mapped.sort((a,b) => new Date(b.date||0) - new Date(a.date||0));
      if (mapped.length > 0) setInscriptions(mapped);
    } catch { /* silencieux */ }
  };
  useEffect(() => { fetchInscriptions(); }, []); // eslint-disable-line

  const fetchMessages = async () => {
    setMessagesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/contact/mes-clients`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const { contacts } = await res.json();
      setClientMessages(contacts || []);
    } catch (e) {
      console.error("Chargement messages:", e);
    } finally {
      setMessagesLoading(false);
    }
  };

  useEffect(() => { fetchMessages(); }, []);

  // ── Paiements (DB) ───────────────────────────────────
  const [paiements, setPaiements]           = useState([]);

  // ── Planning des séances (présentiel + en ligne) ────────────────────────────
  // Permet de suivre le travail réel des coachs : horaire, présences constatées, et
  // pour les cours en ligne le lien Zoom/Teams, comme en disposent les superviseurs.
  // Données, filtres et exports du planning - partagés avec l'onboarding.
  const planning = usePlanningSeances(authHeaders);
  const [, setPaiementsLoading] = useState(false);

  const dbToLocal = (p) => {
    // Décoder offre::XXX||reste depuis inscription
    const raw = p.inscription || "";
    let offre = "", inscription = raw;
    if (raw.startsWith("offre::")) {
      const parts = raw.split("||");
      offre = parts[0].replace("offre::", "");
      inscription = parts.slice(1).join("||");
    }
    return {
      id:             p.id,
      client:         p.client         || "",
      email:          p.email          || "",
      telephone:      p.telephone      || "",
      offre,
      inscription,
      montantDu:      p.montant_du     || 0,
      montantReçu:    p.montant_recu   || 0,
      date:           p.date_paiement  || p.date || "",
      mode:           p.mode_paiement  || p.mode || "Mobile Money",
      statut:         p.statut         || "en_attente",
      notes:          p.notes          || "",
      refTransaction: p.ref_transaction|| "",
      assignationId:  p.assignation_id || null,
      preuveImage:    p.preuve_image   || null,
      paymentToken:   p.payment_token  || null,
      operateur:      p.payment_mode_reel || null,
      niveau:         p.niveau         || null,
    };
  };

  const fetchPaiements = async () => {
    setPaiementsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/paiements/mes-paiements`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const { paiements: data } = await res.json();
      setPaiements((data || []).map(dbToLocal));
    } catch (e) {
      console.error("Chargement paiements:", e);
    } finally {
      setPaiementsLoading(false);
    }
  };

  useEffect(() => { fetchPaiements(); }, []);

  // ── Paiements TouchPoint (toutes tentatives, tous statuts) - distinct de
  // `paiements` (paiements_parcours) qui ne reçoit une ligne QUE quand le paiement
  // aboutit ; les tentatives en_attente/échouées n'existent que dans
  // paiements_touchpoint, scopées à l'assistante côté backend.
  const [touchpointPaiements,        setTouchpointPaiements]        = useState([]);
  const [touchpointPaiementsLoading, setTouchpointPaiementsLoading] = useState(false);
  const [tpShowArchives,             setTpShowArchives]             = useState(false); // vue "paiements archivés"
  const fetchTouchpointPaiements = async (archives = tpShowArchives) => {
    setTouchpointPaiementsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/touchpoint/admin/all?archives=${archives ? "true" : "false"}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const { paiements: data } = await res.json();
      setTouchpointPaiements(data || []);
    } catch (e) {
      console.error("Chargement paiements TouchPoint:", e);
    } finally {
      setTouchpointPaiementsLoading(false);
    }
  };
  useEffect(() => { fetchTouchpointPaiements(false); }, []);
  const [tpSortKey,       setTpSortKey]       = useState("date"); // "client" | "montant" | "date" | "statut"
  const [tpSortDir,       setTpSortDir]       = useState("desc"); // "asc" | "desc"
  const [tpExpandedGroups,setTpExpandedGroups]= useState(() => new Set());
  const archiverTouchPoint = async (ids, archiver = true) => {
    try {
      const res = await fetch(`${API_URL}/api/touchpoint/admin/archiver`, {
        method: "PATCH", headers: { "Content-Type":"application/json", ...authHeaders() },
        body: JSON.stringify({ ids, archiver }),
      });
      if (!res.ok) throw new Error();
      toast.success(archiver ? `${ids.length} paiement${ids.length>1?"s":""} archivé${ids.length>1?"s":""}` : "Paiement restauré");
      fetchTouchpointPaiements();
    } catch (e) {
      toast.error("Échec de l'opération");
    }
  };

  // ── Catalogue offres (en ligne + centres + certifs) ──
  const [catalogueOffres, setCatalogueOffres] = useState([]);
  useEffect(() => {
    fetch(`${API_URL}/api/admin/catalogue-offres`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : { offres: [] })
      .then(d => { if (d.offres?.length) setCatalogueOffres(d.offres); })
      .catch(() => {});
  }, []);

  // ── Sondages acquisition (DB) ────────────────────────
  const [sondages, setSondages] = useState([]);
  const [sondageStats, setSondageStats] = useState({});

  // ── Assignations parcours ────────────────────────────────
  const [assignations,        setAssignations]        = useState([]);
  const [assignationsLoading, setAssignationsLoading] = useState(false);
  const [assignFiltreType,    setAssignFiltreType]    = useState("tous");
  const [assignFiltreStatut,  setAssignFiltreStatut]  = useState("tous");
  const [chatAssignation,     setChatAssignation]     = useState(null); // assignation ouverte en chat
  const [apprenantModal,      setApprenantModal]      = useState(null); // assignation ouverte dans la modale apprenant
  const [showArchiveApprenants, setShowArchiveApprenants] = useState(false); // archive : qui j'ai eu, et qui est parti

  // ── Coaching privé : "Besoin du client" - l'assistante reçoit le dossier en
  // premier désormais (workflow "Cours Privé"), elle doit donc pouvoir le
  // renseigner elle-même au lieu de dépendre du PA (qui passe en lecture seule).
  // Grille de prix lue en direct depuis plateforme_config.coaching_prive_config - la même
  // source que CourseDetail/ParcoursModal (site public), pour ne jamais afficher un prix
  // différent de celui réellement proposé au prospect (ex: prix de test à 10 FCFA).
  const [coachingPriveConfig, setCoachingPriveConfig] = useState(GRILLE_PRIVE_COM_DEFAULT);
  useEffect(() => {
    fetch(`${API_URL}/api/admin/plateforme-config/coaching_prive_config`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.valeur && typeof d.valeur === "object") {
          const merged = { ...GRILLE_PRIVE_COM_DEFAULT };
          Object.keys(d.valeur).forEach(k => { merged[k] = { ...(GRILLE_PRIVE_COM_DEFAULT[k]||{}), ...d.valeur[k] }; });
          setCoachingPriveConfig(merged);
        }
      })
      .catch(() => {});
  }, []);
  /* Grille des FORMULES DE GROUPE - même source que le site public (`groupe_config`).
     Le coaching privé avait sa grille ici ; le groupe n'en avait aucune, si bien qu'une
     inscription en groupe se saisissait à un montant libre, sans rapport avec les tarifs
     annoncés au prospect (3×/semaine, 2×/semaine, week-end). */
  const GRILLE_GROUPE_COM_DEFAULT = {
    "3x_semaine": { label:"3×/semaine", frequence:"Lun/Mer/Ven - séances de 2h", en_ligne:50000, cabinet:80000 },
    "2x_semaine": { label:"2×/semaine", frequence:"Mar/Jeu - séances de 2h",     en_ligne:40000, cabinet:50000 },
    "weekend":    { label:"Week-end",   frequence:"Sam/Dim - séances de 2h",     en_ligne:40000, cabinet:50000 },
  };
  const [groupeConfig, setGroupeConfig] = useState(GRILLE_GROUPE_COM_DEFAULT);
  useEffect(() => {
    fetch(`${API_URL}/api/admin/plateforme-config/groupe_config`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.valeur && typeof d.valeur === "object") {
          const merged = { ...GRILLE_GROUPE_COM_DEFAULT };
          Object.keys(d.valeur).forEach(k => { merged[k] = { ...(GRILLE_GROUPE_COM_DEFAULT[k]||{}), ...d.valeur[k] }; });
          setGroupeConfig(merged);
        }
      })
      .catch(() => {});
  }, []);

  const [dossierPriveForm,    setDossierPriveForm]    = useState({ niveau:"", duree_mois:"", formation:"", type_coaching:"classique", lieu:"cabinet", cout_formation:String(GRILLE_PRIVE_COM_DEFAULT.classique.cabinet), adresse_coaching:"", centre_coaching:"", disponibilites:{ horaires:[] }, date_debut:"" });
  const [savingDossierPrive,  setSavingDossierPrive]  = useState(false);
  const [verifyingTestNiveau, setVerifyingTestNiveau]  = useState(false);

  useEffect(() => {
    if (!apprenantModal?.id) return;
    const sd = apprenantModal.suivi_demarrage || {};
    const tcDefaut   = sd.type_coaching || "classique";
    const lieuDefaut = sd.lieu_coaching || "cabinet";
    const lieuKeyDefaut = lieuDefaut === "domicile" ? "domicile" : lieuDefaut === "en_ligne" ? "en_ligne" : "cabinet";
    setDossierPriveForm({
      niveau:           sd.niveau_prospect || apprenantModal.niveau_client || "",
      duree_mois:        sd.nb_seances_souhaitees || "",
      formation:         sd.formation_souhaitee || "",
      type_coaching:     tcDefaut,
      lieu:              lieuDefaut,
      cout_formation:    sd.cout_formation || String(coachingPriveConfig[tcDefaut]?.[lieuKeyDefaut] || ""),
      adresse_coaching:  sd.adresse_coaching || "",
      centre_coaching:   sd.centre_coaching || "",
      disponibilites:    sd.disponibilites || { horaires:[] },
      date_debut:        sd.date_debut_formation || apprenantModal.date_debut_formation || "",
    });
    // Coaching privé : si le niveau n'est pas déjà renseigné, on va chercher directement
    // le résultat du test de niveau passé par le prospect - l'assistante n'a pas à
    // ressaisir un niveau que le test a déjà déterminé.
    // Pour un profil enfant, prospect_email est celui du PARENT (partagé) - on cible donc
    // par assignation_id plutôt que par email seul, pour ne jamais récupérer le test du
    // parent à la place de celui de l'enfant.
    if (!sd.niveau_prospect && !apprenantModal.niveau_client && apprenantModal.type_coaching === "prive" && apprenantModal.prospect_email) {
      const qs = apprenantModal.profil_type === "enfant" && apprenantModal.id
        ? `assignation_id=${encodeURIComponent(apprenantModal.id)}&email=${encodeURIComponent(apprenantModal.prospect_email)}`
        : `email=${encodeURIComponent(apprenantModal.prospect_email)}`;
      fetch(`${API_URL}/api/level-test/result?${qs}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.result?.level) setDossierPriveForm(prev => ({ ...prev, niveau: d.result.level })); })
        .catch(() => {});
    }
  }, [apprenantModal?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const verifierTestNiveauCommercial = async () => {
    if (!apprenantModal?.prospect_email || verifyingTestNiveau) return;
    setVerifyingTestNiveau(true);
    try {
      // Pour un profil enfant, l'email est celui du PARENT (partagé) - on cible le test par
      // assignation_id (jamais par email seul) pour ne jamais récupérer celui du parent à la
      // place de celui de l'enfant. GET /result sait faire cette distinction depuis peu.
      const qs = apprenantModal.profil_type === "enfant" && apprenantModal.id
        ? `assignation_id=${encodeURIComponent(apprenantModal.id)}&email=${encodeURIComponent(apprenantModal.prospect_email)}`
        : `email=${encodeURIComponent(apprenantModal.prospect_email)}`;
      const r = await fetch(`${API_URL}/api/level-test/result?${qs}`);
      const d = await r.json();
      if (d?.result?.level) {
        setDossierPriveForm(prev => ({ ...prev, niveau: d.result.level }));
        toast.success(`✅ Niveau ${d.result.level} récupéré depuis le test passé`);
      } else {
        toast("⏳ Ce prospect n'a pas encore passé le test de niveau.");
      }
    } catch { toast.error("Erreur lors de la vérification"); }
    finally { setVerifyingTestNiveau(false); }
  };

  // Persiste dossierPriveForm en base - utilisé aussi bien par le bouton "Mettre à
  // jour le dossier" que par "Transmettre au PA" (qui doit sauvegarder AVANT de
  // transmettre, sans quoi le contrôle de complétude côté backend lit encore l'ancien
  // suivi_demarrage et rejette un dossier qui semble pourtant complet à l'écran).
  const persisterDossierPriveCommercial = async () => {
    if (!apprenantModal?.id) return false;
    if (!dossierPriveForm.niveau || !dossierPriveForm.formation) { toast.error("Remplissez au moins le niveau et la formation"); return false; }
    const existing = apprenantModal.suivi_demarrage || {};
    const typeCoachingSave = dossierPriveForm.type_coaching || "classique";
    const lieuSave         = dossierPriveForm.lieu          || "cabinet";
    // cout_formation n'était mis à jour QUE par les onChange des dropdowns Type/Lieu - si
    // l'assistante ne les touche jamais (déjà pré-remplis par défaut), il restait vide même
    // après enregistrement. On le recalcule systématiquement depuis la grille tarifaire à
    // la sauvegarde, avec la valeur déjà saisie comme repli si elle diffère (ex: tarif négocié).
    const coutCalcule = coachingPriveConfig[typeCoachingSave]?.[lieuSave === "domicile" ? "domicile" : lieuSave === "en_ligne" ? "en_ligne" : "cabinet"] || null;
    const sd = {
      ...existing,
      niveau_prospect:       dossierPriveForm.niveau,
      nb_seances_souhaitees: dossierPriveForm.duree_mois ? Number(dossierPriveForm.duree_mois) : (existing.nb_seances_souhaitees || null),
      formation_souhaitee:   dossierPriveForm.formation,
      type_coaching:         typeCoachingSave,
      lieu_coaching:         lieuSave,
      cout_formation:        (dossierPriveForm.cout_formation ? Number(dossierPriveForm.cout_formation) : null) || coutCalcule,
      adresse_coaching:      dossierPriveForm.lieu === "domicile" ? (dossierPriveForm.adresse_coaching || "") : null,
      centre_coaching:       dossierPriveForm.lieu === "cabinet"  ? (dossierPriveForm.centre_coaching  || null) : null,
      disponibilites:        dossierPriveForm.disponibilites || existing.disponibilites || { horaires:[] },
      date_debut_formation:  dossierPriveForm.date_debut || existing.date_debut_formation || null,
    };
    const r = await fetch(`${API_URL}/api/parcours/assignations/${apprenantModal.id}`, {
      method: "PATCH", headers: authHeaders(),
      body: JSON.stringify({ suivi_demarrage: sd, date_debut_formation: dossierPriveForm.date_debut || null }),
    });
    if (!r.ok) throw new Error((await r.json()).error || "Erreur");
    setApprenantModal(prev => prev?.id === apprenantModal.id ? { ...prev, suivi_demarrage: sd } : prev);
    setAssignations(prev => prev.map(a => a.id === apprenantModal.id ? { ...a, suivi_demarrage: sd } : a));
    return true;
  };

  const saveDossierPriveCommercial = async () => {
    if (!apprenantModal?.id || savingDossierPrive) return;
    setSavingDossierPrive(true);
    try {
      const ok = await persisterDossierPriveCommercial();
      if (ok) toast.success("Dossier mis à jour ✓");
    } catch (e) { toast.error(e.message || "Erreur lors de la sauvegarde"); }
    finally { setSavingDossierPrive(false); }
  };

  const [transmettantPA, setTransmettantPA] = useState(false);
  const [confirmTransmettrePA, setConfirmTransmettrePA] = useState(false);
  const transmettrePADossierPrive = async () => {
    if (!apprenantModal?.id || transmettantPA) return;
    setTransmettantPA(true);
    try {
      const saved = await persisterDossierPriveCommercial();
      if (!saved) return;
      const r = await fetch(`${API_URL}/api/parcours/assignations/${apprenantModal.id}/transmettre-pa`, {
        method: "PATCH", headers: authHeaders(),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Erreur");
      toast.success(`✅ Dossier de ${apprenantModal.prospect_nom} transmis au PA pour validation.`);
      setApprenantModal(prev => prev?.id === apprenantModal.id ? { ...prev, dossier_statut:"en_attente_validation", motif_invalidation:null } : prev);
      setAssignations(prev => prev.map(a => a.id === apprenantModal.id ? { ...a, dossier_statut:"en_attente_validation", motif_invalidation:null } : a));
    } catch (e) { toast.error(e.message || "Erreur lors de la transmission"); }
    finally { setTransmettantPA(false); setConfirmTransmettrePA(false); }
  };

  const [apprenantTab,        setApprenantTab]        = useState("infos");
  const [commAccesInfo,       setCommAccesInfo]       = useState(null);
  const [commAccesLoad,       setCommAccesLoad]       = useState(false);
  const [commClasseInfo,      setCommClasseInfo]      = useState(null);
  const [commClasseLoad,      setCommClasseLoad]      = useState(false);

  // ── Transfert de groupe (assistante présentiel - joue son propre rôle d'onboarding) ──
  // Le choix de la classe cible réutilise directement AssignationCentreModal (même
  // composant, même UX que l'affectation initiale d'un apprenant à une classe) plutôt
  // qu'une liste maison - cohérence demandée avec le flux d'assignation existant.
  const [showTransfert,       setShowTransfert]       = useState(false);
  const [transfertForm,       setTransfertForm]       = useState({ ga_id:null, niveau:"", source_groupe_id:null, nom_apprenant:"" });
  const [transfertSaving,     setTransfertSaving]     = useState(false);

  // Étape "Nouveaux créneaux / motif" retirée pour le moment - le clic sur "Changer de
  // groupe" ouvre directement AssignationCentreModal pour choisir la classe cible.
  const ouvrirTransfert = ({ ga_id, niveau, groupe_id, nom_apprenant }) => {
    setTransfertForm({ ga_id, niveau, source_groupe_id: groupe_id, nom_apprenant: nom_apprenant||"" });
    setShowTransfert(true);
  };

  /* LA RÉAFFECTATION PASSE PAR NOTRE ÉCRAN, pas par le sélecteur minimal du composant partagé.
     L'assistante présentiel a déjà le sien - recherche de classes compatibles, création à la
     volée - et lui en imposer un autre pour le même geste serait un recul.
     `demandeEnCours` retient le dossier : c'est lui qu'on refermera une fois le transfert fait. */
  const [demandeEnCours, setDemandeEnCours] = useState(null);
  const [refreshReaffecter, setRefreshReaffecter] = useState(0);

  /* Le plan de paiement du dossier, pour MONTRER la mensualité actuelle avant d'en saisir une
     autre : décider d'un tarif sans voir celui en cours, c'est décider à l'aveugle. */
  const [planMigration, setPlanMigration] = useState(null);
  const [nouvelleMensualite, setNouvelleMensualite] = useState("");
  /* Appliquer le nouveau tarif dès le mois EN COURS, et pas seulement aux mois créés ensuite.
     Décidé dossier par dossier : un transfert en début de mois et un transfert en fin de mois
     n'appellent pas la même réponse, et seule celle qui traite le dossier le sait. */
  const [tarifDesLeMoisEnCours, setTarifDesLeMoisEnCours] = useState(false);

  /* LE SERVEUR RÉSOUT LE DOSSIER, pas l'écran. Le rapprochement n'a rien d'évident :
     l'e-mail de l'inscription est parfois un identifiant technique fabriqué à partir du
     téléphone, et ne correspond alors à aucun dossier. Le refaire ici aurait donné deux
     résolutions pour la même question, dont une seule juste - et c'est l'écran qui aurait
     affiché « aucun plan mensuel » sur un dossier qui en a un. */
  const chargerPlanDeLApprenant = async (demandeId) => {
    setPlanMigration(null);
    if (!demandeId) { setPlanMigration({}); return; }
    try {
      const r = await fetch(`${API_URL}/api/demandes-transfert/${demandeId}/dossier`, { headers: authHeaders() });
      const j = await r.json();
      setPlanMigration(j.plan || {});
    } catch { setPlanMigration({}); }
  };

  const ouvrirTransfertDepuisDemande = (d) => {
    setDemandeEnCours(d);
    setNouvelleMensualite(""); setTarifDesLeMoisEnCours(false);
    /* Le plan de paiement pour les deux circuits SANS TEST : la migration de canal et le
       transfert de centre ajustent tous deux le tarif, et sans ce chargement le champ
       s'ouvrirait vide sur « Aucun plan mensuel » pour un dossier qui en a un. */
    /* Le plan pour TOUS les types : les trois circuits peuvent ajuster le tarif. Le charger
       pour deux seulement laissait le troisième devant « Aucun plan mensuel » sur un dossier
       qui en a un. */
    chargerPlanDeLApprenant(d.id);
    ouvrirTransfert({
      ga_id: d.ga_id,
      // Le niveau rendu par le test d'abord : c'est lui qui désigne la classe d'arrivée.
      niveau: d.niveau_obtenu || d.niveau_actuel,
      groupe_id: d.groupe_id,
      nom_apprenant: [d.prenom_apprenant, d.nom_apprenant].filter(Boolean).join(" "),
    });
  };

  // Point d'entrée unique pour le bouton "Changer de groupe" depuis la fiche apprenant
  // (apprenantModal) - quel que soit le mode d'inscription de l'apprenant (classe, dossier
  // BET direct…), on doit pouvoir le transférer. commClasseInfo.ga_id peut ne pas encore être
  // résolu (fetch asynchrone parti en arrière-plan à l'ouverture de la fiche) : dans ce cas on
  // relance la même résolution ici, à la demande, plutôt que de faire dépendre la visibilité du
  // bouton de ce fetch. Coaching privé exclu pour le moment.
  const ouvrirTransfertDepuisFiche = async (a) => {
    if (a.type_coaching === "prive") {
      toast.error("Le coaching privé n'est pas concerné par le transfert de classe pour le moment");
      return;
    }
    const nomApp = a.prospect_nom || [a.prenom_apprenant, a.nom_apprenant].filter(Boolean).join(" ") || "";
    if (commClasseInfo?.ga_id) {
      ouvrirTransfert({ ga_id: commClasseInfo.ga_id, niveau: commClasseInfo.niveau, groupe_id: commClasseInfo.id, nom_apprenant: nomApp });
      return;
    }
    const email = a.prospect_email || a.email_apprenant || "";
    const _q = [
      email  ? `email=${encodeURIComponent(email)}` : null,
      a.id   ? `assignation_id=${encodeURIComponent(a.id)}` : null,
    ].filter(Boolean).join("&");
    if (!_q) { toast.error("Impossible d'identifier cet apprenant"); return; }
    const tId = toast.loading("Recherche de la classe…");
    try {
      const r = await fetch(`${API_URL}/api/groupes/apprenant-by-email?${_q}`, { headers: authHeaders() });
      const d = r.ok ? await r.json() : { groupe: null };
      toast.dismiss(tId);
      if (!d.groupe?.ga_id) { toast.error("Aucune classe active trouvée pour cet apprenant"); return; }
      setCommClasseInfo(d.groupe);
      ouvrirTransfert({ ga_id: d.groupe.ga_id, niveau: d.groupe.niveau, groupe_id: d.groupe.id, nom_apprenant: nomApp });
    } catch { toast.dismiss(tId); toast.error("Erreur de recherche de la classe"); }
  };

  // Appelé en tant que onSave d'AssignationCentreModal - { classeId } est la classe choisie
  // (ou fraîchement créée) dans ce même composant que celui de l'affectation initiale.
  const finaliserTransfert = async ({ classeId, cloturerSource }) => {
    if (transfertSaving) return;
    if (!transfertForm.ga_id) { toast.error("Impossible d'identifier l'apprenant à transférer"); return; }
    if (!classeId) { toast.error("Sélectionnez un groupe cible"); return; }
    setTransfertSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/groupes/transfert`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          ga_id:             transfertForm.ga_id,
          nouveau_groupe_id: classeId,
          motif:             "Changement de classe",
          initiateur:        "assistant",
          /* Sans lui, la route refuse de sortir l'apprenant d'une classe en ligne : c'est la
             demande acceptée qui autorise ce mouvement, et le serveur la relit. */
          ...(demandeEnCours?.type_demande === "changement_coaching"
              || demandeEnCours?.type_demande === "changement_centre"
              ? { demande_id: demandeEnCours.id } : {}),
          ...(cloturerSource ? { cloturer_groupe_source: true } : {}),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Erreur transfert");
      toast.success(d.message || "Transfert effectué ✓");

      /* La clôture est annoncée à PART du transfert, et son refus aussi : elle a ses propres
         conséquences (plans gelés, évaluations closes) et se tait trop facilement dans le
         message du transfert. Le serveur recompte les apprenants restants — si l'écran
         proposait la case sur un compteur périmé, c'est ici qu'on l'apprend. */
      if (d.cloture_source?.fait) {
        toast.success(`Classe « ${d.cloture_source.nom || "privée"} » clôturée — elle n'avait plus d'apprenant.`);
      } else if (d.cloture_source?.raison === "apprenants_restants") {
        toast(`Classe non clôturée : ${d.cloture_source.restants} apprenant(s) y suivent encore leurs cours.`, { icon: "ℹ️" });
      }

      /* Le dossier ne se referme qu'ICI, le déplacement ayant réussi. L'inverse le ferait
         disparaître de la file même quand le transfert échoue, l'apprenant restant dans la
         classe qu'il devait quitter. Non bloquant : le transfert, lui, est fait. */
      if (demandeEnCours) {
        try {
          const rd = await fetch(`${API_URL}/api/demandes-transfert/${demandeEnCours.id}`, {
            method: "PATCH", headers: authHeaders(),
            body: JSON.stringify({
              statut: "traitee", groupe_cible_id: classeId,
              ...(Number(nouvelleMensualite) > 0 ? { nouvelle_mensualite: Number(nouvelleMensualite), appliquer_mois_en_cours: tarifDesLeMoisEnCours } : {}),
            }),
          });
          if (!rd.ok) {
            const dd = await rd.json().catch(()=>({}));
            toast.error(dd.error || "Transfert fait, mais le dossier n'a pas pu être refermé.");
          }
        } catch {
          toast.error("Transfert fait, mais le dossier n'a pas pu être refermé.");
        }
        setDemandeEnCours(null);
        setRefreshReaffecter(n => n + 1);
      }

      setShowTransfert(false);
      // Rafraîchir la classe assignée affichée dans la fiche apprenant
      if (apprenantModal) openApprenantDetail(apprenantModal, selectedClasse);
      if (classeApprenantModal && transfertForm.ga_id === classeApprenantModal.id) setClasseApprenantModal(null);
      // L'apprenant a quitté cette classe - rafraîchir sa liste d'apprenants/effectif
      if (selectedClasse) {
        fetch(`${API_URL}/api/groupes/${selectedClasse.id}`, { headers: authHeaders() })
          .then(r => r.ok ? r.json() : null)
          .then(dd => { if (dd) setClasseDetailData(dd); })
          .catch(() => {});
      }
      fetchClasses();
    } catch (e) { toast.error(e.message); } finally { setTransfertSaving(false); }
  };

  // Filet de sécurité : le ga_id de commClasseInfo arrive de façon asynchrone (fetch
  // apprenant-by-email dans openApprenantDetail). Si l'utilisateur ouvre l'onglet Notes
  // (ou Présences) avant que ce fetch se termine, le déclencheur onClick ne voit encore
  // aucun ga_id et l'onglet reste vide en permanence - ce useEffect relance le
  // chargement dès que ga_id/l'id de classe deviennent disponibles, quel que soit le
  // moment où l'utilisateur a cliqué sur l'onglet.
  useEffect(() => {
    if (!apprenantModal) return;
    if (apprenantTab === "notes" && commClasseInfo?.ga_id && commAppEvals.length === 0 && !commAppEvalsLoading) {
      setCommAppEvalsLoading(true);
      fetch(`${API_URL}/api/groupes/apprenants/${commClasseInfo.ga_id}/evaluations`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : { evaluations: [] })
        .then(d => setCommAppEvals(d.evaluations || []))
        .catch(() => {})
        .finally(() => setCommAppEvalsLoading(false));
    }
  }, [apprenantTab, apprenantModal, commClasseInfo?.ga_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!apprenantModal) return;
    if (apprenantTab === "historique" && commClasseInfo?.ga_id && commAppHistorique.length === 0 && !commAppHistoriqueLoading) {
      setCommAppHistoriqueLoading(true);
      fetch(`${API_URL}/api/groupes/apprenants/${commClasseInfo.ga_id}/historique`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : { historique: [] })
        .then(d => setCommAppHistorique(d.historique || []))
        .catch(() => {})
        .finally(() => setCommAppHistoriqueLoading(false));
    }
  }, [apprenantTab, apprenantModal, commClasseInfo?.ga_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!apprenantModal) return;
    // Attend le ga_id résolu (réponse complète d'apprenant-by-email, qui porte aussi
    // date_ajout) plutôt que le simple id de classe transmis en instantané - sinon ce
    // fetch part une première fois avec date_ajout inconnu, et le garde-fou
    // "commAppPres.length > 0" empêche ensuite tout second passage correct.
    if (apprenantTab !== "presences" || !commClasseInfo?.ga_id || commAppPres.length > 0 || commAppPresLoading) return;
    const gaId   = commClasseInfo.ga_id;
    const nomRef = (apprenantModal.prospect_nom || apprenantModal.nom_apprenant || "").toLowerCase();
    // Jamais de repli sur "toutes les présences du groupe" quand aucune ne correspond à cet
    // apprenant - un apprenant tout juste créé n'a encore aucune feuille à son nom, et ce
    // repli lui affichait la feuille de TOUT le groupe (dont des "Absent" d'autres élèves,
    // sur des dates où il n'était même pas encore inscrit).
    const filterFn = (allPresences) => allPresences.filter(p =>
      (gaId && (p.ga_id === gaId || p.ga_id == gaId)) ||
      (nomRef && (p.nom_apprenant||"").toLowerCase().includes(nomRef.split(" ").pop()))
    );
    const dateAjout = commClasseInfo.date_ajout ? commClasseInfo.date_ajout.slice(0,10) : null;
    setCommAppPresLoading(true);

    /* ── LES PRÉSENCES DE TOUT LE PARCOURS, PAS SEULEMENT DE LA CLASSE DU MOMENT ──────────
       Cet onglet n'interrogeait que `/api/groupes/<classe actuelle>/presences`. Après un
       transfert, celles des classes précédentes DISPARAISSAIENT de la fiche : mesuré le
       07/10/2026 sur un apprenant transféré d'Angré vers II Plateaux — 0 présence affichée,
       3 bien présentes en base.

       Elles n'avaient jamais été perdues : une présence reste attachée à l'inscription et à la
       séance où elle a eu lieu, ce qui est la seule façon juste de la stocker — la déplacer
       vers la nouvelle classe ferait croire que l'apprenant était là un jour où cette classe
       n'existait pas pour lui. Ce qui manquait, c'était de les LIRE sur tout le parcours.

       `/api/mon-espace/presences` le fait déjà, et c'est la route de l'espace apprenant : même
       lecture pour l'apprenant et pour son assistante, donc aucun risque qu'ils voient deux
       assiduités différentes. Elle est ouverte au personnel BET (`emailAutorise`) et rend, pour
       chaque ligne, `classe_nom` et `classe_passee` — ce qui permet de les GROUPER par classe
       plutôt que de les mélanger. */
    /* L'e-mail de l'INSCRIPTION d'abord : c'est le seul que porte un apprenant à identifiant
       technique, et `prospect_email` est NULL sur une partie des dossiers. */
    const emailApp = commClasseInfo.email_apprenant
      || apprenantModal.email_apprenant || apprenantModal.prospect_email || null;

    const lectureParcours = emailApp
      ? fetch(`${API_URL}/api/mon-espace/presences?email=${encodeURIComponent(emailApp)}`, { headers: authHeaders() })
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      : Promise.resolve(null);

    lectureParcours
      .then(parcours => {
        /* Repli sur l'ancienne lecture si la route parcours n'a rien donné (e-mail inconnu,
           API pas encore déployée) : mieux vaut les présences de la classe actuelle que pas de
           présences du tout. */
        if (parcours?.presences?.length) return { presences: parcours.presences, _parcours: true };
        return fetch(`${API_URL}/api/groupes/${commClasseInfo.id}/presences`, { headers: authHeaders() })
          .then(r => r.ok ? r.json() : { presences: [] });
      })
      .then(d => {
        // La route parcours ne rend QUE les siennes ; celle du groupe rend toute la classe.
        const presences = d._parcours ? (d.presences || []) : filterFn(d.presences || []);
        return fetch(`${API_URL}/api/seances?groupe_id=${commClasseInfo.id}`, { headers: authHeaders() })
          .then(r => r.ok ? r.json() : { seances: [] })
          .then(ds => {
            const datesConnues = new Set(presences.map(p => p.date_seance));
            const aAjouter = (ds.seances || [])
              .filter(s => !datesConnues.has(s.date_seance))
              .map(s => ({
                id:`seance_${s.id}`, date_seance:s.date_seance,
                statut: (dateAjout && s.date_seance < dateAjout) ? "non_inscrit" : (s.statut === "planifiee" ? "planifiee" : s.statut),
                note: s.titre || null,
              }));
            setCommAppPres([...presences, ...aAjouter]);
          })
          .catch(() => setCommAppPres(presences));
      })
      .catch(() => {})
      .finally(() => setCommAppPresLoading(false));
  }, [apprenantTab, apprenantModal, commClasseInfo?.id, commClasseInfo?.ga_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [commAccesCreating,   setCommAccesCreating]   = useState(false);
  const [commAccesResending,  setCommAccesResending]  = useState(false);
  const [commParentAccesInfo,     setCommParentAccesInfo]     = useState(null);
  const [commParentAccesLoad,     setCommParentAccesLoad]     = useState(false);
  const [commParentAccesCreating, setCommParentAccesCreating] = useState(false);
  const [commParentForm,          setCommParentForm]          = useState({ nom_parent:"", telephone_parent:"", email_parent:"" });
  const [commParentSaving,        setCommParentSaving]        = useState(false);
  const [commParentCreateError,   setCommParentCreateError]   = useState("");
  const [archivesOpen,        setArchivesOpen]        = useState(false);
  const [annulesOpen,         setAnnulesOpen]         = useState(false);
  const [cancelModal,         setCancelModal]         = useState({ open:false, target:null, raison:"", raisonLibre:"" });
  const [cancelSaving,        setCancelSaving]        = useState(false);
  const [tranchePayTarget,    setTranchePayTarget]    = useState(null); // { assignationId, trancheId, plan, tranches }
  const [moisPayTarget,       setMoisPayTarget]       = useState(null); // { assignationId, moisId, trancheId, plan }
  // Mois du plan mensuel dépliés (clé "assignationId_moisId") - repliés par défaut pour
  // qu'un plan ouvert accumulant les mois au fil du temps reste lisible.
  const [moisDepliés,         setMoisDepliés]         = useState({});
  // Modal "Modifier ce mois" (montant + échéance) d'un mois précis du plan mensuel
  const [moisEcheanceTarget,  setMoisEcheanceTarget]  = useState(null); // { assignationId, mois, plan }
  const [moisEcheanceValeur,  setMoisEcheanceValeur]  = useState("");
  const [moisMontantValeur,   setMoisMontantValeur]   = useState("");
  const [moisEcheanceSaving,  setMoisEcheanceSaving]  = useState(false);
  // Réduction/promo appliquée à ce mois précis (ex: badge de fidélité "6e mois -10%") - motif
  // libre + type (pourcentage ou montant fixe), affichée en badge sur le mois comme le code promo.
  const [moisReductionActive, setMoisReductionActive] = useState(false);
  const [moisReductionMotif,  setMoisReductionMotif]  = useState("");
  const [moisReductionType,   setMoisReductionType]   = useState("pourcentage"); // "pourcentage" | "montant"
  const [moisReductionValeur, setMoisReductionValeur] = useState("");

  // ── Suspension globale apprenant ────────────────────────────
  const [suspensionLoading, setSuspensionLoading] = useState(false);
  // Arret de formation : distinct du blocage d'acces. Bloquer, c'est fermer la porte a
  // quelqu'un qui reste inscrit ; arreter, c'est acter qu'il est parti - et ce sont les
  // cours publies APRES son depart qui cessent de lui etre servis.
  const [arretLoading, setArretLoading] = useState(false);
  const [arretModal, setArretModal]     = useState(null); // { ga, arrete }
  const [arretMotif, setArretMotif]     = useState("");
  // Modal de motif : bloquer sans dire pourquoi laissait l'apprenant devant un message
  // generique, sans savoir quoi faire. Le motif choisi ici est celui qu'il lira a la
  // connexion et recevra par email.
  const [blocageModal,   setBlocageModal]   = useState(null);  // { appId } quand ouvert
  const [motifsBlocage,  setMotifsBlocage]  = useState([]);
  const [motifChoisi,    setMotifChoisi]    = useState("absences_repetees");
  const [precisionMotif, setPrecisionMotif] = useState("");
  const [blocageStatus,     setBlocageStatus]     = useState(null); // { acces_bloque, nb_absences }
  const [blocageLoad,       setBlocageLoad]        = useState(false);

  const fetchAssignations = async () => {
    setAssignationsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/parcours/assignations`, { headers: authHeaders() });
      if (r.ok) { const d = await r.json(); setAssignations(d.assignations || []); }
    } catch (e) { console.error("Chargement assignations:", e); }
    finally { setAssignationsLoading(false); }
  };

  useEffect(() => {
    fetchAssignations();

    // Recharge en temps réel dès qu'une nouvelle assignation OU un nouveau test arrive
    const onNewAssignation = () => fetchAssignations();
    const onNewTest        = () => { fetchTests(); fetchAssignations(); };

    window.addEventListener("bet:assignation:new", onNewAssignation);
    window.addEventListener("bet:test:new",        onNewTest);

    return () => {
      window.removeEventListener("bet:assignation:new", onNewAssignation);
      window.removeEventListener("bet:test:new",        onNewTest);
    };
  }, []); // eslint-disable-line

  // Nom de l'apprenant réellement rattaché à une ligne (assignation ou groupe/apprenant),
  // utilisé pour désambiguïser plusieurs enfants qui partagent le même email/téléphone parent.
  const nomApprenantDe = (x) => (
    (x.prenom_enfant || x.nom_enfant) ? `${x.prenom_enfant || ""} ${x.nom_enfant || ""}`.trim().toLowerCase()
    : (x.prenom_apprenant || x.nom_apprenant) ? `${x.prenom_apprenant || ""} ${x.nom_apprenant || ""}`.trim().toLowerCase()
    : (x.fullname || x.prospect_nom || "").trim().toLowerCase()
  );

  // Un email (souvent celui du parent) peut être partagé par plusieurs enfants : ne jamais
  // se fier au seul email pour retrouver LA bonne ligne - désambiguïser par nom quand plusieurs
  // candidats partagent le même email.
  // Pour un profil ENFANT, le raccourci "un seul candidat = c'est forcément lui" est dangereux :
  // l'unique test existant sous cet email est très souvent celui du PARENT (déjà apprenant BET
  // sous cette même adresse), jamais celui de l'enfant - l'afficher/le bloquer à tort empêche
  // l'enfant de passer son propre test. Pour un enfant, on exige donc une correspondance de nom
  // explicite ; sans elle, on considère qu'il n'y a pas encore de test pour lui.
  const trouverParEmailEtNom = (liste, cible, getEmail) => {
    const emailCible = (cible.prospect_email || cible.email_apprenant || cible.email || "").toLowerCase();
    if (!emailCible) return null;
    const candidats = liste.filter(x => (getEmail(x) || "").toLowerCase() === emailCible);
    const estEnfant = cible.profil_type === "enfant" || !!(cible.prenom_enfant || cible.nom_enfant);
    if (candidats.length <= 1 && !estEnfant) return candidats[0] || null;
    const nomCible = nomApprenantDe(cible);
    const match = candidats.find(x => nomCible && nomApprenantDe(x) === nomCible);
    if (match) return match;
    return estEnfant ? null : (candidats[0] || null);
  };

  // Match direct par assignation_id en priorité - fiable à 100%, contrairement au
  // rapprochement email+nom (trouverParEmailEtNom) qui échouait systématiquement pour les
  // profils enfant (email partagé avec le parent) et restait fragile pour tous les autres
  // (espace en trop, nom modifié par le prospect…). Le repli par email+nom ne sert plus
  // qu'aux anciennes lignes soumises avant l'ajout de la colonne assignation_id.
  /* LE PLUS RÉCENT, pas le premier venu. `.find()` rendait le premier test rencontré dans la
     liste : depuis qu'un apprenant peut en repasser un lors d'un transfert, c'était souvent
     l'ANCIEN qui s'affichait - « A2 » sur quelqu'un passé B1 la veille. Le niveau montré doit
     être celui qui vaut aujourd'hui. */
  const plusRecent = (liste) => (liste || []).slice().sort(
    (a, b) => String(b.date || b.submitted_at || "").localeCompare(String(a.date || a.submitted_at || ""))
  )[0] || null;

  /* Les tests BRUTS de cet apprenant, du plus récent au plus ancien. `tousLesTestsDe` les
     normalise pour l'affichage de l'évolution ; le PDF, lui, a besoin du score et de l'origine. */
  const tousLesTestsBrutsDe = (a) => {
    const miens = tousTests.filter(t =>
      (a.id && t.assignation_id === a.id) ||
      (!t.assignation_id && a.prospect_email && t.email
        && t.email.toLowerCase() === a.prospect_email.toLowerCase()));
    return miens.slice().sort((x, y) =>
      String(y.date || y.submitted_at || "").localeCompare(String(x.date || x.submitted_at || "")));
  };

  /* LES PASSAGES DU MÊME APPRENANT, à partir d'un test et non d'un dossier. Le modal de détail
     ne connaît que le test ouvert : sans ceci, impossible d'y proposer les autres passages.
     Le rapprochement se fait par dossier quand la colonne existe, sinon par e-mail ET nom -
     un parent et son enfant partagent la même adresse, les mélanger afficherait le test de
     l'un sous le nom de l'autre. */
  const passagesDuMemeApprenant = (t) => {
    if (!t) return [];
    const memeNom = (a, b) =>
      `${a.prenom || ""} ${a.nom || ""}`.trim().toLowerCase() ===
      `${b.prenom || ""} ${b.nom || ""}`.trim().toLowerCase();
    const memeEmail = (a, b) =>
      !!a.email && !!b.email && a.email.toLowerCase() === b.email.toLowerCase();
    const miens = tousTests.filter(x =>
      (t.assignation_id && x.assignation_id === t.assignation_id) ||
      (memeEmail(x, t) && memeNom(x, t)));
    const avecLui = miens.some(x => x.id === t.id) ? miens : [...miens, t];
    return avecLui.slice().sort((x, y) =>
      String(y.date || y.submitted_at || "").localeCompare(String(x.date || x.submitted_at || "")));
  };

  const findTestForProspect = (a) => {
    // Recherche par dossier dans TOUS les tests : celui d'un transfert porte le bon
    // `assignation_id` mais pas de `commercial_id`, il est donc absent de `tests`.
    const parDossier = a.id ? tousTests.filter(t => t.assignation_id === a.id) : [];
    if (parDossier.length) return plusRecent(parDossier);
    // Le repli par e-mail/nom ne rend qu'un élément : on récupère tous ses homologues pour
    // en extraire le plus récent, plutôt que de garder celui que le repli a croisé d'abord.
    const parEmail = trouverParEmailEtNom(tests, a, (t) => t.email);
    if (!parEmail) return null;
    const memeEmail = tests.filter(t => t.email && parEmail.email && t.email.toLowerCase() === parEmail.email.toLowerCase());
    return plusRecent(memeEmail) || parEmail;
  };

  /* TOUS ses tests, du plus ancien au plus récent. `findTestForProspect` n'en rend qu'UN - le
     premier trouvé - alors qu'un apprenant en repasse un chaque fois qu'un coach demande son
     transfert. La fiche affichait donc un niveau sans dire d'où il venait.
     Les objets de cet écran portent `niveau`/`date` ; le composant d'affichage parle
     `level`/`submitted_at` comme la table : on normalise ici plutôt que dans le composant, qui
     sert aussi à l'onboarding où les champs sont déjà aux bons noms. */
  const tousLesTestsDe = (a) => {
    // Même source que le reste : `tousLesTestsBrutsDe` cherche dans TOUS les tests, pas
    // seulement ceux du pipeline de la conseillère - un test de transfert n'y figure pas.
    return tousLesTestsBrutsDe(a)
      .map(t => ({ id: t.id, level: t.niveau || t.level, submitted_at: t.date || t.submitted_at }))
      .filter(t => t.level)
      .sort((x, y) => String(x.submitted_at || "").localeCompare(String(y.submitted_at || "")));
  };

  const openApprenantDetail = (a, classeInfo = null, gaIdConnu = null) => {
    /* LA PRISE EN CHARGE, SIGNALÉE SANS UN CLIC. Ouvrir le dossier, c'est s'en occuper :
       c'est ce moment que `COM-03` mesure (cible 2 h). Envoyé sans attendre la réponse -
       une mesure ne doit ni retarder l'ouverture d'un écran, ni la faire échouer. */
    if (a?.id) {
      fetch(`${API_URL}/api/parcours/assignations/${a.id}/prise-en-charge`, {
        method: "POST", headers: authHeaders(),
      }).catch(() => {});
    }

    // Si l'apprenant vient du détail classe, fusionner avec l'assignation correspondante
    // pour récupérer suivi_presences, suivi_demarrage, niveau_client, etc.
    const email = a.prospect_email || a.email_apprenant || "";
    // Lien fiable posé dans `note` (groupes_apprenants) à la création de la classe - prioritaire
    // sur le match par email, qui échoue systématiquement pour un profil enfant (email_apprenant
    // est un identifiant technique, jamais égal à prospect_email qui est celui du parent).
    let assignationIdDeNote = null;
    try { assignationIdDeNote = JSON.parse(a.note || "{}").assignation_id || null; } catch {}
    // Si `a` est déjà l'assignation elle-même (cas "Prospects parcours"/"Mes apprenants"),
    // on la garde telle quelle - ne jamais la remplacer par une autre ligne partageant le
    // même email (ex : plusieurs enfants d'un même parent ont le même email_parent).
    const assignationLiee = assignations.find(x => x.id === a.id)
      || (assignationIdDeNote && assignations.find(x => x.id === assignationIdDeNote))
      || (email ? trouverParEmailEtNom(assignations, a, (x) => x.prospect_email) : null);
    // L'assignation a la priorité (suivi_presences, id, prospect_nom…) ; les champs ga (nom_apprenant, email_apprenant) complètent
    const enriched = assignationLiee ? { ...a, ...assignationLiee } : a;

    setApprenantModal(enriched);
    setApprenantTab("infos");
    setCommAccesInfo(null);
    setCommClasseInfo(null);
    setCommAppPres([]);
    setCommAppEvals([]);
    setCommAppHistorique([]);
    setCommAppHistoriqueOuvert(null);
    setCommParentAccesInfo(null);
    setCommParentForm({
      nom_parent:       enriched.nom_parent       || "",
      telephone_parent: enriched.telephone_parent || "",
      email_parent:     enriched.email_parent     || "",
    });
    // Fetch accès apprenant - assignation_id (lien fiable posé dans groupes_apprenants.note
    // à la création de la classe) est prioritaire : la liste "Mes apprenants" vient
    // d'assignations_parcours, qui n'a JAMAIS de champ email_apprenant (propre à
    // groupes_apprenants) - pour un profil enfant, prospect_email est celui du PARENT, jamais
    // celui de l'enfant, donc chercher par email seul manquait systématiquement le vrai compte.
    const emailAccesReel = a.email_apprenant || enriched.email_apprenant || a.prospect_email || email;
    if (emailAccesReel || enriched.id) {
      setCommAccesLoad(true);
      const parts = [];
      if (enriched.id)      parts.push(`assignation_id=${encodeURIComponent(enriched.id)}`);
      if (emailAccesReel)   parts.push(`email=${encodeURIComponent(emailAccesReel)}`);
      const _q = parts.join("&");
      setBlocageStatus(null);
      fetch(`${API_URL}/api/apprenants/profil-acces?${_q}`, { headers: authHeaders() })
        .then(async r => { const d = await r.json().catch(() => null); return r.ok ? d : null; })
        .then(d => {
          setCommAccesInfo(d);
          setCommAccesLoad(false);
          // Compteur d'absences / statut blocage - même affichage que bet-elearning
          // (progression vers le blocage automatique à 4 absences).
          if (d?.profil?.id) {
            setBlocageLoad(true);
            fetch(`${API_URL}/api/groupes/apprenants/acces-status?apprenant_id=${encodeURIComponent(d.profil.id)}`, { headers: authHeaders() })
              .then(r => r.ok ? r.json() : null)
              .then(s => setBlocageStatus(s))
              .catch(() => setBlocageStatus(null))
              .finally(() => setBlocageLoad(false));
          }
        })
        .catch(() => setCommAccesLoad(false));
    }
    // Fetch accès parent (profil enfant uniquement)
    if (enriched.profil_type === "enfant" && enriched.email_parent) {
      setCommParentAccesLoad(true);
      fetch(`${API_URL}/api/apprenants/acces-parent?email=${encodeURIComponent(enriched.email_parent)}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : { existe:false })
        .then(d => { setCommParentAccesInfo(d); setCommParentAccesLoad(false); })
        .catch(() => setCommParentAccesLoad(false));
    }
    // Si la classe est déjà connue (appel depuis le détail classe), l'afficher tout de
    // suite pour un rendu instantané - mais `a.id` n'est PAS fiable comme ga_id ici :
    // selon l'écran d'où vient l'appel, `a` est soit la ligne groupes_apprenants (a.id
    // = vrai ga_id), soit l'assignation elle-même (a.id = id d'assignation, une tout
    // autre table). On corrige donc systématiquement via le fetch par email juste après,
    // seule source fiable de ga_id (utilisé par l'onglet Notes/évaluations) - sans ce
    // correctif, Notes et séances restaient vides pour tout apprenant ouvert depuis un
    // écran qui passe l'assignation comme `a`, alors que Présences (autre logique) marchait.
    if (classeInfo) {
      // gaIdConnu : passé explicitement par les écrans qui savent avec certitude que `a` est
      // la ligne groupes_apprenants elle-même (ex. détail classe → onglet Apprenants) - évite
      // d'attendre la résolution asynchrone ci-dessous (par email, faillible pour un profil
      // enfant ou une note sans assignation_id) juste pour activer le bouton "Changer de groupe".
      setCommClasseInfo({ ...classeInfo, ga_id: gaIdConnu || null });
      setCommClasseLoad(false);
    }
    if (email || enriched.id) {
      setCommClasseLoad(true);
      // assignation_id en plus de l'email - seul repère fiable pour un profil enfant, dont
      // l'email ici est souvent celui du parent (prospect_email), jamais email_apprenant.
      const _q = [
        email ? `email=${encodeURIComponent(email)}` : null,
        enriched.id ? `assignation_id=${encodeURIComponent(enriched.id)}` : null,
      ].filter(Boolean).join("&");
      fetch(`${API_URL}/api/groupes/apprenant-by-email?${_q}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : { groupe: null })
        .then(d => { if (d.groupe) setCommClasseInfo(d.groupe); setCommClasseLoad(false); })
        .catch(() => setCommClasseLoad(false));
    }
  };

  const updateStatutAssignation = async (id, statut) => {
    try {
      const r = await fetch(`${API_URL}/api/parcours/assignations/${id}`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify({ statut }),
      });
      if (r.ok) {
        setAssignations(prev => prev.map(a => a.id === id ? { ...a, statut } : a));
        toast.success("Statut mis à jour");
      }
    } catch { toast.error("Erreur"); }
  };

  const handleAnnulerDossier = async () => {
    if (cancelSaving) return;
    const { target, raison, raisonLibre } = cancelModal;
    if (!target) return;
    const motifFinal = raison === "Autre" ? (raisonLibre.trim() || "Autre") : raison;
    if (!motifFinal) { toast.error("Veuillez indiquer une raison d'annulation"); return; }
    setCancelSaving(true);
    try {
      const notesAnnotees = `[ANNULE] ${motifFinal}${target.notes ? " - " + target.notes : ""}`;
      const r = await fetch(`${API_URL}/api/parcours/assignations/${target.id}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ statut: "annule", notes: notesAnnotees }),
      });
      if (!r.ok) throw new Error("Erreur serveur");
      setAssignations(prev => prev.map(a => a.id === target.id ? { ...a, statut: "annule", notes: notesAnnotees } : a));
      setCancelModal({ open:false, target:null, raison:"", raisonLibre:"" });
      toast.success("Dossier annulé - visible dans la section Dossiers annulés");
    } catch { toast.error("Impossible d'annuler le dossier"); }
    finally { setCancelSaving(false); }
  };

  // ── Demandes B2B (formulaires entreprise du site web) ──────
  const [b2bDemandes,        setB2bDemandes]        = useState([]);
  const [b2bDemandesLoading, setB2bDemandesLoading] = useState(false);
  const [b2bFiltreStatut,    setB2bFiltreStatut]    = useState("tous");
  const [b2bFiltreType,      setB2bFiltreType]      = useState("tous");
  const [b2bDetailId,        setB2bDetailId]        = useState(null);
  const [b2bNoteEdit,        setB2bNoteEdit]        = useState({});
  const [b2bNoteSaving,      setB2bNoteSaving]      = useState(null);

  const fetchB2bDemandes = async () => {
    setB2bDemandesLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/entreprise/mes-demandes`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setB2bDemandes(d.demandes || []);
      } else {
        const err = await r.json().catch(() => ({}));
        console.error("[B2B] erreur mes-demandes:", r.status, err);
        toast.error(`Erreur chargement demandes (${r.status}) : ${err.error || "inconnue"}`);
      }
    } catch (e) { console.error("Chargement demandes B2B:", e); }
    finally { setB2bDemandesLoading(false); }
  };

  const updateB2bStatut = async (email, assignationId, statut) => {
    try {
      let ok = false;
      if (assignationId) {
        const r = await fetch(`${API_URL}/api/parcours/assignations/${assignationId}`, {
          method: "PATCH", headers: authHeaders(), body: JSON.stringify({ statut }),
        });
        ok = r.ok;
      } else {
        const r = await fetch(`${API_URL}/api/entreprise/demandes/suivi`, {
          method: "PATCH", headers: authHeaders(), body: JSON.stringify({ email, statut }),
        });
        ok = r.ok;
      }
      if (ok) {
        setB2bDemandes(prev => prev.map(d => d.email === email ? { ...d, statut } : d));
        toast.success("Statut mis à jour");
      }
    } catch { toast.error("Erreur mise à jour"); }
  };

  const saveB2bNote = async (email, assignationId) => {
    const notes = b2bNoteEdit[email] ?? "";
    setB2bNoteSaving(email);
    try {
      let ok = false;
      if (assignationId) {
        const r = await fetch(`${API_URL}/api/parcours/assignations/${assignationId}`, {
          method: "PATCH", headers: authHeaders(), body: JSON.stringify({ notes }),
        });
        ok = r.ok;
      } else {
        const r = await fetch(`${API_URL}/api/entreprise/demandes/suivi`, {
          method: "PATCH", headers: authHeaders(), body: JSON.stringify({ email, notes_commercial: notes }),
        });
        ok = r.ok;
      }
      if (ok) {
        setB2bDemandes(prev => prev.map(d => d.email === email ? { ...d, notes_commercial: notes } : d));
        toast.success("Note sauvegardée");
      }
    } catch { toast.error("Erreur sauvegarde note"); }
    finally { setB2bNoteSaving(null); }
  };

  useEffect(() => {
    if (assistanteProfil === "b2b") fetchB2bDemandes();
  }, [assistanteProfil]); // eslint-disable-line

  const handleSaveDocsDossier = async (assignationId, docs) => {
    try {
      const r = await fetch(`${API_URL}/api/parcours/assignations/${assignationId}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ documents_dossier: docs }),
      });
      if (!r.ok) throw new Error();
      setAssignations(prev => prev.map(a => a.id === assignationId ? { ...a, documents_dossier: docs } : a));
      setApprenantModal(prev => prev?.id === assignationId ? { ...prev, documents_dossier: docs } : prev);
      toast.success("Documents mis à jour ✓");
      setShowDocDossierModal(false); setDocDossierTarget(null);
    } catch { toast.error("Erreur lors de la sauvegarde des documents"); }
  };

  const handleAssignCentre = async (assignationId, data) => {
    try {
      const assignation = assignations.find(a => a.id === assignationId);

      // Plan mensuel récurrent automatique dès qu'une classe est assignée manuellement, si
      // une date de début et au moins un versement réel existent déjà - mensualité dérivée
      // du 1er versement (même règle que l'import apprenants, aucune saisie supplémentaire).
      // Jamais écrasé si un plan existe déjà. Sans ce bloc, une assignation manuelle (par
      // opposition à l'import en masse) ne déclenchait jamais la récurrence, même avec un
      // paiement déjà réel (ex: réglé via TouchPoint avant l'assignation à la classe).
      let planPaiementAuto = null;
      let dateDebutAuto = null; // renseignée seulement si l'assignation n'avait pas déjà la sienne
      if (data.classeId && !assignation?.plan_paiement) {
        try {
          const rPmts = await fetch(`${API_URL}/api/paiements/assignation/${assignationId}`, { headers: authHeaders() });
          if (rPmts.ok) {
            const dPmts = await rPmts.json();
            const paiementsTries = (dPmts.paiements || []).slice().sort((a, b) => (a.date_paiement || "").localeCompare(b.date_paiement || ""));
            if (paiementsTries.length) {
              const mensualite = Number(paiementsTries[0].montant_recu) || 0;
              if (mensualite > 0) {
                // Sans date_debut_formation déjà renseignée (cas fréquent : paiement en ligne
                // avant l'assignation manuelle à une classe par la commerciale - la génération
                // automatique du plan ne se déclenchait alors jamais), on la déduit de la date
                // du 1er paiement réel, plus fidèle que la date du jour.
                const dateDebutEffective = assignation?.date_debut_formation
                  || paiementsTries[0].date_paiement
                  || new Date().toISOString().slice(0, 10);
                if (!assignation?.date_debut_formation) dateDebutAuto = dateDebutEffective;

                const moisGeneres = genererMoisPaiements(dateDebutEffective, null, mensualite);
                const paiementsPourPlan = paiementsTries.map(p => ({ montant: Number(p.montant_recu) || 0, date: p.date_paiement, reference: p.ref_transaction }));
                planPaiementAuto = {
                  type: "mensuel", mensualite, date_debut_formation: dateDebutEffective, date_fin_formation: null,
                  mois_paiements: appliquerVersementsAuPlan(moisGeneres, paiementsPourPlan),
                  jour_echeance: new Date(dateDebutEffective).getDate(), actif: true,
                };
              }
            }
          }
        } catch { /* pas bloquant - l'assignation à la classe reste prioritaire */ }
      }

      const payload = {
        suivi_demarrage: {
          ...(assignation?.suivi_demarrage || {}),
          classe_id:        data.classeId   || null,
          classe_nom:       data.classeNom  || null,
          coach_id:         data.coachId    || null,
          coach_nom:        data.coachNom   || null,
          date_assignation: new Date().toISOString().slice(0, 10),
        },
        routing_statut: "centre_assigne",
        statut:         "converti",
        ...(dateDebutAuto ? { date_debut_formation: dateDebutAuto } : {}),
        // Coaching privé : le pipeline (validation → paiement → routage) est terminé une
        // fois la classe assignée - sans ça, le badge "Paiement reçu - en attente de
        // vérification du PA" restait affiché indéfiniment alors que le PA avait déjà
        // vérifié et routé le dossier jusqu'ici.
        ...(assignation?.type_coaching === "prive" ? { dossier_statut: null } : {}),
        ...(planPaiementAuto ? { plan_paiement: planPaiementAuto } : {}),
      };
      const r = await fetch(`${API_URL}/api/parcours/assignations/${assignationId}`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        throw new Error(d?.error || "Erreur lors de l'assignation");
      }
      setAssignations(prev => prev.map(a => a.id === assignationId ? { ...a, ...payload } : a));
      setApprenantModal(prev => prev?.id === assignationId ? { ...prev, ...payload } : prev);

      // Ajouter l'apprenant dans la table groupes_apprenants
      if (data.classeId && assignation) {
        const nomComplet = (assignation.prospect_nom || "").trim();
        const spaceIdx   = nomComplet.indexOf(" ");
        const prenomApp  = spaceIdx > 0 ? nomComplet.slice(0, spaceIdx) : nomComplet;
        const nomApp     = spaceIdx > 0 ? nomComplet.slice(spaceIdx + 1) : "";
        // Pour un profil enfant, prospect_email/prospect_telephone sont ceux du PARENT -
        // jamais ceux de l'enfant. Laisser ces deux champs vides force le système à générer
        // un identifiant purement technique propre à l'enfant, au lieu de réutiliser les
        // coordonnées réelles du parent comme login de l'enfant (ce qui les "occupe" ensuite
        // et empêche la création d'un compte parent séparé avec les mêmes coordonnées).
        const isEnfantAssign = assignation.profil_type === "enfant";
        await fetch(`${API_URL}/api/groupes/${data.classeId}/apprenants`, {
          method: "POST", headers: authHeaders(),
          body: JSON.stringify({
            nom_apprenant:    nomApp || prenomApp,
            prenom_apprenant: nomApp ? prenomApp : "",
            email_apprenant:  isEnfantAssign ? "" : (assignation.prospect_email || ""),
            telephone:        isEnfantAssign ? "" : (assignation.prospect_telephone || ""),
            niveau:           assignation.niveau_client      || "",
            // Lien fiable vers le dossier BET (assignations_parcours) - indispensable pour
            // un profil enfant, où email_apprenant est vide et ne peut donc pas servir de
            // repère (cf. commentaires plus haut sur le fallback par email qui échoue).
            note:             JSON.stringify({ assignation_id: assignationId }),
          }),
        });

        /* ── LES AUTRES PERSONNES DE LA FAMILLE ──────────────────────────────────────
           UNE ASSISTANTE PRÉSENTIEL N'A PAS D'ONBOARDING : elle affecte la classe
           elle-même, et ce code est alors le SEUL passage où la famille peut être
           inscrite. La même logique existe côté Onboarding pour le circuit en ligne ;
           l'oublier ici aurait laissé les familles du présentiel avec un seul apprenant
           inscrit, sans que rien ne le signale.

           C'est aussi ici que les accès sont générés : l'endpoint crée le compte de
           chaque personne et lui envoie son mot de passe temporaire. Les membres d'une
           famille n'ont donc d'espace apprenant qu'à partir de l'affectation de classe -
           pas avant, puisque rien ne les rattache encore à un coach ni à un planning.

           Échecs tolérés : une adresse déjà utilisée ne doit pas faire échouer
           l'affectation du payeur, qui est l'acte principal. */
        const sdFamilleComm = assignation.suivi_demarrage || {};
        const membresFamilleComm = Array.isArray(sdFamilleComm.membres_famille) ? sdFamilleComm.membres_famille : [];
        if (membresFamilleComm.length > 0) {
          let ajoutesFam = 0;
          const echecsFam = [];
          for (const membre of membresFamilleComm) {
            const prenomM = (membre?.prenom || "").trim();
            const nomM    = (membre?.nom || "").trim();
            if (!prenomM && !nomM) continue;
            try {
              const rm = await fetch(`${API_URL}/api/groupes/${data.classeId}/apprenants`, {
                method: "POST", headers: authHeaders(),
                body: JSON.stringify({
                  nom_apprenant:    nomM || prenomM,
                  prenom_apprenant: nomM ? prenomM : "",
                  email_apprenant:  (membre?.email || "").trim() || "",
                  telephone:        (membre?.telephone || "").trim() || "",
                  niveau:           membre?.niveau || assignation.niveau_client || "",
                  note: JSON.stringify({
                    assignation_id: assignationId,
                    membre_famille: true,
                    lien_famille:   membre?.lien || null,
                  }),
                }),
              });
              if (rm.ok) ajoutesFam++;
              else {
                const dm = await rm.json().catch(() => ({}));
                echecsFam.push(`${prenomM || nomM} : ${dm.error || `erreur ${rm.status}`}`);
              }
            } catch (e) {
              echecsFam.push(`${prenomM || nomM} : ${e.message}`);
            }
          }
          if (ajoutesFam > 0) {
            toast.success(`👥 ${ajoutesFam} personne${ajoutesFam > 1 ? "s" : ""} de la famille inscrite${ajoutesFam > 1 ? "s" : ""} - accès créés et envoyés`);
          }
          if (echecsFam.length > 0) {
            toast.error(`Famille - ${echecsFam.length} non inscrite(s) : ${echecsFam[0]}`, { duration: 8000 });
            console.warn("[famille] échecs d'inscription :", echecsFam);
          }
        }

        fetchClasses();
        // Rafraîchir commClasseInfo pour que le modal apprenant affiche la classe/coach
        // immédiatement - assignation_id en plus de l'email, seul repère fiable pour un
        // profil enfant (prospect_email est celui du parent, jamais email_apprenant).
        if (assignation.prospect_email || assignation.id) {
          const _q2 = [
            assignation.prospect_email ? `email=${encodeURIComponent(assignation.prospect_email)}` : null,
            assignation.id ? `assignation_id=${encodeURIComponent(assignation.id)}` : null,
          ].filter(Boolean).join("&");
          const ci = await fetch(`${API_URL}/api/groupes/apprenant-by-email?${_q2}`, { headers: authHeaders() });
          if (ci.ok) { const cd = await ci.json(); setCommClasseInfo(cd.groupe || null); }
        }
      }

      toast.success("✅ Apprenant assigné à la classe et au coach !");
      setShowAssignCentreModal(false); setAssignCentreTarget(null);
    } catch (e) { toast.error(e.message || "Erreur lors de l'assignation"); }
  };

  const handleSaveSuivi = async (assignationId, suivi) => {
    try {
      const r = await fetch(`${API_URL}/api/parcours/assignations/${assignationId}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ suivi_demarrage: suivi }),
      });
      if (!r.ok) throw new Error();
      setAssignations(prev => prev.map(a => a.id === assignationId ? { ...a, suivi_demarrage: suivi } : a));
      setApprenantModal(prev => prev?.id === assignationId ? { ...prev, suivi_demarrage: suivi } : prev);
      toast.success("Suivi de démarrage enregistré ✓");
      setShowSuiviModal(false); setSuiviTarget(null);
    } catch { toast.error("Erreur lors de la sauvegarde du suivi"); }
  };

  const handleSavePresences = async (assignationId, suivi_presences) => {
    try {
      const r = await fetch(`${API_URL}/api/parcours/assignations/${assignationId}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ suivi_presences }),
      });
      if (!r.ok) throw new Error();
      setAssignations(prev => prev.map(a => a.id === assignationId ? { ...a, suivi_presences } : a));
      setApprenantModal(prev => prev?.id === assignationId ? { ...prev, suivi_presences } : prev);
      toast.success("Présences enregistrées ✓");
      setShowProgressionModal(false); setProgressionTarget(null);
    } catch { toast.error("Erreur lors de la sauvegarde des présences"); }
  };

  const handleSavePlanPaiement = async (assignationId, plan_paiement) => {
    const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
    const token = localStorage.getItem("bet_token") || localStorage.getItem("admin_token") || "";
    await fetch(`${API}/api/parcours/assignations/${assignationId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ plan_paiement }),
    });
    setAssignations(prev => prev.map(a => a.id === assignationId ? { ...a, plan_paiement } : a));
    setApprenantModal(prev => prev?.id === assignationId ? { ...prev, plan_paiement } : prev);
  };

  // ── Sauvegarde du modal "Modifier ce mois" (montant + échéance + réduction) ──
  // La réduction (ex: badge de fidélité "6e mois -10%") s'applique sur le montant standard
  // saisi - mois.montant stocke toujours le montant final réellement dû (comme partout
  // ailleurs dans le plan), la métadonnée mois.reduction ne sert qu'à l'affichage du badge.
  const sauvegarderMoisEcheance = async () => {
    if (!moisEcheanceTarget) return;
    const { assignationId, mois, plan } = moisEcheanceTarget;
    const montantStandard = Number(moisMontantValeur);
    if (!montantStandard || montantStandard <= 0) { toast.error("Montant invalide"); return; }
    if (!moisEcheanceValeur) { toast.error("Date d'échéance requise"); return; }

    let reduction = null;
    let nouveauMontant = montantStandard;
    if (moisReductionActive) {
      const valeurReduction = Number(moisReductionValeur);
      if (!valeurReduction || valeurReduction <= 0) { toast.error("Valeur de réduction invalide"); return; }
      if (!moisReductionMotif.trim()) { toast.error("Motif de la réduction requis"); return; }
      const montantDeduit = moisReductionType === "pourcentage"
        ? Math.round(montantStandard * valeurReduction / 100)
        : Math.min(valeurReduction, montantStandard);
      nouveauMontant = Math.max(0, montantStandard - montantDeduit);
      reduction = { motif: moisReductionMotif.trim(), type: moisReductionType, valeur: valeurReduction, montant_deduit: montantDeduit, montant_avant: montantStandard };
    }

    setMoisEcheanceSaving(true);
    try {
      const tDejaPaye = (mois.tranches||[]).filter(t=>t.statut==="paye").reduce((s,t)=>s+(t.montant||0),0);
      const nouveauStatut = tDejaPaye >= nouveauMontant ? "paye" : tDejaPaye > 0 ? "partiel" : "en_attente";
      const newMoisListe = (plan.mois_paiements||[]).map(m => m.id === mois.id
        ? { ...m, montant: nouveauMontant, echeance_override: moisEcheanceValeur, statut: nouveauStatut, reduction }
        : m
      );
      await handleSavePlanPaiement(assignationId, { ...plan, mois_paiements: newMoisListe });
      toast.success(`${mois.label} mis à jour`);
      setMoisEcheanceTarget(null);
    } finally {
      setMoisEcheanceSaving(false);
    }
  };

  const fetchSondages = async () => {
    try {
      const [rList, rStats] = await Promise.all([
        fetch(`${API_URL}/api/sondage/mes-clients`,  { headers: authHeaders() }),
        fetch(`${API_URL}/api/sondage/stats`,        { headers: authHeaders() }),
      ]);
      if (rList.ok)  { const d = await rList.json();  setSondages(d.sondages || []); }
      if (rStats.ok) { const d = await rStats.json(); setSondageStats(d.stats || {}); }
    } catch (e) { console.error("Chargement sondages:", e); }
  };

  useEffect(() => { fetchSondages(); }, []);

  // Charger les PA advisors + le nom de l'onboarding lié à cette commerciale
  useEffect(() => {
    fetch(`${API_URL}/api/parcours/pa-advisors`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : { pa_advisors: [] })
      .then(d => setPaAdvisors(d.pa_advisors || []))
      .catch(() => {});
    fetch(`${API_URL}/api/admin/mon-onboarding`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.onboarding) { setMyOnboardingNom(`${d.onboarding.prenom} ${d.onboarding.nom}`); setMyOnboardingId(d.onboarding.id || null); } })
      .catch(() => {});
  }, []); // eslint-disable-line

  // Helper : retrouver la source d'un client par email
  const sourceClient = (email) => sondages.find(s => s.email === email)?.source || null;

  // Filtres
  const [periode, setPeriode]         = useState("mois");
  const [filtreOffre, setFiltreOffre] = useState("Toutes");

  const [searchQ, setSearchQ]         = useState("");

  // Filtres paiements
  const [paiementsSubTab, setPaiementsSubTab] = useState("manuel");
  const [paiSearchQ,    setPaiSearchQ]    = useState("");
  const [paiStatut,     setPaiStatut]     = useState("tous");
  const [paiMode,       setPaiMode]       = useState("tous");
  const [paiPeriode,    setPaiPeriode]    = useState("tous");
  const [paiPage,       setPaiPage]       = useState(1);
  const PAI_PAGE_SIZE = 25;

  // Filtres apprenants
  const [appSearchQ,    setAppSearchQ]    = useState("");
  const [appTypeCours,  setAppTypeCours]  = useState("tous");
  const [appTypeCoach,  setAppTypeCoach]  = useState("tous");
  const [appNiveau,     setAppNiveau]     = useState("tous");
  const [appPage,       setAppPage]       = useState(0);
  const APP_PER_PAGE = 20;

  // Routing post-finalisation
  const [showRoutingModal,  setShowRoutingModal]  = useState(false);
  const [routingTarget,     setRoutingTarget]     = useState(null); // { assignation, type_coaching }
  const [paAdvisors,        setPaAdvisors]        = useState([]);
  const [selectedPaId,      setSelectedPaId]      = useState(null);
  const [routingLoading,    setRoutingLoading]    = useState(false);
  const [myOnboardingNom,   setMyOnboardingNom]   = useState(null); // nom de l'onboarding lié
  const [myOnboardingId,    setMyOnboardingId]    = useState(null); // id utilisateurs de l'onboarding lié

  // Modales
  const [showInscriptionModal, setShowInscriptionModal] = useState(false);
  const [showPaiementModal, setShowPaiementModal]       = useState(false);
  const [showTestModal, setShowTestModal]               = useState(false);
  const [showPlanifModal, setShowPlanifModal]           = useState(false);
  const [showTestDetailModal, setShowTestDetailModal]   = useState(false);
  const [selectedTest, setSelectedTest]                 = useState(null);
  // Correction directement depuis le modal détail (audio + notation à côté, sans passer par
  // "Modifier") - resynchronisé à chaque nouveau test sélectionné.
  const [testCorrection, setTestCorrection]             = useState({ niveau:"A2", score:0, notes:"", speaking_notes:{} });
  const [savingCorrectionDetail, setSavingCorrectionDetail] = useState(false);
  useEffect(() => {
    if (selectedTest) {
      setTestCorrection({
        niveau: selectedTest.niveau || "A2",
        score: selectedTest.score || 0,
        notes: selectedTest.notes || "",
        speaking_notes: selectedTest.speaking_notes || {},
      });
    }
  }, [selectedTest]);

  const handleValiderCorrectionDepuisDetail = async () => {
    if (!selectedTest) return;
    setSavingCorrectionDetail(true);
    try {
      await handleSaveTest({ ...selectedTest, niveau: testCorrection.niveau, score: testCorrection.score, notes: testCorrection.notes, speaking_notes: testCorrection.speaking_notes });
    } finally {
      setSavingCorrectionDetail(false);
    }
  };

  const [showInscDetailModal, setShowInscDetailModal]     = useState(false);
  const [selectedInscription, setSelectedInscription]    = useState(null);
  const [editingItem, setEditingItem]                     = useState(null);
  const [showFinalisationModal, setShowFinalisationModal] = useState(false);
  const [finalisationTarget, setFinalisationTarget]       = useState(null);
  const [dbCheckResult,      setDbCheckResult]           = useState(null);
  const [dbCheckLoading,     setDbCheckLoading]          = useState(false);
  const [finClasseId,        setFinClasseId]             = useState("");
  const [finCoachId,         setFinCoachId]              = useState("");
  const [showPmtListModal,    setShowPmtListModal]         = useState(false);
  const [pmtListTarget,       setPmtListTarget]            = useState(null); // assignation dont on liste les paiements
  const [pmtListExpanded,     setPmtListExpanded]          = useState(null); // id du versement actuellement déplié
  const [showTpDetailModal,   setShowTpDetailModal]        = useState(false);
  const [tpDetailTarget,      setTpDetailTarget]           = useState(null); // transaction TouchPoint (paiements_touchpoint) affichée en détail
  const [showProgressionModal,setShowProgressionModal]    = useState(false);
  const [progressionTarget,   setProgressionTarget]        = useState(null); // assignation dont on affiche la progression
  const [progressionNotes,    setProgressionNotes]         = useState({});   // { [assignationId]: { seances, objectif, commentaire } }
  const [showDocDossierModal, setShowDocDossierModal]      = useState(false);
  const [docDossierTarget,    setDocDossierTarget]         = useState(null);  // assignation dont on gère les documents
  const [showSuiviModal,      setShowSuiviModal]           = useState(false);
  const [suiviTarget,         setSuiviTarget]              = useState(null);  // assignation dont on gère le suivi démarrage
  const [showAssignCentreModal,  setShowAssignCentreModal]  = useState(false);
  const [assignCentreTarget,     setAssignCentreTarget]      = useState(null); // assignation présentiel à assigner classe/coach

  /* LES COACHS DÈS QU'UN MODAL D'ASSIGNATION S'OUVRE, et pas seulement depuis l'onglet
     « Mes classes ». `fetchCoachs` n'était appelé qu'à l'arrivée sur cet onglet et dans
     « + Créer une classe » : ouvert depuis « Dossiers à réaffecter » ou « Prospects »,
     `AssignationCentreModal` recevait une liste VIDE et annonçait « 0 coach disponible »
     sur une classe qui a pourtant un coach.
     Placé ICI et non près des autres effets du haut : `assignCentreTarget` et
     `showTransfert` sont déclarés plus bas dans le composant, et un tableau de dépendances
     qui les cite avant leur déclaration est évalué AU RENDU — ReferenceError, écran blanc.
     On ne recharge pas si la liste est déjà là : ce serait un appel par ouverture pour rien. */
  useEffect(() => {
    if ((assignCentreTarget || showTransfert) && coachsList.length === 0) fetchCoachs();
  }, [assignCentreTarget, showTransfert]); // eslint-disable-line

  // Planification
  const [planifConfig, setPlanifConfig] = useState({ frequence:"hebdomadaire", emails:"", format:"pdf" });

  const formatMoney = (val) => Number(val || 0).toLocaleString("fr-FR") + " FCFA";
  const formatDate  = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day:"numeric", month:"short", year:"numeric" }) : "–";

  const isExpired = (d) => d && new Date(d) < new Date();

  // ── Alertes calculées ────────────────────────────────
  const alertes = useMemo(() => {
    const list = [];
    paiements.filter(p => p.statut === "en_attente").forEach(p =>
      list.push({ type:"danger", msg:`Paiement en attente - ${p.client}`, tab:"paiements" })
    );
    tests.filter(t => t.statut === "nouveau").forEach(t =>
      list.push({ type:"info", msg:`Nouveau test reçu - ${t.nom}`, tab:"tests" })
    );
    clientMessages.filter(m => m.statut === "nouveau").forEach(m =>
      list.push({ type:"info", msg:`Nouveau message - ${m.nom} : ${m.sujet || "sans sujet"}`, tab:"assignations" })
    );
    // Tranches de paiement en attente dont l'échéance approche (≤30 jours) ou est
    // dépassée - jusqu'ici cette info n'était visible que dans le dossier de
    // l'apprenant lui-même (onglet Paiement), jamais sur le Tableau de bord.
    const today0 = new Date(); today0.setHours(0,0,0,0);
    (assignations || []).forEach(a => {
      const tranches = a.plan_paiement?.tranches || [];
      tranches.filter(t => t.statut === "en_attente" && t.date_echeance).forEach(t => {
        const echeance = new Date(t.date_echeance); echeance.setHours(0,0,0,0);
        const joursRestants = Math.ceil((echeance - today0) / 86400000);
        if (joursRestants > 30) return;
        list.push({
          type: joursRestants < 0 ? "danger" : "warning",
          msg: joursRestants < 0
            ? `🚨 ${t.label} - ${a.prospect_nom} en retard de ${Math.abs(joursRestants)} jour(s)`
            : `⏰ ${t.label} - ${a.prospect_nom} échéance dans ${joursRestants} jour(s)`,
          tab: "assignations",
        });
      });
    });
    return list;
  }, [paiements, tests, clientMessages, assignations]);

  const { unreadTotal: totalNonLu } = useFirebaseChat();
  // Le badge « Messages » ne comptait que la messagerie privée : un message posté dans le
  // chat d'une classe n'apparaissait nulle part, et personne n'allait le chercher.
  const chatGroupeNonLus = useGroupesChatUnreadTotal(classes.map(g => g.id), profil?.id);
  // Les non-lus des groupes LIBRES : calculés ici pour que la pastille s'allume
  // même quand l'onglet n'est pas ouvert.
  const groupesLibresNonLus = useGroupesLibresNonLus(localStorage.getItem("admin_token"), profil?.id);
  const { notifications: toutesNotifsCM, nbNonLues: notifNonLuesCM, marquerLue: marquerNotifLueCM } = useNotifications(profil?.id);
  // Combien de dossiers de transfert attendent MON action - le badge de l'onglet.
  const dossiersAReaffecter = useCompteDemandesTransfert(authHeaders, "onboarding");
  const [banderoleFermeeCM, setBanderoleFermeeCM] = useState(false);
  const notifsBanderoleCM = toutesNotifsCM.filter(n => !n.lu).slice(0, 3);

  // ── Filtrage période ─────────────────────────────────
  const filterByPeriode = (data, dateField) => {
    const now = new Date();
    return data.filter(item => {
      const d = new Date(item[dateField]);
      if (periode === "semaine") { const w = new Date(); w.setDate(now.getDate()-7); return d >= w; }
      if (periode === "mois")    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      if (periode === "trimestre") return Math.floor(d.getMonth()/3) === Math.floor(now.getMonth()/3) && d.getFullYear() === now.getFullYear();
      if (periode === "annee")   return d.getFullYear() === now.getFullYear();
      return true;
    });
  };

  const [inscTypeFiltre, setInscTypeFiltre] = useState("tous"); // "tous" | "standard" | "prive"
  const [inscSearch,     setInscSearch]     = useState("");
  const [inscPage,       setInscPage]       = useState(1);
  const INSC_PER_PAGE = 10;
  // Inscriptions créées depuis ce modal vivent désormais dans assignations_parcours (même
  // base que Prospects) au lieu de inscriptions_adultes/inscriptions_enfants - reconstruites
  // ici à la forme attendue par le tableau. `inscriptions` (état séparé, fetchInscriptions)
  // ne garde que l'historique déjà créé avant ce changement, affiché en lecture seule.
  const inscriptionsDepuisAssignations = useMemo(() => {
    return assignations
      .filter(a => (a.source === "inscription_directe" || a.source === "formulaire_assistante_commerciale") && a.statut !== "annule")
      .map(a => ({
        id: a.id,
        _source: "assignation",
        type: a.profil_type === "enfant" ? "enfant" : "adulte",
        client: a.prospect_nom || "",
        contact: a.prospect_nom || "",
        email: a.prospect_email || "",
        telephone: a.prospect_telephone || "",
        offre: a.inscription_offre || "",
        niveauTest: a.niveau_client || "",
        montant: a.montant_paye || 0,
        dateDebut: a.date_debut_formation || "",
        date: a.created_at?.slice(0,10) || "",
        statut: a.statut_paiement === "confirme" ? "confirmée" : "en_attente",
        apprenantConverti: a.statut === "converti",
        // Une fois assigné (classe+coach en présentiel, onboarding en ligne, PA en privé),
        // le dossier n'est plus piloté depuis Inscriptions - seul Prospects parcours doit
        // permettre de le modifier davantage.
        assigne: ["centre_assigne", "onboarding_affecte", "pa_affecte"].includes(a.routing_statut),
        documents: a.documents_dossier || [],
        notesAdmin: a.notes || "",
        profil_type: a.profil_type || "adulte",
        nom_parent: a.nom_parent || "",
        telephone_parent: a.telephone_parent || "",
        email_parent: a.email_parent || "",
        prenom_enfant: a.prenom_enfant || "",
        nom_enfant: a.nom_enfant || "",
        age_enfant: a.age_enfant || "",
        niveau_classe_enfant: a.niveau_classe_enfant || "",
      }));
  }, [assignations]);
  const filteredInscriptions = useMemo(() => {
    const legacy = inscriptions.map(i => ({ ...i, _source: "legacy" }));
    let list = filterByPeriode([...inscriptionsDepuisAssignations, ...legacy], "date");
    if (inscSearch) list = list.filter(i => `${i.client} ${i.contact} ${i.email} ${i.offre}`.toLowerCase().includes(inscSearch.toLowerCase()));
    if (inscTypeFiltre === "prive")    list = list.filter(i => isOffreCoachingPrive(i.offre));
    if (inscTypeFiltre === "standard") list = list.filter(i => !isOffreCoachingPrive(i.offre));
    return list;
  }, [inscriptionsDepuisAssignations, inscriptions, periode, inscSearch, inscTypeFiltre]);
  const filteredTests        = useMemo(() => {
    const sorted = [...filterByPeriode(tests, "date")].sort((a, b) => new Date(b.date) - new Date(a.date));
    return sorted;
  }, [tests, periode]);


  const caPaiements = useMemo(() => paiements.reduce((s, p) => s + p.montantReçu, 0), [paiements]);

  // ── KPIs "Tableau de bord" : CA des 6 derniers mois + répartition des dossiers ──
  const DONUT_COLORS = ["#0891b2","#7c3aed","#16a34a","#dc2626","#94a3b8"];
  const STATUT_ASSIGNATION_META = {
    en_attente: { l:"En attente" }, en_cours: { l:"En cours" }, converti: { l:"Converti" },
    annule:     { l:"Annulé" },     archivé:  { l:"Archivé" },
  };
  const caParMois = useMemo(() => {
    const now = new Date();
    const mois = Array.from({ length:6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      return { key:`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`, label:d.toLocaleDateString("fr-FR",{ month:"short", year:"2-digit" }), ca:0 };
    });
    paiements.forEach(p => {
      const m = mois.find(x => x.key === (p.date||"").slice(0,7));
      if (m) m.ca += p.montantReçu || 0;
    });
    return mois;
  }, [paiements]);
  const maxCa = Math.max(1, ...caParMois.map(m=>m.ca));

  const repartitionDossiers = useMemo(() => {
    const counts = {};
    assignations.forEach(a => { counts[a.statut] = (counts[a.statut]||0) + 1; });
    return Object.entries(counts).filter(([,v]) => v > 0);
  }, [assignations]);
  const totalRepartitionDossiers = repartitionDossiers.reduce((s,[,v])=>s+v, 0);

  // ── Helper : sauvegarder un paiement lié à une assignation ──
  const savePmtForAssignation = async (insc, nom, email, tel, assignationId) => {
    const p = insc.pmtData;
    if (!p || !(p.montantReçu > 0)) return;
    try {
      await fetch(`${API_URL}/api/paiements/submit`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          client:          nom           || insc.client || "",
          email:           email         || "",
          telephone:       tel           || "",
          offre:           insc.offre    || "",
          inscription:     insc.offre    || "",
          mois_label:      "Paiement d'inscription",
          montant_du:      p.montantDu   || 0,
          montant_recu:    p.montantReçu || 0,
          date_paiement:   p.date,
          mode_paiement:   p.mode,
          statut:          p.statut,
          notes:           p.notes       || "",
          ref_transaction: p.refTransaction || null,
          assignation_id:  assignationId,
          preuve_image:    null,
        }),
      });
      toast.success("💳 Paiement enregistré ✓");
      await fetchPaiements();
    } catch { toast.error("Inscription créée mais paiement non enregistré"); }
  };

  // ── CRUD Inscriptions ────────────────────────────────
  // Ré-ouvre le formulaire d'inscription pré-rempli avec les coordonnées du même parent,
  // pour enchaîner l'inscription d'un autre enfant sans tout ressaisir. Chaque enfant
  // passe par sa propre soumission complète (donc son propre paiement, son propre dossier).
  const proposerAutreEnfant = async (insc) => {
    const veut = await confirm(
      "Voulez-vous inscrire un autre enfant de ce même parent ?",
      { titre: "Inscrire un autre enfant ?", confirmLabel: "Oui, inscrire un autre enfant", danger: false }
    );
    if (!veut) return;
    setEditingItem({
      client:"", contact:"",
      telephone:  insc.telephone  || "",
      email:      insc.email      || "",
      offre:      insc.offre      || "",
      niveauTest: "",
      dateDebut:  insc.dateDebut  || "",
      date: new Date().toISOString().slice(0,10),
      montant: insc.montant || 0,
      statut: "confirmée", apprenantConverti:false, documents:[], notesAdmin:"",
      profil_type: "enfant",
      nom_parent:       insc.nom_parent       || "",
      telephone_parent: insc.telephone_parent || "",
      email_parent:     insc.email_parent     || "",
      prenom_enfant:"", nom_enfant:"", age_enfant:"", niveau_classe_enfant:"", email_enfant:"",
      type_client: insc.type_client || "particulier",
    });
    setShowInscriptionModal(true);
  };

  const handleSaveInscription = async (insc) => {
    const isEnfant = insc.profil_type === "enfant";
    // Pour un enfant, utiliser les coordonnées parent si le champ principal est vide
    const effectiveTel   = insc.telephone   || (isEnfant ? insc.telephone_parent : "") || "";
    const effectiveEmail = insc.email       || (isEnfant ? insc.email_parent     : "") || "";
    const effectiveNom   = isEnfant && (insc.prenom_enfant || insc.nom_enfant)
      ? `${insc.prenom_enfant || ""} ${insc.nom_enfant || ""}`.trim()
      : insc.client || insc.contact || "";

    if (insc.id) {
      // Historique pré-migration (inscriptions_adultes/inscriptions_enfants) - affiché en
      // lecture seule dans le tableau, ce cas ne devrait plus être atteignable depuis l'UI.
      if (insc._source === "legacy") {
        setInscriptions(inscriptions.map(i=>i.id===insc.id?insc:i));
        toast.success("Inscription modifiée");
        setShowInscriptionModal(false); setEditingItem(null);
        return;
      }
      // Inscription vivant dans assignations_parcours → vraie sauvegarde serveur (avant, cette
      // branche ne faisait qu'une mise à jour locale, jamais persistée côté backend).
      try {
        const r = await fetch(`${API_URL}/api/parcours/assignations/${insc.id}`, {
          method: "PATCH",
          headers: authHeaders(),
          body: JSON.stringify({
            inscription_offre:    insc.offre        || null,
            niveau_client:        insc.niveauTest   || null,
            objectif_client:      (insc.objectif_client || "").trim() || null,
            date_debut_formation: insc.dateDebut    || null,
            montant_paye:         insc.montant      || null,
            statut_paiement:      insc.statut === "confirmée" ? "confirme" : "en_attente",
            notes:                insc.notesAdmin   || null,
            profil_type:          insc.profil_type  || "adulte",
            nom_parent:           isEnfant ? (insc.nom_parent       || null) : null,
            telephone_parent:     isEnfant ? (insc.telephone_parent || null) : null,
            email_parent:         isEnfant ? (insc.email_parent     || null) : null,
          }),
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Erreur serveur");
        await fetchAssignations();
        toast.success("Inscription modifiée");
      } catch (e) { toast.error(e.message || "Erreur lors de la modification"); }
      setShowInscriptionModal(false); setEditingItem(null);
      return;
    }

    // Ferme le modal tout de suite - la création de l'assignation (routing + paiement +
    // test) continue en arrière-plan dans les blocs suivants.
    setShowInscriptionModal(false); setEditingItem(null);

    // Inscription standard (non coaching privé) → routing selon type de commerciale
    if (!insc.id && !insc.coachingPriveData) {
      const commonPayload = {
        prospect_nom:         effectiveNom,
        prospect_email:       effectiveEmail,
        prospect_telephone:   effectiveTel,
        source:               "inscription_directe",
        inscription_offre:    insc.offre        || null,
        formation_souhaitee:  insc.offre        || null,
        niveau_client:        insc.niveauTest   || null,
        objectif_client:      (insc.objectif_client || "").trim() || null,
        date_debut_formation: insc.dateDebut    || null,
        montant_paye:         insc.montant      || null,
        /* Transmis SEULEMENT s'il a été vérifié dans le formulaire. Le serveur le revalide
           de toute façon (`parcours.js` : expiration, quota, catégorie d'offre, centre) et
           calcule lui-même la remise - on ne lui envoie donc pas un code qu'on sait douteux,
           et on n'applique jamais une réduction sur la seule foi de l'écran. */
        code_promo:           insc.code_promo_valide ? (insc.code_promo || null) : null,
        // Le tarif du reste du contrat, s'il a été négocié. Le serveur le range dans
        // `plan_paiement` avec le nom de qui l'a accordé.
        mensualite_negociee:  Number(insc.mensualite_negociee) > 0 ? Number(insc.mensualite_negociee) : null,
        /* La formule retenue, EN ENTIER. J'envoyais d'abord la seule clé technique
           (« 3x_semaine ») : illisible chez l'onboarding, et sans les horaires dont elle a
           besoin pour choisir la classe. */
        ...(insc.formule_groupe_detail ? {
          pack_coaching:       insc.formule_groupe_detail.label,
          formation_souhaitee: `${insc.offre || "Coaching de groupe"} - ${insc.formule_groupe_detail.label}`,
          cout_formation:      insc.formule_groupe_detail.tarif,
          formule_groupe:      insc.formule_groupe_detail,
        } : {}),
        statut_paiement:      insc.statut === "confirmée" ? "confirme" : "en_attente",
        profil_type:          insc.profil_type  || "adulte",
        nom_parent:           isEnfant ? (insc.nom_parent           || null) : null,
        telephone_parent:     isEnfant ? (insc.telephone_parent     || null) : null,
        email_parent:         isEnfant ? (insc.email_parent         || null) : null,
        prenom_enfant:        isEnfant ? (insc.prenom_enfant        || null) : null,
        nom_enfant:           isEnfant ? (insc.nom_enfant           || null) : null,
        age_enfant:           isEnfant ? (insc.age_enfant           || null) : null,
        niveau_classe_enfant: isEnfant ? (insc.niveau_classe_enfant || null) : null,
        // Adresse propre a l'enfant. Facultative : sans elle, un identifiant est genere
        // a la creation de l'acces, comme avant.
        email_enfant:         isEnfant ? (insc.email_enfant         || null) : null,
      };

      // Commerciale EN LIGNE - routing_statut n'est posé que si le dossier était complet au
      // moment de l'enregistrement (insc.envoyerOnboardingImmediat, posé par le bouton
      // "Vérifier & envoyer à l'Onboarding" quand offre/date/niveau/contact sont tous
      // renseignés). Sinon le dossier reste en attente, visible/complétable puis transmis
      // manuellement depuis Prospects parcours - sans quoi Onboarding découvrait le dossier
      // avant même que la commerciale ait fini de le vérifier.
      if (assistanteTypeCours === "en_ligne" && assistanteId) {
        try {
          const rAss = await fetch(`${API_URL}/api/parcours/assignation`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({
              ...commonPayload,
              assistante_id:  assistanteId,
              type_cours:     "en_ligne",
              type_coaching:  "standard",
              statut:         "en_attente",
              ...(insc.envoyerOnboardingImmediat ? { routing_statut: "onboarding_en_attente" } : {}),
            }),
          });
          if (rAss.ok && insc.pmtData) {
            const dAss = await rAss.json();
            const newAssId = dAss.assignation?.id;
            if (newAssId) await savePmtForAssignation(insc, effectiveNom, effectiveEmail, effectiveTel, newAssId);
          }
          await fetchAssignations();
          toast.success(insc.envoyerOnboardingImmediat
            ? `✅ Dossier transmis à l'Onboarding${myOnboardingNom ? ` (${myOnboardingNom})` : ""}`
            : "✅ Inscription enregistrée - vérifiez le dossier puis transmettez-le à l'Onboarding depuis Prospects parcours", { duration: 5000 });
        } catch { /* silencieux */ }
      }

      // Commerciale CENTRE (présentiel) → visible dans Prospects Parcours pour assigner à une classe
      if (assistanteTypeCours === "presentiel" && assistanteId) {
        try {
          const rAss = await fetch(`${API_URL}/api/parcours/assignation`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({
              ...commonPayload,
              assistante_id: assistanteId,
              type_cours:    "presentiel",
              type_coaching: "standard",
              centre_id:     assistanteCentreId || null,
              statut:        "en_attente",
            }),
          });
          if (rAss.ok && insc.pmtData) {
            const dAss = await rAss.json();
            const newAssId = dAss.assignation?.id;
            if (newAssId) await savePmtForAssignation(insc, effectiveNom, effectiveEmail, effectiveTel, newAssId);
          }
          await fetchAssignations();
          toast.success("✅ Inscription créée - visible dans Prospects Parcours", { duration: 4000 });
        } catch { /* silencieux */ }
      }
    }

    // Si coaching privé → transmettre au PA Advisor via parcours/assignation
    if (!insc.id && insc.coachingPriveData) {
      try {
        const cp = insc.coachingPriveData;
        const isEnLigneCp = assistanteTypeCours === "en_ligne";
        const rAss = await fetch(`${API_URL}/api/parcours/assignation`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            prospect_nom:         effectiveNom,
            prospect_email:       effectiveEmail,
            prospect_telephone:   effectiveTel,
            // L'assistante en ligne ne gère jamais cabinet/domicile - son canal est
            // toujours "en_ligne", peu importe cp.lieu (champ non pertinent pour elle).
            type_cours:           isEnLigneCp ? "en_ligne" : (cp.lieu === "domicile" ? "domicile" : "presentiel"),
            type_coaching:        "prive",
            source:               "formulaire_assistante_commerciale",
            // Attribution manquante jusqu'ici : sans assistante_id, ce dossier partait
            // anonymement dans le pool PA au lieu d'être rattaché à l'assistante qui le
            // crée - cassant tout le pipeline (transmission, lecture seule PA, etc.).
            assistante_id:        assistanteId || null,
            centre_id:            isEnLigneCp ? null : (assistanteCentreId || null),
            inscription_offre:    insc.offre || "Coaching Privé",
            pack_coaching:        cp.type_coaching,
            lieu_coaching:        isEnLigneCp ? "en_ligne" : cp.lieu,
            formation_souhaitee:  insc.offre || "Coaching Privé",
            centre_coaching:      (!isEnLigneCp && assistanteTypeCours === "presentiel" && cp.lieu === "cabinet") ? (assistanteCentreId || null) : (isEnLigneCp ? null : (cp.centre || null)),
            adresse_coaching:     isEnLigneCp ? null : (cp.adresse || null),
            disponibilites:       cp.dispos?.length ? { horaires: cp.dispos } : null,
            /* Les autres personnes de la famille. Envoyées au serveur, qui les nettoie et les
               range dans `suivi_demarrage.membres_famille` — un seul dossier pour tout le
               famille, donc un seul montant et un seul échéancier. Les lignes laissées vides
               sont écartées ici comme côté serveur : une ligne ajoutée puis abandonnée ne
               doit pas compter comme un apprenant. */
            membres_famille:        (cp.membres_famille || []).filter(m => (m?.prenom || "").trim() || (m?.nom || "").trim()),
            notes:                cp.notes_pa || null,
            niveau_client:        insc.niveauTest || null,
            objectif_client:      (insc.objectif_client || "").trim() || null,
            date_debut_formation: cp.date_debut   || null,
            commercial_id:        profil?.id      || null,
            /* SEULE la mensualité est transmise ici. En coaching privé, le champ « Montant »
               porte le tarif de la FORMULE - un montant mensuel, recalculé à chaque
               changement de formule ou de lieu - et non ce qui est encaissé le jour même.
               L'envoyer comme `montant_paye` ferait passer douze mois de contrat pour un
               versement unique, et le code promo s'appliquerait sur cette somme fausse.
               L'encaissement réel du privé passe par `savePmtForAssignation`, juste après.
               Le code promo, lui, est bien transmis : faute de `montant_paye`, le serveur
               l'impute sur la PREMIÈRE MENSUALITÉ, une seule fois. */
            mensualite_negociee:  Number(insc.mensualite_negociee) > 0 ? Number(insc.mensualite_negociee) : null,
            code_promo:           insc.code_promo_valide ? (insc.code_promo || null) : null,
            profil_type:          insc.profil_type   || "adulte",
            nom_parent:           isEnfant ? (insc.nom_parent           || null) : null,
            telephone_parent:     isEnfant ? (insc.telephone_parent     || null) : null,
            email_parent:         isEnfant ? (insc.email_parent         || null) : null,
            prenom_enfant:        isEnfant ? (insc.prenom_enfant        || null) : null,
            nom_enfant:           isEnfant ? (insc.nom_enfant           || null) : null,
            age_enfant:           isEnfant ? (insc.age_enfant           || null) : null,
            niveau_classe_enfant: isEnfant ? (insc.niveau_classe_enfant || null) : null,
            email_enfant:         isEnfant ? (insc.email_enfant         || null) : null,
          }),
        });
        if (rAss.ok && insc.pmtData) {
          const dAss = await rAss.json();
          const newAssId = dAss.assignation?.id;
          if (newAssId) await savePmtForAssignation(insc, effectiveNom, effectiveEmail, effectiveTel, newAssId);
        }
        await fetchAssignations();
        toast.success("✅ Dossier coaching privé enregistré - complétez-le puis transmettez-le au PA depuis Prospects parcours", { duration: 5000 });
      } catch { toast.error("Erreur lors de l'enregistrement du dossier"); }
    }

    // Si un résultat de test a été saisi, le sauvegarder via l'API
    if (!insc.id && insc.testScore && insc.testNiveau) {
      try {
        await fetch(`${API_URL}/api/level-test/submit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user: {
              fullname:      effectiveNom   || insc.client || "",
              email:         effectiveEmail || null,
              phone:         effectiveTel   || null,
              profile:       "Particulier",
              consent:       true,
              commercial_id: profil?.id || null,
            },
            test: {
              level:              insc.testNiveau,
              score:              Number(insc.testScore) || 0,
              points_earned:      Number(insc.testScore) || 0,
              points_total:       100,
              correct_answers:    0,
              total_questions:    0,
              time_taken_seconds: 0,
              answers_details:    [],
              audio_answers:      {},
              by_category:        {},
              by_cefr:            {},
              notes_oral:         insc.testNotes || null,
              source:             "oral",
            },
            submitted_at: insc.testDate ? new Date(insc.testDate).toISOString() : new Date().toISOString(),
          }),
        });
        toast.success("Test de niveau enregistré ✅");
        fetchTests();
      } catch { /* non bloquant */ }
    }

    // Enchaînement : proposer d'inscrire un autre enfant du même parent
    if (!insc.id && isEnfant) {
      await proposerAutreEnfant(insc);
    }
  };
  const handleDeleteInscription = async (insc) => {
    // Inscription vivant dans assignations_parcours → pas de suppression physique, on annule
    // le dossier comme partout ailleurs dans Prospects (même table, même règles).
    if (insc._source === "assignation") {
      await updateStatutAssignation(insc.id, "annule"); // gère déjà son propre toast + rafraîchissement local
      return;
    }
    const prevInscriptions = inscriptions;
    setInscriptions(inscriptions.filter(i=>i.id!==insc.id));
    try {
      const res = await fetch(`${API_URL}/api/inscriptions/${insc.type||"adulte"}/${insc.id}`, {
        method: "DELETE", headers: authHeaders(),
      });
      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).error || "Erreur suppression");
      toast.success("Inscription supprimée");
    } catch (e) {
      setInscriptions(prevInscriptions); // rollback si la suppression a échoué côté serveur
      toast.error(e.message || "Erreur lors de la suppression");
    }
  };

  // ── CRUD Paiements ───────────────────────────────────
  const handleSavePaiement = async (p) => {
    try {
      // Le formulaire ne propose pas de champ "Statut" - on le déduit toujours des montants
      // pour éviter qu'un paiement intégralement réglé reste affiché "En attente" (oubli de saisie).
      const totalEncaisseCumul = (p.dejaReçu || 0) + (p.montantReçu || 0);
      const statutAuto = totalEncaisseCumul <= 0
        ? "en_attente"
        : (p.montantDu > 0 && totalEncaisseCumul < p.montantDu)
        ? "partiel"
        : "paye";

      const payload = {
        client:          p.client,
        email:           p.email          || "",
        telephone:       p.telephone      || "",
        offre:           p.offre          || "",
        inscription:     p.offre ? `${p.offre}${p.inscription ? " · " + p.inscription : ""}` : (p.inscription || ""),
        mois_label:      p.moisLabel      || "",
        montant_du:      p.montantDu      || 0,
        montant_recu:    p.montantReçu    || 0,
        date_paiement:   p.date,
        mode_paiement:   p.mode,
        statut:          statutAuto,
        notes:           p.notes          || "",
        ref_transaction: p.refTransaction || null,
        assignation_id:  p.assignationId  || null,
        preuve_image:    p.preuveImage    || null,
      };

      if (p.id) {
        const res = await fetch(`${API_URL}/api/paiements/${p.id}`, {
          method:"PATCH", headers: authHeaders(), body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error((await res.json().catch(()=>({}))).error || "");
        toast.success("Paiement modifié ✓");
      } else {
        const res = await fetch(`${API_URL}/api/paiements/submit`, {
          method:"POST", headers: authHeaders(), body: JSON.stringify(payload),
        });
        // Ex: dossier coaching privé pas encore validé par le PA - le message précis du
        // backend (409) est bien plus utile ici que le générique "Erreur" ci-dessous.
        if (!res.ok) throw new Error((await res.json().catch(()=>({}))).error || "");
        // Coaching privé : le paiement fait passer le dossier en "paiement_recu" côté
        // backend, qui notifie le PA pour qu'il vérifie le justificatif - sans ce toast
        // précis, l'assistante n'avait aucune indication que quelque chose se passait
        // ensuite après l'enregistrement (impression que "ça n'a pas de suite").
        if (p.packCoachingLabel) {
          toast.success("💳 Paiement enregistré - dossier transmis au PA pour validation ✓", { duration: 5000 });
        } else {
          toast.success("Paiement enregistré ✓");
        }
      }
      await fetchPaiements();
      // Mettre à jour date_debut_formation sur l'assignation si renseignée
      if (p.assignationId && p.dateDebutFormation) {
        try {
          const r = await fetch(`${API_URL}/api/parcours/assignations/${p.assignationId}`, {
            method:"PATCH", headers: authHeaders(),
            body: JSON.stringify({ date_debut_formation: p.dateDebutFormation }),
          });
          if (r.ok) {
            setAssignations(prev => prev.map(a => a.id === p.assignationId ? { ...a, date_debut_formation: p.dateDebutFormation } : a));
            setApprenantModal(prev => prev?.id === p.assignationId ? { ...prev, date_debut_formation: p.dateDebutFormation } : prev);
          }
        } catch { /* non bloquant */ }
      }
      // Si tranche legacy → la marquer comme payée
      if (tranchePayTarget) {
        const { assignationId, trancheId, plan: tp, tranches: tt } = tranchePayTarget;
        const newTranches = tt.map(tr => tr.id === trancheId
          ? { ...tr, statut: "payé", date_paiement_effectif: p.date || new Date().toISOString().slice(0, 10) }
          : tr
        );
        await handleSavePlanPaiement(assignationId, { ...tp, tranches: newTranches });
        setTranchePayTarget(null);
      }
      // Si paiement mensuel → mettre à jour le mois dans plan_paiement
      if (moisPayTarget) {
        const { assignationId, moisId, trancheId, plan: mp } = moisPayTarget;
        const newMois = (mp.mois_paiements || []).map(m => {
          if (m.id !== moisId) return m;
          let newTranches;
          if (trancheId) {
            // Paiement d'une tranche existante
            newTranches = (m.tranches||[]).map(t => t.id === trancheId
              ? { ...t, statut:"paye", date_paiement: p.date, mode: p.mode, ref: p.refTransaction||"" }
              : t
            );
          } else {
            // Nouveau versement pour ce mois
            const totalDejaPayé = (m.tranches||[]).filter(t=>t.statut==="paye").reduce((s,t)=>s+(t.montant||0),0);
            const newT = { id:`t_${moisId}_${Date.now()}`, montant: p.montantReçu||0, statut:"paye", date_paiement: p.date, mode: p.mode, ref: p.refTransaction||"" };
            newTranches = [...(m.tranches||[]), newT];
          }
          const totalPaye = newTranches.filter(t=>t.statut==="paye").reduce((s,t)=>s+(t.montant||0),0);
          return { ...m, tranches: newTranches, statut: totalPaye >= m.montant ? "paye" : totalPaye > 0 ? "partiel" : "en_attente" };
        });
        // Pour plan ouvert : générer le mois suivant si le dernier mois vient d'être payé
        let finalMois = newMois;
        if (!mp.date_fin_formation) {
          const lastMois = newMois[newMois.length - 1];
          if (lastMois?.statut === "paye") {
            const nextDate = new Date(lastMois.periode + "-01");
            nextDate.setMonth(nextDate.getMonth() + 1);
            const nextPeriode = `${nextDate.getFullYear()}-${String(nextDate.getMonth()+1).padStart(2,"0")}`;
            const alreadyExists = newMois.some(m => m.periode === nextPeriode);
            if (!alreadyExists) {
              finalMois = [...newMois, { id:`m_${nextPeriode}`, periode:nextPeriode, label:getMoisLabel(nextPeriode), montant:mp.mensualite, statut:"en_attente", tranches:[] }];
            }
          }
        }
        await handleSavePlanPaiement(assignationId, { ...mp, mois_paiements: finalMois });
        setMoisPayTarget(null);
      }
    } catch (e) {
      toast.error(e?.message || "Erreur lors de l'enregistrement du paiement");
    }
    setShowPaiementModal(false); setEditingItem(null);
  };

  // ── Finaliser l'inscription d'un prospect ────────────
  const runDbCheck = async (assignation) => {
    if (!assignation?.id) return;
    setDbCheckLoading(true);
    setDbCheckResult(null);
    try {
      const [rAss, rPmt] = await Promise.all([
        fetch(`${API_URL}/api/parcours/assignations?prospect_email=${encodeURIComponent(assignation.prospect_email || "")}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/paiements/assignation/${assignation.id}`, { headers: authHeaders() }),
      ]);
      const dAss = rAss.ok ? await rAss.json() : { assignations: [] };
      const dPmt = rPmt.ok ? await rPmt.json() : { paiements: [] };
      const dbAss = (dAss.assignations || [])[0] || null;
      setDbCheckResult({ dbAss, dbPaiements: dPmt.paiements || [] });
    } catch (e) {
      setDbCheckResult({ error: e.message });
    } finally {
      setDbCheckLoading(false);
    }
  };

  const handleFinaliserInscription = async (a, formData) => {
    // 1. Créer l'inscription
    const newInsc = {
      ...formData,
      id:         Math.max(...inscriptions.map(i => i.id), 0) + 1,
      client:     a.prospect_nom       || "",
      contact:    a.prospect_nom       || "",
      telephone:  a.prospect_telephone || "",
      email:      a.prospect_email     || "",
      apprenantConverti: false,
    };
    setInscriptions(prev => [newInsc, ...prev]);

    // 2. Sauvegarder tous les champs de formation dans assignations_parcours
    const pmtsFiltered = paiements.filter(p => p.assignationId === a.id);
    const totalReçu    = pmtsFiltered.reduce((s, p) => s + (p.montantReçu || 0), 0);
    const totalDu      = pmtsFiltered.reduce((s, p) => s + (p.montantDu   || 0), 0);
    const reste        = totalDu - totalReçu;
    const modePaiement = pmtsFiltered.find(p => p.mode)?.mode || null;
    const inscriptionPayload = {
      inscription_offre:    formData.offre || a.inscription_offre || "",
      niveau_client:        formData.niveauTest || "",
      montant_paye:         totalReçu           || 0,
      statut_paiement:      reste <= 0 && totalDu > 0 ? "confirme" : totalReçu > 0 ? "partiel" : "en_attente",
      mode_paiement:        modePaiement,
      notes:                formData.notesAdmin || "",
      date_debut_formation: formData.dateDebut  || null,
      /* La formule voyage avec le dossier : c'est elle que l'Onboarding lit pour affecter
         la classe. On FUSIONNE dans `suivi_demarrage` (le PATCH écrase ce champ en entier)
         afin de ne pas perdre le reste du dossier au passage. Absente du formulaire, la
         formule déjà enregistrée reste intacte puisqu'on n'envoie alors rien. */
      ...(formData.formule_groupe_detail ? {
        suivi_demarrage: {
          ...(a.suivi_demarrage || {}),
          pack_coaching:       formData.formule_groupe_detail.label,
          cout_formation:      formData.formule_groupe_detail.tarif,
          formation_souhaitee: `${formData.offre || a.inscription_offre || "Coaching de groupe"} - ${formData.formule_groupe_detail.label}`,
          formule_groupe:      formData.formule_groupe_detail,
        },
      } : {}),
    };
    try {
      const patchRes = await fetch(`${API_URL}/api/parcours/assignations/${a.id}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify(inscriptionPayload),
      });
      if (!patchRes.ok) {
        const errBody = await patchRes.json().catch(() => ({}));
        console.warn("⚠️ PATCH inscription partiel:", errBody.error || patchRes.status);
        toast("⚠️ Données partiellement enregistrées - réessayez si l'offre/niveau n'apparaissent pas chez l'onboarding", { duration:5000 });
      }
      setAssignations(prev => prev.map(ass => ass.id === a.id ? { ...ass, ...inscriptionPayload } : ass));
      setApprenantModal(prev => prev?.id === a.id ? { ...prev, ...inscriptionPayload } : prev);
    } catch { /* non bloquant */ }

    // 3. Fusionner les docs du formulaire dans documents_dossier
    if (formData.documents?.length > 0) {
      const existingDocs = a.documents_dossier || [];
      const mergedDocs   = [...existingDocs, ...formData.documents];
      try {
        const r = await fetch(`${API_URL}/api/parcours/assignations/${a.id}`, {
          method: "PATCH", headers: authHeaders(),
          body: JSON.stringify({ documents_dossier: mergedDocs }),
        });
        if (r.ok) {
          setAssignations(prev => prev.map(ass => ass.id === a.id ? { ...ass, documents_dossier: mergedDocs } : ass));
        }
      } catch { /* non bloquant */ }
    }

    // 4. Marquer l'assignation comme convertie
    await updateStatutAssignation(a.id, "converti");

    setShowFinalisationModal(false);
    setFinalisationTarget(null);
    setDbCheckResult(null);

    // Routing vers PA Advisor (cours privé) ou Onboarding (groupe / en ligne).
    // Objet à jour avec les données du formulaire pour lire inscription_offre et
    // déterminer correctement la destination.
    const aRouting = { ...a, ...inscriptionPayload };
    const isPrivate = aRouting.type_coaching === "prive" || isOffreCoachingPrive(aRouting.inscription_offre || "");

    if (isPrivate) {
      // Cours privé : un vrai choix reste à faire (quel PA Advisor ?) → 2e écran nécessaire.
      toast.success(`✅ Dossier de ${a.prospect_nom} transmis !`, { duration:3000 });
      setRoutingTarget(aRouting);
      setSelectedPaId(null);
      setShowRoutingModal(true);
    } else {
      // Groupe/en ligne : aucune décision à prendre dans le 2e écran (juste un texte
      // informatif + un bouton de confirmation) - le fusionner évite qu'un dossier
      // reste bloqué "converti" mais jamais transmis si ce second clic est manqué/fermé.
      try {
        const onboardingPatch = { routing_statut: "onboarding_en_attente" };
        if (myOnboardingId) onboardingPatch.onboarding_id = myOnboardingId;
        await fetch(`${API_URL}/api/parcours/assignations/${a.id}`, {
          method: "PATCH", headers: authHeaders(),
          body: JSON.stringify(onboardingPatch),
        });
        toast.success(`✅ ${a.prospect_nom} transmis à l'Onboarding${myOnboardingNom ? ` (${myOnboardingNom})` : ""}`, { duration:4000 });
      } catch {
        toast.error("Dossier finalisé mais transmission à l'Onboarding échouée - réessayez depuis la fiche.");
      }
      setActiveTab("apprenants");
    }
  };

  // ── Routing post-finalisation ────────────────────────
  const handleConfirmRouting = async () => {
    if (routingLoading) return;
    const a = routingTarget;
    if (!a) return;
    const isPrivate = a.type_coaching === "prive" || isOffreCoachingPrive(a.inscription_offre || "");
    setRoutingLoading(true);
    try {
      if (isPrivate) {
        if (!selectedPaId) { toast.error("Veuillez sélectionner un PA Advisor"); setRoutingLoading(false); return; }
        await fetch(`${API_URL}/api/parcours/assignations/${a.id}`, {
          method: "PATCH", headers: authHeaders(),
          body: JSON.stringify({ pa_advisor_id: selectedPaId, routing_statut: "pa_en_attente" }),
        });
        const pa = paAdvisors.find(p => p.id === selectedPaId);
        toast.success(`✅ Transmis à ${pa ? `${pa.prenom} ${pa.nom}` : "le PA Advisor"} pour affectation du coach`, { duration:4000 });
      } else {
        const onboardingPatch = { routing_statut: "onboarding_en_attente" };
        if (myOnboardingId) onboardingPatch.onboarding_id = myOnboardingId;
        await fetch(`${API_URL}/api/parcours/assignations/${a.id}`, {
          method: "PATCH", headers: authHeaders(),
          body: JSON.stringify(onboardingPatch),
        });
        toast.success(`✅ ${a.prospect_nom} transmis à l'Onboarding${myOnboardingNom ? ` (${myOnboardingNom})` : ""}`, { duration:4000 });
      }
      setShowRoutingModal(false);
      setRoutingTarget(null);
      setSelectedPaId(null);
      setActiveTab("apprenants");
    } catch {
      toast.error("Erreur lors de la transmission");
    } finally {
      setRoutingLoading(false);
    }
  };

  // ── CRUD Tests ───────────────────────────────────────
  const handleSaveTest = async (t) => {
    if (t.id) {
      // Correction d'un test Writing/Speaking → appel API PATCH
      try {
        const res = await fetch(`${API_URL}/api/level-test/${t.id}/corriger`, {
          method: "PATCH",
          headers: authHeaders(),
          body: JSON.stringify({ level: t.niveau, score: t.score, notes_oral: t.notes, speaking_notes: t.speaking_notes || {} }),
        });
        const data = await res.json();
        if (!res.ok) { toast.error(data.error || "Erreur lors de la correction"); return; }
        toast.success("Correction enregistrée ✅");
        setShowTestModal(false); setEditingItem(null);
        await fetchTests();
        if (data.result) {
          const mapped = { ...t, niveau: data.result.level, score: data.result.score, correction_statut: "corrige" };
          setSelectedTest(mapped); setShowTestDetailModal(true);
        }
      } catch { toast.error("Erreur réseau"); }
      return;
    }
    // Nouveau test oral → enregistrer en DB avec commercial_id
    try {
      const res = await fetch(`${API_URL}/api/level-test/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }, // pas d'auth requise sur /submit
        body: JSON.stringify({
          user: {
            fullname:      `${t.prenom || ""} ${t.nom || ""}`.trim() || t.nom,
            email:         t.email     || null,
            phone:         t.telephone || null,
            profile:       t.profil    || "Particulier",
            consent:       true,
            commercial_id: profil?.id  || null,
            centre_id:     t.centre_id || null,
          },
          test: {
            level:              t.niveau,
            score:              Number(t.score) || 0,
            points_earned:      Number(t.score) || 0,
            points_total:       100,
            correct_answers:    0,
            total_questions:    0,
            time_taken_seconds: 0,
            answers_details:    [],
            audio_answers:      {},
            by_category:        {},
            by_cefr:            {},
            notes_oral:         t.notes || null,
            source:             "oral",
          },
          // Rattache le résultat à CE dossier précis (jamais par email) - indispensable quand
          // parent et enfant partagent le même email/téléphone.
          assignation_id: t.assignation_id || null,
          submitted_at: t.date ? new Date(t.date).toISOString() : new Date().toISOString(),
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.error || "Erreur lors de l'enregistrement"); return; }
      toast.success("Test oral enregistré ✅");
      setShowTestModal(false); setEditingItem(null);
      // Rafraîchir la liste puis ouvrir le détail
      await fetchTests();
      if (data.result) {
        const mapped = {
          id:              data.result.id,
          nom:             (data.result.fullname || "").split(" ")[0] || t.nom,
          prenom:          (data.result.fullname || "").split(" ").slice(1).join(" ") || t.prenom,
          email:           data.result.email        || "",
          telephone:       data.result.phone        || "",
          niveau:          data.result.level,
          score:           data.result.score        || 0,
          date:            data.result.submitted_at ? data.result.submitted_at.split("T")[0] : "",
          commercial_id:   data.result.commercial_id || null,
          source:          "oral",
          notes:           data.result.notes_oral   || t.notes || "",
          by_category:     {},
          by_cefr:         {},
          answers_details: [],
          audio_answers:   {},
        };
        setSelectedTest(mapped);
        setShowTestDetailModal(true);
      }
    } catch (err) {
      toast.error("Erreur réseau lors de l'enregistrement");
    }
  };

  // ── Changer le statut de traitement d'un test (nouveau/contacté/converti/archivé)
  const changerStatutTest = async (testId, statut) => {
    try {
      const res = await fetch(`${API_URL}/api/level-test/${testId}/statut`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify({ statut }),
      });
      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).error || "Erreur");
      setTests(prev => prev.map(t => t.id === testId ? { ...t, statut } : t));
      setSelectedTest(prev => prev && prev.id === testId ? { ...prev, statut } : prev);
      toast.success("Statut mis à jour ✓");
    } catch (e) {
      toast.error(e.message || "Erreur lors de la mise à jour du statut");
    }
  };

  // ── Conversion Inscription → Apprenant BET ──────────
  const convertirEnApprenant = (insc) => {
    if (insc.statut !== "confirmée") { toast.error("Paiement non confirmé - impossible de convertir"); return; }
    const pmt = paiements.find(p => p.client === insc.client && p.statut === "paye");
    if (!pmt) { toast.error("Aucun paiement 'Reçu' trouvé pour ce client - validez d'abord le paiement"); return; }
    setInscriptions(prev => prev.map(i => i.id === insc.id ? { ...i, apprenantConverti:true } : i));
    toast.success(`✅ ${insc.client} converti(e) en Apprenant BET - dossier transféré au Responsable pédagogique`);
    toast(`📋 Responsable pédagogique notifié - dossier ${insc.client} (${insc.offre}) reçu`, { icon:"📨", duration:4000 });
  };

  // ── Générer reçu de paiement ─────────────────────────
  const genererRecu = (p) => {
    const dateStr = p.date ? new Date(p.date).toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" }) : new Date().toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" });
    const now = new Date().toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" });
    const num = `BET-REC-${String(p.id).padStart(4,"0")}-${new Date().getFullYear()}`;
    const reste = (p.montantDu || 0) - (p.montantReçu || 0);
    const isComplet = p.statut === "paye" || reste <= 0;
    const w = window.open("","_blank","width=720,height=900");
    w.document.write(`<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Reçu ${num}</title>
    <style>
      *{box-sizing:border-box}
      body{font-family:Arial,sans-serif;margin:0;padding:40px;color:#1e293b;background:#fff}
      .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #0891b2;padding-bottom:20px;margin-bottom:28px}
      .logo{font-size:28px;font-weight:900;color:#0891b2;letter-spacing:-1pxdisplay:flex;align-items:center;gap:11px}.logo::before{content:'';width:40px;height:40px;flex-shrink:0;background:#fff url('https://res.cloudinary.com/nvp7nxqp/image/upload/v1788473005/bet/branding/bet-logo.png') center / 28px 28px no-repeat;border-radius:9px;box-shadow:0 2px 7px rgba(0,0,0,.14)}
      .logo span{color:#0f172a}
      .logo-sub{font-size:11px;color:#6b7280;margin-top:4px;margin-left:51px}
      h2{margin:0 0 4px;font-size:20px;font-weight:800;color:#0f172a}
      .num{font-size:11px;color:#6b7280;margin-top:2px}
      section{margin-bottom:20px}
      .section-title{font-size:11px;font-weight:800;color:#0891b2;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;border-bottom:1px solid #e0f2fe;padding-bottom:4px}
      table{width:100%;border-collapse:collapse}
      td{padding:9px 12px;border-bottom:1px solid #f1f5f9;font-size:13px;vertical-align:top}
      .lbl{color:#6b7280;font-weight:500;width:42%}
      .val{font-weight:600;color:#0f172a}
      .total-row td{background:#f0f9ff;font-weight:800;font-size:15px;color:#0891b2;border-top:2px solid #0891b2;border-bottom:none}
      .ref-box{background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:8px 12px;font-family:monospace;font-size:13px;color:#0891b2;font-weight:700;margin-top:4px;display:inline-block}
      .badge-ok{display:inline-block;background:#dcfce7;color:#166534;padding:5px 16px;border-radius:20px;font-weight:700;font-size:12px}
      .badge-partial{display:inline-block;background:#fef3c7;color:#92400e;padding:5px 16px;border-radius:20px;font-weight:700;font-size:12px}
      .stamp{display:inline-block;border:3px solid ${isComplet ? "#22c55e" : "#f59e0b"};color:${isComplet ? "#166534" : "#92400e"};padding:6px 20px;border-radius:8px;font-weight:900;font-size:16px;transform:rotate(-4deg);margin-top:12px;letter-spacing:.08em}
      .footer{margin-top:36px;padding-top:14px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af;text-align:center;line-height:1.7}
      @media print{body{padding:20px}*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}}
    </style></head><body>
    <div class="header">
      <div>
        <div class="logo">BET<span>Languages</span></div>
        <div class="logo-sub">Centre de langues - Abidjan, Côte d'Ivoire</div>
        <div class="logo-sub" style="margin-top:2px">📧 contact@binniesenglish.com · 📞 +225 27 22 40 71 99</div>
      </div>
      <div style="text-align:right">
        <h2>REÇU DE PAIEMENT</h2>
        <div class="num">N° ${num}</div>
        <div class="num">Émis le ${now}</div>
      </div>
    </div>

    <section>
      <div class="section-title">📋 Informations client</div>
      <table>
        <tr><td class="lbl">Nom du client</td><td class="val">${p.client || "-"}</td></tr>
        ${p.email    ? `<tr><td class="lbl">Email</td><td class="val">${p.email}</td></tr>` : ""}
        ${p.telephone ? `<tr><td class="lbl">Téléphone</td><td class="val">${p.telephone}</td></tr>` : ""}
        ${p.offre ? `<tr><td class="lbl">Offre souscrite</td><td class="val">${p.offre}</td></tr>` : ""}
        ${p.inscription ? `<tr><td class="lbl">Formation / Parcours</td><td class="val">${p.inscription}</td></tr>` : ""}
      </table>
    </section>

    <section>
      <div class="section-title">💳 Détails du paiement</div>
      <table>
        <tr><td class="lbl">Statut</td><td class="val">${p.statut === "paye" || p.statut === "validé" ? "✅ Validé" : p.statut === "partiel" ? "🔄 Partiel" : p.statut === "remboursé" ? "↩️ Remboursé" : "⏳ " + (p.statut || "En attente")}</td></tr>
        <tr><td class="lbl">Date de réception</td><td class="val">${dateStr}</td></tr>
        <tr><td class="lbl">Mode de paiement</td><td class="val">${p.mode || "-"}</td></tr>
        ${p.refTransaction ? `<tr><td class="lbl">Référence transaction</td><td class="val"><span class="ref-box">🔖 ${p.refTransaction}</span></td></tr>` : ""}
        ${p.paymentToken ? `<tr><td class="lbl">Token TouchPoint</td><td class="val">${p.paymentToken}</td></tr>` : ""}
        ${p.operateur ? `<tr><td class="lbl">Mode / opérateur</td><td class="val">${p.operateur}</td></tr>` : ""}
        ${p.niveau ? `<tr><td class="lbl">Niveau</td><td class="val">${p.niveau}</td></tr>` : ""}
        ${p.assignationId ? `<tr><td class="lbl">Dossier lié</td><td class="val" style="font-family:monospace;font-size:11px;color:#6b7280">${p.assignationId}</td></tr>` : ""}
        ${p.notes ? `<tr><td class="lbl">Notes</td><td class="val" style="color:#6b7280;font-weight:400">${p.notes}</td></tr>` : ""}
      </table>
    </section>

    <section>
      <div class="section-title">💰 Récapitulatif financier</div>
      <table>
        <tr><td class="lbl">Montant total dû</td><td class="val">${Number(p.montantDu || 0).toLocaleString("fr-FR")} FCFA</td></tr>
        <tr><td class="lbl">Montant reçu</td><td class="val" style="color:#22c55e">${Number(p.montantReçu || 0).toLocaleString("fr-FR")} FCFA</td></tr>
        ${reste > 0 ? `<tr><td class="lbl">Reste à payer</td><td class="val" style="color:#ef4444;font-weight:800">${Number(reste).toLocaleString("fr-FR")} FCFA</td></tr>` : ""}
        <tr class="total-row"><td>TOTAL ENCAISSÉ</td><td>${Number(p.montantReçu || 0).toLocaleString("fr-FR")} FCFA</td></tr>
      </table>
    </section>

    <div style="text-align:center;margin-top:28px">
      <div class="${isComplet ? "badge-ok" : "badge-partial"}">${isComplet ? "✅ PAIEMENT INTÉGRAL CONFIRMÉ" : "⏳ PAIEMENT PARTIEL - SOLDE EN ATTENTE"}</div>
      <br/><div class="stamp">${isComplet ? "PAYÉ" : "PARTIEL"}</div>
    </div>
    <div class="footer">
      Ce reçu est généré automatiquement par le système BET Languages. Conservez ce document pour vos archives.<br/>
      BET Languages · Centre de langues agréé · Abidjan, Côte d'Ivoire<br/>
      contact@binniesenglish.com · +225 27 22 40 71 99
    </div>
    <script>window.onload=()=>window.print();</script>
    </body></html>`);
    w.document.close();
    toast.success(`Reçu ${num} généré`);
  };

  // ── Générer reçu de paiement TouchPoint (paiement en ligne direct) ──
  const genererRecuTouchPoint = (p) => {
    const dateStr = p.created_at ? new Date(p.created_at).toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" }) : new Date().toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" });
    const now = new Date().toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" });
    const num = `BET-REC-TP-${p.order_number || String(p.id).padStart(4,"0")}-${new Date().getFullYear()}`;
    const isValide = p.statut === "validé";
    const w = window.open("","_blank","width=720,height=900");
    w.document.write(`<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Reçu ${num}</title>
    <style>
      *{box-sizing:border-box}
      body{font-family:Arial,sans-serif;margin:0;padding:40px;color:#1e293b;background:#fff}
      .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #0891b2;padding-bottom:20px;margin-bottom:28px}
      .logo{font-size:28px;font-weight:900;color:#0891b2;letter-spacing:-1pxdisplay:flex;align-items:center;gap:11px}.logo::before{content:'';width:40px;height:40px;flex-shrink:0;background:#fff url('https://res.cloudinary.com/nvp7nxqp/image/upload/v1788473005/bet/branding/bet-logo.png') center / 28px 28px no-repeat;border-radius:9px;box-shadow:0 2px 7px rgba(0,0,0,.14)}
      .logo span{color:#0f172a}
      .logo-sub{font-size:11px;color:#6b7280;margin-top:4px;margin-left:51px}
      h2{margin:0 0 4px;font-size:20px;font-weight:800;color:#0f172a}
      .num{font-size:11px;color:#6b7280;margin-top:2px}
      section{margin-bottom:20px}
      .section-title{font-size:11px;font-weight:800;color:#0891b2;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;border-bottom:1px solid #e0f2fe;padding-bottom:4px}
      table{width:100%;border-collapse:collapse}
      td{padding:9px 12px;border-bottom:1px solid #f1f5f9;font-size:13px;vertical-align:top}
      .lbl{color:#6b7280;font-weight:500;width:42%}
      .val{font-weight:600;color:#0f172a}
      .total-row td{background:#f0f9ff;font-weight:800;font-size:15px;color:#0891b2;border-top:2px solid #0891b2;border-bottom:none}
      .ref-box{background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:8px 12px;font-family:monospace;font-size:13px;color:#0891b2;font-weight:700;margin-top:4px;display:inline-block}
      .badge-ok{display:inline-block;background:#dcfce7;color:#166534;padding:5px 16px;border-radius:20px;font-weight:700;font-size:12px}
      .badge-partial{display:inline-block;background:#fef3c7;color:#92400e;padding:5px 16px;border-radius:20px;font-weight:700;font-size:12px}
      .stamp{display:inline-block;border:3px solid ${isValide ? "#22c55e" : "#f59e0b"};color:${isValide ? "#166534" : "#92400e"};padding:6px 20px;border-radius:8px;font-weight:900;font-size:16px;transform:rotate(-4deg);margin-top:12px;letter-spacing:.08em}
      .footer{margin-top:36px;padding-top:14px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af;text-align:center;line-height:1.7}
      @media print{body{padding:20px}*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}}
    </style></head><body>
    <div class="header">
      <div>
        <div class="logo">BET<span>Languages</span></div>
        <div class="logo-sub">Centre de langues - Abidjan, Côte d'Ivoire</div>
        <div class="logo-sub" style="margin-top:2px">📧 contact@binniesenglish.com · 📞 +225 27 22 40 71 99</div>
      </div>
      <div style="text-align:right">
        <h2>REÇU DE PAIEMENT</h2>
        <div class="num">N° ${num}</div>
        <div class="num">Émis le ${now}</div>
      </div>
    </div>

    <section>
      <div class="section-title">📋 Informations client</div>
      <table>
        <tr><td class="lbl">Nom du client</td><td class="val">${p.client_nom || "-"}</td></tr>
        ${p.client_email     ? `<tr><td class="lbl">Email</td><td class="val">${p.client_email}</td></tr>` : ""}
        ${p.client_telephone ? `<tr><td class="lbl">Téléphone</td><td class="val">${p.client_telephone}</td></tr>` : ""}
        ${p.offre_label      ? `<tr><td class="lbl">Offre souscrite</td><td class="val">${p.offre_label}</td></tr>` : ""}
      </table>
    </section>

    <section>
      <div class="section-title">💳 Détails du paiement en ligne (TouchPoint)</div>
      <table>
        <tr><td class="lbl">Statut</td><td class="val">${isValide ? "✅ Validé" : p.statut === "en_attente" ? "⏳ En attente" : p.statut === "échoué" ? "❌ Échoué" : p.statut === "annulé" ? "↩️ Annulé" : (p.statut || "-")}</td></tr>
        <tr><td class="lbl">Date de la transaction</td><td class="val">${dateStr}</td></tr>
        ${p.payment_mode  ? `<tr><td class="lbl">Opérateur / mode</td><td class="val">${p.payment_mode}</td></tr>` : ""}
        ${p.order_number  ? `<tr><td class="lbl">Référence transaction</td><td class="val"><span class="ref-box">🔖 ${p.order_number}</span></td></tr>` : ""}
        ${p.payment_token ? `<tr><td class="lbl">Token TouchPoint</td><td class="val" style="font-family:monospace;font-size:11px">${p.payment_token}</td></tr>` : ""}
        ${p.assistante_nom ? `<tr><td class="lbl">Assistante en charge</td><td class="val">${p.assistante_nom}</td></tr>` : ""}
        ${p.assignation_id ? `<tr><td class="lbl">Dossier lié</td><td class="val" style="font-family:monospace;font-size:11px;color:#6b7280">${p.assignation_id}</td></tr>` : ""}
      </table>
    </section>

    <section>
      <div class="section-title">💰 Récapitulatif financier</div>
      <table>
        <tr class="total-row"><td>MONTANT PAYÉ</td><td>${Number(p.montant || 0).toLocaleString("fr-FR")} FCFA</td></tr>
      </table>
    </section>

    <div style="text-align:center;margin-top:28px">
      <div class="${isValide ? "badge-ok" : "badge-partial"}">${isValide ? "✅ PAIEMENT VALIDÉ" : "⏳ PAIEMENT NON CONFIRMÉ"}</div>
      <br/><div class="stamp">${isValide ? "PAYÉ" : p.statut === "en_attente" ? "EN ATTENTE" : (p.statut || "").toUpperCase()}</div>
    </div>
    <div class="footer">
      Ce reçu est généré automatiquement par le système BET Languages. Conservez ce document pour vos archives.<br/>
      BET Languages · Centre de langues agréé · Abidjan, Côte d'Ivoire<br/>
      contact@binniesenglish.com · +225 27 22 40 71 99
    </div>
    <script>window.onload=()=>window.print();</script>
    </body></html>`);
    w.document.close();
    toast.success(`Reçu ${num} généré`);
  };

  // ── Télécharger / ouvrir document ───────────────────
  const telechargerDocument = (doc, client) => {
    if (doc.url) {
      window.open(doc.url, "_blank", "noopener,noreferrer");
      return;
    }
    const content = `DOCUMENT : ${doc.nom}\nClient : ${client}\nDate : ${new Date().toLocaleDateString("fr-FR")}\n\n[Fichier non encore uploadé]`;
    const blob = new Blob([content], { type:"text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${doc.nom.replace(/\s+/g,"_")}_${client.replace(/\s+/g,"_")}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    toast.success(`📎 ${doc.nom} téléchargé`);
  };


  // ── Export ───────────────────────────────────────────
  const exportCSV = (data, filename) => {
    if (!data.length) return toast.error("Aucune donnée à exporter");
    const headers = Object.keys(data[0]);
    exporterExcelStyle({
      nomFichier: filename,
      titre: `📊 Export - ${filename}`,
      sousTitre: `${data.length} ligne${data.length>1?'s':''} · Généré le ${new Date().toLocaleDateString('fr-FR')}`,
      couleurEntete: '6366F1',
      colonnes: headers.map(h => ({ label: h, width: 20 })),
      lignes: data.map(r => headers.map(h => r[h] ?? "")),
    });
    toast.success(`Export ${filename}.xlsx effectué`);
  };
  const exportPDF = (data, title) => {
    if (!data.length) return toast.error("Aucune donnée à exporter");
    const w = window.open("","_blank");
    w.document.write(`<html><head><title>${title}</title><style>body{font-family:sans-serif;padding:20px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:8px;text-align:left}</style></head><body><h1>${title}</h1><table><thead><tr>${Object.keys(data[0]).map(h=>`<th>${h}</th>`).join("")}</tr></thead><tbody>${data.map(r=>`<tr>${Object.values(r).map(v=>`<td>${v}</td>`).join("")}</tr>`).join("")}</tbody></table></body></html>`);
    w.document.close(); w.print(); toast.success(`PDF ${title} lancé`);
  };
  const handlePlanifierEnvoi = () => {
    toast.success(`Planification enregistrée : ${planifConfig.frequence} → ${planifConfig.emails} (${planifConfig.format.toUpperCase()})`);
    setShowPlanifModal(false);
  };

  // ── Tab config (dynamique selon profil b2c/b2b/les_deux) ─
  const B2B_PIPELINE_STEPS = [
    { id:"prospection", label:"Prospection", color:"#64748b", bg:"#f8fafc" },
    { id:"negociation", label:"Négociation", color:"#f59e0b", bg:"#fffbeb" },
    { id:"proposition", label:"Proposition", color:"#0891b2", bg:"#e0f2fe" },
    { id:"conclu",      label:"Conclu ✅",   color:"#16a34a", bg:"#f0fdf4" },
    { id:"perdu",       label:"Perdu ❌",    color:"#dc2626", bg:"#fef2f2" },
  ];
  const B2B_DOC_TYPES = { proforma:"📄 Proforma", bon_commande:"📋 Bon de commande", contrat:"📑 Contrat", autre:"📎 Autre" };
  const B2B_FACT_STATUTS = { brouillon:"⚪ Brouillon", envoyée:"📤 Envoyée", payée:"✅ Payée", en_retard:"🔴 En retard", annulée:"❌ Annulée" };
  const B2B_FACT_COLORS  = { brouillon:"#94a3b8", envoyée:"#0891b2", payée:"#16a34a", en_retard:"#dc2626", annulée:"#9ca3af" };
  const B2B_SECTEURS = ["Finance / Banque","Industrie","Commerce / Distribution","Services","Santé","Éducation","Télécoms","BTP / Immobilier","Agro-alimentaire","Énergie","Transport / Logistique","Autre"];

  const b2bFacturesEnRetard = b2bFactures.filter(f => f.statut === "en_retard").length;
  const b2bProspectsActifs  = b2bProspects.filter(p => !["conclu","perdu"].includes(p.statut)).length;

  /* ══ LES SEPT ÉTAPES DU CYCLE ══
     Même vocabulaire que la contrainte posée en base par `sql/crm_corporate_sept_statuts.sql`.
     `converti` S'AFFICHE « GAGNÉ » : le mot du métier n'est pas celui du schéma, et renommer
     la colonne aurait obligé à reprendre 90 dossiers et tous les écrans qui la lisent. */
  const CYCLE_ETAPES = [
    { id:"nouveau",     label:"Nouveau",     color:"#64748b", bg:"#f8fafc" },
    { id:"contacte",    label:"Contacté",    color:"#0891b2", bg:"#e0f2fe" },
    { id:"qualifie",    label:"Qualifié",    color:"#7c3aed", bg:"#f3e8ff" },
    { id:"rendez_vous", label:"Rendez-vous", color:"#f59e0b", bg:"#fffbeb" },
    { id:"devis",       label:"Devis",       color:"#0c33ac", bg:"#e8edff" },
    { id:"converti",    label:"Gagné",       color:"#16a34a", bg:"#f0fdf4" },
    { id:"perdu",       label:"Perdu",       color:"#dc2626", bg:"#fef2f2" },
  ];
  const etapeCycle = (id) => CYCLE_ETAPES.find(e => e.id === id) || CYCLE_ETAPES[0];

  /* UNE RELANCE EST DUE QUAND SA DATE EST PASSÉE, et seulement si le dossier est encore
     ouvert : relancer un dossier gagné ou perdu n'a pas de sens, et le compter ferait un
     badge qui ne retombe jamais. */
  const corpLeadsARelancer = corpLeads.filter(l =>
    l.relance_at && new Date(l.relance_at) <= new Date() && !["converti","perdu"].includes(l.cycle_statut)
  ).length;

  const chargerCorporate = async () => {
    setCorpLoading(true);
    try {
      const debut = new Date(Date.now() - corpJours * 86400000).toISOString().slice(0, 10);
      const [rI, rL] = await Promise.all([
        fetch(`${API_URL}/api/corporate/indicateurs?debut=${debut}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/corporate/leads`, { headers: authHeaders() }),
      ]);
      const [dI, dL] = await Promise.all([rI.json(), rL.json()]);
      setCorpIndic(dI.indicateurs ? dI : null);
      setCorpLeads(dL.leads || []);
    } catch { toast.error("Erreur chargement du tableau de bord Corporate"); }
    finally { setCorpLoading(false); }
  };

  /* Faire avancer un lead. L'écran se met à jour SANS tout recharger : les indicateurs ne
     changent pas pour un statut qui bouge, et recharger la liste entière ferait sauter la
     position de défilement sous les doigts de la personne qui travaille. */
  const majLead = async (id, champs) => {
    try {
      const r = await fetch(`${API_URL}/api/corporate/leads/${id}`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify(champs),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Échec");
      setCorpLeads(prev => prev.map(l => l.id === id ? { ...l, ...d.lead } : l));
      toast.success("Dossier mis à jour ✓");
    } catch (e) { toast.error(e.message); }
  };

  // ── "Mes tâches" - agrège tous les compteurs "à traiter" déjà calculés pour
  // chaque onglet, pour que la commerciale ait un point d'entrée unique au lieu
  // de devoir parcourir chaque onglet pour savoir ce qui reste à faire.
  const tachesAssignations = assignations.filter(a => a.statut !== "converti").length;
  const tachesTests        = tests.filter(t => t.statut === "nouveau").length;
  const tachesSuivi        = suiviApprenants.filter(a => !Object.values(suiviChecklistEffectif(a)).every(Boolean)).length;
  const tachesPaiements    = paiements.filter(p => p.statut === "en_attente").length;
  const tachesB2bDemandes  = b2bDemandes.filter(d => d.statut === "en_attente").length;
  /* `dossiersAReaffecter` manquait ici alors qu'il porte déjà son propre badge sur l'onglet :
     une assistante de centre avec un dossier revenu du test de niveau le voyait signalé sur
     « Dossiers à réaffecter », mais « Mes tâches » l'ignorait - or c'est justement l'écran
     censé dispenser de parcourir les onglets un par un. Il compte les demandes au statut
     `test_fait`, c'est-à-dire celles qui n'attendent plus qu'elle. */
  const tachesTotal = (hasB2C ? tachesAssignations + tachesTests + dossiersAReaffecter + (isCommercialeCentre ? tachesSuivi : 0) + tachesPaiements : 0)
    + (hasB2B ? b2bProspectsActifs + b2bFacturesEnRetard + (!hasB2C ? tachesB2bDemandes : 0) : 0);

  const TABS = [
    { key:"taches", label:"Mes tâches", icon:"✅", badge: tachesTotal || null },
    ...(hasB2C ? [
      { key:"dashboard",     label:"Tableau de bord",   icon:"📊" },
      { key:"assignations",  label:"Prospects parcours",icon:"🎯", badge: assignations.filter(a => a.statut !== "converti").length || null },
      { key:"tests",         label:"Tests reçus",       icon:"📝", badge: tests.filter(t=>t.statut==="nouveau").length },
      { key:"inscriptions",  label:"Inscriptions",      icon:"✅" },
      { key:"classes",       label:"Mes classes",        icon:"📚" },
      { key:"plannings",     label:"Plannings",         icon:"📅", badge: planning.planningsAAlerter || null },
      { key:"apprenants",    label:"Mes apprenants",    icon:"🎓" },
      // En presentiel, c'est l'assistante qui affecte classe et coach : les demandes de
      // transfert emises par ses coachs lui reviennent, comme a l'onboarding pour le en ligne.
      { key:"reaffecter",    label:"Dossiers à réaffecter", icon:"🔄", badge: dossiersAReaffecter || null },
      ...(isCommercialeCentre ? [
        { key:"suivi", label:"Suivi démarrage", icon:"✅", badge: suiviApprenants.filter(a=>!Object.values(suiviChecklistEffectif(a)).every(Boolean)).length || null },
      ] : []),
      { key:"paiements",     label:"Paiements",         icon:"💳", badge: paiements.filter(p=>p.statut==="en_attente").length },
    ] : []),
    ...(hasB2B ? [
      ...(assistanteProfil === "les_deux" ? [{ key:"__b2b_sep", label:"── Corporate ──", isSep:true }] : []),
      /* EN TÊTE DU BLOC CORPORATE, avant les comptes et le pipeline : c'est la vue qui dit
         où en est l'activité, celle qu'on ouvre d'abord. Les écrans de saisie viennent
         après — on regarde avant d'agir. */
      { key:"tdb_corporate",  label:"Tableau de bord",     icon:"📊", badge: corpLeadsARelancer || null, danger: corpLeadsARelancer > 0 },
      { key:"entreprises",    label:"Comptes entreprises", icon:"🏢" },
      { key:"pipeline_b2b",   label:"Pipeline B2B",        icon:"🔄", badge: b2bProspectsActifs || null },
      { key:"documents_b2b",  label:"Documents",           icon:"📄" },
      { key:"facturation_b2b",label:"Facturation",         icon:"🧾", badge: b2bFacturesEnRetard || null },
    ] : []),
    ...(hasB2C ? [
      { key:"sondages",      label:"Sondages",          icon:"🎯" },
    ] : []),
    ...(hasB2B && !hasB2C ? [{ key:"assignations", label:"Demandes reçues", icon:"📥", badge: (b2bDemandes.filter(d => d.statut === "en_attente").length) || null }] : []),
    { key:"messages",    label:"Messages",    icon:"💬", badge: (totalNonLu || 0) + chatGroupeNonLus || null },
    { key:"notifications", label:"Notifications", icon:"🔔", badge: notifNonLuesCM || null },
  ];

  const currentExportData = () => {
    if (activeTab === "inscriptions") return filteredInscriptions;
    if (activeTab === "tests")        return filteredTests;
    if (activeTab === "paiements")    return paiements;
    if (activeTab === "plannings")    return planning.planningExport;
    return [];
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", background:"#f0f9ff" }}>

      {/* HERO */}
      <div style={{ flexShrink:0, background:BET_GRADIENT, padding:"28px 32px 0", color:"#fff", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-40, right:-40, width:200, height:200, borderRadius:"50%", background:"rgba(255,255,255,0.04)", zIndex:0 }} />
        <div style={{ position:"absolute", bottom:-60, right:80, width:120, height:120, borderRadius:"50%", background:"rgba(255,255,255,0.03)", zIndex:0 }} />
        <div style={{ position:"relative", zIndex:1, display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:28 }}>
          <div style={{ display:"flex", alignItems:"center", gap:20 }}>
            <div style={{ position:"relative", width:64, height:64, flexShrink:0 }} onClick={()=>{ if (avatarUrl) setPhotoProfilOuverte(true); }}>
              <div style={{ width:64, height:64, borderRadius:"50%", background:"rgba(255,255,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:22, color:"#fff", border:"3px solid rgba(255,255,255,0.3)", overflow:"hidden" , cursor: avatarUrl ? "zoom-in" : "default"}}>
                {avatarUrl ? <img src={avatarUrl} alt={initiales} style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : initiales}
              </div>
              {photoProfilOuverte && <VisionneuseImage url={avatarUrl} onClose={()=>setPhotoProfilOuverte(false)} />}
              <label title="Changer la photo" onClick={e=>e.stopPropagation()} style={{ position:"absolute", bottom:0, right:0, width:22, height:22, borderRadius:"50%", background:"rgba(0,0,0,0.55)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:12, border:"2px solid #fff" }}>
                📷
                <input type="file" accept="image/*" style={{ display:"none" }} onChange={(e) => { const f=e.target.files?.[0]; if(f){ uploadFichier(f, "avatar").then(handleAvatarSuccess).catch(e=>toast.error(e.message)); } e.target.value=""; }} />
              </label>
            </div>
            <div>
              <div style={{ fontSize:11, color:"#7dd3fc", fontWeight:600, letterSpacing:"0.08em" }}>Bonjour 👋</div>
              <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:"#fff" }}>{nomAffiche}<BoutonMonProfil onClick={() => setMonProfilOuvert(true)} /></h1>
              {surnomPerso.trim() && nomComplet !== nomAffiche && (
                <div style={{ fontSize:11, color:"rgba(255,255,255,.6)", marginTop:1 }}>{nomComplet}</div>
              )}
              <MonProfilModal ouvert={monProfilOuvert} onClose={() => setMonProfilOuvert(false)}
                onEnregistre={(v) => setSurnomPerso(v || "")} />
              <div style={{ fontSize:12, color:"#bae6fd", marginTop:3 }}>
                {assistanteProfil === "b2b" ? "🏢 Commerciale Corporate B2B" : assistanteProfil === "les_deux" ? "🔀 Commerciale B2C + Corporate B2B" : "Commerciale"} · {profil?.email || ""}
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:5, flexWrap:"wrap" }}>
                {/* Badge type cours */}
                {assistanteTypeCours === "en_ligne" && (
                  <span style={{ fontSize:11, fontWeight:700, background:"rgba(255,255,255,0.15)", color:"#bae6fd", borderRadius:999, padding:"2px 10px", border:"1px solid rgba(255,255,255,0.25)" }}>🌐 En ligne</span>
                )}
                {assistanteTypeCours === "presentiel" && (
                  <span style={{ fontSize:11, fontWeight:700, background:"rgba(255,255,255,0.15)", color:"#bae6fd", borderRadius:999, padding:"2px 10px", border:"1px solid rgba(255,255,255,0.25)" }}>
                    🏢 Présentiel{assistanteCentreNom ? ` - ${assistanteCentreNom}` : ""}
                  </span>
                )}
                {assistanteTypeCours === "les_deux" && (
                  <>
                    <span style={{ fontSize:11, fontWeight:700, background:"rgba(255,255,255,0.15)", color:"#bae6fd", borderRadius:999, padding:"2px 10px", border:"1px solid rgba(255,255,255,0.25)" }}>🌐 En ligne</span>
                    <span style={{ fontSize:11, fontWeight:700, background:"rgba(255,255,255,0.15)", color:"#bae6fd", borderRadius:999, padding:"2px 10px", border:"1px solid rgba(255,255,255,0.25)" }}>
                      🏢 Présentiel{assistanteCentreNom ? ` - ${assistanteCentreNom}` : ""}
                    </span>
                  </>
                )}
                {/* Fallback ancien scope */}
                {!assistanteTypeCours && centreName && (
                  <span style={{ fontSize:11, fontWeight:700, background:"rgba(255,255,255,0.15)", color:"#bae6fd", borderRadius:999, padding:"2px 10px", border:"1px solid rgba(255,255,255,0.25)" }}>📍 {centreName}</span>
                )}
              </div>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <NotificationBell userId={profil?.id} />
            <DashboardColorPicker />
              <button onClick={toggleLang} style={{ display:"flex", alignItems:"center", gap:5, padding:"8px 14px", background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:10, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", transition:"background .2s", letterSpacing:"0.06em" }}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.2)"}
                onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}>
                {lang === "FR" ? "🇫🇷 FR" : "🇬🇧 EN"}
              </button>
            {accesElearning && (
              <button
                type="button"
                onClick={() => openElearningSSO(ELEARNING_URL)}
                style={{ display:"flex", alignItems:"center", gap:7, padding:"8px 16px", background:"rgba(8,145,178,0.25)", border:"1.5px solid rgba(8,145,178,0.5)", borderRadius:10, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", transition:"background .2s" }}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(8,145,178,0.45)"}
                onMouseLeave={e=>e.currentTarget.style.background="rgba(8,145,178,0.25)"}
                title="Accéder à la plateforme E-Learning BET"
              >
                <span style={{ fontSize:15 }}>🎓</span> E-Learning
              </button>
            )}
            <button onClick={handleLogout} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px", background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:10, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", backdropFilter:"blur(4px)", transition:"background .2s" }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.2)"}
              onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}>
              <span>🚪</span> Déconnexion
            </button>
          </div>
        </div>
        <div style={{ display:"flex", gap:0, background:"rgba(0,0,0,0.15)", borderRadius:"12px 12px 0 0", overflow:"hidden" }}>
          {(assistanteProfil === "b2b" ? [
            { l:"Comptes entreprises",  v:b2bEntreprises.length,        c:"#818cf8" },
            { l:"Prospects actifs",     v:b2bProspectsActifs,           c:"#38bdf8" },
            { l:"CA estimé (pipeline)", v:fmtB2B(b2bProspects.filter(p=>p.statut!=="perdu").reduce((s,p)=>s+(Number(p.montant_estime)||0),0)), c:"#34d399" },
            { l:"Factures en retard",   v:b2bFacturesEnRetard,          c:"#f87171" },
          ] : assistanteProfil === "les_deux" ? [
            { l:"Apprenants actifs",    v:assignations.filter(a=>a.statut==="converti").length, c:"#38bdf8" },
            { l:"Prospects B2B actifs", v:b2bProspectsActifs,           c:"#818cf8" },
            { l:"CA encaissé (B2C)",    v:Number(caPaiements).toLocaleString("fr-FR")+" F",    c:"#34d399" },
            { l:"Factures retard (B2B)",v:b2bFacturesEnRetard,          c:"#f87171" },
          ] : [
            { l:"Tests reçus (mois)",   v:filteredTests.length,         c:"#38bdf8" },
            { l:"Inscriptions (mois)",  v:filteredInscriptions.length,  c:"#818cf8" },
            { l:"CA encaissé",          v:Number(caPaiements).toLocaleString("fr-FR")+" F",    c:"#fbbf24" },
          ]).map((s,i,arr)=>(
            <div key={s.l} style={{ flex:1, textAlign:"center", padding:"14px 8px", borderRight:i<arr.length-1?"1px solid rgba(255,255,255,0.08)":"none" }}>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.5)", marginBottom:3 }}>{s.l}</div>
              <div style={{ fontSize:18, fontWeight:800, color:s.c }}>{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ flex:"1 1 auto", padding:"0 24px 32px" }}>
        <div style={{ background:"#fff", borderRadius:"0 0 12px 12px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", overflow:"hidden" }}>

          {/* TABS */}
          <div style={{ display:"flex", gap:0, borderBottom:"1px solid #e5e7eb", flexWrap:"wrap", background:"#fafafa" }}>
            {TABS.map(tab => tab.isSep ? (
              <div key={tab.key} style={{ display:"flex", alignItems:"center", padding:"0 8px", fontSize:10, color:"#94a3b8", fontWeight:700, letterSpacing:"0.08em", whiteSpace:"nowrap", borderLeft:"2px solid #e5e7eb", marginLeft:4 }}>
                🏢 CORPORATE
              </div>
            ) : (
              <button key={tab.key} onClick={() => { setActiveTab(tab.key); if (tab.key === "tests") setTestPage(1); }} style={{
                padding:"12px 16px", border:"none", borderBottom: activeTab===tab.key ? `3px solid ${BET_COLOR}` : "3px solid transparent",
                cursor:"pointer", fontWeight:600, fontSize:12, whiteSpace:"nowrap",
                background:"transparent", color:activeTab===tab.key ? BET_COLOR : "#6b7280",
                display:"flex", alignItems:"center", justifyContent:"center", gap:6, transition:"color .15s",
                flex:"1 1 130px",
              }}>
                <span style={{ fontSize:14 }}>{tab.icon}</span>
                {tab.label}
                {tab.badge > 0 && <span style={{ background:"#ef4444", color:"#fff", borderRadius:99, fontSize:10, fontWeight:800, padding:"1px 6px", minWidth:18, textAlign:"center" }}>{tab.badge}</span>}
              </button>
            ))}
          </div>

          {!banderoleFermeeCM && notifsBanderoleCM.length > 0 && (
            <div style={{ padding:"10px 24px", background:"#fff7ed", borderBottom:"1px solid #fed7aa", display:"flex", alignItems:"flex-start", gap:10 }}>
              <span style={{ fontSize:16, flexShrink:0, marginTop:1 }}>🔔</span>
              <div style={{ flex:1, display:"flex", flexDirection:"column", gap:4 }}>
                {notifsBanderoleCM.map(n => (
                  <div key={n.id} onClick={() => { marquerNotifLueCM(n.id); setActiveTab("notifications"); }}
                    style={{ cursor:"pointer", display:"flex", flexDirection:"column" }}>
                    <span style={{ fontWeight:700, color:"#9a3412", fontSize:12 }}>{n.titre}</span>
                    <span style={{ color:"#78716c", fontSize:11 }}>{n.message}</span>
                  </div>
                ))}
                {notifNonLuesCM > notifsBanderoleCM.length && (
                  <button onClick={()=>setActiveTab("notifications")} style={{ alignSelf:"flex-start", background:"none", border:"none", color:"#9a3412", fontWeight:700, fontSize:11, cursor:"pointer", padding:0 }}>
                    +{notifNonLuesCM - notifsBanderoleCM.length} autre{notifNonLuesCM - notifsBanderoleCM.length > 1 ? "s" : ""} →
                  </button>
                )}
              </div>
              <button onClick={()=>setBanderoleFermeeCM(true)} title="Masquer" style={{ background:"none", border:"none", cursor:"pointer", color:"#9a3412", fontSize:14, flexShrink:0 }}>✕</button>
            </div>
          )}

          <div style={{ padding:24 }}>
            {/* Barre d'outils commune */}
            {activeTab !== "messages" && activeTab !== "taches" && (
              <div style={{ display:"flex", gap:10, marginBottom:20, flexWrap:"wrap", alignItems:"center" }}>
                {/* Le planning ne porte ni client ni offre commerciale, et filtre par plage
                    de dates plutôt que par période glissante : ces trois contrôles y étaient
                    inertes, celui des offres franchement trompeur. Les exports, eux, restent -
                    ils savent désormais exporter le planning affiché. */}
                {activeTab !== "plannings" && (
                  <>
                    <input
                      placeholder="🔍 Rechercher un client..."
                      value={searchQ}
                      onChange={e => setSearchQ(e.target.value)}
                      style={{ padding:"7px 12px", borderRadius:6, border:"1px solid #e5e7eb", fontSize:12, width:200 }}
                    />
                    <select value={periode} onChange={e=>{ setPeriode(e.target.value); setTestPage(1); }} style={selectSt}>
                      <option value="semaine">Semaine</option><option value="mois">Mois</option>
                      <option value="trimestre">Trimestre</option><option value="annee">Année</option>
                    </select>
                    <select value={filtreOffre} onChange={e=>setFiltreOffre(e.target.value)} style={selectSt}>
                      <option value="Toutes">Toutes offres</option>
                      {OFFRE_LIST.map(o=><option key={o}>{o}</option>)}
                    </select>
                  </>
                )}
                <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
                  <button onClick={() => exportCSV(currentExportData(), activeTab)} style={btnSecondary}>📊 Excel</button>
                  <button onClick={() => exportPDF(currentExportData(), activeTab)} style={btnSecondary}>📄 PDF</button>
                  <button onClick={() => setShowPlanifModal(true)} style={btnSecondary}>📧 Planifier</button>
                </div>
              </div>
            )}

            {/* ══ MES TÂCHES ══ */}
            {activeTab === "taches" && (() => {
              const taches = [
                ...(hasB2C ? [
                  { icon:"🎯", label:"Prospects à traiter",        n:tachesAssignations, tab:"assignations" },
                  { icon:"📝", label:"Tests de niveau à corriger", n:tachesTests,        tab:"tests" },
                  // Placé juste après les tests : c'est la suite du même circuit - le coach
                  // demande le transfert, l'agent placement rend le niveau, et le dossier
                  // revient ici en attendant sa nouvelle classe.
                  { icon:"🔄", label:"Dossiers à réaffecter",      n:dossiersAReaffecter, tab:"reaffecter" },
                  ...(isCommercialeCentre ? [{ icon:"✅", label:"Suivis de démarrage incomplets", n:tachesSuivi, tab:"suivi" }] : []),
                  { icon:"💳", label:"Paiements en attente",       n:tachesPaiements,    tab:"paiements" },
                ] : []),
                ...(hasB2B ? [
                  { icon:"🔄", label:"Prospects B2B actifs à faire avancer", n:b2bProspectsActifs,  tab:"pipeline_b2b" },
                  { icon:"🧾", label:"Factures B2B en retard",              n:b2bFacturesEnRetard, tab:"facturation_b2b" },
                  ...(!hasB2C ? [{ icon:"📥", label:"Demandes B2B reçues",  n:tachesB2bDemandes,  tab:"assignations" }] : []),
                ] : []),
              ];
              const enCours = taches.filter(t => t.n > 0);
              return (
                <div>
                  <div style={{ fontSize:15, fontWeight:800, color:"#0f172a", marginBottom:4 }}>✅ Mes tâches</div>
                  <div style={{ fontSize:12, color:"#64748b", marginBottom:18 }}>
                    {enCours.length > 0
                      ? `${tachesTotal} élément${tachesTotal>1?"s":""} à traiter au total.`
                      : "Rien à traiter pour le moment - tout est à jour."}
                  </div>
                  {enCours.length === 0 ? (
                    <div style={{ textAlign:"center", padding:"50px 0", color:"#94a3b8" }}>
                      <div style={{ fontSize:"2.4rem", marginBottom:8 }}>🎉</div>
                      Aucune tâche en attente.
                    </div>
                  ) : (
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))", gap:14 }}>
                      {enCours.map((t,i) => (
                        <div key={i} onClick={() => setActiveTab(t.tab)}
                          style={{ background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:14, padding:"16px 18px", cursor:"pointer", display:"flex", alignItems:"center", gap:14, transition:"border-color .15s" }}
                          onMouseEnter={e=>e.currentTarget.style.borderColor=BET_COLOR}
                          onMouseLeave={e=>e.currentTarget.style.borderColor="#e2e8f0"}>
                          <div style={{ fontSize:"1.6rem" }}>{t.icon}</div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>{t.label}</div>
                            <div style={{ fontSize:11, color:"#94a3b8", marginTop:2 }}>Cliquer pour ouvrir</div>
                          </div>
                          <div style={{ background:"#ef4444", color:"#fff", borderRadius:99, fontSize:13, fontWeight:800, padding:"3px 11px", minWidth:26, textAlign:"center" }}>{t.n}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ══ DASHBOARD ══ */}
            {activeTab === "dashboard" && (
              <div>
                {/* Alertes */}
                {alertes.length > 0 && (
                  <div style={{ marginBottom:20, display:"flex", flexDirection:"column", gap:8 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#374151", marginBottom:4 }}>🔔 Alertes</div>
                    {alertes.slice(0,5).map((a,i) => {
                      const colors = { warning:{ bg:"#fef9c3", text:"#92400e", border:"#fcd34d" }, info:{ bg:"#e0f2fe", text:"#0369a1", border:"#38bdf8" }, danger:{ bg:"#fee2e2", text:"#991b1b", border:"#fca5a5" } };
                      const c = colors[a.type];
                      return (
                        <div key={i} onClick={() => setActiveTab(a.tab)} style={{ padding:"9px 14px", borderRadius:8, background:c.bg, border:`1px solid ${c.border}`, color:c.text, fontSize:12, fontWeight:600, cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                          <span>{a.msg}</span>
                          <span style={{ fontSize:10, opacity:0.6 }}>Voir →</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Stat cards */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:24 }}>
                  <StatCard label="Tests reçus" value={tests.length} color="#0891b2" icon="📝" sub={`${tests.filter(t=>t.statut==="nouveau").length} nouveau(x)`} onClick={() => setActiveTab("tests")} />
                  <StatCard label="Inscriptions" value={inscriptions.length} color="#f59e0b" icon="✅" sub="dossiers en cours" onClick={() => setActiveTab("inscriptions")} />
                  <StatCard label="CA encaissé" value={Number(caPaiements).toLocaleString("fr-FR")+" F"} color="#22c55e" icon="💰" sub="paiements confirmés" onClick={() => setActiveTab("paiements")} />
                </div>

                {/* Pipeline commercial */}
                <div style={{ background:"#f8fafc", borderRadius:12, padding:20, marginBottom:20 }}>
                  <div style={{ fontSize:13, fontWeight:700, marginBottom:16 }}>🔄 Pipeline commercial</div>
                  <div style={{ display:"flex", alignItems:"center", gap:0 }}>
                    {[
                      { label:"Tests reçus",   count:tests.length,         color:"#0891b2", bg:"#e0f2fe" },
                      { label:"Inscriptions",   count:inscriptions.length,  color:"#22c55e", bg:"#dcfce7" },
                      { label:"Paiements reçus",count:paiements.filter(p=>p.statut==="paye").length, color:"#14b8a6", bg:"#ccfbf1" },
                    ].map((step, i, arr) => (
                      <React.Fragment key={step.label}>
                        <div style={{ flex:1, textAlign:"center", padding:"12px 8px", background:step.bg, borderRadius:i===0?"8px 0 0 8px":i===arr.length-1?"0 8px 8px 0":"0", border:`1px solid ${step.color}30` }}>
                          <div style={{ fontSize:22, fontWeight:800, color:step.color }}>{step.count}</div>
                          <div style={{ fontSize:10, color:step.color, fontWeight:600 }}>{step.label}</div>
                        </div>
                        {i < arr.length-1 && (
                          <div style={{ fontSize:18, color:"#94a3b8", zIndex:1, margin:"0 -2px" }}>›</div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* Courbes & KPIs */}
                <div style={{ display:"grid", gridTemplateColumns:"1.4fr 1fr", gap:16 }}>
                  <div style={{ background:"#fff", border:"1px solid #f1f5f9", borderRadius:12, padding:18 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:12 }}>💰 Évolution du CA encaissé (6 derniers mois)</div>
                    <div style={{ height:230 }}>
                      <Line
                        data={{
                          labels: caParMois.map(m=>m.label),
                          datasets: [{
                            label: "CA encaissé",
                            data: caParMois.map(m=>m.ca),
                            borderColor: BET_COLOR,
                            backgroundColor: BET_COLOR+"22",
                            fill: true,
                            tension: 0.35,
                            pointRadius: 3,
                            pointBackgroundColor: BET_COLOR,
                          }],
                        }}
                        options={{
                          responsive:true, maintainAspectRatio:false,
                          plugins:{ legend:{ display:false }, tooltip:{ callbacks:{ label: ctx => `${Number(ctx.parsed.y).toLocaleString("fr-FR")} F` } } },
                          scales:{ y:{ ticks:{ callback:v=>`${(v/1000).toLocaleString("fr-FR")}k` }, suggestedMax: maxCa*1.15 } },
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ background:"#fff", border:"1px solid #f1f5f9", borderRadius:12, padding:18 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:12 }}>🔀 Répartition des dossiers par statut</div>
                    {repartitionDossiers.length === 0 ? (
                      <div style={{ textAlign:"center", padding:"30px 12px", color:"#94a3b8", fontSize:12 }}>Aucun dossier.</div>
                    ) : (
                      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                        <div style={{ width:150, height:150, flexShrink:0 }}>
                          <Doughnut
                            data={{
                              labels: repartitionDossiers.map(([s])=>STATUT_ASSIGNATION_META[s]?.l || s),
                              datasets: [{ data: repartitionDossiers.map(([,v])=>v), backgroundColor: repartitionDossiers.map((_,i)=>DONUT_COLORS[i%DONUT_COLORS.length]), borderWidth:0 }],
                            }}
                            options={{ responsive:true, maintainAspectRatio:false, plugins:{ legend:{ display:false } }, cutout:"65%" }}
                          />
                        </div>
                        <div style={{ display:"flex", flexDirection:"column", gap:8, flex:1 }}>
                          {repartitionDossiers.map(([s,v],i)=>(
                            <div key={s} style={{ display:"flex", alignItems:"center", gap:8, fontSize:12 }}>
                              <span style={{ width:9, height:9, borderRadius:3, background:DONUT_COLORS[i%DONUT_COLORS.length], flexShrink:0 }} />
                              <span style={{ color:"#374151", flex:1 }}>{STATUT_ASSIGNATION_META[s]?.l || s}</span>
                              <span style={{ fontWeight:700, color:"#0f172a" }}>{v}</span>
                              <span style={{ color:"#9ca3af", fontSize:11 }}>({Math.round(v/totalRepartitionDossiers*100)}%)</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}


            {/* ══ TESTS REÇUS ══ */}
            {activeTab === "tests" && (
              <div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                  <span style={{ fontSize:12, color:"#6b7280" }}>
                    {testsLoading ? "⏳ Chargement…" : `${filteredTests.length} test${filteredTests.length > 1 ? "s" : ""} · ${tests.filter(t=>t.statut==="nouveau").length} nouveau${tests.filter(t=>t.statut==="nouveau").length > 1 ? "x" : ""}`}
                  </span>
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={() => { fetchTests(); toast.success("Actualisation…"); }} style={{ ...btnSecondary, fontSize:11, padding:"6px 12px" }} disabled={testsLoading}>
                      🔄 Actualiser
                    </button>
                    <button disabled={!peut("examens","create")} title={permBtnTitle("Ajouter manuellement",peut("examens","create"))} onClick={() => { setEditingItem(null); setShowTestModal(true); }} style={permBtnStyle(btnPrimary,peut("examens","create"))}>{peut("examens","create")?"+":"🔒"} Ajouter manuellement</button>
                  </div>
                </div>
                {testsLoading && <div style={{ textAlign:"center", padding:"30px", color:"#0891b2", fontSize:13 }}>⏳ Chargement des tests depuis la base de données…</div>}
                {/* Stats sources acquisition */}
                {Object.keys(sondageStats).length > 0 && (
                  <div style={{ background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:12, padding:"14px 18px", marginBottom:16 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#7c3aed", marginBottom:10 }}>🎯 Sources d'acquisition de vos clients</div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                      {Object.entries(sondageStats).sort((a,b)=>b[1]-a[1]).map(([src, count]) => {
                        const total = Object.values(sondageStats).reduce((s,v)=>s+v,0);
                        const pct = Math.round((count/total)*100);
                        return (
                          <div key={src} style={{ background:"#fff", border:"1px solid #ddd6fe", borderRadius:8, padding:"6px 12px", fontSize:11, display:"flex", alignItems:"center", gap:6 }}>
                            <span style={{ fontWeight:800, color:"#7c3aed" }}>{count}</span>
                            <span style={{ color:"#374151" }}>{src}</span>
                            <span style={{ color:"#a78bfa", fontSize:10 }}>({pct}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(() => {
                  const totalPages = Math.max(1, Math.ceil(filteredTests.length / TESTS_PER_PAGE));
                  const safePage   = Math.min(testPage, totalPages);
                  const pageTests  = filteredTests.slice((safePage - 1) * TESTS_PER_PAGE, safePage * TESTS_PER_PAGE);
                  return (
                    <>
                      <table style={{ width:"100%", borderCollapse:"collapse", display: testsLoading ? "none" : "table" }}>
                        <thead><tr style={{ background:"#f9fafb", fontSize:11, color:"#374151" }}>
                          <th style={th}>Candidat</th><th style={th}>Contact</th><th style={th}>Profil</th><th style={th}>Niveau</th><th style={th}>Score</th><th style={th}>Source</th><th style={th}>Offre recommandée</th><th style={th}>Date</th><th style={th}>Statut</th><th style={th}>Actions</th>
                        </tr></thead>
                        <tbody>
                          {pageTests.map(t => {
                            const st = STATUT_TEST[t.statut] || {};
                            const src = sourceClient(t.email);
                            return (
                              <tr key={t.id} style={{ borderTop:"1px solid #e5e7eb", fontSize:12 }}>
                                <td style={td}><div style={{ fontWeight:700 }}>{t.prenom} {t.nom}</div></td>
                                <td style={td}><div style={{ fontSize:11 }}>{t.telephone}</div><div style={{ fontSize:10,color:"#9ca3af" }}>{t.email}</div></td>
                                <td style={td}>{t.profil}</td>
                                <td style={td}><span style={{ fontWeight:800, color:BET_COLOR, background:BET_LIGHT, padding:"2px 8px", borderRadius:6 }}>{t.niveau}</span></td>
                                <td style={td}>
                                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                    <div style={{ width:50, height:5, background:"#e5e7eb", borderRadius:5, overflow:"hidden" }}>
                                      <div style={{ height:"100%", width:`${t.score}%`, background:t.score>=70?"#22c55e":t.score>=50?"#f59e0b":"#ef4444", borderRadius:5 }} />
                                    </div>
                                    <span style={{ fontWeight:700 }}>{t.score}%</span>
                                  </div>
                                </td>
                                <td style={td}>
                                  {src
                                    ? <span style={{ fontSize:11, background:"#f3e8ff", color:"#7c3aed", padding:"2px 8px", borderRadius:6, fontWeight:600 }}>{src}</span>
                                    : <span style={{ fontSize:10, color:"#d1d5db", fontStyle:"italic" }}>–</span>
                                  }
                                </td>
                                <td style={td}><span style={{ fontSize:11, color:"#6b7280" }}>{t.offreRecommandee||"-"}</span></td>
                                <td style={td}>{formatDate(t.date)}</td>
                                <td style={td}><Badge {...st} label={st.label||t.statut} /></td>
                                <td style={td}>
                                  <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                                    <button onClick={() => { setSelectedTest(t); setShowTestDetailModal(true); if (t.statut === "nouveau") changerStatutTest(t.id, "contacté"); }} style={{ ...btnIconEdit, background:"#e0f2fe", color:"#0369a1" }}>🔍 Voir</button>
                                    <button disabled={!peut("examens","update")} title={permBtnTitle("Modifier",peut("examens","update"))} onClick={() => { setEditingItem(t); setShowTestModal(true); }} style={permBtnStyle(btnIconEdit,peut("examens","update"))}>{peut("examens","update")?"✏️":"🔒"}</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          {!testsLoading && pageTests.length === 0 && (
                            <tr><td colSpan={10} style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af", fontSize:13 }}>Aucun test pour cette période</td></tr>
                          )}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {!testsLoading && filteredTests.length > TESTS_PER_PAGE && (
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:16, padding:"10px 4px" }}>
                          <span style={{ fontSize:12, color:"#6b7280" }}>
                            {(safePage - 1) * TESTS_PER_PAGE + 1}–{Math.min(safePage * TESTS_PER_PAGE, filteredTests.length)} sur {filteredTests.length} test{filteredTests.length > 1 ? "s" : ""}
                          </span>
                          <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                            <button
                              onClick={() => setTestPage(1)}
                              disabled={safePage === 1}
                              style={{ padding:"5px 10px", borderRadius:6, border:"1px solid #e2e8f0", background: safePage===1?"#f9fafb":"#fff", color: safePage===1?"#d1d5db":"#374151", cursor: safePage===1?"default":"pointer", fontSize:12, fontWeight:700 }}>
                              «
                            </button>
                            <button
                              onClick={() => setTestPage(p => Math.max(1, p - 1))}
                              disabled={safePage === 1}
                              style={{ padding:"5px 12px", borderRadius:6, border:"1px solid #e2e8f0", background: safePage===1?"#f9fafb":"#fff", color: safePage===1?"#d1d5db":"#374151", cursor: safePage===1?"default":"pointer", fontSize:12, fontWeight:700 }}>
                              ‹ Préc.
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                              .reduce((acc, p, idx, arr) => {
                                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…");
                                acc.push(p);
                                return acc;
                              }, [])
                              .map((p, idx) => p === "…"
                                ? <span key={`ellipsis-${idx}`} style={{ padding:"5px 4px", color:"#9ca3af", fontSize:12 }}>…</span>
                                : <button key={p} onClick={() => setTestPage(p)}
                                    style={{ padding:"5px 10px", borderRadius:6, border:`1px solid ${safePage===p?"#0891b2":"#e2e8f0"}`, background: safePage===p?"#0891b2":"#fff", color: safePage===p?"#fff":"#374151", cursor:"pointer", fontSize:12, fontWeight:700, minWidth:32 }}>
                                    {p}
                                  </button>
                              )}
                            <button
                              onClick={() => setTestPage(p => Math.min(totalPages, p + 1))}
                              disabled={safePage === totalPages}
                              style={{ padding:"5px 12px", borderRadius:6, border:"1px solid #e2e8f0", background: safePage===totalPages?"#f9fafb":"#fff", color: safePage===totalPages?"#d1d5db":"#374151", cursor: safePage===totalPages?"default":"pointer", fontSize:12, fontWeight:700 }}>
                              Suiv. ›
                            </button>
                            <button
                              onClick={() => setTestPage(totalPages)}
                              disabled={safePage === totalPages}
                              style={{ padding:"5px 10px", borderRadius:6, border:"1px solid #e2e8f0", background: safePage===totalPages?"#f9fafb":"#fff", color: safePage===totalPages?"#d1d5db":"#374151", cursor: safePage===totalPages?"default":"pointer", fontSize:12, fontWeight:700 }}>
                              »
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Tests non assignés (pas encore choisi de commerciale) */}
                {testsNonAssignes.length > 0 && myCommercialId && (
                  <div style={{ marginTop:24, padding:"14px 18px", borderRadius:12, background:"#fff7ed", border:"1px solid #fed7aa" }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                      <div>
                        <span style={{ fontSize:13, fontWeight:700, color:"#92400e" }}>
                          📥 {testsNonAssignes.length} test{testsNonAssignes.length > 1 ? "s" : ""} sans conseillère assignée
                        </span>
                        <div style={{ fontSize:11, color:"#b45309", marginTop:2 }}>Ces prospects n'ont pas encore choisi leur conseillère depuis leur espace.</div>
                      </div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      {testsNonAssignes.slice(0, 5).map((t, i) => (
                        <div key={t.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", borderRadius:8, background:"#fff", border:"1px solid #fde68a" }}>
                          <div style={{ width:32, height:32, borderRadius:"50%", background:"#fef3c7", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:12, color:"#92400e" }}>
                            {t.niveau}
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontWeight:600, fontSize:13, color:"#0f172a" }}>{t.prenom} {t.nom}</div>
                            <div style={{ fontSize:11, color:"#6b7280" }}>{t.email} · {t.date}</div>
                          </div>
                          <span style={{ fontSize:11, color:"#b45309", fontWeight:600 }}>En attente de choix</span>
                        </div>
                      ))}
                      {testsNonAssignes.length > 5 && (
                        <div style={{ fontSize:11, color:"#92400e", textAlign:"center", paddingTop:4 }}>
                          + {testsNonAssignes.length - 5} autre{testsNonAssignes.length - 5 > 1 ? "s" : ""}…
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}


            {/* ══ INSCRIPTIONS ══ */}
            {activeTab === "inscriptions" && (() => {
              const totalPages = Math.max(1, Math.ceil(filteredInscriptions.length / INSC_PER_PAGE));
              const pageClamped = Math.min(inscPage, totalPages);
              const pageItems = filteredInscriptions.slice((pageClamped-1)*INSC_PER_PAGE, pageClamped*INSC_PER_PAGE);
              return (
                <div>
                  {/* Barre outils */}
                  <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center", marginBottom:16 }}>
                    {/* Recherche */}
                    <input
                      placeholder="🔍 Rechercher client, email, formation…"
                      value={inscSearch}
                      onChange={e=>{ setInscSearch(e.target.value); setInscPage(1); }}
                      style={{ flex:1, minWidth:200, padding:"8px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, outline:"none" }}
                    />
                    {/* Filtre type */}
                    <div style={{ display:"flex", gap:6 }}>
                      {[
                        { val:"tous",     label:"Tous"              },
                        { val:"standard", label:"📚 Standard"       },
                        { val:"prive",    label:"🎯 Coaching Privé" },
                      ].map(f => (
                        <button key={f.val} onClick={()=>{ setInscTypeFiltre(f.val); setInscPage(1); }}
                          style={{ padding:"7px 14px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", border:`1.5px solid ${inscTypeFiltre===f.val?BET_COLOR:"#e2e8f0"}`, background:inscTypeFiltre===f.val?BET_COLOR+"15":"#f8fafc", color:inscTypeFiltre===f.val?BET_COLOR:"#6b7280" }}>
                          {f.label}
                        </button>
                      ))}
                    </div>
                    <button disabled={!peut("inscriptions","create")} title={permBtnTitle("Nouvelle inscription",peut("inscriptions","create"))} onClick={() => { setEditingItem(null); setShowInscriptionModal(true); }} style={permBtnStyle(btnPrimary,peut("inscriptions","create"))}>{peut("inscriptions","create")?"+":"🔒"} Nouvelle inscription</button>
                  </div>

                  {/* Compteur */}
                  <div style={{ fontSize:11, color:"#94a3b8", marginBottom:10 }}>
                    {filteredInscriptions.length} inscription{filteredInscriptions.length!==1?"s":""} · page {pageClamped}/{totalPages}
                  </div>

                  {/* Tableau */}
                  <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead>
                        <tr style={{ background:"#f8fafc" }}>
                          {["Client", "Formation", "Type", "Niveau", "Montant", "Début", "Statut", "Actions"].map(h => (
                            <th key={h} style={{ ...th, padding:"10px 12px", fontSize:11, fontWeight:700, color:"#374151", textAlign:"left", borderBottom:"1.5px solid #e2e8f0" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pageItems.length === 0 ? (
                          <tr><td colSpan={8} style={{ textAlign:"center", padding:32, color:"#94a3b8", fontSize:13 }}>Aucune inscription trouvée</td></tr>
                        ) : pageItems.map((insc, idx) => {
                          const isPrive = isOffreCoachingPrive(insc.offre);
                          return (
                            <tr key={insc.id} style={{ borderTop:"1px solid #f1f5f9", background:idx%2===0?"#fff":"#fafafa", fontSize:12 }}
                              onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                              onMouseLeave={e=>e.currentTarget.style.background=idx%2===0?"#fff":"#fafafa"}>
                              {/* Client */}
                              <td style={{ ...td, padding:"10px 12px" }}>
                                <div style={{ fontWeight:700, color:"#0f172a" }}>{insc.client}</div>
                                <div style={{ fontSize:10, color:"#6b7280" }}>{insc.telephone}</div>
                                {insc.email && <div style={{ fontSize:10, color:"#94a3b8" }}>{insc.email}</div>}
                              </td>
                              {/* Formation */}
                              <td style={{ ...td, padding:"10px 12px", maxWidth:180 }}>
                                <div style={{ fontWeight:600, color:"#0f172a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{insc.offre||"-"}</div>
                              </td>
                              {/* Type */}
                              <td style={{ ...td, padding:"10px 12px" }}>
                                <span style={{ fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:99, background:isPrive?"#faf5ff":"#eff6ff", color:isPrive?"#7c3aed":"#0369a1", border:`1px solid ${isPrive?"#ddd6fe":"#bfdbfe"}` }}>
                                  {isPrive ? "🎯 Privé" : "📚 Standard"}
                                </span>
                              </td>
                              {/* Niveau */}
                              <td style={{ ...td, padding:"10px 12px" }}>
                                {insc.niveauTest
                                  ? <span style={{ fontSize:11, fontWeight:800, padding:"2px 8px", borderRadius:99, background:"#4338ca18", color:"#4338ca" }}>{insc.niveauTest}</span>
                                  : <span style={{ color:"#94a3b8" }}>-</span>}
                              </td>
                              {/* Montant */}
                              <td style={{ ...td, padding:"10px 12px" }}>
                                <span style={{ fontWeight:700, color:"#15803d" }}>{formatMoney(insc.montant)}</span>
                              </td>
                              {/* Début */}
                              <td style={{ ...td, padding:"10px 12px", color:"#374151" }}>
                                {insc.dateDebut ? formatDate(insc.dateDebut) : "-"}
                              </td>
                              {/* Statut */}
                              <td style={{ ...td, padding:"10px 12px" }}>
                                <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                                  {insc.assigne
                                    ? <Badge label="🏫 Assigné" color="#166534" bg="#dcfce7" />
                                    : <Badge label={insc.statut==="confirmée"?"Confirmée":"En attente"} color={insc.statut==="confirmée"?"#15803d":"#92400e"} bg={insc.statut==="confirmée"?"#dcfce7":"#fef3c7"} />}
                                  {insc.apprenantConverti && <Badge label="🎓 Apprenant" color="#7c3aed" bg="#f3e8ff" />}
                                </div>
                              </td>
                              {/* Actions */}
                              <td style={{ ...td, padding:"10px 12px" }}>
                                <div style={{ display:"flex", gap:4, flexWrap:"nowrap", alignItems:"center" }}>
                                  <button onClick={() => { setSelectedInscription(insc); setShowInscDetailModal(true); }} style={{ ...btnIconEdit, background:"#e0f2fe", color:"#0369a1" }} title="Détail">🔍</button>
                                  {!insc.apprenantConverti && insc.statut==="confirmée" && !isPrive && (
                                    <button onClick={() => convertirEnApprenant(insc)} style={{ ...btnIconEdit, background:"#f3e8ff", color:"#7c3aed", borderColor:"#8b5cf640" }} title="Convertir en apprenant">🎓</button>
                                  )}
                                  {insc.documents?.length > 0 && (
                                    <button onClick={() => telechargerDocument(insc.documents[0], insc.client)} style={{ ...btnIconEdit, background:"#e0f2fe", color:"#0369a1" }} title={`${insc.documents.length} doc(s)`}>📎</button>
                                  )}
                                  {isPrive ? (
                                    <span style={{ fontSize:10, color:"#7c3aed", fontWeight:700, padding:"3px 8px", background:"#faf5ff", border:"1px solid #ddd6fe", borderRadius:99 }}>
                                      📨 Transmis au PA
                                    </span>
                                  ) : insc._source === "legacy" ? (
                                    <span style={{ fontSize:10, color:"#6b7280", fontWeight:700, padding:"3px 8px", background:"#f3f4f6", border:"1px solid #e5e7eb", borderRadius:99 }} title="Dossier créé avant la bascule vers Prospects - conservé en lecture seule">
                                      🕓 Ancienne inscription
                                    </span>
                                  ) : insc.assigne ? (
                                    <span style={{ fontSize:10, color:"#166534", fontWeight:700, padding:"3px 8px", background:"#dcfce7", border:"1px solid #bbf7d0", borderRadius:99 }} title="Classe/coach déjà assignés - se modifie désormais depuis Prospects parcours">
                                      🏫 Assigné - voir dans Prospects
                                    </span>
                                  ) : (
                                    <>
                                      <button disabled={!peut("inscriptions","update")} onClick={() => { setEditingItem(insc); setShowInscriptionModal(true); }} style={permBtnStyle(btnIconEdit,peut("inscriptions","update"))} title={permBtnTitle("Modifier",peut("inscriptions","update"))}>{peut("inscriptions","update")?"✏️":"🔒"}</button>
                                      <button disabled={!peut("inscriptions","delete")} onClick={() => handleDeleteInscription(insc)} style={permBtnStyle({ ...btnIconEdit, color:"#dc2626", background:"#fee2e2", borderColor:"#ef444440" },peut("inscriptions","delete"))} title={permBtnTitle("Supprimer",peut("inscriptions","delete"))}>{peut("inscriptions","delete")?"🗑️":"🔒"}</button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:6, marginTop:16 }}>
                      <button onClick={()=>setInscPage(p=>Math.max(1,p-1))} disabled={pageClamped===1}
                        style={{ padding:"6px 14px", borderRadius:8, border:"1px solid #e2e8f0", background: pageClamped===1?"#f1f5f9":"#fff", color:pageClamped===1?"#94a3b8":"#374151", cursor:pageClamped===1?"not-allowed":"pointer", fontWeight:700, fontSize:13 }}>←</button>
                      {Array.from({length:totalPages},(_,i)=>i+1).map(n=>(
                        <button key={n} onClick={()=>setInscPage(n)}
                          style={{ width:34, height:34, borderRadius:8, border:`1.5px solid ${pageClamped===n?BET_COLOR:"#e2e8f0"}`, background:pageClamped===n?BET_COLOR:"#fff", color:pageClamped===n?"#fff":"#374151", cursor:"pointer", fontWeight:700, fontSize:13 }}>
                          {n}
                        </button>
                      ))}
                      <button onClick={()=>setInscPage(p=>Math.min(totalPages,p+1))} disabled={pageClamped===totalPages}
                        style={{ padding:"6px 14px", borderRadius:8, border:"1px solid #e2e8f0", background:pageClamped===totalPages?"#f1f5f9":"#fff", color:pageClamped===totalPages?"#94a3b8":"#374151", cursor:pageClamped===totalPages?"not-allowed":"pointer", fontWeight:700, fontSize:13 }}>→</button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ══ PAIEMENTS (NOUVEAU) ══ */}
            {activeTab === "paiements" && paiementsSubTab === "touchpoint" && (() => {
              const TP_STATUT = {
                validé:    { label:"✅ Validé",            color:"#16a34a", bg:"#dcfce7" },
                en_attente:{ label:"⏳ En attente",         color:"#d97706", bg:"#fef3c7" },
                échoué:    { label:"❌ Échoué",             color:"#dc2626", bg:"#fee2e2" },
                annulé:    { label:"↩️ Annulé",             color:"#6b7280", bg:"#f3f4f6" },
              };
              // Même mécanisme que "Renvoyer le lien" dans l'onglet Prospects - permet au
              // client de reprendre directement le paiement sans refaire l'inscription.
              // paiements_touchpoint stocke déjà toutes les infos nécessaires sur la ligne.
              const renvoyerLienTouchPoint = (p) => {
                if (!p.client_email || !(Number(p.montant) > 0)) { toast.error("Informations insuffisantes pour générer un lien."); return; }
                const SITE = process.env.REACT_APP_FRONTEND_URL || "http://localhost:3000";
                const qs = new URLSearchParams({
                  nom: p.client_nom || "", email: p.client_email || "", telephone: p.client_telephone || "",
                  montant: String(p.montant), offre: p.offre_label || "Votre formation",
                  assignation_id: p.assignation_id || "", assistante_id: p.assistante_id || "",
                });
                const lien = `${SITE}/paiement/relancer?${qs.toString()}`;
                navigator.clipboard.writeText(lien).then(() => toast.success("Lien de paiement copié !")).catch(() => toast.error("Impossible de copier"));
              };
              const kpiAttenteDepasse = (p) => p.statut === "en_attente" && p.created_at && (Date.now() - new Date(p.created_at).getTime()) > 15*60*1000;
              const nonAboutiStatut = (p) => p.statut !== "validé"; // échoué, annulé, en_attente (frais ou dépassé) - tout ce qui peut être archivé/relancé

              const now2 = new Date();
              const tpFiltered = touchpointPaiements.filter(p => {
                if (paiSearchQ && !`${p.client_nom} ${p.client_email} ${p.client_telephone} ${p.order_number}`.toLowerCase().includes(paiSearchQ.toLowerCase())) return false;
                if (paiStatut !== "tous" && p.statut !== paiStatut) return false;
                if (paiPeriode !== "tous") {
                  const d = new Date(p.created_at);
                  if (paiPeriode === "semaine") { const w=new Date(); w.setDate(now2.getDate()-7); if(d<w) return false; }
                  else if (paiPeriode === "mois")      { if(d.getMonth()!==now2.getMonth()||d.getFullYear()!==now2.getFullYear()) return false; }
                  else if (paiPeriode === "trimestre") { if(Math.floor(d.getMonth()/3)!==Math.floor(now2.getMonth()/3)||d.getFullYear()!==now2.getFullYear()) return false; }
                  else if (paiPeriode === "annee")     { if(d.getFullYear()!==now2.getFullYear()) return false; }
                }
                return true;
              }).sort((a,b) => new Date(b.created_at||0) - new Date(a.created_at||0));

              const kpiValide  = tpFiltered.filter(p=>p.statut==="validé").length;
              const kpiAttente = tpFiltered.filter(p=>p.statut==="en_attente" && !kpiAttenteDepasse(p)).length;
              const kpiProbEchoue = tpFiltered.filter(kpiAttenteDepasse).length;
              const kpiEchoue  = tpFiltered.filter(p=>p.statut==="échoué").length + kpiProbEchoue;
              const kpiMontantValide = tpFiltered.filter(p=>p.statut==="validé").reduce((s,p)=>s+(Number(p.montant)||0),0);

              // ── Regroupement par client (email, sinon téléphone, sinon nom) - une tentative
              // à répétition ne doit pas polluer le tableau d'une ligne par essai.
              const groupsMap = {};
              tpFiltered.forEach(p => {
                const key = (p.client_email || p.client_telephone || p.client_nom || "inconnu").toLowerCase();
                if (!groupsMap[key]) groupsMap[key] = { key, client_nom:p.client_nom, client_email:p.client_email, client_telephone:p.client_telephone, attempts:[] };
                groupsMap[key].attempts.push(p);
              });
              let tpGroups = Object.values(groupsMap).map(g => {
                const sorted = [...g.attempts].sort((a,b)=> new Date(b.created_at||0)-new Date(a.created_at||0));
                return { ...g, attempts: sorted, latest: sorted[0], nbEchecsArchivables: sorted.filter(nonAboutiStatut).length };
              });

              // ── Tri par colonne (sur la tentative la plus récente de chaque client) ──
              const sortVal = (g) => {
                if (tpSortKey === "client")  return (g.client_nom || "").toLowerCase();
                if (tpSortKey === "montant") return Number(g.latest.montant) || 0;
                if (tpSortKey === "statut")  return g.latest.statut || "";
                return new Date(g.latest.created_at || 0).getTime();
              };
              tpGroups.sort((a,b) => {
                const va = sortVal(a), vb = sortVal(b);
                const cmp = va < vb ? -1 : va > vb ? 1 : 0;
                return tpSortDir === "asc" ? cmp : -cmp;
              });
              const toggleSort = (key) => {
                if (tpSortKey === key) setTpSortDir(d => d==="asc"?"desc":"asc");
                else { setTpSortKey(key); setTpSortDir(key==="client"?"asc":"desc"); }
              };
              const sortIcon = (key) => tpSortKey===key ? (tpSortDir==="asc"?" ▲":" ▼") : "";

              const totalPagesTp = Math.max(1, Math.ceil(tpGroups.length / PAI_PAGE_SIZE));
              const safePageTp   = Math.min(paiPage, totalPagesTp);
              const tpPage_      = tpGroups.slice((safePageTp-1)*PAI_PAGE_SIZE, safePageTp*PAI_PAGE_SIZE);

              return (
              <div>
                {/* ── Sous-onglets Paiements ── */}
                <div style={{ display:"flex", gap:4, marginBottom:20, background:"#f1f5f9", borderRadius:10, padding:4, width:"fit-content" }}>
                  {[
                    { key:"manuel",     label:"💳 Paiements manuels" },
                    { key:"touchpoint", label:"🌐 TouchPoint paiement", badge: touchpointPaiements.length },
                  ].map(st => (
                    <button key={st.key} onClick={() => { setPaiementsSubTab(st.key); setPaiPage(1); setPaiSearchQ(""); setPaiStatut("tous"); }}
                      style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:8, border:"none", background: paiementsSubTab===st.key?"#fff":"transparent", color: paiementsSubTab===st.key?BET_COLOR:"#64748b", fontWeight:700, fontSize:13, cursor:"pointer", boxShadow: paiementsSubTab===st.key?"0 1px 3px rgba(0,0,0,0.1)":"none" }}>
                      {st.label}
                      {st.badge > 0 && <span style={{ background:"#0891b2", color:"#fff", borderRadius:999, padding:"1px 7px", fontSize:10, fontWeight:800 }}>{st.badge}</span>}
                    </button>
                  ))}
                </div>

                {touchpointPaiementsLoading ? (
                  <div style={{ textAlign:"center", padding:"40px 0", color:"#94a3b8", fontSize:13 }}>Chargement…</div>
                ) : (
                <>
                {/* KPI row */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:20 }}>
                  <StatCard label="Montant validé" value={Number(kpiMontantValide).toLocaleString("fr-FR")+" F"} color="#22c55e" icon="✅" sub={`${kpiValide} transaction${kpiValide>1?"s":""}`} />
                  <StatCard label="En attente"     value={kpiAttente} color="#d97706" icon="⏳" />
                  <StatCard label="Échoués"        value={kpiEchoue}  color="#dc2626" icon="❌" sub={kpiProbEchoue>0?`dont ${kpiProbEchoue} probablement échoué${kpiProbEchoue>1?"s":""}`:undefined} />
                  <StatCard label="Total"          value={tpFiltered.length} color="#0891b2" icon="🌐" />
                </div>

                {/* Barre filtres */}
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginBottom:14, padding:"12px 14px", background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0" }}>
                  <input
                    value={paiSearchQ}
                    onChange={e=>{ setPaiSearchQ(e.target.value); setPaiPage(1); }}
                    placeholder="🔍 Rechercher client, email, tél, réf…"
                    style={{ ...inputSt, width:220, margin:0 }}
                  />
                  <select value={paiStatut} onChange={e=>{ setPaiStatut(e.target.value); setPaiPage(1); }} style={{ ...inputSt, width:170, margin:0 }}>
                    <option value="tous">Tous les statuts</option>
                    <option value="validé">✅ Validé</option>
                    <option value="en_attente">⏳ En attente</option>
                    <option value="échoué">❌ Échoué</option>
                    <option value="annulé">↩️ Annulé</option>
                  </select>
                  <select value={paiPeriode} onChange={e=>{ setPaiPeriode(e.target.value); setPaiPage(1); }} style={{ ...inputSt, width:150, margin:0 }}>
                    <option value="tous">Toutes périodes</option>
                    <option value="semaine">Cette semaine</option>
                    <option value="mois">Ce mois</option>
                    <option value="trimestre">Ce trimestre</option>
                    <option value="annee">Cette année</option>
                  </select>
                  {(paiSearchQ||paiStatut!=="tous"||paiPeriode!=="tous") && (
                    <button onClick={()=>{ setPaiSearchQ(""); setPaiStatut("tous"); setPaiPeriode("tous"); setPaiPage(1); }} style={{ ...btnSecondary, padding:"6px 12px", margin:0, fontSize:11 }}>✕ Réinitialiser</button>
                  )}
                  <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#374151", cursor:"pointer", marginLeft:"auto" }}>
                    <input type="checkbox" checked={tpShowArchives} onChange={e=>{ const v=e.target.checked; setTpShowArchives(v); setPaiPage(1); fetchTouchpointPaiements(v); }} />
                    🗄️ Voir les archivés
                  </label>
                  <button onClick={()=>fetchTouchpointPaiements()} style={{ ...btnSecondary, padding:"6px 12px", margin:0, fontSize:11 }}>🔄 Actualiser</button>
                </div>

                {/* Table */}
                {tpGroups.length === 0 ? (
                  <div style={{ textAlign:"center", padding:"40px 0", color:"#94a3b8", fontSize:13 }}>
                    {tpShowArchives ? "Aucun paiement archivé." : "Aucun paiement TouchPoint pour le moment."}
                  </div>
                ) : (
                  <>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead>
                      <tr style={{ background:"#f9fafb", fontSize:11 }}>
                        <th style={{ ...th, cursor:"pointer", userSelect:"none" }} onClick={()=>toggleSort("client")}>Client{sortIcon("client")}</th>
                        <th style={th}>Offre</th>
                        <th style={{ ...th, cursor:"pointer", userSelect:"none" }} onClick={()=>toggleSort("montant")}>Montant{sortIcon("montant")}</th>
                        <th style={th}>Opérateur</th>
                        <th style={th}>Référence</th>
                        <th style={{ ...th, cursor:"pointer", userSelect:"none" }} onClick={()=>toggleSort("date")}>Date{sortIcon("date")}</th>
                        <th style={{ ...th, cursor:"pointer", userSelect:"none" }} onClick={()=>toggleSort("statut")}>Statut{sortIcon("statut")}</th>
                        <th style={th}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tpPage_.map(g => {
                        const p = g.latest;
                        const enAttenteDepasse = kpiAttenteDepasse(p);
                        const st = enAttenteDepasse
                          ? { label:"⚠️ Probablement échoué", color:"#9a3412", bg:"#ffedd5" }
                          : (TP_STATUT[p.statut] || { label:p.statut||"-", color:"#374151", bg:"#f3f4f6" });
                        const openDetail = () => { setTpDetailTarget(p); setShowTpDetailModal(true); };
                        const expanded = tpExpandedGroups.has(g.key);
                        const toggleExpand = () => setTpExpandedGroups(prev => { const n=new Set(prev); n.has(g.key)?n.delete(g.key):n.add(g.key); return n; });
                        return (
                          <React.Fragment key={g.key}>
                          <tr onClick={openDetail}
                            style={{ borderTop:"1px solid #e5e7eb", fontSize:12, cursor:"pointer" }}
                            onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                            onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                            <td style={td}>
                              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                {g.attempts.length > 1 && (
                                  <button onClick={e=>{ e.stopPropagation(); toggleExpand(); }} title={expanded?"Replier":"Voir toutes les tentatives"}
                                    style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:"#64748b", padding:0 }}>{expanded?"▾":"▸"}</button>
                                )}
                                <div>
                                  <div style={{ fontWeight:700 }}>{g.client_nom || "-"}</div>
                                  {g.client_email    && <div style={{ fontSize:10, color:"#9ca3af" }}>✉ {g.client_email}</div>}
                                  {g.client_telephone && <div style={{ fontSize:10, color:"#9ca3af" }}>📞 {g.client_telephone}</div>}
                                </div>
                              </div>
                              {g.attempts.length > 1 && (
                                <span onClick={e=>{ e.stopPropagation(); toggleExpand(); }} style={{ display:"inline-block", marginTop:3, marginLeft:17, background:"#e0f2fe", color:"#0369a1", borderRadius:999, padding:"1px 7px", fontSize:10, fontWeight:700, cursor:"pointer" }}>
                                  {g.attempts.length} tentatives
                                </span>
                              )}
                            </td>
                            <td style={td}><div style={{ maxWidth:200 }}>{p.offre_label || "–"}</div></td>
                            <td style={td}><span style={{ fontWeight:700 }}>{formatMoney(p.montant)}</span></td>
                            <td style={td}>{p.payment_mode || "–"}</td>
                            <td style={td}><span style={{ fontFamily:"monospace", fontSize:11 }}>{p.order_number || "–"}</span></td>
                            <td style={td}>{formatDate(p.created_at)}</td>
                            <td style={td}><Badge label={st.label} color={st.color} bg={st.bg} /></td>
                            <td style={td} onClick={e=>e.stopPropagation()}>
                              <div style={{ display:"flex", flexDirection:"column", gap:4, alignItems:"flex-start" }}>
                                {nonAboutiStatut(p) && p.client_email && Number(p.montant) > 0 && !tpShowArchives && (
                                  <button onClick={()=>renvoyerLienTouchPoint(p)} title="Copier un nouveau lien de paiement à envoyer au client (WhatsApp, SMS…)"
                                    style={{ background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe", borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                                    🔄 Renvoyer le lien
                                  </button>
                                )}
                                {!tpShowArchives && g.nbEchecsArchivables > 0 && (
                                  <button onClick={()=>archiverTouchPoint(g.attempts.filter(nonAboutiStatut).map(a=>a.id), true)} title="Masquer les tentatives non abouties de ce client (conservées en base, réversible)"
                                    style={{ background:"#fef2f2", color:"#b91c1c", border:"1px solid #fecaca", borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                                    🗄️ Archiver ({g.nbEchecsArchivables})
                                  </button>
                                )}
                                {tpShowArchives && (
                                  <button onClick={()=>archiverTouchPoint(g.attempts.map(a=>a.id), false)} title="Restaurer ces tentatives dans la vue principale"
                                    style={{ background:"#f0fdf4", color:"#15803d", border:"1px solid #bbf7d0", borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                                    ↩️ Désarchiver
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {expanded && g.attempts.map(a => {
                            const aDepasse = kpiAttenteDepasse(a);
                            const ast = aDepasse ? { label:"⚠️ Probablement échoué", color:"#9a3412", bg:"#ffedd5" } : (TP_STATUT[a.statut] || { label:a.statut||"-", color:"#374151", bg:"#f3f4f6" });
                            return (
                              <tr key={a.id} onClick={()=>{ setTpDetailTarget(a); setShowTpDetailModal(true); }}
                                style={{ borderTop:"1px solid #f1f5f9", fontSize:11, background:"#fafbfc", cursor:"pointer" }}>
                                <td style={{ ...td, paddingLeft:34, color:"#94a3b8" }}>↳ tentative</td>
                                <td style={td}>{a.offre_label || "–"}</td>
                                <td style={td}>{formatMoney(a.montant)}</td>
                                <td style={td}>{a.payment_mode || "–"}</td>
                                <td style={td}><span style={{ fontFamily:"monospace" }}>{a.order_number || "–"}</span></td>
                                <td style={td}>{formatDate(a.created_at)}</td>
                                <td style={td}><Badge label={ast.label} color={ast.color} bg={ast.bg} /></td>
                                <td style={td} onClick={e=>e.stopPropagation()}>
                                  {!tpShowArchives ? (
                                    nonAboutiStatut(a) && (
                                      <button onClick={()=>archiverTouchPoint([a.id], true)} title="Archiver cette tentative"
                                        style={{ background:"#fef2f2", color:"#b91c1c", border:"1px solid #fecaca", borderRadius:6, padding:"3px 8px", fontSize:10, fontWeight:700, cursor:"pointer" }}>
                                        🗄️
                                      </button>
                                    )
                                  ) : (
                                    <button onClick={()=>archiverTouchPoint([a.id], false)} title="Restaurer cette tentative"
                                      style={{ background:"#f0fdf4", color:"#15803d", border:"1px solid #bbf7d0", borderRadius:6, padding:"3px 8px", fontSize:10, fontWeight:700, cursor:"pointer" }}>
                                      ↩️
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {totalPagesTp > 1 && (
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:14, padding:"10px 4px" }}>
                      <span style={{ fontSize:11, color:"#6b7280" }}>
                        {(safePageTp-1)*PAI_PAGE_SIZE+1}–{Math.min(safePageTp*PAI_PAGE_SIZE, tpGroups.length)} sur {tpGroups.length} client{tpGroups.length>1?"s":""}
                      </span>
                      <div style={{ display:"flex", gap:4 }}>
                        <button onClick={()=>setPaiPage(1)} disabled={safePageTp===1} style={{ ...btnIconEdit, opacity:safePageTp===1?.4:1 }}>«</button>
                        <button onClick={()=>setPaiPage(p=>Math.max(1,p-1))} disabled={safePageTp===1} style={{ ...btnIconEdit, opacity:safePageTp===1?.4:1 }}>‹</button>
                        {Array.from({length:totalPagesTp},(_,i)=>i+1).filter(n=>Math.abs(n-safePageTp)<=2).map(n=>(
                          <button key={n} onClick={()=>setPaiPage(n)} style={{ ...btnIconEdit, background:n===safePageTp?BET_COLOR:"#fff", color:n===safePageTp?"#fff":"#374151", fontWeight:n===safePageTp?700:400 }}>{n}</button>
                        ))}
                        <button onClick={()=>setPaiPage(p=>Math.min(totalPagesTp,p+1))} disabled={safePageTp===totalPagesTp} style={{ ...btnIconEdit, opacity:safePageTp===totalPagesTp?.4:1 }}>›</button>
                        <button onClick={()=>setPaiPage(totalPagesTp)} disabled={safePageTp===totalPagesTp} style={{ ...btnIconEdit, opacity:safePageTp===totalPagesTp?.4:1 }}>»</button>
                      </div>
                    </div>
                  )}
                  </>
                )}
                </>
                )}
              </div>
              );
            })()}

            {/* ══ PAIEMENTS (NOUVEAU) - sous-onglet manuel ══ */}
            {activeTab === "paiements" && paiementsSubTab === "manuel" && (() => {
              const touchpointCount = touchpointPaiements.length;
              // ── Filtrage ────────────────────────────────────────
              const now2 = new Date();
              const basePaiements = paiements;
              const paiFiltered = basePaiements.filter(p => {
                if (paiSearchQ && !`${p.client} ${p.email} ${p.telephone} ${p.refTransaction}`.toLowerCase().includes(paiSearchQ.toLowerCase())) return false;
                if (paiStatut !== "tous" && p.statut !== paiStatut) return false;
                if (paiMode   !== "tous" && p.mode   !== paiMode)   return false;
                if (paiPeriode !== "tous") {
                  const d = new Date(p.date);
                  if (paiPeriode === "semaine") { const w=new Date(); w.setDate(now2.getDate()-7); if(d<w) return false; }
                  else if (paiPeriode === "mois")      { if(d.getMonth()!==now2.getMonth()||d.getFullYear()!==now2.getFullYear()) return false; }
                  else if (paiPeriode === "trimestre") { if(Math.floor(d.getMonth()/3)!==Math.floor(now2.getMonth()/3)||d.getFullYear()!==now2.getFullYear()) return false; }
                  else if (paiPeriode === "annee")     { if(d.getFullYear()!==now2.getFullYear()) return false; }
                }
                return true;
              });

              // ── Regroupement par apprenant/client - une ligne par personne, pas par
              // versement. Clé = assignationId quand connu (fiable), sinon email/nom pour les
              // paiements isolés jamais liés à un dossier.
              const groupesMap = {};
              paiFiltered.forEach(p => {
                const key = p.assignationId || `noass_${(p.email || p.client || "").toLowerCase()}`;
                if (!groupesMap[key]) groupesMap[key] = { key, assignationId: p.assignationId || null, client: p.client, email: p.email, telephone: p.telephone, pmts: [] };
                groupesMap[key].pmts.push(p);
              });
              const paiementsGroupes = Object.values(groupesMap).map(g => {
                // montantDu est le total dû répété sur chaque versement, jamais un montant par
                // versement - le sommer double-compterait dès qu'il y a plus d'un versement.
                const montantDu   = g.pmts.length ? Math.max(...g.pmts.map(p => p.montantDu || 0)) : 0;
                const montantReçu = g.pmts.reduce((s,p) => s + (p.montantReçu || 0), 0);
                const reste       = montantDu - montantReçu;
                const sorted      = [...g.pmts].sort((a,b) => new Date(b.date||0) - new Date(a.date||0));
                const dernier     = sorted[0] || {};
                const statutGroupe = reste <= 0 && montantDu > 0 ? "paye" : montantReçu > 0 ? "partiel" : "en_attente";
                return { ...g, montantDu, montantReçu, reste, date: dernier.date, mode: dernier.mode, inscription: dernier.inscription, statutGroupe };
              }).sort((a,b) => new Date(b.date||0) - new Date(a.date||0));

              // ── KPI sur données filtrées (par client, pas par versement) ─────
              const kpiTotalEncaisse  = paiementsGroupes.reduce((s,g) => s + g.montantReçu, 0);
              const kpiTotalDu        = paiementsGroupes.reduce((s,g) => s + g.montantDu, 0);
              const kpiReste          = kpiTotalDu - kpiTotalEncaisse;
              const kpiNbComplet      = paiementsGroupes.filter(g => g.statutGroupe==="paye").length;
              const kpiNbPartiel      = paiementsGroupes.filter(g => g.statutGroupe==="partiel").length;
              const kpiNbAttente      = paiementsGroupes.filter(g => g.statutGroupe==="en_attente").length;

              // ── Pagination (par client) ──────────────────────────
              const totalPages = Math.max(1, Math.ceil(paiementsGroupes.length / PAI_PAGE_SIZE));
              const safePage   = Math.min(paiPage, totalPages);
              const paiPage_   = paiementsGroupes.slice((safePage-1)*PAI_PAGE_SIZE, safePage*PAI_PAGE_SIZE);

              // ── Modes uniques pour le filtre ────────────────────
              const modesUniques = [...new Set(basePaiements.map(p=>p.mode).filter(Boolean))];

              return (
              <div>
                {/* ── Sous-onglets Paiements ── */}
                <div style={{ display:"flex", gap:4, marginBottom:20, background:"#f1f5f9", borderRadius:10, padding:4, width:"fit-content" }}>
                  {[
                    { key:"manuel",     label:"💳 Paiements manuels" },
                    { key:"touchpoint", label:"🌐 TouchPoint paiement", badge: touchpointCount },
                  ].map(st => (
                    <button key={st.key} onClick={() => { setPaiementsSubTab(st.key); setPaiPage(1); }}
                      style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:8, border:"none", background: paiementsSubTab===st.key?"#fff":"transparent", color: paiementsSubTab===st.key?BET_COLOR:"#64748b", fontWeight:700, fontSize:13, cursor:"pointer", boxShadow: paiementsSubTab===st.key?"0 1px 3px rgba(0,0,0,0.1)":"none" }}>
                      {st.label}
                      {st.badge > 0 && <span style={{ background:"#0891b2", color:"#fff", borderRadius:999, padding:"1px 7px", fontSize:10, fontWeight:800 }}>{st.badge}</span>}
                    </button>
                  ))}
                </div>

                {/* KPI row */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:10, marginBottom:20 }}>
                  <StatCard label="Total encaissé"  value={Number(kpiTotalEncaisse).toLocaleString("fr-FR")+" F"} color="#22c55e" icon="✅" sub={`${paiFiltered.length} paiement${paiFiltered.length>1?"s":""}`} />
                  <StatCard label="Total dû"         value={Number(kpiTotalDu).toLocaleString("fr-FR")+" F"}       color="#0891b2" icon="📋" />
                  <StatCard label="Reste à percevoir" value={Number(kpiReste>0?kpiReste:0).toLocaleString("fr-FR")+" F"} color={kpiReste>0?"#ef4444":"#22c55e"} icon="⏳" />
                  <StatCard label="Paiements complets" value={kpiNbComplet} color="#22c55e" icon="✔️" />
                  <StatCard label="Partiels"          value={kpiNbPartiel} color="#f59e0b" icon="🔄" />
                  <StatCard label="En attente"        value={kpiNbAttente} color="#ef4444" icon="⚠️" />
                </div>

                {/* Barre filtres */}
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginBottom:14, padding:"12px 14px", background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0" }}>
                  <input
                    value={paiSearchQ}
                    onChange={e=>{ setPaiSearchQ(e.target.value); setPaiPage(1); }}
                    placeholder="🔍 Rechercher client, email, tél, réf…"
                    style={{ ...inputSt, width:220, margin:0 }}
                  />
                  <select value={paiStatut} onChange={e=>{ setPaiStatut(e.target.value); setPaiPage(1); }} style={{ ...inputSt, width:160, margin:0 }}>
                    <option value="tous">Tous les statuts</option>
                    <option value="en_attente">⏳ En attente</option>
                    <option value="partiel">🔄 Partiel</option>
                    <option value="paye">✅ Reçu</option>
                    <option value="remboursé">↩️ Remboursé</option>
                  </select>
                  <select value={paiMode} onChange={e=>{ setPaiMode(e.target.value); setPaiPage(1); }} style={{ ...inputSt, width:170, margin:0 }}>
                    <option value="tous">Tous les modes</option>
                    {modesUniques.map(m=><option key={m} value={m}>{m}</option>)}
                  </select>
                  <select value={paiPeriode} onChange={e=>{ setPaiPeriode(e.target.value); setPaiPage(1); }} style={{ ...inputSt, width:150, margin:0 }}>
                    <option value="tous">Toutes périodes</option>
                    <option value="semaine">Cette semaine</option>
                    <option value="mois">Ce mois</option>
                    <option value="trimestre">Ce trimestre</option>
                    <option value="annee">Cette année</option>
                  </select>
                  {(paiSearchQ||paiStatut!=="tous"||paiMode!=="tous"||paiPeriode!=="tous") && (
                    <button onClick={()=>{ setPaiSearchQ(""); setPaiStatut("tous"); setPaiMode("tous"); setPaiPeriode("tous"); setPaiPage(1); }} style={{ ...btnSecondary, padding:"6px 12px", margin:0, fontSize:11 }}>✕ Réinitialiser</button>
                  )}
                  {/* {paiementsSubTab === "manuel" && (
                    <div style={{ marginLeft:"auto" }}>
                      <button disabled={!peut("finances","create")} title={permBtnTitle("Nouveau paiement",peut("finances","create"))} onClick={() => { setEditingItem(null); setShowPaiementModal(true); }} style={permBtnStyle(btnPrimary,peut("finances","create"))}>{peut("finances","create")?"+":"🔒"} Nouveau paiement</button>
                    </div>
                  )} */}
                </div>

                {/* Table */}
                {paiementsGroupes.length === 0 ? (
                  <div style={{ textAlign:"center", padding:"40px 0", color:"#94a3b8", fontSize:13 }}>
                    {paiementsSubTab === "touchpoint" ? "Aucun paiement TouchPoint pour le moment." : "Aucun paiement ne correspond aux filtres."}
                  </div>
                ) : (
                  <>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead>
                      <tr style={{ background:"#f9fafb", fontSize:11 }}>
                        <th style={th}>Client</th>
                        <th style={th}>Formation / Réf.</th>
                        <th style={th}>Dû</th>
                        <th style={th}>Reçu</th>
                        <th style={th}>Reste</th>
                        <th style={th}>Date</th>
                        <th style={th}>Mode</th>
                        <th style={th}>Statut</th>
                        <th style={th}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paiPage_.map(g => {
                        const st = STATUT_PAIEMENT[g.statutGroupe] || {};
                        const aLie = g.assignationId ? assignations.find(a => a.id === g.assignationId) : null;
                        const openHistorique = () => {
                          setPmtListTarget(aLie || { id: g.assignationId, prospect_nom: g.client, prospect_email: g.email, prospect_telephone: g.telephone, pmts: g.pmts });
                          setShowPmtListModal(true);
                        };
                        return (
                          <tr key={g.key} onClick={openHistorique}
                            style={{ borderTop:"1px solid #e5e7eb", fontSize:12, background: g.assignationId ? "#fffdf5" : "#fff", cursor:"pointer" }}
                            onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                            onMouseLeave={e=>e.currentTarget.style.background=g.assignationId ? "#fffdf5" : "#fff"}>
                            <td style={td}>
                              <div style={{ fontWeight:700 }}>{g.client}</div>
                              {g.email     && <div style={{ fontSize:10, color:"#9ca3af" }}>✉ {g.email}</div>}
                              {g.telephone && <div style={{ fontSize:10, color:"#9ca3af" }}>📞 {g.telephone}</div>}
                              {g.assignationId && <span style={{ display:"inline-block", marginTop:3, background:"#fef3c7", color:"#d97706", borderRadius:999, padding:"1px 7px", fontSize:10, fontWeight:700 }}>🎯 Prospect</span>}
                            </td>
                            <td style={td}>
                              <div style={{ maxWidth:200 }}>{g.inscription || "–"}</div>
                              <div style={{ marginTop:3, fontSize:10, color:"#6b7280" }}>{g.pmts.length} versement{g.pmts.length>1?"s":""}</div>
                            </td>
                            <td style={td}>{formatMoney(g.montantDu)}</td>
                            <td style={td}><span style={{ fontWeight:700, color:"#22c55e" }}>{formatMoney(g.montantReçu)}</span></td>
                            <td style={td}><span style={{ fontWeight:700, color:g.reste>0?"#ef4444":"#22c55e" }}>{g.reste>0?formatMoney(g.reste):"–"}</span></td>
                            <td style={td}>{formatDate(g.date)}</td>
                            <td style={td}>{g.mode||"–"}</td>
                            <td style={td}><Badge {...st} label={st.label||g.statutGroupe} /></td>
                            <td style={td} onClick={e=>e.stopPropagation()}>
                              <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                                <button onClick={openHistorique} title="Voir l'historique des paiements" style={{ ...btnIconEdit, background:"#eff6ff", color:"#1d4ed8", borderColor:"#bfdbfe" }}>👁 Historique</button>
                                {paiementsSubTab === "manuel" && g.assignationId && (() => {
                                  const full = g.montantDu > 0 && g.reste <= 0;
                                  return (
                                    <button
                                      disabled={full || !peut("finances","create")}
                                      title={!peut("finances","create") ? permBtnTitle("Ajouter un versement",false) : full ? "Paiement à jour" : "Ajouter le reste du paiement pour cet apprenant"}
                                      onClick={() => {
                                        if (!aLie || full || !peut("finances","create")) return;
                                        const ml = libelleCanal(aLie.type_cours, aLie.centre_nom);
                                        const tl = ["groupe","standard"].includes(aLie.type_coaching)?"Coaching groupe":aLie.type_coaching==="prive"?"Coaching privé":"Formation";
                                        setEditingItem({ client:aLie.prospect_nom||"", email:aLie.prospect_email||"", telephone:aLie.prospect_telephone||"", inscription:`Parcours BET · ${ml} · ${tl}`, montantDu:g.montantDu||0, montantReçu:0, dejaReçu:g.montantReçu||0, date:new Date().toISOString().slice(0,10), mode:"Mobile Money", statut:"en_attente", notes:"", refTransaction:"", assignationId:aLie.id, _isApprenant: true, packCoachingLabel: aLie.type_coaching === "prive" ? `${aLie.suivi_demarrage?.type_coaching === "intensive" ? "Intensive - 5 séances/sem" : "Classique - 3 séances × 2h/sem"}${aLie.suivi_demarrage?.lieu_coaching ? " · " + (aLie.suivi_demarrage.lieu_coaching === "domicile" ? "À domicile" : aLie.suivi_demarrage.lieu_coaching === "en_ligne" ? "En ligne" : "Au cabinet") : ""}` : null, formuleGroupe: aLie.suivi_demarrage?.formule_groupe || null });
                                        setShowPaiementModal(true);
                                      }}
                                      style={{ ...btnIconEdit, background: full?"#f1f5f9":"#fffbeb", color: full?"#94a3b8":"#d97706", borderColor: full?"#e2e8f0":"#fde68a", cursor: full?"not-allowed":"pointer", opacity: full?0.6:1 }}
                                    >{full ? "✅" : "➕ Reste"}</button>
                                  );
                                })()}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:14, padding:"10px 4px" }}>
                      <span style={{ fontSize:11, color:"#6b7280" }}>
                        {(safePage-1)*PAI_PAGE_SIZE+1}–{Math.min(safePage*PAI_PAGE_SIZE, paiementsGroupes.length)} sur {paiementsGroupes.length} client{paiementsGroupes.length>1?"s":""}
                      </span>
                      <div style={{ display:"flex", gap:4 }}>
                        <button onClick={()=>setPaiPage(1)} disabled={safePage===1} style={{ ...btnIconEdit, opacity:safePage===1?.4:1 }}>«</button>
                        <button onClick={()=>setPaiPage(p=>Math.max(1,p-1))} disabled={safePage===1} style={{ ...btnIconEdit, opacity:safePage===1?.4:1 }}>‹</button>
                        {Array.from({length:totalPages},(_,i)=>i+1).filter(n=>Math.abs(n-safePage)<=2).map(n=>(
                          <button key={n} onClick={()=>setPaiPage(n)} style={{ ...btnIconEdit, background:n===safePage?BET_COLOR:"#fff", color:n===safePage?"#fff":"#374151", fontWeight:n===safePage?700:400 }}>{n}</button>
                        ))}
                        <button onClick={()=>setPaiPage(p=>Math.min(totalPages,p+1))} disabled={safePage===totalPages} style={{ ...btnIconEdit, opacity:safePage===totalPages?.4:1 }}>›</button>
                        <button onClick={()=>setPaiPage(totalPages)} disabled={safePage===totalPages} style={{ ...btnIconEdit, opacity:safePage===totalPages?.4:1 }}>»</button>
                      </div>
                    </div>
                  )}
                  </>
                )}
              </div>
              );
            })()}

            {/* ══ MES CLASSES ══ */}
            {/* Le planning vit dans Components/PlanningSeances : l'onboarding monte
                exactement le même. Le dupliquer aurait produit deux écrans décrivant
                différemment le même cours, chacun corrigé de son côté. */}
            {activeTab === "plannings" && <PlanningSeances {...planning} />}

            {activeTab === "reaffecter" && (
              <FileDemandesTransfert accent={BET_COLOR} authHdrs={authHeaders} mode="onboarding"
                onReaffecter={ouvrirTransfertDepuisDemande} refreshKey={refreshReaffecter} />
            )}

            {activeTab === "classes" && (() => {
              // NIVEAUX, FILIERES, TYPE_COURS, CECRL_COLORS, STATUT_CLASSE définis au niveau module

              // Filtrage + Pagination
              const filteredClasses = classes.filter(cl => {
                if (classesNiveauFil && cl.niveau !== classesNiveauFil) return false;
                if (classesStatutFil && cl.statut !== classesStatutFil) return false;
                if (classesDateDebut && cl.date_debut && cl.date_debut < classesDateDebut) return false;
                if (classesDateFin   && cl.date_debut && cl.date_debut > classesDateFin)   return false;
                return true;
              });
              const totalPages    = Math.max(1, Math.ceil(filteredClasses.length / CLASSES_PER_PAGE));
              const pagedClasses  = filteredClasses.slice((classesPage-1)*CLASSES_PER_PAGE, classesPage*CLASSES_PER_PAGE);

              // filteredCoachs défini au niveau module (accessible depuis tous les modals)

              return (
                <div>
                  {/* Header */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:12 }}>
                    <div>
                      <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#0f172a" }}>📚 Mes classes</h3>
                      <p style={{ margin:"4px 0 0", fontSize:12, color:"#6b7280" }}>
                        Créez et gérez vos classes de formation
                        {assistanteCentreNom ? ` - ${assistanteCentreNom}` : ""}
                      </p>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={fetchClasses} style={{ ...btnSecondary, fontSize:11, padding:"6px 12px" }}>🔄 Actualiser</button>
                      {classes.length > 0 && (
                        <button onClick={exportClassesExcel} style={{ ...btnSecondary, fontSize:11, padding:"6px 12px", color:"#16a34a", borderColor:"#bbf7d0", background:"#f0fdf4" }}>
                          📥 Export Excel
                        </button>
                      )}
                      <button disabled={!peut("classes","create")} title={permBtnTitle("Créer une classe",peut("classes","create"))} onClick={() => { fetchCoachs(); setClasseForm({ nom:"", niveau:"B1", filiere:"Anglais Pro", type_cours: (assistanteTypeCours && assistanteTypeCours !== "les_deux") ? assistanteTypeCours : "", coach_id:"", centre_id:"", date_debut:"", date_fin:"", capacite_max:20, horaire:[], photo_url:null, public_cible:"" }); setClasseCoachSearch(""); setShowCreateClasse(true); }} style={permBtnStyle(btnPrimary,peut("classes","create"))}>{peut("classes","create")?"+":"🔒"} Créer une classe</button>
                    </div>
                  </div>

                  {/* Stats rapides */}
                  {classes.length > 0 && (
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:20 }}>
                      {[
                        { label:"Total classes",  value:classes.length,                                       color:"#0891b2", icon:"📚" },
                        { label:"Actives",        value:classes.filter(c=>c.statut==="actif").length,          color:"#16a34a", icon:"✅" },
                        { label:"Apprenants",     value:classes.reduce((s,c)=>s+(c.nb_apprenants||0),0),       color:"#7c3aed", icon:"🎓" },
                        { label:"Sans coach",     value:classes.filter(c=>!c.coach_id).length,                 color:"#f59e0b", icon:"⚠️" },
                      ].map((s,i) => (
                        <div key={i} style={{ background:"#fff", borderRadius:10, padding:"14px 12px", border:"1px solid #e2e8f0", textAlign:"center" }}>
                          <div style={{ fontSize:18 }}>{s.icon}</div>
                          <div style={{ fontSize:22, fontWeight:800, color:s.color, marginTop:2 }}>{s.value}</div>
                          <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {classesLoading && <div style={{ textAlign:"center", padding:40, color:"#0891b2" }}>⏳ Chargement…</div>}

                  {!classesLoading && classes.length === 0 && (
                    <div style={{ textAlign:"center", padding:56, color:"#94a3b8" }}>
                      <div style={{ fontSize:40, marginBottom:12 }}>📚</div>
                      <div style={{ fontWeight:700, fontSize:14 }}>Aucune classe créée</div>
                      <div style={{ fontSize:12, marginTop:6, marginBottom:20 }}>Créez votre première classe pour commencer à organiser vos formations.</div>
                      <button disabled={!peut("classes","create")} title={permBtnTitle("Créer une classe",peut("classes","create"))} onClick={() => { fetchCoachs(); setShowCreateClasse(true); }} style={permBtnStyle(btnPrimary,peut("classes","create"))}>{peut("classes","create")?"+":"🔒"} Créer une classe</button>
                    </div>
                  )}

                  {/* ── Barre de filtres - modèle pastilles partagé (Components/BarreFiltres).
                       Les filtres restent ceux de la commerciale : niveau, statut (ses 4
                       valeurs réelles) et période, cette dernière gardant ses champs date,
                       qui ne se prêtent pas à une pastille. ── */}
                  {!classesLoading && classes.length > 0 && (
                    <BarreFiltres accent={BET_COLOR}>
                      <RangeePastilles label="Niveau :" valeur={classesNiveauFil}
                        onChange={(v) => { setClassesNiveauFil(v); setClassesPage(1); }}
                        options={[{ v:"", l:"Tous" }, ...NIVEAUX.map(n => ({ v:n, l:n }))]}
                        compte={(v) => v === "" ? classes.length : classes.filter(c => c.niveau === v).length} />

                      <RangeePastilles label="Statut :" valeur={classesStatutFil}
                        onChange={(v) => { setClassesStatutFil(v); setClassesPage(1); }}
                        options={[
                          { v:"",         l:"Tous" },
                          { v:"actif",    l:"🟢 Actives" },
                          { v:"suspendu", l:"⏸ Suspendues" },
                          { v:"terminé",  l:"✅ Terminées" },
                          { v:"cloture",  l:"🔒 Clôturées" },
                        ]}
                        compte={(v) => v === "" ? classes.length : classes.filter(c => c.statut === v).length} />

                      <RangeeChamps label="Période :" style={{ marginBottom:16 }}
                        resume={`${filteredClasses.length} classe${filteredClasses.length !== 1 ? "s" : ""} affichée${filteredClasses.length !== 1 ? "s" : ""}${filteredClasses.length !== classes.length ? ` / ${classes.length} au total` : ""}`}
                        onReinitialiser={(classesDateDebut || classesDateFin || classesNiveauFil || classesStatutFil)
                          ? () => { setClassesDateDebut(""); setClassesDateFin(""); setClassesNiveauFil(""); setClassesStatutFil(""); setClassesPage(1); }
                          : null}>
                        <input type="date" value={classesDateDebut} aria-label="Début de la période"
                          onChange={e => { setClassesDateDebut(e.target.value); setClassesPage(1); }}
                          style={{ ...champFiltre, width:150 }} />
                        <span style={{ fontSize:11, color:"#9ca3af" }}>au</span>
                        <input type="date" value={classesDateFin} aria-label="Fin de la période"
                          onChange={e => { setClassesDateFin(e.target.value); setClassesPage(1); }}
                          style={{ ...champFiltre, width:150 }} />
                      </RangeeChamps>
                    </BarreFiltres>
                  )}


                  {/* Tableau des classes */}
                  {!classesLoading && classes.length > 0 && (
                    <>
                      <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,.04)" }}>
                        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                          <thead>
                            <tr style={{ background:"#f8fafc", borderBottom:"2px solid #e2e8f0" }}>
                              {["Classe", "Niveau", "Type", "Coach assigné", "Apprenants", "Dates", "Statut", ""].map(h => (
                                <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:".04em", whiteSpace:"nowrap" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {pagedClasses.map((cl, idx) => {
                              const niv  = CECRL_COLORS[cl.niveau] || { color:"#6b7280", bg:"#f3f4f6" };
                              const stat = STATUT_CLASSE[cl.statut] || { label:cl.statut, color:"#6b7280", bg:"#f3f4f6" };
                              const fill = cl.capacite_max ? Math.round(((cl.nb_apprenants||0)/cl.capacite_max)*100) : 0;
                              return (
                                <tr key={cl.id} onClick={() => openClasseDetail(cl)} style={{ borderBottom:"1px solid #f1f5f9", background: idx%2===0 ? "#fff" : "#fafbfc", cursor:"pointer", transition:"background .12s" }} onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"} onMouseLeave={e=>e.currentTarget.style.background=idx%2===0?"#fff":"#fafbfc"}>
                                  <td style={{ padding:"12px 14px" }}>
                                    {/* La photo de la classe, celle-là même qui habille son fil
                                        de discussion : sur une liste longue, l'image situe plus
                                        vite que le nom. */}
                                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                                      <PastilleGroupe nom={cl.nom} url={cl.photo_url} taille={32} accent={BET_COLOR} />
                                      <div style={{ minWidth:0 }}>
                                        <div style={{ fontWeight:700, color:"#0f172a" }}>{cl.nom}</div>
                                        <div style={{ fontSize:11, color:"#94a3b8", marginTop:1 }}>{cl.filiere || "-"}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td style={{ padding:"12px 14px" }}>
                                    {cl.niveau ? (
                                      <span style={{ fontSize:12, fontWeight:800, padding:"3px 10px", borderRadius:6, background:niv.bg, color:niv.color, border:`1.5px solid ${niv.color}` }}>{cl.niveau}</span>
                                    ) : <span style={{ color:"#d1d5db" }}>-</span>}
                                  </td>
                                  <td style={{ padding:"12px 14px", fontSize:12, color:"#475569", whiteSpace:"nowrap" }}>
                                    {estClassePrivee(cl) && <span style={{ display:"block", fontSize:9, fontWeight:800, color:"#9333ea", marginBottom:2 }}>🔒 COACHING PRIVÉ</span>}
                                    {libelleTypeCours(cl)}
                                  </td>
                                  <td style={{ padding:"12px 14px", minWidth:160 }}>
                                    {cl.coach_nom ? (() => {
                                      const coachData = cl.coach_id ? coachsList.find(c => String(c.id) === String(cl.coach_id)) : null;
                                      const isSuspendu = coachData?.acces_bloque || false;
                                      const photoUrl = cl.coach_photo_url || coachData?.photo_url || null;
                                      return (
                                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                          <div style={{ width:30, height:30, borderRadius:"50%", background: isSuspendu ? "#dc2626" : `linear-gradient(135deg,${BET_COLOR},#7c3aed)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:"#fff", flexShrink:0, overflow:"hidden" }}>
                                            {isSuspendu ? "🔒" : photoUrl ? <img src={photoUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : cl.coach_nom.split(" ").map(p=>p[0]).slice(0,2).join("").toUpperCase()}
                                          </div>
                                          <div>
                                            <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap" }}>
                                              <span style={{ fontSize:12, fontWeight:700, color:"#0f172a" }}>{cl.coach_nom}</span>
                                              {isSuspendu && <span style={{ fontSize:9, fontWeight:700, background:"#dc2626", color:"#fff", borderRadius:99, padding:"1px 6px", whiteSpace:"nowrap" }}>🔒 SUSPENDU</span>}
                                            </div>
                                            {cl.coach_email && <div style={{ fontSize:10, color:"#94a3b8" }}>{cl.coach_email}</div>}
                                          </div>
                                        </div>
                                      );
                                    })() : (
                                      <span style={{ fontSize:11, color:"#f59e0b", fontWeight:600, background:"#fef9c3", padding:"2px 8px", borderRadius:6 }}>⚠️ Non assigné</span>
                                    )}
                                  </td>
                                  <td style={{ padding:"12px 14px", minWidth:100 }}>
                                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                      <span style={{ fontSize:14, fontWeight:800, color:"#7c3aed" }}>{cl.nb_apprenants || 0}</span>
                                      <span style={{ fontSize:10, color:"#94a3b8" }}>/ {cl.capacite_max || 20}</span>
                                    </div>
                                    <div style={{ marginTop:4, height:4, background:"#f1f5f9", borderRadius:99, overflow:"hidden" }}>
                                      <div style={{ height:"100%", width:`${Math.min(fill,100)}%`, background: fill >= 90 ? "#dc2626" : fill >= 70 ? "#f59e0b" : "#16a34a", borderRadius:99, transition:"width .3s" }} />
                                    </div>
                                    <div style={{ fontSize:10, color:"#94a3b8", marginTop:2 }}>{fill}% rempli</div>
                                  </td>
                                  <td style={{ padding:"12px 14px", fontSize:11, color:"#64748b", whiteSpace:"nowrap" }}>
                                    {cl.date_debut ? new Date(cl.date_debut).toLocaleDateString("fr-FR",{day:"numeric",month:"short"}) : "-"}
                                    {cl.date_fin   ? <><br/>→ {new Date(cl.date_fin).toLocaleDateString("fr-FR",{day:"numeric",month:"short",year:"numeric"})}</> : ""}
                                  </td>
                                  <td style={{ padding:"12px 14px" }}>
                                    <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:999, background:stat.bg, color:stat.color }}>{stat.label}</span>
                                  </td>
                                  <td style={{ padding:"12px 14px" }} onClick={e => e.stopPropagation()}>
                                    {cl.statut === "cloture" ? (
                                      <button disabled title="Classe clôturée - modification impossible"
                                        style={{ padding:"5px 12px", borderRadius:6, border:"1px solid #e2e8f0", background:"#f1f5f9", fontSize:11, fontWeight:600, color:"#9ca3af", cursor:"not-allowed" }}
                                      >🔒 Verrouillée</button>
                                    ) : (
                                    <button
                                      disabled={!peut("classes","update")}
                                      title={permBtnTitle("Modifier",peut("classes","update"))}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setClasseForm({
                                          nom: cl.nom, niveau: cl.niveau || "B1", filiere: cl.filiere || "Anglais Pro",
                                          type_cours: cl.type_cours || "", coach_id: cl.coach_id || "",
                                          centre_id: cl.centre_id || "", date_debut: cl.date_debut || "",
                                          date_fin: cl.date_fin || "", capacite_max: cl.capacite_max || 20,
                                          horaire: cl.horaire || [], photo_url: cl.photo_url || null,
                                          public_cible: cl.public_cible || "",
                                          _id: cl.id, coach_id_original: cl.coach_id || "",
                                        });
                                        setClasseCoachSearch(""); fetchCoachs(); setShowCreateClasse(true);
                                      }}
                                      style={permBtnStyle({ padding:"5px 12px", borderRadius:6, border:"1px solid #e2e8f0", background:"#f8fafc", fontSize:11, fontWeight:600, color:"#475569", cursor:"pointer" },peut("classes","update"))}
                                    >{peut("classes","update")?"✏️":"🔒"} Modifier</button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:14, padding:"0 4px" }}>
                          <div style={{ fontSize:12, color:"#6b7280" }}>
                            {(classesPage-1)*CLASSES_PER_PAGE+1}–{Math.min(classesPage*CLASSES_PER_PAGE,filteredClasses.length)} sur {filteredClasses.length} classe{filteredClasses.length!==1?"s":""}{filteredClasses.length!==classes.length?` (${classes.length} au total)`:""}
                          </div>
                          <div style={{ display:"flex", gap:4 }}>
                            <button onClick={() => setClassesPage(p=>Math.max(1,p-1))} disabled={classesPage===1}
                              style={{ padding:"5px 12px", borderRadius:6, border:"1px solid #e2e8f0", background: classesPage===1?"#f8fafc":"#fff", color: classesPage===1?"#d1d5db":"#374151", fontSize:12, cursor: classesPage===1?"default":"pointer", fontWeight:600 }}>← Préc.</button>
                            {Array.from({length:totalPages},(_,i)=>i+1).map(p => (
                              <button key={p} onClick={() => setClassesPage(p)}
                                style={{ padding:"5px 12px", borderRadius:6, border: p===classesPage?`2px solid ${BET_COLOR}`:"1px solid #e2e8f0", background: p===classesPage?BET_COLOR:"#fff", color: p===classesPage?"#fff":"#374151", fontSize:12, cursor:"pointer", fontWeight:700, minWidth:34 }}>
                                {p}
                              </button>
                            ))}
                            <button onClick={() => setClassesPage(p=>Math.min(totalPages,p+1))} disabled={classesPage===totalPages}
                              style={{ padding:"5px 12px", borderRadius:6, border:"1px solid #e2e8f0", background: classesPage===totalPages?"#f8fafc":"#fff", color: classesPage===totalPages?"#d1d5db":"#374151", fontSize:12, cursor: classesPage===totalPages?"default":"pointer", fontWeight:600 }}>Suiv. →</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Modal créer/modifier classe → rendu global (voir section modaux globaux) */}
                </div>
              );
            })()}

            {/* ══ MODAL DÉTAIL CLASSE ══ */}
            {selectedClasse && (
              <ModaleAnimee ouvert
                style={{ position:"fixed", inset:0, background:"rgba(15,23,42,.55)", zIndex:9000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
                panneauStyle={{ background:"#fff", borderRadius:18, width:"90vw", maxWidth:"90vw", height:"90vh", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 80px rgba(0,0,0,.22)", display:"flex", flexDirection:"column" }}>

                  {/* Header modal */}
                  {(() => {
                    const hdr = classeDetailData?.groupe || selectedClasse;
                    const horaires = Array.isArray(hdr.horaire) && hdr.horaire.length ? hdr.horaire : [];
                    const nbApp   = classeDetailData?.apprenants?.length ?? hdr.nb_apprenants ?? 0;
                    const fill    = hdr.capacite_max ? Math.round((nbApp / hdr.capacite_max) * 100) : 0;
                    const statCfg = STATUT_CLASSE[hdr.statut] || { label: hdr.statut || "-", color:"#6b7280", bg:"#f3f4f6" };
                    const nivCfg  = CECRL_COLORS[hdr.niveau]  || { color:"#fff", bg:"rgba(255,255,255,.15)" };
                    return (
                      <div style={{ background: BET_GRADIENT, borderRadius:"18px 18px 0 0", padding:"20px 24px 0 24px", flexShrink:0 }}>
                        {/* Ligne titre + fermer */}
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14, gap:16 }}>
                          {/* LA PHOTO DE LA CLASSE, modifiable d'ici. L'assistante crée la
                              classe : elle doit pouvoir corriger l'image en consultant la
                              fiche, sans rouvrir le formulaire. L'enregistrement est immédiat
                              - cette modale n'a pas de bouton « Enregistrer ». */}
                          <PhotoClasseEntete
                            groupe={hdr}
                            modifiable={peut("classes","update")}
                            authHeaders={authHeaders}
                            onChange={url => {
                              setSelectedClasse(c => c ? { ...c, photo_url: url } : c);
                              setClasseDetailData(d => d ? { ...d, groupe: { ...d.groupe, photo_url: url } } : d);
                              setClasses(prev => prev.map(c => c.id === hdr.id ? { ...c, photo_url: url } : c));
                            }} />
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:11, color:"rgba(255,255,255,.55)", fontWeight:600, letterSpacing:".08em", textTransform:"uppercase", marginBottom:5 }}>Détail de la classe</div>
                            <div style={{ fontSize:22, fontWeight:900, color:"#fff", lineHeight:1.2 }}>{hdr.nom}</div>
                            <div style={{ display:"flex", gap:6, marginTop:8, flexWrap:"wrap", alignItems:"center" }}>
                              {hdr.niveau && (
                                <span style={{ padding:"2px 10px", borderRadius:99, fontSize:11, fontWeight:800, background:"rgba(255,255,255,.18)", color:"#fff", border:"1px solid rgba(255,255,255,.3)" }}>{hdr.niveau}</span>
                              )}
                              {hdr.filiere && (
                                <span style={{ padding:"2px 10px", borderRadius:99, fontSize:11, fontWeight:600, background:"rgba(255,255,255,.1)", color:"rgba(255,255,255,.85)" }}>{hdr.filiere}</span>
                              )}
                              <span style={{ padding:"2px 10px", borderRadius:99, fontSize:11, fontWeight:700, background: statCfg.bg, color: statCfg.color }}>{statCfg.label}</span>
                              {/* Le badge « coaching privé » n'apparaissait QUE dans la liste :
                                  ouvrir la fiche d'un coaching privé n'en disait plus rien, on
                                  n'y lisait plus que la modalité. La distinction compte - un
                                  cours particulier ne se gère pas comme un groupe. */}
                              {estClassePrivee(hdr) && (
                                <span style={{ padding:"2px 10px", borderRadius:99, fontSize:11, fontWeight:800, background:"rgba(255,255,255,.22)", color:"#fff" }}>🔒 Coaching privé</span>
                              )}
                              <span style={{ padding:"2px 10px", borderRadius:99, fontSize:11, fontWeight:600, background:"rgba(255,255,255,.1)", color:"rgba(255,255,255,.85)" }}>
                                {libelleTypeCours(hdr)}
                              </span>
                            </div>
                          </div>
                          <button onClick={() => setSelectedClasse(null)} style={{ background:"rgba(255,255,255,.15)", border:"none", width:36, height:36, borderRadius:"50%", color:"#fff", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginLeft:12 }}>✕</button>
                        </div>

                        {/* Bande de métriques */}
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:1, background:"rgba(255,255,255,.1)", borderRadius:"10px 10px 0 0", overflow:"hidden", marginTop:4 }}>
                          {/* Apprenants */}
                          <div style={{ background:"rgba(255,255,255,.08)", padding:"10px 14px", textAlign:"center" }}>
                            <div style={{ fontSize:18, fontWeight:900, color:"#fff" }}>{nbApp}<span style={{ fontSize:11, fontWeight:500, color:"rgba(255,255,255,.6)" }}>/{hdr.capacite_max||20}</span></div>
                            <div style={{ fontSize:10, color:"rgba(255,255,255,.6)", marginTop:1, fontWeight:600 }}>Apprenants</div>
                            <div style={{ marginTop:5, height:3, background:"rgba(255,255,255,.15)", borderRadius:99, overflow:"hidden" }}>
                              <div style={{ height:"100%", width:`${Math.min(fill,100)}%`, background: fill>=90?"#ef4444":fill>=70?"#f59e0b":"#4ade80", borderRadius:99 }} />
                            </div>
                          </div>

                          {/* Horaires */}
                          <div style={{ background:"rgba(255,255,255,.08)", padding:"10px 14px", textAlign:"center" }}>
                            <div style={{ fontSize:13, fontWeight:700, color:"#fff", lineHeight:1.3 }}>
                              {horaires.length > 0
                                ? horaires.slice(0,2).map((h, i) => <div key={i} style={{ fontSize:11 }}>{fmtHoraire(h)}</div>)
                                : <span style={{ fontSize:11, color:"rgba(255,255,255,.45)" }}>-</span>
                              }
                              {horaires.length > 2 && <div style={{ fontSize:10, color:"rgba(255,255,255,.5)" }}>+{horaires.length-2} autres</div>}
                            </div>
                            <div style={{ fontSize:10, color:"rgba(255,255,255,.6)", marginTop:3, fontWeight:600 }}>🕐 Horaires</div>
                          </div>

                          {/* Dates */}
                          <div style={{ background:"rgba(255,255,255,.08)", padding:"10px 14px", textAlign:"center" }}>
                            <div style={{ fontSize:11, fontWeight:700, color:"#fff" }}>
                              {hdr.date_debut ? new Date(hdr.date_debut).toLocaleDateString("fr-FR",{day:"numeric",month:"short"}) : "-"}
                            </div>
                            <div style={{ fontSize:10, color:"rgba(255,255,255,.5)", margin:"2px 0" }}>↓</div>
                            <div style={{ fontSize:11, fontWeight:700, color:"#fff" }}>
                              {hdr.date_fin ? new Date(hdr.date_fin).toLocaleDateString("fr-FR",{day:"numeric",month:"short",year:"numeric"}) : "-"}
                            </div>
                            <div style={{ fontSize:10, color:"rgba(255,255,255,.6)", marginTop:3, fontWeight:600 }}>📅 Dates</div>
                          </div>

                          {/* Coach */}
                          <div style={{ background:"rgba(255,255,255,.08)", padding:"10px 14px", textAlign:"center" }}>
                            <div style={{ fontSize:12, fontWeight:700, color:"#fff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {hdr.coach_nom || selectedClasse?.coach_nom || <span style={{ color:"rgba(255,255,255,.4)" }}>Non assigné</span>}
                            </div>
                            {(hdr.coach_grade || (hdr.coach_nom && "Coach")) && (
                              <div style={{ fontSize:10, color:"rgba(255,255,255,.55)", marginTop:1 }}>{hdr.coach_grade || "Coach"}</div>
                            )}
                            <div style={{ fontSize:10, color:"rgba(255,255,255,.6)", marginTop:3, fontWeight:600 }}>👤 Coach</div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Onglets */}
                  <div style={{ display:"flex", borderBottom:"2px solid #f1f5f9", padding:"0 24px", flexShrink:0, overflowX:"auto", gap:2 }}>
                    {[
                      { key:"infos",       label:"📋 Informations" },
                      { key:"apprenants",  label:`🎓 Apprenants (${classeDetailData?.apprenants?.length ?? "…"})` },
                      { key:"sessions",    label:`📡 Sessions (${(classeSessions.live.length + classeSessions.cours.length) || "…"})` },
                      { key:"presences",   label:"📅 Présences" },
                      { key:"progression", label:"📈 Progression" },
                      { key:"historique",  label:"🕓 Historique" },
                    ].map(t => (
                      <button key={t.key} onClick={() => {
                        setClasseDetailTab(t.key);
                        if (t.key === "sessions" && selectedClasse && !classeSessions.live.length && !classeSessions.cours.length) {
                          fetchClasseSessions(selectedClasse.id);
                        }
                        if (t.key === "historique" && selectedClasse && !classeHistorique.length && !classeHistoriqueLoading) {
                          setClasseHistoriqueLoading(true);
                          fetch(`${API_URL}/api/groupes/${selectedClasse.id}/historique`, { headers: authHeaders() })
                            .then(r => r.ok ? r.json() : { historique: [] })
                            .then(d => setClasseHistorique(d.historique || []))
                            .catch(() => {})
                            .finally(() => setClasseHistoriqueLoading(false));
                        }
                      }}
                        style={{ padding:"12px 16px", border:"none", borderBottom: classeDetailTab===t.key?`3px solid ${BET_COLOR}`:"3px solid transparent", background:"none", fontWeight: classeDetailTab===t.key?800:500, color: classeDetailTab===t.key?BET_COLOR:"#64748b", fontSize:13, cursor:"pointer", whiteSpace:"nowrap", marginBottom:-2, transition:"all .15s" }}>
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {/* Contenu onglets */}
                  <div style={{ padding:24, flex:1, overflowY:"auto" }}>
                    {classeDetailLoading && (
                      <div style={{ textAlign:"center", padding:48, color:BET_COLOR, fontSize:14 }}>⏳ Chargement…</div>
                    )}

                    {/* ── ONGLET INFORMATIONS ── */}
                    {!classeDetailLoading && classeDetailTab === "infos" && (() => {
                      // Fusionner : detail pour les champs enrichis, selectedClasse comme fallback pour le coach
                      const base = classeDetailData?.groupe || selectedClasse;
                      const cl   = {
                        ...selectedClasse,                      // valeurs list (coach_nom, coach_email déjà enrichis)
                        ...base,                                // override avec valeurs detail (plus riches)
                        coach_nom:   base.coach_nom   || selectedClasse?.coach_nom   || null,
                        coach_email: base.coach_email || selectedClasse?.coach_email || null,
                        coach_telephone: base.coach_telephone || selectedClasse?.coach_telephone || null,
                        coach_photo_url: base.coach_photo_url || selectedClasse?.coach_photo_url || null,
                        coach_grade:     base.coach_grade     || null,
                      };
                      const niv  = CECRL_COLORS[cl.niveau] || { color:"#6b7280", bg:"#f3f4f6" };
                      const stat = STATUT_CLASSE[cl.statut] || { label:cl.statut, color:"#6b7280", bg:"#f3f4f6" };
                      const fill = cl.capacite_max ? Math.round(((cl.nb_apprenants||classeDetailData?.apprenants?.length||0)/cl.capacite_max)*100) : 0;
                      return (
                        <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
                          {/* Badges status */}
                          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                            {cl.niveau && <span style={{ padding:"4px 12px", borderRadius:99, fontSize:12, fontWeight:800, background:niv.bg, color:niv.color, border:`1.5px solid ${niv.color}` }}>{cl.niveau}</span>}
                            <span style={{ padding:"4px 12px", borderRadius:99, fontSize:12, fontWeight:700, background:stat.bg, color:stat.color }}>{stat.label}</span>
                            {estClassePrivee(cl) && (
                              <span style={{ padding:"4px 12px", borderRadius:99, fontSize:12, fontWeight:800, background:"#f3e8ff", color:"#9333ea" }}>🔒 Coaching privé</span>
                            )}
                            <span style={{ padding:"4px 12px", borderRadius:99, fontSize:12, fontWeight:600, background:"#f1f5f9", color:"#475569" }}>
                              {libelleTypeCours(cl)}
                            </span>
                          </div>

                          {/* Grille infos */}
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                            {[
                              { icon:"📚", label:"Filière",      val: cl.filiere || "-" },
                              { icon:"🎯", label:"Niveau",       val: cl.niveau  || "-" },
                              /* « Non renseigné » plutôt qu'un tiret : les classes créées avant
                                 l'existence du champ sont nombreuses, et il faut qu'on voie qu'il
                                 reste à remplir — un « - » se lit comme « sans objet ». */
                              { icon: emojiPublicCible(cl.public_cible) || "🧑\u200d🤝\u200d🧑",
                                label:"Profil du public",
                                val: libellePublicCible(cl.public_cible) || "Non renseigné" },
                              { icon:"📅", label:"Date début",   val: cl.date_debut ? new Date(cl.date_debut).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"}) : "-" },
                              { icon:"🏁", label:"Date fin",     val: cl.date_fin   ? new Date(cl.date_fin).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"})   : "-" },
                              { icon:"👥", label:"Capacité max", val: `${cl.capacite_max || 20} apprenants` },
                              // Un coaching privé à domicile se donne chez l'apprenant : afficher le
                              // centre y est trompeur, d'autant que le repli sur assistanteCentreNom
                              // affichait le centre de la commerciale connectée même quand la classe
                              // n'en avait aucun. L'adresse vient du dossier, remontée par /api/groupes/:id.
                              (classeDetailData?.groupe?.lieu_coaching === "domicile"
                                ? { icon:"🏠", label:"Adresse", val: classeDetailData?.groupe?.adresse_coaching || "Non renseignée" }
                                : { icon:"🏢", label:"Centre",  val: cl.centre_nom || cl.centre_id || "-" }),
                              { icon:"✍️", label:"Créé par",     val: cl.created_by_nom || "-" },
                              { icon:"📝", label:"Horaire",      val: Array.isArray(cl.horaire) && cl.horaire.length ? cl.horaire.map(fmtHoraire).join(", ") : "-" },
                            ].map(row => (
                              <div key={row.label} style={{ background:"#f8fafc", borderRadius:10, padding:"12px 14px", display:"flex", gap:10, alignItems:"flex-start" }}>
                                <span style={{ fontSize:16 }}>{row.icon}</span>
                                <div>
                                  <div style={{ fontSize:10, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:".04em" }}>{row.label}</div>
                                  <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginTop:2 }}>{row.val}</div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Coach card */}
                          <div style={{ background:"#f0f9ff", borderRadius:12, padding:16, border:"1px solid #bae6fd" }}>
                            <div style={{ fontSize:11, color:"#0891b2", fontWeight:700, textTransform:"uppercase", letterSpacing:".04em", marginBottom:10 }}>Coach assigné</div>
                            {cl.coach_nom || cl.coach_id ? (
                              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                                <div style={{ width:44, height:44, borderRadius:"50%", background:`linear-gradient(135deg,${BET_COLOR},#7c3aed)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:800, color:"#fff", flexShrink:0, overflow:"hidden" }}>
                                  {cl.coach_photo_url ? <img src={cl.coach_photo_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : (cl.coach_nom||"?").split(" ").map(p=>p[0]).slice(0,2).join("").toUpperCase()}
                                </div>
                                <div>
                                  <div style={{ fontWeight:800, color:"#0f172a", fontSize:15 }}>{cl.coach_nom || "Coach assigné"}</div>
                                  {cl.coach_email && <div style={{ fontSize:12, color:"#0891b2", marginTop:2 }}>✉ {cl.coach_email}</div>}
                                  {cl.coach_telephone && <div style={{ fontSize:12, color:"#64748b", marginTop:1 }}>📞 {cl.coach_telephone}</div>}
                                </div>
                              </div>
                            ) : (
                              <div style={{ color:"#f59e0b", fontWeight:600, fontSize:13 }}>⚠️ Aucun coach assigné pour cette classe</div>
                            )}
                          </div>

                          {/* Taux remplissage */}
                          <div style={{ background:"#f8fafc", borderRadius:12, padding:16, border:"1px solid #e2e8f0" }}>
                            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                              <span style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>Taux de remplissage</span>
                              <span style={{ fontSize:13, fontWeight:800, color: fill>=90?"#dc2626":fill>=70?"#f59e0b":"#16a34a" }}>{fill}%</span>
                            </div>
                            <div style={{ height:8, background:"#e2e8f0", borderRadius:99, overflow:"hidden" }}>
                              <div style={{ height:"100%", width:`${Math.min(fill,100)}%`, background: fill>=90?"#dc2626":fill>=70?"#f59e0b":"#16a34a", borderRadius:99, transition:"width .4s" }} />
                            </div>
                            <div style={{ fontSize:12, color:"#64748b", marginTop:6 }}>
                              {classeDetailData?.apprenants?.length ?? cl.nb_apprenants ?? 0} apprenant(s) / {cl.capacite_max || 20} places
                            </div>
                          </div>

                          {/* Bouton modifier */}
                          <div style={{ display:"flex", justifyContent:"flex-end", gap:10 }}>
                            <button onClick={() => {
                              setClasseForm({ nom:cl.nom, niveau:cl.niveau||"B1", filiere:cl.filiere||"Anglais Pro", type_cours:cl.type_cours||"", coach_id:cl.coach_id||"", centre_id:cl.centre_id||"", date_debut:cl.date_debut||"", date_fin:cl.date_fin||"", capacite_max:cl.capacite_max||20, horaire:cl.horaire||[], photo_url:cl.photo_url||null, public_cible:cl.public_cible||"", _id:cl.id, coach_id_original:cl.coach_id||"" });
                              setSelectedClasse(null); setClasseCoachSearch(""); fetchCoachs(); setShowCreateClasse(true);
                            }} style={{ padding:"9px 20px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer" }}>
                              ✏️ Modifier cette classe
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    {/* ── ONGLET SESSIONS ── */}
                    {!classeDetailLoading && classeDetailTab === "sessions" && (() => {
                      const now = new Date();
                      const PLAT_META = {
                        zoom:       { label:"Zoom",        color:"#2563eb", bg:"#dbeafe", icon:"🎥" },
                        meet:       { label:"Google Meet", color:"#16a34a", bg:"#dcfce7", icon:"🟢" },
                        teams:      { label:"Teams",       color:"#7c3aed", bg:"#ede9fe", icon:"🟣" },
                        presentiel: { label:"Présentiel",  color:"#059669", bg:"#ecfdf5", icon:"🏢" },
                        webex:      { label:"Webex",       color:"#0891b2", bg:"#e0f2fe", icon:"🔵" },
                      };
                      const STATUT_COURS_META = {
                        dispense:         { label:"Dispensé",     bg:"#f1f5f9", c:"#64748b" },
                        annule:           { label:"Annulé",       bg:"#fee2e2", c:"#dc2626" },
                        apprenant_absent: { label:"Appr. absent", bg:"#fef3c7", c:"#92400e" },
                        coach_absent:     { label:"Coach absent", bg:"#fef3c7", c:"#d97706" },
                        catch_up:         { label:"Rattrapage",   bg:"#ede9fe", c:"#7c3aed" },
                        holiday:          { label:"Férié",        bg:"#f3f4f6", c:"#6b7280" },
                      };

                      // ── Calcul borne période ──────────────────────────────
                      const getBorne = () => {
                        if (sessFiltrePeriode === "custom") {
                          return {
                            debut: sessFiltreDateDebut ? new Date(sessFiltreDateDebut) : null,
                            fin:   sessFiltreDateFin   ? new Date(sessFiltreDateFin + "T23:59:59") : null,
                          };
                        }
                        const fin = new Date(now); fin.setHours(23,59,59);
                        const jours = { "30j":30, "90j":90, "6m":180, "1an":365 }[sessFiltrePeriode];
                        if (!jours) return { debut: null, fin: null }; // "tout"
                        const debut = new Date(now); debut.setDate(debut.getDate() - jours);
                        return { debut, fin: null }; // fin = futur illimité
                      };
                      const { debut: borneDebut, fin: borneFin } = getBorne();

                      // ── Construction timeline unifiée ─────────────────────
                      const itemsLive = (sessFiltreType === "cours" ? [] : classeSessions.live).map(s => ({
                        _type:"live", _date:new Date(`${s.date}T${s.heure||"00:00"}`), _key:`live-${s.id}`, ...s,
                      }));
                      const itemsCours = (sessFiltreType === "live" ? [] : classeSessions.cours).map(c => ({
                        _type:"cours", _date:new Date(c.date_cours||c.date), _key:`cours-${c.id}`, ...c,
                      }));
                      const allRaw = [...itemsLive, ...itemsCours].sort((a,b) => b._date - a._date);

                      // ── Appliquer filtre période + statut ─────────────────
                      const allItems = allRaw.filter(i => {
                        if (borneDebut && i._date < borneDebut) return false;
                        if (borneFin   && i._date > borneFin)   return false;
                        if (sessStatutFil === "actif")  return i._date >= now && (i._type !== "live" || i.statut !== "terminee");
                        if (sessStatutFil === "cloture") return i._date < now || (i._type === "live" && i.statut === "terminee");
                        return true;
                      });

                      const aVenir = allItems.filter(i => i._date >= now);
                      const passes = allItems.filter(i => i._date <  now);

                      // ── Pagination ────────────────────────────────────────
                      const totalPages = Math.ceil(allItems.length / SESS_PAR_PAGE);
                      const pageItems  = allItems.slice(sessPage * SESS_PAR_PAGE, (sessPage + 1) * SESS_PAR_PAGE);

                      const goPage = (p) => setSessPage(Math.max(0, Math.min(p, totalPages - 1)));

                      // ── Carte session ─────────────────────────────────────
                      const SessionCard = ({ item }) => {
                        const isPast = item._date < now;
                        if (item._type === "live") {
                          const plat = PLAT_META[item.plateforme] || { label:item.plateforme||"En ligne", color:"#2563eb", bg:"#dbeafe", icon:"📡" };
                          const isClosed = item.statut === "terminee";
                          return (
                            <div style={{ display:"flex", gap:12, padding:"13px 15px", borderRadius:10, border:`1px solid ${isClosed?"#e5e7eb":isPast?"#fde68a":plat.bg}`, background:isClosed?"#f9fafb":isPast?"#fffbeb":plat.bg+"44", marginBottom:8 }}>
                              <div style={{ textAlign:"center", padding:"7px 9px", borderRadius:8, background:plat.bg, flexShrink:0, minWidth:40 }}>
                                <div style={{ fontSize:18 }}>{plat.icon}</div>
                                <div style={{ fontSize:9, color:plat.color, fontWeight:700, marginTop:2 }}>LIVE</div>
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontWeight:800, fontSize:13, color:"#0f172a" }}>{item.titre}</div>
                                <div style={{ fontSize:11, color:"#6b7280", marginTop:3, display:"flex", gap:8, flexWrap:"wrap" }}>
                                  <span>📅 {item._date.toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short",year:"numeric"})}</span>
                                  <span>🕐 {item.heure}</span>
                                  {item.duree && <span>⏱ {item.duree} min</span>}
                                  <span style={{ padding:"2px 7px", borderRadius:10, fontSize:10, fontWeight:700, background:plat.bg, color:plat.color }}>{plat.label}</span>
                                </div>
                                {item.notes && <div style={{ fontSize:11, color:"#6b7280", marginTop:3, fontStyle:"italic" }}>💬 {item.notes}</div>}
                              </div>
                              <span style={{ padding:"4px 9px", borderRadius:10, fontSize:11, fontWeight:700, height:"fit-content", background:isClosed?"#f1f5f9":isPast?"#fef3c7":"#dcfce7", color:isClosed?"#64748b":isPast?"#92400e":"#065f46" }}>
                                {isClosed?"🔒 Clôturée":isPast?"Passée":"✅ À venir"}
                              </span>
                            </div>
                          );
                        }
                        const sm = STATUT_COURS_META[item.statut] || { label:item.statut||"Dispensé", bg:"#f1f5f9", c:"#64748b" };
                        return (
                          <div style={{ display:"flex", gap:12, padding:"12px 15px", borderRadius:10, border:"1px solid #e5e7eb", background:isPast?"#f9fafb":"#fff", marginBottom:8 }}>
                            <div style={{ textAlign:"center", padding:"7px 9px", borderRadius:8, background:"#f0fdf4", flexShrink:0, minWidth:40 }}>
                              <div style={{ fontSize:18 }}>📗</div>
                              <div style={{ fontSize:9, color:"#16a34a", fontWeight:700, marginTop:2 }}>COURS</div>
                            </div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontWeight:700, fontSize:13, color:"#0f172a" }}>{item.objectif||"Cours de groupe"}</div>
                              <div style={{ fontSize:11, color:"#6b7280", marginTop:3, display:"flex", gap:8, flexWrap:"wrap" }}>
                                <span>📅 {item._date.toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short",year:"numeric"})}</span>
                                {item.grammaire && <span>📐 {item.grammaire}</span>}
                                {item.sujet_discussion && <span>💬 {item.sujet_discussion}</span>}
                              </div>
                              {item.commentaire && <div style={{ fontSize:11, color:"#7c3aed", marginTop:3, fontStyle:"italic" }}>{item.commentaire}</div>}
                            </div>
                            <span style={{ padding:"4px 9px", borderRadius:10, fontSize:11, fontWeight:700, height:"fit-content", background:sm.bg, color:sm.c }}>{sm.label}</span>
                          </div>
                        );
                      };

                      const btnFilter = (active) => ({
                        padding:"6px 12px", borderRadius:6, border:`1.5px solid ${active?BET_COLOR:"#e2e8f0"}`,
                        background:active?`${BET_COLOR}15`:"#fff", color:active?BET_COLOR:"#64748b",
                        fontSize:12, fontWeight:active?700:500, cursor:"pointer",
                      });

                      return (
                        <div>
                          {classeSessionsLoading ? (
                            <div style={{ textAlign:"center", padding:48, color:BET_COLOR }}>⏳ Chargement des sessions…</div>
                          ) : (
                            <>
                              {/* ── Barre de filtres ── */}
                              <div style={{ background:"#f8fafc", borderRadius:10, padding:"14px 16px", marginBottom:16, display:"flex", flexDirection:"column", gap:12 }}>

                                {/* Type */}
                                <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                                  <span style={{ fontSize:11, fontWeight:700, color:"#64748b", marginRight:4 }}>Type :</span>
                                  {[
                                    { key:"all",   label:"Tout" },
                                    { key:"live",  label:"📡 Sessions live" },
                                    { key:"cours", label:"📗 Cours de groupe" },
                                  ].map(f => (
                                    <button key={f.key} style={btnFilter(sessFiltreType===f.key)}
                                      onClick={() => { setSessFiltreType(f.key); setSessPage(0); }}>
                                      {f.label}
                                    </button>
                                  ))}
                                </div>

                                {/* Statut */}
                                <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                                  <span style={{ fontSize:11, fontWeight:700, color:"#64748b", marginRight:4 }}>Statut :</span>
                                  {[
                                    { key:"all",     label:"Tous" },
                                    { key:"actif",   label:"✅ Actif" },
                                    { key:"cloture", label:"🔒 Clôturé" },
                                  ].map(f => (
                                    <button key={f.key} style={btnFilter(sessStatutFil===f.key)}
                                      onClick={() => { setSessStatutFil(f.key); setSessPage(0); }}>
                                      {f.label}
                                    </button>
                                  ))}
                                </div>

                                {/* Période rapide */}
                                <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                                  <span style={{ fontSize:11, fontWeight:700, color:"#64748b", marginRight:4 }}>Période :</span>
                                  {[
                                    { key:"tout", label:"Tout" },
                                    { key:"30j",  label:"30 jours" },
                                    { key:"90j",  label:"3 mois" },
                                    { key:"6m",   label:"6 mois" },
                                    { key:"1an",  label:"1 an" },
                                    { key:"custom",label:"Personnalisé" },
                                  ].map(f => (
                                    <button key={f.key} style={btnFilter(sessFiltrePeriode===f.key)}
                                      onClick={() => { setSessFiltrePeriode(f.key); setSessPage(0); }}>
                                      {f.label}
                                    </button>
                                  ))}
                                </div>

                                {/* Dates custom */}
                                {sessFiltrePeriode === "custom" && (
                                  <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                                    <span style={{ fontSize:11, fontWeight:700, color:"#64748b" }}>Du</span>
                                    <input type="date" value={sessFiltreDateDebut}
                                      onChange={e => { setSessFiltreDateDebut(e.target.value); setSessPage(0); }}
                                      style={{ padding:"5px 8px", borderRadius:6, border:"1.5px solid #e2e8f0", fontSize:12, color:"#0f172a" }} />
                                    <span style={{ fontSize:11, fontWeight:700, color:"#64748b" }}>au</span>
                                    <input type="date" value={sessFiltreDateFin}
                                      onChange={e => { setSessFiltreDateFin(e.target.value); setSessPage(0); }}
                                      style={{ padding:"5px 8px", borderRadius:6, border:"1.5px solid #e2e8f0", fontSize:12, color:"#0f172a" }} />
                                    {(sessFiltreDateDebut || sessFiltreDateFin) && (
                                      <button onClick={() => { setSessFiltreDateDebut(""); setSessFiltreDateFin(""); setSessPage(0); }}
                                        style={{ padding:"5px 10px", borderRadius:6, border:"none", background:"#fee2e2", color:"#dc2626", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                                        ✕ Effacer
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* ── Compteurs (sur données filtrées) ── */}
                              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:16 }}>
                                {[
                                  { label:"Résultats",  val:allItems.length, color:BET_COLOR,  bg:`${BET_COLOR}15`, icon:"📊" },
                                  { label:"À venir",    val:aVenir.length,   color:"#16a34a",  bg:"#dcfce7",        icon:"✅" },
                                  { label:"Effectuées", val:passes.length,   color:"#64748b",  bg:"#f1f5f9",        icon:"🔒" },
                                ].map(c => (
                                  <div key={c.label} style={{ background:c.bg, borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                                    <div style={{ fontSize:18 }}>{c.icon}</div>
                                    <div style={{ fontSize:20, fontWeight:900, color:c.color, lineHeight:1.2 }}>{c.val}</div>
                                    <div style={{ fontSize:10, color:"#64748b", fontWeight:600 }}>{c.label}</div>
                                  </div>
                                ))}
                              </div>

                              {/* ── Liste paginée ── */}
                              {allItems.length === 0 ? (
                                <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>
                                  <div style={{ fontSize:36, marginBottom:10 }}>📡</div>
                                  <div style={{ fontWeight:700, marginBottom:4 }}>Aucune session pour cette période</div>
                                  <div style={{ fontSize:12 }}>Modifiez les filtres ou changez la période.</div>
                                </div>
                              ) : (
                                <>
                                  {pageItems.map(item => <SessionCard key={item._key} item={item} />)}

                                  {/* ── Pagination ── */}
                                  {totalPages > 1 && (
                                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:16, padding:"12px 16px", background:"#f8fafc", borderRadius:10 }}>
                                      <button onClick={() => goPage(sessPage - 1)} disabled={sessPage === 0}
                                        style={{ padding:"7px 14px", borderRadius:7, border:"1.5px solid #e2e8f0", background:sessPage===0?"#f1f5f9":"#fff", color:sessPage===0?"#9ca3af":"#374151", fontWeight:700, fontSize:12, cursor:sessPage===0?"default":"pointer" }}>
                                        ← Précédent
                                      </button>
                                      <div style={{ display:"flex", gap:4 }}>
                                        {Array.from({ length: totalPages }, (_, i) => i).map(i => (
                                          <button key={i} onClick={() => goPage(i)}
                                            style={{ width:30, height:30, borderRadius:6, border:`1.5px solid ${i===sessPage?BET_COLOR:"#e2e8f0"}`, background:i===sessPage?BET_COLOR:"#fff", color:i===sessPage?"#fff":"#374151", fontWeight:700, fontSize:12, cursor:"pointer" }}>
                                            {i + 1}
                                          </button>
                                        ))}
                                      </div>
                                      <button onClick={() => goPage(sessPage + 1)} disabled={sessPage >= totalPages - 1}
                                        style={{ padding:"7px 14px", borderRadius:7, border:"1.5px solid #e2e8f0", background:sessPage>=totalPages-1?"#f1f5f9":"#fff", color:sessPage>=totalPages-1?"#9ca3af":"#374151", fontWeight:700, fontSize:12, cursor:sessPage>=totalPages-1?"default":"pointer" }}>
                                        Suivant →
                                      </button>
                                    </div>
                                  )}

                                  <div style={{ textAlign:"center", fontSize:11, color:"#9ca3af", marginTop:8 }}>
                                    {sessPage * SESS_PAR_PAGE + 1}–{Math.min((sessPage+1)*SESS_PAR_PAGE, allItems.length)} sur {allItems.length} session{allItems.length>1?"s":""}
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })()}

                    {/* ── ONGLET APPRENANTS ── */}
                    {!classeDetailLoading && classeDetailTab === "apprenants" && (() => {
                      const apprenants = classeDetailData?.apprenants || [];

                      return (
                        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                          {/* Barre d'actions */}
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
                            <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>
                              {apprenants.length} apprenant(s) inscrit(s)
                            </div>
                          </div>

                          {/* Liste */}
                          {apprenants.length === 0 ? (
                            <div style={{ textAlign:"center", padding:32, color:"#94a3b8", fontSize:13 }}>
                              <div style={{ fontSize:32, marginBottom:8 }}>🎓</div>
                              Aucun apprenant dans cette classe.<br/>Les apprenants rejoignent une classe via une inscription sur le site (onglet Prospects parcours) ou une inscription manuelle (onglet Inscription).
                            </div>
                          ) : (
                            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                              {apprenants.map((a) => {
                                const niv = CECRL_COLORS[a.niveau] || { color:"#6b7280", bg:"#f3f4f6" };
                                // Lien fiable posé dans `note` à la création de la classe - prioritaire sur le
                                // match par email, qui échoue systématiquement pour un profil enfant (email_apprenant
                                // est un identifiant technique, jamais égal à prospect_email qui est celui du parent).
                                let assignationIdDeNote = null;
                                try { assignationIdDeNote = JSON.parse(a.note || "{}").assignation_id || null; } catch {}
                                const linkedAss = (assignationIdDeNote && assignations.find(ass => ass.id === assignationIdDeNote))
                                  || (a.email_apprenant
                                    ? assignations.find(ass => ass.prospect_email && ass.prospect_email.toLowerCase() === a.email_apprenant.toLowerCase())
                                    : null);
                                return (
                                  <div key={a.id}
                                    onClick={() => {
                                      // `a` vient directement de classeDetailData.apprenants (une ligne
                                      // groupes_apprenants) - a.id est donc ici avec certitude le vrai ga_id.
                                      if (linkedAss) { openApprenantDetail(a, selectedClasse, a.id); }
                                      else setClasseApprenantModal(a);
                                    }}
                                    style={{ background:"#fff", borderRadius:10, border:"1px solid #e2e8f0", padding:"12px 14px", display:"flex", alignItems:"center", gap:12, cursor:"pointer", transition:"background .12s" }}
                                    onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                                    onMouseLeave={e=>e.currentTarget.style.background="#fff"}
                                  >
                                    <div style={{ width:36, height:36, borderRadius:"50%", background:`linear-gradient(135deg,#7c3aed,${BET_COLOR})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800, color:"#fff", flexShrink:0, overflow:"hidden" }}>
                                      {a.avatar_url ? <img src={a.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : (a.nom_apprenant||"?")[0].toUpperCase()}
                                    </div>
                                    <div style={{ flex:1, minWidth:0 }}>
                                      <div style={{ fontWeight:700, color:"#0f172a", fontSize:13, display:"flex", alignItems:"center", gap:6 }}>
                                        {a.prenom_apprenant ? `${a.prenom_apprenant} ` : ""}{a.nom_apprenant}
                                        {linkedAss && <span style={{ fontSize:9, background:BET_COLOR, color:"#fff", borderRadius:99, padding:"1px 6px", fontWeight:700 }}>DOSSIER</span>}
                                        {a.acces_bloque && <span style={{ fontSize:9, background:"#dc2626", color:"#fff", borderRadius:99, padding:"1px 7px", fontWeight:700 }}>🔒 SUSPENDU</span>}
                                      </div>
                                      <div style={{ fontSize:11, color:"#64748b", marginTop:1 }}>
                                        {a.email_apprenant && <span>✉ {a.email_apprenant} · </span>}
                                        {a.telephone && <span>📞 {a.telephone}</span>}
                                      </div>
                                    </div>
                                    <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
                                      {a.niveau && <span style={{ fontSize:11, fontWeight:800, padding:"2px 8px", borderRadius:6, background:niv.bg, color:niv.color }}>{a.niveau}</span>}
                                      <AncienNiveau niveau={a.niveau} precedent={a.niveau_precedent} changeLe={a.niveau_change_le} />
                                      <span style={{ fontSize:11, fontWeight:600, padding:"2px 8px", borderRadius:6, background: a.statut==="actif"?"#dcfce7":"#fee2e2", color: a.statut==="actif"?"#16a34a":"#dc2626" }}>
                                        {a.statut || "actif"}
                                      </span>
                                      {/* Le bouton de suppression a été retiré : sortir un apprenant
                                          d'une classe passe par « Changer de groupe », qui conserve
                                          la trace du mouvement. Une suppression, elle, effaçait la
                                          ligne et avec elle tout l'historique de son parcours. */}
                                      <span style={{ fontSize:11, color:"#94a3b8" }}>›</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* ── ONGLET PRÉSENCES ── */}
                    {!classeDetailLoading && classeDetailTab === "presences" && (() => {
                      const presences  = classePresences?.presences || [];
                      const parDate    = classePresences?.parDate   || {};
                      const allDates   = Object.keys(parDate).sort().reverse();
                      const dates      = allDates.filter(d => (!presDateDebut || d >= presDateDebut) && (!presDateFin || d <= presDateFin));
                      const totalPresPages = Math.max(1, Math.ceil(dates.length / PRES_PAR_PAGE));
                      const pageClamped   = Math.min(presPage, totalPresPages - 1);
                      const pagedDates    = dates.slice(pageClamped * PRES_PAR_PAGE, (pageClamped + 1) * PRES_PAR_PAGE);
                      if (!allDates.length) return (
                        <div style={{ textAlign:"center", padding:48, color:"#94a3b8", fontSize:13 }}>
                          <div style={{ fontSize:32, marginBottom:8 }}>📅</div>
                          Aucune séance enregistrée
                        </div>
                      );
                      return (
                        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                          {/* Filtre par date */}
                          <div style={{ background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0", padding:"12px 16px", display:"flex", flexWrap:"wrap", gap:10, alignItems:"flex-end" }}>
                            <div>
                              <div style={{ fontSize:10, fontWeight:700, color:"#64748b", marginBottom:4, textTransform:"uppercase", letterSpacing:".05em" }}>Date ≥</div>
                              <input type="date" value={presDateDebut} onChange={e => { setPresDateDebut(e.target.value); setPresPage(0); }}
                                style={{ ...inputSt, width:150, fontSize:12, padding:"6px 10px" }} />
                            </div>
                            <div>
                              <div style={{ fontSize:10, fontWeight:700, color:"#64748b", marginBottom:4, textTransform:"uppercase", letterSpacing:".05em" }}>Date ≤</div>
                              <input type="date" value={presDateFin} onChange={e => { setPresDateFin(e.target.value); setPresPage(0); }}
                                style={{ ...inputSt, width:150, fontSize:12, padding:"6px 10px" }} />
                            </div>
                            {(presDateDebut || presDateFin) && (
                              <button onClick={() => { setPresDateDebut(""); setPresDateFin(""); setPresPage(0); }}
                                style={{ padding:"6px 14px", borderRadius:7, border:"1px solid #fca5a5", background:"#fff5f5", color:"#dc2626", fontSize:12, fontWeight:700, cursor:"pointer", alignSelf:"flex-end" }}>
                                ✕ Réinitialiser
                              </button>
                            )}
                            <div style={{ marginLeft:"auto", fontSize:11, color:"#64748b", alignSelf:"flex-end", fontWeight:600, paddingBottom:2 }}>
                              {dates.length} séance{dates.length !== 1 ? "s" : ""} affichée{dates.length !== 1 ? "s" : ""}
                              {dates.length !== allDates.length && ` / ${allDates.length} au total`}
                            </div>
                          </div>

                          {dates.length === 0 ? (
                            <div style={{ textAlign:"center", padding:48, color:"#94a3b8", fontSize:13 }}>
                              <div style={{ fontSize:32, marginBottom:8 }}>📅</div>
                              Aucune séance sur cette période
                            </div>
                          ) : pagedDates.map(date => {
                            const seance  = parDate[date];
                            const present = seance.filter(p=>p.statut==="present").length;
                            const total   = seance.length;
                            const taux    = total ? Math.round((present/total)*100) : 0;
                            return (
                              <div key={date} style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                                <div style={{ background:"#f8fafc", padding:"10px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:"1px solid #f1f5f9" }}>
                                  <div style={{ fontWeight:700, color:"#0f172a", fontSize:13 }}>
                                    📅 {new Date(date).toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
                                  </div>
                                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                                    <span style={{ fontSize:12, color:"#16a34a", fontWeight:700 }}>✅ {present}/{total}</span>
                                    <span style={{ fontSize:12, fontWeight:800, padding:"2px 10px", borderRadius:99, background: taux>=80?"#dcfce7":taux>=60?"#fef9c3":"#fee2e2", color: taux>=80?"#16a34a":taux>=60?"#92400e":"#dc2626" }}>
                                      {taux}%
                                    </span>
                                  </div>
                                </div>
                                <div style={{ padding:"8px 16px", display:"flex", flexWrap:"wrap", gap:6 }}>
                                  {seance.map(p => {
                                    const sCfg = { present:{ bg:"#dcfce7", c:"#16a34a", icon:"✅" }, absent:{ bg:"#fee2e2", c:"#dc2626", icon:"❌" }, retard:{ bg:"#fef9c3", c:"#92400e", icon:"⏰" }, excuse:{ bg:"#f1f5f9", c:"#6b7280", icon:"📝" } };
                                    const s = sCfg[p.statut] || { bg:"#f1f5f9", c:"#6b7280", icon:"•" };
                                    return (
                                      <span key={p.id} style={{ fontSize:11, padding:"3px 10px", borderRadius:99, background:s.bg, color:s.c, fontWeight:600 }}>
                                        {s.icon} {p.prenom_apprenant ? `${p.prenom_apprenant} ` : ""}{p.nom_apprenant}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}

                          {/* Pagination */}
                          {dates.length > PRES_PAR_PAGE && (
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:4, padding:"0 4px" }}>
                              <div style={{ fontSize:12, color:"#6b7280" }}>
                                {pageClamped*PRES_PAR_PAGE+1}–{Math.min((pageClamped+1)*PRES_PAR_PAGE,dates.length)} sur {dates.length} séance{dates.length!==1?"s":""}
                              </div>
                              <div style={{ display:"flex", gap:4 }}>
                                <button onClick={() => setPresPage(p=>Math.max(0,p-1))} disabled={pageClamped===0}
                                  style={{ padding:"5px 12px", borderRadius:6, border:"1px solid #e2e8f0", background: pageClamped===0?"#f8fafc":"#fff", color: pageClamped===0?"#d1d5db":"#374151", fontSize:12, cursor: pageClamped===0?"default":"pointer", fontWeight:600 }}>← Préc.</button>
                                {Array.from({length:totalPresPages},(_,i)=>i).map(p => (
                                  <button key={p} onClick={() => setPresPage(p)}
                                    style={{ padding:"5px 12px", borderRadius:6, border: p===pageClamped?`2px solid ${BET_COLOR}`:"1px solid #e2e8f0", background: p===pageClamped?BET_COLOR:"#fff", color: p===pageClamped?"#fff":"#374151", fontSize:12, cursor:"pointer", fontWeight:700, minWidth:34 }}>
                                    {p+1}
                                  </button>
                                ))}
                                <button onClick={() => setPresPage(p=>Math.min(totalPresPages-1,p+1))} disabled={pageClamped===totalPresPages-1}
                                  style={{ padding:"5px 12px", borderRadius:6, border:"1px solid #e2e8f0", background: pageClamped===totalPresPages-1?"#f8fafc":"#fff", color: pageClamped===totalPresPages-1?"#d1d5db":"#374151", fontSize:12, cursor: pageClamped===totalPresPages-1?"default":"pointer", fontWeight:600 }}>Suiv. →</button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* ── ONGLET PROGRESSION ── */}
                    {!classeDetailLoading && classeDetailTab === "historique" && (() => {
                      /* Mouvements d'apprenants : qui a rejoint la classe, qui l'a quittée.
                         Même lecture chronologique que l'historique d'un apprenant, mais vue
                         depuis la classe - le plus récent en tête. */
                      const fmt = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day:"2-digit", month:"short", year:"numeric" }) : "-";
                      return (
                        <div style={{ padding:"20px 24px" }}>
                          <div style={{ display:"flex", alignItems:"baseline", gap:10, marginBottom:16 }}>
                            <h4 style={{ margin:0, fontSize:14, fontWeight:800, color:"#0f172a" }}>🕓 Mouvements d'apprenants</h4>
                            <span style={{ fontSize:11, color:"#94a3b8" }}>
                              {classeHistorique.length} mouvement{classeHistorique.length > 1 ? "s" : ""}
                            </span>
                          </div>

                          {classeHistoriqueLoading ? (
                            <div style={{ textAlign:"center", padding:40, color:"#94a3b8", fontSize:13 }}>Chargement…</div>
                          ) : classeHistorique.length === 0 ? (
                            <div style={{ textAlign:"center", padding:48, color:"#94a3b8" }}>
                              <div style={{ fontSize:32, marginBottom:10 }}>🕓</div>
                              <div style={{ fontWeight:700, fontSize:13 }}>Aucun mouvement d'apprenant</div>
                              <div style={{ fontSize:12, marginTop:6 }}>
                                Les arrivées et départs par transfert apparaîtront ici.
                              </div>
                            </div>
                          ) : (
                            <div style={{ display:"grid", gap:10 }}>
                              {classeHistorique.map((m, i) => {
                                const entrant = m.type === "arrivee";
                                const teinte  = entrant ? "#16a34a" : "#b45309";
                                const fond    = entrant ? "#f0fdf4" : "#fffbeb";
                                return (
                                  <div key={i} style={{ display:"flex", gap:12, alignItems:"flex-start", background:fond,
                                    border:`1px solid ${teinte}22`, borderLeft:`3px solid ${teinte}`, borderRadius:8, padding:"12px 14px" }}>
                                    <span style={{ fontSize:16, lineHeight:1.2 }}>{entrant ? "↘" : "↗"}</span>
                                    <div style={{ flex:1, minWidth:0 }}>
                                      <div style={{ display:"flex", flexWrap:"wrap", alignItems:"baseline", gap:8 }}>
                                        <span style={{ fontWeight:700, fontSize:13, color:"#0f172a" }}>{m.apprenant}</span>
                                        {m.niveau && (
                                          <span style={{ fontSize:10, fontWeight:700, color:teinte, background:"#fff",
                                            border:`1px solid ${teinte}44`, borderRadius:99, padding:"1px 7px" }}>{m.niveau}</span>
                                        )}
                                        <span style={{ fontSize:12, color:teinte, fontWeight:600 }}>
                                          {entrant ? "a rejoint cette classe" : "a quitté cette classe"}
                                        </span>
                                      </div>
                                      {m.classe_liee && (
                                        <div style={{ fontSize:12, color:"#475569", marginTop:3 }}>
                                          {entrant ? "En provenance de" : "Vers"} <strong>{m.classe_liee}</strong>
                                        </div>
                                      )}
                                      {m.motif && (
                                        <div style={{ fontSize:12, color:"#64748b", marginTop:3, fontStyle:"italic" }}>« {m.motif} »</div>
                                      )}
                                      <div style={{ fontSize:11, color:"#94a3b8", marginTop:4 }}>
                                        {fmt(m.date)}{m.par ? ` · par ${m.par}` : ""}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })()}


                    {!classeDetailLoading && classeDetailTab === "progression" && (() => {
                      const apprenants = classeDetailData?.apprenants || [];
                      const presences  = classePresences?.presences   || [];
                      const parDate    = classePresences?.parDate      || {};
                      const nbSeances  = Object.keys(parDate).length;

                      // Taux global présence
                      const totalP  = presences.length;
                      const presentP = presences.filter(p=>p.statut==="present").length;
                      const tauxGlobal = totalP ? Math.round((presentP/totalP)*100) : null;

                      // Présence par apprenant
                      const presenceParApprenant = {};
                      presences.forEach(p => {
                        const key = (p.nom_apprenant||"")+"|"+(p.prenom_apprenant||"");
                        if (!presenceParApprenant[key]) presenceParApprenant[key] = { present:0, total:0, nom:p.nom_apprenant, prenom:p.prenom_apprenant };
                        presenceParApprenant[key].total++;
                        if (p.statut==="present") presenceParApprenant[key].present++;
                      });

                      const cl = classeDetailData?.groupe || selectedClasse;
                      const dureeJours = cl.date_debut && cl.date_fin ? Math.ceil((new Date(cl.date_fin)-new Date(cl.date_debut))/(1000*60*60*24)) : null;

                      return (
                        <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
                          {/* Métriques globales */}
                          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
                            <div style={{ background:"#f0f9ff", borderRadius:12, padding:16, textAlign:"center", border:"1px solid #bae6fd" }}>
                              <div style={{ fontSize:28, fontWeight:900, color:"#0891b2" }}>{nbSeances}</div>
                              <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>Séances enregistrées</div>
                            </div>
                            <div style={{ background: tauxGlobal===null?"#f8fafc":tauxGlobal>=80?"#f0fdf4":tauxGlobal>=60?"#fefce8":"#fff5f5", borderRadius:12, padding:16, textAlign:"center", border:`1px solid ${tauxGlobal===null?"#e2e8f0":tauxGlobal>=80?"#bbf7d0":tauxGlobal>=60?"#fde68a":"#fecaca"}` }}>
                              <div style={{ fontSize:28, fontWeight:900, color: tauxGlobal===null?"#94a3b8":tauxGlobal>=80?"#16a34a":tauxGlobal>=60?"#d97706":"#dc2626" }}>
                                {tauxGlobal !== null ? `${tauxGlobal}%` : "-"}
                              </div>
                              <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>Taux de présence moyen</div>
                            </div>
                            <div style={{ background:"#faf5ff", borderRadius:12, padding:16, textAlign:"center", border:"1px solid #e9d5ff" }}>
                              <div style={{ fontSize:28, fontWeight:900, color:"#7c3aed" }}>{apprenants.length}</div>
                              <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>Apprenants inscrits</div>
                            </div>
                          </div>

                          {/* Durée formation */}
                          {dureeJours && (
                            <div style={{ background:"#f8fafc", borderRadius:10, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", border:"1px solid #e2e8f0" }}>
                              <span style={{ fontSize:13, color:"#475569" }}>⏱ Durée totale de la formation</span>
                              <span style={{ fontSize:14, fontWeight:800, color:"#0f172a" }}>{dureeJours} jours</span>
                            </div>
                          )}

                          {/* Présence par apprenant */}
                          {Object.keys(presenceParApprenant).length > 0 ? (
                            <div>
                              <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:10 }}>📊 Assiduité individuelle</div>
                              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                                {Object.values(presenceParApprenant).sort((a,b)=>(b.present/b.total)-(a.present/a.total)).map((ap, i) => {
                                  const taux = ap.total ? Math.round((ap.present/ap.total)*100) : 0;
                                  return (
                                    <div key={i} style={{ background:"#fff", borderRadius:9, border:"1px solid #f1f5f9", padding:"10px 14px" }}>
                                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                                        <span style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>
                                          {ap.prenom ? `${ap.prenom} ` : ""}{ap.nom}
                                        </span>
                                        <span style={{ fontSize:12, fontWeight:800, color: taux>=80?"#16a34a":taux>=60?"#d97706":"#dc2626" }}>
                                          {ap.present}/{ap.total} séances · {taux}%
                                        </span>
                                      </div>
                                      <div style={{ height:5, background:"#f1f5f9", borderRadius:99, overflow:"hidden" }}>
                                        <div style={{ height:"100%", width:`${taux}%`, background: taux>=80?"#16a34a":taux>=60?"#f59e0b":"#ef4444", borderRadius:99, transition:"width .4s" }} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <div style={{ textAlign:"center", padding:32, color:"#94a3b8", fontSize:13 }}>
                              <div style={{ fontSize:32, marginBottom:8 }}>📈</div>
                              Les données de progression apparaîtront après les premières séances de présence
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
              </ModaleAnimee>
            )}

            {/* ══ MESSAGERIE FIREBASE TEMPS RÉEL ══ */}
            {activeTab === "messages" && (
              <div>
                {/* Deux canaux distincts : la messagerie privée parle à une personne, le chat
                    de classe parle à TOUS ses apprenants. Les séparer évite de confondre
                    une relance individuelle avec une annonce collective. */}
                <div style={{ display:"flex", gap:0, borderBottom:"1px solid #e5e7eb", marginBottom:18 }}>
                  {[{ key:"prive", label:"💬 Messagerie privée", badge:0 },
                    { key:"groupe", label:"👥 Chat de classe", badge: chatGroupeNonLus },
                  // Les groupes LIBRES, ceux qui ne suivent pas une classe. Sans cet onglet, une
                  // personne ajoutée à un groupe recevait la notification sans avoir nulle part
                  // où ouvrir le fil - c'est ce qui a été constaté.
                    { key:"libre", label:"💬 Groupes", badge: groupesLibresNonLus }].map(t => (
                    <button key={t.key} onClick={() => setMsgSubTab(t.key)}
                      style={{ padding:"10px 20px", border:"none", borderBottom: msgSubTab===t.key ? "2px solid #0891b2" : "2px solid transparent", marginBottom:-1, fontSize:13, fontWeight:600, cursor:"pointer", background:"transparent", color: msgSubTab===t.key ? "#0891b2" : "#64748b" }}>
                      {t.label}
                    {t.badge > 0 && (
                      <span style={{ marginLeft:7, display:"inline-block", minWidth:18, padding:"1px 6px", borderRadius:99, background:"#dc2626", color:"#fff", fontSize:10.5, fontWeight:800, lineHeight:"16px", textAlign:"center" }}>
                        {t.badge > 99 ? "99+" : t.badge}
                      </span>
                    )}
                    </button>
                  ))}
                </div>
                {msgSubTab === "prive" ? <MessagerieTab /> : msgSubTab === "libre" ? (
                  <GroupesLibres profil={profil} token={localStorage.getItem("admin_token")}
                    accent="#0891b2" accentClair="#cffafe" />
                ) : (
                  <ChatGroupe
                    groupes={classes}
                    profil={profil}
                    token={localStorage.getItem("admin_token")}
                    accent="#0891b2"
                    accentClair="#cffafe"
                  />
                )}
              </div>
            )}
            {/* ══ ONGLET SONDAGES ══ */}
            {activeTab === "sondages" && (() => {
              const SOURCE_ICONS = {
                "Bouche à oreille":"🗣️","Facebook / Instagram":"📱","LinkedIn":"💼",
                "Google / Recherche web":"🔍","Radio / Télévision":"📺",
                "Affichage / Flyers":"📋","Recommandé par un ami":"👫",
                "Recommandé par mon entreprise":"🏢","Autre":"✏️",
              };
              const total = sondages.length;
              const topSource = Object.entries(sondageStats).sort((a,b)=>b[1]-a[1])[0];

              return (
                <div>
                  {/* En-tête */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
                    <div>
                      <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#0f172a" }}>🎯 Sondage - Comment vos clients vous ont-ils connu ?</h3>
                      <p style={{ margin:"4px 0 0", fontSize:12, color:"#6b7280" }}>Réponses recueillies automatiquement depuis l'espace client après le test de niveau.</p>
                    </div>
                    <button onClick={() => { fetchSondages(); toast.success("Actualisation…"); }} style={{ ...btnSecondary, fontSize:11, padding:"6px 12px" }}>🔄 Actualiser</button>
                  </div>

                  {/* Stat cards */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:24 }}>
                    <StatCard label="Réponses reçues"  value={total}                         color="#7c3aed" icon="📋" sub="clients ayant répondu" />
                    <StatCard label="Source principale" value={topSource ? topSource[0] : "-"} color="#0891b2" icon="🏆" sub={topSource ? `${topSource[1]} client(s)` : ""} />
                    <StatCard label="Sources distinctes" value={Object.keys(sondageStats).length} color="#059669" icon="🌐" />
                  </div>

                  {total === 0 ? (
                    <div style={{ textAlign:"center", padding:"60px 24px", background:"#f8fafc", borderRadius:16, border:"1.5px dashed #e2e8f0" }}>
                      <div style={{ fontSize:"3rem", marginBottom:12 }}>🎯</div>
                      <div style={{ fontWeight:700, color:"#0f172a", marginBottom:8 }}>Aucune réponse pour l'instant</div>
                      <div style={{ fontSize:13, color:"#6b7280" }}>Le sondage s'affiche automatiquement dans l'espace client après le test de niveau.</div>
                    </div>
                  ) : (
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>

                      {/* Graphique barres */}
                      <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e5e7eb", padding:"20px 24px" }}>
                        <h4 style={{ margin:"0 0 16px", fontSize:13, fontWeight:800, color:"#0f172a" }}>Répartition par source</h4>
                        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                          {Object.entries(sondageStats).sort((a,b)=>b[1]-a[1]).map(([src, count]) => {
                            const pct = Math.round((count / total) * 100);
                            return (
                              <div key={src}>
                                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#374151", marginBottom:4 }}>
                                  <span>{SOURCE_ICONS[src] || "•"} {src}</span>
                                  <span style={{ fontWeight:700, color:"#7c3aed" }}>{count} ({pct}%)</span>
                                </div>
                                <div style={{ background:"#f1f5f9", borderRadius:999, height:8, overflow:"hidden" }}>
                                  <div style={{ width:`${pct}%`, height:"100%", background:"linear-gradient(90deg,#7c3aed,#a78bfa)", borderRadius:999, transition:"width .6s ease" }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Liste des réponses */}
                      <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e5e7eb", padding:"20px 24px", overflowY:"auto", maxHeight:400 }}>
                        <h4 style={{ margin:"0 0 16px", fontSize:13, fontWeight:800, color:"#0f172a" }}>Détail par client</h4>
                        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                          {sondages.map(s => (
                            <div key={s.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", borderRadius:10, background:"#faf5ff", border:"1px solid #e9d5ff" }}>
                              <div style={{ width:36, height:36, borderRadius:"50%", background:"#7c3aed", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:13, flexShrink:0 }}>
                                {(s.email?.[0] || "?").toUpperCase()}
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:12, fontWeight:700, color:"#0f172a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.email}</div>
                                <div style={{ fontSize:11, color:"#7c3aed", fontWeight:600, marginTop:2 }}>
                                  {SOURCE_ICONS[s.source] || "•"} {s.source}
                                  {s.source_detail && <span style={{ color:"#a78bfa", fontWeight:400 }}> - {s.source_detail}</span>}
                                </div>
                              </div>
                              <div style={{ fontSize:10, color:"#9ca3af", whiteSpace:"nowrap", flexShrink:0 }}>
                                {s.created_at ? new Date(s.created_at).toLocaleDateString("fr-FR", { day:"numeric", month:"short" }) : "–"}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── SUIVI DÉMARRAGE (commerciale centre) ── */}
            {activeTab === "suivi" && isCommercialeCentre && (
              <div>
                <div style={{ marginBottom:18 }}>
                  <h2 style={{ margin:0,fontSize:17,fontWeight:800,color:"#0f172a" }}>🚀 Suivi de démarrage - {assistanteCentreNom || "mon centre"}</h2>
                  <p style={{ margin:"4px 0 0",fontSize:12,color:"#6b7280" }}>Nouveaux apprenants de mon centre : email de bienvenue · accès espace apprenant · matériel de cours · planning</p>
                </div>
                {suiviLoading ? (
                  <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:13 }}>Chargement…</div>
                ) : (() => {
                  const suiviFiltres = suiviApprenants.filter(a => {
                    const pct = pctSuiviChecklist(suiviChecklistEffectif(a));
                    if (suiviFiltreStatut === "termine" && pct !== 100) return false;
                    if (suiviFiltreStatut === "non_termine" && pct === 100) return false;
                    if (suiviFiltreDateDebut && (!a.date_ajout || a.date_ajout < suiviFiltreDateDebut)) return false;
                    if (suiviFiltreDateFin && (!a.date_ajout || a.date_ajout > suiviFiltreDateFin)) return false;
                    return true;
                  });
                  const suiviPage = paginateSuivi(suiviFiltres, pageSuivi);
                  return (
                    <>
                      <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap", marginBottom:16 }}>
                        <div style={{ display:"flex", gap:6 }}>
                          {[{v:"tous",l:"Tous"},{v:"non_termine",l:"Non terminés"},{v:"termine",l:"Terminés"}].map(s=>(
                            <button key={s.v} onClick={()=>{ setSuiviFiltreStatut(s.v); setPageSuivi(1); }}
                              style={{ padding:"6px 14px", borderRadius:20, border:`1.5px solid ${suiviFiltreStatut===s.v?"#7c3aed":"#e2e8f0"}`, background:suiviFiltreStatut===s.v?"#ede9fe":"#fff", color:suiviFiltreStatut===s.v?"#7c3aed":"#374151", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                              {s.l}
                            </button>
                          ))}
                        </div>
                        <div style={{ display:"flex", gap:6, alignItems:"center", marginLeft:"auto" }}>
                          <label style={{ fontSize:11, color:"#6b7280" }}>Du</label>
                          <input type="date" value={suiviFiltreDateDebut} onChange={e=>{ setSuiviFiltreDateDebut(e.target.value); setPageSuivi(1); }}
                            style={{ padding:"5px 8px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12 }} />
                          <label style={{ fontSize:11, color:"#6b7280" }}>au</label>
                          <input type="date" value={suiviFiltreDateFin} onChange={e=>{ setSuiviFiltreDateFin(e.target.value); setPageSuivi(1); }}
                            style={{ padding:"5px 8px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12 }} />
                          {(suiviFiltreDateDebut||suiviFiltreDateFin) && (
                            <button onClick={()=>{ setSuiviFiltreDateDebut(""); setSuiviFiltreDateFin(""); setPageSuivi(1); }}
                              style={{ padding:"5px 10px", borderRadius:7, border:"1px solid #e2e8f0", background:"#fff", color:"#6b7280", fontSize:11, cursor:"pointer" }}>✕</button>
                          )}
                        </div>
                      </div>
                      {suiviFiltres.length===0 ? (
                        <div style={{ textAlign:"center",padding:40,color:"#9ca3af",fontSize:13,background:"#f8fafc",borderRadius:12 }}>
                          {suiviApprenants.length===0 ? "Aucun apprenant inscrit pour le moment dans votre centre." : "Aucun apprenant ne correspond à ces filtres."}
                        </div>
                      ) : (
                        <>
                          <div className="table-wrap" style={{ overflowX:"auto", border:"1px solid #e5e7eb", borderRadius:10 }}>
                            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                              <thead>
                                <tr style={{ textAlign:"left", background:"#f8fafc", borderBottom:"1px solid #e5e7eb" }}>
                                  <th style={{ padding:"9px 12px", fontSize:11, color:"#6b7280", textTransform:"uppercase" }}>Apprenant</th>
                                  <th style={{ padding:"9px 12px", fontSize:11, color:"#6b7280", textTransform:"uppercase" }}>Groupe</th>
                                  <th style={{ padding:"9px 12px", fontSize:11, color:"#6b7280", textTransform:"uppercase" }}>Coach</th>
                                  <th style={{ padding:"9px 12px", fontSize:11, color:"#6b7280", textTransform:"uppercase" }}>Inscrit le</th>
                                  <th style={{ padding:"9px 12px", fontSize:11, color:"#6b7280", textTransform:"uppercase" }}>Progression</th>
                                </tr>
                              </thead>
                              <tbody>
                                {suiviPage.map(a=>{
                                  const pct = pctSuiviChecklist(suiviChecklistEffectif(a));
                                  const nomComplet = `${a.prenom_apprenant||""} ${a.nom_apprenant||""}`.trim()||"?";
                                  return (
                                    <tr key={a.id} onClick={()=>setDetailSuiviApprenant(a)} style={{ borderBottom:"1px solid #f1f5f9", cursor:"pointer" }}
                                      onMouseEnter={e=>e.currentTarget.style.background="#f8fafc"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                                      <td style={{ padding:"10px 12px" }}>
                                        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                                          <div style={{ width:32,height:32,borderRadius:"50%",background:"#ede9fe",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:13,color:"#7c3aed",flexShrink:0 }}>
                                            {nomComplet[0].toUpperCase()}
                                          </div>
                                          <span style={{ fontWeight:700,color:"#0f172a" }}>{nomComplet} <span style={{ fontSize:10 }}>✅</span></span>
                                        </div>
                                      </td>
                                      <td style={{ padding:"10px 12px", color:"#374151" }}>{a.groupe_nom||"-"}</td>
                                      <td style={{ padding:"10px 12px", color:"#374151" }}>{a.coach_nom||"-"}</td>
                                      <td style={{ padding:"10px 12px", color:"#6b7280", fontSize:11 }}>{a.date_ajout ? new Date(a.date_ajout).toLocaleDateString("fr-FR") : "-"}</td>
                                      <td style={{ padding:"10px 12px" }}>
                                        <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                                          <div style={{ flex:1,maxWidth:80,height:6,background:"#e5e7eb",borderRadius:4,overflow:"hidden" }}>
                                            <div style={{ height:"100%",width:`${pct}%`,background:pct===100?"#22c55e":pct>=50?"#7c3aed":"#d97706",borderRadius:4 }}/>
                                          </div>
                                          <span style={{ fontSize:12,fontWeight:800,color:pct===100?"#22c55e":pct>=50?"#7c3aed":"#d97706" }}>{pct}%</span>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <SuiviPagination page={pageSuivi} setPage={setPageSuivi} total={suiviFiltres.length} color="#7c3aed" />
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* ── MODAL DÉTAIL SUIVI DÉMARRAGE D'UN APPRENANT ── */}
            {detailSuiviApprenant && (() => {
              const a = suiviApprenants.find(x=>x.id===detailSuiviApprenant.id) || detailSuiviApprenant;
              const checklist = suiviChecklistEffectif(a);
              const pct = pctSuiviChecklist(checklist);
              const tousEnvoyes = SUIVI_CHECKLIST_ITEMS.every(it=>checklist[it.id]);
              const nomComplet = `${a.prenom_apprenant||""} ${a.nom_apprenant||""}`.trim()||"?";
              return (
                <Modal title={`✅ Suivi démarrage - ${nomComplet}`} onClose={()=>setDetailSuiviApprenant(null)}>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16 }}>
                    <div style={{ display:"flex",gap:12,alignItems:"center" }}>
                      <div style={{ width:42,height:42,borderRadius:"50%",background:"#ede9fe",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:15,color:"#7c3aed" }}>
                        {nomComplet[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight:800,fontSize:14,color:"#0f172a" }}>{nomComplet}</div>
                        <div style={{ fontSize:11,color:"#6b7280" }}>{a.groupe_nom||""} · Coach : {a.coach_nom||"-"}</div>
                        <div style={{ fontSize:11,color:"#9ca3af" }}>Inscrit le : {a.date_ajout ? new Date(a.date_ajout).toLocaleDateString("fr-FR") : "-"}</div>
                      </div>
                    </div>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:24,fontWeight:900,color:pct===100?"#22c55e":pct>=50?"#7c3aed":"#d97706" }}>{pct}%</div>
                      <div style={{ fontSize:10,color:"#9ca3af" }}>envoyé</div>
                    </div>
                  </div>
                  <div style={{ height:6,background:"#e5e7eb",borderRadius:4,overflow:"hidden",marginBottom:16 }}>
                    <div style={{ height:"100%",width:`${pct}%`,background:pct===100?"#22c55e":pct>=50?"#7c3aed":"#d97706",borderRadius:4,transition:"width .3s" }}/>
                  </div>
                  <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12 }}>
                    {SUIVI_CHECKLIST_ITEMS.map(item=>{
                      const fait = checklist[item.id]||false;
                      const isAuto = SUIVI_ITEMS_AUTO.includes(item.id);
                      const peutModifier = !isAuto && peut("inscriptions","update");
                      return (
                        <button
                          key={item.id}
                          disabled={!peutModifier}
                          title={isAuto ? "Automatique - déclenché dès la création du compte apprenant, non modifiable manuellement" : permBtnTitle(item.label,peut("inscriptions","update"))}
                          onClick={()=>{ if (!isAuto) toggleSuiviCheck(a.id,item.id); }}
                          style={{
                            display:"flex",alignItems:"center",gap:10,
                            padding:"10px 14px",borderRadius:10,cursor:peutModifier?"pointer":"default",
                            border:`1.5px solid ${fait?"#bbf7d0":"#e5e7eb"}`,
                            background:fait?"#f0fdf4":"#f8fafc",
                            textAlign:"left",width:"100%",
                            opacity:isAuto?1:(peut("inscriptions","update")?1:0.45),
                          }}
                        >
                          <span style={{ fontSize:18,flexShrink:0 }}>{fait?"✅":item.icon}</span>
                          <div>
                            <div style={{ fontSize:12,fontWeight:700,color:fait?"#166534":"#374151" }}>{item.label}</div>
                            <div style={{ fontSize:10,color:fait?"#22c55e":"#9ca3af" }}>
                              {isAuto ? (fait?"Envoyé automatiquement":"En attente - compte pas encore créé") : (fait?"Envoyé":"En attente")}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    value={a.suivi_notes||""}
                    disabled={!peut("inscriptions","update")}
                    title={permBtnTitle("Notes de suivi",peut("inscriptions","update"))}
                    onChange={e=>setSuiviApprenants(prev=>prev.map(x=>x.id===a.id?{...x,suivi_notes:e.target.value}:x))}
                    onBlur={e=>enregistrerSuiviNotes(a.id,e.target.value)}
                    placeholder="Notes de suivi…"
                    style={{ width:"100%",minHeight:60,padding:"8px 12px",borderRadius:8,background:peut("inscriptions","update")?"#f8fafc":"#f1f5f9",border:"1px solid #e2e8f0",fontSize:12,color:"#374151",fontFamily:"inherit",resize:"vertical",opacity:peut("inscriptions","update")?1:0.6 }}
                  />
                  {tousEnvoyes && (
                    <div style={{ marginTop:12,padding:"10px 14px",borderRadius:10,background:"#dcfce7",border:"1px solid #bbf7d0",fontSize:13,color:"#166534",fontWeight:700,textAlign:"center" }}>
                      🎉 Kick-off complet ! Tous les envois ont été effectués.
                    </div>
                  )}
                </Modal>
              );
            })()}

            {/* ══════════════════════════════════════════
                ONGLETS CORPORATE B2B
            ══════════════════════════════════════════ */}

            {/* ── COMPTES ENTREPRISES ── */}
            {activeTab === "entreprises" && (() => {
              const searchE = b2bEntrepriseForm?.__search || "";
              const EMPTY_ENT = {
                nom:"", rccm:"", secteur:"", nb_employes:"", referent_rh_nom:"", referent_rh_email:"", referent_rh_telephone:"",
                budget_formation:"", ville:"", adresse:"", site_web:"", notes:"",
                couleur:"#0891B2",
                package_elearning:"", nb_licences:10, libelle_personnalise:"", tarif_personnalise:"",
                date_debut_contrat:"", date_fin_contrat:"",
                types_sessions:["ligne"], option_replay:false,
                rh_prenom:"", rh_nom:"", rh_email:"",
              };
              const pkgSel = b2bPackages.find(p => p.id === b2bEntrepriseForm?.package_elearning);
              const caEstime = b2bEntrepriseForm?.package_elearning === "personnalise"
                ? Number(b2bEntrepriseForm?.tarif_personnalise || 0) * Number(b2bEntrepriseForm?.nb_licences || 0)
                : Number(pkgSel?.tarif || 0) * Number(b2bEntrepriseForm?.nb_licences || 0);
              const filtered = b2bEntreprises.filter(e => !searchE || e.nom?.toLowerCase().includes(searchE.toLowerCase()) || e.secteur?.toLowerCase().includes(searchE.toLowerCase()));

              return (
                <div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:10 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <input placeholder="🔍 Rechercher une entreprise…" style={{ padding:"7px 12px", borderRadius:6, border:"1px solid #e5e7eb", fontSize:12, width:220 }}
                        onChange={e => setB2bEntrepriseForm(f => ({ ...(f||{}), __search: e.target.value }))} />
                      <button onClick={() => { setB2bLoaded(false); loadB2B(); toast.success("Actualisation…"); }} style={{ ...btnSecondary, fontSize:11, padding:"6px 12px" }}>🔄</button>
                    </div>
                    <button disabled={!peut("crm_entreprises","create")} title={permBtnTitle("Nouvelle entreprise",peut("crm_entreprises","create"))} onClick={() => setB2bEntrepriseForm({ ...EMPTY_ENT, __mode:"new" })} style={permBtnStyle(btnPrimary,peut("crm_entreprises","create"))}>{peut("crm_entreprises","create")?"➕":"🔒"} Nouvelle entreprise</button>
                  </div>

                  {b2bLoading ? <div style={{ textAlign:"center", padding:40, color:"#94a3b8" }}>⏳ Chargement…</div> : filtered.length === 0 ? (
                    <div style={{ textAlign:"center", padding:60, background:"#f8fafc", borderRadius:16, border:"1.5px dashed #e2e8f0" }}>
                      <div style={{ fontSize:"3rem", marginBottom:10 }}>🏢</div>
                      <div style={{ fontWeight:700, color:"#0f172a" }}>Aucun compte entreprise</div>
                      <div style={{ fontSize:12, color:"#6b7280", marginTop:4 }}>Créez votre premier client B2B.</div>
                    </div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      {filtered.map(e => {
                        const isExp = (b2bEntrepriseForm?.__expanded) === e.id;
                        return (
                          <div key={e.id} style={{ background:"#fff", borderRadius:12, border:"1.5px solid #e5e7eb", overflow:"hidden" }}>
                            <div style={{ padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer", gap:10 }}
                              onClick={() => setB2bEntrepriseForm(f => ({ ...(f||{}), __expanded: isExp ? null : e.id }))}>
                              <div style={{ display:"flex", alignItems:"center", gap:10, flex:1, minWidth:0 }}>
                                <div style={{ width:38, height:38, borderRadius:10, background:"#eff6ff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>🏢</div>
                                <div style={{ minWidth:0 }}>
                                  <div style={{ fontWeight:800, fontSize:13, color:"#0f172a" }}>{e.nom}</div>
                                  <div style={{ fontSize:11, color:"#6b7280" }}>{e.secteur || "Secteur N/R"}{e.ville ? ` · ${e.ville}` : ""}</div>
                                </div>
                              </div>
                              <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
                                {e.budget_formation && <span style={{ fontSize:11, fontWeight:700, color:"#059669" }}>{fmtB2B(e.budget_formation)}</span>}
                                {e.nb_employes && <span style={{ fontSize:11, color:"#6b7280" }}>{e.nb_employes} emp.</span>}
                                <span style={{ fontSize:10, padding:"2px 8px", borderRadius:999, background: e.statut==="actif"?"#dcfce7":"#fee2e2", color: e.statut==="actif"?"#166534":"#dc2626", fontWeight:700 }}>{e.statut}</span>
                                <button disabled={!peut("crm_entreprises","update")} title={permBtnTitle("Modifier",peut("crm_entreprises","update"))} onClick={ev => { ev.stopPropagation(); setB2bEntrepriseForm({ ...e, __mode:"edit" }); }} style={permBtnStyle({ padding:"4px 8px", background:BET_LIGHT, color:BET_DARK, border:`1px solid ${BET_COLOR}40`, borderRadius:4, cursor:"pointer", fontSize:11, fontWeight:600 },peut("crm_entreprises","update"))}>{peut("crm_entreprises","update")?"✏️":"🔒"} Modifier</button>
                                <button disabled={b2bDeletingId===e.id || !peut("crm_entreprises","delete")} title={permBtnTitle("Supprimer",peut("crm_entreprises","delete"))} onClick={async ev => { ev.stopPropagation(); if (b2bDeletingId===e.id) return; if(await confirm("Supprimer cette entreprise ?", { titre: "Supprimer l'entreprise" })) { setB2bDeletingId(e.id); fetch(`${API_URL}/api/corporate/entreprises/${e.id}`,{method:"DELETE",headers:authHeaders()}).then(()=>{ setB2bLoaded(false); loadB2B(); toast.success("Supprimé"); }).finally(()=>setB2bDeletingId(null)); } }} style={permBtnStyle({ padding:"4px 8px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:4, cursor:b2bDeletingId===e.id?"not-allowed":"pointer", fontSize:11, opacity:b2bDeletingId===e.id?0.5:1 },peut("crm_entreprises","delete"))}>{peut("crm_entreprises","delete")?"🗑️":"🔒"}</button>
                              </div>
                            </div>
                            {isExp && (
                              <div style={{ padding:"0 16px 14px", borderTop:"1px solid #f1f5f9", display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:10, marginTop:10 }}>
                                {[["📋 RCCM",e.rccm],["📧 Email RH",e.referent_rh_email],["📞 Tél RH",e.referent_rh_telephone],["👤 Référent RH",e.referent_rh_nom],["📍 Adresse",e.adresse],["🌐 Site web",e.site_web]].filter(([,v])=>v).map(([l,v])=>(
                                  <div key={l}><div style={{ fontSize:9, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", marginBottom:2 }}>{l}</div><div style={{ fontSize:12, color:"#0f172a" }}>{v}</div></div>
                                ))}
                                {e.notes && <div style={{ gridColumn:"1/-1" }}><div style={{ fontSize:9, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", marginBottom:2 }}>📝 NOTES</div><div style={{ fontSize:12, color:"#0f172a" }}>{e.notes}</div></div>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Modal entreprise */}
                  {b2bEntrepriseForm && (b2bEntrepriseForm.__mode === "new" || b2bEntrepriseForm.__mode === "edit") && (
                    <div style={modalOverlay} onClick={e => { if(e.target===e.currentTarget) setB2bEntrepriseForm(null); }}>
                      <div style={{ ...modalBox, width:580, maxHeight:"90vh", overflowY:"auto" }}>
                        <h3 style={{ margin:"0 0 16px", fontSize:15, fontWeight:800, color:"#0f172a" }}>{b2bEntrepriseForm.__mode==="edit" ? "✏️ Modifier l'entreprise" : "🏢 Nouvelle entreprise cliente"}</h3>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 14px" }}>
                          {[["Nom de l'entreprise *","nom","text",true,"BET Industries SA"],["RCCM","rccm","text",false,"CI-ABJ-2024-…"],["Nombre d'employés","nb_employes","number",false,"250"],["Budget formation (FCFA)","budget_formation","number",false,"5 000 000"],["Référent RH - Nom","referent_rh_nom","text",false,""],["Référent RH - Email","referent_rh_email","email",false,""],["Référent RH - Tél.","referent_rh_telephone","text",false,""],["Ville","ville","text",false,"Abidjan"],["Site web","site_web","url",false,"https://…"]].map(([label, key, type, req, ph])=>(
                            <div key={key} style={{ gridColumn: key==="nom" ? "1/-1" : undefined, marginBottom:10 }}>
                              <label style={labelSt}>{label}</label>
                              <input type={type} value={b2bEntrepriseForm[key]||""} onChange={e=>setB2bEntrepriseForm(f=>({...f,[key]:e.target.value}))} placeholder={ph||""} style={inputSt} />
                            </div>
                          ))}
                          <div style={{ gridColumn:"1/-1", marginBottom:10 }}>
                            <label style={labelSt}>Secteur d'activité</label>
                            <select value={b2bEntrepriseForm.secteur||""} onChange={e=>setB2bEntrepriseForm(f=>({...f,secteur:e.target.value}))} style={inputSt}>
                              <option value="">- Choisir -</option>
                              {B2B_SECTEURS.map(s=><option key={s}>{s}</option>)}
                            </select>
                          </div>

                          {/* Charte graphique */}
                          <div style={{ gridColumn:"1/-1", marginBottom:10 }}>
                            <label style={labelSt}>Couleur du dashboard entreprise</label>
                            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                              <input type="color" value={b2bEntrepriseForm.couleur||"#0891B2"} onChange={e=>setB2bEntrepriseForm(f=>({...f,couleur:e.target.value}))} style={{ width:40, height:36, border:"1px solid #e5e7eb", borderRadius:6, cursor:"pointer", padding:2 }} />
                              <input type="text" value={b2bEntrepriseForm.couleur||"#0891B2"} onChange={e=>setB2bEntrepriseForm(f=>({...f,couleur:e.target.value}))} style={{ ...inputSt, flex:1 }} />
                            </div>
                          </div>

                          {/* Contrat & Package */}
                          <div style={{ gridColumn:"1/-1", padding:"12px 14px", background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0", marginBottom:10 }}>
                            <div style={{ fontWeight:800, fontSize:12, color:"#0f172a", marginBottom:10 }}>📦 Contrat & Package</div>
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 14px" }}>
                              <div style={{ marginBottom:10 }}>
                                <label style={labelSt}>Package</label>
                                <select value={b2bEntrepriseForm.package_elearning||""} onChange={e=>setB2bEntrepriseForm(f=>({...f,package_elearning:e.target.value}))} style={inputSt}>
                                  <option value="">- Sélectionner un package -</option>
                                  {b2bPackages.map(p=><option key={p.id} value={p.id}>{p.libelle} - {(p.tarif||0).toLocaleString("fr-FR")} FCFA/bénéf.</option>)}
                                  <option value="personnalise">Package personnalisé…</option>
                                </select>
                              </div>
                              <div style={{ marginBottom:10 }}>
                                <label style={labelSt}>Nombre de bénéficiaires</label>
                                <input type="number" min="1" value={b2bEntrepriseForm.nb_licences||10} onChange={e=>setB2bEntrepriseForm(f=>({...f,nb_licences:e.target.value}))} style={inputSt} />
                              </div>
                              {b2bEntrepriseForm.package_elearning === "personnalise" && (
                                <>
                                  <div style={{ marginBottom:10 }}>
                                    <label style={labelSt}>Libellé de l'offre</label>
                                    <input type="text" value={b2bEntrepriseForm.libelle_personnalise||""} onChange={e=>setB2bEntrepriseForm(f=>({...f,libelle_personnalise:e.target.value}))} style={inputSt} />
                                  </div>
                                  <div style={{ marginBottom:10 }}>
                                    <label style={labelSt}>Tarif personnalisé (FCFA/bénéf.)</label>
                                    <input type="number" value={b2bEntrepriseForm.tarif_personnalise||""} onChange={e=>setB2bEntrepriseForm(f=>({...f,tarif_personnalise:e.target.value}))} style={inputSt} />
                                  </div>
                                </>
                              )}
                              <div style={{ marginBottom:10 }}>
                                <label style={labelSt}>Date de début *</label>
                                <input type="date" value={b2bEntrepriseForm.date_debut_contrat||""} onChange={e=>setB2bEntrepriseForm(f=>({...f,date_debut_contrat:e.target.value}))} style={inputSt} />
                              </div>
                              <div style={{ marginBottom:10 }}>
                                <label style={labelSt}>Date de fin *</label>
                                <input type="date" value={b2bEntrepriseForm.date_fin_contrat||""} onChange={e=>setB2bEntrepriseForm(f=>({...f,date_fin_contrat:e.target.value}))} style={inputSt} />
                              </div>
                            </div>
                            {!(b2bEntrepriseForm.package_elearning && b2bEntrepriseForm.date_debut_contrat && b2bEntrepriseForm.date_fin_contrat) && (
                              <div style={{ fontSize:11, color:"#d97706", marginBottom:8 }}>⚠️ Sans package ni ces deux dates, le contrat ne sera pas créé automatiquement à la validation.</div>
                            )}
                            {caEstime > 0 && (
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 12px", background:"#dcfce7", borderRadius:8 }}>
                                <span style={{ fontSize:12, fontWeight:700, color:"#166534" }}>CA estimé</span>
                                <span style={{ fontSize:14, fontWeight:800, color:"#166534" }}>{caEstime.toLocaleString("fr-FR")} FCFA</span>
                              </div>
                            )}
                          </div>

                          {/* Modalités de formation */}
                          <div style={{ gridColumn:"1/-1", marginBottom:10 }}>
                            <label style={labelSt}>Modalités de formation</label>
                            <div style={{ display:"flex", gap:8 }}>
                              {[["ligne","💻 En ligne"],["presentiel","🏢 Présentiel"]].map(([v,l])=>{
                                const actif = (b2bEntrepriseForm.types_sessions||[]).includes(v);
                                return (
                                  <button key={v} type="button" onClick={()=>setB2bEntrepriseForm(f=>{
                                    const cur = f.types_sessions||[];
                                    return { ...f, types_sessions: cur.includes(v) ? cur.filter(x=>x!==v) : [...cur, v] };
                                  })} style={{ flex:1, padding:"9px 10px", borderRadius:8, border:`1.5px solid ${actif?BET_COLOR:"#e5e7eb"}`, background:actif?BET_LIGHT:"#fff", color:actif?BET_DARK:"#374151", fontWeight:700, fontSize:12, cursor:"pointer" }}>{l} {actif?"✓":""}</button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Compte RH */}
                          <div style={{ gridColumn:"1/-1", padding:"12px 14px", background:"#eff6ff", borderRadius:10, border:"1px solid #bfdbfe", marginBottom:10 }}>
                            <div style={{ fontWeight:800, fontSize:12, color:"#0f172a", marginBottom:10 }}>👤 Compte d'accès RH - Dashboard entreprise</div>
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 14px" }}>
                              <div style={{ marginBottom:10 }}>
                                <label style={labelSt}>Prénom RH</label>
                                <input type="text" value={b2bEntrepriseForm.rh_prenom||""} onChange={e=>setB2bEntrepriseForm(f=>({...f,rh_prenom:e.target.value}))} style={inputSt} />
                              </div>
                              <div style={{ marginBottom:10 }}>
                                <label style={labelSt}>Nom RH</label>
                                <input type="text" value={b2bEntrepriseForm.rh_nom||""} onChange={e=>setB2bEntrepriseForm(f=>({...f,rh_nom:e.target.value}))} style={inputSt} />
                              </div>
                              <div style={{ gridColumn:"1/-1", marginBottom:0 }}>
                                <label style={labelSt}>Email de connexion RH</label>
                                <input type="email" value={b2bEntrepriseForm.rh_email||""} onChange={e=>setB2bEntrepriseForm(f=>({...f,rh_email:e.target.value}))} placeholder="rh@entreprise.ci - servira d'identifiant" style={inputSt} />
                              </div>
                            </div>
                          </div>

                          <div style={{ gridColumn:"1/-1", marginBottom:10 }}>
                            <label style={labelSt}>Notes internes</label>
                            <textarea value={b2bEntrepriseForm.notes||""} onChange={e=>setB2bEntrepriseForm(f=>({...f,notes:e.target.value}))} style={{ ...inputSt, minHeight:55, resize:"vertical" }} />
                          </div>
                        </div>
                        <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:8 }}>
                          <button onClick={()=>setB2bEntrepriseForm(null)} style={btnSecondary}>Annuler</button>
                          {(() => { const entAction = b2bEntrepriseForm.__mode==="edit" ? "update" : "create"; const entOk = peut("crm_entreprises",entAction); return (
                          <button disabled={b2bSavingId==="entreprise" || !entOk} title={permBtnTitle("Enregistrer",entOk)} onClick={()=>saveB2BEntreprise(b2bEntrepriseForm)} style={permBtnStyle(btnPrimary,entOk)}>{b2bSavingId==="entreprise"?"⏳…":!entOk?"🔒":b2bEntrepriseForm.__mode==="edit"?"💾 Mettre à jour":"✅ Créer l'entreprise"}</button>
                          ); })()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Compte RH créé - identifiants à transmettre */}
                  {b2bCompteRH && (
                    <div style={modalOverlay} onClick={e => { if(e.target===e.currentTarget) setB2bCompteRH(null); }}>
                      <div style={{ ...modalBox, width:420 }}>
                        <h3 style={{ margin:"0 0 12px", fontSize:15, fontWeight:800, color:"#0f172a" }}>✅ Compte RH créé</h3>
                        <p style={{ fontSize:12, color:"#6b7280", marginBottom:14 }}>Transmettez ces identifiants à {b2bCompteRH.prenom} {b2bCompteRH.nom} pour son premier accès au dashboard entreprise.</p>
                        <div style={{ background:"#f8fafc", borderRadius:8, padding:12, fontSize:13, marginBottom:16 }}>
                          <div><strong>Email :</strong> {b2bCompteRH.email}</div>
                          <div><strong>Mot de passe :</strong> {b2bCompteRH.mot_de_passe}</div>
                        </div>
                        <button onClick={()=>setB2bCompteRH(null)} style={{ ...btnPrimary, width:"100%" }}>Fermer</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── PIPELINE B2B ── */}
            {/* ══════════════ TABLEAU DE BORD CORPORATE ══════════════ */}
            {activeTab === "tdb_corporate" && (() => {
              const I = corpIndic?.indicateurs;
              const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("fr-FR");
              const fcfa = (n) => n == null || !n ? "—" : `${Number(n).toLocaleString("fr-FR")} F`;

              /* LES DEUX INDICATEURS SANS SOURCE SONT ANNONCÉS COMME TELS.
                 Afficher « 0 contrat signé » se lirait comme un échec commercial et
                 déclencherait des réunions ; « non mesuré » dit la vérité : rien ne relie
                 encore un contrat à une campagne, et les coûts de campagne ne sont saisis
                 nulle part. Un zéro faux coûte plus cher qu'une case vide assumée. */
              const CARTES = [
                { l:"Visites campagne",    v: fmt(I?.visites_campagne),    sous:"Sessions issues d’un lien traqué", c:"#0891b2" },
                { l:"Demandes Corporate",  v: fmt(I?.demandes_corporate),  sous:"Formulaires « Former mes collaborateurs »", c:"#0c33ac" },
                { l:"Demandes découverte", v: fmt(I?.demandes_decouverte), sous:"Formulaires « Tester notre pédagogie »", c:"#7c3aed" },
                { l:"Rendez-vous",         v: fmt(I?.rendez_vous),         sous:"Échanges planifiés avec décideurs", c:"#f59e0b" },
                { l:"Devis envoyés",       v: fmt(I?.devis_envoyes),       sous: I?.devis_montant ? fcfa(I.devis_montant) : "Propositions émises", c:"#16a34a" },
                { l:"Contrats signés",     v: "—", sous:"Non mesuré — aucun lien contrat ↔ campagne", c:"#94a3b8", absent:true },
                { l:"Particuliers",        v: fmt(I?.particuliers),        sous: I?.particuliers_ca ? fcfa(I.particuliers_ca) : "Demandes hors Corporate", c:"#e93747" },
                { l:"Rentabilité",         v: "—", sous:"Non mesuré — coûts de campagne non saisis", c:"#94a3b8", absent:true },
              ];

              const leads = corpLeads.filter(l => !corpFiltre || l.cycle_statut === corpFiltre);
              const maxEntonnoir = Math.max(1, ...(corpIndic?.entonnoir || []).map(e => e.n));

              return (
                <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                    <div>
                      <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#0f172a" }}>📊 Suivi commercial Corporate</h3>
                      <p style={{ margin:"4px 0 0", fontSize:12, color:"#6b7280" }}>
                        {corpIndic ? `Du ${corpIndic.periode.debut} au ${corpIndic.periode.fin}` : "Chargement…"}
                      </p>
                    </div>
                    <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                      <select value={corpJours} onChange={e => setCorpJours(Number(e.target.value))}
                        style={{ padding:"7px 12px", borderRadius:8, border:"1px solid #e2e8f0", fontSize:12, background:"#fff", color:"#334155" }}>
                        <option value={7}>7 derniers jours</option>
                        <option value={30}>30 derniers jours</option>
                        <option value={90}>90 derniers jours</option>
                        <option value={365}>12 derniers mois</option>
                      </select>
                      <button onClick={chargerCorporate} disabled={corpLoading}
                        style={{ padding:"7px 14px", borderRadius:8, border:"1px solid #e2e8f0", background:"#fff", color:"#334155", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                        {corpLoading ? "⏳…" : "↻ Actualiser"}
                      </button>
                    </div>
                  </div>

                  {/* ── Les huit indicateurs ── */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))", gap:12 }}>
                    {CARTES.map(k => (
                      <div key={k.l} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"15px 17px", borderTop:`3px solid ${k.c}`, opacity: k.absent ? 0.72 : 1 }}>
                        <div style={{ fontSize:23, fontWeight:800, color:k.c, lineHeight:1.1 }}>{k.v}</div>
                        <div style={{ fontSize:12.5, fontWeight:700, color:"#0f172a", marginTop:5 }}>{k.l}</div>
                        <div style={{ fontSize:10.5, color:"#94a3b8", marginTop:3, lineHeight:1.4 }}>{k.sous}</div>
                      </div>
                    ))}
                  </div>

                  {/* ── L'entonnoir ──
                      IL SE LIT DE HAUT EN BAS, chaque étage plus court que le précédent :
                      un décrochage se voit alors d'un coup d'œil, là où sept nombres alignés
                      demandent de les comparer soi-même. */}
                  {corpIndic?.entonnoir && (
                    <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"18px 20px" }}>
                      <div style={{ fontSize:12.5, fontWeight:800, color:"#0f172a", marginBottom:14 }}>Entonnoir des demandes Corporate</div>
                      <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                        {corpIndic.entonnoir.map(e => {
                          const et = etapeCycle(e.cle);
                          return (
                            <div key={e.cle} style={{ display:"flex", alignItems:"center", gap:12 }}>
                              <span style={{ width:92, fontSize:11.5, color:"#64748b", flexShrink:0 }}>{e.label}</span>
                              <div style={{ flex:1, background:"#f1f5f9", borderRadius:6, height:22, overflow:"hidden" }}>
                                <div style={{ width:`${Math.round((e.n / maxEntonnoir) * 100)}%`, height:"100%", background:et.color, borderRadius:6, transition:"width .4s ease" }} />
                              </div>
                              <span style={{ width:40, textAlign:"right", fontSize:12.5, fontWeight:800, color:et.color, flexShrink:0 }}>{e.n}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ── La file des leads ── */}
                  <div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, marginBottom:12, flexWrap:"wrap" }}>
                      <div style={{ fontSize:13, fontWeight:800, color:"#0f172a" }}>
                        Demandes à traiter
                        {corpLeadsARelancer > 0 && (
                          <span style={{ marginLeft:8, padding:"2px 9px", borderRadius:99, background:"#fef2f2", color:"#b91c1c", fontSize:11, fontWeight:700 }}>
                            {corpLeadsARelancer} relance{corpLeadsARelancer > 1 ? "s" : ""} due{corpLeadsARelancer > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                        <button onClick={() => setCorpFiltre("")}
                          style={{ padding:"5px 12px", borderRadius:99, fontSize:11.5, fontWeight:700, cursor:"pointer",
                            border: corpFiltre === "" ? "2px solid #0c33ac" : "1px solid #e2e8f0",
                            background: corpFiltre === "" ? "#e8edff" : "#fff", color: corpFiltre === "" ? "#0c33ac" : "#64748b" }}>
                          Tous ({corpLeads.length})
                        </button>
                        {CYCLE_ETAPES.map(et => {
                          const n = corpLeads.filter(l => l.cycle_statut === et.id).length;
                          if (!n) return null;
                          return (
                            <button key={et.id} onClick={() => setCorpFiltre(corpFiltre === et.id ? "" : et.id)}
                              style={{ padding:"5px 12px", borderRadius:99, fontSize:11.5, fontWeight:700, cursor:"pointer",
                                border: corpFiltre === et.id ? `2px solid ${et.color}` : "1px solid #e2e8f0",
                                background: corpFiltre === et.id ? et.bg : "#fff", color: corpFiltre === et.id ? et.color : "#64748b" }}>
                              {et.label} ({n})
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {corpLoading ? (
                      <div style={{ textAlign:"center", padding:30, color:"#94a3b8", fontSize:13 }}>Chargement…</div>
                    ) : leads.length === 0 ? (
                      <div style={{ background:"#f8fafc", border:"1px dashed #cbd5e1", borderRadius:12, padding:"28px 20px", textAlign:"center", fontSize:12.5, color:"#64748b" }}>
                        {corpLeads.length === 0
                          ? "Aucune demande Corporate reçue pour l’instant."
                          : "Aucune demande à cette étape."}
                      </div>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
                        {leads.map(l => {
                          const et = etapeCycle(l.cycle_statut);
                          const relanceDue = l.relance_at && new Date(l.relance_at) <= new Date() && !["converti","perdu"].includes(l.cycle_statut);
                          return (
                            <div key={l.id} style={{ background:"#fff", border:"1px solid #e5e7eb", borderLeft:`3px solid ${et.color}`, borderRadius:10, padding:"13px 16px" }}>
                              <div style={{ display:"flex", gap:14, alignItems:"flex-start", flexWrap:"wrap" }}>
                                <div style={{ flex:"1 1 260px", minWidth:0 }}>
                                  <div style={{ fontSize:13.5, fontWeight:800, color:"#0f172a" }}>
                                    {l.entreprise_nom || "Entreprise non précisée"}
                                    <span style={{ marginLeft:8, padding:"2px 9px", borderRadius:99, fontSize:10.5, fontWeight:700, background: l.source === "corporate_decouverte" ? "#f3e8ff" : "#e8edff", color: l.source === "corporate_decouverte" ? "#7c3aed" : "#0c33ac" }}>
                                      {l.source === "corporate_decouverte" ? "Découverte" : "Formation"}
                                    </span>
                                  </div>
                                  <div style={{ fontSize:11.5, color:"#64748b", marginTop:3 }}>
                                    {l.prospect_nom}{l.contact_fonction ? ` · ${l.contact_fonction}` : ""} · {l.prospect_telephone}
                                  </div>
                                  <div style={{ fontSize:11, color:"#94a3b8", marginTop:3 }}>
                                    {[l.secteur_activite, l.effectif_estime ? `${l.effectif_estime} collaborateurs` : null,
                                      l.echeance_souhaitee, l.proprietaire ? `Suivi par ${l.proprietaire}` : null]
                                      .filter(Boolean).join(" · ")}
                                  </div>
                                </div>

                                <div style={{ display:"flex", flexDirection:"column", gap:7, flexShrink:0 }}>
                                  <select value={l.cycle_statut || "nouveau"} onChange={e => majLead(l.id, { cycle_statut: e.target.value })}
                                    style={{ padding:"6px 10px", borderRadius:8, border:`1.5px solid ${et.color}`, background:et.bg, color:et.color, fontSize:11.5, fontWeight:700, cursor:"pointer" }}>
                                    {CYCLE_ETAPES.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
                                  </select>
                                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                    <span style={{ fontSize:10.5, color: relanceDue ? "#b91c1c" : "#94a3b8", fontWeight: relanceDue ? 700 : 400 }}>
                                      {relanceDue ? "⏰ Relance due" : "Relance"}
                                    </span>
                                    <input type="date" value={l.relance_at ? String(l.relance_at).slice(0, 10) : ""}
                                      onChange={e => majLead(l.id, { relance_at: e.target.value })}
                                      style={{ padding:"5px 8px", borderRadius:7, border:`1px solid ${relanceDue ? "#fca5a5" : "#e2e8f0"}`, fontSize:11, color:"#334155", background: relanceDue ? "#fef2f2" : "#fff" }} />
                                  </div>
                                </div>
                              </div>

                              {(l.objectif_client || l.notes) && (
                                <div style={{ marginTop:9, paddingTop:9, borderTop:"1px solid #f1f5f9", fontSize:11.5, color:"#64748b", lineHeight:1.55, whiteSpace:"pre-wrap" }}>
                                  {[l.objectif_client, l.notes].filter(Boolean).join("\n")}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {activeTab === "pipeline_b2b" && (() => {
              const EMPTY_PR = { entreprise_id:"", titre:"", statut:"prospection", montant_estime:"", date_cloture_prevue:"", notes:"" };
              const byStatut = {};
              B2B_PIPELINE_STEPS.forEach(s => { byStatut[s.id] = b2bProspects.filter(p => p.statut === s.id); });

              return (
                <div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                    <div>
                      <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#0f172a" }}>🔄 Pipeline de vente B2B</h3>
                      <p style={{ margin:"4px 0 0", fontSize:12, color:"#6b7280" }}>{b2bProspects.length} prospect(s) · {b2bProspects.filter(p=>p.statut==="conclu").length} conclu(s)</p>
                    </div>
                    <button onClick={()=>setB2bProspectForm({...EMPTY_PR,__mode:"new"})} style={btnPrimary}>➕ Nouveau prospect</button>
                  </div>

                  {/* Résumé KPIs */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:8, marginBottom:20 }}>
                    {B2B_PIPELINE_STEPS.map(s => {
                      const items = byStatut[s.id] || [];
                      const total = items.reduce((a,p)=>a+(Number(p.montant_estime)||0),0);
                      return (
                        <div key={s.id} style={{ background:s.bg, borderRadius:10, padding:"10px 12px", border:`1.5px solid ${s.color}30`, textAlign:"center" }}>
                          <div style={{ fontSize:18, fontWeight:800, color:s.color }}>{items.length}</div>
                          <div style={{ fontSize:10, color:s.color, fontWeight:700, marginTop:1 }}>{s.label}</div>
                          {total > 0 && <div style={{ fontSize:9, color:s.color, marginTop:2, opacity:0.8 }}>{fmtB2B(total)}</div>}
                        </div>
                      );
                    })}
                  </div>

                  {/* Table des prospects */}
                  {b2bLoading ? <div style={{ textAlign:"center", padding:30, color:"#94a3b8" }}>⏳ Chargement…</div> : (
                    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse" }}>
                        <thead><tr style={{ background:"#f8fafc", borderBottom:"1px solid #e5e7eb" }}>
                          {["Titre","Entreprise","Statut","Montant estimé","Clôture prévue","Actions"].map(h=><th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"#374151" }}>{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {b2bProspects.length === 0 ? (
                            <tr><td colSpan={6} style={{ padding:40, textAlign:"center", color:"#94a3b8" }}>Aucun prospect. Créez votre premier deal.</td></tr>
                          ) : b2bProspects.map(p => {
                            const st = B2B_PIPELINE_STEPS.find(s=>s.id===p.statut);
                            return (
                              <tr key={p.id} style={{ borderBottom:"1px solid #f1f5f9" }}>
                                <td style={{ padding:"10px 12px", fontSize:12, fontWeight:600, color:"#0f172a" }}>{p.titre}</td>
                                <td style={{ padding:"10px 12px", fontSize:12, color:"#6b7280" }}>🏢 {p.entreprise_nom}</td>
                                <td style={{ padding:"10px 12px" }}>
                                  <select value={p.statut} onChange={async e => { await patchB2BStatut("prospects", p.id, { statut: e.target.value }); toast.success("Statut mis à jour"); }}
                                    style={{ padding:"3px 8px", borderRadius:6, border:`1.5px solid ${st?.color}40`, background:st?.bg, color:st?.color, fontWeight:700, fontSize:11, cursor:"pointer" }}>
                                    {B2B_PIPELINE_STEPS.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                                  </select>
                                </td>
                                <td style={{ padding:"10px 12px", fontSize:12, fontWeight:700, color:"#059669" }}>{p.montant_estime ? fmtB2B(p.montant_estime) : "-"}</td>
                                <td style={{ padding:"10px 12px", fontSize:11, color:"#6b7280" }}>{fmtDateB2B(p.date_cloture_prevue)}</td>
                                <td style={{ padding:"10px 12px", display:"flex", gap:6 }}>
                                  <button onClick={()=>setB2bProspectForm({...p,__mode:"edit"})} style={{ padding:"3px 8px", background:BET_LIGHT, color:BET_DARK, border:`1px solid ${BET_COLOR}40`, borderRadius:4, cursor:"pointer", fontSize:11, fontWeight:600 }}>✏️</button>
                                  <button onClick={()=>setB2bHistoriqueItem(p)} style={{ padding:"3px 8px", background:"#f8fafc", color:"#6b7280", border:"1px solid #e5e7eb", borderRadius:4, cursor:"pointer", fontSize:11 }}>📋</button>
                                  <button disabled={b2bDeletingId===p.id} onClick={async ()=>{ if (b2bDeletingId===p.id) return; if(await confirm("Supprimer ?", { titre: "Supprimer le prospect" })) { setB2bDeletingId(p.id); fetch(`${API_URL}/api/corporate/prospects/${p.id}`,{method:"DELETE",headers:authHeaders()}).then(()=>{setB2bLoaded(false);loadB2B();}).finally(()=>setB2bDeletingId(null)); } }} style={{ padding:"3px 8px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:4, cursor:b2bDeletingId===p.id?"not-allowed":"pointer", fontSize:11, opacity:b2bDeletingId===p.id?0.5:1 }}>🗑️</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Modal prospect */}
                  {b2bProspectForm && (b2bProspectForm.__mode === "new" || b2bProspectForm.__mode === "edit") && (
                    <div style={modalOverlay} onClick={e=>{if(e.target===e.currentTarget)setB2bProspectForm(null);}}>
                      <div style={{ ...modalBox, width:480, maxHeight:"90vh", overflowY:"auto" }}>
                        <h3 style={{ margin:"0 0 16px", fontSize:15, fontWeight:800, color:"#0f172a" }}>{b2bProspectForm.__mode==="edit"?"✏️ Modifier le prospect":"🔄 Nouveau prospect B2B"}</h3>
                        <div style={{ marginBottom:10 }}>
                          <label style={labelSt}>Entreprise *</label>
                          <select value={b2bProspectForm.entreprise_id||""} onChange={e=>setB2bProspectForm(f=>({...f,entreprise_id:e.target.value}))} style={inputSt}>
                            <option value="">- Choisir l'entreprise -</option>
                            {b2bEntreprises.map(e=><option key={e.id} value={e.id}>{e.nom}</option>)}
                          </select>
                        </div>
                        <div style={{ marginBottom:10 }}><label style={labelSt}>Titre / Objet *</label><input value={b2bProspectForm.titre||""} onChange={e=>setB2bProspectForm(f=>({...f,titre:e.target.value}))} style={inputSt} placeholder="Formation Anglais - 20 employés" /></div>
                        <div style={{ marginBottom:10 }}>
                          <label style={labelSt}>Étape pipeline</label>
                          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                            {B2B_PIPELINE_STEPS.map(s=>(
                              <button key={s.id} onClick={()=>setB2bProspectForm(f=>({...f,statut:s.id}))}
                                style={{ padding:"5px 10px", borderRadius:7, border:`1.5px solid ${b2bProspectForm.statut===s.id?s.color:"#e2e8f0"}`, background:b2bProspectForm.statut===s.id?s.bg:"#fff", color:b2bProspectForm.statut===s.id?s.color:"#374151", fontWeight:700, fontSize:11, cursor:"pointer" }}>
                                {s.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                          <div><label style={labelSt}>Montant estimé (FCFA)</label><input type="number" value={b2bProspectForm.montant_estime||""} onChange={e=>setB2bProspectForm(f=>({...f,montant_estime:e.target.value}))} style={inputSt} /></div>
                          <div><label style={labelSt}>Date clôture prévue</label><input type="date" value={b2bProspectForm.date_cloture_prevue||""} onChange={e=>setB2bProspectForm(f=>({...f,date_cloture_prevue:e.target.value}))} style={inputSt} /></div>
                        </div>
                        <div style={{ marginBottom:12 }}><label style={labelSt}>Notes</label><textarea value={b2bProspectForm.notes||""} onChange={e=>setB2bProspectForm(f=>({...f,notes:e.target.value}))} style={{ ...inputSt, minHeight:60, resize:"vertical" }} /></div>
                        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                          <button onClick={()=>setB2bProspectForm(null)} style={btnSecondary}>Annuler</button>
                          <button disabled={b2bSavingId==="prospect"} onClick={()=>saveB2BProspect(b2bProspectForm)} style={btnPrimary}>{b2bSavingId==="prospect"?"⏳…":b2bProspectForm.__mode==="edit"?"💾 Mettre à jour":"✅ Créer"}</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Modal historique */}
                  {b2bHistoriqueItem && (
                    <div style={modalOverlay} onClick={e=>{if(e.target===e.currentTarget)setB2bHistoriqueItem(null);}}>
                      <div style={{ ...modalBox, width:420, maxHeight:"80vh", overflowY:"auto" }}>
                        <h3 style={{ margin:"0 0 4px", fontSize:14, fontWeight:800, color:"#0f172a" }}>📋 Historique - {b2bHistoriqueItem.titre}</h3>
                        <div style={{ fontSize:11, color:"#6b7280", marginBottom:14 }}>🏢 {b2bHistoriqueItem.entreprise_nom}</div>
                        {(b2bHistoriqueItem.historique||[]).length === 0 ? <p style={{ color:"#94a3b8", textAlign:"center" }}>Aucun historique</p> : (
                          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                            {[...(b2bHistoriqueItem.historique||[])].reverse().map((h,i) => {
                              const st = B2B_PIPELINE_STEPS.find(s=>s.id===h.statut);
                              return (
                                <div key={i} style={{ padding:"8px 12px", borderRadius:8, background:st?.bg||"#f8fafc", border:`1px solid ${st?.color||"#e2e8f0"}30`, display:"flex", gap:10, alignItems:"flex-start" }}>
                                  <div style={{ width:7, height:7, borderRadius:"50%", background:st?.color||"#94a3b8", marginTop:4, flexShrink:0 }} />
                                  <div>
                                    <div style={{ fontWeight:700, fontSize:12, color:st?.color||"#0f172a" }}>{h.action}</div>
                                    {h.note && <div style={{ fontSize:11, color:"#6b7280" }}>{h.note}</div>}
                                    <div style={{ fontSize:10, color:"#94a3b8", marginTop:2 }}>{fmtDateB2B(h.date)}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div style={{ marginTop:16, textAlign:"right" }}><button onClick={()=>setB2bHistoriqueItem(null)} style={btnSecondary}>Fermer</button></div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── DOCUMENTS COMMERCIAUX B2B ── */}
            {activeTab === "documents_b2b" && (() => {
              const EMPTY_DOC = { entreprise_id:"", type_doc:"proforma", titre:"", montant:"", statut:"brouillon", notes:"", fichier_url:"" };
              const STATUT_DOC_COLOR = { brouillon:"#94a3b8", "envoyé":"#0891b2", "signé":"#16a34a", "annulé":"#dc2626" };

              return (
                <div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                    <div>
                      <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#0f172a" }}>📄 Documents commerciaux</h3>
                      <p style={{ margin:"4px 0 0", fontSize:12, color:"#6b7280" }}>Proformas · Bons de commande · Contrats</p>
                    </div>
                    <button disabled={!peut("contrat","create")} title={permBtnTitle("Nouveau document",peut("contrat","create"))} onClick={()=>setB2bDocumentForm({...EMPTY_DOC})} style={permBtnStyle(btnPrimary,peut("contrat","create"))}>{peut("contrat","create")?"➕":"🔒"} Nouveau document</button>
                  </div>

                  {/* Filtres type */}
                  <div style={{ display:"flex", gap:6, marginBottom:16, flexWrap:"wrap" }}>
                    {["tous","proforma","bon_commande","contrat","autre"].map(t=>(
                      <button key={t} onClick={()=>setB2bDocumentForm(f=>({...(f||{}),__filterType:t}))}
                        style={{ padding:"5px 11px", borderRadius:999, border:`1.5px solid ${(b2bDocumentForm?.__filterType||"tous")===t?BET_COLOR:"#e5e7eb"}`, background:(b2bDocumentForm?.__filterType||"tous")===t?BET_LIGHT:"#fff", color:(b2bDocumentForm?.__filterType||"tous")===t?BET_COLOR:"#374151", fontWeight:700, fontSize:11, cursor:"pointer" }}>
                        {t==="tous"?"Tous":B2B_DOC_TYPES[t]}
                      </button>
                    ))}
                  </div>

                  {b2bLoading ? <div style={{ textAlign:"center", padding:30, color:"#94a3b8" }}>⏳ Chargement…</div> : (() => {
                    const filterType = b2bDocumentForm?.__filterType || "tous";
                    const filteredDocs = filterType === "tous" ? b2bDocuments : b2bDocuments.filter(d=>d.type_doc===filterType);
                    return filteredDocs.length === 0 ? (
                      <div style={{ textAlign:"center", padding:60, background:"#f8fafc", borderRadius:16, border:"1.5px dashed #e2e8f0" }}>
                        <div style={{ fontSize:"2.5rem", marginBottom:10 }}>📄</div>
                        <div style={{ fontWeight:700, color:"#0f172a" }}>Aucun document</div>
                      </div>
                    ) : (
                      <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden" }}>
                        <table style={{ width:"100%", borderCollapse:"collapse" }}>
                          <thead><tr style={{ background:"#f8fafc", borderBottom:"1px solid #e5e7eb" }}>
                            {["Type","Titre","Entreprise","Montant","Statut","Date","Actions"].map(h=><th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"#374151" }}>{h}</th>)}
                          </tr></thead>
                          <tbody>
                            {filteredDocs.map(d=>(
                              <tr key={d.id} style={{ borderBottom:"1px solid #f1f5f9" }}>
                                <td style={{ padding:"10px 12px", fontSize:13 }}>{B2B_DOC_TYPES[d.type_doc]?.split(" ")[0]}</td>
                                <td style={{ padding:"10px 12px", fontSize:12, fontWeight:600, color:"#0f172a" }}>{d.titre}</td>
                                <td style={{ padding:"10px 12px", fontSize:12, color:"#6b7280" }}>🏢 {d.entreprise_nom}</td>
                                <td style={{ padding:"10px 12px", fontSize:12, fontWeight:700, color:"#059669" }}>{d.montant ? fmtB2B(d.montant) : "-"}</td>
                                <td style={{ padding:"10px 12px" }}>
                                  <span style={{ padding:"2px 8px", borderRadius:999, fontSize:10, fontWeight:700, background:`${STATUT_DOC_COLOR[d.statut]||"#94a3b8"}20`, color:STATUT_DOC_COLOR[d.statut]||"#94a3b8" }}>{d.statut}</span>
                                </td>
                                <td style={{ padding:"10px 12px", fontSize:11, color:"#9ca3af" }}>{fmtDateB2B(d.created_at)}</td>
                                <td style={{ padding:"10px 12px", display:"flex", gap:5 }}>
                                  {d.fichier_url && <a href={d.fichier_url} target="_blank" rel="noreferrer" style={{ padding:"3px 8px", background:BET_LIGHT, color:BET_DARK, borderRadius:4, fontSize:11, fontWeight:600, textDecoration:"none" }}>📎 Fichier</a>}
                                  {d.statut==="brouillon" && <button disabled={!peut("contrat","update")} title={permBtnTitle("Marquer envoyé",peut("contrat","update"))} onClick={()=>{ patchB2BStatut("documents",d.id,{statut:"envoyé"}); toast.success("Marqué envoyé"); }} style={permBtnStyle({ padding:"3px 8px", background:"#e0f2fe", color:BET_COLOR, border:"none", borderRadius:4, cursor:"pointer", fontSize:11, fontWeight:600 },peut("contrat","update"))}>{peut("contrat","update")?"📤":"🔒"} Envoyé</button>}
                                  {d.statut==="envoyé"    && <button disabled={!peut("contrat","update")} title={permBtnTitle("Marquer signé",peut("contrat","update"))} onClick={()=>{ patchB2BStatut("documents",d.id,{statut:"signé"}); toast.success("Marqué signé ✓"); }} style={permBtnStyle({ padding:"3px 8px", background:"#dcfce7", color:"#166534", border:"none", borderRadius:4, cursor:"pointer", fontSize:11, fontWeight:600 },peut("contrat","update"))}>{peut("contrat","update")?"✅":"🔒"} Signé</button>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}

                  {/* Modal document */}
                  {b2bDocumentForm && !b2bDocumentForm.__filterType && (
                    <div style={modalOverlay} onClick={e=>{if(e.target===e.currentTarget)setB2bDocumentForm(null);}}>
                      <div style={{ ...modalBox, width:480, maxHeight:"90vh", overflowY:"auto" }}>
                        <h3 style={{ margin:"0 0 16px", fontSize:15, fontWeight:800, color:"#0f172a" }}>📄 Nouveau document commercial</h3>
                        <div style={{ marginBottom:10 }}>
                          <label style={labelSt}>Entreprise *</label>
                          <select value={b2bDocumentForm.entreprise_id||""} onChange={e=>setB2bDocumentForm(f=>({...f,entreprise_id:e.target.value}))} style={inputSt}>
                            <option value="">- Choisir -</option>
                            {b2bEntreprises.map(e=><option key={e.id} value={e.id}>{e.nom}</option>)}
                          </select>
                        </div>
                        <div style={{ marginBottom:10 }}>
                          <label style={labelSt}>Type de document</label>
                          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                            {Object.entries(B2B_DOC_TYPES).map(([v,l])=>(
                              <button key={v} onClick={()=>setB2bDocumentForm(f=>({...f,type_doc:v}))}
                                style={{ padding:"6px 11px", borderRadius:7, border:`1.5px solid ${b2bDocumentForm.type_doc===v?BET_COLOR:"#e5e7eb"}`, background:b2bDocumentForm.type_doc===v?BET_LIGHT:"#fff", color:b2bDocumentForm.type_doc===v?BET_COLOR:"#374151", fontWeight:700, fontSize:11, cursor:"pointer" }}>
                                {l}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div style={{ marginBottom:10 }}><label style={labelSt}>Titre *</label><input value={b2bDocumentForm.titre||""} onChange={e=>setB2bDocumentForm(f=>({...f,titre:e.target.value}))} style={inputSt} placeholder="Proforma Formation Anglais Q2 2026" /></div>
                        <div style={{ marginBottom:10 }}><label style={labelSt}>Montant (FCFA)</label><input type="number" value={b2bDocumentForm.montant||""} onChange={e=>setB2bDocumentForm(f=>({...f,montant:e.target.value}))} style={inputSt} /></div>
                        <div style={{ marginBottom:10 }}><label style={labelSt}>URL du fichier</label><input value={b2bDocumentForm.fichier_url||""} onChange={e=>setB2bDocumentForm(f=>({...f,fichier_url:e.target.value}))} style={inputSt} placeholder="https://…" /></div>
                        <div style={{ marginBottom:12 }}><label style={labelSt}>Notes</label><textarea value={b2bDocumentForm.notes||""} onChange={e=>setB2bDocumentForm(f=>({...f,notes:e.target.value}))} style={{ ...inputSt, minHeight:55, resize:"vertical" }} /></div>
                        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                          <button onClick={()=>setB2bDocumentForm(null)} style={btnSecondary}>Annuler</button>
                          <button disabled={b2bSavingId==="document" || !peut("contrat","create")} title={permBtnTitle("Enregistrer",peut("contrat","create"))} onClick={()=>saveB2BDocument(b2bDocumentForm)} style={permBtnStyle(btnPrimary,peut("contrat","create"))}>{b2bSavingId==="document"?"⏳…":!peut("contrat","create")?"🔒":"✅ Enregistrer"}</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── FACTURATION B2B ── */}
            {activeTab === "facturation_b2b" && (() => {
              const EMPTY_FACT = { entreprise_id:"", objet:"", montant_ht:"", taux_tva:18, date_echeance:"", notes:"" };
              const totalPaye     = b2bFactures.filter(f=>f.statut==="payée").reduce((s,f)=>s+(Number(f.montant_ttc)||0),0);
              const totalAttente  = b2bFactures.filter(f=>["envoyée","brouillon"].includes(f.statut)).reduce((s,f)=>s+(Number(f.montant_ttc)||0),0);
              const totalRetard   = b2bFactures.filter(f=>f.statut==="en_retard").reduce((s,f)=>s+(Number(f.montant_ttc)||0),0);
              const filtreStatutF = b2bFactureForm?.__filtreStatut || "tous";
              const filteredFact  = filtreStatutF === "tous" ? b2bFactures : b2bFactures.filter(f=>f.statut===filtreStatutF);

              return (
                <div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                    <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#0f172a" }}>🧾 Facturation B2B</h3>
                    <button disabled={!peut("contrat","create")} title={permBtnTitle("Nouvelle facture",peut("contrat","create"))} onClick={()=>setB2bFactureForm({...EMPTY_FACT})} style={permBtnStyle(btnPrimary,peut("contrat","create"))}>{peut("contrat","create")?"➕":"🔒"} Nouvelle facture</button>
                  </div>

                  {/* KPIs */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20 }}>
                    <StatCard label="CA encaissé (B2B)" value={fmtB2B(totalPaye)} color="#22c55e" icon="✅" sub="factures payées" />
                    <StatCard label="En attente règlement" value={fmtB2B(totalAttente)} color="#0891b2" icon="📤" sub="envoyées / brouillons" />
                    <StatCard label="En retard" value={fmtB2B(totalRetard)} color="#ef4444" icon="🔴" sub="à relancer" onClick={()=>setB2bFactureForm(f=>({...(f||{}),__filtreStatut:"en_retard"}))} />
                  </div>

                  {/* Filtres */}
                  <div style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap" }}>
                    {["tous",...Object.keys(B2B_FACT_STATUTS)].map(s=>(
                      <button key={s} onClick={()=>setB2bFactureForm(f=>({...(f||{}),__filtreStatut:s}))}
                        style={{ padding:"5px 10px", borderRadius:999, border:`1.5px solid ${filtreStatutF===s?(B2B_FACT_COLORS[s]||BET_COLOR):"#e5e7eb"}`, background:filtreStatutF===s?`${B2B_FACT_COLORS[s]||BET_COLOR}12`:"#fff", color:filtreStatutF===s?(B2B_FACT_COLORS[s]||BET_COLOR):"#374151", fontWeight:700, fontSize:11, cursor:"pointer" }}>
                        {s==="tous"?"Toutes":B2B_FACT_STATUTS[s]}
                      </button>
                    ))}
                  </div>

                  {b2bLoading ? <div style={{ textAlign:"center", padding:30, color:"#94a3b8" }}>⏳ Chargement…</div> : filteredFact.length === 0 ? (
                    <div style={{ textAlign:"center", padding:60, background:"#f8fafc", borderRadius:16, border:"1.5px dashed #e2e8f0" }}>
                      <div style={{ fontSize:"2.5rem", marginBottom:10 }}>🧾</div>
                      <div style={{ fontWeight:700, color:"#0f172a" }}>Aucune facture</div>
                    </div>
                  ) : (
                    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse" }}>
                        <thead><tr style={{ background:"#f8fafc", borderBottom:"1px solid #e5e7eb" }}>
                          {["Numéro","Entreprise","Objet","Montant TTC","Statut","Échéance","Actions"].map(h=><th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"#374151" }}>{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {filteredFact.map(f => {
                            const c = B2B_FACT_COLORS[f.statut] || "#94a3b8";
                            return (
                              <tr key={f.id} style={{ borderBottom:"1px solid #f1f5f9" }}>
                                <td style={{ padding:"10px 12px", fontSize:12, fontWeight:700, color:"#0f172a" }}>{f.numero}</td>
                                <td style={{ padding:"10px 12px", fontSize:12, color:"#6b7280" }}>🏢 {f.entreprise_nom}</td>
                                <td style={{ padding:"10px 12px", fontSize:12, color:"#374151" }}>{f.objet || "-"}</td>
                                <td style={{ padding:"10px 12px", fontSize:12, fontWeight:800, color:"#0f172a" }}>{fmtB2B(f.montant_ttc)}</td>
                                <td style={{ padding:"10px 12px" }}>
                                  <span style={{ padding:"2px 8px", borderRadius:999, fontSize:10, fontWeight:700, background:`${c}15`, color:c }}>{B2B_FACT_STATUTS[f.statut]}</span>
                                </td>
                                <td style={{ padding:"10px 12px", fontSize:11, color: f.statut==="en_retard"?"#dc2626":"#6b7280", fontWeight: f.statut==="en_retard"?700:400 }}>{fmtDateB2B(f.date_echeance)}</td>
                                <td style={{ padding:"10px 12px", display:"flex", gap:5 }}>
                                  {f.statut==="brouillon"  && <button disabled={!peut("contrat","update")} title={permBtnTitle("Envoyer",peut("contrat","update"))} onClick={()=>{ patchB2BStatut("factures",f.id,{statut:"envoyée"}); toast.success("Facture envoyée"); }} style={permBtnStyle({ padding:"3px 8px", background:"#e0f2fe", color:BET_COLOR, border:"none", borderRadius:4, cursor:"pointer", fontSize:11, fontWeight:600 },peut("contrat","update"))}>{peut("contrat","update")?"📤":"🔒"}</button>}
                                  {f.statut==="envoyée"    && <button disabled={!peut("contrat","update")} title={permBtnTitle("Marquer payée",peut("contrat","update"))} onClick={()=>{ patchB2BStatut("factures",f.id,{statut:"payée",date_paiement:new Date().toISOString().slice(0,10)}); toast.success("✅ Paiement enregistré"); }} style={permBtnStyle({ padding:"3px 8px", background:"#dcfce7", color:"#166534", border:"none", borderRadius:4, cursor:"pointer", fontSize:11, fontWeight:600 },peut("contrat","update"))}>{peut("contrat","update")?"✅":"🔒"} Payée</button>}
                                  {f.statut==="envoyée"    && <button disabled={!peut("contrat","update")} title={permBtnTitle("Marquer en retard",peut("contrat","update"))} onClick={()=>{ patchB2BStatut("factures",f.id,{statut:"en_retard"}); toast.success("Marqué en retard"); }} style={permBtnStyle({ padding:"3px 8px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:4, cursor:"pointer", fontSize:11, fontWeight:600 },peut("contrat","update"))}>{peut("contrat","update")?"🔴":"🔒"}</button>}
                                  {f.statut==="en_retard"  && <button disabled={!peut("contrat","update")} title={permBtnTitle("Encaisser",peut("contrat","update"))} onClick={()=>{ patchB2BStatut("factures",f.id,{statut:"payée",date_paiement:new Date().toISOString().slice(0,10)}); toast.success("✅ Encaissé"); }} style={permBtnStyle({ padding:"3px 8px", background:"#dcfce7", color:"#166534", border:"none", borderRadius:4, cursor:"pointer", fontSize:11, fontWeight:600 },peut("contrat","update"))}>{peut("contrat","update")?"✅":"🔒"} Encaisser</button>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Modal facture */}
                  {b2bFactureForm && b2bFactureForm.entreprise_id !== undefined && !b2bFactureForm.__filtreStatut && (
                    <div style={modalOverlay} onClick={e=>{if(e.target===e.currentTarget)setB2bFactureForm(null);}}>
                      <div style={{ ...modalBox, width:460, maxHeight:"90vh", overflowY:"auto" }}>
                        <h3 style={{ margin:"0 0 16px", fontSize:15, fontWeight:800, color:"#0f172a" }}>🧾 Nouvelle facture B2B</h3>
                        <div style={{ marginBottom:10 }}>
                          <label style={labelSt}>Entreprise *</label>
                          <select value={b2bFactureForm.entreprise_id||""} onChange={e=>setB2bFactureForm(f=>({...f,entreprise_id:e.target.value}))} style={inputSt}>
                            <option value="">- Choisir -</option>
                            {b2bEntreprises.map(e=><option key={e.id} value={e.id}>{e.nom}</option>)}
                          </select>
                        </div>
                        <div style={{ marginBottom:10 }}><label style={labelSt}>Objet</label><input value={b2bFactureForm.objet||""} onChange={e=>setB2bFactureForm(f=>({...f,objet:e.target.value}))} style={inputSt} placeholder="Formation Anglais professionnel - 15 employés" /></div>
                        <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:10, marginBottom:10 }}>
                          <div><label style={labelSt}>Montant HT (FCFA) *</label><input type="number" value={b2bFactureForm.montant_ht||""} onChange={e=>setB2bFactureForm(f=>({...f,montant_ht:e.target.value}))} style={inputSt} /></div>
                          <div><label style={labelSt}>TVA (%)</label><input type="number" value={b2bFactureForm.taux_tva||18} onChange={e=>setB2bFactureForm(f=>({...f,taux_tva:e.target.value}))} style={inputSt} /></div>
                        </div>
                        {b2bFactureForm.montant_ht && (
                          <div style={{ marginBottom:12, padding:"8px 12px", borderRadius:7, background:"#f0fdf4", border:"1px solid #bbf7d0", fontSize:12, fontWeight:700, color:"#166534" }}>
                            TTC estimé : {fmtB2B(parseFloat(b2bFactureForm.montant_ht||0) * (1 + parseFloat(b2bFactureForm.taux_tva||18)/100))}
                          </div>
                        )}
                        <div style={{ marginBottom:10 }}><label style={labelSt}>Date d'échéance</label><input type="date" value={b2bFactureForm.date_echeance||""} onChange={e=>setB2bFactureForm(f=>({...f,date_echeance:e.target.value}))} style={inputSt} /></div>
                        <div style={{ marginBottom:12 }}><label style={labelSt}>Notes</label><textarea value={b2bFactureForm.notes||""} onChange={e=>setB2bFactureForm(f=>({...f,notes:e.target.value}))} style={{ ...inputSt, minHeight:55, resize:"vertical" }} /></div>
                        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                          <button onClick={()=>setB2bFactureForm(null)} style={btnSecondary}>Annuler</button>
                          <button disabled={b2bSavingId==="facture" || !peut("contrat","create")} title={permBtnTitle("Créer la facture",peut("contrat","create"))} onClick={()=>saveB2BFacture(b2bFactureForm)} style={permBtnStyle(btnPrimary,peut("contrat","create"))}>{b2bSavingId==="facture"?"⏳…":!peut("contrat","create")?"🔒":"✅ Créer la facture"}</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ═══ ONGLET DEMANDES ENTREPRISE (B2B uniquement) ═══ */}
            {activeTab === "assignations" && assistanteProfil === "b2b" && (() => {
              const B2B_STATUT = {
                en_attente: { label:"Nouvelle demande",  color:"#d97706", bg:"#fef3c7",  icon:"🆕" },
                en_cours:   { label:"En contact",        color:"#0891b2", bg:"#e0f2fe",  icon:"📞" },
                converti:   { label:"Contrat signé",     color:"#16a34a", bg:"#dcfce7",  icon:"✅" },
                perdu:      { label:"Perdu",             color:"#dc2626", bg:"#fee2e2",  icon:"❌" },
              };
              const TYPE_DEMANDE = {
                devis:                { label:"Demande de devis",    color:"#7c3aed", bg:"#ede9fe", icon:"📋" },
                simulateur:           { label:"Simulateur détaillé", color:"#0891b2", bg:"#e0f2fe", icon:"🔬" },
                web:                  { label:"Formulaire web",      color:"#64748b", bg:"#f1f5f9", icon:"🌐" },
                /* Les deux formulaires de /bet-for-business. Ils se distinguent à l'œil parce
                   qu'ils n'appellent pas la même réponse : l'un attend une proposition
                   commerciale, l'autre les conditions de l'offre découverte. */
                corporate_formation:  { label:"BET for Business",    color:"#b91c1c", bg:"#fee2e2", icon:"🏢" },
                corporate_decouverte: { label:"Test pédagogie",      color:"#b45309", bg:"#fef3c7", icon:"🎓" },
              };

              /* LE CYCLE RÉEL, POUR LE MODAL. La liste affiche le statut projeté sur quatre
                 valeurs (voir `PROJECTION_CYCLE` côté serveur) ; l'étape exacte ne se perd
                 pas pour autant — elle s'affiche à l'ouverture de la demande. */
              const ETAPE_CYCLE = {
                nouveau:     { label:"Nouveau",     color:"#64748b" },
                contacte:    { label:"Contacté",    color:"#0891b2" },
                qualifie:    { label:"Qualifié",    color:"#7c3aed" },
                rendez_vous: { label:"Rendez-vous", color:"#f59e0b" },
                devis:       { label:"Devis envoyé",color:"#0c33ac" },
                converti:    { label:"Gagné",       color:"#16a34a" },
                perdu:       { label:"Perdu",       color:"#dc2626" },
              };
              const estCorpo = (d) => d?.type_demande === "corporate_formation" || d?.type_demande === "corporate_decouverte";
              /* `cle` vient du serveur. Le repli sur l'email couvre une réponse servie par un
                 backend pas encore redémarré : sans lui, l'écran n'ouvrirait plus aucune
                 fiche le temps du déploiement. */
              const cleDe = (d) => d?.cle || `mail:${d?.email}`;
              // CECRL_COLORS défini au niveau module
              const FORMATS = [
                { id:"presentiel", label:"Présentiel",      icon:"🏢", desc:"Dans nos locaux à Abidjan" },
                { id:"distanciel", label:"À distance",      icon:"🌐", desc:"Sessions Zoom / Teams" },
                { id:"hybride",    label:"Hybride",         icon:"⚡", desc:"Mix présentiel & en ligne" },
                { id:"elearning",  label:"E-learning seul", icon:"💡", desc:"100% autonome, à votre rythme" },
              ];
              const RYTHMES = [
                { id:"intensif", label:"Intensif",      sessions:5, desc:"5 sessions/semaine" },
                { id:"regulier", label:"Régulier",      sessions:3, desc:"3 sessions/semaine" },
                { id:"leger",    label:"Modéré",        sessions:2, desc:"2 sessions/semaine" },
                { id:"weekend",  label:"Week-end only", sessions:2, desc:"Sam + Dim uniquement" },
              ];
              const GROUPES = [
                { id:"individuel", label:"Individuel",    max:1,  desc:"Cours 100% personnalisé" },
                { id:"duo",        label:"Duo",           max:2,  desc:"2 participants" },
                { id:"petit",      label:"Petit groupe",  max:6,  desc:"3 à 6 participants" },
                { id:"groupe",     label:"Groupe",        max:15, desc:"7 à 15 participants" },
                { id:"classe",     label:"Grande classe", max:25, desc:"16 à 25 participants" },
              ];
              const DEFAULT_OBJECTIFS = [
                { id:"communication", label:"Communication quotidienne", icon:"💬" },
                { id:"business",      label:"Anglais des affaires",      icon:"💼" },
                { id:"toeic",         label:"Certification TOEIC",       icon:"🏆" },
                { id:"ielts",         label:"Certification IELTS",       icon:"📜" },
                { id:"presentations", label:"Présentations & réunions",  icon:"🎤" },
                { id:"redaction",     label:"Rédaction professionnelle", icon:"✍️" },
                { id:"negociation",   label:"Négociation commerciale",   icon:"🤝" },
                { id:"technique",     label:"Anglais technique / IT",    icon:"💻" },
              ];
              const getObjLabel = (id) => {
                if (typeof id === "string" && id.startsWith("autre:")) return { icon:"✏️", label: id.slice(6) };
                const found = DEFAULT_OBJECTIFS.find(o => o.id === id);
                return found || { icon:"🎯", label: id };
              };
              const getFormat = (id) => FORMATS.find(f => f.id === id) || { id, label: id, icon:"📋", desc:"" };
              const getRythme = (id) => RYTHMES.find(r => r.id === id) || { id, label: id, sessions:"?", desc: id };
              const getGroupe = (id) => GROUPES.find(g => g.id === id) || { id, label: id, max:"?", desc: id };

              const filtered = b2bDemandes.filter(d => {
                if (b2bFiltreStatut !== "tous" && d.statut !== b2bFiltreStatut) return false;
                if (b2bFiltreType   !== "tous" && d.type_demande !== b2bFiltreType)  return false;
                return true;
              });

              const counts = {
                total:      b2bDemandes.length,
                nouvelles:  b2bDemandes.filter(d => d.statut === "en_attente").length,
                en_cours:   b2bDemandes.filter(d => d.statut === "en_cours").length,
                signees:    b2bDemandes.filter(d => d.statut === "converti").length,
                perdues:    b2bDemandes.filter(d => d.statut === "perdu").length,
              };

              // ── Modal demande sélectionnée ──────────────────────
              /* INDEXÉ PAR CLÉ, PLUS PAR EMAIL. Un même contact peut avoir demandé un devis
                 en mars et une formation Corporate en octobre : deux demandes distinctes que
                 l'email confondait — ouvrir l'une affichait l'autre. */
              const sel = b2bDetailId ? b2bDemandes.find(d => cleDe(d) === b2bDetailId) : null;

              return (
                <div>
                  {/* En-tête */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:12 }}>
                    <div>
                      <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#0f172a" }}>🏢 Demandes entreprises - Formulaires web</h3>
                      <p style={{ margin:"4px 0 0", fontSize:12, color:"#6b7280" }}>Cliquez sur une ligne pour voir tous les détails</p>
                    </div>
                    <button onClick={() => { fetchB2bDemandes(); toast.success("Actualisé"); }} style={{ ...btnSecondary, fontSize:11, padding:"6px 12px" }}>🔄 Actualiser</button>
                  </div>

                  {/* Stat cards */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:20 }}>
                    {[
                      { label:"Total",      value:counts.total,     color:"#0891b2", icon:"📊" },
                      { label:"Nouvelles",  value:counts.nouvelles,  color:"#d97706", icon:"🆕" },
                      { label:"En contact", value:counts.en_cours,   color:"#7c3aed", icon:"📞" },
                      { label:"Signées",    value:counts.signees,    color:"#16a34a", icon:"✅" },
                      { label:"Perdues",    value:counts.perdues,    color:"#dc2626", icon:"❌" },
                    ].map((s,i) => (
                      <div key={i} style={{ background:"#fff", borderRadius:10, padding:"14px 12px", border:"1px solid #e2e8f0", textAlign:"center", boxShadow:"0 1px 3px rgba(0,0,0,.05)" }}>
                        <div style={{ fontSize:18 }}>{s.icon}</div>
                        <div style={{ fontSize:22, fontWeight:800, color:s.color, marginTop:2 }}>{s.value}</div>
                        <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Filtres */}
                  <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap" }}>
                    <select value={b2bFiltreStatut} onChange={e => setB2bFiltreStatut(e.target.value)} style={selectSt}>
                      <option value="tous">Tous les statuts</option>
                      <option value="en_attente">Nouvelles demandes</option>
                      <option value="en_cours">En contact</option>
                      <option value="converti">Contrat signé</option>
                      <option value="perdu">Perdu</option>
                    </select>
                    <select value={b2bFiltreType} onChange={e => setB2bFiltreType(e.target.value)} style={selectSt}>
                      <option value="tous">Tous les types</option>
                      <option value="corporate_formation">🏢 BET for Business</option>
                      <option value="corporate_decouverte">🎓 Test pédagogie</option>
                      <option value="devis">Demande de devis</option>
                      <option value="simulateur">Simulateur détaillé</option>
                    </select>
                  </div>

                  {b2bDemandesLoading && <div style={{ textAlign:"center", padding:40, color:"#0891b2", fontSize:13 }}>⏳ Chargement des demandes…</div>}

                  {!b2bDemandesLoading && filtered.length === 0 && (
                    <div style={{ textAlign:"center", padding:48, color:"#94a3b8", fontSize:13 }}>
                      <div style={{ fontSize:36, marginBottom:10 }}>📭</div>
                      <div style={{ fontWeight:700 }}>Aucune demande entreprise reçue</div>
                      <div style={{ fontSize:12, marginTop:4 }}>Elles apparaîtront ici dès qu'une entreprise soumettra un formulaire sur le site web.</div>
                    </div>
                  )}

                  {/* ── Tableau cliquable ── */}
                  {!b2bDemandesLoading && filtered.length > 0 && (
                    <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,.04)" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                        <thead>
                          <tr style={{ background:"#f8fafc", borderBottom:"2px solid #e2e8f0" }}>
                            {["Entreprise", "Contact", "Statut", "Type", "Niveaux", "Format", "Date"].map(h => (
                              <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:".04em", whiteSpace:"nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((d, idx) => {
                            const sm   = B2B_STATUT[d.statut]        || { label:d.statut,       color:"#64748b", bg:"#f1f5f9", icon:"•" };
                            const tm   = TYPE_DEMANDE[d.type_demande] || TYPE_DEMANDE.web;
                            const nAct = d.niveau_actuel ? CECRL_COLORS[d.niveau_actuel] : null;
                            const nCib = d.niveau_cible  ? CECRL_COLORS[d.niveau_cible]  : null;
                            const fmt  = d.format_souhaite ? getFormat(d.format_souhaite) : null;
                            return (
                              <tr
                                key={d.email || idx}
                                onClick={() => setB2bDetailId(cleDe(d))}
                                style={{ borderBottom:"1px solid #f1f5f9", cursor:"pointer", transition:"background .12s", background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                                onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"}
                                onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}
                              >
                                <td style={{ padding:"12px 14px" }}>
                                  <div style={{ fontWeight:700, color:"#0f172a" }}>{d.entreprise || "-"}</div>
                                  {d.secteur && <div style={{ fontSize:11, color:"#94a3b8", marginTop:1 }}>{d.secteur}</div>}
                                </td>
                                <td style={{ padding:"12px 14px" }}>
                                  <div style={{ color:"#374151" }}>{d.contact || d.email}</div>
                                  {d.nb_employes && <div style={{ fontSize:11, color:"#94a3b8" }}>👥 {d.nb_employes} employés</div>}
                                </td>
                                <td style={{ padding:"12px 14px" }}>
                                  <span style={{ fontSize:11, fontWeight:700, background:sm.bg, color:sm.color, borderRadius:999, padding:"3px 10px", whiteSpace:"nowrap" }}>
                                    {sm.icon} {sm.label}
                                  </span>
                                </td>
                                <td style={{ padding:"12px 14px" }}>
                                  <span style={{ fontSize:10, fontWeight:700, background:tm.bg, color:tm.color, borderRadius:6, padding:"2px 8px", whiteSpace:"nowrap" }}>
                                    {tm.icon} {tm.label}
                                  </span>
                                </td>
                                <td style={{ padding:"12px 14px" }}>
                                  {(d.niveau_actuel || d.niveau_cible) ? (
                                    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                                      {d.niveau_actuel && <span style={{ fontSize:11, fontWeight:800, padding:"2px 7px", borderRadius:5, background: nAct?.bg || "#f3f4f6", color: nAct?.color || "#6b7280" }}>{d.niveau_actuel}</span>}
                                      {d.niveau_actuel && d.niveau_cible && <span style={{ color:"#94a3b8", fontSize:12 }}>→</span>}
                                      {d.niveau_cible  && <span style={{ fontSize:11, fontWeight:800, padding:"2px 7px", borderRadius:5, background: nCib?.bg || "#f3f4f6", color: nCib?.color || "#6b7280" }}>{d.niveau_cible}</span>}
                                    </div>
                                  ) : <span style={{ color:"#d1d5db" }}>-</span>}
                                </td>
                                <td style={{ padding:"12px 14px" }}>
                                  {fmt ? <span style={{ fontSize:12 }}>{fmt.icon} {fmt.label}</span> : <span style={{ color:"#d1d5db" }}>-</span>}
                                </td>
                                <td style={{ padding:"12px 14px", whiteSpace:"nowrap", color:"#64748b", fontSize:12 }}>
                                  {new Date(d.date_reception).toLocaleDateString("fr-FR", { day:"numeric", month:"short", year:"numeric" })}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* ── Modal détail ── */}
                  {sel && (() => {
                    const sm      = B2B_STATUT[sel.statut]        || { label:sel.statut,    color:"#64748b", bg:"#f1f5f9", icon:"•" };
                    const tm      = TYPE_DEMANDE[sel.type_demande] || TYPE_DEMANDE.web;
                    const nAct    = sel.niveau_actuel ? CECRL_COLORS[sel.niveau_actuel] : null;
                    const nCib    = sel.niveau_cible  ? CECRL_COLORS[sel.niveau_cible]  : null;
                    const fmt     = sel.format_souhaite ? getFormat(sel.format_souhaite) : null;
                    const ryt     = sel.rythme_souhaite ? getRythme(sel.rythme_souhaite) : null;
                    const grp     = sel.groupe           ? getGroupe(sel.groupe)          : null;
                    const noteVal = b2bNoteEdit[sel.email] ?? sel.notes_commercial ?? "";
                    const isSaving = b2bNoteSaving === sel.email;

                    return (
                      <ModaleAnimee ouvert
                        onClicVoile={e => { if (e.target === e.currentTarget) setB2bDetailId(null); }}
                        style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:3000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
                        panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:700, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 64px rgba(0,0,0,0.2)" }}>

                          {/* Modal header */}
                          <div style={{ padding:"20px 24px 16px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between", alignItems:"flex-start", position:"sticky", top:0, background:"#fff", zIndex:1 }}>
                            <div>
                              <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                                <span style={{ fontSize:18, fontWeight:900, color:"#0f172a" }}>🏢 {sel.entreprise || "Entreprise non précisée"}</span>
                                <span style={{ fontSize:11, fontWeight:700, background:sm.bg, color:sm.color, borderRadius:999, padding:"3px 12px" }}>{sm.icon} {sm.label}</span>
                                <span style={{ fontSize:10, fontWeight:700, background:tm.bg, color:tm.color, borderRadius:6, padding:"2px 8px" }}>{tm.icon} {tm.label}</span>
                                {/* L'ÉTAPE EXACTE, RENDUE ICI. La liste a projeté les sept
                                    temps du cycle sur quatre pour rester triable ; c'est à
                                    l'ouverture de la demande que la granularité redevient
                                    utile — « rendez-vous » et « devis envoyé » n'appellent
                                    pas le même geste, et la liste les affiche tous deux
                                    « en contact ». */}
                                {estCorpo(sel) && ETAPE_CYCLE[sel.cycle_statut] && (
                                  <span style={{ fontSize:10, fontWeight:800, border:`1.5px solid ${ETAPE_CYCLE[sel.cycle_statut].color}`,
                                    color:ETAPE_CYCLE[sel.cycle_statut].color, borderRadius:6, padding:"2px 8px", background:"#fff" }}>
                                    {ETAPE_CYCLE[sel.cycle_statut].label}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize:12, color:"#6b7280", marginTop:4 }}>
                                Reçue le {new Date(sel.date_reception).toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" })}
                              </div>
                            </div>
                            <button onClick={() => setB2bDetailId(null)} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#94a3b8", lineHeight:1, padding:4 }}>✕</button>
                          </div>

                          <div style={{ padding:"20px 24px" }}>
                            {/* Contacts */}
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>
                              <div>
                                <div style={{ fontSize:10, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", marginBottom:4 }}>Contact</div>
                                <div style={{ fontWeight:700, color:"#0f172a" }}>👤 {sel.contact || sel.email}</div>
                                {sel.secteur    && <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>🏭 {sel.secteur}</div>}
                                {sel.nb_employes && <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>👥 {sel.nb_employes} employés</div>}
                              </div>
                              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                                {sel.email && <a href={`mailto:${sel.email}`} style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"6px 12px", background:"#eff6ff", color:"#1d4ed8", borderRadius:8, fontSize:12, fontWeight:600, textDecoration:"none", border:"1px solid #bfdbfe" }}>✉️ {sel.email}</a>}
                                {sel.telephone && <a href={`tel:${sel.telephone}`} style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"6px 12px", background:"#f0fdf4", color:"#15803d", borderRadius:8, fontSize:12, fontWeight:600, textDecoration:"none", border:"1px solid #bbf7d0" }}>📞 {sel.telephone}</a>}
                              </div>
                            </div>

                            {/* ══ LE DOSSIER CORPORATE ══
                                Tout ce que le formulaire de /bet-for-business a recueilli et
                                que le reste de ce modal, pensé pour le simulateur, ne savait
                                pas afficher. C'est la matière de l'étude : sans elle,
                                l'assistante rappelait pour redemander ce que l'entreprise
                                avait déjà écrit.
                                Les champs vides disparaissent — une fiche criblée de tirets
                                se lit moins bien qu'une fiche courte. */}
                            {estCorpo(sel) && (() => {
                              const lignes = [
                                ["Fonction du contact",  sel.contact_fonction],
                                ["Collaborateurs à former", sel.nb_employes != null ? `${sel.nb_employes}` : null],
                                ["Échéance souhaitée",   sel.echeance_souhaitee],
                                ["Format préféré",       sel.format_souhaite ? (getFormat(sel.format_souhaite)?.label || sel.format_souhaite) : null],
                                ["Collaborateur proposé", sel.collaborateur_propose],
                                ["Disponibilités",       sel.disponibilites_contact],
                                ["Montant du devis",     sel.devis_montant != null ? `${Number(sel.devis_montant).toLocaleString("fr-FR")} FCFA` : null],
                              ].filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== "");

                              return (
                                <div style={{ marginBottom:20, border:"1.5px solid #fecaca", borderRadius:12, overflow:"hidden" }}>
                                  <div style={{ padding:"9px 14px", background:"#fef2f2", borderBottom:"1px solid #fecaca",
                                    fontSize:11, fontWeight:800, color:"#b91c1c", textTransform:"uppercase", letterSpacing:".05em" }}>
                                    {sel.type_demande === "corporate_decouverte"
                                      ? "🎓 Demande de test pédagogique"
                                      : "🏢 Demande de formation"}
                                  </div>

                                  {sel.besoins && (
                                    <div style={{ padding:"12px 14px", borderBottom: lignes.length ? "1px solid #f1f5f9" : "none" }}>
                                      <div style={{ fontSize:10, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", marginBottom:4 }}>
                                        {sel.type_demande === "corporate_decouverte" ? "Objectif de l’essai" : "Objectif principal"}
                                      </div>
                                      <div style={{ fontSize:13, color:"#0f172a", lineHeight:1.6 }}>{sel.besoins}</div>
                                    </div>
                                  )}

                                  {lignes.map(([k, v], i) => (
                                    <div key={k} style={{ display:"flex", gap:12, padding:"8px 14px",
                                      borderBottom: i < lignes.length - 1 ? "1px solid #f8fafc" : "none", alignItems:"baseline" }}>
                                      <div style={{ flex:"0 0 42%", fontSize:11, color:"#94a3b8", fontWeight:600 }}>{k}</div>
                                      <div style={{ flex:1, fontSize:13, color:"#0f172a", fontWeight:600, wordBreak:"break-word" }}>{v}</div>
                                    </div>
                                  ))}

                                  {/* LA RELANCE EST LA SEULE INFORMATION QUI PÉRIME. On la
                                      sort du tableau et on la date, parce qu'une relance due
                                      hier et une relance prévue dans trois semaines ne se
                                      regardent pas de la même façon. */}
                                  {sel.relance_at && (() => {
                                    const due = new Date(sel.relance_at) <= new Date();
                                    return (
                                      <div style={{ padding:"10px 14px", background: due ? "#fffbeb" : "#f8fafc", borderTop:"1px solid #f1f5f9" }}>
                                        <div style={{ fontSize:12, fontWeight:700, color: due ? "#b45309" : "#475569" }}>
                                          {due ? "⏰ Relance due le " : "📅 Relance prévue le "}
                                          {new Date(sel.relance_at).toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" })}
                                        </div>
                                        {sel.relance_note && <div style={{ fontSize:12, color:"#64748b", marginTop:3 }}>{sel.relance_note}</div>}
                                      </div>
                                    );
                                  })()}

                                  {/* Le consentement n'est demandé que par le formulaire
                                      découverte, où l'on recontacte au sujet d'une personne
                                      nommée. `false` n'est pas `null` : un refus explicite
                                      doit se voir, pas disparaître comme une absence. */}
                                  {sel.consentement_contact !== null && sel.consentement_contact !== undefined && (
                                    <div style={{ padding:"9px 14px", borderTop:"1px solid #f1f5f9",
                                      fontSize:12, fontWeight:600, color: sel.consentement_contact ? "#15803d" : "#b91c1c" }}>
                                      {sel.consentement_contact
                                        ? "✓ A accepté d’être recontacté"
                                        : "✕ N’a PAS accepté d’être recontacté"}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Niveaux CECRL */}
                            {(sel.niveau_actuel || sel.niveau_cible) && (
                              <div style={{ marginBottom:20 }}>
                                <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".05em", marginBottom:12 }}>🎯 Niveaux CECRL</div>
                                <div style={{ display:"flex", alignItems:"flex-end", gap:14 }}>
                                  {sel.niveau_actuel && (
                                    <div style={{ textAlign:"center" }}>
                                      <div style={{ fontSize:10, color:"#9ca3af", marginBottom:6 }}>Niveau actuel</div>
                                      <div style={{ fontSize:22, fontWeight:900, padding:"8px 24px", borderRadius:10, background: nAct?.bg || "#f3f4f6", color: nAct?.color || "#6b7280", border:`2px solid ${nAct?.color || "#d1d5db"}`, display:"inline-block" }}>{sel.niveau_actuel}</div>
                                    </div>
                                  )}
                                  {sel.niveau_actuel && sel.niveau_cible && (
                                    <div style={{ fontSize:22, color:"#94a3b8", paddingBottom:10, lineHeight:1 }}>→</div>
                                  )}
                                  {sel.niveau_cible && (
                                    <div style={{ textAlign:"center" }}>
                                      <div style={{ fontSize:10, color:"#9ca3af", marginBottom:6 }}>Niveau cible</div>
                                      <div style={{ fontSize:22, fontWeight:900, padding:"8px 24px", borderRadius:10, background: nCib?.bg || "#f3f4f6", color: nCib?.color || "#6b7280", border:`2px solid ${nCib?.color || "#d1d5db"}`, display:"inline-block" }}>{sel.niveau_cible}</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Objectifs */}
                            {(sel.besoins || sel.objectifs?.length > 0) && (
                              <div style={{ marginBottom:20 }}>
                                <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".05em", marginBottom:10 }}>🎯 Objectifs de formation</div>
                                {sel.besoins && <div style={{ fontSize:13, color:"#374151", background:"#f8fafc", borderRadius:8, padding:"10px 12px", border:"1px solid #e2e8f0", marginBottom:8 }}>{sel.besoins}</div>}
                                {sel.objectifs?.length > 0 && (
                                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                                    {sel.objectifs.map((o, i) => {
                                      const obj = getObjLabel(o);
                                      return (
                                        <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:12, background:"#ede9fe", color:"#7c3aed", borderRadius:8, padding:"5px 12px", fontWeight:600, border:"1px solid #c4b5fd" }}>
                                          {obj.icon} {obj.label}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Modalités (simulateur) */}
                            {sel.type_demande === "simulateur" && (fmt || ryt || grp || sel.nb_participants || sel.duree_semaines) && (
                              <div style={{ marginBottom:20 }}>
                                <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".05em", marginBottom:10 }}>📋 Modalités de formation</div>
                                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:10 }}>
                                  {fmt && (
                                    <div style={{ background:"#f0f9ff", borderRadius:10, padding:"12px 14px", border:"1px solid #bae6fd" }}>
                                      <div style={{ fontSize:10, fontWeight:700, color:"#0369a1", textTransform:"uppercase", marginBottom:4 }}>Format des cours</div>
                                      <div style={{ fontSize:14, fontWeight:800, color:"#0c4a6e" }}>{fmt.icon} {fmt.label}</div>
                                      <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>{fmt.desc}</div>
                                      {(sel.format_souhaite === "presentiel" || sel.format_souhaite === "hybride") && sel.centre_nom && (
                                        <div style={{ marginTop:8, padding:"8px 10px", background:"#e0f2fe", borderRadius:6, borderLeft:"3px solid #0891b2" }}>
                                          <div style={{ fontSize:11, fontWeight:700, color:"#0369a1" }}>📍 {sel.centre_nom}</div>
                                          {sel.centre_adresse && <div style={{ fontSize:10, color:"#0369a1", marginTop:2 }}>{sel.centre_adresse}</div>}
                                          {sel.centre_horaires && <div style={{ fontSize:10, color:"#64748b", marginTop:2 }}>🕐 {sel.centre_horaires}</div>}
                                        </div>
                                      )}
                                      {(sel.format_souhaite === "presentiel" || sel.format_souhaite === "hybride") && !sel.centre_nom && sel.centre_key && (
                                        <div style={{ marginTop:6, fontSize:11, color:"#64748b" }}>📍 Centre : {sel.centre_key}</div>
                                      )}
                                    </div>
                                  )}
                                  {ryt && (
                                    <div style={{ background:"#faf5ff", borderRadius:10, padding:"12px 14px", border:"1px solid #e9d5ff" }}>
                                      <div style={{ fontSize:10, fontWeight:700, color:"#7c3aed", textTransform:"uppercase", marginBottom:4 }}>Rythme</div>
                                      <div style={{ fontSize:14, fontWeight:800, color:"#4c1d95" }}>⚡ {ryt.label}</div>
                                      <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>{ryt.desc}</div>
                                      <div style={{ fontSize:11, fontWeight:700, color:"#7c3aed", marginTop:4 }}>{ryt.sessions} sessions / semaine</div>
                                    </div>
                                  )}
                                  {grp && (
                                    <div style={{ background:"#fff7ed", borderRadius:10, padding:"12px 14px", border:"1px solid #fed7aa" }}>
                                      <div style={{ fontSize:10, fontWeight:700, color:"#c2410c", textTransform:"uppercase", marginBottom:4 }}>Type de groupe</div>
                                      <div style={{ fontSize:14, fontWeight:800, color:"#7c2d12" }}>👥 {grp.label}</div>
                                      <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>{grp.desc}</div>
                                      <div style={{ fontSize:11, fontWeight:700, color:"#ea580c", marginTop:4 }}>Max {grp.max} participant{grp.max > 1 ? "s" : ""}</div>
                                    </div>
                                  )}
                                  {sel.nb_participants && (
                                    <div style={{ background:"#f0fdf4", borderRadius:10, padding:"12px 14px", border:"1px solid #bbf7d0" }}>
                                      <div style={{ fontSize:10, fontWeight:700, color:"#15803d", textTransform:"uppercase", marginBottom:4 }}>Participants</div>
                                      <div style={{ fontSize:26, fontWeight:900, color:"#14532d" }}>{sel.nb_participants}</div>
                                      <div style={{ fontSize:11, color:"#64748b" }}>personnes à former</div>
                                    </div>
                                  )}
                                  {sel.duree_semaines && (
                                    <div style={{ background:"#fff1f2", borderRadius:10, padding:"12px 14px", border:"1px solid #fecdd3" }}>
                                      <div style={{ fontSize:10, fontWeight:700, color:"#be123c", textTransform:"uppercase", marginBottom:4 }}>Durée souhaitée</div>
                                      <div style={{ fontSize:26, fontWeight:900, color:"#881337" }}>{sel.duree_semaines}</div>
                                      <div style={{ fontSize:11, color:"#64748b" }}>semaines</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Avancement du dossier */}
                            <div style={{ marginBottom:20 }}>
                              <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".05em", marginBottom:10 }}>Avancement du dossier</div>
                              {sel.statut === "converti" ? (
                                <div style={{ padding:"10px 14px", borderRadius:8, background:"#dcfce7", border:"1px solid #86efac", color:"#166534", fontSize:12, fontWeight:600, display:"flex", alignItems:"center", gap:6 }}>
                                  🔒 ✅ Contrat signé - dossier transmis à l'admin BET pour validation, le statut ne peut plus être modifié ici.
                                </div>
                              ) : (
                              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                                {[
                                  { key:"en_attente", label:"Nouvelle",     icon:"🆕" },
                                  { key:"en_cours",   label:"En contact",   icon:"📞" },
                                  { key:"converti",   label:"Contrat signé",icon:"✅" },
                                  { key:"perdu",      label:"Perdu",        icon:"❌" },
                                ].map(s => (
                                  <button
                                    key={s.key}
                                    disabled={!peut("prospects_b2b","update")}
                                    title={permBtnTitle(s.label, peut("prospects_b2b","update"))}
                                    onClick={() => { if (!peut("prospects_b2b","update")) return; updateB2bStatut(sel.email, sel.assignation_id, s.key); }}
                                    style={{
                                      padding:"9px 18px", borderRadius:8, fontSize:13, fontWeight:700, cursor: peut("prospects_b2b","update") ? "pointer" : "not-allowed", transition:"all .15s",
                                      border: sel.statut === s.key ? `2px solid ${B2B_STATUT[s.key].color}` : "1px solid #e2e8f0",
                                      background: sel.statut === s.key ? B2B_STATUT[s.key].bg : "#f8fafc",
                                      color: sel.statut === s.key ? B2B_STATUT[s.key].color : "#64748b",
                                      transform: sel.statut === s.key ? "scale(1.04)" : "none",
                                      opacity: peut("prospects_b2b","update") ? 1 : 0.6,
                                    }}
                                  >
                                    {peut("prospects_b2b","update") ? s.icon : "🔒"} {s.label}
                                  </button>
                                ))}
                              </div>
                              )}
                            </div>

                            {/* Notes */}
                            <div>
                              <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".05em", marginBottom:8 }}>Notes / Suivi</div>
                              <textarea
                                value={noteVal}
                                onChange={e => setB2bNoteEdit(prev => ({ ...prev, [sel.email]: e.target.value }))}
                                placeholder="Ajoutez vos notes de suivi, informations sur l'entreprise, prochaines étapes…"
                                rows={4}
                                style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:"1px solid #d1d5db", fontSize:13, color:"#374151", resize:"vertical", fontFamily:"inherit", boxSizing:"border-box" }}
                              />
                              <div style={{ display:"flex", justifyContent:"flex-end", marginTop:8, gap:8 }}>
                                <button onClick={() => setB2bDetailId(null)} style={{ ...btnSecondary, fontSize:13, padding:"8px 20px" }}>Fermer</button>
                                <button
                                  onClick={() => { if (!peut("prospects_b2b","update")) return; saveB2bNote(sel.email, sel.assignation_id); }}
                                  disabled={isSaving || !peut("prospects_b2b","update")}
                                  title={permBtnTitle("Sauvegarder la note", peut("prospects_b2b","update"))}
                                  style={{ padding:"8px 20px", background: (isSaving || !peut("prospects_b2b","update")) ? "#9ca3af" : BET_COLOR, color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:13, cursor: (isSaving || !peut("prospects_b2b","update")) ? "not-allowed" : "pointer" }}
                                >
                                  {isSaving ? "Enregistrement…" : peut("prospects_b2b","update") ? "💾 Sauvegarder la note" : "🔒 Sauvegarder la note"}
                                </button>
                              </div>
                            </div>
                          </div>
                      </ModaleAnimee>
                    );
                  })()}
                </div>
              );
            })()}

            {/* ═══ ONGLET ASSIGNATIONS PARCOURS ═══ */}
            {activeTab === "assignations" && assistanteProfil !== "b2b" && (() => {
              const TYPE_COURS  = { en_ligne:"En ligne", presentiel:"Présentiel" };
              const TYPE_COACH  = { groupe:"Groupe", prive:"Privé" };
              const STATUT_META = {
                en_attente: { label:"En attente",  color:"#d97706", bg:"#fef3c7" },
                en_cours:   { label:"En cours",    color:"#0891b2", bg:"#e0f2fe" },
                converti:   { label:"Converti",    color:"#22c55e", bg:"#dcfce7" },
                perdu:      { label:"Perdu",       color:"#ef4444", bg:"#fee2e2" },
                archivé:    { label:"Archivé",     color:"#6b7280", bg:"#f3f4f6" },
              };

              const filtered = assignations.filter(a => {
                if (a.statut === "converti") return false; // convertis → onglet "Mes apprenants"
                if (a.statut === "archivé")  return false; // archivés → section Archives
                if (a.statut === "annule")   return false; // annulés  → section Dossiers annulés
                if (assignFiltreType   !== "tous" && a.type_cours !== assignFiltreType)   return false;
                if (assignFiltreStatut !== "tous" && a.statut     !== assignFiltreStatut) return false;
                return true;
              });

              const archives = assignations.filter(a => a.statut === "archivé");
              const annules  = assignations.filter(a => a.statut === "annule");

              return (
                <div>

                  {/* ── Section Mes clients (commentée temporairement) ── */}
                  {false && <div style={{ marginBottom:32 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                      <div>
                        <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#0f172a" }}>📬 Mes clients - Prises en charge</h3>
                        <p style={{ margin:"4px 0 0", fontSize:12, color:"#9ca3af" }}>Messages et demandes des clients qui vous ont choisi comme conseillère</p>
                      </div>
                      <button onClick={() => { fetchMessages(); }} style={{ ...btnSecondary, fontSize:11, padding:"6px 12px" }} disabled={messagesLoading}>
                        🔄 Actualiser
                      </button>
                    </div>

                    {/* Stats */}
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 }}>
                      {[
                        { label:"Total messages", val:clientMessages.length,                                        color:"#0891b2" },
                        { label:"Nouveaux",       val:clientMessages.filter(m=>m.statut==="nouveau").length,         color:"#dc2626" },
                        { label:"En cours",       val:clientMessages.filter(m=>m.statut==="en_cours").length,        color:"#f59e0b" },
                        { label:"Traités",        val:clientMessages.filter(m=>m.statut==="traité").length,          color:"#22c55e" },
                      ].map((s,i) => (
                        <div key={i} style={{ background:"#fff", borderRadius:10, padding:"14px 16px", border:"1px solid #f1f5f9", textAlign:"center" }}>
                          <div style={{ fontSize:22, fontWeight:800, color:s.color }}>{s.val}</div>
                          <div style={{ fontSize:11, color:"#6b7280", marginTop:3 }}>{s.label}</div>
                        </div>
                      ))}
                    </div>

                    {messagesLoading && (
                      <div style={{ textAlign:"center", padding:32, color:"#0891b2", fontSize:13 }}>⏳ Chargement des messages…</div>
                    )}

                    {!messagesLoading && clientMessages.length === 0 && (
                      <div style={{ textAlign:"center", padding:"36px 24px", background:"#f8fafc", borderRadius:14, border:"1.5px dashed #e2e8f0" }}>
                        <div style={{ fontSize:"2.5rem", marginBottom:10 }}>📭</div>
                        <div style={{ fontWeight:700, color:"#0f172a", marginBottom:4 }}>Aucun message reçu</div>
                        <div style={{ fontSize:13, color:"#9ca3af" }}>Les messages de vos clients apparaîtront ici dès qu'ils vous contacteront depuis leur espace.</div>
                      </div>
                    )}

                    {!messagesLoading && clientMessages.length > 0 && (
                      <div style={{ display:"flex", gap:20 }}>
                        {/* Liste */}
                        <div style={{ flex:1, display:"flex", flexDirection:"column", gap:10 }}>
                          {clientMessages.map(m => {
                            const statutColor = { nouveau:"#dc2626", lu:"#6b7280", en_cours:"#f59e0b", "traité":"#22c55e" }[m.statut] || "#6b7280";
                            const statutBg    = { nouveau:"#fee2e2", lu:"#f3f4f6", en_cours:"#fef3c7", "traité":"#d1fae5" }[m.statut] || "#f3f4f6";
                            const isSelected  = selectedMessage?.id === m.id;
                            return (
                              <div
                                key={m.id}
                                onClick={() => setSelectedMessage(m)}
                                style={{
                                  padding:"14px 16px", borderRadius:12, cursor:"pointer", transition:"all .15s",
                                  border:`1.5px solid ${isSelected ? BET_COLOR : m.statut==="nouveau" ? "#fca5a5" : "#e5e7eb"}`,
                                  background: isSelected ? BET_LIGHT : m.statut==="nouveau" ? "#fff5f5" : "#fff",
                                }}
                              >
                                <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
                                  <div style={{ width:38, height:38, borderRadius:"50%", background:BET_LIGHT, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:13, color:BET_COLOR, flexShrink:0 }}>
                                    {(m.nom?.[0] || "?").toUpperCase()}
                                  </div>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3 }}>
                                      <span style={{ fontWeight:700, fontSize:13, color:"#0f172a" }}>{m.nom}</span>
                                      <span style={{ fontSize:10, color:"#9ca3af" }}>
                                        {m.created_at ? new Date(m.created_at).toLocaleDateString("fr-FR", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" }) : "-"}
                                      </span>
                                    </div>
                                    <div style={{ fontSize:12, fontWeight:600, color:"#374151", marginBottom:3 }}>{m.sujet || "Sans sujet"}</div>
                                    <div style={{ fontSize:11, color:"#9ca3af", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.message}</div>
                                    <div style={{ marginTop:6 }}>
                                      <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:999, background:statutBg, color:statutColor }}>
                                        {m.statut === "nouveau" ? "🔴 Nouveau" : m.statut === "en_cours" ? "🟡 En cours" : m.statut === "traité" ? "🟢 Traité" : "⚪ Lu"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Détail message */}
                        {selectedMessage && (
                          <div style={{ width:360, flexShrink:0, background:"#fff", borderRadius:14, border:"1.5px solid #e5e7eb", padding:22, alignSelf:"flex-start", position:"sticky", top:20 }}>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
                              <div>
                                <h3 style={{ margin:0, fontSize:15, fontWeight:800, color:"#0f172a" }}>{selectedMessage.nom}</h3>
                                <div style={{ fontSize:12, color:"#9ca3af", marginTop:3 }}>{selectedMessage.email}</div>
                                {selectedMessage.telephone && <div style={{ fontSize:12, color:"#0891b2", marginTop:2 }}>📞 {selectedMessage.telephone}</div>}
                              </div>
                              <button onClick={() => setSelectedMessage(null)} style={{ background:"none", border:"none", fontSize:18, cursor:"pointer", color:"#9ca3af" }}>✕</button>
                            </div>
                            <div style={{ padding:"10px 14px", borderRadius:8, background:"#f8fafc", marginBottom:12 }}>
                              <div style={{ fontSize:11, fontWeight:700, color:"#6b7280", marginBottom:4 }}>SUJET</div>
                              <div style={{ fontSize:14, fontWeight:600, color:"#0f172a" }}>{selectedMessage.sujet || "Sans sujet"}</div>
                            </div>
                            <div style={{ padding:"12px 14px", borderRadius:8, background:"#f8fafc", marginBottom:16, minHeight:80 }}>
                              <div style={{ fontSize:11, fontWeight:700, color:"#6b7280", marginBottom:8 }}>MESSAGE</div>
                              <div style={{ fontSize:13, color:"#374151", lineHeight:1.7, whiteSpace:"pre-wrap" }}>{selectedMessage.message}</div>
                            </div>
                            <div style={{ fontSize:11, color:"#9ca3af", marginBottom:14 }}>
                              Reçu le {selectedMessage.created_at ? new Date(selectedMessage.created_at).toLocaleDateString("fr-FR", { weekday:"long", day:"numeric", month:"long", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "-"}
                            </div>
                            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                              <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:2 }}>Changer le statut :</div>
                              {[
                                { key:"en_cours", label:"🟡 Marquer En cours", bg:"#fef3c7", color:"#92400e" },
                                { key:"traité",   label:"🟢 Marquer Traité",   bg:"#d1fae5", color:"#065f46" },
                              ].map(s => (
                                <button
                                  key={s.key}
                                  disabled={selectedMessage.statut === s.key}
                                  onClick={async () => {
                                    try {
                                      const res = await fetch(`${API_URL}/api/contact/${selectedMessage.id}/statut`, {
                                        method:"PATCH", headers: authHeaders(),
                                        body: JSON.stringify({ statut: s.key }),
                                      });
                                      if (!res.ok) throw new Error();
                                      setClientMessages(prev => prev.map(m => m.id === selectedMessage.id ? { ...m, statut: s.key } : m));
                                      setSelectedMessage(prev => ({ ...prev, statut: s.key }));
                                      toast.success(`Statut mis à jour : ${s.key}`);
                                    } catch { toast.error("Erreur mise à jour"); }
                                  }}
                                  style={{ padding:"9px 14px", borderRadius:8, border:"none", cursor: selectedMessage.statut===s.key ? "not-allowed" : "pointer", background: selectedMessage.statut===s.key ? "#f3f4f6" : s.bg, color: selectedMessage.statut===s.key ? "#9ca3af" : s.color, fontWeight:700, fontSize:12, opacity: selectedMessage.statut===s.key ? 0.5 : 1 }}
                                >
                                  {s.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>}

                  {/* En-tête */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:12 }}>
                    <div>
                      <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#0f172a" }}>🎯 Prospects - Parcours d'inscription</h3>
                      <p style={{ margin:"4px 0 0", fontSize:12, color:"#6b7280" }}>
                        Prospects ayant choisi une assistante via le tunnel /parcours/inscription
                      </p>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={() => { if (isCommercialeCentre && !assistanteCentreId) { toast.error("Centre introuvable pour votre compte - contactez le Super Admin avant d'importer, sinon les classes créées seraient invisibles dans votre liste."); return; } fetchCoachs(); setShowImportGlobalUpload(true); }} style={{ padding:"8px 16px", background:"#0e7490", border:"none", borderRadius:8, fontSize:12, color:"#fff", cursor:"pointer", fontWeight:700 }}>🌐 Import global</button>
                      <button onClick={ouvrirHistoriqueImports} title="Historique des imports - relancer un lot interrompu ou annuler un lot créé par erreur" style={{ position:"relative", padding:"8px 16px", background:"#fff", border:"1.5px solid #cbd5e1", borderRadius:8, fontSize:12, color:"#334155", cursor:"pointer", fontWeight:700 }}>📥 Historique{(nbImportsInterrompus > 0 || nbImportsAnnules > 0) && (<span style={{ position:"absolute", top:-7, right:-7, display:"flex", gap:3 }}>{nbImportsInterrompus > 0 && (<span title={`${nbImportsInterrompus} import(s) interrompu(s) - à relancer pour créer les lignes manquantes`} style={{ minWidth:18, height:18, padding:"0 5px", color:"#fff", borderRadius:99, fontSize:10, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 0 0 2px #fff", background:"#f59e0b" }}>{nbImportsInterrompus}</span>)}{nbImportsAnnules > 0 && (<span title={`${nbImportsAnnules} import(s) annulé(s) - les données créées ont été supprimées`} style={{ minWidth:18, height:18, padding:"0 5px", color:"#fff", borderRadius:99, fontSize:10, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 0 0 2px #fff", background:"#dc2626" }}>{nbImportsAnnules}</span>)}</span>)}</button>
                      {isCommercialeCentre && (
                        <button disabled={!peut("classes","create")} title={permBtnTitle("Importer du coaching privé",peut("classes","create"))} onClick={() => { if (!assistanteCentreId) { toast.error("Centre introuvable pour votre compte - contactez le Super Admin avant d'importer, sinon les classes créées seraient invisibles dans votre liste."); return; } fetchCoachs(); setShowImportPriveUpload(true); }} style={{ padding:"8px 16px", background:"#fdf2f8", color:"#be185d", border:"1px solid #fbcfe8", borderRadius:8, fontSize:12, cursor:"pointer", fontWeight:700 }}>
                          🔒 Importer coaching privé
                        </button>
                      )}
                      <button onClick={() => { fetchAssignations(); toast.success("Actualisé"); }} style={{ ...btnSecondary, fontSize:11, padding:"6px 12px" }}>🔄 Actualiser</button>
                    </div>
                  </div>

                  {/* Stat cards */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
                    <StatCard label="Total prospects"  value={assignations.length}                                             color="#0891b2" icon="👥" />
                    <StatCard label="En attente"        value={assignations.filter(a=>a.statut==="en_attente").length}          color="#d97706" icon="⏳" />
                    <StatCard label="En cours"          value={assignations.filter(a=>a.statut==="en_cours").length}            color="#7c3aed" icon="🔄" />
                    <StatCard label="Convertis"         value={assignations.filter(a=>a.statut==="converti").length}            color="#22c55e" icon="✅" />
                  </div>

                  {/* Filtres */}
                  <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
                    <select value={assignFiltreType} onChange={e => setAssignFiltreType(e.target.value)} style={selectSt}>
                      <option value="tous">Tous les modes</option>
                      <option value="en_ligne">En ligne</option>
                      <option value="presentiel">Présentiel</option>
                      <option value="hybride">Hybride</option>
                    </select>
                    <select value={assignFiltreStatut} onChange={e => setAssignFiltreStatut(e.target.value)} style={selectSt}>
                      <option value="tous">Tous les statuts</option>
                      <option value="en_attente">En attente</option>
                      <option value="en_cours">En cours</option>
                      <option value="perdu">Perdu</option>
                    </select>
                    <span style={{ fontSize:12, color:"#6b7280", alignSelf:"center" }}>{filtered.length} résultat{filtered.length>1?"s":""}</span>
                  </div>

                  {assignationsLoading ? (
                    <div style={{ textAlign:"center", padding:48, color:"#6b7280" }}>Chargement…</div>
                  ) : filtered.length === 0 ? (
                    <div style={{ textAlign:"center", padding:"60px 24px", background:"#f8fafc", borderRadius:16, border:"1.5px dashed #e2e8f0" }}>
                      <div style={{ fontSize:"2.5rem", marginBottom:12 }}>🎯</div>
                      <div style={{ fontWeight:700, color:"#0f172a", marginBottom:6 }}>Aucun prospect pour l'instant</div>
                      <div style={{ fontSize:13, color:"#6b7280" }}>Les prospects apparaissent ici dès qu'ils choisissent une assistante sur le site.</div>
                    </div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {filtered.map(a => {
                        const sm = STATUT_META[a.statut] || STATUT_META.en_attente;
                        const testProspect = findTestForProspect(a);
                        // Un paiement TouchPoint n'est JAMAIS inséré dans `paiements` avant confirmation
                        // réussie (finalizeSuccessfulPayment, côté backend) - sa seule présence suffit
                        // à prouver un paiement en ligne réussi. Ne jamais se fier à a.source pour ça :
                        // "paiement_en_ligne" n'est écrit par AUCUN formulaire public actuel (bug trouvé
                        // le 22/08/2026) - tous les vrais paiements en ligne passaient donc inaperçus.
                        const pmtsAssignation = paiements.filter(p => p.assignationId === a.id);
                        const paiementEnLigneReussi = pmtsAssignation.some(p => p.mode === "TouchPoint");
                        const paiementAutreReussi = pmtsAssignation.some(p => p.mode !== "TouchPoint" && ["paye","validé"].includes(p.statut));
                        const NIVEAU_COLOR = { A1:"#64748b", A2:"#d97706", B1:"#1e3a8a", B2:"#7c3aed", C1:"#059669", C2:"#dc2626" };
                        const niveauColor  = testProspect ? (NIVEAU_COLOR[testProspect.niveau] || "#0891b2") : null;
                        return (
                          <div key={a.id}
                            onClick={() => { openApprenantDetail(a); }}
                            style={{ background:"#fff", border:`1.5px solid ${testProspect ? "#bbf7d0" : "#e5e7eb"}`, borderRadius:14, padding:"14px 18px", display:"flex", alignItems:"flex-start", gap:14, cursor:"pointer", transition:"box-shadow .15s" }}
                            onMouseEnter={e => e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.08)"}
                            onMouseLeave={e => e.currentTarget.style.boxShadow="none"}
                          >
                            {/* Avatar */}
                            <div style={{ width:42, height:42, borderRadius:"50%", background:"linear-gradient(135deg,#1e3a8a,#0891b2)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:14, flexShrink:0, overflow:"hidden" }}>
                              {a.avatar_url ? <img src={a.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : (a.prospect_nom||"?")[0].toUpperCase()}
                            </div>
                            {/* Infos */}
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
                                <div style={{ fontWeight:800, fontSize:14, color:"#0f172a" }}>{a.prospect_nom}</div>
                                <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                                  <span style={{ fontSize:11, color:"#94a3b8" }}>
                                    {new Date(a.created_at).toLocaleDateString("fr-FR", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}
                                  </span>
                                  <span style={{ background:sm.bg, color:sm.color, borderRadius:999, padding:"2px 10px", fontSize:11, fontWeight:700 }}>{sm.label}</span>
                                </div>
                              </div>
                              <div style={{ display:"flex", gap:10, marginTop:4, flexWrap:"wrap" }}>
                                {a.prospect_telephone && <span style={{ fontSize:12, color:"#0891b2", fontWeight:600 }}>📞 {a.prospect_telephone}</span>}
                                {a.prospect_email    && <span style={{ fontSize:12, color:"#64748b" }}>✉️ {a.prospect_email}</span>}
                              </div>
                              <div style={{ display:"flex", gap:8, marginTop:6, flexWrap:"wrap" }}>
                                <span style={{ background:"#eff6ff", color:"#1e3a8a", borderRadius:999, padding:"2px 10px", fontSize:11, fontWeight:700 }}>
                                  {TYPE_COURS[a.type_cours] || a.type_cours}
                                  {a.type_coaching ? ` · ${TYPE_COACH[a.type_coaching] || a.type_coaching}` : ""}
                                </span>
                                {a.centre_nom && <span style={{ background:"#f0fdf4", color:"#16a34a", borderRadius:999, padding:"2px 10px", fontSize:11, fontWeight:700 }}>📍 {a.centre_nom}</span>}
                                <span style={{ background:"#f8fafc", color:"#475569", borderRadius:999, padding:"2px 10px", fontSize:11 }}>
                                  👤 {a.assistante_nom}
                                </span>
                                {/* Niveau depuis le test formel */}
                                {testProspect?.niveau && (
                                  <span style={{ background: niveauColor+"20", color: niveauColor, borderRadius:999, padding:"2px 10px", fontSize:11, fontWeight:800, border:`1px solid ${niveauColor}40` }}>
                                    🎯 {testProspect.niveau}
                                  </span>
                                )}
                                {/* Niveau saisi manuellement (pas de test formel) */}
                                {!testProspect && a.niveau_client && (
                                  <span style={{ background:"#ede9fe", color:"#6d28d9", borderRadius:999, padding:"2px 10px", fontSize:11, fontWeight:800 }}>
                                    🎯 {a.niveau_client}
                                  </span>
                                )}
                                {a.source === "test_niveau" && !testProspect && (
                                  <span style={{ background:"#fef3c7", color:"#92400e", borderRadius:999, padding:"2px 10px", fontSize:11, fontWeight:700 }}>
                                    🧪 Test en cours…
                                  </span>
                                )}
                              </div>

                              {/* ── Bandeau prospect site web (formulaire sans paiement) ── */}
                              {a.source === "formulaire_en_ligne" && (
                                <div style={{ marginTop:10, padding:"8px 12px", background:"#eff6ff", borderRadius:8, border:"1px solid #bfdbfe", display:"flex", alignItems:"center", gap:8 }}>
                                  <span style={{ fontSize:11, fontWeight:800, color:"#1d4ed8" }}>🌐 Prospect site web</span>
                                  {a.inscription_offre && <span style={{ fontSize:11, color:"#1d4ed8" }}>· {a.inscription_offre}</span>}
                                </div>
                              )}

                              {/* ── Bandeau paiement en ligne déjà réglé ── */}
                              {paiementEnLigneReussi && (
                                <div style={{ marginTop:10, padding:"10px 14px", background:"linear-gradient(135deg,#f0fdf4,#dcfce7)", borderRadius:10, border:"1px solid #86efac" }}>
                                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                                    <span style={{ fontSize:12, fontWeight:800, color:"#15803d" }}>✅ Paiement en ligne confirmé</span>
                                    {a.montant_paye > 0 && (
                                      <span style={{ fontSize:13, fontWeight:900, color:"#15803d", background:"#bbf7d0", borderRadius:6, padding:"2px 10px" }}>
                                        {Number(a.montant_paye).toLocaleString("fr-FR")} FCFA
                                      </span>
                                    )}
                                    {a.niveau_client && (
                                      <span style={{ fontSize:11, fontWeight:700, background:"#ede9fe", color:"#7c3aed", borderRadius:6, padding:"2px 8px" }}>
                                        {a.niveau_client}
                                      </span>
                                    )}
                                  </div>
                                  {a.inscription_offre && (
                                    <div style={{ fontSize:12, fontWeight:700, color:"#0f172a", marginTop:5 }}>
                                      📚 {a.inscription_offre}
                                    </div>
                                  )}
                                  {/* La formule retenue, visible sans ouvrir le dossier : c'est
                                      elle qui détermine le rythme ET le tarif facturé. */}
                                  {a.suivi_demarrage?.formule_groupe && (
                                    <div style={{ display:"inline-flex", alignItems:"center", gap:5, background:"#f0fdf4", color:"#15803d", border:"1px solid #bbf7d0", borderRadius:999, padding:"2px 9px", fontSize:10.5, fontWeight:800, marginTop:5 }}>
                                      👥 {a.suivi_demarrage.formule_groupe.label}
                                      {a.suivi_demarrage.formule_groupe.tarif ? ` · ${Number(a.suivi_demarrage.formule_groupe.tarif).toLocaleString("fr-FR")} F/mois` : ""}
                                    </div>
                                  )}
                                  {a.code_promo && (
                                    <div style={{ display:"inline-flex", alignItems:"center", gap:4, background:"#fef3c7", color:"#92400e", borderRadius:999, padding:"2px 9px", fontSize:10, fontWeight:800, marginTop:5 }}>
                                      🎟️ {a.code_promo}{a.reduction_montant ? ` · -${Number(a.reduction_montant).toLocaleString("fr-FR")} FCFA` : ""}
                                    </div>
                                  )}
                                  {a.objectif_client && (
                                    <div style={{ fontSize:11, color:"#374151", marginTop:4, fontStyle:"italic" }}>
                                      🎯 &laquo;{a.objectif_client}&raquo;
                                    </div>
                                  )}
                                  {a.notes && (
                                    <div style={{ fontSize:11, color:"#6b7280", marginTop:4 }}>
                                      📝 {a.notes}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* ── Détail du dossier, HORS paiement en ligne ────────────────────
                                  Ces informations - l'offre, la formule retenue, la remise, le
                                  besoin exprimé - vivaient uniquement dans le bandeau vert
                                  ci-dessus, que seul un paiement en ligne réussi fait apparaître.
                                  Une inscription saisie à la main portait donc exactement les
                                  mêmes données sans qu'aucune ne s'affiche : la commerciale devait
                                  ouvrir le dossier pour retrouver le rythme qu'elle venait de
                                  choisir. Le bandeau vert reste intact ; celui-ci donne le même
                                  niveau de détail à tous les autres dossiers, en gris, pour qu'on
                                  ne le confonde jamais avec un paiement confirmé. ── */}
                              {!paiementEnLigneReussi && (() => {
                                const fg = a.suivi_demarrage?.formule_groupe;
                                // L'offre est déjà portée par le bandeau « 🌐 Prospect site web ».
                                const offre = a.source === "formulaire_en_ligne" ? null : a.inscription_offre;
                                if (!fg && !offre && !a.code_promo && !a.objectif_client) return null;
                                return (
                                  <div style={{ marginTop:10, padding:"10px 14px", background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0" }}>
                                    {offre && (
                                      <div style={{ fontSize:12, fontWeight:700, color:"#0f172a" }}>
                                        📚 {offre}
                                      </div>
                                    )}
                                    {fg && (
                                      <div style={{ display:"inline-flex", alignItems:"center", gap:5, background:"#f0fdf4", color:"#15803d", border:"1px solid #bbf7d0", borderRadius:999, padding:"2px 9px", fontSize:10.5, fontWeight:800, marginTop:offre ? 5 : 0 }}>
                                        👥 {fg.label}
                                        {fg.tarif ? ` · ${Number(fg.tarif).toLocaleString("fr-FR")} F/mois` : ""}
                                        {fg.canal === "presentiel" ? " · 🏢" : fg.canal === "en_ligne" ? " · 💻" : ""}
                                      </div>
                                    )}
                                    {fg?.frequence && (
                                      <div style={{ fontSize:11, color:"#64748b", marginTop:3 }}>{fg.frequence}</div>
                                    )}
                                    {a.code_promo && (
                                      <div style={{ display:"inline-flex", alignItems:"center", gap:4, background:"#fef3c7", color:"#92400e", borderRadius:999, padding:"2px 9px", fontSize:10, fontWeight:800, marginTop:5 }}>
                                        🎟️ {a.code_promo}{a.reduction_montant ? ` · -${Number(a.reduction_montant).toLocaleString("fr-FR")} FCFA` : ""}
                                      </div>
                                    )}
                                    {a.objectif_client && (
                                      <div style={{ fontSize:11, color:"#374151", marginTop:4, fontStyle:"italic" }}>
                                        🎯 &laquo;{a.objectif_client}&raquo;
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}

                              {/* ── Demande entreprise via formulaire web ── */}
                              {a.source === "devis_entreprise" && (
                                <div style={{ marginTop:10, padding:"10px 14px", background:"linear-gradient(135deg,#eff6ff,#dbeafe)", borderRadius:10, border:"1px solid #93c5fd" }}>
                                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                                    <span style={{ fontSize:12, fontWeight:800, color:"#1d4ed8" }}>🏢 Demande entreprise - Formulaire web</span>
                                    <span style={{ fontSize:11, fontWeight:700, background:"#bfdbfe", color:"#1e40af", borderRadius:6, padding:"2px 8px" }}>Corporate B2B</span>
                                  </div>
                                  <div style={{ fontSize:11, color:"#3b82f6", marginTop:4 }}>
                                    📋 Ce prospect a soumis une demande de formation entreprise depuis le site. Prenez contact rapidement pour qualifier l'offre.
                                  </div>
                                </div>
                              )}

                              {/* ── Prospect venu via test de niveau, test non encore soumis ── */}
                              {a.source === "test_niveau" && !testProspect && (
                                <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8, padding:"8px 12px", background:"#fff7ed", borderRadius:8, border:"1px solid #fed7aa", flexWrap:"wrap" }}>
                                  <span style={{ fontSize:11, fontWeight:700, color:"#c2410c" }}>⏳ En attente du test de niveau</span>
                                  <span style={{ fontSize:10, color:"#94a3b8", marginLeft:"auto" }}>Contactez ce prospect pour le relancer</span>
                                </div>
                              )}

                              {/* ── Bandeaux test de niveau ── */}
                              {testProspect ? (() => {
                                const enAttente = testProspect.correction_statut === "en_attente";
                                const FORMAT_LABEL = { mixte:"Mixte", reading:"Reading", writing:"Writing", speaking:"Speaking", listening:"Listening" };
                                const fmtLabel = FORMAT_LABEL[testProspect.format_test] || "Test";
                                if (enAttente) return (
                                  <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8, padding:"8px 12px", background:"#fffbeb", borderRadius:8, border:"1px solid #fde68a", flexWrap:"wrap" }}>
                                    <span style={{ fontSize:11, fontWeight:700, color:"#92400e" }}>⏳ {fmtLabel} - À corriger</span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTest(testProspect);
                                        setShowTestDetailModal(true);
                                      }}
                                      style={{ marginLeft:"auto", fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:6, border:"1px solid #f59e0b", background:"#fef3c7", color:"#92400e", cursor:"pointer" }}
                                    >
                                      ✏️ Corriger →
                                    </button>
                                  </div>
                                );
                                return (
                                  <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8, padding:"8px 12px", background:"#f0fdf4", borderRadius:8, border:"1px solid #bbf7d0", flexWrap:"wrap" }}>
                                    <span style={{ fontSize:11, fontWeight:700, color:"#15803d" }}>
                                      {testProspect.source === "oral" ? "📝 Test oral" : `✅ ${fmtLabel}`} reçu
                                    </span>
                                    <span style={{ fontWeight:800, fontSize:12, color:niveauColor, background:"#fff", border:`1px solid ${niveauColor}40`, borderRadius:999, padding:"1px 9px" }}>
                                      {testProspect.niveau}
                                    </span>
                                    <span style={{ fontSize:11, color:"#64748b" }}>{testProspect.score}% · {testProspect.date}</span>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setSelectedTest(testProspect); setShowTestDetailModal(true); }}
                                      style={{ marginLeft:"auto", fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:6, border:"1px solid #16a34a40", background:"#dcfce7", color:"#15803d", cursor:"pointer" }}
                                    >
                                      Voir le détail →
                                    </button>
                                  </div>
                                );
                              })() : (
                                <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8, padding:"7px 12px", background:"#fafafa", borderRadius:8, border:"1px dashed #e2e8f0" }}>
                                  <span style={{ fontSize:11, color:"#94a3b8" }}>⏳ Test de niveau non encore effectué</span>
                                </div>
                              )}

                              {/* ── Résumé paiement lié (compact) ── */}
                              {(() => {
                                const pmts = paiements.filter(p => p.assignationId === a.id);
                                if (pmts.length === 0) return (
                                  <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8, padding:"7px 12px", background:"#fafafa", borderRadius:8, border:"1px dashed #e2e8f0" }}>
                                    <span style={{ fontSize:11, color:"#94a3b8" }}>💳 Aucun paiement enregistré</span>
                                  </div>
                                );
                                const totalDu   = pmts.length > 0 ? Math.max(...pmts.map(p => p.montantDu || 0)) : 0;
                                const totalReçu = pmts.reduce((s,p) => s+(p.montantReçu||0), 0);
                                const reste     = totalDu - totalReçu;
                                const last      = pmts[0];
                                const PSTAT = { en_attente:{label:"En attente",color:"#d97706",bg:"#fef3c7",border:"#fde68a",icon:"⏳"}, partiel:{label:"Partiel",color:"#0891b2",bg:"#e0f2fe",border:"#bae6fd",icon:"🔄"}, reçu:{label:"Effectué",color:"#16a34a",bg:"#dcfce7",border:"#bbf7d0",icon:"✅"}, confirme:{label:"Effectué",color:"#16a34a",bg:"#dcfce7",border:"#bbf7d0",icon:"✅"}, paye:{label:"Effectué",color:"#16a34a",bg:"#dcfce7",border:"#bbf7d0",icon:"✅"}, "validé":{label:"Effectué",color:"#16a34a",bg:"#dcfce7",border:"#bbf7d0",icon:"✅"}, remboursé:{label:"Remboursé",color:"#7c3aed",bg:"#ede9fe",border:"#ddd6fe",icon:"↩️"} };
                                const ps = PSTAT[last.statut] || PSTAT.en_attente;
                                return (
                                  <div onClick={() => { setPmtListTarget({ id:a.id, prospect_nom:a.prospect_nom, prospect_email:a.prospect_email, type_cours:a.type_cours, centre_nom:a.centre_nom, pmts }); setShowPmtListModal(true); }}
                                    title="Voir tous les détails du paiement"
                                    style={{ display:"flex", alignItems:"center", gap:8, marginTop:8, padding:"7px 12px", background: ps.bg, borderRadius:8, border:`1px solid ${ps.border}`, flexWrap:"wrap", cursor:"pointer" }}>
                                    <span style={{ fontSize:12 }}>{ps.icon}</span>
                                    <span style={{ fontSize:11, fontWeight:700, color: ps.color }}>{ps.label}</span>
                                    <span style={{ fontSize:11, color:"#475569" }}>·</span>
                                    <span style={{ fontSize:11, color:"#475569" }}><strong style={{ color:"#22c55e" }}>{Number(totalReçu).toLocaleString("fr-FR")} F</strong> reçu</span>
                                    {reste > 0 && <span style={{ fontSize:11, color:"#ef4444", fontWeight:700 }}>· {Number(reste).toLocaleString("fr-FR")} F restant</span>}
                                    {last.refTransaction && <span style={{ fontSize:10, fontFamily:"monospace", color:"#0891b2", background:"#fff", border:"1px solid #bae6fd", borderRadius:4, padding:"0 5px", marginLeft:2 }}>🔖 {last.refTransaction}</span>}
                                    {pmts.length > 1 && <span style={{ marginLeft:"auto", fontSize:10, color:"#94a3b8" }}>{pmts.length} paiements</span>}
                                    <span style={{ marginLeft: pmts.length > 1 ? 0 : "auto", fontSize:10, color:"#64748b", fontWeight:700 }}>👁 Détails</span>
                                  </div>
                                );
                              })()}

                              {/* ── Alerte hors canal - dossier en attente d'arbitrage PA ── */}
                              {a.hors_canal_en_attente && (
                                <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8, padding:"8px 12px", background:"#fef2f2", borderRadius:8, border:"1px solid #fecaca", flexWrap:"wrap" }}>
                                  <span style={{ fontSize:11, fontWeight:700, color:"#b91c1c" }}>⚠️ Dossier en attente d'arbitrage PA - ce prospect a une autre assistante sur un autre canal, actions bloquées jusqu'à consolidation</span>
                                </div>
                              )}

                            </div>
                            {/* Actions */}
                            <div onClick={e => e.stopPropagation()} style={{ display:"flex", flexDirection:"column", gap:6, flexShrink:0 }}>
                              <select
                                value={a.statut}
                                onChange={e => {
                                  if (e.target.value === "archivé") {
                                    toast("📦 Dossier archivé - retrouvez-le dans la section Archives en bas", { duration:4000 });
                                  }
                                  updateStatutAssignation(a.id, e.target.value);
                                }}
                                style={{ ...selectSt, fontSize:11, padding:"4px 8px", cursor:"pointer", borderColor: a.statut==="archivé"?"#6b7280":"#e2e8f0" }}
                              >
                                <option value="en_attente">⏳ En attente</option>
                                <option value="archivé">📦 Archiver</option>
                              </select>
                              <button
                                onClick={() => setChatAssignation(chatAssignation?.id === a.id ? null : a)}
                                style={{ position:"relative", background: chatAssignation?.id === a.id ? "#0f172a" : BET_COLOR, color:"#fff", border:"none", borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:5, justifyContent:"center" }}
                              >
                                💬 {chatAssignation?.id === a.id ? "Fermer" : "Discuter"}
                                {(prospectUnreadMap[a.id] || 0) > 0 && chatAssignation?.id !== a.id && (
                                  <span style={{ position:"absolute", top:-5, right:-5, background:"#ef4444", color:"#fff", borderRadius:"50%", minWidth:16, height:16, fontSize:9, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 3px", lineHeight:1, border:"2px solid #fff" }}>
                                    {prospectUnreadMap[a.id] > 9 ? "9+" : prospectUnreadMap[a.id]}
                                  </span>
                                )}
                              </button>
                              {/* ── Lien test ── */}
                              <button
                                onClick={() => {
                                  if (testProspect || a.hors_canal_en_attente) return;
                                  const SITE = process.env.REACT_APP_FRONTEND_URL || "http://localhost:3000";
                                  const emailParam = a.prospect_email ? `&email=${encodeURIComponent(a.prospect_email)}` : "";
                                  const nomParam   = a.prospect_nom   ? `&nom=${encodeURIComponent(a.prospect_nom)}`     : "";
                                  // Rattache le résultat à CE dossier précis (jamais par email) - indispensable quand
                                  // parent et enfant partagent le même email/téléphone, sinon le test se recolle
                                  // au mauvais dossier.
                                  const dossierParam = a.id ? `&assignation_id=${encodeURIComponent(a.id)}` : "";
                                  const lien = `${SITE}/test-niveau?ref=${profil?.id}${emailParam}${nomParam}${dossierParam}`;
                                  navigator.clipboard.writeText(lien).then(() => toast.success("Lien test copié !")).catch(() => toast.error("Impossible de copier"));
                                }}
                                disabled={!!testProspect || a.hors_canal_en_attente}
                                title={a.hors_canal_en_attente ? "Dossier en attente d'arbitrage PA" : testProspect ? "Test déjà effectué" : "Copier le lien du test"}
                                style={{ background: (testProspect || a.hors_canal_en_attente) ? "#f1f5f9" : "#f0fdf4", color: (testProspect || a.hors_canal_en_attente) ? "#94a3b8" : "#16a34a", border: `1px solid ${(testProspect || a.hors_canal_en_attente) ? "#e2e8f0" : "#bbf7d0"}`, borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, cursor: (testProspect || a.hors_canal_en_attente) ? "not-allowed" : "pointer", display:"flex", alignItems:"center", gap:5, justifyContent:"center", opacity: (testProspect || a.hors_canal_en_attente) ? 0.6 : 1 }}
                              >
                                📋 Lien test
                              </button>
                              <button
                                onClick={() => {
                                  if (testProspect || a.hors_canal_en_attente) return;
                                  // prospect_nom est stocké "Prénom Nom" (ex: "aziz Tanoh") - le premier
                                  // mot est le prénom, pas le nom. Ces champs étaient inversés, ce qui
                                  // produisait un fullname ("Tanoh aziz") ne correspondant jamais au nom
                                  // réel de l'enfant ("aziz Tanoh") lors du rapprochement par nom.
                                  const nameParts = (a.prospect_nom || "").trim().split(/\s+/);
                                  setEditingItem({
                                    prenom:    nameParts[0] || "",
                                    nom:       nameParts.slice(1).join(" ") || "",
                                    email:     a.prospect_email    || "",
                                    telephone: a.prospect_telephone || "",
                                    assignation_id: a.id || null,
                                    profil:    "Particulier",
                                    niveau:    "A2",
                                    score:     0,
                                    date:      new Date().toISOString().slice(0, 10),
                                    statut:    "nouveau",
                                    notes:     "",
                                    offreRecommandee: "",
                                    centre_id: a.centre_id || null,
                                  });
                                  setShowTestModal(true);
                                }}
                                disabled={!!testProspect || a.hors_canal_en_attente || !peut("examens","create")}
                                title={a.hors_canal_en_attente ? "Dossier en attente d'arbitrage PA" : testProspect ? "Test déjà effectué" : permBtnTitle("Saisir le résultat d'un test physique",peut("examens","create"))}
                                style={{ background: (testProspect || a.hors_canal_en_attente || !peut("examens","create")) ? "#f1f5f9" : "#faf5ff", color: (testProspect || a.hors_canal_en_attente || !peut("examens","create")) ? "#94a3b8" : "#7c3aed", border: `1px solid ${(testProspect || a.hors_canal_en_attente || !peut("examens","create")) ? "#e2e8f0" : "#ddd6fe"}`, borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, cursor: (testProspect || a.hors_canal_en_attente || !peut("examens","create")) ? "not-allowed" : "pointer", display:"flex", alignItems:"center", gap:5, justifyContent:"center", opacity: (testProspect || a.hors_canal_en_attente || !peut("examens","create")) ? 0.6 : 1 }}
                              >
                                {peut("examens","create")?"📝":"🔒"} Test physique
                              </button>
                              {/* ── Bouton paiement prospect ── */}
                              {(() => {
                                const isWebSource = paiementEnLigneReussi;
                                // Coaching privé : bloqué par défaut tant que dossier_statut n'est
                                // pas explicitement "paiement_en_attente"/"paiement_recu" - même
                                // verrou que dans "Mes apprenants" (ce bouton-ci est un 3e point
                                // d'entrée vers le paiement qui n'était pas encore couvert).
                                // Un dossier déjà "converti" (classe assignée, apprenant actif) a
                                // terminé le pipeline - ne plus jamais bloquer ses versements
                                // complémentaires même si dossier_statut a été remis à null.
                                const dossierPasValide = a.type_coaching === "prive" && a.statut !== "converti" && !["paiement_en_attente","paiement_recu"].includes(a.dossier_statut);
                                // Ne bloque que si un paiement a RÉELLEMENT abouti (en ligne via
                                // TouchPoint, ou manuel validé) - une tentative en attente/échouée ne
                                // doit jamais empêcher de ressaisir/relancer le paiement.
                                const btnDisabled = paiementEnLigneReussi || paiementAutreReussi || a.hors_canal_en_attente || !peut("finances","create") || dossierPasValide;
                                const typeLabel = ["groupe","standard"].includes(a.type_coaching) ? "Coaching groupe" : a.type_coaching === "prive" ? "Coaching privé" : "Formation";
                                const modeLabel = libelleCanal(a.type_cours, a.centre_nom, " - ");
                                // Fallback pour prospects web sans inscription_offre : chercher dans les paiements CinetPay par email
                                const pmtByEmail = !a.inscription_offre
                                  ? paiements.find(p => p.email && a.prospect_email && p.email.toLowerCase() === a.prospect_email.toLowerCase() && p.offre)
                                  : null;
                                const offreEffective = a.inscription_offre || pmtByEmail?.offre || "";
                                const offreCatalogue = matchOffreCatalogue(catalogueOffres, offreEffective);
                                const montantOffre = a.suivi_demarrage?.cout_formation || offreCatalogue?.prix_num || pmtByEmail?.montantDu || 0;
                                const renvoyerLien = () => {
                                  if (!montantOffre) { toast.error("Montant inconnu pour cette offre - impossible de générer un lien."); return; }
                                  const SITE = process.env.REACT_APP_FRONTEND_URL || "http://localhost:3000";
                                  const qs = new URLSearchParams({
                                    nom: a.prospect_nom || "", email: a.prospect_email || "", telephone: a.prospect_telephone || "",
                                    montant: String(montantOffre), offre: offreEffective || typeLabel,
                                    assignation_id: a.id || "", assistante_id: a.assistantes?.id || "",
                                  });
                                  const lien = `${SITE}/paiement/relancer?${qs.toString()}`;
                                  navigator.clipboard.writeText(lien).then(() => toast.success("Lien de paiement copié !")).catch(() => toast.error("Impossible de copier"));
                                };
                                return (
                                  <>
                                  <button
                                    disabled={btnDisabled}
                                    onClick={() => {
                                      if (btnDisabled) return;
                                      setEditingItem({
                                        client:        a.prospect_nom       || "",
                                        email:         a.prospect_email     || "",
                                        telephone:     a.prospect_telephone || "",
                                        offre:         offreEffective,
                                        inscription:   `Parcours BET · ${modeLabel} · ${typeLabel}`,
                                        montantDu:     montantOffre,
                                        montantReçu:   isWebSource && montantOffre > 0 ? montantOffre : 0,
                                        date:          new Date().toISOString().slice(0, 10),
                                        mode:          isWebSource ? "TouchPoint" : "Mobile Money",
                                        statut:        isWebSource && montantOffre > 0 ? "paye" : "en_attente",
                                        notes:         isWebSource ? "Paiement en ligne préalablement effectué" : "",
                                        refTransaction:"",
                                        assignationId: a.id,
                                        dateDebutFormation: a.date_debut_formation ? a.date_debut_formation.slice(0,10) : "",
                                        packCoachingLabel: a.type_coaching === "prive"
                                          ? `${a.suivi_demarrage?.type_coaching === "intensive" ? "Intensive - 5 séances/sem" : "Classique - 3 séances × 2h/sem"}${a.suivi_demarrage?.lieu_coaching ? " · " + (a.suivi_demarrage.lieu_coaching === "domicile" ? "À domicile" : a.suivi_demarrage.lieu_coaching === "en_ligne" ? "En ligne" : "Au cabinet") : ""}`
                                          : null,
                                        // Le rythme retenu accompagne le versement : c'est lui
                                        // qui fixe le montant qu'on est en train d'encaisser.
                                        formuleGroupe: a.suivi_demarrage?.formule_groupe || null,
                                      });
                                      setShowPaiementModal(true);
                                    }}
                                    title={dossierPasValide ? "Ce dossier doit d'abord être validé par le PA avant d'enregistrer un paiement." : a.hors_canal_en_attente ? "Dossier en attente d'arbitrage PA" : !peut("finances","create") ? permBtnTitle("Enregistrer un paiement",false) : btnDisabled ? "Paiement déjà réglé" : "Enregistrer un paiement pour ce prospect"}
                                    style={{ background: btnDisabled ? "#f1f5f9" : "#fffbeb", color: btnDisabled ? "#94a3b8" : "#d97706", border: `1px solid ${btnDisabled ? "#e2e8f0" : "#fde68a"}`, borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, cursor: btnDisabled ? "not-allowed" : "pointer", display:"flex", alignItems:"center", gap:5, justifyContent:"center", opacity: btnDisabled ? 0.65 : 1 }}
                                  >
                                    {dossierPasValide ? "⏳" : peut("finances","create")?"💳":"🔒"} Paiement
                                  </button>
                                  {/* Toujours disponible (même si le paiement en ligne a échoué/traîne en
                                      attente) - évite de faire refaire tout le parcours d'inscription au
                                      prospect : la commerciale peut lui renvoyer un lien de paiement frais. */}
                                  {!btnDisabled && !dossierPasValide && !a.hors_canal_en_attente && peut("finances","create") && (
                                    <button onClick={renvoyerLien} title="Copier un nouveau lien de paiement à envoyer au prospect (WhatsApp, SMS…)"
                                      style={{ background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe", borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:5, justifyContent:"center" }}>
                                      🔄 Renvoyer le lien
                                    </button>
                                  )}
                                  </>
                                );
                              })()}

                              {/* ── Assignation classe/coach - présentiel GROUPE uniquement. Coaching
                                  privé (présentiel ou domicile) exclu : doit toujours passer par le
                                  circuit PA dédié ("Transmettre au PA" → validation → paiement →
                                  "Router → Assistante"), jamais cette assignation directe. ── */}
                              {a.type_cours === "presentiel" && a.type_coaching !== "prive" && a.routing_statut !== "centre_assigne" && (() => {
                                const pmtsDispo = paiements.filter(p => p.assignationId === a.id);
                                const hasPaiement = pmtsDispo.length > 0 && pmtsDispo.some(p => (p.montantReçu||0) > 0);
                                const hasTest = !!testProspect;
                                const actionOk = hasPaiement && hasTest && !a.hors_canal_en_attente && peut("inscriptions","update");
                                return (
                                  <button
                                    onClick={() => {
                                      if (a.hors_canal_en_attente) return;
                                      if (!peut("inscriptions","update")) return;
                                      if (!hasPaiement) { toast.error("Un paiement doit être enregistré avant l'assignation."); return; }
                                      if (!hasTest) { toast.error("Le test de niveau doit être effectué avant l'assignation."); return; }
                                      fetchClasses(); fetchCoachs();
                                      setAssignCentreTarget(a);
                                      setShowAssignCentreModal(true);
                                    }}
                                    title={!peut("inscriptions","update") ? permBtnTitle("Assigner à une classe et un coach", false) : a.hors_canal_en_attente ? "Dossier en attente d'arbitrage PA" : !hasTest ? "Test de niveau requis avant assignation" : hasPaiement ? "Assigner à une classe et un coach" : "Paiement requis avant assignation"}
                                    style={{ background: actionOk ? "linear-gradient(135deg,#7c3aed,#a855f7)" : "#f1f5f9", color: actionOk ? "#fff" : "#94a3b8", border: actionOk ? "none" : "1px solid #e2e8f0", borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, cursor: actionOk ? "pointer" : "not-allowed", display:"flex", alignItems:"center", gap:5, justifyContent:"center", opacity: actionOk ? 1 : 0.6 }}
                                  >
                                    {peut("inscriptions","update") ? "🏫" : "🔒"} Assigner
                                  </button>
                                );
                              })()}
                              {a.type_cours === "presentiel" && a.routing_statut === "centre_assigne" && (
                                <span style={{ fontSize:10, background:"#f5f3ff", color:"#7c3aed", border:"1px solid #ddd6fe", borderRadius:99, padding:"3px 8px", fontWeight:700 }}>✅ Assigné</span>
                              )}

                              {/* ── Finaliser l'inscription - uniquement en ligne standard (Onboarding).
                                  Le coaching privé (domicile inclus - type_cours !== "presentiel" aussi)
                                  a son propre circuit PA ("Transmettre au PA" dans Besoin du client) et
                                  ne doit jamais passer par ce modal générique. ── */}
                              {a.type_cours !== "presentiel" && a.type_coaching !== "prive" && (() => {
                                const pmtsDispo = paiements.filter(p => p.assignationId === a.id);
                                const hasPaiement = pmtsDispo.length > 0 && pmtsDispo.some(p => (p.montantReçu||0) > 0);
                                const hasTest = !!testProspect;
                                const actionOk = hasPaiement && hasTest && !a.hors_canal_en_attente && peut("inscriptions","update");
                                return (
                                  <button
                                    onClick={() => {
                                      if (a.hors_canal_en_attente) return;
                                      if (!peut("inscriptions","update")) return;
                                      if (!hasPaiement) {
                                        toast.error("Un paiement doit être enregistré avant de finaliser l'inscription.");
                                        return;
                                      }
                                      if (!hasTest) {
                                        toast.error("Le test de niveau doit être effectué avant de transmettre le dossier.");
                                        return;
                                      }
                                      setFinalisationTarget(a);
                                      setShowFinalisationModal(true);
                                      runDbCheck(a);
                                    }}
                                    title={!peut("inscriptions","update") ? permBtnTitle("Vérifier & transmettre le dossier au PA / Onboarding", false) : a.hors_canal_en_attente ? "Dossier en attente d'arbitrage PA" : !hasTest ? "Test de niveau requis avant transmission" : hasPaiement ? "Vérifier & transmettre le dossier au PA / Onboarding" : "Paiement requis avant transmission"}
                                    style={{
                                      background: actionOk ? "linear-gradient(135deg,#15803d,#22c55e)" : "#f1f5f9",
                                      color:      actionOk ? "#fff" : "#94a3b8",
                                      border:     actionOk ? "none" : "1px solid #e2e8f0",
                                      borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700,
                                      cursor: actionOk ? "pointer" : "not-allowed",
                                      display:"flex", alignItems:"center", gap:5, justifyContent:"center",
                                      opacity: actionOk ? 1 : 0.6,
                                    }}
                                  >
                                    {peut("inscriptions","update") ? "📤" : "🔒"} Transmettre
                                  </button>
                                );
                              })()}
                              {/* Bouton annuler */}
                              <button
                                onClick={() => { if (a.hors_canal_en_attente || !peut("inscriptions","delete")) return; setCancelModal({ open:true, target:a, raison:"", raisonLibre:"" }); }}
                                disabled={a.hors_canal_en_attente || !peut("inscriptions","delete")}
                                title={!peut("inscriptions","delete") ? permBtnTitle("Annuler ce dossier", false) : a.hors_canal_en_attente ? "Dossier en attente d'arbitrage PA" : "Annuler ce dossier"}
                                style={{ background: (a.hors_canal_en_attente || !peut("inscriptions","delete")) ? "#f1f5f9" : "#fff5f5", color: (a.hors_canal_en_attente || !peut("inscriptions","delete")) ? "#94a3b8" : "#dc2626", border: `1px solid ${(a.hors_canal_en_attente || !peut("inscriptions","delete")) ? "#e2e8f0" : "#fecaca"}`, borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, cursor: (a.hors_canal_en_attente || !peut("inscriptions","delete")) ? "not-allowed" : "pointer", display:"flex", alignItems:"center", gap:5, justifyContent:"center", opacity: (a.hors_canal_en_attente || !peut("inscriptions","delete")) ? 0.65 : 1 }}
                              >
                                {peut("inscriptions","delete") ? "✕" : "🔒"} Annuler
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* ── Archives ── */}
                  {archives.length > 0 && (() => {
                    const [open, setOpen] = [archivesOpen, setArchivesOpen];
                    return (
                      <div style={{ marginTop:24, borderRadius:14, border:"1.5px dashed #d1d5db", overflow:"hidden" }}>
                        <div
                          onClick={() => setArchivesOpen(v => !v)}
                          style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 18px", background:"#f9fafb", cursor:"pointer" }}
                        >
                          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <span style={{ fontSize:16 }}>📦</span>
                            <span style={{ fontWeight:700, fontSize:13, color:"#374151" }}>Dossiers archivés</span>
                            <span style={{ background:"#e5e7eb", color:"#6b7280", borderRadius:99, padding:"1px 8px", fontSize:11, fontWeight:700 }}>{archives.length}</span>
                          </div>
                          <span style={{ color:"#9ca3af", fontSize:14 }}>{open ? "▲" : "▼"}</span>
                        </div>
                        {open && (
                          <div style={{ display:"flex", flexDirection:"column", gap:8, padding:"12px 16px", background:"#fff" }}>
                            {archives.map(a => (
                              <div key={a.id} style={{ display:"flex", alignItems:"center", gap:14, padding:"10px 14px", background:"#f9fafb", borderRadius:10, border:"1px solid #e5e7eb" }}>
                                <div style={{ width:36, height:36, borderRadius:"50%", background:"#e5e7eb", color:"#6b7280", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:13, flexShrink:0, overflow:"hidden" }}>
                                  {a.avatar_url ? <img src={a.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : (a.prospect_nom||"?")[0].toUpperCase()}
                                </div>
                                <div style={{ flex:1, minWidth:0 }}>
                                  <div style={{ fontWeight:700, fontSize:13, color:"#374151" }}>{a.prospect_nom}</div>
                                  <div style={{ fontSize:11, color:"#9ca3af" }}>{a.prospect_email || a.prospect_telephone || "-"}</div>
                                </div>
                                <div style={{ fontSize:11, color:"#9ca3af" }}>
                                  {new Date(a.updated_at || a.created_at).toLocaleDateString("fr-FR")}
                                </div>
                                <button
                                  onClick={() => { if (!peut("inscriptions","update")) return; updateStatutAssignation(a.id, "en_attente"); toast.success("♻️ Dossier réactivé - remis En attente"); }}
                                  disabled={!peut("inscriptions","update")}
                                  title={permBtnTitle("Réactiver ce dossier", peut("inscriptions","update"))}
                                  style={{ padding:"5px 12px", background: peut("inscriptions","update") ? "#eff6ff" : "#f1f5f9", border: `1px solid ${peut("inscriptions","update") ? "#bfdbfe" : "#e2e8f0"}`, borderRadius:8, color: peut("inscriptions","update") ? "#1d4ed8" : "#94a3b8", fontSize:11, fontWeight:700, cursor: peut("inscriptions","update") ? "pointer" : "not-allowed", whiteSpace:"nowrap" }}
                                >
                                  {peut("inscriptions","update") ? "♻️" : "🔒"} Réactiver
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── Dossiers annulés ── */}
                  {annules.length > 0 && (
                    <div style={{ marginTop:16, borderRadius:14, border:"1.5px dashed #fecaca", overflow:"hidden" }}>
                      <div
                        onClick={() => setAnnulesOpen(v => !v)}
                        style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 18px", background:"#fff5f5", cursor:"pointer" }}
                      >
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <span style={{ fontSize:16 }}>🚫</span>
                          <span style={{ fontWeight:700, fontSize:13, color:"#dc2626" }}>Dossiers annulés</span>
                          <span style={{ background:"#fecaca", color:"#dc2626", borderRadius:99, padding:"1px 8px", fontSize:11, fontWeight:700 }}>{annules.length}</span>
                        </div>
                        <span style={{ color:"#fca5a5", fontSize:14 }}>{annulesOpen ? "▲" : "▼"}</span>
                      </div>
                      {annulesOpen && (
                        <div style={{ display:"flex", flexDirection:"column", gap:8, padding:"12px 16px", background:"#fff" }}>
                          {annules.map(a => {
                            const motif = (a.notes || "").replace(/^\[ANNULE\]\s*/, "").split(/ [\u2014-] /)[0];
                            return (
                              <div key={a.id} style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 14px", background:"#fff5f5", borderRadius:10, border:"1px solid #fecaca" }}>
                                <div style={{ width:36, height:36, borderRadius:"50%", background:"#fee2e2", color:"#dc2626", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:13, flexShrink:0, overflow:"hidden" }}>
                                  {a.avatar_url ? <img src={a.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : (a.prospect_nom||"?")[0].toUpperCase()}
                                </div>
                                <div style={{ flex:1, minWidth:0 }}>
                                  <div style={{ fontWeight:700, fontSize:13, color:"#374151" }}>{a.prospect_nom}</div>
                                  <div style={{ fontSize:11, color:"#9ca3af" }}>{a.prospect_email || a.prospect_telephone || "-"}</div>
                                  {motif && <div style={{ fontSize:11, fontWeight:600, color:"#dc2626", marginTop:2 }}>Raison : {motif}</div>}
                                </div>
                                <div style={{ fontSize:11, color:"#9ca3af", flexShrink:0 }}>
                                  {new Date(a.updated_at || a.created_at).toLocaleDateString("fr-FR")}
                                </div>
                                <button
                                  onClick={() => { if (!peut("inscriptions","update")) return; updateStatutAssignation(a.id, "en_attente"); toast.success("♻️ Dossier réactivé"); }}
                                  disabled={!peut("inscriptions","update")}
                                  title={permBtnTitle("Réactiver ce dossier", peut("inscriptions","update"))}
                                  style={{ padding:"5px 12px", background: peut("inscriptions","update") ? "#eff6ff" : "#f1f5f9", border: `1px solid ${peut("inscriptions","update") ? "#bfdbfe" : "#e2e8f0"}`, borderRadius:8, color: peut("inscriptions","update") ? "#1d4ed8" : "#94a3b8", fontSize:11, fontWeight:700, cursor: peut("inscriptions","update") ? "pointer" : "not-allowed", whiteSpace:"nowrap" }}
                                >
                                  {peut("inscriptions","update") ? "♻️" : "🔒"} Réactiver
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Modal annulation ── */}
                  {cancelModal.open && cancelModal.target && (
                    <ModaleAnimee ouvert
                      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:10001, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
                      panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:440, boxShadow:"0 20px 60px rgba(0,0,0,0.25)", overflow:"hidden" }}>
                        <div style={{ background:"linear-gradient(135deg,#dc2626,#ef4444)", padding:"18px 24px", color:"#fff" }}>
                          <div style={{ fontSize:18, fontWeight:800, marginBottom:4 }}>🚫 Annuler le dossier</div>
                          <div style={{ fontSize:12, opacity:0.85 }}>{cancelModal.target.prospect_nom}</div>
                        </div>
                        <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:14 }}>
                          <div>
                            <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:8 }}>Raison de l'annulation *</label>
                            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                              {["Prospect injoignable","Offre non adaptée","Abandon volontaire","Doublon / erreur de saisie","Autre"].map(r => (
                                <label key={r} style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:13, padding:"8px 12px", borderRadius:8, background: cancelModal.raison===r ? "#fff5f5" : "#f8fafc", border:`1.5px solid ${cancelModal.raison===r ? "#fca5a5" : "#e2e8f0"}` }}>
                                  <input type="radio" name="raison_annul" value={r} checked={cancelModal.raison===r} onChange={() => setCancelModal(p=>({...p, raison:r}))} style={{ accentColor:"#dc2626" }} />
                                  {r}
                                </label>
                              ))}
                            </div>
                          </div>
                          {cancelModal.raison === "Autre" && (
                            <textarea
                              value={cancelModal.raisonLibre}
                              onChange={e => setCancelModal(p=>({...p, raisonLibre:e.target.value}))}
                              placeholder="Précisez la raison…"
                              style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #fca5a5", fontSize:13, resize:"vertical", minHeight:70, boxSizing:"border-box" }}
                            />
                          )}
                        </div>
                        <div style={{ display:"flex", gap:10, padding:"0 24px 20px" }}>
                          <button onClick={() => setCancelModal({ open:false, target:null, raison:"", raisonLibre:"" })}
                            style={{ flex:1, padding:"10px", border:"1.5px solid #e2e8f0", borderRadius:9, background:"#f8fafc", color:"#374151", fontWeight:700, fontSize:13, cursor:"pointer" }}>
                            ← Retour
                          </button>
                          <button
                            onClick={handleAnnulerDossier}
                            disabled={!cancelModal.raison || cancelSaving || !peut("inscriptions","delete")}
                            title={permBtnTitle("Confirmer l'annulation", peut("inscriptions","delete"))}
                            style={{ flex:2, padding:"10px", background: (cancelModal.raison && peut("inscriptions","delete")) ? "linear-gradient(135deg,#dc2626,#ef4444)" : "#e5e7eb", color: (cancelModal.raison && peut("inscriptions","delete")) ? "#fff" : "#9ca3af", border:"none", borderRadius:9, fontWeight:700, fontSize:13, cursor: (cancelModal.raison && peut("inscriptions","delete")) ? "pointer" : "not-allowed" }}
                          >
                            {cancelSaving ? "Annulation…" : peut("inscriptions","delete") ? "✕ Confirmer l'annulation" : "🔒 Confirmer l'annulation"}
                          </button>
                        </div>
                    </ModaleAnimee>
                  )}
                </div>
              );
            })()}

            {/* ══ MES APPRENANTS ══ */}
            {activeTab === "apprenants" && (() => {
              const NIVEAU_COLOR = { A1:"#6b7280",A2:"#0891b2",B1:"#16a34a",B2:"#7c3aed",C1:"#d97706",C2:"#dc2626" };

              // Source : toutes les assignations converties
              const apprenants = assignations.filter(a => a.statut === "converti");

              // Filtrage
              const appFiltered = apprenants.filter(a => {
                const q = appSearchQ.toLowerCase();
                if (q && !`${a.prospect_nom} ${a.prospect_email} ${a.prospect_telephone}`.toLowerCase().includes(q)) return false;
                if (appTypeCours !== "tous" && a.type_cours    !== appTypeCours)  return false;
                // "groupe" (anciens dossiers) et "standard" (dossiers créés depuis Inscriptions)
                // désignent la même chose - jamais deux valeurs unifiées côté données, donc on
                // les traite comme équivalentes ici plutôt que de les distinguer.
                if (appTypeCoach === "standard" && !["standard","groupe"].includes(a.type_coaching)) return false;
                if (appTypeCoach !== "tous" && appTypeCoach !== "standard" && a.type_coaching !== appTypeCoach)  return false;
                if (appNiveau    !== "tous") {
                  const t = tests.find(t => t.email && a.prospect_email && t.email.toLowerCase() === a.prospect_email.toLowerCase());
                  const niveauEffectifFiltre = t?.niveau || a.niveau_client || null;
                  if (niveauEffectifFiltre !== appNiveau) return false;
                }
                return true;
              });

              // KPI globaux (sur toute la liste, pas filtrée)
              const totalEncaisse = apprenants.reduce((s, a) => {
                const pmts = paiements.filter(p => p.assignationId === a.id);
                return s + pmts.reduce((ss,p) => ss+(p.montantReçu||0), 0);
              }, 0);
              const totalDuGlobal = apprenants.reduce((s, a) => {
                const pmts = paiements.filter(p => p.assignationId === a.id);
                return s + pmts.reduce((ss,p) => ss+(p.montantDu||0), 0);
              }, 0);
              const nbAvecTest = apprenants.filter(a =>
                a.niveau_client || tests.some(t => t.email && a.prospect_email && t.email.toLowerCase() === a.prospect_email.toLowerCase())
              ).length;

              return (
                <div>
                  {/* ── Header ── */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
                    <div>
                      <h2 style={{ margin:0, fontSize:18, fontWeight:800, color:"#0f172a" }}>🎓 Mes apprenants</h2>
                      <p style={{ margin:"4px 0 0", fontSize:12, color:"#9ca3af" }}>Tous les prospects que vous avez convertis en apprenants BET Languages</p>
                    </div>
                    <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                      {/* L'ARCHIVE EST À CÔTÉ DE LA LISTE, pas dans un autre onglet. Cette liste
                          montre les dossiers `converti`, c'est-à-dire l'instantané du jour : un
                          apprenant qui arrête n'en disparaît pas, son départ n'est simplement
                          écrit nulle part ici. La question « combien j'en ai eu, et qui est
                          parti » se pose en regardant cet écran - la réponse doit s'ouvrir
                          depuis lui. */}
                      <button onClick={() => setShowArchiveApprenants(true)}
                        style={{ ...btnSecondary, fontSize:11, padding:"7px 14px", fontWeight:700 }}>
                        📒 Archive
                      </button>
                      <button onClick={() => { setActiveTab("assignations"); }} style={{ ...btnSecondary, fontSize:11, padding:"7px 14px" }}>
                        ← Retour aux prospects
                      </button>
                    </div>
                  </div>

                  {/* ── KPI ── */}
                  {(() => {
                    const appAvecStats = apprenants.filter(a => (a.suivi_presences?.seances_effectuees || 0) > 0);
                    const totalSeances   = appAvecStats.reduce((s,a) => s + (a.suivi_presences?.seances_effectuees||0), 0);
                    const totalPresences = appAvecStats.reduce((s,a) => s + (a.suivi_presences?.presences||0), 0);
                    const totalAbsences  = appAvecStats.reduce((s,a) => s + (a.suivi_presences?.absences ||0), 0);
                    const tauxPartGlobal = totalSeances > 0 ? Math.round((totalPresences / totalSeances) * 100) : null;
                    const tauxAbsGlobal  = totalSeances > 0 ? Math.round((totalAbsences  / totalSeances) * 100) : null;
                    return (
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:12, marginBottom:22 }}>
                        <StatCard label="Total apprenants"  value={apprenants.length}        color={BET_COLOR}  icon="🎓" sub="convertis" />
                        <StatCard label="Avec test de niveau" value={nbAvecTest}             color="#7c3aed"    icon="📝" sub={`sur ${apprenants.length}`} />
                        <StatCard label="Total encaissé"    value={Number(totalEncaisse).toLocaleString("fr-FR")+" F"} color="#22c55e" icon="✅" />
                        <StatCard label="Reste à percevoir" value={Number(Math.max(0,totalDuGlobal-totalEncaisse)).toLocaleString("fr-FR")+" F"} color={totalDuGlobal-totalEncaisse>0?"#ef4444":"#22c55e"} icon="⏳" />
                        <StatCard
                          label="Taux de participation"
                          value={tauxPartGlobal !== null ? `${tauxPartGlobal}%` : "-"}
                          color={tauxPartGlobal===null?"#94a3b8":tauxPartGlobal>=80?"#22c55e":tauxPartGlobal>=60?"#f59e0b":"#ef4444"}
                          icon="✅"
                          sub={tauxPartGlobal !== null ? `${totalPresences}/${totalSeances} séances` : "Aucune donnée"}
                        />
                        <StatCard
                          label="Taux d'absentéisme"
                          value={tauxAbsGlobal !== null ? `${tauxAbsGlobal}%` : "-"}
                          color={tauxAbsGlobal===null?"#94a3b8":tauxAbsGlobal<=10?"#22c55e":tauxAbsGlobal<=25?"#f59e0b":"#ef4444"}
                          icon="❌"
                          sub={tauxAbsGlobal !== null ? `${totalAbsences} absence${totalAbsences>1?"s":""}` : "Aucune donnée"}
                        />
                      </div>
                    );
                  })()}

                  {/* ── Barre filtres ── */}
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginBottom:18, padding:"10px 14px", background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0" }}>
                    <input
                      value={appSearchQ}
                      onChange={e => { setAppSearchQ(e.target.value); setAppPage(0); }}
                      placeholder="🔍 Rechercher un apprenant…"
                      style={{ ...inputSt, width:220, margin:0 }}
                    />
                    <select value={appTypeCours} onChange={e => { setAppTypeCours(e.target.value); setAppPage(0); }} style={{ ...inputSt, width:150, margin:0 }}>
                      <option value="tous">Tous les modes</option>
                      <option value="en_ligne">En ligne</option>
                      <option value="presentiel">Présentiel</option>
                      <option value="hybride">Hybride</option>
                    </select>
                    <select value={appTypeCoach} onChange={e => { setAppTypeCoach(e.target.value); setAppPage(0); }} style={{ ...inputSt, width:150, margin:0 }}>
                      <option value="tous">Tout coaching</option>
                      <option value="standard">Groupe</option>
                      <option value="prive">Privé</option>
                    </select>
                    <select value={appNiveau} onChange={e => { setAppNiveau(e.target.value); setAppPage(0); }} style={{ ...inputSt, width:140, margin:0 }}>
                      <option value="tous">Tous niveaux</option>
                      {["A1","A2","B1","B2","C1","C2"].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    {(appSearchQ||appTypeCours!=="tous"||appTypeCoach!=="tous"||appNiveau!=="tous") && (
                      <button onClick={() => { setAppSearchQ(""); setAppTypeCours("tous"); setAppTypeCoach("tous"); setAppNiveau("tous"); setAppPage(0); }} style={{ ...btnSecondary, padding:"6px 12px", margin:0, fontSize:11 }}>✕ Effacer</button>
                    )}
                    <span style={{ marginLeft:"auto", fontSize:11, color:"#6b7280" }}>{appFiltered.length} apprenant{appFiltered.length!==1?"s":""}</span>
                  </div>

                  {/* ── Vide ── */}
                  {apprenants.length === 0 && (
                    <div style={{ textAlign:"center", padding:"60px 24px", background:"#f8fafc", borderRadius:16, border:"2px dashed #e2e8f0" }}>
                      <div style={{ fontSize:"3.5rem", marginBottom:12 }}>🎓</div>
                      <div style={{ fontSize:15, fontWeight:800, color:"#0f172a", marginBottom:6 }}>Aucun apprenant pour l'instant</div>
                      <div style={{ fontSize:13, color:"#9ca3af", maxWidth:360, margin:"0 auto 20px" }}>
                        Lorsqu'un prospect est marqué <strong>Converti</strong> dans l'onglet Prospects, il apparaîtra automatiquement ici.
                      </div>
                      <button onClick={() => setActiveTab("assignations")} style={btnPrimary}>Voir mes prospects →</button>
                    </div>
                  )}

                  {apprenants.length > 0 && appFiltered.length === 0 && (
                    <div style={{ textAlign:"center", padding:"40px 0", color:"#94a3b8", fontSize:13 }}>Aucun apprenant ne correspond aux filtres.</div>
                  )}

                  {/* ── Tableau apprenants ── */}
                  {appFiltered.length > 0 && (() => {
                    const totalPages = Math.ceil(appFiltered.length / APP_PER_PAGE);
                    const safePage   = Math.min(appPage, totalPages - 1);
                    const pageData   = appFiltered.slice(safePage * APP_PER_PAGE, (safePage + 1) * APP_PER_PAGE);
                    const today = new Date(); today.setHours(0,0,0,0);

                    return (
                      <div>
                        <div style={{ overflowX:"auto", borderRadius:12, border:"1px solid #e2e8f0", background:"#fff" }}>
                          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                            <thead>
                              <tr style={{ background:"#f8fafc" }}>
                                <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, fontSize:11, color:"#6b7280", borderBottom:"1px solid #e2e8f0", whiteSpace:"nowrap" }}>#</th>
                                <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, fontSize:11, color:"#6b7280", borderBottom:"1px solid #e2e8f0", minWidth:200 }}>Apprenant</th>
                                <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, fontSize:11, color:"#6b7280", borderBottom:"1px solid #e2e8f0" }}>Niveau</th>
                                <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, fontSize:11, color:"#6b7280", borderBottom:"1px solid #e2e8f0" }}>Mode</th>
                                <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, fontSize:11, color:"#6b7280", borderBottom:"1px solid #e2e8f0" }}>Coaching</th>
                                <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, fontSize:11, color:"#6b7280", borderBottom:"1px solid #e2e8f0", minWidth:160 }}>Paiement</th>
                                <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, fontSize:11, color:"#6b7280", borderBottom:"1px solid #e2e8f0" }}>Assiduité</th>
                                <th style={{ padding:"10px 14px", textAlign:"center", fontWeight:700, fontSize:11, color:"#6b7280", borderBottom:"1px solid #e2e8f0" }}>Alertes</th>
                                <th style={{ padding:"10px 14px", textAlign:"center", fontWeight:700, fontSize:11, color:"#6b7280", borderBottom:"1px solid #e2e8f0" }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pageData.map((a, idx) => {
                                const globalIdx = safePage * APP_PER_PAGE + idx + 1;
                                const initiales = (a.prospect_nom || "?").split(" ").map(p=>p[0]).slice(0,2).join("").toUpperCase();
                                const testProspect = findTestForProspect(a);
                                const pmts = paiements.filter(p => p.assignationId === a.id);
                                const totalReçu = pmts.reduce((s,p)=>s+(p.montantReçu||0),0);
                                const totalDu   = pmts.length > 0 ? Math.max(...pmts.map(p => p.montantDu || 0)) : 0;
                                const reste     = totalDu - totalReçu;
                                const niveauColor = NIVEAU_COLOR[testProspect?.niveau] || "#6b7280";
                                const modeLabel = libelleCanal(a.type_cours, a.centre_nom);
                                const coachLabel = ["groupe","standard"].includes(a.type_coaching) ? "Groupe" : a.type_coaching === "prive" ? "Privé" : "-";

                                const plan = a.plan_paiement || {};
                                const tranches = plan.tranches || [];
                                const hasAlertePmt = tranches.some(t => {
                                  if (t.statut !== "en_attente") return false;
                                  const d = new Date(t.date_echeance); d.setHours(0,0,0,0);
                                  return d < today;
                                });

                                // Plan mensuel récurrent : même seuils J-7/J-3/J-1 que le job cron,
                                // affichés ici pour que la commerciale voie d'un coup d'œil quels
                                // apprenants ont un rappel actif, sans ouvrir chaque fiche.
                                const moisMensuels = plan.mois_paiements || [];
                                let joursRestantsMensuel = null;
                                if (plan.actif !== false && moisMensuels.length) {
                                  const moisDu = moisMensuels.find(m => {
                                    const paye = (m.tranches||[]).filter(t=>t.statut==="paye"||t.statut==="payé").reduce((s,t)=>s+(t.montant||0),0);
                                    return paye < m.montant;
                                  });
                                  if (moisDu) {
                                    const echeance = dateEcheanceMois(moisDu.periode, plan.jour_echeance || 1);
                                    joursRestantsMensuel = Math.round((echeance - today) / 86400000);
                                  }
                                }
                                const hasRappelMensuel = joursRestantsMensuel !== null && joursRestantsMensuel <= 7;
                                const tranches_paye = tranches.filter(t => t.statut === "payé").reduce((s,t) => s + (t.montant || 0), 0);
                                const tranches_total = tranches.reduce((s,t) => s + (t.montant || 0), 0);
                                const pctTranches = tranches_total > 0 ? Math.round((tranches_paye / tranches_total) * 100) : 0;

                                const sp = a.suivi_presences || {};
                                const sessions = sp.sessions || [];
                                const seances = sp.seances_effectuees || sessions.length || 0;
                                const presences = sp.presences || 0;
                                const tauxParticipation = seances > 0 ? Math.round((presences / seances) * 100) : null;
                                const now2 = new Date();
                                const monday = new Date(now2); monday.setDate(now2.getDate() - ((now2.getDay() + 6) % 7)); monday.setHours(0,0,0,0);
                                const absWeek = sessions.filter(s => new Date(s.date) >= monday && s.statut === "absent").length;
                                const alertSemaine = absWeek > 2;
                                const sortedSess = [...sessions].sort((x,y) => x.date.localeCompare(y.date));
                                let consec = 0, maxConsec = 0;
                                for (const s of sortedSess) {
                                  if (s.statut === "absent") { consec++; if (consec > maxConsec) maxConsec = consec; } else consec = 0;
                                }
                                const alertConsecutif = maxConsec >= 3;
                                const hasAlerteAss = alertSemaine || alertConsecutif;

                                const rowBg = a.acces_bloque ? "#fff5f5" : hasAlertePmt || hasRappelMensuel ? "#fff8f1" : hasAlerteAss ? "#fffbeb" : "#fff";

                                return (
                                  <tr key={a.id}
                                    style={{ background: rowBg, borderBottom:"1px solid #f1f5f9", cursor:"pointer", transition:"background .12s" }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"}
                                    onMouseLeave={e => e.currentTarget.style.background = rowBg}
                                    onClick={() => { openApprenantDetail(a); }}
                                  >
                                    {/* # */}
                                    <td style={{ padding:"10px 14px", color:"#94a3b8", fontSize:12, fontWeight:600 }}>{globalIdx}</td>

                                    {/* Apprenant */}
                                    <td style={{ padding:"10px 14px" }}>
                                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                                        <div style={{ width:34, height:34, borderRadius:"50%", background:`linear-gradient(135deg,#0f172a,${BET_COLOR})`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:12, color:"#fff", flexShrink:0, overflow:"hidden" }}>{a.avatar_url ? <img src={a.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : initiales}</div>
                                        <div style={{ minWidth:0 }}>
                                          <div style={{ fontWeight:700, color:"#0f172a", fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:5 }}>
                                            {a.prospect_nom || "-"}
                                            {a.profil_type === "enfant" && <span title="Profil enfant" style={{ fontSize:11, background:"#fef9c3", color:"#854d0e", borderRadius:999, padding:"1px 6px", fontWeight:700, flexShrink:0 }}>👶 Enfant</span>}
                                            {a.profil_type === "etudiant" && <span title="Profil étudiant" style={{ fontSize:11, background:"#eff6ff", color:"#1d4ed8", borderRadius:999, padding:"1px 6px", fontWeight:700, flexShrink:0 }}>🎒 Étudiant</span>}
                                          </div>
                                          <div style={{ fontSize:11, color:"#64748b", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.prospect_email || a.prospect_telephone || "-"}</div>
                                        </div>
                                      </div>
                                    </td>

                                    {/* Niveau - priorité au test formel, sinon celui saisi/importé
                                        manuellement (a.niveau_client, ex: import en masse avec
                                        colonne niveau) ; sans ce repli, un import en masse ayant
                                        renseigné le niveau affichait quand même "-" faute de test
                                        formel correspondant dans level_test_results. */}
                                    <td style={{ padding:"10px 14px" }}>
                                      {testProspect
                                        ? <span style={{ display:"inline-block", padding:"2px 9px", borderRadius:999, fontSize:11, fontWeight:800, color:"#fff", background:niveauColor }}>{testProspect.niveau}</span>
                                        : a.niveau_client
                                        ? <span style={{ display:"inline-block", padding:"2px 9px", borderRadius:999, fontSize:11, fontWeight:800, color:"#fff", background:NIVEAU_COLOR[a.niveau_client] || "#6b7280" }}>{a.niveau_client}</span>
                                        : <span style={{ fontSize:11, color:"#cbd5e1" }}>-</span>}
                                    </td>

                                    {/* Mode */}
                                    <td style={{ padding:"10px 14px" }}>
                                      <span style={{ fontSize:11, fontWeight:600, padding:"2px 8px", borderRadius:999, background:"#e0f2fe", color:"#0369a1", whiteSpace:"nowrap" }}>{modeLabel}</span>
                                    </td>

                                    {/* Coaching */}
                                    <td style={{ padding:"10px 14px" }}>
                                      <span style={{ fontSize:11, fontWeight:600, padding:"2px 8px", borderRadius:999, background:"#f3e8ff", color:"#7c3aed" }}>👥 {coachLabel}</span>
                                    </td>

                                    {/* Paiement */}
                                    <td style={{ padding:"10px 14px" }}>
                                      {tranches.length > 0 ? (
                                        <div>
                                          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                                            <div style={{ flex:1, height:4, borderRadius:999, background:"#e5e7eb", overflow:"hidden", minWidth:80 }}>
                                              <div style={{ height:"100%", borderRadius:999, background: pctTranches >= 100 ? "#22c55e" : pctTranches > 0 ? BET_COLOR : "#e5e7eb", width:`${Math.min(100,pctTranches)}%` }} />
                                            </div>
                                            <span style={{ fontSize:10, fontWeight:700, color: pctTranches >= 100 ? "#22c55e" : "#64748b", whiteSpace:"nowrap" }}>{pctTranches}%</span>
                                          </div>
                                          <div style={{ fontSize:10, color:(tranches_total-tranches_paye)>0?"#ef4444":"#22c55e", fontWeight:600 }}>
                                            {(tranches_total-tranches_paye)>0 ? `${Number(tranches_total-tranches_paye).toLocaleString("fr-FR")} F restants` : "✓ Soldé"}
                                          </div>
                                        </div>
                                      ) : pmts.length > 0 ? (
                                        <div>
                                          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                                            <div style={{ flex:1, height:4, borderRadius:999, background:"#e5e7eb", overflow:"hidden", minWidth:80 }}>
                                              <div style={{ height:"100%", borderRadius:999, background: reste<=0?"#22c55e":totalReçu>0?BET_COLOR:"#e5e7eb", width:totalDu>0?`${Math.min(100,Math.round((totalReçu/totalDu)*100))}%`:"0%" }} />
                                            </div>
                                            <span style={{ fontSize:10, fontWeight:700, color:reste<=0?"#22c55e":"#64748b", whiteSpace:"nowrap" }}>{totalDu>0?Math.min(100,Math.round((totalReçu/totalDu)*100)):0}%</span>
                                          </div>
                                          <div style={{ fontSize:10, color:reste>0?"#ef4444":"#22c55e", fontWeight:600 }}>
                                            {reste>0 ? `${Number(reste).toLocaleString("fr-FR")} F restants` : "✓ Soldé"}
                                          </div>
                                        </div>
                                      ) : (
                                        <span style={{ fontSize:11, color:"#94a3b8" }}>-</span>
                                      )}
                                    </td>

                                    {/* Assiduité */}
                                    <td style={{ padding:"10px 14px" }}>
                                      {seances > 0 ? (
                                        <div>
                                          <span style={{ fontSize:12, fontWeight:700, color: tauxParticipation >= 80 ? "#15803d" : tauxParticipation >= 60 ? "#d97706" : "#ef4444" }}>
                                            {tauxParticipation}%
                                          </span>
                                          <div style={{ fontSize:10, color:"#94a3b8" }}>{presences}/{seances} séances</div>
                                        </div>
                                      ) : (
                                        <span style={{ fontSize:11, color:"#cbd5e1" }}>-</span>
                                      )}
                                    </td>

                                    {/* Alertes */}
                                    <td style={{ padding:"10px 14px", textAlign:"center" }}>
                                      <div style={{ display:"flex", gap:4, justifyContent:"center", flexWrap:"wrap" }}>
                                        {(a.routing_statut === "onboarding_en_attente") && (
                                          <span title="En attente d'affectation Onboarding" style={{ fontSize:10, background:"#fffbeb", color:"#92400e", border:"1px solid #fde68a", borderRadius:99, padding:"2px 7px", fontWeight:700, whiteSpace:"nowrap" }}>⏳ Onboarding</span>
                                        )}
                                        {(a.routing_statut === "pa_en_attente") && (
                                          <span title="En attente d'affectation PA Advisor" style={{ fontSize:10, background:"#f5f3ff", color:"#6d28d9", border:"1px solid #ddd6fe", borderRadius:99, padding:"2px 7px", fontWeight:700, whiteSpace:"nowrap" }}>⏳ PA Advisor</span>
                                        )}
                                        {(a.routing_statut === "centre_assigne") && (
                                          <span title="Classe et coach assignés" style={{ fontSize:10, background:"#f5f3ff", color:"#7c3aed", border:"1px solid #ddd6fe", borderRadius:99, padding:"2px 7px", fontWeight:700, whiteSpace:"nowrap" }}>🏫 Assigné</span>
                                        )}
                                        {a.acces_bloque && <span title="Accès bloqué" style={{ fontSize:10, background:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5", borderRadius:99, padding:"2px 7px", fontWeight:700, whiteSpace:"nowrap" }}>🔒 Suspendu</span>}
                                        {hasAlertePmt && <span title="Retard de paiement">💳</span>}
                                        {hasRappelMensuel && (() => {
                                          const enRetard = joursRestantsMensuel < 0;
                                          const label = enRetard ? `⏰ Retard mensualité` : `🔔 Mensualité J-${joursRestantsMensuel}`;
                                          const c = enRetard ? "#991b1b" : joursRestantsMensuel <= 1 ? "#c2410c" : "#92400e";
                                          const bg = enRetard ? "#fee2e2" : joursRestantsMensuel <= 1 ? "#ffedd5" : "#fef3c7";
                                          const bd = enRetard ? "#fca5a5" : joursRestantsMensuel <= 1 ? "#fdba74" : "#fde68a";
                                          return <span title="Rappel de paiement mensuel actif" style={{ fontSize:10, background:bg, color:c, border:`1px solid ${bd}`, borderRadius:99, padding:"2px 7px", fontWeight:700, whiteSpace:"nowrap" }}>{label}</span>;
                                        })()}
                                        {hasAlerteAss && <span title="Alerte assiduité">⚠️</span>}
                                        {!a.routing_statut && !a.acces_bloque && !hasAlertePmt && !hasRappelMensuel && !hasAlerteAss && <span style={{ color:"#22c55e", fontSize:13 }}>✓</span>}
                                      </div>
                                    </td>

                                    {/* Action */}
                                    <td style={{ padding:"10px 14px", textAlign:"center" }} onClick={e => e.stopPropagation()}>
                                      <button
                                        onClick={() => { openApprenantDetail(a, selectedClasse); }}
                                        style={{ padding:"5px 12px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:7, fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}
                                      >
                                        Voir →
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* ── Pagination ── */}
                        {totalPages > 1 && (
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:16, padding:"10px 4px" }}>
                            <span style={{ fontSize:12, color:"#6b7280" }}>
                              {safePage * APP_PER_PAGE + 1}–{Math.min((safePage + 1) * APP_PER_PAGE, appFiltered.length)} sur {appFiltered.length} apprenants
                            </span>
                            <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                              <button onClick={() => setAppPage(0)} disabled={safePage===0} style={{ padding:"4px 8px", borderRadius:6, border:"1px solid #e2e8f0", background:safePage===0?"#f8fafc":"#fff", color:safePage===0?"#cbd5e1":"#374151", cursor:safePage===0?"default":"pointer", fontSize:13 }}>«</button>
                              <button onClick={() => setAppPage(p => Math.max(0,p-1))} disabled={safePage===0} style={{ padding:"4px 10px", borderRadius:6, border:"1px solid #e2e8f0", background:safePage===0?"#f8fafc":"#fff", color:safePage===0?"#cbd5e1":"#374151", cursor:safePage===0?"default":"pointer", fontSize:13 }}>‹</button>
                              {Array.from({ length: totalPages }, (_, i) => {
                                if (totalPages <= 7 || Math.abs(i - safePage) <= 1 || i === 0 || i === totalPages - 1) {
                                  return (
                                    <button key={i} onClick={() => setAppPage(i)}
                                      style={{ padding:"4px 10px", borderRadius:6, border:`1px solid ${i===safePage?BET_COLOR:"#e2e8f0"}`, background:i===safePage?BET_COLOR+"18":"#fff", color:i===safePage?BET_COLOR:"#374151", fontWeight:i===safePage?700:400, cursor:"pointer", fontSize:12 }}>
                                      {i+1}
                                    </button>
                                  );
                                }
                                if (Math.abs(i - safePage) === 2) return <span key={i} style={{ padding:"0 2px", color:"#94a3b8" }}>…</span>;
                                return null;
                              })}
                              <button onClick={() => setAppPage(p => Math.min(totalPages-1,p+1))} disabled={safePage===totalPages-1} style={{ padding:"4px 10px", borderRadius:6, border:"1px solid #e2e8f0", background:safePage===totalPages-1?"#f8fafc":"#fff", color:safePage===totalPages-1?"#cbd5e1":"#374151", cursor:safePage===totalPages-1?"default":"pointer", fontSize:13 }}>›</button>
                              <button onClick={() => setAppPage(totalPages-1)} disabled={safePage===totalPages-1} style={{ padding:"4px 8px", borderRadius:6, border:"1px solid #e2e8f0", background:safePage===totalPages-1?"#f8fafc":"#fff", color:safePage===totalPages-1?"#cbd5e1":"#374151", cursor:safePage===totalPages-1?"default":"pointer", fontSize:13 }}>»</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Chat panel ancré en bas si ouvert */}
                  {chatAssignation && activeTab === "apprenants" && (
                    <div style={{ marginTop:24 }}>
                      <ProspectChatPanel assignation={chatAssignation} commercialId={myCommercialId} onClose={() => setChatAssignation(null)} />
                    </div>
                  )}

                </div>
              );
            })()}

            {activeTab === "notifications" && (
              <div style={{ padding: "24px 0" }}>
                <NotificationsTab userId={profil?.id} accentColor="#0891b2" />
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ══ MODAL APPRENANT ══ */}
      {apprenantModal && (() => {
        const a = apprenantModal;
        const initiales = (a.prospect_nom || "?").split(" ").map(p=>p[0]).slice(0,2).join("").toUpperCase();
        const pmts = paiements.filter(p => p.assignationId === a.id);
        const totalReçu = pmts.reduce((s,p)=>s+(p.montantReçu||0),0);
        const totalDu = pmts.length > 0 ? Math.max(...pmts.map(p => p.montantDu || 0)) : 0;
        const testProspect = findTestForProspect(a);
        // Fallback offre : pour prospects web sans inscription_offre, chercher dans paiements CinetPay par email
        const pmtByEmail = !a.inscription_offre
          ? paiements.find(p => p.email && a.prospect_email && p.email.toLowerCase() === a.prospect_email.toLowerCase() && p.offre)
          : null;
        const offreEffective = a.inscription_offre || pmtByEmail?.offre || "";
        // Fallback niveau : si niveau_client non renseigné, utiliser le test de niveau
        const niveauEffectif = a.niveau_client || testProspect?.niveau || "";
        const docsApp = a.documents_dossier || [];
        const plan = a.plan_paiement || {};
        const tranches = plan.tranches || [];
        const accesBloque = a.acces_bloque || false;
        /* ── LA CLASSE EN COURS FAIT FOI, PAS LE DOSSIER ──────────────────────────────────
           `a.type_cours` et `a.centre_nom` viennent de `assignations_parcours` — le DOSSIER —
           réécrit APRÈS coup par les transferts, et parfois incomplètement. Constaté le
           07/10/2026 : un apprenant migré vers « Anglais B2 soir WITH ISMAEL » (classe hybride,
           SANS centre) gardait « Hybride · BET Angré », le centre de son ANCIENNE classe. Son
           assistante lisait donc un centre où il ne va plus, alors que le bloc « Classe & coach
           assignés » juste en dessous montrait la bonne classe : les deux se contredisaient.

           `commClasseInfo` porte la classe réellement en cours (`apprenant-by-email`).
           LE DOSSIER RESTE LE REPLI : un apprenant pas encore affecté n'a pas de classe, et
           c'est alors le canal vendu qui renseigne.

           ICI SEULEMENT, et c'est volontaire. `commClasseInfo` désigne la fiche OUVERTE : s'en
           servir dans la liste ou dans les autres modals (qui ont leur propre cible) collerait
           la classe de cet apprenant à tous les autres. Les cinq autres `modeLabel` de ce
           fichier restent donc sur le dossier. */
        const canalEffectif  = commClasseInfo?.type_cours || a.type_cours;
        const centreEffectif = commClasseInfo ? (commClasseInfo.centre_nom || null) : a.centre_nom;
        const modeLabel = libelleCanal(canalEffectif, centreEffectif);
        const coachLabel = ["groupe","standard"].includes(a.type_coaching) ? "Groupe" : a.type_coaching === "prive" ? "Privé" : "-";
        const today = new Date(); today.setHours(0,0,0,0);
        const sp = a.suivi_presences || {};
        const prog = progressionNotes[a.id] || {};
        const progSessions = prog.sessions ?? (sp.sessions || []);

        const TABS = [
          { id:"infos",       label:"📋 Informations" },
          { id:"acces",       label:"🔑 Accès" },
          ...(a.profil_type === "enfant" ? [{ id:"parent", label:"👨‍👩‍👧 Parent" }] : []),
          { id:"presences",   label:"✅ Présences" },
          // Les tests ont leur onglet : ils étaient noyés dans « Informations », qui n'en
          // montrait qu'un seul. Le compteur évite d'ouvrir l'onglet pour découvrir qu'il est vide.
          { id:"niveau",      label:`🎓 Tests de niveau${tousLesTestsBrutsDe(a).length ? ` (${tousLesTestsBrutsDe(a).length})` : ""}` },
          { id:"paiement",    label:"💳 Paiement" },
          { id:"docs",        label:"📎 Documents" },
          { id:"notes",       label:"📝 Notes" },
          { id:"historique",  label:"🕓 Historique" },
        ];

        const ficheApprenantData = () => {
          const num = `BET-FA-${String(a.id||"").slice(0,8).toUpperCase()}`;
          const milestones = prog.jalons || progSessions.filter(s => s.jalon) || [];
          const infoRow = (lbl, val) => val==null || val==="" ? "" : `<div class="info-row"><span class="info-label">${lbl}</span><span class="info-value">${val}</span></div>`;
          return { num, milestones, infoRow };
        };

        const exporterFicheApprenantPDF = () => {
          try {
            const { num, milestones, infoRow } = ficheApprenantData();
            const section = (title, rows) => {
              const body = rows.filter(Boolean).join("");
              if (!body) return "";
              return `<div class="section-title">${title}</div><div class="info-grid">${body}</div>`;
            };
            const subTable = (title, head, lignes) => {
              if (!lignes.length) return "";
              return `<div class="section-title">${title}</div>
                <table><thead><tr>${head.map(h=>`<th>${h}</th>`).join("")}</tr></thead>
                <tbody>${lignes.map(l => `<tr>${l.map((c,i)=>`<td${i===l.length-1&&typeof c!=="string"?' class="td-right"':''}>${c}</td>`).join("")}</tr>`).join("")}</tbody>
                </table>`;
            };

            genererPDF(`
              <div class="doc-header">
                <div>
                  <div class="doc-header-logo">BET Languages</div>
                  <div class="doc-header-sub">Centre de langues - Abidjan, Côte d'Ivoire</div>
                  <div class="doc-header-sub">📧 contact@binniesenglish.com · 📞 +225 27 22 40 71 99</div>
                </div>
                <div class="doc-header-right">
                  N° ${num}<br/>Émis le ${fmtDatePDF(new Date().toISOString())}
                </div>
              </div>
              <div class="doc-title-band">👤 FICHE APPRENANT - ${a.prospect_nom||""}</div>
              <div class="doc-body">

                ${section("👤 Informations personnelles", [
                  infoRow("Nom complet", a.prospect_nom),
                  infoRow("Email", a.prospect_email),
                  infoRow("Téléphone", a.prospect_telephone),
                  infoRow("Niveau", niveauEffectif),
                  /* L'ÉVOLUTION DANS LE PDF AUSSI. Le document sert à transmettre le dossier -
                     au conseiller pédagogique, à un coach : « B1 → C1 » y explique un
                     changement de classe que le seul niveau courant laisse inexpliqué. */
                  infoRow("Évolution du niveau", texteEvolutionNiveau(tousLesTestsDe(a))),
                  infoRow("Mode", modeLabel),
                  infoRow("Coaching", coachLabel),
                  infoRow("Assistante", a.assistante_nom),
                  infoRow("Date de conversion", a.updated_at ? fmtDatePDF(a.updated_at) : null),
                  infoRow("Source", a.source),
                ])}

                ${/* L'OBJECTIF DU CLIENT, en pleine largeur et non en ligne de tableau.
                      C'est une phrase, pas une valeur : serrée dans une demi-colonne à côté
                      d'un libellé, elle se casse en quatre lignes illisibles. Et c'est la
                      seule information de cette fiche qui dise POURQUOI la personne est là -
                      elle mérite d'être lue, pas survolée.
                      Placée avant la formation : on lit l'intention, puis ce qui a été vendu. */
                  (a.objectif_client || "").trim()
                    ? `<div class="section-title">🎯 Objectif du client</div>
                       <div style="background:#f5f3ff;border:1px solid #ddd6fe;border-radius:8px;padding:11px 14px;margin-bottom:18px;font-size:13px;line-height:1.6;color:#0f172a;white-space:pre-wrap">${a.objectif_client}</div>`
                    : ""}

                ${section("🎓 Formation", [
                  infoRow("Offre souscrite", offreEffective),
                  infoRow("Date de début", a.date_debut_formation ? fmtDatePDF(a.date_debut_formation) : null),
                  commClasseInfo ? infoRow("Classe", commClasseInfo.nom) : "",
                  commClasseInfo ? infoRow("Coach", commClasseInfo.coach_nom || "Non assigné") : "",
                  commClasseInfo ? infoRow("Centre", commClasseInfo.centre_nom) : "",
                ])}

                ${a.profil_type === "enfant" ? section("👨‍👩‍👧 Parent / tuteur", [
                  infoRow("Nom", commParentForm.nom_parent),
                  infoRow("Téléphone", commParentForm.telephone_parent),
                  infoRow("Email", commParentForm.email_parent),
                  infoRow("Compte parent", commParentAccesInfo?.existe ? `Actif (${commParentAccesInfo.login_email})` : "Non créé"),
                ]) : ""}

                ${section("🔑 Accès à l'espace apprenant", [
                  infoRow("Compte", commAccesInfo?.has_account ? `<span class="badge badge-green">Actif</span>` : `<span class="badge badge-red">Non créé</span>`),
                  commAccesInfo?.has_account ? infoRow("Identifiant", commAccesInfo.profil?.email || a.prospect_email) : "",
                  commAccesInfo?.has_account ? infoRow("Mot de passe", commAccesInfo.profil?.mdp_temporaire ? "Temporaire (non changé)" : "Changé par l'apprenant") : "",
                  commAccesInfo?.has_account ? infoRow("Créé le", commAccesInfo.profil?.date_creation ? fmtDatePDF(commAccesInfo.profil.date_creation) : null) : "",
                  infoRow("Accès formation", accesBloque ? `<span class="badge badge-red">Bloqué</span>` : `<span class="badge badge-green">Actif</span>`),
                  blocageStatus ? infoRow("Absences comptabilisées", `${blocageStatus.nb_absences}/4`) : "",
                ])}

                ${commAppPres.length > 0 ? `<div class="section-title">📅 Assiduité${
                  new Set(commAppPres.map(p => p.classe_nom).filter(Boolean)).size > 1
                    ? ` <span style="font-weight:400;font-size:11px;color:#64748b">— sur l'ensemble du parcours</span>` : ""
                }</div><div class="kpi-row">
                  <div class="kpi-card"><div class="kpi-val">${commAppPres.length}</div><div class="kpi-lbl">Séances</div></div>
                  <div class="kpi-card"><div class="kpi-val">${commAppPres.filter(p=>p.statut==="present").length}</div><div class="kpi-lbl">Présences</div></div>
                  <div class="kpi-card"><div class="kpi-val">${commAppPres.filter(p=>p.statut==="absent").length}</div><div class="kpi-lbl">Absences</div></div>
                </div>` : ""}
                ${/* TOUS SES TESTS, pas seulement le dernier. Ce document part au conseiller
                      pédagogique et au coach : ils doivent voir d'où vient le niveau, et « A2 →
                      B1 » explique un changement de classe qu'un niveau isolé laisse sans
                      justification. Le plus récent d'abord, comme partout ailleurs. */""}
                ${subTable("📝 Tests de niveau", ["Date","Niveau","Score","Origine"],
                  tousLesTestsBrutsDe(a).map(t => [
                    (t.date || t.submitted_at) ? fmtDatePDF(t.date || t.submitted_at) : "-",
                    (t.niveau || t.level || "-"),
                    t.score != null ? `${t.score}%` : "-",
                    t.source === "oral" ? "Évalué en présentiel" : "Passé en ligne",
                  ]))}

                ${/* LA CLASSE DE CHAQUE SÉANCE, depuis que le détail couvre tout le parcours : sans
       elle, un transféré présente une liste de dates dont on ne sait plus laquelle
       appartient à quelle classe. La colonne n'apparaît que s'il y en a plusieurs. */
   new Set(commAppPres.map(p => p.classe_nom).filter(Boolean)).size > 1
     ? subTable("📅 Détail des séances", ["Date","Classe","Statut"],
         commAppPres.map(p => [p.date_seance ? fmtDatePDF(p.date_seance) : "-", p.classe_nom || "-", p.statut || "-"]))
     : subTable("📅 Détail des séances", ["Date","Statut"],
         commAppPres.map(p => [p.date_seance ? fmtDatePDF(p.date_seance) : "-", p.statut || "-"]))}

                ${subTable("📈 Progression / jalons", ["Date","Jalon","Détail"], milestones.map(m => [m.date ? fmtDatePDF(m.date) : "-", m.type || "-", `${m.valeur || ""}${m.commentaire ? " - "+m.commentaire : ""}`]))}

                ${(totalDu || totalReçu) ? `<div class="section-title">💰 Paiement</div><div class="kpi-row">
                  <div class="kpi-card"><div class="kpi-val">${totalDu ? totalDu.toLocaleString("fr-FR") : 0}</div><div class="kpi-lbl">Montant dû (FCFA)</div></div>
                  <div class="kpi-card"><div class="kpi-val">${totalReçu ? totalReçu.toLocaleString("fr-FR") : 0}</div><div class="kpi-lbl">Montant reçu (FCFA)</div></div>
                  <div class="kpi-card"><div class="kpi-val">${tranches.length}</div><div class="kpi-lbl">Tranches</div></div>
                </div>` : ""}
                ${subTable("💳 Détail des versements", ["Date","Montant reçu","Mode","Opérateur","Référence"], pmts.map(p => [
                  p.date ? fmtDatePDF(p.date) : "-",
                  p.montantReçu ? `${Number(p.montantReçu).toLocaleString("fr-FR")} FCFA` : "-",
                  p.mode || "-",
                  p.operateur || "-",
                  p.refTransaction || "-",
                ]))}
                ${subTable("💰 Échéances", ["Échéance","Montant","Statut"], tranches.map(t => [t.date_echeance ? fmtDatePDF(t.date_echeance) : "-", t.montant ? `${Number(t.montant).toLocaleString("fr-FR")} FCFA` : "-", t.statut || "-"]))}

                ${subTable("📎 Documents du dossier", ["Document","Type"], docsApp.map(d => [d.nom || "-", DOC_TYPES.find(dt=>dt.key===d.type)?.label || d.type || "-"]))}

                ${subTable("📝 Notes / évaluations de classe", ["Évaluation","Date","Note","Commentaire"], commAppEvals.map(ev => {
                  const r = ev.resultat || {};
                  return [ev.titre || "Évaluation", ev.date_evaluation ? fmtDatePDF(ev.date_evaluation) : "-", r.note!=null ? `${r.note}/${r.note_max ?? ev.note_max ?? "-"}` : "-", r.commentaire_coach || "-"];
                }))}

                <div class="doc-footer">
                  <span>BET Languages · Centre de langues agréé · Abidjan, Côte d'Ivoire</span>
                  <span>contact@binniesenglish.com · +225 27 22 40 71 99</span>
                </div>
              </div>
            `, `Fiche apprenant - ${a.prospect_nom||""}`);
            toast.success("Fiche exportée en PDF");
          } catch (e) { toast.error("Erreur lors de l'export PDF"); }
        };

        return (
          <div style={modalOverlay} onClick={() => setApprenantModal(null)}>
            <div style={{ ...modalBox, width:"90vw", maxWidth:"90vw", height:"90vh", maxHeight:"90vh", overflowY:"auto", padding:0 }} onClick={e=>e.stopPropagation()}>

              {/* Header */}
              <div style={{ background:BET_GRADIENT, padding:"20px 24px", borderRadius:"14px 14px 0 0", display:"flex", alignItems:"center", gap:14, position:"sticky", top:0, zIndex:10 }}>
                <div style={{ width:50, height:50, borderRadius:"50%", background:"rgba(255,255,255,0.18)", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:18, color:"#fff", flexShrink:0, overflow:"hidden", border:"2px solid rgba(255,255,255,0.3)" }}>
                  {(commAccesInfo?.profil?.avatar_url || a.avatar_url)
                    ? <img src={commAccesInfo?.profil?.avatar_url || a.avatar_url} alt={initiales} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                    : initiales}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:17, fontWeight:800, color:"#fff" }}>{a.prospect_nom || "-"}</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.65)", display:"flex", gap:10 }}>
                    {a.prospect_email && <span>{a.prospect_email}</span>}
                    {a.prospect_telephone && <span>📞 {a.prospect_telephone}</span>}
                  </div>
                </div>
                {accesBloque && <span style={{ background:"#ef4444", color:"#fff", borderRadius:999, fontSize:11, fontWeight:800, padding:"4px 12px" }}>🔒 Accès bloqué</span>}
                {/* LA MIGRATION EST UNE ACTION SUR LA FICHE, pas une information dans son corps.
                    Elle etait perdue en bas du contenu, sous un pave de texte ; a cote d'« Export
                    PDF » elle se trouve la ou l'on cherche les gestes possibles. */}
                {/* DEMANDER UN TRANSFERT DE CLASSE. Jusqu'ici réservé au coach, alors que
                    l'apprenant dit tout aussi souvent à son assistante — au comptoir ou au
                    téléphone — que la classe ne lui convient plus. Elle n'avait alors qu'un
                    message informel à envoyer à l'onboarding : hors circuit, sans trace, et
                    sans déclencher le test de niveau qui conditionne la nouvelle classe.
                    Elle ouvre le dossier, elle ne l'exécute pas : le test passe d'abord. */}
                {/* LE COACHING PRIVÉ N'EST PLUS EXCLU. Ces deux gestes valent pour TOUS les
                    apprenants : un apprenant en privé change de classe comme un autre, et il
                    peut tout autant passer du en ligne au présentiel - sa formule ne change
                    pas, c'est l'endroit où il la suit qui change.
                    Le canal vient de `a.type_cours`, le DOSSIER. Sa classe, elle, porte
                    `prive` (10 classes sur 12) : ce n'est pas un canal mais un type de
                    coaching, et le lire là donnerait toujours la même destination. */}
                {commClasseInfo?.ga_id && commClasseInfo.statut_ga === "actif" && (
                  <BoutonDemandeTransfert
                    gaId={commClasseInfo.ga_id}
                    nomApprenant={a.prospect_nom || "cet apprenant"}
                    demandeur="commerciale"
                    /* Le centre actuel ouvre le second choix du bouton — « changer de centre » —
                       et sert à retirer ce centre de la liste des destinations : s'y transférer
                       n'aurait aucun sens. Un dossier sans centre (en ligne, ou présentiel dont
                       le centre n'a jamais été renseigné) n'a que le changement de classe. */
                    centreActuel={a.centre_id || commClasseInfo?.centre_id || null}
                    /* L'assistante PRÉSENTIEL affecte elle-même la classe de ses apprenants —
                       elle est son propre onboarding. Lui dire le contraire la faisait attendre
                       une étape qui n'arrivait jamais. */
                    affecteElleMeme={isCommercialeCentre}
                    authHdrs={authHeaders}
                    onEmise={() => toast("Le placement test va faire passer le niveau.", { icon: "🧪" })}
                    styleBouton={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px",
                      background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.25)",
                      borderRadius:8, color:"#fff", fontSize:12.5, fontWeight:700, cursor:"pointer",
                      fontFamily:"inherit", whiteSpace:"nowrap" }} />
                )}
                {commClasseInfo?.ga_id && commClasseInfo.statut_ga === "actif" && (
                  <BoutonMigrationCanal
                    gaId={commClasseInfo.ga_id}
                    nomApprenant={a.prospect_nom || "cet apprenant"}
                    canalActuel={a.type_cours}
                    authHdrs={authHeaders}
                    onEmise={() => toast("Suivez l'avancement dans « Dossiers à réaffecter ».", { icon: "🔀" })}
                    styleBouton={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px",
                      background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.25)",
                      borderRadius:8, color:"#fff", fontSize:12.5, fontWeight:700, cursor:"pointer",
                      fontFamily:"inherit", whiteSpace:"nowrap" }} />
                )}
                <button onClick={exporterFicheApprenantPDF} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", background:"rgba(255,255,255,0.15)", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:700, whiteSpace:"nowrap", flexShrink:0 }}>📄 Export PDF</button>
                <button onClick={() => setApprenantModal(null)} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", width:30, height:30, borderRadius:"50%", cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>✕</button>
              </div>

              {/* Tabs */}
              <div style={{ display:"flex", borderBottom:"2px solid #e2e8f0", background:"#f8fafc", padding:"0 24px" }}>
                {TABS.map(t => (
                  <button key={t.id} onClick={() => setApprenantTab(t.id)}
                    style={{ padding:"12px 16px", border:"none", background:"none", cursor:"pointer", fontSize:13, fontWeight:apprenantTab===t.id?800:500, color:apprenantTab===t.id?"#0891b2":"#64748b", borderBottom:apprenantTab===t.id?"2.5px solid #0891b2":"2.5px solid transparent", marginBottom:-2, whiteSpace:"nowrap" }}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div style={{ padding:"24px" }}>

                {/* INFOS */}
                {apprenantTab === "infos" && (
                  <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

                    {/* ── Statut de routing ── (coaching privé exclu : son routage passe
                        exclusivement par le pipeline PA, cf. bloc plus bas gated sur
                        routing_statut === "assistante_a_traiter" - sans quoi ce bloc
                        générique du parcours standard permettait d'assigner une classe
                        à un dossier privé jamais validé par le PA) */}
                    {a.type_cours === "presentiel" && a.type_coaching !== "prive" && a.routing_statut === "centre_assigne" && (
                      <div style={{ background:"#f5f3ff", borderRadius:10, border:"1.5px solid #ddd6fe", padding:"12px 16px", display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontSize:18 }}>🏫</span>
                        <div>
                          <div style={{ fontSize:13, fontWeight:800, color:"#7c3aed" }}>Classe et coach assignés</div>
                          <div style={{ fontSize:11, color:"#6d28d9", marginTop:2 }}>
                            {a.suivi_demarrage?.classe_nom && <>📚 {a.suivi_demarrage.classe_nom}</>}
                            {a.suivi_demarrage?.coach_nom  && <> · 👩‍🏫 {a.suivi_demarrage.coach_nom}</>}
                          </div>
                        </div>
                      </div>
                    )}
                    {a.type_cours === "presentiel" && a.type_coaching !== "prive" && a.routing_statut !== "centre_assigne" && (
                      <div style={{ background:"#faf5ff", borderRadius:10, border:"1.5px solid #ddd6fe", padding:"12px 16px", display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontSize:18 }}>⏳</span>
                        <div>
                          <div style={{ fontSize:13, fontWeight:800, color:"#7c3aed" }}>En attente d'assignation à une classe</div>
                          <div style={{ fontSize:11, color:"#6d28d9", marginTop:2 }}>Utilisez le bouton "🏫 Assigner classe & coach" pour affecter cet apprenant à une classe et un formateur.</div>
                        </div>
                      </div>
                    )}
                    {a.type_cours !== "presentiel" && a.routing_statut === "onboarding_en_attente" && (
                      <div style={{ background:"#fffbeb", borderRadius:10, border:"1.5px solid #fde68a", padding:"12px 16px", display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontSize:18 }}>⏳</span>
                        <div>
                          <div style={{ fontSize:13, fontWeight:800, color:"#92400e" }}>En attente d'affectation à une classe</div>
                          <div style={{ fontSize:11, color:"#b45309", marginTop:2 }}>Le dossier a été transmis à l'Onboarding - en attente d'affectation à une classe et d'un coach.</div>
                        </div>
                      </div>
                    )}
                    {a.type_cours !== "presentiel" && a.routing_statut === "pa_en_attente" && (
                      <div style={{ background:"#f5f3ff", borderRadius:10, border:"1.5px solid #ddd6fe", padding:"12px 16px", display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontSize:18 }}>⏳</span>
                        <div>
                          <div style={{ fontSize:13, fontWeight:800, color:"#6d28d9" }}>En attente d'affectation d'un coach privé</div>
                          <div style={{ fontSize:11, color:"#7c3aed", marginTop:2 }}>Le dossier a été transmis au PA Advisor - en attente d'affectation d'un coach.</div>
                        </div>
                      </div>
                    )}

                    {/* ── Coaching privé : pipeline assistante → PA → paiement (workflow "Cours Privé") ── */}
                    {a.type_coaching === "prive" && a.dossier_statut && (() => {
                      // Un paiement en ligne (TouchPoint) peut arriver AVANT que le PA n'ait
                      // validé le dossier - dossier_statut reste alors bloqué sur
                      // "paiement_en_attente" (écrit par la validation PA) alors que l'argent a
                      // déjà été reçu. On affiche l'état réel du paiement plutôt que le seul
                      // dossier_statut, pour ne plus jamais dire "activez le paiement" à tort.
                      const statutAffiche = (a.dossier_statut === "paiement_en_attente" && a.statut_paiement === "payé")
                        ? "paiement_recu"
                        : a.dossier_statut;
                      return (
                      <div style={{
                        background: statutAffiche==="invalide" ? "#fef2f2" : statutAffiche==="en_attente_validation" ? "#fffbeb" : "#eff6ff",
                        borderRadius:10, border:`1.5px solid ${statutAffiche==="invalide" ? "#fecaca" : statutAffiche==="en_attente_validation" ? "#fde68a" : "#bfdbfe"}`,
                        padding:"12px 16px", display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontSize:18 }}>{ { en_attente_validation:"⏳", valide:"✅", invalide:"❌", paiement_en_attente:"💳", paiement_recu:"💰" }[statutAffiche] || "ℹ️" }</span>
                        <div>
                          <div style={{ fontSize:13, fontWeight:800, color: statutAffiche==="invalide" ? "#dc2626" : "#1d4ed8" }}>
                            {{
                              en_attente_validation: "Dossier en attente de validation par le PA",
                              valide: "Dossier validé - paiement à activer",
                              invalide: "Dossier invalidé par le PA",
                              paiement_en_attente: "Validé - activez le paiement avec le client",
                              paiement_recu: "Paiement reçu - en attente de vérification du PA",
                            }[statutAffiche] }
                          </div>
                          {statutAffiche === "invalide" && a.motif_invalidation && (
                            <div style={{ fontSize:11, color:"#b91c1c", marginTop:2 }}>Motif : {a.motif_invalidation}</div>
                          )}
                        </div>
                      </div>
                      );
                    })()}
                    {(a.type_coaching === "prive" || a.type_coaching === "groupe") && a.routing_statut === "assistante_a_traiter" && (
                      <div style={{ background:"#f0fdf4", borderRadius:10, border:"1.5px solid #bbf7d0", padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <span style={{ fontSize:18 }}>🏢</span>
                          <div>
                            <div style={{ fontSize:13, fontWeight:800, color:"#15803d" }}>À finaliser - créez ou affectez la classe</div>
                            <div style={{ fontSize:11, color:"#166534", marginTop:2 }}>
                              {a.type_coaching === "groupe"
                                ? "Importé sans classe précisée - affectez cet apprenant à une classe existante."
                                : `Le PA a vérifié le paiement. Affectez cet apprenant à une classe ${a.type_cours === "domicile" ? "à domicile" : "présentielle"}.`}
                            </div>
                          </div>
                        </div>
                        <button onClick={() => { setApprenantModal(null); setAssignCentreTarget(a); setShowAssignCentreModal(true); }}
                          style={{ padding:"7px 14px", borderRadius:8, border:"none", background:"#16a34a", color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer", whiteSpace:"nowrap" }}>
                          🏫 Assigner classe & coach
                        </button>
                      </div>
                    )}

                    {/* ── Formule de groupe choisie par le client ──────────────────────────
                        Le client choisit sa formule (3×/semaine, week-end…) sur le site, et
                        c'est ce tarif-là qui est facturé. Le dossier la portait bien
                        (`suivi_demarrage.formule_groupe`) mais aucun écran ne l'affichait :
                        seul le coaching privé avait son bloc de détail, si bien que
                        l'assistante ne savait pas quel rythme transmettre à l'onboarding. ── */}
                    {/* ── Cycle commercial ─────────────────────────────────────────────
                        AUCUN BOUTON, volontairement. Les étapes sont déduites de gestes qui
                        existent déjà : ouvrir le dossier vaut prise en charge, un versement
                        ou une transmission vaut conversion, une inscription saisie à la main
                        vaut les deux. Demander trois clics par dossier aurait garanti qu'on
                        ne les fasse plus au bout d'une semaine — et un indicateur qu'on ne
                        remplit pas vaut moins que pas d'indicateur du tout. ── */}
                    {(() => {
                      /* CE BLOC DÉCRIT LA PHASE PROSPECT, et s'arrête avec elle. Une fois le
                         dossier converti - a fortiori quand l'apprenant a sa classe et son
                         coach - il n'apprend plus rien et donne l'impression que le dossier
                         est encore à traiter. Les dates de cycle restent consultables dans
                         l'onglet Historique. */
                      const etat = a.cycle_statut || "nouveau";
                      if (etat === "converti" || a.converti_at) return null;
                      const ETATS = {
                        nouveau:  { lib:"En attente de prise en charge", fond:"#F1F5F9", bord:"#E2E8F0", txt:"#475569" },
                        qualifie: { lib:"Pris en charge", fond:"#EFF6FF", bord:"#BFDBFE", txt:"#1D4ED8" },
                        converti: { lib:"Converti",  fond:"#F0FDF4", bord:"#BBF7D0", txt:"#15803D" },
                        perdu:    { lib:"Sans suite", fond:"#FEF2F2", bord:"#FECACA", txt:"#B91C1C" },
                      };
                      // « Pris en charge » n'est pas un état stocké : c'est un dossier encore
                      // nouveau, mais que quelqu'un a déjà ouvert.
                      const affiche = etat === "nouveau" && a.premier_contact_at ? "qualifie" : etat;
                      const e = ETATS[affiche] || ETATS.nouveau;
                      const fmt = (d) => d ? new Date(d).toLocaleString("fr-FR", { dateStyle:"short", timeStyle:"short" }) : null;
                      const delaiH = a.premier_contact_at && a.created_at
                        ? (new Date(a.premier_contact_at) - new Date(a.created_at)) / 3600000 : null;
                      return (
                        <div style={{ background:"#fff", border:`1.5px solid ${e.bord}`, borderRadius:12, padding:"14px 18px" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10, flexWrap:"wrap" }}>
                            <span style={{ fontSize:16 }}>🔄</span>
                            <div style={{ fontSize:13, fontWeight:800, color:"#0f172a" }}>Cycle commercial</div>
                            <span style={{ fontSize:11, fontWeight:800, padding:"2px 10px", borderRadius:99,
                              background:e.fond, color:e.txt, border:`1px solid ${e.bord}` }}>{e.lib}</span>
                            <span style={{ marginLeft:"auto", fontSize:10.5, color:"#94a3b8" }}>suivi automatique</span>
                          </div>
                          <div style={{ display:"flex", gap:16, flexWrap:"wrap", fontSize:11.5, color:"#64748b" }}>
                            <span>Arrivé le <strong style={{ color:"#334155" }}>{fmt(a.created_at) || "-"}</strong></span>
                            {a.premier_contact_at && (
                              <span>Pris en charge le <strong style={{ color:"#334155" }}>{fmt(a.premier_contact_at)}</strong>
                                {delaiH !== null && (
                                  <strong style={{ color: delaiH <= 2 ? "#15803d" : delaiH <= 24 ? "#b45309" : "#b91c1c", marginLeft:6 }}>
                                    {delaiH < 1 ? `${Math.round(delaiH * 60)} min` : `${delaiH.toFixed(1)} h`}
                                  </strong>
                                )}
                              </span>
                            )}
                            {a.converti_at && <span>Converti le <strong style={{ color:"#15803d" }}>{fmt(a.converti_at)}</strong></span>}
                          </div>
                        </div>
                      );
                    })()}

                    {(() => {
                      const fg = a.suivi_demarrage?.formule_groupe;
                      const packSeul = !fg && a.type_coaching !== "prive" ? a.suivi_demarrage?.pack_coaching : null;
                      if (!fg && !packSeul) return null;
                      const canal = fg?.canal === "presentiel" ? "🏢 En présentiel" : fg?.canal === "en_ligne" ? "💻 En ligne" : null;
                      return (
                        <div style={{ background:"#f0fdf4", borderRadius:12, padding:"14px 18px", border:"1.5px solid #bbf7d0" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                            <span style={{ fontSize:16 }}>👥</span>
                            <div style={{ fontSize:13, fontWeight:800, color:"#15803d" }}>Formule choisie par le client</div>
                          </div>
                          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                            <div style={{ minWidth:0 }}>
                              <div style={{ fontSize:14, fontWeight:800, color:"#0f172a" }}>{fg?.label || packSeul}</div>
                              {fg?.frequence && <div style={{ fontSize:12, color:"#475569", marginTop:2 }}>{fg.frequence}</div>}
                              {canal && <div style={{ fontSize:11.5, color:"#64748b", marginTop:3 }}>{canal}</div>}
                              {a.suivi_demarrage?.formation_souhaitee && (
                                <div style={{ fontSize:11.5, color:"#64748b", marginTop:3 }}>📚 {a.suivi_demarrage.formation_souhaitee}</div>
                              )}
                            </div>
                            {(fg?.tarif || a.suivi_demarrage?.cout_formation) > 0 && (
                              <div style={{ textAlign:"right", flexShrink:0 }}>
                                <div style={{ fontSize:15, fontWeight:800, color:"#15803d" }}>
                                  {Number(fg?.tarif || a.suivi_demarrage.cout_formation).toLocaleString("fr-FR")} FCFA
                                </div>
                                <div style={{ fontSize:10.5, color:"#64748b", fontWeight:700 }}>/ mois</div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* ── Coaching privé : "Besoin du client" - l'assistante reçoit le dossier
                        en premier désormais, c'est elle qui le renseigne (le PA le lit ensuite
                        en lecture seule avant de valider). ── */}
                    {a.type_coaching === "prive" && (() => {
                      const lieuRempli = dossierPriveForm.lieu === "domicile"
                        ? !!dossierPriveForm.adresse_coaching
                        : dossierPriveForm.lieu === "en_ligne"
                        ? true
                        : !!dossierPriveForm.centre_coaching;
                      const dossierComplet = !!(
                        dossierPriveForm.niveau &&
                        dossierPriveForm.formation &&
                        dossierPriveForm.type_coaching &&
                        lieuRempli &&
                        (dossierPriveForm.disponibilites?.horaires?.length > 0)
                      );
                      const locked = a.statut === "converti";
                      const disabledStyle = locked ? { background:"#f1f5f9", color:"#64748b", cursor:"not-allowed", opacity:0.75 } : {};
                      const centresListe = (() => {
                        try {
                          const s = localStorage.getItem("bet_centres_master");
                          if (s) {
                            const parsed = JSON.parse(s).filter(c => c.actif !== false).map(c => ({ key:c.key, name:c.name }));
                            if (parsed.length) return parsed;
                          }
                        } catch {}
                        return [
                          { key:"angre",    name:"BET Angré"    },
                          { key:"bouake",   name:"BET Bouaké"   },
                          { key:"plateaux", name:"BET II Plateaux" },
                          { key:"yopougon", name:"BET Yopougon" },
                          { key:"koumassi", name:"BET Koumassi" },
                          { key:"abatta",   name:"BET Abatta"   },
                        ];
                      })();
                      const offresPrivees = catalogueOffres.filter(o => (o.label||"").toLowerCase().includes("priv") || (o.categorie === "presentiel"));
                      /* Les formules de CETTE offre, si elle en définit - sinon la grille
                         globale. C'est ce qui permet au SuperAdmin d'en ajouter une
                         troisième sans que cet écran l'ignore. */
                      const grillePriveEff = grillePriveDeLOffre(catalogueOffres, dossierPriveForm.formation || a.inscription_offre, coachingPriveConfig);
                      return (
                        <div style={{ background: dossierComplet ? "#f0fdf4" : "#fffbeb", borderRadius:12, padding:"16px 18px", border:`1.5px solid ${dossierComplet?"#bbf7d0":"#fde68a"}` }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                            <span style={{ fontSize:16 }}>{dossierComplet ? "✅" : "⚠️"}</span>
                            <div style={{ fontSize:13, fontWeight:800, color: dossierComplet ? "#15803d" : "#92400e" }}>
                              Besoin du client {dossierComplet ? "(renseigné)" : "(à compléter avant transmission au PA)"}
                            </div>
                          </div>
                          {locked && (
                            <div style={{ display:"flex", alignItems:"center", gap:8, background:"#eff6ff", border:"1.5px solid #bfdbfe", borderRadius:9, padding:"10px 14px", marginBottom:12, fontSize:12, color:"#1d4ed8", fontWeight:600 }}>
                              <span style={{ fontSize:15 }}>🔒</span>
                              <span>Dossier converti - verrouillé.</span>
                            </div>
                          )}
                          {/* LA FAMILLE, sur la fiche que la commerciale rouvre.
                              Elle a saisi un couple ou une famille à l'inscription ; rouvrir
                              le dossier ne lui montrait qu'un nom. Impossible de vérifier, de
                              corriger une coordonnée, ou même de se rappeler qui est concerné
                              avant de transmettre au PA. */}
                          {(() => {
                            const membresF = (a.suivi_demarrage?.membres_famille || [])
                              .filter(m => (m?.prenom || "").trim() || (m?.nom || "").trim());
                            if (membresF.length === 0) return null;
                            const sansNiveau = membresF.filter(m => !m?.niveau).length;
                            return (
                              <div style={{ background:"#eff6ff", border:"1.5px solid #bfdbfe", borderRadius:10, padding:"11px 14px", marginBottom:10 }}>
                                <div style={{ fontSize:10.5, fontWeight:800, color:"#1d4ed8", textTransform:"uppercase", letterSpacing:".04em", marginBottom:7 }}>
                                  👥 Coaching en famille · {1 + membresF.length} apprenants
                                </div>
                                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                                  <span style={{ fontSize:11.5, fontWeight:700, padding:"3px 10px", borderRadius:999, background:"#1d4ed8", color:"#fff" }}>
                                    {a.prospect_nom} · payeur
                                  </span>
                                  {membresF.map((m, i) => (
                                    <span key={i} style={{ fontSize:11.5, fontWeight:700, padding:"3px 10px", borderRadius:999, background:"#dbeafe", color:"#1e40af" }}>
                                      {[m.prenom, m.nom].filter(Boolean).join(" ")}
                                      {m.lien ? ` · ${m.lien}` : ""}
                                      {m.niveau ? ` · ${m.niveau}` : ""}
                                    </span>
                                  ))}
                                </div>
                                {/* LE NIVEAU DE CHAQUE PERSONNE conditionne l'affectation : un coach
                                    ne prépare pas la même séance pour un A1 et un C1 assis côte à
                                    côte. Un niveau manquant se signale ici, pendant qu'il est encore
                                    temps de faire passer le test - pas après l'affectation. */}
                                {sansNiveau > 0 && (
                                  <div style={{ marginTop:8, fontSize:11, color:"#92400e", background:"#fffbeb",
                                    border:"1px solid #fde68a", borderRadius:7, padding:"7px 10px", lineHeight:1.55 }}>
                                    ⚠️ {sansNiveau} personne{sansNiveau > 1 ? "s" : ""} sans niveau évalué.
                                    Faites passer le test depuis l'onglet « Tests de niveau », puis renseignez-le ici —
                                    le PA en a besoin pour affecter un coach.
                                  </div>
                                )}
                                <div style={{ fontSize:10, color:"#64748b", marginTop:7, lineHeight:1.5 }}>
                                  Un seul dossier, un seul montant, un seul échéancier. Les accès sont créés à l'affectation de la classe.
                                </div>
                              </div>
                            );
                          })()}
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                              <label style={{ fontSize:11, fontWeight:700, color:"#374151" }}>Niveau détecté *</label>
                              <div style={{ display:"flex", gap:6 }}>
                                <select value={dossierPriveForm.niveau} onChange={e=>setDossierPriveForm(prev=>({...prev, niveau:e.target.value}))}
                                  disabled={locked} style={{ flex:1, padding:"8px 10px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, outline:"none", ...disabledStyle }}>
                                  <option value="">- Choisir -</option>
                                  {NIVEAUX.map(n=><option key={n} value={n}>{n}</option>)}
                                </select>
                                <button type="button" onClick={verifierTestNiveauCommercial} disabled={locked || verifyingTestNiveau}
                                  title="Récupérer le niveau depuis le test passé par le prospect"
                                  style={{ padding:"7px 10px", borderRadius:7, border:`1.5px solid ${locked?"#e2e8f0":"#16a34a"}`, background: locked ? "#f1f5f9" : "#f0fdf4", color: locked ? "#94a3b8" : "#16a34a", fontSize:11, fontWeight:700, cursor: locked ? "not-allowed" : "pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                                  {verifyingTestNiveau ? "…" : "🧪 Vérifier"}
                                </button>
                              </div>
                            </div>
                            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                              <label style={{ fontSize:11, fontWeight:700, color:"#374151" }}>Formation souhaitée *</label>
                              <select value={dossierPriveForm.formation} onChange={e=>setDossierPriveForm(prev=>({...prev, formation:e.target.value}))}
                                disabled={locked} style={{ padding:"8px 10px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, outline:"none", ...disabledStyle }}>
                                <option value="">- Choisir une formation privée -</option>
                                {offresPrivees.map(o => <option key={o.label} value={o.label}>{o.label}{o.prix_str ? ` - ${o.prix_str}` : ""}</option>)}
                                {dossierPriveForm.formation && !offresPrivees.some(o=>o.label===dossierPriveForm.formation) && (
                                  <option value={dossierPriveForm.formation}>📝 {dossierPriveForm.formation}</option>
                                )}
                              </select>
                            </div>
                          </div>
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                              <label style={{ fontSize:11, fontWeight:700, color:"#374151" }}>Type de coaching *</label>
                              <select value={dossierPriveForm.type_coaching} onChange={e=>{
                                const tc = e.target.value;
                                const lieuKey = dossierPriveForm.lieu==="domicile"?"domicile":dossierPriveForm.lieu==="en_ligne"?"en_ligne":"cabinet";
                                const tarif = grillePriveEff[tc]?.[lieuKey] || 0;
                                setDossierPriveForm(prev=>({...prev, type_coaching:tc, cout_formation:String(tarif)}));
                              }} disabled={locked} style={{ padding:"8px 10px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, outline:"none", ...disabledStyle }}>
                                {/* Les formules viennent de l'offre : autant que le SuperAdmin en a créées. */}
                                {Object.entries(grillePriveEff).map(([cle, g]) => (
                                  <option key={cle} value={cle}>{g.label}{g.frequence ? ` - ${g.frequence}` : ""}</option>
                                ))}
                              </select>
                            </div>
                            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                              <label style={{ fontSize:11, fontWeight:700, color:"#374151" }}>Lieu du coaching *</label>
                              <select value={dossierPriveForm.lieu} onChange={e=>{
                                const lieu = e.target.value;
                                const lieuKey = lieu==="domicile"?"domicile":lieu==="en_ligne"?"en_ligne":"cabinet";
                                const tarif = grillePriveEff[dossierPriveForm.type_coaching]?.[lieuKey] || 0;
                                setDossierPriveForm(prev=>({...prev, lieu, cout_formation:String(tarif)}));
                              }} disabled={locked} style={{ padding:"8px 10px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, outline:"none", ...disabledStyle }}>
                                <option value="cabinet">Au cabinet</option>
                                <option value="domicile">À domicile</option>
                                <option value="en_ligne">En ligne</option>
                              </select>
                            </div>
                          </div>
                          {dossierPriveForm.cout_formation > 0 && (
                            <div style={{ marginBottom:10, fontSize:12, fontWeight:700, color:"#15803d" }}>💰 {Number(dossierPriveForm.cout_formation).toLocaleString("fr-FR")} FCFA/mois</div>
                          )}
                          {dossierPriveForm.lieu === "domicile" ? (
                            <div style={{ display:"flex", flexDirection:"column", gap:4, marginBottom:10 }}>
                              <label style={{ fontSize:11, fontWeight:700, color:"#374151" }}>🏠 Adresse du domicile *</label>
                              <input type="text" value={dossierPriveForm.adresse_coaching}
                                onChange={e=>setDossierPriveForm(prev=>({...prev, adresse_coaching:e.target.value}))}
                                disabled={locked} placeholder="Ex : Cocody Riviera 3, Rue des Jardins, Abidjan"
                                style={{ padding:"8px 10px", border:"1.5px solid #fde68a", borderRadius:7, fontSize:12, outline:"none", ...(locked?disabledStyle:{background:"#fffbeb"}) }} />
                            </div>
                          ) : dossierPriveForm.lieu === "en_ligne" ? (
                            <div style={{ display:"flex", alignItems:"center", gap:8, background:"#eff6ff", border:"1.5px solid #bfdbfe", borderRadius:9, padding:"10px 14px", marginBottom:10, fontSize:12, color:"#1d4ed8", fontWeight:700 }}>
                              💻 Séances en ligne (visio) - aucun centre à sélectionner
                            </div>
                          ) : (
                            <div style={{ display:"flex", flexDirection:"column", gap:4, marginBottom:10 }}>
                              <label style={{ fontSize:11, fontWeight:700, color:"#374151" }}>🏢 Centre BET *</label>
                              <select value={dossierPriveForm.centre_coaching} onChange={e=>setDossierPriveForm(prev=>({...prev, centre_coaching:e.target.value}))}
                                disabled={locked} style={{ padding:"8px 10px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, outline:"none", ...disabledStyle }}>
                                <option value="">- Sélectionner un centre -</option>
                                {centresListe.map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
                              </select>
                            </div>
                          )}
                          {/* Disponibilités */}
                          {(() => {
                            const JOURS_FORM = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];
                            const horaires = dossierPriveForm.disponibilites?.horaires || [];
                            const setHoraires = (fn) => setDossierPriveForm(prev => ({ ...prev, disponibilites:{ ...prev.disponibilites, horaires: fn(prev.disponibilites?.horaires||[]) } }));
                            return (
                              <div style={{ marginBottom:10 }}>
                                <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:8 }}>📅 Disponibilités de l'apprenant</label>
                                <div style={{ display:"flex", flexWrap:"wrap", gap:7, marginBottom:horaires.length>0?10:0 }}>
                                  {JOURS_FORM.map(jour => {
                                    const active = horaires.some(h=>h.jour===jour);
                                    return (
                                      <button key={jour} type="button" disabled={locked}
                                        onClick={()=>{ if(locked) return; setHoraires(prev => prev.some(h=>h.jour===jour) ? prev.filter(h=>h.jour!==jour) : [...prev,{jour,debut:"08:00",fin:"10:00"}].sort((a,b)=>JOURS_FORM.indexOf(a.jour)-JOURS_FORM.indexOf(b.jour))); }}
                                        style={{ padding:"6px 14px", borderRadius:20, border:`2px solid ${active?"#16a34a":"#e2e8f0"}`, background:active?"#16a34a15":"#f8fafc", color:active?"#15803d":"#64748b", fontWeight:700, fontSize:11, cursor:locked?"not-allowed":"pointer" }}>
                                        {jour}
                                      </button>
                                    );
                                  })}
                                </div>
                                {horaires.length>0 && (
                                  <div style={{ fontSize:10, color:"#9ca3af", marginBottom:6 }}>Renseignez l'heure de début ET de fin exactes pour chaque jour - Onboarding réutilisera ces horaires tels quels.</div>
                                )}
                                {horaires.map((h,i)=>(
                                  <div key={h.jour} style={{ display:"flex", alignItems:"center", gap:9, padding:"7px 11px", background:"#16a34a0d", border:"1.5px solid #16a34a30", borderRadius:9, marginBottom:6 }}>
                                    <span style={{ fontWeight:800, fontSize:11, color:"#15803d", minWidth:32 }}>{h.jour}</span>
                                    <span style={{ fontSize:10, color:"#9ca3af" }}>de</span>
                                    <input type="time" value={h.debut||""} disabled={locked}
                                      onChange={e=>setHoraires(prev=>prev.map((hh,j)=>j===i?{...hh,debut:e.target.value}:hh))}
                                      style={{ padding:"4px 7px", border:"1px solid #16a34a40", borderRadius:7, fontSize:11 }} />
                                    <span style={{ fontSize:10, color:"#9ca3af" }}>à</span>
                                    <input type="time" value={h.fin||""} disabled={locked}
                                      onChange={e=>setHoraires(prev=>prev.map((hh,j)=>j===i?{...hh,fin:e.target.value}:hh))}
                                      style={{ padding:"4px 7px", border:"1px solid #16a34a40", borderRadius:7, fontSize:11 }} />
                                    {!locked && <button type="button" onClick={()=>setHoraires(prev=>prev.filter((_,j)=>j!==i))} style={{ background:"none", border:"none", cursor:"pointer", color:"#ef4444", fontWeight:900, fontSize:14, marginLeft:"auto" }}>×</button>}
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                          <div style={{ display:"flex", flexDirection:"column", gap:4, marginBottom:12 }}>
                            <label style={{ fontSize:11, fontWeight:700, color:"#374151" }}>📅 Date de début de la formation</label>
                            <input type="date" value={dossierPriveForm.date_debut||""} onChange={e=>setDossierPriveForm(prev=>({...prev, date_debut:e.target.value}))}
                              disabled={locked} style={{ padding:"8px 10px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, outline:"none", ...(locked?disabledStyle:{}) }} />
                          </div>
                          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                            <button onClick={saveDossierPriveCommercial}
                              disabled={locked || savingDossierPrive || !dossierPriveForm.niveau || !dossierPriveForm.formation}
                              style={{ padding:"8px 18px", background: locked ? "#e2e8f0" : dossierComplet ? "#16a34a" : "#f59e0b", color: locked ? "#94a3b8" : "#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:12, cursor: locked ? "not-allowed" : "pointer", opacity:(locked||savingDossierPrive||!dossierPriveForm.niveau||!dossierPriveForm.formation)?0.65:1 }}>
                              {savingDossierPrive ? "Enregistrement…" : locked ? "🔒 Dossier verrouillé" : dossierComplet ? "✓ Mettre à jour le dossier" : "💾 Enregistrer le dossier"}
                            </button>
                            {!locked && (!a.dossier_statut || a.dossier_statut === "invalide") && (
                              <button onClick={() => setConfirmTransmettrePA(true)}
                                disabled={transmettantPA || !dossierComplet}
                                title={!dossierComplet ? "Renseignez le niveau et la formation avant de transmettre" : undefined}
                                style={{ padding:"8px 18px", background: !dossierComplet ? "#e2e8f0" : "#7c3aed", color: !dossierComplet ? "#94a3b8" : "#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:12, cursor: (!dossierComplet||transmettantPA) ? "not-allowed" : "pointer", opacity:(transmettantPA||!dossierComplet)?0.65:1 }}>
                                {transmettantPA ? "Envoi…" : a.dossier_statut === "invalide" ? "📤 Re-transmettre au PA" : "📤 Transmettre au PA pour validation"}
                              </button>
                            )}
                          </div>
                          {a.dossier_statut === "en_attente_validation" && (
                            <div style={{ marginTop:8, fontSize:11, color:"#6d28d9", fontWeight:600 }}>⏳ Déjà transmis - en attente de validation par le PA.</div>
                          )}
                        </div>
                      );
                    })()}

                    {/* ── Profil apprenant (adulte / enfant / étudiant) ── */}
                    <ProfilEnfantSection
                      a={a}
                      setAssignations={setAssignations}
                      setApprenantModal={setApprenantModal}
                    />

                    {/* ── OBJECTIF DU CLIENT, éditable sur place ────────────────────────
                        Juste après le profil, parce que c'est la suite naturelle de la
                        question « qui est cet apprenant » : « et qu'attend-il de nous ».
                        ÉDITABLE ICI et pas seulement à l'inscription : les dossiers venus
                        du site web arrivent sans objectif, personne ne l'ayant demandé au
                        prospect. C'est au premier appel qu'on l'apprend, et c'est cet
                        écran-là qui est ouvert à ce moment. ── */}
                    <ObjectifClientBloc a={a} authHeaders={authHeaders}
                      setAssignations={setAssignations} setApprenantModal={setApprenantModal} />

                    {/* Offre + Niveau + Date de début - toujours visible */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      <div style={{ background:"#f0fdf4", borderRadius:8, padding:"10px 14px", border:"1px solid #bbf7d0" }}>
                        <div style={{ fontSize:10, fontWeight:700, color:"#15803d", textTransform:"uppercase", letterSpacing:".05em", marginBottom:3 }}>📚 Offre souscrite</div>
                        <div style={{ fontSize:13, fontWeight:700, color: offreEffective ? "#0f172a" : "#94a3b8" }}>{offreEffective || "Non renseignée"}</div>
                        {a.code_promo && (
                          <div style={{ marginTop:6, display:"inline-flex", alignItems:"center", gap:4, background:"#fef3c7", color:"#92400e", borderRadius:999, padding:"2px 9px", fontSize:10, fontWeight:800 }}>
                            🎟️ {a.code_promo}{a.reduction_montant ? ` · -${Number(a.reduction_montant).toLocaleString("fr-FR")} FCFA` : ""}
                          </div>
                        )}
                      </div>
                      <div style={{ background:"#faf5ff", borderRadius:8, padding:"10px 14px", border:"1px solid #ddd6fe" }}>
                        <div style={{ fontSize:10, fontWeight:700, color:"#7c3aed", textTransform:"uppercase", letterSpacing:".05em", marginBottom:3 }}>🎯 Niveau évalué</div>
                        <div style={{ fontSize:13, fontWeight:700, color: niveauEffectif ? "#0f172a" : "#94a3b8" }}>{niveauEffectif || "Non renseigné"}</div>
                      </div>
                      <div style={{ background:"#eff6ff", borderRadius:8, padding:"10px 14px", border:"1px solid #bfdbfe" }}>
                        <div style={{ fontSize:10, fontWeight:700, color:"#1d4ed8", textTransform:"uppercase", letterSpacing:".05em", marginBottom:3 }}>📅 Date de début</div>
                        <div style={{ fontSize:13, fontWeight:700, color: a.date_debut_formation ? "#0f172a" : "#94a3b8" }}>
                          {a.date_debut_formation ? new Date(a.date_debut_formation).toLocaleDateString("fr-FR", { day:"2-digit", month:"long", year:"numeric" }) : "Non renseignée"}
                        </div>
                      </div>
                    </div>

                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      {[
                        ["Mode de cours", modeLabel],
                        ["Coaching", coachLabel],
                        ["Assistante", a.assistante_nom || "-"],
                        ["Téléphone assistante", a.assistante_tel || "-"],
                        ["Date de conversion", a.updated_at ? new Date(a.updated_at).toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" }) : "-"],
                        ["Source", a.source || "-"],
                      ].map(([l,v]) => (
                        <div key={l} style={{ background:"#f8fafc", borderRadius:8, padding:"10px 14px", border:"1px solid #e2e8f0" }}>
                          <div style={{ fontSize:10, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".05em", marginBottom:3 }}>{l}</div>
                          <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>{v}</div>
                        </div>
                      ))}
                    </div>

                    {/* ── Classe & Coach assignés (données réelles via groupes_apprenants) ── */}
                    {commClasseLoad ? (
                      <div style={{ background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0", padding:"14px 16px", display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:18, height:18, borderRadius:"50%", border:"2px solid #0891b2", borderTopColor:"transparent", animation:"spin 1s linear infinite", flexShrink:0 }} />
                        <span style={{ fontSize:12, color:"#94a3b8" }}>Chargement de la classe…</span>
                      </div>
                    ) : commClasseInfo ? (
                      <div style={{ background:"linear-gradient(135deg,#f0fdf4,#e0f2fe)", borderRadius:12, border:"1px solid #86efac", padding:"14px 18px" }}>
                        <div style={{ fontSize:11, fontWeight:800, color:"#15803d", textTransform:"uppercase", letterSpacing:".06em", marginBottom:12 }}>🏫 Classe & Coach assignés</div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                          {/* Classe */}
                          <div style={{ background:"#fff", borderRadius:8, padding:"10px 14px", border:"1px solid #bbf7d0" }}>
                            <div style={{ fontSize:9, color:"#6b7280", fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>Classe</div>
                            <div style={{ fontSize:13, fontWeight:800, color:"#0f172a" }}>{commClasseInfo.nom}</div>
                            {commClasseInfo.niveau && (
                              <span style={{ display:"inline-block", marginTop:4, padding:"1px 8px", borderRadius:99, fontSize:10, fontWeight:800, background:"#1e3a8a", color:"#fff" }}>{commClasseInfo.niveau}</span>
                            )}
                            {commClasseInfo.filiere && <div style={{ fontSize:11, color:"#64748b", marginTop:3 }}>{commClasseInfo.filiere}</div>}
                          </div>
                          {/* Coach */}
                          <div style={{ background:"#fff", borderRadius:8, padding:"10px 14px", border:"1px solid #bbf7d0", display:"flex", alignItems:"center", gap:10 }}>
                            <div style={{ width:36, height:36, borderRadius:"50%", background:BET_GRADIENT, overflow:"hidden", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:13 }}>
                              {commClasseInfo.coach_photo
                                ? <img src={commClasseInfo.coach_photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                                : (commClasseInfo.coach_nom || "?")[0].toUpperCase()}
                            </div>
                            <div style={{ minWidth:0 }}>
                              <div style={{ fontSize:9, color:"#6b7280", fontWeight:700, textTransform:"uppercase", marginBottom:2 }}>Coach</div>
                              <div style={{ fontSize:13, fontWeight:800, color:"#0f172a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{commClasseInfo.coach_nom || "Non assigné"}</div>
                              {commClasseInfo.coach_telephone && <div style={{ fontSize:11, color:"#0891b2" }}>📞 {commClasseInfo.coach_telephone}</div>}
                            </div>
                          </div>
                          {/* Horaires */}
                          {commClasseInfo.horaire?.length > 0 && (
                            <div style={{ gridColumn:"1/-1", background:"#fff", borderRadius:8, padding:"10px 14px", border:"1px solid #bbf7d0" }}>
                              <div style={{ fontSize:9, color:"#6b7280", fontWeight:700, textTransform:"uppercase", marginBottom:6 }}>Horaires</div>
                              <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                                {commClasseInfo.horaire.map((h,i) => (
                                  <span key={i} style={{ padding:"3px 10px", borderRadius:99, background:"#eff6ff", color:"#1e3a8a", fontSize:11, fontWeight:700, border:"1px solid #bfdbfe" }}>🕐 {fmtHoraire(h)}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Centre */}
                          {commClasseInfo.centre_nom && (
                            <div style={{ background:"#fff", borderRadius:8, padding:"10px 14px", border:"1px solid #bbf7d0" }}>
                              <div style={{ fontSize:9, color:"#6b7280", fontWeight:700, textTransform:"uppercase", marginBottom:2 }}>Centre</div>
                              <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>📍 {commClasseInfo.centre_nom}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : a.statut === "converti" && a.type_cours !== "presentiel" ? (
                      <div style={{ background:"#fffbeb", borderRadius:10, border:"1px solid #fde68a", padding:"12px 16px", display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontSize:16 }}>⏳</span>
                        <span style={{ fontSize:12, color:"#92400e", fontWeight:600 }}>En attente d'affectation à une classe par l'Onboarding ou le PA</span>
                      </div>
                    ) : null}
                    {/* CHANGER DE GROUPE : réservé au PRÉSENTIEL, où la commerciale affecte
                        elle-même la classe et le coach. En ligne, c'est l'Onboarding qui place
                        l'apprenant : lui laisser ce bouton permettait de défaire une affectation
                        dont elle n'est pas responsable, sans que personne en soit informé.
                        Le critère est le canal DU DOSSIER, non le profil de l'assistante : une
                        commerciale « les deux » garde ainsi le bouton sur ses dossiers
                        présentiels et le perd sur ceux qui partent chez l'Onboarding.
                        Coaching privé exclu, comme avant. */}
                    {/* LE CHANGEMENT DE GROUPE NE PART PLUS D'ICI. Déclenché depuis la fiche, il
                        permettait de déplacer un apprenant sans demande du coach ni test de
                        niveau - donc sans savoir vers quelle classe l'envoyer. Il passe par
                        l'onglet « Dossiers à réaffecter », où le niveau rendu par le test
                        désigne la classe d'arrivée. */}
                    {a.type_coaching !== "prive" && (
                      <div style={{ padding:"9px 12px", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:9, fontSize:11.5, color:"#64748b", textAlign:"center", lineHeight:1.6 }}>
                        {a.type_cours === "en_ligne"
                          ? "L'affectation et le changement de groupe sont gérés par l'Onboarding."
                          : "Le changement de groupe se fait depuis l'onglet « Dossiers à réaffecter », après la demande du coach et le test de niveau."}
                      </div>
                    )}
                    {/* L'ÉVOLUTION, quand il y a plus d'un test : « B1 → C1 » explique le
                        changement de classe que le seul niveau courant passe sous silence. */}
                    <EvolutionNiveauTests tests={tousLesTestsDe(a)} compact />

                    {testProspect && (
                      <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", background:"#f0fdf4", borderRadius:10, border:"1px solid #bbf7d0" }}>
                        <span style={{ fontSize:12, fontWeight:700, color:"#15803d" }}>📝 Test de niveau</span>
                        <span style={{ fontWeight:800, fontSize:14, color:"#fff", background:"#0891b2", borderRadius:999, padding:"2px 12px" }}>{testProspect.niveau}</span>
                        <span style={{ fontSize:12, color:"#64748b" }}>{testProspect.score}% · {testProspect.date && new Date(testProspect.date).toLocaleDateString("fr-FR")}</span>
                        <button onClick={() => { setSelectedTest(testProspect); setShowTestDetailModal(true); }} style={{ marginLeft:"auto", fontSize:11, padding:"4px 10px", borderRadius:6, border:"1px solid #16a34a40", background:"#dcfce7", color:"#15803d", cursor:"pointer", fontWeight:700 }}>Voir →</button>
                      </div>
                    )}
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      <button onClick={() => setChatAssignation(chatAssignation?.id===a.id?null:a)} style={{ ...btnPrimary, fontSize:12, padding:"8px 16px" }}>💬 Chat</button>
                      <button onClick={() => { setSuiviTarget(a); setShowSuiviModal(true); }} style={{ ...btnSecondary, fontSize:12, padding:"8px 16px" }}>🚀 Suivi démarrage</button>
                      {a.type_cours === "presentiel" && a.type_coaching !== "prive" && a.routing_statut !== "centre_assigne" && !commClasseInfo && (() => {
                        const hasPmt = pmts.length > 0 && pmts.some(p => (p.montantReçu||0) > 0);
                        // Même exigence que "Prospects parcours" (déjà corrigée) : impossible de
                        // placer un apprenant dans une classe sans connaître son niveau réel - ce
                        // bouton-ci, dans "Mes apprenants", n'était pas couvert par ce correctif.
                        const hasTest = !!testProspect;
                        const peutAssigner = hasPmt && hasTest;
                        return (
                          <button
                            onClick={() => {
                              if (!hasPmt) { toast.error("Un paiement doit être enregistré avant l'assignation."); return; }
                              if (!hasTest) { toast.error("Le test de niveau doit être effectué avant l'assignation."); return; }
                              fetchClasses(); fetchCoachs();
                              setApprenantModal(null);
                              setAssignCentreTarget(a);
                              setShowAssignCentreModal(true);
                            }}
                            title={!hasPmt ? "Paiement requis avant assignation" : !hasTest ? "Test de niveau requis avant assignation" : "Assigner à une classe et un coach"}
                            style={{ fontSize:12, padding:"8px 16px", background: peutAssigner ? "linear-gradient(135deg,#7c3aed,#a855f7)" : "#f1f5f9", color: peutAssigner ? "#fff" : "#94a3b8", border:"none", borderRadius:8, cursor: peutAssigner ? "pointer" : "not-allowed", fontWeight:700, opacity: peutAssigner ? 1 : 0.7 }}
                          >
                            🏫 Assigner classe & coach
                          </button>
                        );
                      })()}
                      {a.type_cours === "presentiel" && (a.routing_statut === "centre_assigne" || commClasseInfo) && (
                        <div style={{ background:"#f5f3ff", border:"1.5px solid #ddd6fe", borderRadius:10, padding:"10px 14px", fontSize:12, color:"#7c3aed", fontWeight:700 }}>
                          ✅ Assigné - {commClasseInfo?.nom || a.suivi_demarrage?.classe_nom || "classe"}{(commClasseInfo?.coach_nom || a.suivi_demarrage?.coach_nom) ? ` · Coach : ${commClasseInfo?.coach_nom || a.suivi_demarrage.coach_nom}` : ""}
                        </div>
                      )}
                      {/* Idem : coaching privé exclu (domicile inclus), circuit PA dédié uniquement. */}
                      {a.statut !== "converti" && a.type_cours !== "presentiel" && a.type_coaching !== "prive" && (() => {
                        const hasPaiementPourTransmission = pmts.length > 0 && pmts.some(p => (p.montantReçu||0) > 0);
                        /* MÊME EXIGENCE QUE LE CANAL PRÉSENTIEL, dix lignes plus haut : le test de
                           niveau conditionne déjà « Assigner classe & coach », mais ce bouton-ci
                           ne l'avait jamais reçue. Or il mène au même endroit - l'Onboarding, qui
                           affecte à une classe et ne peut pas la choisir sans connaître le niveau.
                           Un dossier transmis sans test repartait en arrière. */
                        const hasTestTransmission = !!testProspect;
                        const peutTransmettre = hasPaiementPourTransmission && hasTestTransmission;
                        return (
                          <button
                            onClick={() => {
                              if (!hasPaiementPourTransmission) {
                                toast.error("Un paiement doit être enregistré avant de finaliser l'inscription.");
                                return;
                              }
                              if (!hasTestTransmission) {
                                toast.error("Le test de niveau doit être effectué avant la transmission.");
                                return;
                              }
                              setApprenantModal(null);
                              setFinalisationTarget(a);
                              setShowFinalisationModal(true);
                              runDbCheck(a);
                            }}
                            title={!hasPaiementPourTransmission ? "Paiement requis avant transmission" : !hasTestTransmission ? "Test de niveau requis avant transmission" : "Finaliser et transmettre le dossier"}
                            style={{ fontSize:12, padding:"8px 16px", background: peutTransmettre ? "linear-gradient(135deg,#15803d,#22c55e)" : "#f1f5f9", color: peutTransmettre ? "#fff" : "#94a3b8", border:"none", borderRadius:8, cursor: peutTransmettre ? "pointer" : "not-allowed", fontWeight:700, opacity: peutTransmettre ? 1 : 0.7 }}
                          >
                            📤 Transmettre le dossier
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* ACCÈS & CONNEXION */}
                {apprenantTab === "acces" && (
                  <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:4 }}>🔑 Accès à l'espace apprenant</div>
                    {commAccesLoad ? (
                      <div style={{ textAlign:"center", padding:"30px 0", color:"#94a3b8", fontSize:13 }}>Vérification du compte…</div>
                    ) : commAccesInfo?.has_account ? (
                      <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:12, padding:"16px 18px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                          <span style={{ fontSize:20 }}>✅</span>
                          <div>
                            <div style={{ fontWeight:800, color:"#065f46", fontSize:14 }}>Compte actif</div>
                            <div style={{ fontSize:11, color:"#6b7280" }}>L'apprenant peut se connecter à l'espace apprenant</div>
                          </div>
                          <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8 }}>
                            {commAccesInfo.profil?.mdp_temporaire ? (
                              <>
                                <span style={{ padding:"3px 10px", background:"#fef3c7", color:"#92400e", borderRadius:99, fontSize:11, fontWeight:700 }}>Mot de passe temporaire</span>
                                <button
                                  disabled={commAccesResending}
                                  title="Régénère et renvoie un nouveau mot de passe temporaire - uniquement pour les comptes créés par le staff (ex: enfant/ado sans email propre)"
                                  onClick={async () => {
                                    if (commAccesResending) return;
                                    setCommAccesResending(true);
                                    try {
                                      const r = await fetch(`${API_URL}/api/apprenants/creer-acces`, {
                                        method: "POST", headers: { ...authHeaders(), "Content-Type":"application/json" },
                                        body: JSON.stringify({
                                          nom:              a.prospect_nom?.split(" ").slice(-1)[0] || a.prospect_nom || "",
                                          prenom:           a.prospect_nom?.split(" ").slice(0,-1).join(" ") || "",
                                          email:            a.prospect_email     || null,
                                          telephone:        a.prospect_telephone || null,
                                          email_parent:     a.email_parent       || null,
                                          telephone_parent: a.telephone_parent   || null,
                                        }),
                                      });
                                      const d = r.ok ? await r.json() : null;
                                      if (d?.user_id && d?.mot_de_passe_conserve) {
                                        toast(`ℹ️ ${a.prospect_nom || "Cet apprenant"} a déjà son propre mot de passe (compte auto-inscrit) - non modifié.`);
                                        setCommAccesInfo(prev => prev ? { ...prev, profil: { ...prev.profil, mdp_temporaire: false, mdp_initial: null } } : prev);
                                      } else if (d?.user_id) {
                                        const ident = d.login_identifier || a.prospect_email || a.prospect_telephone;
                                        toast.success(d.login_type === "telephone"
                                          ? `📧 Accès renvoyés - identifiant : ${ident} (connexion par téléphone)`
                                          : d.login_type === "technique"
                                          ? `📧 Accès renvoyés - identifiant technique : ${ident} (à communiquer manuellement)`
                                          : `📧 Nouveaux identifiants envoyés à ${ident}`);
                                        setCommAccesInfo(prev => prev ? { ...prev, profil: { ...prev.profil, mdp_temporaire: true, mdp_initial: d.mdp_initial || prev.profil.mdp_initial } } : prev);
                                      } else {
                                        toast.error(d?.error || "Erreur lors du renvoi des accès");
                                      }
                                    } catch { toast.error("Erreur réseau"); }
                                    finally { setCommAccesResending(false); }
                                  }}
                                  style={{ padding:"5px 12px", borderRadius:8, border:"1px solid #86efac", background:commAccesResending?"#f1f5f9":"#fff", color:commAccesResending?"#9ca3af":"#15803d", fontSize:11, fontWeight:700, cursor:commAccesResending?"not-allowed":"pointer", whiteSpace:"nowrap" }}>
                                  {commAccesResending ? "⏳ Envoi…" : "📧 Renvoyer les accès"}
                                </button>
                              </>
                            ) : (
                              <span title="Compte auto-inscrit avec son propre mot de passe (site web / Google) - impossible et inutile de le renvoyer" style={{ fontSize:11, color:"#9ca3af" }}>
                                🔒 Mot de passe personnel
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                          <div style={{ background:"#fff", borderRadius:8, padding:"10px 12px", border:"1px solid #d1fae5" }}>
                            {/* L'IDENTIFIANT, jamais le téléphone à sa place. Cet écran
                                affichait « +2250777880082 » quand l'apprenant doit taper
                                « tel.2250777880082.…@betacademy.app » : l'assistante ne
                                pouvait ni l'aider à se connecter, ni deviner l'écart. */}
                            <IdentifiantConnexion
                              email={commAccesInfo.profil?.email || a.prospect_email}
                              telephone={commAccesInfo.profil?.telephone || a.prospect_telephone}
                            />
                            {/* L'identifiant se corrige APRÈS l'inscription : une adresse
                                saisie de travers condamnait sinon l'apprenant à un compte
                                qu'il ne peut pas utiliser. */}
                            {commAccesInfo.profil?.id && (
                              <ModifierEmailApprenant
                                apprenantId={commAccesInfo.profil.id}
                                emailActuel={commAccesInfo.profil.email}
                                nomApprenant={a.prospect_nom || ""}
                                accent="#0891b2"
                                onChange={(mail) => setCommAccesInfo(p => p ? { ...p, profil: { ...p.profil, email: mail } } : p)}
                              />
                            )}
                          </div>
                          {commAccesInfo.profil?.mdp_temporaire && commAccesInfo.profil?.mdp_initial ? (
                            <div style={{ background:"#fffbeb", borderRadius:8, padding:"10px 12px", border:"1.5px solid #fde68a" }}>
                              <div style={{ fontSize:9, color:"#92400e", fontWeight:700, marginBottom:3 }}>MOT DE PASSE TEMPORAIRE</div>
                              <div style={{ fontSize:15, fontWeight:900, color:"#0f172a", fontFamily:"monospace", letterSpacing:"0.06em" }}>{commAccesInfo.profil.mdp_initial}</div>
                              <div style={{ fontSize:10, color:"#d97706", marginTop:4 }}>⚠️ À communiquer à l'apprenant - il devra le changer à la première connexion</div>
                            </div>
                          ) : (
                            <div style={{ background:"#f0fdf4", borderRadius:8, padding:"10px 12px", border:"1px solid #86efac" }}>
                              <div style={{ fontSize:9, color:"#065f46", fontWeight:700, marginBottom:3 }}>MOT DE PASSE</div>
                              <div style={{ fontSize:12, fontWeight:700, color:"#065f46" }}>✅ Changé par l'apprenant</div>
                            </div>
                          )}
                          <div style={{ background:"#f8fafc", borderRadius:8, padding:"10px 12px", border:"1px solid #e2e8f0" }}>
                            <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, marginBottom:3 }}>COMPTE CRÉÉ LE</div>
                            <div style={{ fontSize:12, fontWeight:700, color:"#374151" }}>{commAccesInfo.profil?.date_creation ? new Date(commAccesInfo.profil.date_creation).toLocaleDateString("fr-FR", {day:"numeric",month:"long",year:"numeric"}) : "-"}</div>
                          </div>
                        </div>
                      </div>
                    ) : (a.prospect_email || a.prospect_telephone) ? (
                      <div style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:12, padding:"20px 18px" }}>
                        <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:16 }}>
                          <span style={{ fontSize:24 }}>⚠️</span>
                          <div>
                            <div style={{ fontWeight:800, color:"#92400e", fontSize:14, marginBottom:3 }}>Aucun compte créé</div>
                            <div style={{ fontSize:12, color:"#78350f", lineHeight:1.6 }}>
                              Cet apprenant n'a pas encore d'accès à l'espace apprenant.<br/>
                              {a.prospect_email
                                ? <>Identifiant : <strong>{a.prospect_email}</strong> (email)</>
                                : <>Identifiant : <strong>{a.prospect_telephone}</strong> (téléphone)</>
                              }
                            </div>
                          </div>
                        </div>
                        <button
                          disabled={commAccesCreating}
                          onClick={async () => {
                            if (commAccesCreating) return;
                            setCommAccesCreating(true);
                            const r = await fetch(`${API_URL}/api/apprenants/creer-acces`, {
                              method: "POST", headers: { ...authHeaders(), "Content-Type":"application/json" },
                              body: JSON.stringify({
                                nom:              a.prospect_nom?.split(" ").slice(-1)[0] || a.prospect_nom || "",
                                prenom:           a.prospect_nom?.split(" ").slice(0,-1).join(" ") || "",
                                email:            a.prospect_email     || null,
                                telephone:        a.prospect_telephone || null,
                                email_parent:     a.email_parent       || null,
                                telephone_parent: a.telephone_parent   || null,
                              }),
                            });
                            const d = r.ok ? await r.json() : null;
                            if (d?.user_id) {
                              const ident = d.login_identifier || a.prospect_email || a.prospect_telephone;
                              toast.success(d.login_type === "telephone"
                                ? `✅ Accès créés - identifiant: ${ident} (connexion par téléphone)`
                                : d.login_type === "technique"
                                ? `✅ Accès créés - identifiant technique : ${ident} (à communiquer manuellement, aucun email réel)`
                                : `✅ Accès créés - identifiants envoyés à ${ident}`);
                              // Mettre à jour l'UI immédiatement depuis la réponse
                              setCommAccesInfo({
                                has_account: true,
                                profil: {
                                  id:             d.user_id,
                                  email:          d.login_email || a.prospect_email,
                                  telephone:      a.prospect_telephone || null,
                                  actif:          true,
                                  acces_bloque:   false,
                                  role:           "apprenant",
                                  mdp_temporaire: true,
                                  mdp_initial:    d.mdp_initial || null,
                                  created_at:     new Date().toISOString(),
                                },
                              });
                            } else {
                              toast.error(d?.error || "Erreur création accès");
                            }
                            setCommAccesCreating(false);
                          }}
                          style={{ width:"100%", padding:"12px", background:commAccesCreating?"#e2e8f0":"#f97316", color:commAccesCreating?"#9ca3af":"#fff", border:"none", borderRadius:10, fontSize:13, fontWeight:700, cursor:commAccesCreating?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                          {commAccesCreating ? <><span style={{ width:14,height:14,border:"2px solid rgba(0,0,0,0.2)",borderTopColor:"#666",borderRadius:"50%",display:"inline-block",animation:"spin .7s linear infinite" }}/>Création…</> : "🔑 Créer les accès et envoyer les identifiants"}
                        </button>
                      </div>
                    ) : (
                      <div style={{ textAlign:"center", padding:"30px 0", color:"#94a3b8", fontSize:13 }}>Aucun email ni téléphone - impossible de créer un compte.</div>
                    )}

                    {/* isSuspendu/toggleSuspension remontés ici (au lieu de rester enfermés dans
                        l'IIFE "Suspension globale" plus bas) pour pouvoir aussi placer un bouton
                        "Débloquer" directement à côté du compteur d'absences - jusqu'ici la
                        commerciale devait obligatoirement descendre jusqu'au bloc "Accès à la
                        formation" séparé, ce qui prêtait à confusion (bouton qui bascule entre
                        "Bloquer"/"Réintégrer" selon l'état, pas un vrai bouton "Débloquer" dédié). */}
                    {commAccesInfo?.profil?.id && (() => {
                      const appId = commAccesInfo.profil.id;
                      const isSuspendu = commAccesInfo.profil.acces_bloque || false;
                      // Affecté à une classe : par la classe chargée pour cet écran, ou par le
                      // dossier lui-même quand ce chargement n'a pas encore abouti.
                      const estInscritEnClasse = !!(commClasseInfo?.id || a?.suivi_demarrage?.classe_id);
                      // Debloquer reste immediat : c'est une bonne nouvelle, sans motif a saisir.
                      // Bloquer passe par le modal, pour que l'apprenant sache pourquoi.
                      const toggleSuspension = async () => {
                        if (!isSuspendu) {
                          setMotifChoisi("absences_repetees");
                          setPrecisionMotif("");
                          setBlocageModal({ appId });
                          if (!motifsBlocage.length) {
                            fetch(`${API_URL}/api/apprenants/motifs-blocage`, { headers: authHeaders() })
                              .then(r => r.ok ? r.json() : { motifs: [] })
                              .then(d => setMotifsBlocage(d.motifs || []))
                              .catch(() => {});
                          }
                          return;
                        }
                        setSuspensionLoading(true);
                        try {
                          const r = await fetch(`${API_URL}/api/apprenants/${appId}/suspension`, {
                            method: "PATCH",
                            headers: authHeaders(),
                            body: JSON.stringify({ bloque: !isSuspendu }),
                          });
                          if (!r.ok) { const e = await r.json(); toast.error(e.error || "Erreur"); return; }
                          setCommAccesInfo(prev => prev ? { ...prev, profil: { ...prev.profil, acces_bloque: !isSuspendu } } : prev);
                          setBlocageStatus(prev => prev ? { ...prev, acces_bloque: !isSuspendu, nb_absences: isSuspendu?0:prev.nb_absences } : prev);
                          toast.success(isSuspendu ? "✅ Apprenant réintégré - accès restaurés" : "🔒 Apprenant suspendu - accès bloqués");
                        } catch { toast.error("Erreur réseau"); }
                        finally { setSuspensionLoading(false); }
                      };

                      return (
                        <>
                    {/* Compteur d'absences - progression vers le blocage automatique (4) */}
                    {!blocageLoad && blocageStatus && (
                      <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, padding:"16px 20px", marginBottom:16 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                          <span style={{ fontSize:13, fontWeight:600, color:"#0f172a" }}>Absences enregistrées</span>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <span style={{ fontSize:20, fontWeight:800, color: blocageStatus.acces_bloque?"#dc2626":blocageStatus.nb_absences>=3?"#f59e0b":"#22c55e" }}>
                              {blocageStatus.nb_absences}
                            </span>
                            <span style={{ fontSize:13, color:"#94a3b8", fontWeight:600 }}>/4</span>
                            {isSuspendu && (
                              <button
                                disabled={suspensionLoading}
                                onClick={toggleSuspension}
                                title="Débloquer l'accès et remettre le compteur à zéro"
                                style={{ marginLeft:6, padding:"5px 12px", borderRadius:999, border:"none", cursor:suspensionLoading?"not-allowed":"pointer", fontWeight:700, fontSize:11, background:suspensionLoading?"#e2e8f0":"#22c55e", color:suspensionLoading?"#9ca3af":"#fff", whiteSpace:"nowrap" }}>
                                {suspensionLoading ? "…" : "🔓 Débloquer"}
                              </button>
                            )}
                          </div>
                        </div>
                        <div style={{ height:10, background:"#f1f5f9", borderRadius:5, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${Math.min(100,(blocageStatus.nb_absences/4)*100)}%`, background: blocageStatus.acces_bloque?"#ef4444":blocageStatus.nb_absences>=3?"#f59e0b":"#22c55e", borderRadius:5, transition:"width .4s" }} />
                        </div>
                        <div style={{ display:"flex", justifyContent:"space-between", marginTop:8 }}>
                          {[1,2,3,4].map(n => (
                            <div key={n} style={{ textAlign:"center", flex:1 }}>
                              <div style={{ width:8, height:8, borderRadius:"50%", margin:"0 auto 3px", background: blocageStatus.nb_absences>=n?(n>=4?"#ef4444":"#f59e0b"):"#e2e8f0" }} />
                              <div style={{ fontSize:9, color: blocageStatus.nb_absences>=n?"#0f172a":"#94a3b8", fontWeight:600 }}>{n}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ marginTop:10, fontSize:11, color:"#64748b", textAlign:"center" }}>
                          {blocageStatus.acces_bloque
                            ? "🔒 Accès bloqué - débloquer pour remettre le compteur à zéro"
                            : blocageStatus.nb_absences>=3
                              ? "⚠️ Attention - 1 absence supplémentaire entraînera un blocage automatique"
                              : `✅ ${4-blocageStatus.nb_absences} absence(s) restante(s) avant blocage automatique`}
                        </div>
                      </div>
                    )}

                    {/* Arret de formation - borne la fenetre d'acces aux contenus.
                        A cote du blocage d'acces, mais ce n'est PAS la meme chose : on
                        bloque quelqu'un qui reste inscrit, on arrete quelqu'un qui part. */}
                    {(() => {
                      /* CE BLOC NE LIT PAS `a`. `apprenantModal` est le résultat d'une fusion
                         entre la ligne `groupes_apprenants` et l'assignation du dossier
                         (`openApprenantDetail`), et l'assignation y écrase `id` et `statut` :
                         `a.id` est alors un id d'`assignations_parcours`, que la route d'arrêt
                         ne trouve pas (404 « Rattachement introuvable »), et `a.statut` vaut
                         l'état du DOSSIER (« converti »…), pas celui du rattachement.
                         `commClasseInfo`, résolu par /api/groupes/apprenant-by-email, est la
                         source utilisée partout ailleurs - Notes, Historique, Transfert. */
                      const gaId        = commClasseInfo?.ga_id || null;
                      const dateRetrait = commClasseInfo?.date_retrait || null;
                      const arrete      = !!dateRetrait || commClasseInfo?.statut_ga === "retire";
                      // Tant que le rattachement n'est pas résolu, on n'envoie RIEN : envoyer un
                      // identifiant faux échouait en silence côté écran.
                      const pret        = !!gaId && !commClasseLoad;
                      return (
                        <div style={{ borderRadius:12, border:`1.5px solid ${arrete?"#fdba74":"#e2e8f0"}`, background:arrete?"#fff7ed":"#f8fafc", padding:"14px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, marginBottom:12 }}>
                          <div>
                            <div style={{ fontSize:13, fontWeight:800, color:arrete?"#c2410c":"#0f172a" }}>
                              {arrete ? "🎓 Formation arrêtée" : "📚 Formation en cours"}
                            </div>
                            <div style={{ fontSize:11, color:"#64748b", marginTop:3 }}>
                              {commClasseLoad
                                ? "Recherche du rattachement en classe…"
                                : !gaId
                                ? "Aucun rattachement à une classe : il n'y a pas de formation à arrêter ici."
                                : arrete
                                ? `Arrêtée le ${new Date(dateRetrait).toLocaleDateString("fr-FR")} - les cours publiés après cette date ne lui sont plus accessibles. Il garde ceux qu'il a suivis.`
                                : "L'apprenant reçoit les nouveaux cours publiés par son coach."}
                            </div>
                          </div>
                          <button
                            disabled={arretLoading || !pret}
                            title={pret ? "" : "Le rattachement à la classe n'est pas encore identifié."}
                            onClick={()=>{ setArretMotif(""); setArretModal({ ga:a, gaId, arrete:!arrete }); }}
                            style={{ flexShrink:0, padding:"9px 18px", borderRadius:8, border:"none", cursor:(arretLoading||!pret)?"not-allowed":"pointer", fontWeight:700, fontSize:12, background:(arretLoading||!pret)?"#e2e8f0":arrete?"#22c55e":"#ea580c", color:(arretLoading||!pret)?"#9ca3af":"#fff", whiteSpace:"nowrap" }}>
                            {arretLoading ? "…" : arrete ? "↩ Reprendre la formation" : "🎓 Arrêter la formation"}
                          </button>
                        </div>
                      );
                    })()}

                    {/* SUSPENSION GLOBALE - un compte ne suffit pas, il faut être EN FORMATION.
                        Avoir un compte et suivre des cours sont deux choses : le compte naît du
                        paiement en ligne ou du bouton « Créer les accès », l'affectation vient
                        après. Bloquer avant l'affectation ne sanctionne rien - ce levier répond
                        aux absences et aux impayés d'un apprenant en cours de formation, et son
                        propre libellé promet de l'exclure des listes de présences, où il ne
                        figure pas encore. */}
                    {!estInscritEnClasse ? (
                      <div style={{ borderRadius:12, border:"1.5px solid #e2e8f0", background:"#f8fafc", padding:"14px 16px" }}>
                        <div style={{ fontSize:13, fontWeight:800, color:"#0f172a" }}>🔓 Pas encore en formation</div>
                        <div style={{ fontSize:11, color:"#64748b", marginTop:3, lineHeight:1.5 }}>
                          Le compte est créé, mais l'apprenant n'est affecté à aucune classe.
                          Le blocage d'accès s'appliquera une fois qu'il aura rejoint un groupe.
                        </div>
                      </div>
                    ) : (
                    <div style={{ borderRadius:12, border:`1.5px solid ${isSuspendu?"#fca5a5":"#e2e8f0"}`, background:isSuspendu?"#fef2f2":"#f8fafc", padding:"14px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
                      <div>
                        <div style={{ fontSize:13, fontWeight:800, color:isSuspendu?"#b91c1c":"#0f172a" }}>
                          {isSuspendu ? "🔒 Accès à la formation bloqué" : "🔓 Accès à la formation actif"}
                        </div>
                        <div style={{ fontSize:11, color:"#64748b", marginTop:3 }}>
                          {isSuspendu
                            ? "L'apprenant ne peut pas se connecter et est exclu des listes de présences futures."
                            : "L'apprenant a accès à tous ses cours et apparaît dans les listes de présences."}
                        </div>
                      </div>
                      <button
                        disabled={suspensionLoading}
                        onClick={toggleSuspension}
                        style={{ flexShrink:0, padding:"9px 18px", borderRadius:8, border:"none", cursor:suspensionLoading?"not-allowed":"pointer", fontWeight:700, fontSize:12, background:suspensionLoading?"#e2e8f0":isSuspendu?"#22c55e":"#dc2626", color:suspensionLoading?"#9ca3af":"#fff", whiteSpace:"nowrap" }}>
                        {suspensionLoading ? "…" : isSuspendu ? "🔓 Réintégrer" : "🔒 Bloquer l'accès"}
                      </button>
                    </div>
                    )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* PARENT (profil enfant uniquement) */}
                {apprenantTab === "parent" && (
                  <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:4 }}>👨‍👩‍👧 Coordonnées du parent / tuteur</div>

                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                      <div>
                        <label style={labelSt}>Nom du parent</label>
                        <input style={inputSt} value={commParentForm.nom_parent}
                          onChange={e => setCommParentForm(f => ({ ...f, nom_parent: e.target.value }))} />
                      </div>
                      <div>
                        <label style={labelSt}>Téléphone</label>
                        <input style={inputSt} value={commParentForm.telephone_parent}
                          onChange={e => setCommParentForm(f => ({ ...f, telephone_parent: e.target.value }))} />
                      </div>
                      <div>
                        <label style={labelSt}>Email</label>
                        <input type="email" style={inputSt} value={commParentForm.email_parent}
                          onChange={e => setCommParentForm(f => ({ ...f, email_parent: e.target.value }))}
                          placeholder="Différent de l'email de l'enfant" />
                      </div>
                    </div>

                    {(commParentForm.nom_parent !== (a.nom_parent||"") || commParentForm.telephone_parent !== (a.telephone_parent||"") || commParentForm.email_parent !== (a.email_parent||"")) && (
                      <button
                        disabled={commParentSaving}
                        onClick={async () => {
                          setCommParentSaving(true);
                          try {
                            const r = await fetch(`${API_URL}/api/parcours/assignations/${a.id}`, {
                              method: "PATCH", headers: authHeaders(),
                              body: JSON.stringify({
                                nom_parent:       commParentForm.nom_parent       || null,
                                telephone_parent: commParentForm.telephone_parent || null,
                                email_parent:     commParentForm.email_parent     || null,
                              }),
                            });
                            if (!r.ok) { const e = await r.json().catch(()=>({})); toast.error(e.error || "Erreur d'enregistrement"); return; }
                            setApprenantModal(prev => prev ? { ...prev, ...commParentForm } : prev);
                            setAssignations(prev => prev.map(x => x.id === a.id ? { ...x, ...commParentForm } : x));
                            setCommParentAccesInfo(null);
                            setCommParentCreateError("");
                            toast.success("✅ Coordonnées du parent enregistrées");
                          } catch { toast.error("Erreur réseau"); }
                          finally { setCommParentSaving(false); }
                        }}
                        style={{ alignSelf:"flex-start", padding:"8px 16px", background: commParentSaving ? "#e2e8f0" : "#0891b2", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor: commParentSaving ? "not-allowed" : "pointer" }}>
                        {commParentSaving ? "Enregistrement…" : "💾 Enregistrer les coordonnées"}
                      </button>
                    )}

                    <div style={{ fontSize:11, color:"#64748b", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:"8px 12px" }}>
                      ℹ️ Le compte parent est distinct du compte de l'enfant - deux connexions séparées, chacune avec son propre email et mot de passe. L'email du parent doit rester <strong>identique pour tous ses enfants</strong> (c'est ce qui permet au parent de tous les retrouver dans son espace de suivi) - évitez de le modifier ici pour un seul enfant, sauf s'il était simplement erroné. Si la création du compte parent est refusée car « cet email appartient déjà à un apprenant », c'est l'accès de l'<strong>enfant</strong> (onglet 🔑 Accès) qu'il faut recréer - le système lui attribuera automatiquement un identifiant technique distinct plutôt que de réutiliser l'email du parent.
                    </div>

                    {commParentCreateError && (
                      <div style={{ fontSize:12, color:"#991b1b", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px" }}>
                        ⚠️ {commParentCreateError}
                      </div>
                    )}

                    {!commParentForm.email_parent && !commParentForm.telephone_parent ? (
                      <div style={{ textAlign:"center", padding:"30px 0", color:"#94a3b8", fontSize:13 }}>Aucune coordonnée parent renseignée sur ce dossier.</div>
                    ) : commParentAccesLoad ? (
                      <div style={{ textAlign:"center", padding:"30px 0", color:"#94a3b8", fontSize:13 }}>Vérification du compte…</div>
                    ) : commParentAccesInfo?.existe ? (
                      <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:12, padding:"16px 18px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                          <span style={{ fontSize:20 }}>✅</span>
                          <div>
                            <div style={{ fontWeight:800, color:"#065f46", fontSize:14 }}>Compte parent actif</div>
                            <div style={{ fontSize:11, color:"#6b7280" }}>Le parent peut se connecter pour suivre son enfant</div>
                          </div>
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                          <div style={{ background:"#fff", borderRadius:8, padding:"10px 12px", border:"1px solid #d1fae5" }}>
                            <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, marginBottom:3 }}>EMAIL DE CONNEXION</div>
                            <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", wordBreak:"break-all" }}>{commParentAccesInfo.login_email}</div>
                          </div>
                          {commParentAccesInfo.mdp_initial ? (
                            <div style={{ background:"#fffbeb", borderRadius:8, padding:"10px 12px", border:"1.5px solid #fde68a" }}>
                              <div style={{ fontSize:9, color:"#92400e", fontWeight:700, marginBottom:3 }}>MOT DE PASSE TEMPORAIRE</div>
                              <div style={{ fontSize:15, fontWeight:900, color:"#0f172a", fontFamily:"monospace", letterSpacing:"0.06em" }}>{commParentAccesInfo.mdp_initial}</div>
                              <div style={{ fontSize:10, color:"#d97706", marginTop:4 }}>⚠️ À communiquer au parent - il devra le changer à la première connexion</div>
                            </div>
                          ) : (
                            <div style={{ background:"#f0fdf4", borderRadius:8, padding:"10px 12px", border:"1px solid #86efac" }}>
                              <div style={{ fontSize:9, color:"#065f46", fontWeight:700, marginBottom:3 }}>MOT DE PASSE</div>
                              <div style={{ fontSize:12, fontWeight:700, color:"#065f46" }}>✅ Changé par le parent</div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:12, padding:"20px 18px" }}>
                        <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:16 }}>
                          <span style={{ fontSize:24 }}>⚠️</span>
                          <div>
                            <div style={{ fontWeight:800, color:"#92400e", fontSize:14, marginBottom:3 }}>Aucun compte parent créé</div>
                            <div style={{ fontSize:12, color:"#78350f", lineHeight:1.6 }}>
                              Le parent n'a pas encore d'accès pour suivre son enfant.<br/>
                              {commParentForm.email_parent
                                ? <>Identifiant : <strong>{commParentForm.email_parent}</strong> (email)</>
                                : <>Identifiant : <strong>{commParentForm.telephone_parent}</strong> (téléphone)</>
                              }
                              {(commParentForm.nom_parent !== (a.nom_parent||"") || commParentForm.telephone_parent !== (a.telephone_parent||"") || commParentForm.email_parent !== (a.email_parent||"")) && (
                                <div style={{ marginTop:6, fontWeight:700 }}>⚠️ N'oubliez pas d'enregistrer les coordonnées modifiées avant de créer l'accès.</div>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          disabled={commParentAccesCreating}
                          onClick={async () => {
                            if (commParentAccesCreating) return;
                            setCommParentAccesCreating(true);
                            setCommParentCreateError("");
                            const r = await fetch(`${API_URL}/api/apprenants/creer-acces-parent`, {
                              method: "POST", headers: { ...authHeaders(), "Content-Type":"application/json" },
                              body: JSON.stringify({
                                nom_parent:       commParentForm.nom_parent       || null,
                                email_parent:     commParentForm.email_parent     || null,
                                telephone_parent: commParentForm.telephone_parent || null,
                                enfant_nom:       a.prospect_nom     || null,
                              }),
                            });
                            const d = await r.json().catch(() => null);
                            if (r.ok && d?.user_id) {
                              toast.success(`✅ Accès parent créé - identifiants envoyés à ${d.login_email}`);
                              setCommParentAccesInfo({ existe:true, login_email: d.login_email, mdp_initial: d.mdp_initial || null });
                            } else {
                              setCommParentCreateError(d?.error || "Erreur création accès parent");
                              toast.error(d?.error || "Erreur création accès parent");
                            }
                            setCommParentAccesCreating(false);
                          }}
                          style={{ width:"100%", padding:"12px", background:commParentAccesCreating?"#e2e8f0":"#f97316", color:commParentAccesCreating?"#9ca3af":"#fff", border:"none", borderRadius:10, fontSize:13, fontWeight:700, cursor:commParentAccesCreating?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                          {commParentAccesCreating ? <><span style={{ width:14,height:14,border:"2px solid rgba(0,0,0,0.2)",borderTopColor:"#666",borderRadius:"50%",display:"inline-block",animation:"spin .7s linear infinite" }}/>Création…</> : "🔑 Créer l'accès parent et envoyer les identifiants"}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* PRÉSENCES */}
                {apprenantTab === "presences" && (() => {
                  const STATUT_P = {
                    present:  { label:"Présent",  bg:"#dcfce7", c:"#166534" },
                    absent:   { label:"Absent",   bg:"#fee2e2", c:"#991b1b" },
                    retard:   { label:"En retard",bg:"#fef3c7", c:"#92400e" },
                    excuse:   { label:"Excusé",   bg:"#dbeafe", c:"#1e40af" },
                    planifiee:{ label:"Planifiée",bg:"#e0e7ff", c:"#3730a3" },
                    realisee: { label:"Réalisée",  bg:"#dcfce7", c:"#166534" },
                    annulee:  { label:"Annulée",   bg:"#f3f4f6", c:"#6b7280" },
                    non_inscrit: { label:"⏳ Non inscrit à cette période", bg:"#f8fafc", c:"#94a3b8" },
                  };
                  // Le taux de présence ne porte que sur les feuilles de présence réellement
                  // pointées - les séances "planifiée"/"réalisée (non pointée)"/"annulée" issues
                  // de la table unifiée n'ont pas de statut de présence individuel à ce stade.
                  const presencesPointees = commAppPres.filter(p => ["present","absent","retard","excuse"].includes(p.statut));
                  const nbPresent = presencesPointees.filter(p=>p.statut==="present").length;
                  const taux = presencesPointees.length ? Math.round(nbPresent/presencesPointees.length*100) : null;
                  return (
                    <div>
                      {/* Info groupe */}
                      {commClasseInfo ? (
                        <div style={{ background:"#f0f9ff", borderRadius:10, padding:"10px 14px", border:"1px solid #bae6fd", marginBottom:16, display:"flex", gap:10, alignItems:"center", fontSize:12, color:"#0369a1" }}>
                          <span style={{ fontSize:18 }}>🏫</span>
                          <div><strong>{commClasseInfo.nom}</strong>{commClasseInfo.coach_nom ? ` · Coach : ${commClasseInfo.coach_nom}` : ""}</div>
                        </div>
                      ) : (
                        <div style={{ background:"#fff7ed", borderRadius:10, padding:"10px 14px", border:"1px solid #fed7aa", marginBottom:16, fontSize:12, color:"#92400e" }}>
                          ⚠️ Cet apprenant n'est pas encore assigné à une classe - aucune présence disponible.
                        </div>
                      )}
                      {/* KPIs — sur TOUT le parcours depuis que les classes précédentes y sont
                          incluses. On le dit quand il y en a plusieurs : « 12 séances » sans
                          précision laisserait croire qu'elles ont toutes eu lieu dans la classe
                          affichée juste au-dessus. */}
                      {commAppPres.length > 0 && (
                        <>
                        {new Set(commAppPres.map(p => p.classe_nom).filter(Boolean)).size > 1 && (
                          <div style={{ fontSize:11, color:"#64748b", marginBottom:8 }}>
                            Sur l'ensemble de son parcours, toutes classes confondues.
                          </div>
                        )}
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
                          {[
                            { l:"Séances",    v:commAppPres.length,  c:"#0f172a" },
                            { l:"Présences",  v:nbPresent,           c:"#059669" },
                            { l:"Absences",   v:presencesPointees.filter(p=>p.statut==="absent").length,  c:"#dc2626" },
                            { l:"Taux",       v:taux !== null ? `${taux}%` : "-", c:"#0891b2" },
                          ].map(k => (
                            <div key={k.l} style={{ textAlign:"center", padding:"12px 10px", borderRadius:10, background:"#f8fafc", border:"1px solid #e5e7eb" }}>
                              <div style={{ fontSize:10, color:"#9ca3af", fontWeight:600 }}>{k.l}</div>
                              <div style={{ fontSize:18, fontWeight:800, color:k.c, marginTop:4 }}>{k.v}</div>
                            </div>
                          ))}
                        </div>
                        </>
                      )}
                      {/* Liste */}
                      {commAppPresLoading ? (
                        <div style={{ textAlign:"center", padding:"32px 0", color:"#9ca3af", fontSize:13 }}>Chargement des présences…</div>
                      ) : !commClasseInfo ? null : commAppPres.length === 0 ? (
                        <div style={{ textAlign:"center", padding:"32px 0", color:"#9ca3af" }}>
                          <div style={{ fontSize:28, marginBottom:8 }}>📋</div>
                          <div>Aucune feuille de présence enregistrée pour l'instant.</div>
                          <div style={{ fontSize:12, marginTop:4, color:"#cbd5e1" }}>Le coach renseigne les présences depuis son espace.</div>
                        </div>
                      ) : (
                        /* ── GROUPÉES PAR CLASSE, JAMAIS MÉLANGÉES ──────────────────────────
                           Les présences d'un apprenant transféré viennent de plusieurs classes.
                           Les empiler dans une seule liste chronologique donnerait une suite de
                           dates sans dire laquelle appartient à quelle classe — on ne saurait
                           plus lire l'assiduité de la classe en cours, ni celle d'avant.
                           Un bloc par classe, la classe ACTUELLE en premier, les précédentes
                           ensuite et explicitement nommées comme telles. Chaque bloc porte son
                           propre taux : c'est ce que regarde le coach qui reçoit l'apprenant. */
                        (() => {
                          const groupes = new Map();
                          [...commAppPres]
                            .sort((x, y) => (y.date_seance || "").localeCompare(x.date_seance || ""))
                            .forEach(p => {
                              /* Sans `classe_nom` (repli sur l'ancienne lecture, ou séance
                                 ajoutée localement), tout retombe dans la classe du moment —
                                 c'est le comportement d'avant, qui reste juste dans ce cas. */
                              const cle = p.classe_nom || commClasseInfo?.nom || "Sa classe";
                              if (!groupes.has(cle)) groupes.set(cle, { nom: cle, passee: !!p.classe_passee, lignes: [] });
                              groupes.get(cle).lignes.push(p);
                            });
                          const blocs = [...groupes.values()].sort((x, y) => Number(x.passee) - Number(y.passee));

                          return (
                            <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
                              {blocs.map(bloc => {
                                const pointees = bloc.lignes.filter(p => ["present","absent","retard","excuse"].includes(p.statut));
                                const presents = pointees.filter(p => p.statut === "present").length;
                                const tauxBloc = pointees.length ? Math.round(presents / pointees.length * 100) : null;
                                return (
                                  <div key={bloc.nom}>
                                    {/* L'en-tête n'apparaît que s'il y a plusieurs classes : sur un
                                        apprenant jamais transféré, il n'apprendrait rien. */}
                                    {blocs.length > 1 && (
                                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, flexWrap:"wrap" }}>
                                        <span style={{ fontSize:12.5, fontWeight:800, color: bloc.passee ? "#64748b" : "#0f172a" }}>
                                          🏫 {bloc.nom}
                                        </span>
                                        {bloc.passee && (
                                          <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:20, background:"#f1f5f9", color:"#64748b" }}>
                                            ancienne classe
                                          </span>
                                        )}
                                        <span style={{ fontSize:11, color:"#94a3b8" }}>
                                          {bloc.lignes.length} séance{bloc.lignes.length > 1 ? "s" : ""}
                                          {tauxBloc !== null && ` · ${tauxBloc}% de présence`}
                                        </span>
                                      </div>
                                    )}
                                    <div style={{ display:"flex", flexDirection:"column", gap:6, opacity: bloc.passee ? 0.88 : 1 }}>
                                      {bloc.lignes.map((p, i) => {
                                        const st = STATUT_P[p.statut] || { label:p.statut, bg:"#f3f4f6", c:"#374151" };
                                        return (
                                          <div key={p.id||`${bloc.nom}-${i}`} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", borderRadius:10, background: i%2===0?"#fff":"#f8fafc", border:"1px solid #f1f5f9" }}>
                                            <div style={{ fontWeight:700, color:"#0f172a", minWidth:130, fontSize:13 }}>
                                              {p.date_seance ? new Date(p.date_seance).toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short",year:"numeric"}) : "-"}
                                            </div>
                                            <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:st.bg, color:st.c, flexShrink:0 }}>{st.label}</span>
                                            {p.note && <div style={{ fontSize:12, color:"#6b7280", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.note}</div>}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()
                      )}
                    </div>
                  );
                })()}

                {/* TESTS DE NIVEAU — tous les passages, du plus récent au plus ancien.
                    Un apprenant en repasse un à chaque demande de transfert : l'onglet
                    « Informations » n'en montrant qu'un, l'ancien niveau et le nouveau
                    n'étaient jamais lisibles côte à côte. */}
                {apprenantTab === "niveau" && (() => {
                  const passages = tousLesTestsBrutsDe(a);
                  const CEFR_C = { A1:"#94a3b8", A2:"#f59e0b", B1:"#1e3a8a", B2:"#7c3aed", C1:"#059669", C2:"#dc2626" };
                  if (!passages.length) {
                    return (
                      <div style={{ padding:"40px 20px", textAlign:"center", color:"#94a3b8" }}>
                        <div style={{ fontSize:34, marginBottom:8 }}>🎓</div>
                        <div style={{ fontSize:13, fontWeight:700, color:"#64748b" }}>Aucun test de niveau enregistré</div>
                        <div style={{ fontSize:12, marginTop:6, maxWidth:420, margin:"6px auto 0" }}>
                          Le test se fait depuis l'onglet « Tests de niveau ». Lors d'un transfert,
                          c'est l'agent placement test qui le fait passer, et le résultat remonte ici.
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                      <EvolutionNiveauTests tests={tousLesTestsDe(a)} />

                      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                        {passages.map((t, i) => {
                          const c = CEFR_C[String(t.niveau || "").toUpperCase()] || "#475569";
                          const actuel = i === 0;   // trié du plus récent au plus ancien
                          return (
                            <div key={t.id || i} style={{
                              display:"flex", alignItems:"center", gap:14, padding:"13px 16px", borderRadius:10,
                              background:"#fff", border: actuel ? `2px solid ${c}` : "1px solid #e5e7eb",
                            }}>
                              <div style={{ minWidth:60, textAlign:"center" }}>
                                <div style={{ fontSize:20, fontWeight:900, color:c, lineHeight:1.1 }}>{t.niveau || "-"}</div>
                                {t.score != null && <div style={{ fontSize:11, color:"#94a3b8" }}>{t.score}%</div>}
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:3 }}>
                                  <span style={{ fontSize:13, fontWeight:700, color:"#1f2937" }}>
                                    {formatDate(t.date || t.submitted_at)}
                                  </span>
                                  {actuel && <span style={{ fontSize:9, fontWeight:800, color:"#15803d", background:"#dcfce7", padding:"2px 7px", borderRadius:99 }}>NIVEAU ACTUEL</span>}
                                  <span style={{ fontSize:10, fontWeight:700, color:"#475569", background:"#f1f5f9", padding:"2px 8px", borderRadius:99 }}>
                                    {t.source === "oral" ? "Évalué en présentiel" : "Passé en ligne"}
                                  </span>
                                  {t.correction_statut === "en_attente" && (
                                    <span style={{ fontSize:10, fontWeight:800, color:"#b45309", background:"#fef3c7", padding:"2px 8px", borderRadius:99 }}>À corriger</span>
                                  )}
                                </div>
                                <div style={{ fontSize:11, color:"#94a3b8" }}>
                                  {t.points_earned != null && t.points_total ? `${t.points_earned}/${t.points_total} pts · ` : ""}
                                  {t.correct_answers != null && t.total_questions ? `${t.correct_answers}/${t.total_questions} bonnes réponses` : ""}
                                  {t.notes ? (t.correct_answers != null ? " · " : "") + t.notes : ""}
                                </div>
                              </div>
                              <button
                                onClick={() => { setSelectedTest(t); setShowTestDetailModal(true); }}
                                style={{ padding:"6px 13px", borderRadius:7, border:"1px solid #bae6fd", background:"#e0f2fe",
                                  color:"#0369a1", fontSize:12, fontWeight:700, cursor:"pointer", flexShrink:0 }}
                              >🔍 Détail</button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* PAIEMENT */}
                {apprenantTab === "paiement" && (() => {
                  const savePlan = async (newPlan) => {
                    if (planActionLoading) return;
                    setPlanActionLoading(true);
                    try { await handleSavePlanPaiement(a.id, newPlan); }
                    finally { setPlanActionLoading(false); }
                  };

                  // Réactiver un plan mis en pause ne peut PAS se faire par un simple
                  // actif:true : la boucle du cron repartirait du dernier mois d'avant la
                  // pause et générerait tous les mois du creux d'un coup, le jour même.
                  // La route de reprise rebase le plan sur le mois courant.
                  const suspensionOuverte = (plan.suspensions || []).find(sp => !sp.fin) || null;
                  const reactiverPlan = async () => {
                    if (planActionLoading) return;
                    if (!suspensionOuverte) { await savePlan({ ...plan, actif:true }); return; }
                    setPlanActionLoading(true);
                    try {
                      const r = await fetch(`${API_URL}/api/parcours/assignations/${a.id}/reprendre-plan`, {
                        method:"POST", headers: authHeaders(),
                        body: JSON.stringify({ date_reprise: new Date().toISOString().slice(0,10) }),
                      });
                      const d = await r.json().catch(()=>({}));
                      if (!r.ok) { toast.error(d.error || "Reprise impossible"); return; }
                      toast.success(d.prolongation
                        ? `▶️ Facturation reprise - contrat prolongé de ${d.prolongation} mois`
                        : "▶️ Facturation reprise");
                      if (d.plan_paiement) {
                        setAssignations(prev => prev.map(x => x.id === a.id ? { ...x, plan_paiement: d.plan_paiement } : x));
                        setApprenantModal(prev => prev?.id === a.id ? { ...prev, plan_paiement: d.plan_paiement } : prev);
                      }
                    } catch { toast.error("Erreur réseau"); }
                    finally { setPlanActionLoading(false); }
                  };
                  const todayStr = new Date().toISOString().slice(0,10);
                  const moisPaiements = plan.mois_paiements || [];
                  const hasPlanMensuel = moisPaiements.length > 0;

                  // ── Ouvrir le formulaire de paiement pour un mois/tranche ──
                  const ouvrirPaiementMois = (mois, trancheId = null) => {
                    const tDejaPaye = (mois.tranches||[]).filter(t=>t.statut==="paye").reduce((s,t)=>s+(t.montant||0),0);
                    const tranche   = trancheId ? (mois.tranches||[]).find(t=>t.id===trancheId) : null;
                    const montantCe = tranche ? tranche.montant : Math.max(0, mois.montant - tDejaPaye);
                    const ml = libelleCanal(a.type_cours, a.centre_nom);
                    setMoisPayTarget({ assignationId:a.id, moisId:mois.id, trancheId, plan });
                    setEditingItem({
                      client: a.prospect_nom||"", email: a.prospect_email||"", telephone: a.prospect_telephone||"",
                      offre: offreEffective||"", inscription: `${mois.label} - ${ml}`,
                      montantDu: mois.montant, montantReçu: montantCe, dejaReçu: tDejaPaye,
                      date: todayStr, mode:"Mobile Money", statut:"reçu",
                      notes: `Mensualité ${mois.label}`, refTransaction:"", assignationId: a.id, preuveImage:null, _isApprenant: true,
                      moisLabel: mois.label,
                    });
                    setShowPaiementModal(true);
                  };

                  // ── Générer un lien de paiement TouchPoint pour un mois précis (le client
                  // paie lui-même en ligne, au lieu que la commerciale saisisse le versement
                  // manuellement) - mois_id est lu par finalizeSuccessfulPayment côté backend
                  // pour marquer automatiquement ce mois payé dès la confirmation TouchPoint.
                  const envoyerLienMois = (mois, trancheId = null) => {
                    if (!a.prospect_email || !(Number(mois.montant) > 0)) { toast.error("Informations insuffisantes pour générer un lien."); return; }
                    const tDejaPaye = (mois.tranches||[]).filter(t=>t.statut==="paye").reduce((s,t)=>s+(t.montant||0),0);
                    const tranche   = trancheId ? (mois.tranches||[]).find(t=>t.id===trancheId) : null;
                    const montantCe = tranche ? tranche.montant : Math.max(0, mois.montant - tDejaPaye);
                    const SITE = process.env.REACT_APP_FRONTEND_URL || "http://localhost:3000";
                    const qs = new URLSearchParams({
                      nom: a.prospect_nom || "", email: a.prospect_email || "", telephone: a.prospect_telephone || "",
                      montant: String(montantCe), offre: offreEffective || mois.label || "Votre formation",
                      assignation_id: a.id || "", assistante_id: assistanteId || "", mois_id: mois.id,
                    });
                    const lien = `${SITE}/paiement/relancer?${qs.toString()}`;
                    navigator.clipboard.writeText(lien).then(() => toast.success("Lien de paiement copié !")).catch(() => toast.error("Impossible de copier"));
                  };

                  // ── Modifier le montant et/ou l'échéance d'un mois précis - l'apprenant
                  // ne paie pas forcément le jour d'ancrage du plan ni le montant standard
                  // (prorata, remise ponctuelle...) ; ne modifie que ce mois précis.
                  const ouvrirModifierEcheance = (mois, echeanceISO) => {
                    if (!peut("finances","create")) return;
                    setMoisEcheanceTarget({ assignationId: a.id, mois, plan });
                    setMoisEcheanceValeur(echeanceISO);
                    // Montant standard réaffiché = montant avant réduction si une réduction est
                    // déjà active sur ce mois, sinon le montant actuel.
                    setMoisMontantValeur(String(mois.reduction?.montant_avant ?? mois.montant));
                    setMoisReductionActive(!!mois.reduction);
                    setMoisReductionMotif(mois.reduction?.motif || "");
                    setMoisReductionType(mois.reduction?.type || "pourcentage");
                    setMoisReductionValeur(mois.reduction ? String(mois.reduction.valeur) : "");
                  };

                  // ── Générer mois suivant manuellement ──
                  const genererMoisSuivant = async () => {
                    const last = moisPaiements[moisPaiements.length - 1];
                    if (!last) return;
                    const d = new Date(last.periode + "-01"); d.setMonth(d.getMonth()+1);
                    const nextPeriode = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
                    if (moisPaiements.some(m => m.periode === nextPeriode)) return;
                    await savePlan({ ...plan, mois_paiements:[...moisPaiements, { id:`m_${nextPeriode}`, periode:nextPeriode, label:getMoisLabel(nextPeriode), montant:plan.mensualite||0, statut:"en_attente", tranches:[] }] });
                  };

                  const tranchesPaye  = tranches.filter(t => t.statut === "payé").reduce((s,t) => s + (t.montant||0), 0);
                  const tranchesTotal = tranches.reduce((s,t) => s + (t.montant||0), 0);
                  const isSolde = hasPlanMensuel
                    ? moisPaiements.every(m => getMoisStatut(m, todayStr, plan.jour_echeance) === "paye")
                    : tranches.length > 0
                      ? tranchesTotal > 0 && tranchesPaye >= tranchesTotal
                      : totalDu > 0 && totalReçu >= totalDu;

                  /* Stats plan mensuel - LES MOIS NEUTRALISES SONT EXCLUS.
                     Un mois annulé par un arrêt de formation porte `montant: 0` : sans ce
                     filtre il passait pour « payé » (0 ≥ 0) et gonflait le compteur des
                     mois réglés, en faisant croire à un dossier plus à jour qu'il ne l'est.
                     Il reste affiché dans la liste, simplement il ne compte plus. */
                  const moisFacturables   = hasPlanMensuel ? moisPaiements.filter(m => !m.neutralise) : [];
                  const totalPayéMensuel  = hasPlanMensuel ? moisPaiements.reduce((s,m)=>(m.tranches||[]).filter(t=>t.statut==="paye").reduce((ss,t)=>ss+(t.montant||0),s),0) : 0;
                  const moisPayésCount    = moisFacturables.filter(m=>getMoisStatut(m,todayStr,plan.jour_echeance)==="paye").length;
                  const moisRetardCount   = moisFacturables.filter(m=>getMoisStatut(m,todayStr,plan.jour_echeance)==="retard").length;
                  const moisAnnulesCount  = hasPlanMensuel ? moisPaiements.length - moisFacturables.length : 0;
                  const totalMensuelPrévu = moisFacturables.reduce((s,m)=>s+(m.montant||0),0);
                  // La suspension encore ouverte, s'il y en a une : c'est elle qui explique
                  // les mois annulés, et le bandeau la nomme.
                  const suspensionOuverteInfo = (plan.suspensions || []).find(s => !s.fin) || null;

                  // ── Reçu de paiement imprimable (même gabarit que la fiche apprenant) ──
                  const exporterRecuPaiementPDF = (p) => {
                    try {
                      const num = `BET-REC-${String(p.id || a.id || "").slice(0,8).toUpperCase()}`;
                      const now = new Date().toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" });
                      const row = (lbl, val) => val==null || val==="" ? "" : `<tr><td class="lbl">${lbl}</td><td class="val">${val}</td></tr>`;
                      const w = window.open("","_blank","width=760,height=920");
                      w.document.write(`<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Reçu de paiement - ${a.prospect_nom||""}</title>
                      <style>
                        *{box-sizing:border-box}
                        body{font-family:Arial,sans-serif;margin:0;padding:40px;color:#1e293b;background:#fff}
                        .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #0891b2;padding-bottom:20px;margin-bottom:28px}
                        .logo{font-size:28px;font-weight:900;color:#0891b2;letter-spacing:-1pxdisplay:flex;align-items:center;gap:11px}.logo::before{content:'';width:40px;height:40px;flex-shrink:0;background:#fff url('https://res.cloudinary.com/nvp7nxqp/image/upload/v1788473005/bet/branding/bet-logo.png') center / 28px 28px no-repeat;border-radius:9px;box-shadow:0 2px 7px rgba(0,0,0,.14)}
                        .logo span{color:#0f172a}
                        .logo-sub{font-size:11px;color:#6b7280;margin-top:4px;margin-left:51px}
                        h2{margin:0 0 4px;font-size:20px;font-weight:800;color:#0f172a}
                        .num{font-size:11px;color:#6b7280;margin-top:2px}
                        section{margin-bottom:20px}
                        .section-title{font-size:11px;font-weight:800;color:#0891b2;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;border-bottom:1px solid #e0f2fe;padding-bottom:4px}
                        table{width:100%;border-collapse:collapse}
                        td{padding:9px 12px;border-bottom:1px solid #f1f5f9;font-size:13px;vertical-align:top}
                        .lbl{color:#6b7280;font-weight:500;width:42%}
                        .val{font-weight:600;color:#0f172a}
                        .amount{text-align:center;background:#f0fdf4;border:1.5px solid #bbf7d0;border-radius:12px;padding:18px;margin-bottom:20px}
                        .amount .big{font-size:28px;font-weight:900;color:#15803d}
                        .amount .tag{display:inline-block;margin-top:8px;background:#dcfce7;color:#166534;padding:4px 16px;border-radius:20px;font-weight:700;font-size:12px}
                        .footer{margin-top:36px;padding-top:14px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af;text-align:center;line-height:1.7}
                        @media print{body{padding:20px}*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}}
                      </style></head><body>
                      <div class="header">
                        <div>
                          <div class="logo">BET<span>Languages</span></div>
                          <div class="logo-sub">Centre de langues - Abidjan, Côte d'Ivoire</div>
                          <div class="logo-sub" style="margin-top:2px">📧 contact@binniesenglish.com · 📞 +225 27 22 40 71 99</div>
                        </div>
                        <div style="text-align:right">
                          <h2>REÇU DE PAIEMENT</h2>
                          <div class="num">N° ${num}</div>
                          <div class="num">Émis le ${now}</div>
                        </div>
                      </div>

                      <div class="amount">
                        <div class="big">${Number(p.montantReçu||0).toLocaleString("fr-FR")} FCFA</div>
                        <div class="tag">✅ Paiement reçu</div>
                      </div>

                      ${row("Client", p.client || a.prospect_nom) ? `<section><div class="section-title">👤 Client</div><table>
                        ${row("Nom complet", p.client || a.prospect_nom)}
                        ${row("Email", a.prospect_email)}
                        ${row("Téléphone", a.prospect_telephone)}
                      </table></section>` : ""}

                      <section><div class="section-title">💳 Détails du paiement</div><table>
                        ${row("Offre", p.offre || offreEffective)}
                        ${row("Mode", modeLabel)}
                        ${row("Moyen de paiement", p.mode)}
                        ${row("Date", p.date ? new Date(p.date).toLocaleDateString("fr-FR") : null)}
                        ${row("Montant dû", p.montantDu ? `${Number(p.montantDu).toLocaleString("fr-FR")} FCFA` : null)}
                        ${row("Montant reçu", `${Number(p.montantReçu||0).toLocaleString("fr-FR")} FCFA`)}
                        ${row("Opérateur", p.operateur)}
                        ${row("Référence transaction", p.refTransaction)}
                        ${row("Token", p.paymentToken)}
                        ${row("Notes", p.notes)}
                      </table></section>

                      <div class="footer">
                        Ce reçu est généré automatiquement par le système BET Languages. Conservez ce document pour vos archives.<br/>
                        BET Languages · Centre de langues agréé · Abidjan, Côte d'Ivoire<br/>
                        contact@binniesenglish.com · +225 27 22 40 71 99
                      </div>
                      <script>window.onload=()=>window.print();</script>
                      </body></html>`);
                      w.document.close();
                      toast.success("Reçu exporté en PDF");
                    } catch (e) { toast.error("Erreur lors de l'export du reçu"); }
                  };

                  return (
                    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

                      {/* Code promo appliqué à ce dossier - affiché directement sur le mois
                          concerné (le 1er, remise à l'inscription) plutôt qu'en bandeau général
                          au-dessus de tout le plan, ce qui prêtait à confusion sur le mois
                          réellement concerné par la remise. Voir la ligne du 1er mois plus bas. */}

                      {/* ══ PLAN MENSUEL ══ */}
                      {hasPlanMensuel && (() => {
                        const isOuvert = !plan.date_fin_formation;
                        const lastMois = moisPaiements[moisPaiements.length-1];
                        const lastPaye = lastMois && getMoisStatut(lastMois, todayStr, plan.jour_echeance) === "paye";
                        const SICO = { paye:{ icon:"✅", c:"#15803d", bg:"#f0fdf4", border:"#bbf7d0" }, partiel:{ icon:"🔄", c:"#0891b2", bg:"#e0f2fe", border:"#bae6fd" }, retard:{ icon:"❌", c:"#b91c1c", bg:"#fef2f2", border:"#fca5a5" }, en_attente:{ icon:"⏳", c:"#d97706", bg:"#fffbeb", border:"#fde68a" } };
                        return (
                          <div style={{ borderRadius:12, border:"1.5px solid #e2e8f0", overflow:"hidden" }}>
                            {/* Header plan */}
                            <div style={{ background:BET_GRADIENT, padding:"12px 16px", color:"#fff" }}>
                              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                                <div>
                                  <div style={{ fontSize:13, fontWeight:800 }}>💳 Plan mensuel - {Number(plan.mensualite||0).toLocaleString("fr-FR")} F/mois</div>
                                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.7)", marginTop:2 }}>
                                    {plan.date_debut_formation && `Début : ${new Date(plan.date_debut_formation).toLocaleDateString("fr-FR")}`}
                                    {plan.date_fin_formation   && ` · Fin : ${new Date(plan.date_fin_formation).toLocaleDateString("fr-FR")}`}
                                    {isOuvert && " · Durée ouverte"}
                                  </div>
                                  {/* ── QUAND L'EN-TÊTE ET LES LIGNES NE DISENT PAS LA MÊME CHOSE ──
                                      L'en-tête affiche `plan.mensualite`, c'est-à-dire le prix des
                                      PROCHAINS mois ; les lignes affichent le montant propre à
                                      chaque mois, figé à sa création. Après un changement de
                                      tarif, l'écran annonçait donc « 5 F/mois » au-dessus de deux
                                      mois à 10 F et d'un total de 20 F, sans un mot d'explication :
                                      l'assistante en concluait que son nouveau montant n'avait pas
                                      été pris en compte. Il l'était — mais seulement pour la suite.
                                      On le dit, plutôt que de laisser le lecteur arbitrer entre
                                      deux chiffres qui se contredisent. */}
                                  {(() => {
                                    /* ── CE QUE CE MESSAGE DOIT DIRE, ET CE QU'IL DISAIT DE FAUX ──
                                       Il comparait TOUS les mois à `plan.mensualite` et sortait un
                                       ⚠ dès qu'un seul différait, en concluant toujours « le
                                       nouveau montant s'applique aux mois créés ensuite ».

                                       Deux mensonges pour un dossier où l'on a coché « dès le mois
                                       en cours ». Vu le 07/10/2026 sur Doumbia seydou : tarif passé
                                       de 10 à 5 F, octobre ET novembre bien réécrits à 5 — la
                                       fonctionnalité avait parfaitement marché. Restait septembre,
                                       passé et réglé 10 F. L'écran l'annonçait en ⚠ comme si
                                       quelque chose n'avait pas pris, et affirmait que le nouveau
                                       tarif ne vaudrait que pour la suite, alors qu'il venait
                                       justement d'être appliqué au mois en cours.

                                       UN MOIS PASSÉ OU DÉJÀ PAYÉ NE SE RÉÉCRIT PAS — c'est la règle
                                       du serveur, et elle est juste : on ne refacture pas ce qui est
                                       encaissé. Le signaler comme une anomalie apprend à ignorer
                                       l'avertissement, qui ne sert alors plus à rien le jour où il
                                       dit vrai.

                                       On sépare donc les deux populations :
                                         · à venir, impayés, encore à l'ancien prix → ACTIONNABLE,
                                           le tarif ne leur a pas été appliqué, ⚠ ;
                                         · passés ou déjà réglés → le passé garde son prix, simple
                                           note, sans alarme. */
                                    const mois = (moisFacturables || []);
                                    const nouvelle = Number(plan.mensualite || 0);
                                    if (!nouvelle || !mois.length) return null;

                                    const periodeCourante = todayStr.slice(0, 7);
                                    const regle = (m) => (m.tranches || [])
                                      .reduce((t, x) => t + (Number(x.montant) || 0), 0) > 0;

                                    const aRevoir = mois.filter(m =>
                                      Number(m.montant || 0) !== nouvelle
                                      && String(m.periode || "") >= periodeCourante
                                      && !regle(m));
                                    const acquis = mois.filter(m =>
                                      Number(m.montant || 0) !== nouvelle
                                      && !(String(m.periode || "") >= periodeCourante && !regle(m)));

                                    if (!aRevoir.length && !acquis.length) return null;

                                    const chg = Array.isArray(plan.changements_tarif) && plan.changements_tarif.length
                                      ? plan.changements_tarif[plan.changements_tarif.length - 1] : null;
                                    const leChangement = chg?.le
                                      ? ` le ${new Date(chg.le).toLocaleDateString("fr-FR")}` : "";
                                    const f = (n) => Number(n || 0).toLocaleString("fr-FR");

                                    /* Le cas qui APPELLE UNE ACTION passe en premier et seul : s'il
                                       y a quelque chose à corriger, c'est cela qu'on doit lire. */
                                    if (aRevoir.length) {
                                      return (
                                        <div style={{ fontSize:10.5, color:"#fde68a", marginTop:4, lineHeight:1.45 }}>
                                          ⚠ {aRevoir.length} mois à venir {aRevoir.length > 1 ? "sont encore" : "est encore"} à{" "}
                                          {f(aRevoir[0].montant)} F au lieu de {f(nouvelle)} F.
                                          {/* L'ACTION EST ICI, et plus dans une phrase qui renvoie ailleurs.
                                              Le texte disait « refaites le changement de tarif en cochant… » :
                                              rouvrir une demande de transfert entière pour une case à cocher,
                                              personne ne le fait, et l'écart reste en place des mois. */}
                                          {" "}
                                          <button
                                            onClick={() => setAlignTarif({
                                              assignationId: a.id, prospectNom: a.prospect_nom,
                                              mensualite: nouvelle, aRevoir, changement: chg,
                                            })}
                                            style={{
                                              background:"rgba(255,255,255,0.16)", border:"1px solid rgba(255,255,255,0.3)",
                                              color:"#fff", borderRadius:6, padding:"2px 8px", fontSize:10.5,
                                              fontWeight:700, cursor:"pointer", marginLeft:2,
                                            }}
                                          >
                                            Aligner les mois à venir
                                          </button>
                                        </div>
                                      );
                                    }

                                    return (
                                      <div style={{ fontSize:10.5, color:"rgba(255,255,255,0.72)", marginTop:4, lineHeight:1.45 }}>
                                        Tarif passé à {f(nouvelle)} F{leChangement}. {acquis.length} mois déjà réglé
                                        {acquis.length > 1 ? "s restent" : " reste"} à {f(acquis[0].montant)} F : le passé garde son prix.
                                      </div>
                                    );
                                  })()}
                                </div>
                                <div style={{ textAlign:"right" }}>
                                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.7)" }}>{moisPayésCount}/{moisFacturables.length} mois</div>
                                  <div style={{ fontSize:13, fontWeight:800 }}>{Number(totalPayéMensuel).toLocaleString("fr-FR")} F payés</div>
                                </div>
                              </div>
                              {/* Barre progression */}
                              <div style={{ marginTop:8, height:5, background:"rgba(255,255,255,0.2)", borderRadius:999, overflow:"hidden" }}>
                                <div style={{ height:"100%", borderRadius:999, background:"#22c55e", width:`${totalMensuelPrévu>0?Math.min(100,Math.round(totalPayéMensuel/totalMensuelPrévu*100)):0}%`, transition:"width .4s" }} />
                              </div>
                            </div>

                            {/* Bandeau de suspension - il NOMME la cause des mois annulés
                                juste en dessous. Sans lui, on lisait « 0 F » sur un mois
                                sans savoir si c'était une erreur de saisie ou une décision. */}
                            {suspensionOuverteInfo && (
                              <div style={{ padding:"10px 14px", background:"#fff7ed", borderBottom:"1px solid #fed7aa", display:"flex", alignItems:"flex-start", gap:9 }}>
                                <span style={{ fontSize:15, lineHeight:1.2 }}>⏸️</span>
                                <div style={{ fontSize:11.5, color:"#9a3412", lineHeight:1.55 }}>
                                  <strong>Facturation suspendue depuis {suspensionOuverteInfo.debut || "-"}</strong>
                                  {suspensionOuverteInfo.motif ? ` - ${suspensionOuverteInfo.motif}` : ""}
                                  {suspensionOuverteInfo.cree_par ? ` · par ${suspensionOuverteInfo.cree_par}` : ""}
                                  <div style={{ color:"#b45309", marginTop:2 }}>
                                    {moisAnnulesCount > 0
                                      ? `${moisAnnulesCount} mensualité${moisAnnulesCount>1?"s":""} annulée${moisAnnulesCount>1?"s":""} - aucun prélèvement ni rappel sur cette période.`
                                      : "Aucune mensualité à venir n'était à annuler."}
                                    {" "}À la reprise, la facturation repartira du mois en cours, sans rattrapage.
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* KPIs */}
                            {(moisRetardCount > 0 || moisPayésCount > 0 || moisAnnulesCount > 0) && (
                              <div style={{ display:"flex", gap:0, borderBottom:"1px solid #e2e8f0" }}>
                                {[
                                  { l:"Payés", v:moisPayésCount,  c:"#15803d", bg:"#f0fdf4" },
                                  { l:"Retard", v:moisRetardCount, c:"#b91c1c", bg:"#fef2f2" },
                                  ...(moisAnnulesCount > 0 ? [{ l:"Annulés", v:moisAnnulesCount, c:"#9a3412", bg:"#fff7ed" }] : []),
                                  { l:"Total prévu", v:`${Number(totalMensuelPrévu).toLocaleString("fr-FR")} F`, c:"#0f172a", bg:"#f8fafc" },
                                ].map((s,i,arr) => (
                                  <div key={s.l} style={{ flex:1, textAlign:"center", padding:"8px 4px", background:s.bg, borderRight:i<arr.length-1?"1px solid #e2e8f0":"none" }}>
                                    <div style={{ fontSize:9, color:"#94a3b8", fontWeight:700 }}>{s.l}</div>
                                    <div style={{ fontSize:13, fontWeight:800, color:s.c }}>{s.v}</div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Liste des mois */}
                            <div style={{ display:"flex", flexDirection:"column" }}>
                              {moisPaiements.map((mois, idx) => {
                                /* Un mois NEUTRALISE par un arrêt de formation porte
                                   `montant: 0` et garde son `montant_initial`. Sans ce cas
                                   particulier, `getMoisStatut` le lisait « payé » (0 ≥ 0),
                                   avec sa coche verte et son bouton Payer : rien ne disait
                                   qu'il avait été annulé, ni par qui, ni pourquoi. */
                                const annule  = !!mois.neutralise;
                                const statut  = annule ? "annule" : getMoisStatut(mois, todayStr, plan.jour_echeance);
                                const si      = annule
                                  ? { icon:"🚫", bg:"#fff7ed", c:"#9a3412", border:"#fed7aa" }
                                  : (SICO[statut] || SICO.en_attente);
                                const tPaye   = (mois.tranches||[]).filter(t=>t.statut==="paye").reduce((s,t)=>s+(t.montant||0),0);
                                const hasTranches = (mois.tranches||[]).length > 0;
                                // echeance_override : échéance ajustée manuellement pour ce mois précis
                                // (l'apprenant ne paie pas forcément le jour d'ancrage du plan) - sinon
                                // calculée normalement à partir du jour d'échéance du plan.
                                const echeanceMoisDate = mois.echeance_override ? new Date(mois.echeance_override) : dateEcheanceMois(mois.periode, plan.jour_echeance || 1);
                                const echeanceMois = echeanceMoisDate.toLocaleDateString("fr-FR");
                                const echeanceMoisISO = mois.echeance_override || echeanceMoisDate.toISOString().slice(0, 10);
                                const moisKey = `${a.id}_${mois.id}`;
                                const estDeplié = !!moisDepliés[moisKey];
                                return (
                                  <div key={mois.id} style={{ borderBottom: idx < moisPaiements.length-1 ? "1px solid #f1f5f9" : "none", background: si.bg }}>
                                    {/* Ligne mois */}
                                    <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px" }}>
                                      {hasTranches ? (
                                        <button onClick={() => setMoisDepliés(prev => ({ ...prev, [moisKey]: !prev[moisKey] }))} title={estDeplié ? "Replier" : "Déplier le détail des versements"} style={{ background:"none", border:"none", cursor:"pointer", padding:0, fontSize:12, color:"#94a3b8", flexShrink:0, width:16 }}>{estDeplié ? "▾" : "▸"}</button>
                                      ) : (
                                        <span style={{ width:16, flexShrink:0 }} />
                                      )}
                                      <span style={{ fontSize:16, flexShrink:0 }}>{si.icon}</span>
                                      <div style={{ flex:1 }}>
                                        <div style={{ fontSize:13, fontWeight:800, color:"#0f172a", display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                                          {mois.label}
                                          {idx === 0 && a.code_promo && (
                                            <span style={{ display:"inline-flex", alignItems:"center", gap:4, background:"#fef3c7", color:"#92400e", borderRadius:999, padding:"2px 8px", fontSize:10, fontWeight:800 }}>
                                              🎟️ {a.code_promo}{a.reduction_montant ? ` · -${Number(a.reduction_montant).toLocaleString("fr-FR")} FCFA` : ""}
                                            </span>
                                          )}
                                          {mois.reduction && (
                                            <span title={`Montant standard : ${Number(mois.reduction.montant_avant).toLocaleString("fr-FR")} F`} style={{ display:"inline-flex", alignItems:"center", gap:4, background:"#fef3c7", color:"#92400e", borderRadius:999, padding:"2px 8px", fontSize:10, fontWeight:800 }}>
                                              🎁 {mois.reduction.motif} · -{Number(mois.reduction.montant_deduit).toLocaleString("fr-FR")} FCFA
                                            </span>
                                          )}
                                        </div>
                                        <div style={{ fontSize:10.5, color:"#94a3b8" }}>Échéance : {echeanceMois}</div>
                                        {annule && (
                                          <div style={{ fontSize:11, color:"#9a3412", marginTop:1 }}>
                                            Annulée - {mois.neutralise.motif || "arrêt de formation"}
                                            {mois.neutralise.par ? ` · ${mois.neutralise.par}` : ""}
                                            {mois.neutralise.le ? ` · le ${new Date(mois.neutralise.le).toLocaleDateString("fr-FR")}` : ""}
                                          </div>
                                        )}
                                        {!annule && statut === "partiel" && <div style={{ fontSize:11, color:"#0891b2" }}>{Number(tPaye).toLocaleString("fr-FR")} F / {Number(mois.montant).toLocaleString("fr-FR")} F payés</div>}
                                        {!annule && statut === "paye"    && <div style={{ fontSize:11, color:"#15803d" }}>{Number(mois.montant).toLocaleString("fr-FR")} F ✓</div>}
                                      </div>
                                      {annule ? (
                                        // Le montant d'ORIGINE, barré : il dit ce qui n'est plus dû.
                                        // Afficher « 0 F » aurait laissé croire à un mois gratuit.
                                        <div style={{ fontSize:12, fontWeight:800, color:"#9a3412" }}>
                                          <span style={{ textDecoration:"line-through", opacity:.65 }}>
                                            {Number(mois.montant_initial ?? mois.montant).toLocaleString("fr-FR")} F
                                          </span>
                                          <span style={{ marginLeft:6, fontSize:10.5, fontWeight:700 }}>annulée</span>
                                        </div>
                                      ) : (
                                        <div style={{ fontSize:12, fontWeight:800, color:si.c }}>{Number(mois.montant).toLocaleString("fr-FR")} F</div>
                                      )}
                                      {/* Actions - jamais sur un mois annulé : il n'y a rien à encaisser. */}
                                      {!annule && statut !== "paye" && !hasTranches && (
                                        <div style={{ display:"flex", gap:4 }}>
                                          <button disabled={!peut("finances","create")} title={permBtnTitle("Payer",peut("finances","create"))} onClick={() => { if (!peut("finances","create")) return; ouvrirPaiementMois(mois); }} style={permBtnStyle({ fontSize:11, fontWeight:700, padding:"4px 10px", borderRadius:6, border:"none", background:"#0891b2", color:"#fff", cursor:"pointer" },peut("finances","create"))}>{peut("finances","create")?"💳":"🔒"} Payer</button>
                                          <button disabled={!peut("finances","create")} title={permBtnTitle("Envoyer le lien de paiement",peut("finances","create"))} onClick={() => { if (!peut("finances","create")) return; envoyerLienMois(mois); }} style={permBtnStyle({ fontSize:11, fontWeight:700, padding:"4px 8px", borderRadius:6, border:"1px solid #e2e8f0", background:"#fff", color:"#374151", cursor:"pointer" },peut("finances","create"))}>{peut("finances","create")?"🔗":"🔒"}</button>
                                          <button disabled={!peut("finances","create")} title={permBtnTitle("Modifier le montant/l'échéance de ce mois",peut("finances","create"))} onClick={() => { if (!peut("finances","create")) return; ouvrirModifierEcheance(mois, echeanceMoisISO); }} style={permBtnStyle({ fontSize:11, fontWeight:700, padding:"4px 8px", borderRadius:6, border:"1px solid #e2e8f0", background:"#fff", color:"#374151", cursor:"pointer" },peut("finances","create"))}>{peut("finances","create")?"✏️":"🔒"}</button>
                                        </div>
                                      )}
                                      {!annule && statut === "partiel" && !hasTranches && (
                                        <div style={{ display:"flex", gap:4 }}>
                                          <button disabled={!peut("finances","create")} title={permBtnTitle("Compléter",peut("finances","create"))} onClick={() => { if (!peut("finances","create")) return; ouvrirPaiementMois(mois); }} style={permBtnStyle({ fontSize:11, fontWeight:700, padding:"4px 10px", borderRadius:6, border:"none", background:"#0891b2", color:"#fff", cursor:"pointer" },peut("finances","create"))}>{peut("finances","create")?"💳":"🔒"} Compléter</button>
                                          <button disabled={!peut("finances","create")} title={permBtnTitle("Envoyer le lien de paiement",peut("finances","create"))} onClick={() => { if (!peut("finances","create")) return; envoyerLienMois(mois); }} style={permBtnStyle({ fontSize:11, fontWeight:700, padding:"4px 8px", borderRadius:6, border:"1px solid #e2e8f0", background:"#fff", color:"#374151", cursor:"pointer" },peut("finances","create"))}>{peut("finances","create")?"🔗":"🔒"}</button>
                                          <button disabled={!peut("finances","create")} title={permBtnTitle("Modifier le montant/l'échéance de ce mois",peut("finances","create"))} onClick={() => { if (!peut("finances","create")) return; ouvrirModifierEcheance(mois, echeanceMoisISO); }} style={permBtnStyle({ fontSize:11, fontWeight:700, padding:"4px 8px", borderRadius:6, border:"1px solid #e2e8f0", background:"#fff", color:"#374151", cursor:"pointer" },peut("finances","create"))}>{peut("finances","create")?"✏️":"🔒"}</button>
                                        </div>
                                      )}
                                    </div>
                                    {/* Sous-tranches si fractionnées - repliées par défaut */}
                                    {hasTranches && estDeplié && (
                                      <div style={{ paddingLeft:38, paddingBottom:8, display:"flex", flexDirection:"column", gap:4 }}>
                                        {(mois.tranches||[]).map((t, ti) => {
                                          const tsi = t.statut==="paye" ? SICO.paye : SICO.en_attente;
                                          return (
                                            <div key={t.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 12px", borderRadius:8, background:tsi.bg, border:`1px solid ${tsi.border}` }}>
                                              <span style={{ fontSize:13 }}>{tsi.icon}</span>
                                              <div style={{ flex:1 }}>
                                                <span style={{ fontSize:12, fontWeight:700, color:"#0f172a" }}>{Number(t.montant).toLocaleString("fr-FR")} F</span>
                                                {t.date_paiement && <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>payé le {new Date(t.date_paiement).toLocaleDateString("fr-FR")}</span>}
                                                {t.mode && <span style={{ fontSize:10, color:"#94a3b8", marginLeft:6 }}>· {t.mode}</span>}
                                                {t.ref && <span style={{ fontSize:10, color:"#0891b2", fontFamily:"monospace", marginLeft:6 }}>· Réf: {t.ref}</span>}
                                              </div>
                                              {t.statut !== "paye" && (
                                                <div style={{ display:"flex", gap:4 }}>
                                                  <button disabled={!peut("finances","create")} title={permBtnTitle("Payer",peut("finances","create"))} onClick={() => { if (!peut("finances","create")) return; ouvrirPaiementMois(mois, t.id); }} style={permBtnStyle({ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:6, border:"none", background:"#0891b2", color:"#fff", cursor:"pointer" },peut("finances","create"))}>{peut("finances","create")?"💳":"🔒"} Payer</button>
                                                  <button disabled={!peut("finances","create")} title={permBtnTitle("Envoyer le lien de paiement",peut("finances","create"))} onClick={() => { if (!peut("finances","create")) return; envoyerLienMois(mois, t.id); }} style={permBtnStyle({ fontSize:11, fontWeight:700, padding:"3px 8px", borderRadius:6, border:"1px solid #e2e8f0", background:"#fff", color:"#374151", cursor:"pointer" },peut("finances","create"))}>{peut("finances","create")?"🔗":"🔒"}</button>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {/* Pied : générer mois suivant (plan ouvert) */}
                            {isOuvert && (
                              <div style={{ padding:"10px 14px", borderTop:"1px solid #e2e8f0", background:"#f8fafc" }}>
                                <button onClick={() => { if (!peut("finances","create")) return; genererMoisSuivant(); }} disabled={planActionLoading || !peut("finances","create")} title={permBtnTitle("Générer mois suivant",peut("finances","create"))} style={permBtnStyle({ ...btnSecondary, fontSize:11, width:"100%", justifyContent:"center", opacity:planActionLoading?0.6:1, cursor:planActionLoading?"not-allowed":"pointer" },peut("finances","create"))}>
                                  {peut("finances","create")?"➕":"🔒"} Générer mois suivant
                                </button>
                              </div>
                            )}

                            {/* Récurrence automatique : génération + rappels J-7/J-3/J-1 tournent tant
                                que ce plan n'a pas été explicitement arrêté - distinct de l'annulation
                                complète du dossier, le dossier et l'apprenant restent actifs. */}
                            <div style={{ padding:"10px 14px", borderTop:"1px solid #e2e8f0", background: plan.actif===false ? "#fef2f2" : "#f0fdf4" }}>
                              {plan.actif === false ? (
                                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
                                  <span style={{ fontSize:11.5, color:"#b91c1c", fontWeight:700 }}>🚫 Récurrence arrêtée - plus aucune génération ni rappel automatique</span>
                                  <button disabled={planActionLoading || !peut("finances","create")} onClick={async () => { if (!peut("finances","create")) return; await reactiverPlan(); }} style={{ padding:"5px 12px", borderRadius:7, border:"1px solid #86efac", background:"#fff", color:"#15803d", fontSize:11, fontWeight:700, cursor: planActionLoading?"not-allowed":"pointer", whiteSpace:"nowrap" }}>
                                    ↺ Réactiver
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
                                  <span style={{ fontSize:11.5, color:"#15803d", fontWeight:700 }}>✅ Récurrence active - génération et rappels automatiques</span>
                                  <button
                                    disabled={planActionLoading || !peut("finances","create")}
                                    onClick={async () => {
                                      if (!peut("finances","create")) return;
                                      if (!(await confirm("Plus aucun mois ne sera généré, ni aucun rappel envoyé à l'apprenant. Les mois déjà générés restent inchangés.", { titre: "Arrêter la récurrence de ce plan ?", confirmLabel: "Arrêter", danger: true }))) return;
                                      await savePlan({ ...plan, actif:false });
                                    }}
                                    style={{ padding:"5px 12px", borderRadius:7, border:"1px solid #fca5a5", background:"#fff", color:"#dc2626", fontSize:11, fontWeight:700, cursor: planActionLoading?"not-allowed":"pointer", whiteSpace:"nowrap" }}>
                                    🚫 Arrêter la récurrence
                                  </button>
                                </div>
                              )}
                            </div>

                          </div>
                        );
                      })()}

                      {/* ── Configurer le plan mensuel (toujours visible si pas encore créé) ── */}
                      {!hasPlanMensuel && peut("finances","create") && (() => {
                        const classeApp  = classes.find(cl => cl.id === a.classe_id);
                        const offreApp   = matchOffreCatalogue(catalogueOffres, offreEffective);
                        /* LA MENSUALITÉ NÉGOCIÉE PRIME sur le tarif catalogue.
                           L'échéancier est souvent créé plusieurs jours après l'inscription,
                           parfois par quelqu'un d'autre : proposer le prix public effaçait
                           la remise accordée au comptoir, sans que personne le remarque. */
                        const mensNegociee = Number(plan?.mensualite) > 0 && plan?.negociee ? Number(plan.mensualite) : 0;
                        const mensAuto   = mensNegociee || offreApp?.prix_num || 0;
                        const debutAuto  = classeApp?.date_debut || new Date().toISOString().slice(0,10);
                        const finAuto    = classeApp?.date_fin   || "";
                        return (
                          <SetupPlanMensuel
                            mensAuto={mensAuto}
                            negociee={mensNegociee ? plan.negociee : null}
                            tarifCatalogue={offreApp?.prix_num || null}
                            debutAuto={debutAuto}
                            finAuto={finAuto}
                            classeNom={classeApp?.nom || ""}
                            offreNom={offreEffective || ""}
                            onGenerer={async ({ mensualite, dateDebut, dateFin }) => {
                              const moisG = genererMoisPaiements(dateDebut, dateFin||null, mensualite);
                              /* La remise déposée à l'inscription s'applique au PREMIER mois,
                                 et à lui seul. On la recalcule sur la mensualité réellement
                                 retenue ici - elle a pu être ajustée entre-temps - plutôt que
                                 de reprendre un montant figé qui ne correspondrait plus. */
                              const promo1 = plan?.promo_premiere_mensualite;
                              if (promo1 && moisG.length) {
                                const base = Number(moisG[0].montant) || 0;
                                const deduit = promo1.type_reduction === "pourcentage"
                                  ? Math.round(base * (Number(promo1.valeur) / 100))
                                  : Math.min(Number(promo1.valeur), base);
                                moisG[0] = {
                                  ...moisG[0],
                                  montant: Math.max(0, base - deduit),
                                  reduction: {
                                    motif: promo1.code,
                                    type: promo1.type_reduction === "pourcentage" ? "pourcentage" : "montant",
                                    valeur: Number(promo1.valeur),
                                    montant_avant: base,
                                    montant_deduit: deduit,
                                  },
                                };
                              }
                              // jour_echeance : ancre l'échéance mensuelle sur le vrai jour de départ
                              // (ex: démarré le 20 → dû le 20 chaque mois), plutôt que de forcer le 1er.
                              // actif:true - la récurrence tourne tant que "Arrêter la récurrence" n'a
                              // pas été cliqué (job cron ignore alors ce dossier).
                              const jourEcheance = new Date(dateDebut).getDate();
                              await savePlan({ ...plan, type:"mensuel", mensualite, date_debut_formation:dateDebut, date_fin_formation:dateFin||null, mois_paiements:moisG, jour_echeance:jourEcheance, actif:true });
                            }}
                          />
                        );
                      })()}

                      {/* Alerts from tranches */}
                      {tranches.filter(t=>t.statut==="en_attente").map(t => {
                        const d = new Date(t.date_echeance); d.setHours(0,0,0,0);
                        const diff = Math.ceil((today - d) / 86400000);
                        const daysLeft = Math.ceil((d - today) / 86400000);
                        if (diff > 7) return <div key={t.id} style={{ padding:"10px 14px", background:"#fef2f2", borderRadius:8, border:"1px solid #fca5a5", fontSize:12, color:"#b91c1c", fontWeight:700 }}>🚨 {t.label} - Retard critique de {diff} jours</div>;
                        if (diff > 0) return <div key={t.id} style={{ padding:"10px 14px", background:"#fff7ed", borderRadius:8, border:"1px solid #fed7aa", fontSize:12, color:"#c2410c", fontWeight:700 }}>⚠️ {t.label} - Retard de {diff} jour{diff>1?"s":""}</div>;
                        if (daysLeft >= 0 && daysLeft <= 5) return <div key={t.id} style={{ padding:"10px 14px", background:"#fefce8", borderRadius:8, border:"1px solid #fde68a", fontSize:12, color:"#854d0e", fontWeight:700 }}>⏰ {t.label} - Échéance dans {daysLeft} jour{daysLeft!==1?"s":""}</div>;
                        return null;
                      })}

                      {/* Existing paiements */}
                      {pmts.length > 0 && (
                        <div>
                          <div style={{ fontSize:12, fontWeight:800, color:"#374151", marginBottom:8 }}>💳 Versements enregistrés ({pmts.length})</div>
                          <div style={{ border:"1px solid #e2e8f0", borderRadius:10, overflow:"hidden" }}>
                            {pmts.map((p,i) => (
                              <div key={p.id||i} style={{ padding:"8px 14px", borderBottom:i<pmts.length-1?"1px solid #f1f5f9":"none" }}>
                                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                                  <div style={{ flex:1 }}>
                                    <div style={{ fontSize:12, fontWeight:700, color:"#0f172a" }}>{p.client || a.prospect_nom} · encaissé le {p.date || "-"}</div>
                                    <div style={{ fontSize:11, color:"#64748b" }}>{p.mode || "-"}{p.refTransaction?` · Réf: ${p.refTransaction}`:""}</div>
                                    {/* Cette liste est en camelCase : on rétablit la clé attendue plutôt
                                        que d'élargir l'utilitaire à chaque convention de nommage. */}
                                    {(()=>{ const lbl=libelleMoisSolde({ ...p, ref_transaction:p.refTransaction }, a.plan_paiement); return lbl
                                      ? <div style={{ fontSize:10, fontWeight:700, color:"#0369a1" }}>{lbl}</div>
                                      : null; })()}
                                  </div>
                                  <div style={{ textAlign:"right" }}>
                                    <div style={{ fontSize:12, fontWeight:800, color:"#22c55e" }}>+{Number(p.montantReçu||0).toLocaleString("fr-FR")} F</div>
                                    {p.montantDu > 0 && <div style={{ fontSize:10, color:"#94a3b8" }}>sur {Number(p.montantDu).toLocaleString("fr-FR")} F</div>}
                                  </div>
                                  <button onClick={() => exporterRecuPaiementPDF(p)} title="Exporter ce reçu en PDF"
                                    style={{ flexShrink:0, padding:"5px 8px", background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:6, cursor:"pointer", fontSize:12, lineHeight:1 }}>
                                    📄
                                  </button>
                                </div>
                                {(p.operateur || p.paymentToken) && (
                                  <div style={{ marginTop:6, paddingTop:6, borderTop:"1px dashed #e2e8f0", display:"flex", gap:14, flexWrap:"wrap", fontSize:10.5, color:"#0891b2" }}>
                                    {p.operateur && <span>📱 Opérateur : <strong>{p.operateur}</strong></span>}
                                    {p.paymentToken && <span style={{ fontFamily:"monospace" }}>🔑 Token : {p.paymentToken}</span>}
                                  </div>
                                )}
                              </div>
                            ))}
                            <div style={{ display:"flex", justifyContent:"space-between", padding:"8px 14px", background:"#f8fafc", borderTop:"1.5px solid #e2e8f0", fontSize:12, fontWeight:800 }}>
                              <span>Total reçu</span>
                              <span style={{ color:"#22c55e" }}>{Number(totalReçu).toLocaleString("fr-FR")} F</span>
                            </div>
                          </div>
                          <button disabled={!peut("finances","create")} title={permBtnTitle("Ajouter un versement",peut("finances","create"))} onClick={() => { if (!peut("finances","create")) return; setPmtListTarget(a); setShowPmtListModal(true); }} style={permBtnStyle({ ...btnSecondary, fontSize:11, marginTop:8, width:"100%" },peut("finances","create"))}>{peut("finances","create")?"+":"🔒"} Ajouter un versement</button>
                        </div>
                      )}
                      {pmts.length === 0 && (() => {
                        // Coaching privé : paiement bloqué tant que le PA n'a pas validé le
                        // dossier (même verrou que côté backend, mais visible avant même de
                        // cliquer, plutôt qu'un rejet après coup sans explication).
                        // Bloqué par défaut (y compris dossier_statut vide/null) - même
                        // correctif que côté backend : ne jamais laisser passer un dossier
                        // dont le statut n'est pas explicitement autorisé.
                        const dossierPasValide = a.type_coaching === "prive" && a.statut !== "converti" && !["paiement_en_attente","paiement_recu"].includes(a.dossier_statut);
                        return (
                        <button
                          disabled={isSolde || !peut("finances","create") || dossierPasValide}
                          title={dossierPasValide ? "Ce dossier doit d'abord être validé par le PA avant d'enregistrer un paiement." : permBtnTitle("Enregistrer un versement",peut("finances","create"))}
                          onClick={() => {
                            if (isSolde || !peut("finances","create") || dossierPasValide) return;
                            const typeLabel = ["groupe","standard"].includes(a.type_coaching)?"Coaching groupe":a.type_coaching==="prive"?"Coaching privé":"Formation";
                            const ml = libelleCanal(a.type_cours, a.centre_nom);
                            const offreLabel = offreEffective || `Parcours BET · ${ml} · ${typeLabel}`;
                            const offreCat = matchOffreCatalogue(catalogueOffres, offreEffective);
                            // Priorité au prix réellement choisi par le client (pack Classique/
                            // Intensive × lieu, capturé à la soumission) - le prix générique du
                            // catalogue ne reflète pas le pack sélectionné pour un coaching privé.
                            const montantContrat = a.suivi_demarrage?.cout_formation || offreCat?.prix_num || totalDu || 0;
                            setEditingItem({ client:a.prospect_nom||"", email:a.prospect_email||"", telephone:a.prospect_telephone||"", offre:offreLabel, inscription:offreLabel, montantDu:montantContrat, montantReçu:0, dejaReçu:totalReçu, date:new Date().toISOString().slice(0,10), mode:"Mobile Money", statut:"en_attente", notes:"", refTransaction:"", assignationId:a.id, preuveImage:null, _isApprenant: true, packCoachingLabel: a.type_coaching === "prive" ? `${a.suivi_demarrage?.type_coaching === "intensive" ? "Intensive - 5 séances/sem" : "Classique - 3 séances × 2h/sem"}${a.suivi_demarrage?.lieu_coaching ? " · " + (a.suivi_demarrage.lieu_coaching === "domicile" ? "À domicile" : a.suivi_demarrage.lieu_coaching === "en_ligne" ? "En ligne" : "Au cabinet") : ""}` : null, formuleGroupe: a.suivi_demarrage?.formule_groupe || null });
                            setShowPaiementModal(true);
                          }}
                          style={{ ...btnSecondary, width:"100%", fontSize:12, opacity:(isSolde || !peut("finances","create") || dossierPasValide)?0.45:1, cursor:(isSolde || !peut("finances","create") || dossierPasValide)?"not-allowed":"pointer" }}
                        >
                          {isSolde ? "✅ Paiement soldé" : dossierPasValide ? "⏳ En attente de validation PA" : !peut("finances","create") ? "🔒 Enregistrer un versement" : "💳 Enregistrer un versement"}
                        </button>
                        );
                      })()}

                    </div>
                  );
                })()}

                {/* DOCUMENTS */}
                {apprenantTab === "docs" && (
                  <div>
                    <div style={{ marginBottom:12, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div style={{ fontSize:12, fontWeight:800, color:"#374151" }}>📎 Documents du dossier ({docsApp.length})</div>
                      <button onClick={() => { setDocDossierTarget(a); setShowDocDossierModal(true); }} style={{ ...btnSecondary, fontSize:11, padding:"6px 14px" }}>+ Gérer</button>
                    </div>
                    {docsApp.length === 0 && <div style={{ textAlign:"center", padding:"40px 0", color:"#94a3b8", fontSize:12 }}>Aucun document dans le dossier.</div>}
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      {docsApp.map((doc,di) => (
                        <div key={di} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderRadius:8, border:"1px solid #e2e8f0", background:"#f8fafc" }}>
                          <span style={{ fontSize:20 }}>{DOC_TYPES.find(d=>d.key===doc.type)?.icon||"📎"}</span>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:12, fontWeight:700, color:"#0f172a" }}>{doc.nom}</div>
                            {doc.url && <a href={doc.url} target="_blank" rel="noreferrer" style={{ fontSize:11, color:"#0891b2" }}>Voir le document →</a>}
                          </div>
                          <span style={{ fontSize:10, background:"#ede9fe", color:"#6d28d9", padding:"2px 8px", borderRadius:5, fontWeight:600 }}>{DOC_TYPES.find(d=>d.key===doc.type)?.label||doc.type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* NOTES */}
                {apprenantTab === "notes" && (
                  <div>
                    <div style={{ fontSize:12, fontWeight:800, color:"#374151", marginBottom:10 }}>📝 Notes de classe - évaluées par le coach</div>
                    {!commClasseInfo?.ga_id ? (
                      <div style={{ textAlign:"center", padding:"32px 0", color:"#94a3b8", fontSize:12 }}>
                        Aucune classe liée pour l'instant - les notes apparaîtront une fois l'apprenant assigné à une classe.
                      </div>
                    ) : commAppEvalsLoading ? (
                      <div style={{ textAlign:"center", padding:"32px 0", color:"#9ca3af", fontSize:13 }}>Chargement…</div>
                    ) : commAppEvals.length === 0 ? (
                      <div style={{ textAlign:"center", padding:"32px 0", color:"#9ca3af" }}>
                        <div style={{ fontSize:28, marginBottom:8 }}>📝</div>
                        <div>Aucune évaluation publiée pour l'instant.</div>
                        <div style={{ fontSize:12, marginTop:4, color:"#cbd5e1" }}>Le coach publie et corrige les évaluations depuis son espace.</div>
                      </div>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        {commAppEvals.map(ev => {
                          const r = ev.resultat || {};
                          const ST = {
                            a_faire: { label:"À faire",  bg:"#f1f5f9", c:"#64748b" },
                            rendu:   { label:"Rendu",    bg:"#e0f2fe", c:"#0891b2" },
                            corrige: { label:"Corrigé",  bg:"#dcfce7", c:"#16a34a" },
                          };
                          const st = ST[r.statut] || ST.a_faire;
                          return (
                            <div key={ev.id} style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"12px 14px", borderRadius:10, border:"1px solid #e2e8f0", background:"#fff" }}>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                                  <span style={{ fontWeight:700, fontSize:13, color:"#0f172a" }}>{ev.titre || "Évaluation"}</span>
                                  <span style={{ fontSize:10, background:"#ede9fe", color:"#6d28d9", padding:"2px 8px", borderRadius:5, fontWeight:600 }}>{ev.type_evaluation || "devoir"}</span>
                                  <span style={{ fontSize:10, background:st.bg, color:st.c, padding:"2px 8px", borderRadius:5, fontWeight:700 }}>{st.label}</span>
                                </div>
                                <div style={{ fontSize:11, color:"#94a3b8", marginTop:3 }}>
                                  {ev.date_evaluation ? new Date(ev.date_evaluation).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"}) : "-"}
                                </div>
                                {r.commentaire_coach && (
                                  <div style={{ fontSize:12, color:"#6b7280", marginTop:6, fontStyle:"italic" }}>💬 {r.commentaire_coach}</div>
                                )}
                              </div>
                              <div style={{ textAlign:"center", flexShrink:0 }}>
                                <div style={{ fontSize:18, fontWeight:900, color: r.note!=null ? "#0f172a" : "#cbd5e1" }}>
                                  {r.note!=null ? r.note : "-"}<span style={{ fontSize:11, fontWeight:600, color:"#94a3b8" }}>/{r.note_max ?? ev.note_max ?? "-"}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* HISTORIQUE */}
                {apprenantTab === "historique" && (
                  <div>
                    <div style={{ fontSize:12, fontWeight:800, color:"#374151", marginBottom:10 }}>🕓 Parcours de l'apprenant</div>
                    {!commClasseInfo?.ga_id ? (
                      <div style={{ textAlign:"center", padding:"32px 0", color:"#94a3b8", fontSize:12 }}>
                        Aucune classe liée pour l'instant - l'historique apparaîtra une fois l'apprenant assigné à une classe.
                      </div>
                    ) : commAppHistoriqueLoading ? (
                      <div style={{ textAlign:"center", padding:"32px 0", color:"#9ca3af", fontSize:13 }}>Chargement…</div>
                    ) : commAppHistorique.length === 0 ? (
                      <div style={{ textAlign:"center", padding:"32px 0", color:"#9ca3af" }}>
                        <div style={{ fontSize:28, marginBottom:8 }}>🕓</div>
                        <div>Aucun historique pour l'instant.</div>
                        <div style={{ fontSize:12, marginTop:4, color:"#cbd5e1" }}>Les changements de classe et autres événements apparaîtront ici.</div>
                      </div>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
                        {[...commAppHistorique].reverse().map((h,i) => {
                          const estActuel = h.statut === "actif" || h.statut === "periode_test";
                          const typeLabel = h.type_cours==="en_ligne"?"💻 En ligne":h.type_cours==="hybride"?"⚡ Hybride":h.type_cours?"🏢 Présentiel":null;
                          const ouvert = commAppHistoriqueOuvert === h.id;
                          return (
                            <div key={h.id} style={{ display:"grid", gridTemplateColumns:"20px 1fr", gap:12 }}>
                              <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                                <div style={{ width:12, height:12, borderRadius:"50%", background:estActuel?"#16a34a":"#cbd5e1", marginTop:6, flexShrink:0 }} />
                                {i < commAppHistorique.length-1 && <div style={{ width:2, flex:1, background:"#e5e7eb", marginTop:2 }} />}
                              </div>
                              <div style={{ paddingBottom:18 }}>
                                <div onClick={()=>setCommAppHistoriqueOuvert(ouvert?null:h.id)} style={{ cursor:"pointer" }}>
                                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                                    <span style={{ fontWeight:800, fontSize:13, color:"#0f172a" }}>{h.groupe_nom}</span>
                                    {h.niveau && <span style={{ fontSize:10, fontWeight:800, padding:"1px 8px", borderRadius:99, background:"#1e3a8a", color:"#fff" }}>{h.niveau}</span>}
                                    {typeLabel && <span style={{ fontSize:10, fontWeight:600, color:"#64748b" }}>{typeLabel}</span>}
                                    <span style={{ fontSize:10, fontWeight:700, padding:"1px 8px", borderRadius:99, background:estActuel?"#dcfce7":"#f1f5f9", color:estActuel?"#166534":"#6b7280" }}>
                                      {estActuel ? "Classe actuelle" : "Transféré"}
                                    </span>
                                    <span style={{ fontSize:11, color:"#94a3b8", marginLeft:"auto" }}>{ouvert?"▲":"▼"}</span>
                                  </div>
                                  <div style={{ fontSize:11, color:"#94a3b8", marginTop:3 }}>
                                    {h.date_ajout ? new Date(h.date_ajout).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"}) : "-"}
                                    {h.date_retrait ? ` → ${new Date(h.date_retrait).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"})}` : estActuel ? " → aujourd'hui" : ""}
                                  </div>
                                  {h.motif && <div style={{ fontSize:12, color:"#6b7280", marginTop:4, fontStyle:"italic" }}>💬 {h.motif}</div>}
                                </div>
                                {ouvert && (
                                  <div style={{ marginTop:10, padding:"10px 12px", borderRadius:10, background:"#f8fafc", border:"1px solid #e2e8f0", display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                                    <div>
                                      <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", marginBottom:2 }}>Coach</div>
                                      <div style={{ fontSize:12, fontWeight:600, color:"#0f172a" }}>{h.coach_nom || "Non assigné"}</div>
                                      {h.coach_telephone && <div style={{ fontSize:11, color:"#0891b2" }}>📞 {h.coach_telephone}</div>}
                                    </div>
                                    <div>
                                      <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", marginBottom:2 }}>Assistante</div>
                                      <div style={{ fontSize:12, fontWeight:600, color:"#0f172a" }}>{h.assistante_nom || "-"}</div>
                                    </div>
                                    {h.centre_nom && (
                                      <div style={{ gridColumn:"1/-1" }}>
                                        <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", marginBottom:2 }}>Centre</div>
                                        <div style={{ fontSize:12, fontWeight:600, color:"#0f172a" }}>📍 {h.centre_nom}{h.centre_ville?` · ${h.centre_ville}`:""}</div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ MODALES ══ */}
      {alignTarif && (
        <ModalAlignementTarif
          cible={alignTarif}
          onClose={() => setAlignTarif(null)}
          onApplique={(planMaj) => {
            setAssignations(prev => prev.map(x => x.id === alignTarif.assignationId ? { ...x, plan_paiement: planMaj } : x));
            setApprenantModal(prev => prev?.id === alignTarif.assignationId ? { ...prev, plan_paiement: planMaj } : prev);
            setAlignTarif(null);
          }}
        />
      )}

      {showInscriptionModal && (
        <Modal title={editingItem?.id ? "Modifier l'inscription" : "Ajouter une inscription"} size="xl" onClose={() => { setShowInscriptionModal(false); setEditingItem(null); }}>
          <InscriptionForm initialData={editingItem} onSave={handleSaveInscription} onCancel={() => setShowInscriptionModal(false)} catalogueOffres={catalogueOffres} assistanteTypeCours={assistanteTypeCours} assistanteCentreId={assistanteCentreId} assistanteCentreNom={assistanteCentreNom} commercialId={profil?.id} coachingPriveConfig={coachingPriveConfig} groupeConfig={groupeConfig} />
        </Modal>
      )}

      {/* ══ MODAL LISTE PAIEMENTS APPRENANT ══ */}
      {showPmtListModal && pmtListTarget && (() => {
        const a    = pmtListTarget;
        // a.pmts (précalculé) sert quand le regroupement vient d'un client sans assignationId
        // réel (ancien paiement isolé) - sinon on filtre par assignationId comme d'habitude.
        const pmts = a.pmts || paiements.filter(p => p.assignationId === a.id);
        const totalDu   = pmts.length > 0 ? Math.max(...pmts.map(p => p.montantDu || 0)) : 0;
        const totalReçu = pmts.reduce((s,p)=>s+(p.montantReçu||0),0);
        const reste     = totalDu - totalReçu;
        const modeLabel = libelleCanal(a.type_cours, a.centre_nom);
        const PSTAT = { en_attente:{label:"En attente",color:"#d97706",bg:"#fef3c7",icon:"⏳"}, partiel:{label:"Partiel",color:"#0891b2",bg:"#e0f2fe",icon:"🔄"}, reçu:{label:"Effectué",color:"#16a34a",bg:"#dcfce7",icon:"✅"}, confirme:{label:"Effectué",color:"#16a34a",bg:"#dcfce7",icon:"✅"}, paye:{label:"Effectué",color:"#16a34a",bg:"#dcfce7",icon:"✅"}, remboursé:{label:"Remboursé",color:"#7c3aed",bg:"#ede9fe",icon:"↩️"} };

        return (
          <ModaleAnimee ouvert
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:9300, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
            panneauStyle={{ background:"#fff", borderRadius:16, width:"90vw", maxWidth:"90vw", height:"90vh", maxHeight:"90vh", overflowY:"auto", display:"flex", flexDirection:"column" }}>

              {/* Header */}
              <div style={{ background:BET_GRADIENT, padding:"18px 22px", borderRadius:"16px 16px 0 0", color:"#fff", display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexShrink:0 }}>
                <div>
                  <div style={{ fontSize:10, color:"#fde68a", fontWeight:700, letterSpacing:".06em", marginBottom:3 }}>PAIEMENTS - {a.prospect_nom}</div>
                  <h3 style={{ margin:0, fontSize:16, fontWeight:800 }}>💳 Historique des paiements</h3>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.65)", marginTop:3 }}>{modeLabel} · {pmts.length} versement{pmts.length!==1?"s":""}</div>
                </div>
                <button onClick={()=>{ setShowPmtListModal(false); setPmtListTarget(null); setPmtListExpanded(null); }} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", width:28, height:28, borderRadius:"50%", cursor:"pointer", fontSize:15, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
              </div>

              <div style={{ padding:"18px 22px", display:"flex", flexDirection:"column", gap:14 }}>

                {/* Récap financier */}
                <div style={{ display:"flex", gap:0, background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                  {[{l:"Total dû",v:totalDu,c:"#0f172a"},{l:"Total reçu",v:totalReçu,c:"#22c55e"},{l:"Reste",v:reste,c:reste>0?"#ef4444":"#22c55e"}].map((s,i,arr)=>(
                    <div key={s.l} style={{ flex:1, textAlign:"center", padding:"10px 8px", borderRight:i<arr.length-1?"1px solid #e2e8f0":"none" }}>
                      <div style={{ fontSize:10, color:"#94a3b8", marginBottom:3 }}>{s.l}</div>
                      <div style={{ fontSize:15, fontWeight:800, color:s.c }}>{Number(Math.max(0,s.v)).toLocaleString("fr-FR")} F</div>
                    </div>
                  ))}
                </div>

                {/* Liste paiements */}
                {pmts.length === 0 ? (
                  <div style={{ textAlign:"center", padding:"30px 0", color:"#94a3b8" }}>Aucun paiement enregistré.</div>
                ) : (
                  <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                    {pmts.map((p, idx) => {
                      const pmtKey = p.id || idx;
                      const isOpen = pmtListExpanded === pmtKey;
                      return (
                        <div key={pmtKey} style={{ background:"#fff", borderRadius:12, border:"1.5px solid #e2e8f0", overflow:"hidden" }}>
                          {/* Verssement individuel - pas de badge Partiel/Payé ici : ce statut décrit
                              l'état CUMULÉ après ce versement, pas ce versement isolément, donc
                              l'afficher par ligne donnait l'impression contradictoire de deux
                              statuts différents pour un même dossier. Le vrai statut (soldé ou non)
                              est déjà dans le récap agrégé ci-dessus (Total dû/reçu/reste). */}
                          <div onClick={() => setPmtListExpanded(isOpen ? null : pmtKey)} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", background:"#f8fafc", borderBottom: isOpen ? "1px solid #e2e8f0" : "none", cursor:"pointer" }}>
                            <span style={{ fontSize:10, color:"#94a3b8", transition:"transform .15s", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
                            <span>💳</span>
                            <span style={{ fontSize:11, fontWeight:700, color:"#374151" }}>Versement {pmts.length - idx}</span>
                            <span style={{ fontSize:11, color:"#64748b" }}>· {p.mode || "-"}</span>
                            <span style={{ marginLeft:"auto", fontSize:11, color:"#64748b" }}>{p.date ? new Date(p.date).toLocaleDateString("fr-FR",{day:"numeric",month:"short",year:"numeric"}) : "-"}</span>
                          </div>
                          {/* corps */}
                          {isOpen && (
                          <div style={{ padding:"10px 14px", display:"flex", flexDirection:"column", gap:6 }}>
                            <div style={{ display:"flex", gap:0 }}>
                              {[{l:"Dû",v:p.montantDu,c:"#0f172a"},{l:"Reçu",v:p.montantReçu,c:"#22c55e"},{l:"Reste",v:(p.montantDu||0)-(p.montantReçu||0),c:(p.montantDu||0)-(p.montantReçu||0)>0?"#ef4444":"#22c55e"}].map((s,i,arr)=>(
                                <div key={s.l} style={{ flex:1, textAlign:"center", borderRight:i<arr.length-1?"1px solid #f1f5f9":"none" }}>
                                  <div style={{ fontSize:9, color:"#94a3b8" }}>{s.l}</div>
                                  <div style={{ fontSize:13, fontWeight:800, color:s.c }}>{Number(Math.max(0,s.v)).toLocaleString("fr-FR")} F</div>
                                </div>
                              ))}
                            </div>
                            {p.offre && <div style={{ fontSize:11, color:"#374151" }}>📚 <strong>{p.offre}</strong></div>}
                            {p.niveau && <div style={{ fontSize:11, color:"#374151" }}>🎯 Niveau : <strong>{p.niveau}</strong></div>}
                            <div style={{ fontSize:10, color:"#94a3b8" }}>Statut de ce versement : <strong style={{ color:"#374151" }}>{p.statut || "-"}</strong></div>
                            {p.operateur && <div style={{ fontSize:10, color:"#94a3b8" }}>Opérateur : <strong style={{ color:"#374151" }}>{p.operateur}</strong></div>}
                            {p.paymentToken && <div style={{ fontSize:10, color:"#94a3b8", fontFamily:"monospace" }}>Token TouchPoint : {p.paymentToken}</div>}
                            {p.refTransaction && (
                              <div style={{ fontSize:11, color:"#0891b2", fontFamily:"monospace", background:"#f0f9ff", borderRadius:6, padding:"3px 8px", alignSelf:"flex-start" }}>
                                🔖 {p.refTransaction}
                              </div>
                            )}
                            {p.notes && <div style={{ fontSize:10, color:"#94a3b8", fontStyle:"italic" }}>📝 {p.notes}</div>}

                            {/* Preuve de paiement (chèque / virement / RIA…) */}
                            {p.preuveImage && (
                              <div style={{ marginTop:4 }}>
                                <div style={{ fontSize:10, fontWeight:700, color:"#92400e", marginBottom:4 }}>📸 Preuve de paiement</div>
                                {p.preuveImage.startsWith("data:image") ? (
                                  <img
                                    src={p.preuveImage}
                                    alt="Preuve"
                                    style={{ maxWidth:"100%", maxHeight:160, objectFit:"contain", borderRadius:6, border:"1px solid #fde68a", background:"#fffbeb", cursor:"pointer" }}
                                    onClick={() => window.open(p.preuveImage, "_blank")}
                                    title="Cliquer pour agrandir"
                                  />
                                ) : (
                                  <a href={p.preuveImage} target="_blank" rel="noopener noreferrer" style={{ fontSize:11, color:"#0891b2", fontWeight:700 }}>
                                    📎 Voir le justificatif
                                  </a>
                                )}
                              </div>
                            )}

                            {/* Bouton reçu individuel */}
                            <div style={{ display:"flex", justifyContent:"flex-end", gap:6, marginTop:6 }}>
                              <button
                                disabled={!peut("finances","update")}
                                title={permBtnTitle("Modifier",peut("finances","update"))}
                                onClick={() => { if (!peut("finances","update")) return; setEditingItem(p); setShowPaiementModal(true); }}
                                style={permBtnStyle({ fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:6, border:"1px solid #e2e8f0", background:"#f8fafc", color:"#374151", cursor:"pointer" },peut("finances","update"))}
                              >{peut("finances","update")?"✏️":"🔒"} Modifier</button>
                              <button
                                onClick={() => genererRecu(p)}
                                style={{ fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:6, border:"1px solid #bbf7d0", background:"#f0fdf4", color:"#15803d", cursor:"pointer" }}
                              >📄 Générer reçu</button>
                            </div>
                          </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Bouton nouveau paiement */}
                {(() => {
                  const isPaidUp = totalDu > 0 && totalReçu >= totalDu;
                  const inscLie = inscriptions.find(i => i.email && a.prospect_email && i.email.toLowerCase() === a.prospect_email.toLowerCase());
                  const montantTotal = a.suivi_demarrage?.cout_formation || inscLie?.montant || totalDu || 0;
                  const dossierPasValide = a.type_coaching === "prive" && a.statut !== "converti" && !["paiement_en_attente","paiement_recu"].includes(a.dossier_statut);
                  const blocked = isPaidUp || !peut("finances","create") || dossierPasValide;
                  return (
                    <button
                      disabled={blocked}
                      title={dossierPasValide ? "Ce dossier doit d'abord être validé par le PA avant d'enregistrer un paiement." : permBtnTitle("Enregistrer un nouveau paiement",peut("finances","create"))}
                      onClick={() => {
                        if (blocked) return;
                        const typeLabel = ["groupe","standard"].includes(a.type_coaching)?"Coaching groupe":a.type_coaching==="prive"?"Coaching privé":"Formation";
                        const offreLabelFallback = `Parcours BET · ${modeLabel} · ${typeLabel}`;
                        const pmtEmailLocal = !a.inscription_offre ? paiements.find(p => p.email && a.prospect_email && p.email.toLowerCase() === a.prospect_email.toLowerCase() && p.offre) : null;
                        const offreLocal = a.inscription_offre || pmtEmailLocal?.offre || offreLabelFallback;
                        setEditingItem({ client:a.prospect_nom||"", email:a.prospect_email||"", telephone:a.prospect_telephone||"", offre:offreLocal, inscription:offreLabelFallback, montantDu:montantTotal, montantReçu:0, dejaReçu:totalReçu, date:new Date().toISOString().slice(0,10), mode:"Mobile Money", statut:"en_attente", notes:"", refTransaction:"", assignationId:a.id, preuveImage:null, _isApprenant: true, packCoachingLabel: a.type_coaching === "prive" ? `${a.suivi_demarrage?.type_coaching === "intensive" ? "Intensive - 5 séances/sem" : "Classique - 3 séances × 2h/sem"}${a.suivi_demarrage?.lieu_coaching ? " · " + (a.suivi_demarrage.lieu_coaching === "domicile" ? "À domicile" : a.suivi_demarrage.lieu_coaching === "en_ligne" ? "En ligne" : "Au cabinet") : ""}` : null, formuleGroupe: a.suivi_demarrage?.formule_groupe || null });
                        setShowPmtListModal(false);
                        setShowPaiementModal(true);
                      }}
                      style={{ ...btnPrimary, width:"100%", justifyContent:"center", marginTop:4,
                        background: blocked ? "#f1f5f9" : "linear-gradient(135deg,#d97706,#f59e0b)",
                        color: blocked ? "#94a3b8" : "#fff",
                        cursor: blocked ? "not-allowed" : "pointer",
                        opacity: blocked ? 0.8 : 1,
                        border: blocked ? "1px solid #e2e8f0" : "none"
                      }}
                    >{isPaidUp ? "✅ Paiement à jour - aucun solde restant" : dossierPasValide ? "⏳ En attente de validation PA" : !peut("finances","create") ? "🔒 Enregistrer un nouveau paiement" : "+ Enregistrer un nouveau paiement"}</button>
                  );
                })()}

              </div>
          </ModaleAnimee>
        );
      })()}

      {/* ══ MODAL DÉTAIL TRANSACTION TOUCHPOINT ══ */}
      {showTpDetailModal && tpDetailTarget && (() => {
        const p = tpDetailTarget;
        const TP_STATUT = {
          validé:    { label:"✅ Validé",    color:"#16a34a", bg:"#dcfce7" },
          en_attente:{ label:"⏳ En attente", color:"#d97706", bg:"#fef3c7" },
          échoué:    { label:"❌ Échoué",     color:"#dc2626", bg:"#fee2e2" },
          annulé:    { label:"↩️ Annulé",     color:"#6b7280", bg:"#f3f4f6" },
        };
        const enAttenteDepasse = p.statut === "en_attente" && p.created_at && (Date.now() - new Date(p.created_at).getTime()) > 15*60*1000;
        const st = enAttenteDepasse
          ? { label:"⚠️ Probablement échoué", color:"#9a3412", bg:"#ffedd5" }
          : (TP_STATUT[p.statut] || { label:p.statut||"-", color:"#374151", bg:"#f3f4f6" });
        return (
          <ModaleAnimee ouvert
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:9300, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
            panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:820, maxHeight:"92vh", overflowY:"auto", display:"flex", flexDirection:"column" }}>

              {/* Header */}
              <div style={{ background:BET_GRADIENT, padding:"18px 22px", borderRadius:"16px 16px 0 0", color:"#fff", display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexShrink:0 }}>
                <div>
                  <div style={{ fontSize:10, color:"#a5f3fc", fontWeight:700, letterSpacing:".06em", marginBottom:3 }}>TRANSACTION TOUCHPOINT - {p.client_nom}</div>
                  <h3 style={{ margin:0, fontSize:16, fontWeight:800 }}>🌐 Détail du paiement en ligne</h3>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.65)", marginTop:3 }}>{formatDate(p.created_at)}</div>
                </div>
                <button onClick={()=>{ setShowTpDetailModal(false); setTpDetailTarget(null); }} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", width:28, height:28, borderRadius:"50%", cursor:"pointer", fontSize:15, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
              </div>

              <div style={{ padding:"18px 22px", display:"flex", flexDirection:"column", gap:14 }}>

                {/* Statut + montant */}
                <div style={{ display:"flex", gap:0, background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                  <div style={{ flex:1, textAlign:"center", padding:"12px 8px", borderRight:"1px solid #e2e8f0" }}>
                    <div style={{ fontSize:10, color:"#94a3b8", marginBottom:3 }}>Montant</div>
                    <div style={{ fontSize:17, fontWeight:800, color:"#0f172a" }}>{formatMoney(p.montant)}</div>
                  </div>
                  <div style={{ flex:1, textAlign:"center", padding:"12px 8px" }}>
                    <div style={{ fontSize:10, color:"#94a3b8", marginBottom:3 }}>Statut</div>
                    <div style={{ marginTop:2 }}><Badge label={st.label} color={st.color} bg={st.bg} /></div>
                  </div>
                </div>

                {/* Infos client */}
                <div style={{ background:"#fff", borderRadius:12, border:"1.5px solid #e2e8f0", padding:"12px 14px", display:"flex", flexDirection:"column", gap:6 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:2 }}>👤 Client</div>
                  <div style={{ fontSize:12, color:"#374151" }}>{p.client_nom || "-"}</div>
                  {p.client_email     && <div style={{ fontSize:11, color:"#6b7280" }}>✉ {p.client_email}</div>}
                  {p.client_telephone && <div style={{ fontSize:11, color:"#6b7280" }}>📞 {p.client_telephone}</div>}
                </div>

                {/* Détail transaction */}
                <div style={{ background:"#fff", borderRadius:12, border:"1.5px solid #e2e8f0", padding:"12px 14px", display:"flex", flexDirection:"column", gap:6 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:2 }}>🌐 Transaction TouchPoint</div>
                  {p.offre_label   && <div style={{ fontSize:11, color:"#374151" }}>📚 <strong>{p.offre_label}</strong></div>}
                  {p.payment_mode  && <div style={{ fontSize:11, color:"#374151" }}>Opérateur : <strong>{p.payment_mode}</strong></div>}
                  {p.order_number  && (
                    <div style={{ fontSize:11, color:"#0891b2", fontFamily:"monospace", background:"#f0f9ff", borderRadius:6, padding:"3px 8px", alignSelf:"flex-start" }}>
                      🔖 {p.order_number}
                    </div>
                  )}
                  {p.payment_token && <div style={{ fontSize:10, color:"#94a3b8", fontFamily:"monospace" }}>Token : {p.payment_token}</div>}
                  {p.assistante_nom && <div style={{ fontSize:11, color:"#6b7280" }}>Assistante en charge : <strong>{p.assistante_nom}</strong></div>}
                </div>

                {/* Générer le reçu - même modèle que les reçus de paiement manuel */}
                <button
                  onClick={() => genererRecuTouchPoint(p)}
                  style={{ ...btnPrimary, width:"100%", justifyContent:"center" }}
                >🧾 Générer le reçu</button>

                {/* Renvoyer le lien - même mécanisme que l'onglet Prospects, permet au client
                    de reprendre le paiement sans refaire l'inscription. */}
                {p.statut !== "validé" && p.client_email && Number(p.montant) > 0 && (
                  <button
                    onClick={() => {
                      const SITE = process.env.REACT_APP_FRONTEND_URL || "http://localhost:3000";
                      const qs = new URLSearchParams({
                        nom: p.client_nom || "", email: p.client_email || "", telephone: p.client_telephone || "",
                        montant: String(p.montant), offre: p.offre_label || "Votre formation",
                        assignation_id: p.assignation_id || "", assistante_id: p.assistante_id || "",
                      });
                      const lien = `${SITE}/paiement/relancer?${qs.toString()}`;
                      navigator.clipboard.writeText(lien).then(() => toast.success("Lien de paiement copié !")).catch(() => toast.error("Impossible de copier"));
                    }}
                    style={{ ...btnPrimary, width:"100%", justifyContent:"center", background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe" }}
                  >🔄 Renvoyer le lien de paiement</button>
                )}

              </div>
          </ModaleAnimee>
        );
      })()}

      {/* ══ MODAL PROCESSUS DE RETRAIT ══ */}
      {/* ══ MODAL PROGRESSION APPRENANT ══ */}
      {showProgressionModal && progressionTarget && (() => {
        const a         = progressionTarget;
        const pmts      = paiements.filter(p => p.assignationId === a.id);
        const totalDu   = pmts.length > 0 ? Math.max(...pmts.map(p => p.montantDu || 0)) : 0;
        const totalReçu = pmts.reduce((s,p)=>s+(p.montantReçu||0),0);
        const testLie   = tests.find(t => t.email && a.prospect_email && t.email.toLowerCase()===a.prospect_email.toLowerCase());
        const modeLabel = libelleCanal(a.type_cours, a.centre_nom);
        const coachLabel= ["groupe","standard"].includes(a.type_coaching)?"Coaching groupe":a.type_coaching==="prive"?"Coaching privé":"-";
        const inscLie   = inscriptions.find(i => i.email && a.prospect_email && i.email.toLowerCase()===a.prospect_email.toLowerCase());
        const NIVEAU_COLOR = { A1:"#6b7280",A2:"#0891b2",B1:"#16a34a",B2:"#7c3aed",C1:"#d97706",C2:"#dc2626" };
        const NIVEAUX     = ["A1","A2","B1","B2","C1","C2"];
        const progDefault = { sessions: a.suivi_presences?.sessions || [], newSessionDate: new Date().toISOString().slice(0,10), newSessionStatut:"present", seances:"", niveauActuel: testLie?.niveau || "", niveauCible:"", commentaire:"", presences:"", absences:"" };
        const prog        = progressionNotes[a.id] ? { ...progDefault, ...progressionNotes[a.id], sessions: progressionNotes[a.id].sessions ?? (a.suivi_presences?.sessions || []) } : progDefault;
        const setP        = (k,v) => setProgressionNotes(prev => ({ ...prev, [a.id]: { ...prog, [k]:v } }));
        const pctPaiement = totalDu > 0 ? Math.round((totalReçu/totalDu)*100) : 0;

        return (
          <ModaleAnimee ouvert
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:9300, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
            panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:640, maxHeight:"92vh", overflowY:"auto", display:"flex", flexDirection:"column" }}>

              {/* Header */}
              <div style={{ background:BET_GRADIENT, padding:"18px 22px", borderRadius:"16px 16px 0 0", color:"#fff", display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexShrink:0 }}>
                <div>
                  <div style={{ fontSize:10, color:"#ddd6fe", fontWeight:700, letterSpacing:".06em", marginBottom:3 }}>SUIVI DE PROGRESSION</div>
                  <h3 style={{ margin:0, fontSize:16, fontWeight:800 }}>📈 {a.prospect_nom}</h3>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.65)", marginTop:3 }}>{modeLabel} · {coachLabel}{a.assistante_nom?" · "+a.assistante_nom:""}</div>
                </div>
                <button onClick={()=>{ setShowProgressionModal(false); setProgressionTarget(null); }} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", width:28, height:28, borderRadius:"50%", cursor:"pointer", fontSize:15, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
              </div>

              <div style={{ padding:"18px 22px", display:"flex", flexDirection:"column", gap:16 }}>

                {/* ── Infos formation ── */}
                {inscLie ? (
                  <div style={{ background:"#f0f9ff", borderRadius:10, padding:"12px 14px", border:"1px solid #bae6fd" }}>
                    <div style={{ fontSize:10, fontWeight:800, color:"#0369a1", marginBottom:8, textTransform:"uppercase", letterSpacing:".06em" }}>📚 Formation inscrite</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                      {[
                        { l:"Offre", v:inscLie.offre||"-" },
                        { l:"Statut inscription", v:inscLie.statut||"-" },
                        { l:"Date de début", v:inscLie.dateDebut ? new Date(inscLie.dateDebut).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"}) : "-" },
                        { l:"Montant", v:Number(inscLie.montant||0).toLocaleString("fr-FR")+" FCFA" },
                      ].map(s=>(
                        <div key={s.l} style={{ background:"#fff", borderRadius:6, padding:"6px 10px" }}>
                          <div style={{ fontSize:9, color:"#94a3b8" }}>{s.l}</div>
                          <div style={{ fontSize:12, fontWeight:700, color:"#0f172a" }}>{s.v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ padding:"10px 14px", background:"#fafafa", borderRadius:8, border:"1px dashed #e2e8f0", fontSize:11, color:"#94a3b8" }}>
                    📋 Aucune inscription liée trouvée - finalisez d'abord l'inscription dans les Prospects.
                  </div>
                )}

                {/* ── Niveau CECRL ── */}
                <div style={{ background:"#faf5ff", borderRadius:10, padding:"12px 14px", border:"1px solid #ddd6fe" }}>
                  <div style={{ fontSize:10, fontWeight:800, color:"#7c3aed", marginBottom:10, textTransform:"uppercase", letterSpacing:".06em" }}>🎯 Niveaux CECRL</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                    <div>
                      <label style={{ fontSize:10, color:"#6b7280", display:"block", marginBottom:4 }}>Niveau initial (test)</label>
                      <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                        {NIVEAUX.map(n => (
                          <span key={n} style={{ padding:"3px 10px", borderRadius:999, fontSize:11, fontWeight:800, cursor:"default", background: (testLie?.niveau===n?NIVEAU_COLOR[n]:"#f1f5f9"), color: testLie?.niveau===n?"#fff":"#9ca3af" }}>{n}</span>
                        ))}
                      </div>
                      {testLie && <div style={{ fontSize:10, color:"#6b7280", marginTop:4 }}>Score : {testLie.score}% · {testLie.date}</div>}
                    </div>
                    <div>
                      <label style={{ fontSize:10, color:"#6b7280", display:"block", marginBottom:4 }}>Niveau actuel</label>
                      <select value={prog.niveauActuel||""} onChange={e=>setP("niveauActuel",e.target.value)} style={{ ...inputSt, margin:0, fontWeight:800 }}>
                        <option value="">– Choisir –</option>
                        {NIVEAUX.map(n=><option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize:10, color:"#6b7280", display:"block", marginBottom:4 }}>Niveau cible</label>
                      <select value={prog.niveauCible||""} onChange={e=>setP("niveauCible",e.target.value)} style={{ ...inputSt, margin:0, fontWeight:800 }}>
                        <option value="">– Choisir –</option>
                        {NIVEAUX.map(n=><option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", justifyContent:"flex-end" }}>
                      {prog.niveauActuel && prog.niveauCible && (
                        <div style={{ padding:"8px 10px", background:"#f3e8ff", borderRadius:8, fontSize:11, fontWeight:700, color:"#7c3aed" }}>
                          {NIVEAUX.indexOf(prog.niveauCible) > NIVEAUX.indexOf(prog.niveauActuel)
                            ? `🚀 Objectif : passer de ${prog.niveauActuel} → ${prog.niveauCible} (${NIVEAUX.indexOf(prog.niveauCible)-NIVEAUX.indexOf(prog.niveauActuel)} niveau${NIVEAUX.indexOf(prog.niveauCible)-NIVEAUX.indexOf(prog.niveauActuel)>1?"x":""})`
                            : prog.niveauActuel===prog.niveauCible
                              ? "✅ Niveau cible atteint !"
                              : "⚠️ Niveau cible inférieur au niveau actuel"
                          }
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Assiduité (lecture seule - saisie par le formateur) ── */}
                <div style={{ background:"#f0fdf4", borderRadius:10, padding:"12px 14px", border:"1px solid #bbf7d0" }}>
                  <div style={{ fontSize:10, fontWeight:800, color:"#15803d", marginBottom:10, textTransform:"uppercase", letterSpacing:".06em" }}>📅 Assiduité - saisie par le formateur</div>
                  {(() => {
                    const sp = a.suivi_presences || {};
                    const seances   = Number(sp.seances_effectuees) || 0;
                    const presences = Number(sp.presences)          || 0;
                    const absences  = Number(sp.absences)           || 0;
                    if (seances === 0) return (
                      <div style={{ padding:"14px", background:"#f8fafc", borderRadius:8, border:"1px dashed #d1fae5", textAlign:"center" }}>
                        <div style={{ fontSize:13, color:"#9ca3af" }}>⏳ Aucune donnée de présence pour l'instant</div>
                        <div style={{ fontSize:11, color:"#b0b9c6", marginTop:4 }}>Le formateur renseignera les présences depuis son espace</div>
                      </div>
                    );
                    const tauxPart = Math.round((presences / seances) * 100);
                    const tauxAbs  = Math.round((absences  / seances) * 100);
                    return (
                      <div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:12 }}>
                          {[
                            { l:"Séances", v:seances,   c:"#0891b2", bg:"#e0f2fe" },
                            { l:"Présences", v:presences, c:"#15803d", bg:"#dcfce7" },
                            { l:"Absences",  v:absences,  c:"#b91c1c", bg:"#fee2e2" },
                          ].map(s => (
                            <div key={s.l} style={{ background:s.bg, borderRadius:8, padding:"8px 10px", textAlign:"center" }}>
                              <div style={{ fontSize:9, color:s.c, fontWeight:600, marginBottom:2 }}>{s.l}</div>
                              <div style={{ fontSize:18, fontWeight:900, color:s.c }}>{s.v}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                          <div style={{ background:"#dcfce7", borderRadius:10, padding:"12px 14px", textAlign:"center", border:"1px solid #86efac" }}>
                            <div style={{ fontSize:10, fontWeight:700, color:"#15803d", marginBottom:4 }}>✅ TAUX DE PARTICIPATION</div>
                            <div style={{ fontSize:30, fontWeight:900, color: tauxPart>=80?"#15803d":tauxPart>=60?"#d97706":"#ef4444", lineHeight:1 }}>{tauxPart}%</div>
                          </div>
                          <div style={{ background:"#fee2e2", borderRadius:10, padding:"12px 14px", textAlign:"center", border:"1px solid #fca5a5" }}>
                            <div style={{ fontSize:10, fontWeight:700, color:"#b91c1c", marginBottom:4 }}>❌ TAUX D'ABSENTÉISME</div>
                            <div style={{ fontSize:30, fontWeight:900, color: tauxAbs<=10?"#15803d":tauxAbs<=25?"#d97706":"#ef4444", lineHeight:1 }}>{tauxAbs}%</div>
                          </div>
                        </div>
                        <div style={{ height:8, background:"#e5e7eb", borderRadius:999, overflow:"hidden" }}>
                          <div style={{ height:"100%", background: tauxPart>=80?"#22c55e":tauxPart>=60?"#f59e0b":"#ef4444", borderRadius:999, width:`${tauxPart}%`, transition:"width .4s" }} />
                        </div>
                        {sp.updated_at && (
                          <div style={{ marginTop:6, fontSize:10, color:"#9ca3af" }}>
                            Mis à jour le {new Date(sp.updated_at).toLocaleDateString("fr-FR", { day:"numeric", month:"long" })} par le formateur
                          </div>
                        )}
                        {tauxAbs > 25 && (
                          <div style={{ marginTop:8, padding:"6px 10px", background:"#fff7ed", borderRadius:7, border:"1px solid #fed7aa", fontSize:11, color:"#c2410c", fontWeight:600 }}>
                            ⚠️ Taux d'absentéisme élevé - relance recommandée
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* ── Progression paiement ── */}
                <div style={{ background:"#f8fafc", borderRadius:10, padding:"12px 14px", border:"1px solid #e2e8f0" }}>
                  <div style={{ fontSize:10, fontWeight:800, color:"#374151", marginBottom:8, textTransform:"uppercase", letterSpacing:".06em" }}>💰 Progression paiement</div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"#6b7280", marginBottom:6 }}>
                    <span>{Number(totalReçu).toLocaleString("fr-FR")} F reçus sur {Number(totalDu).toLocaleString("fr-FR")} F dus</span>
                    <span style={{ fontWeight:700, color: pctPaiement===100?"#22c55e":"#d97706" }}>{pctPaiement}%</span>
                  </div>
                  <div style={{ height:8, background:"#e5e7eb", borderRadius:999, overflow:"hidden" }}>
                    <div style={{ height:"100%", borderRadius:999, width:`${pctPaiement}%`, transition:"width .4s", background: pctPaiement===100?"#22c55e":pctPaiement>=50?"#0891b2":"#f59e0b" }} />
                  </div>
                </div>

                {/* ── Commentaire libre ── */}
                {/* ── Saisie des séances ── */}
                <div style={{ background:"#f8fafc", borderRadius:10, padding:"12px 14px", border:"1px solid #e2e8f0" }}>
                  <div style={{ fontSize:10, fontWeight:800, color:"#374151", marginBottom:10, textTransform:"uppercase", letterSpacing:".06em" }}>📅 Saisie des séances</div>
                  <div style={{ display:"flex", gap:8, marginBottom:10, alignItems:"center" }}>
                    <input
                      type="date"
                      value={prog.newSessionDate || new Date().toISOString().slice(0,10)}
                      onChange={e => setP("newSessionDate", e.target.value)}
                      style={{ ...inputSt, margin:0, flex:1, fontSize:12 }}
                    />
                    <button
                      onClick={() => {
                        setP("newSessionStatut", prog.newSessionStatut === "present" ? "absent" : "present");
                      }}
                      style={{ padding:"6px 12px", borderRadius:7, border:"1.5px solid #e2e8f0", background: prog.newSessionStatut === "absent" ? "#fee2e2" : "#dcfce7", color: prog.newSessionStatut === "absent" ? "#b91c1c" : "#15803d", fontWeight:700, fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}
                    >
                      {prog.newSessionStatut === "absent" ? "❌ Absent" : "✅ Présent"}
                    </button>
                    <button
                      onClick={() => {
                        const date   = prog.newSessionDate || new Date().toISOString().slice(0,10);
                        const statut = prog.newSessionStatut || "present";
                        const updated = [...(prog.sessions || []), { date, statut }].sort((x,y) => x.date.localeCompare(y.date));
                        setP("sessions", updated);
                      }}
                      style={{ padding:"6px 14px", borderRadius:7, border:"none", background:"#0891b2", color:"#fff", fontWeight:700, fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}
                    >+ Ajouter</button>
                  </div>
                  {(prog.sessions || []).length === 0 ? (
                    <div style={{ fontSize:11, color:"#94a3b8", textAlign:"center", padding:"10px 0" }}>Aucune séance enregistrée - ajoutez la première ci-dessus</div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:4, maxHeight:220, overflowY:"auto" }}>
                      {[...(prog.sessions || [])].map((s, origIdx) => ({ ...s, origIdx })).sort((x,y) => y.date.localeCompare(x.date)).map((s) => (
                        <div key={s.origIdx} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", background: s.statut==="present"?"#f0fdf4":"#fef2f2", borderRadius:7, border:`1px solid ${s.statut==="present"?"#bbf7d0":"#fca5a5"}` }}>
                          <span style={{ fontSize:12 }}>{s.statut==="present"?"✅":"❌"}</span>
                          <span style={{ fontSize:12, color:"#374151", flex:1, fontWeight:600 }}>
                            {new Date(s.date+"T12:00:00").toLocaleDateString("fr-FR", { weekday:"short", day:"numeric", month:"short", year:"numeric" })}
                          </span>
                          <span style={{ fontSize:11, fontWeight:700, color: s.statut==="present"?"#15803d":"#b91c1c" }}>
                            {s.statut==="present" ? "Présent" : "Absent"}
                          </span>
                          <button
                            onClick={() => setP("sessions", (prog.sessions||[]).filter((_,j) => j !== s.origIdx))}
                            style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af", fontSize:14, lineHeight:1, padding:"0 2px" }}
                          >✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {(prog.sessions || []).length > 0 && (
                    <div style={{ marginTop:8, display:"flex", gap:12, fontSize:11, color:"#6b7280" }}>
                      <span>✅ {(prog.sessions||[]).filter(s=>s.statut==="present").length} présence{(prog.sessions||[]).filter(s=>s.statut==="present").length!==1?"s":""}</span>
                      <span>❌ {(prog.sessions||[]).filter(s=>s.statut==="absent").length} absence{(prog.sessions||[]).filter(s=>s.statut==="absent").length!==1?"s":""}</span>
                      <span style={{ marginLeft:"auto", fontWeight:700 }}>{Math.round(((prog.sessions||[]).filter(s=>s.statut==="present").length / (prog.sessions||[]).length)*100)}% participation</span>
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ fontSize:10, fontWeight:700, color:"#374151", display:"block", marginBottom:6 }}>📝 Notes de suivi (commercial)</label>
                  <textarea
                    value={prog.commentaire||""}
                    onChange={e=>setP("commentaire",e.target.value)}
                    rows={3}
                    placeholder="Observations, difficultés, points positifs, prochaines étapes…"
                    style={{ ...inputSt, margin:0, height:"auto", resize:"vertical" }}
                  />
                </div>

                <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                  <button onClick={()=>{ setShowProgressionModal(false); setProgressionTarget(null); }} style={btnSecondary}>Fermer</button>
                  <button
                    onClick={async () => {
                      const sessions = prog.sessions || [];
                      const presences = sessions.filter(s => s.statut === "present").length;
                      const absences  = sessions.filter(s => s.statut === "absent").length;
                      const sp = {
                        sessions,
                        presences,
                        absences,
                        seances_effectuees: sessions.length,
                        updated_at: new Date().toISOString(),
                      };
                      await handleSavePresences(a.id, sp);
                    }}
                    style={{ ...btnPrimary, background:BET_GRADIENT }}
                  >💾 Sauvegarder</button>
                </div>

              </div>
          </ModaleAnimee>
        );
      })()}

      {/* ══ MODAL DOCUMENTS DOSSIER ══ */}
      {showDocDossierModal && docDossierTarget && (
        <DocDossierModal
          assignation={docDossierTarget}
          onSave={(docs) => handleSaveDocsDossier(docDossierTarget.id, docs)}
          onClose={() => { setShowDocDossierModal(false); setDocDossierTarget(null); }}
        />
      )}

      {/* ══ MODAL SUIVI DÉMARRAGE ══ */}
      {showSuiviModal && suiviTarget && (
        <SuiviDemarrageModal
          assignation={suiviTarget}
          onSave={(suivi) => handleSaveSuivi(suiviTarget.id, suivi)}
          onClose={() => { setShowSuiviModal(false); setSuiviTarget(null); }}
        />
      )}

      {/* ── Archive des apprenants ──
          Plein écran plutôt qu'un panneau : le parcours d'un apprenant se déplie sous sa ligne,
          et un modal étroit obligerait à choisir entre voir la liste et voir l'histoire. */}
      {showArchiveApprenants && (
        <ModaleAnimee ouvert
          onClicVoile={e => { if (e.target === e.currentTarget) setShowArchiveApprenants(false); }}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9500, display:"flex",
          alignItems:"center", justifyContent:"center", backdropFilter:"blur(3px)" }}
          panneauStyle={{ background:"#fff", borderRadius:18, width:"92vw", height:"90vh", display:"flex",
            flexDirection:"column", boxShadow:"0 24px 64px rgba(0,0,0,0.28)", overflow:"hidden" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
              padding:"18px 24px", borderBottom:"1px solid #e5e7eb" }}>
              <div>
                <div style={{ fontSize:17, fontWeight:900, color:"#0f172a" }}>📒 Archive de mes apprenants</div>
                <div style={{ fontSize:12, color:"#94a3b8", marginTop:2 }}>
                  Qui j'ai accompagné, qui est parti, et tout ce qui leur est arrivé
                </div>
              </div>
              <button onClick={() => setShowArchiveApprenants(false)}
                style={{ background:"#f1f5f9", border:"none", borderRadius:10, width:34, height:34, fontSize:17,
                  cursor:"pointer", color:"#475569", lineHeight:1, flexShrink:0 }}>
                ✕
              </button>
            </div>
            {/* `flex:1` + `minHeight:0` : sans eux, un tableau long dépasse la hauteur fixe du
                modal au lieu de défiler à l'intérieur. */}
            <div style={{ padding:"18px 24px 24px", overflowY:"auto", flex:1, minHeight:0 }}>
              <ArchiveApprenantsCommerciale
                commercialeNom={`${profilInitial?.prenom || ""} ${profilInitial?.nom || ""}`.trim()}
                accent={BET_COLOR}
                authHdrs={authHeaders}
              />
            </div>
        </ModaleAnimee>
      )}

      {/* ── Modal confirmation "Transmettre au PA" (dossier coaching privé) ── */}
      {confirmTransmettrePA && apprenantModal && (
        // z-index 9400 : au-dessus de la fiche apprenant (modalOverlay, 9200), qui reste
        // ouverte en arrière-plan - sans quoi ce modal s'ouvre bien mais invisible derrière.
        <ModaleAnimee ouvert
          onClicVoile={e => { if (e.target === e.currentTarget) setConfirmTransmettrePA(false); }}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", zIndex:9400, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
          panneauStyle={{ background:"#fff", borderRadius:14, padding:22, width:"100%", maxWidth:440 }}>
            <div style={{ fontWeight:800, fontSize:15, color:"#0f172a", marginBottom:4 }}>📤 Transmettre au PA</div>
            <div style={{ fontSize:12.5, color:"#64748b", marginBottom:18 }}>
              Confirmer la transmission du dossier de <strong>{apprenantModal.prospect_nom}</strong> au PA Advisor pour validation ? Le dossier sera enregistré avant l'envoi.
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => setConfirmTransmettrePA(false)} disabled={transmettantPA}
                style={{ padding:"8px 16px", borderRadius:8, border:"1.5px solid #e2e8f0", background:"#fff", color:"#64748b", fontWeight:700, fontSize:12.5, cursor:"pointer" }}>Annuler</button>
              <button onClick={transmettrePADossierPrive} disabled={transmettantPA}
                style={{ padding:"8px 16px", borderRadius:8, border:"none", background:"#7c3aed", color:"#fff", fontWeight:700, fontSize:12.5, cursor: transmettantPA?"wait":"pointer" }}>
                {transmettantPA ? "⏳ Envoi…" : "Confirmer la transmission"}
              </button>
            </div>
        </ModaleAnimee>
      )}

      {showAssignCentreModal && assignCentreTarget && (
        <AssignationCentreModal
          assignation={assignCentreTarget}
          // Les classes de coaching privé à domicile importées stockent désormais réellement
          // type_cours="domicile" ; les anciennes (créées avant ce correctif) sont restées en
          // "presentiel" - on accepte donc les deux pour ne perdre aucune classe existante.
          classes={classes.filter(cl => {
            const cible = assignCentreTarget.type_cours || "presentiel";
            const matchDirect = cl.type_cours === cible;
            const matchAncienDomicile = cible === "domicile" && cl.type_cours === "presentiel";
            return (matchDirect || matchAncienDomicile) && cl.statut !== "terminé" && cl.statut !== "cloture";
          })}
          coachs={coachsList}
          coachDispoMap={coachDispoMap}
          onSave={(data) => handleAssignCentre(assignCentreTarget.id, data)}
          onClose={() => { setShowAssignCentreModal(false); setAssignCentreTarget(null); }}
          onCreateClasse={(niveauSuggere) => {
            setShowAssignCentreModal(false);
            // La fiche apprenant (z-index 9200) reste ouverte en arrière-plan tant qu'on ne
            // la ferme pas explicitement - comme le modal "Créer une classe" n'a qu'un
            // z-index de 3000, elle réapparaissait au premier plan et le masquait entièrement.
            setApprenantModal(null);
            // Coaching privé : reprendre le niveau/disponibilités déjà validés dans le
            // "Besoin du client" au lieu de forcer une re-saisie complète - même logique
            // que côté Onboarding pour ses propres créations de classe privée.
            const isPrive = assignCentreTarget.type_coaching === "prive";
            const sd = assignCentreTarget.suivi_demarrage || {};
            const horairePrefill = isPrive
              ? (sd.disponibilites?.horaires || []).map(h => {
                  const [hh, mm] = (h.debut || "08:00").split(":").map(Number);
                  const finH = (hh + 2) % 24;
                  return { jour: h.jour, debut: h.debut || "08:00", fin: `${String(finH).padStart(2,"0")}:${String(mm||0).padStart(2,"0")}` };
                })
              : [];
            setClasseForm({
              nom: isPrive ? `Coaching privé - ${assignCentreTarget.prospect_nom || ""}`.trim() : "",
              niveau: niveauSuggere || sd.niveau_prospect || "",
              filiere:"Anglais Pro", type_cours:"presentiel", coach_id:"", centre_id: assistanteCentreId || "",
              date_debut: sd.date_debut_formation || assignCentreTarget.date_debut_formation || "",
              /* Capacité = le payeur + les autres personnes de la famille. La commerciale
                 peut encore la corriger dans le formulaire ; on lui propose simplement la
                 valeur juste plutôt que 1, qui l'obligeait à la rectifier à la main. */
              date_fin:"", horaire: horairePrefill,
              capacite_max: isPrive
                ? 1 + (Array.isArray(sd.membres_famille) ? sd.membres_famille.length : 0)
                : 20,
              photo_url: null,
              public_cible:"",
            });
            setClasseCoachSearch("");
            setShowCreateClasseInAssign(true);
            fetchCoachs();
            setShowCreateClasse(true);
          }}
        />
      )}

      {/* ══ MODAL CRÉER / MODIFIER CLASSE (global - accessible depuis tous les onglets) ══ */}
      {(() => {
      // Classe déjà pourvue d'un coach au moment de l'ouverture du formulaire : on verrouille
      // les champs qui endommageraient des données déjà écrites (coach, type de cours, filière, niveau).
      const champsClasseVerrouilles = !!classeForm._id && !!classeForm.coach_id_original;
      return showCreateClasse && (
        <ModaleAnimee ouvert
          onClicVoile={e => { if (e.target === e.currentTarget) { setShowCreateClasse(false); setShowCreateClasseInFin(false); setShowCreateClasseInTransfert(false); setShowCreateClasseInAssign(false); } }}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:9700, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
          panneauStyle={{ background:"#fff", borderRadius:16, width:"90vw", maxWidth:"90vw", height:"90vh", maxHeight:"90vh", display:"flex", flexDirection:"column", boxShadow:"0 24px 64px rgba(0,0,0,.2)" }}>
            <div style={{ padding:"20px 24px 16px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between", alignItems:"center", flex:"0 0 auto", background:"#fff" }}>
              <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#0f172a" }}>
                {classeForm._id ? "✏️ Modifier la classe" : "📚 Nouvelle classe"}
                {showCreateClasseInFin && <span style={{ fontSize:11, fontWeight:500, color:"#7c3aed", marginLeft:8 }}>- depuis finalisation</span>}
              </h3>
              <button onClick={() => { setShowCreateClasse(false); setShowCreateClasseInFin(false); setShowCreateClasseInTransfert(false); setShowCreateClasseInAssign(false); }} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding:"20px 24px", overflowY:"auto", flex:"1 1 auto" }}>
              <div style={{ display:"grid", gap:14 }}>
                {/* Nom */}
                <div>
                  <label style={labelSt}>Nom de la classe *</label>
                  <input value={classeForm.nom} onChange={e => setClasseForm(p=>({...p,nom:e.target.value}))}
                    placeholder="ex: Anglais Pro B2 - Mardi/Jeudi"
                    style={inputSt} />
                </div>
                {/* Photo - la même que celle du fil de discussion de la classe. */}
                <ChampPhotoClasse
                  valeur={classeForm.photo_url || null}
                  onChange={url => setClasseForm(p => ({ ...p, photo_url: url }))}
                  nom={classeForm.nom}
                  accent={BET_COLOR} />
                {champsClasseVerrouilles && (
                  <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", borderRadius:9, background:"#fffbeb", border:"1px solid #fde68a", fontSize:12, color:"#92400e", fontWeight:600 }}>
                    🔒 Un coach est déjà assigné à cette classe. La filière, le niveau, le type de cours et le coach sont verrouillés pour ne pas endommager les séances/présences déjà enregistrées.
                  </div>
                )}
                {/* Filière + Niveau */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  <div>
                    <label style={labelSt}>Filière</label>
                    {filieresCatalogue.length > 0 ? (
                      <select value={classeForm.filiere} disabled={champsClasseVerrouilles} onChange={e => setClasseForm(p=>({...p,filiere:e.target.value}))}
                        style={{ ...selectSt, ...(champsClasseVerrouilles ? { background:"#f1f5f9", color:"#94a3b8", cursor:"not-allowed" } : {}) }}>
                        <option value="">- Sélectionner -</option>
                        {filieresCatalogue.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    ) : (
                      // Catalogue non chargé : saisie libre plutôt que de bloquer la création,
                      // avec l'avertissement qui manquait quand la liste était inventée.
                      <>
                        <input value={classeForm.filiere} disabled={champsClasseVerrouilles} placeholder="Libellé exact de l'offre"
                          onChange={e => setClasseForm(p=>({...p,filiere:e.target.value}))}
                          style={{ ...selectSt, ...(champsClasseVerrouilles ? { background:"#f1f5f9", color:"#94a3b8", cursor:"not-allowed" } : {}) }} />
                        <div style={{ fontSize:10, color:"#b45309", marginTop:3 }}>
                          Catalogue indisponible - saisissez le libellé exact d'une offre, sinon les
                          inscriptions de cette classe seront exclues des statistiques.
                        </div>
                      </>
                    )}
                  </div>
                  <div>
                    <label style={labelSt}>Niveau CECRL</label>
                    <select value={classeForm.niveau} disabled={champsClasseVerrouilles} onChange={e => setClasseForm(p=>({...p,niveau:e.target.value}))}
                      style={{ ...selectSt, ...(champsClasseVerrouilles ? { background:"#f1f5f9", color:"#94a3b8", cursor:"not-allowed" } : {}) }}>
                      {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
                {/* Type cours. HYBRIDE EST DÉSORMAIS PROPOSÉ : des apprenants suivent
                    réellement une partie de leurs séances au centre et l'autre en ligne, et
                    la valeur existait déjà partout ailleurs (affichage, historiques, offres
                    entreprise) - seul ce formulaire la retirait. Une classe hybride reste UNE
                    classe : c'est chaque SÉANCE qui se tient en ligne ou au centre.
                    Le verrouillage par canal demeure pour le reste : une assistante présentiel
                    ne crée pas de classe en ligne. Mais l'hybride lui est ouvert, puisqu'il
                    comprend du présentiel. */}
                <div>
                  <label style={labelSt}>Type de cours</label>
                  <div style={{ display:"flex", gap:8 }}>
                    {TYPE_COURS.map(t => {
                      const verrouPresentiel = assistanteTypeCours === "presentiel" && t.id !== "presentiel" && t.id !== "hybride";
                      const disabled = champsClasseVerrouilles || verrouPresentiel;
                      return (
                        <button key={t.id} disabled={disabled} onClick={() => !disabled && setClasseForm(p=>({...p,type_cours:t.id}))}
                          style={{ flex:1, padding:"8px 12px", borderRadius:8, fontSize:12, fontWeight:700, cursor: disabled ? "not-allowed" : "pointer",
                            border: classeForm.type_cours===t.id ? `2px solid ${BET_COLOR}` : "1px solid #e2e8f0",
                            background: disabled ? "#f1f5f9" : classeForm.type_cours===t.id ? BET_COLOR+"15" : "#f8fafc",
                            color: disabled ? "#94a3b8" : classeForm.type_cours===t.id ? BET_COLOR : "#64748b",
                            opacity: disabled ? 0.7 : 1,
                          }}>{t.label}</button>
                      );
                    })}
                  </div>
                </div>
                {/* ══ PROFIL DU PUBLIC ══
                    FACULTATIF, ET AUCUNE VALEUR N'EST PRÉSÉLECTIONNÉE : un défaut posé
                    d'office — « Adultes », le cas le plus fréquent — serait indiscernable
                    d'un choix réel, et c'est justement cette distinction qui permet de
                    retrouver plus tard les classes à compléter.
                    RECLIQUER SUR LE CHOIX ACTIF LE RETIRE. Sans cela, une sélection faite
                    par erreur ne pourrait plus être annulée : il n'existe pas de bouton
                    « aucun », et il n'en faut pas un cinquième qui encombrerait la ligne.
                    Pas de verrouillage par `champsClasseVerrouilles` : contrairement au
                    niveau ou au type de cours, ce champ n'alimente aucun calcul déjà écrit —
                    le corriger sur une classe en cours ne casse rien. */}
                <div>
                  <label style={labelSt}>
                    Profil du public
                    <span style={{ marginLeft:6, fontSize:11, fontWeight:600, color:"#94a3b8" }}>facultatif</span>
                  </label>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    {PUBLICS_CIBLES.map(pc => {
                      const actif = classeForm.public_cible === pc.cle;
                      return (
                        <button key={pc.cle} title={pc.aide}
                          onClick={() => setClasseForm(p => ({ ...p, public_cible: actif ? "" : pc.cle }))}
                          style={{ flex:"1 1 110px", padding:"8px 12px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer",
                            border: actif ? `2px solid ${BET_COLOR}` : "1px solid #e2e8f0",
                            background: actif ? BET_COLOR+"15" : "#f8fafc",
                            color: actif ? BET_COLOR : "#64748b",
                          }}>{pc.emoji} {pc.libelle}</button>
                      );
                    })}
                  </div>
                </div>
                {/* Coach - cartes visuelles */}
                <div style={champsClasseVerrouilles ? { opacity:0.55, pointerEvents:"none", position:"relative" } : undefined}>
                  <label style={labelSt}>
                    Assigner un coach
                    {champsClasseVerrouilles && (
                      <span style={{ marginLeft:8, fontSize:11, fontWeight:700, color:"#92400e", background:"#fef3c7", borderRadius:99, padding:"1px 8px" }}>
                        🔒 Verrouillé - coach déjà assigné
                      </span>
                    )}
                    {!champsClasseVerrouilles && classeForm.niveau && (
                      <span style={{ marginLeft:8, fontSize:11, fontWeight:600, color:"#15803d", background:"#dcfce7", borderRadius:99, padding:"1px 8px" }}>
                        Niveau : {classeForm.niveau} - coachs compatibles en vert
                      </span>
                    )}
                  </label>
                  <input
                    value={classeCoachSearch}
                    onChange={e => setClasseCoachSearch(e.target.value)}
                    placeholder="🔍 Rechercher un coach (nom, grade, niveau…)"
                    style={{ ...inputSt, marginBottom:8 }}
                  />

                  {/* FILTRE PAR CATÉGORIE DE CONTRAT, sous la recherche. Chercher par nom
                      suppose qu'on sait déjà qui l'on veut ; filtrer par catégorie répond
                      à la vraie question - « qui puis-je mettre sur cette classe ? ».
                      Chaque pastille porte son compte : une catégorie vide se voit avant
                      d'être cliquée, au lieu de renvoyer une liste vide. */}
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10 }}>
                    {[{ cle:"", libelle:"Toutes", couleur:"#475569", fond:"#f1f5f9", bord:"#e2e8f0" },
                      ...CATEGORIES_COACH,
                      CATEGORIE_SANS_NOTE].map(cat => {
                      const n = !cat.cle
                        ? coachsList.length
                        : coachsList.filter(c => (c.moyenne != null ? categorieCoach(Number(c.moyenne)).cle : CATEGORIE_SANS_NOTE.cle) === cat.cle).length;
                      const actif = classeCoachCategorie === cat.cle;
                      return (
                        <button key={cat.cle || "toutes"}
                          onClick={() => setClasseCoachCategorie(actif ? "" : cat.cle)}
                          disabled={n === 0 && !!cat.cle}
                          title={cat.cle === CATEGORIE_SANS_NOTE.cle ? "Coachs jamais évalués - aucune observation à ce jour" : undefined}
                          style={{ padding:"4px 11px", borderRadius:99, fontSize:11, fontWeight:700, fontFamily:"inherit",
                            cursor: n === 0 && cat.cle ? "not-allowed" : "pointer",
                            border: `1.5px solid ${actif ? cat.couleur : cat.bord}`,
                            background: actif ? cat.couleur : cat.fond,
                            color: actif ? "#fff" : cat.couleur,
                            opacity: n === 0 && cat.cle ? .45 : 1 }}>
                          {cat.court || cat.libelle} <span style={{ opacity:.75, fontWeight:600 }}>{n}</span>
                        </button>
                      );
                    })}
                  </div>
                  {coachsList.length === 0 && (
                    <div style={{ fontSize:11, color:"#94a3b8", padding:12, textAlign:"center" }}>⏳ Chargement des coachs…</div>
                  )}
                  {coachsList.length > 0 && (
                    <div style={{ maxHeight:"56vh", overflowY:"auto", padding:"2px 0" }}>
                      <DispoMoisToggle moisSel={classeCoachDispoMois} setMoisSel={setClasseCoachDispoMois} periodes={getPeriodesDispo()} />
                      {/* Option "non assigné" */}
                      <div
                        onClick={() => setClasseForm(p=>({...p,coach_id:""}))}
                        style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:10, border: !classeForm.coach_id ? `2px solid #f59e0b` : "1px solid #e2e8f0", background: !classeForm.coach_id ? "#fef9c3" : "#f8fafc", cursor:"pointer", transition:"all .15s", marginBottom:10 }}
                      >
                        <div style={{ width:38, height:38, borderRadius:"50%", background:"#fef3c7", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>🚫</div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:700, color: !classeForm.coach_id ? "#92400e" : "#64748b" }}>Non assigné pour l'instant</div>
                          <div style={{ fontSize:11, color:"#94a3b8" }}>Le coach sera assigné ultérieurement</div>
                        </div>
                        {!classeForm.coach_id && <div style={{ fontSize:16 }}>✓</div>}
                      </div>
                      {/* Grille 3 colonnes */}
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                        {filteredCoachs.map(c => {
                          const name       = [c.prenom, c.nom].filter(Boolean).join(" ");
                          const initials   = name.split(" ").map(p=>p[0]).slice(0,2).join("").toUpperCase();
                          const isSelected = classeForm.coach_id === c.id;
                          const specs      = Array.isArray(c.specialites)       ? c.specialites       : [];
                          const niveaux    = Array.isArray(c.niveaux_enseignes) ? c.niveaux_enseignes : [];
                          const CCOL = { A1:"#6b7280",A2:"#d97706",B1:"#1a4fd6",B2:"#7c3aed",C1:"#059669",C2:"#dc2626" };
                          const CBGC = { A1:"#f3f4f6",A2:"#fef3c7",B1:"#dbeafe",B2:"#ede9fe",C1:"#dcfce7",C2:"#fee2e2" };
                          const niveauClasse  = classeForm.niveau || "";
                          const isCompatible  = niveauClasse && niveaux.includes(niveauClasse);
                          // Disponibilité réelle - dérivée des classes actives déjà assignées à ce coach
                          const coachClassesActives = classes.filter(cl => cl.coach_id === c.id && cl.statut !== "terminé" && cl.statut !== "cloture" && cl.id !== classeForm._id);
                          const creneauxOccupes = coachClassesActives.flatMap(cl => Array.isArray(cl.horaire) ? cl.horaire : []);
                          const nbClassesActives = coachClassesActives.length;
                          const dispo = nbClassesActives === 0
                            ? { label:"Disponible", icon:"🟢", color:"#15803d", bg:"#dcfce7" }
                            : nbClassesActives <= 2
                              ? { label:"Peu chargé", icon:"🟡", color:"#a16207", bg:"#fef9c3" }
                              : { label:"Chargé", icon:"🔴", color:"#b91c1c", bg:"#fee2e2" };
                          const noteMoy = c.moyenne != null ? Number(c.moyenne) : null;
                          const nbNotes = c.nb_evaluations ?? 0;
                          const tendanceCoach = c.tendance || "stable";
                          /* De la plus ANCIENNE à la plus récente : le classement les
                             renvoie dans l'ordre inverse, et la courbe se lirait à
                             l'envers - un coach en progression semblerait décrocher. */
                          const dernieres = [...(c.historique || [])]
                            .map(h => Number(h?.note)).filter(n => Number.isFinite(n) && n > 0)
                            .reverse().slice(-5);
                          const joursOccupes = [...new Set(creneauxOccupes.map(h => fmtHoraire(h).split(" ")[0]).filter(Boolean))];
                          const conflitPotentiel = (classeForm.horaire||[]).some(h => joursOccupes.includes(fmtHoraire(h).split(" ")[0]));
                          return (
                            <div key={c.id}
                              onClick={() => setClasseForm(p=>({...p, coach_id: c.id}))}
                              style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"12px 14px", borderRadius:10, border: isSelected ? `2px solid ${BET_COLOR}` : isCompatible ? "1.5px solid #22c55e" : "1px solid #e2e8f0", background: isSelected ? BET_COLOR+"0D" : isCompatible ? "#f0fdf4" : "#fff", cursor:"pointer", transition:"all .15s", position:"relative" }}
                            >
                              {/* Photo / avatar */}
                              {c.photo_url ? (
                                <img src={c.photo_url} alt={name} style={{ width:50, height:50, borderRadius:"50%", objectFit:"cover", flexShrink:0, border:`2px solid ${isSelected ? BET_COLOR : "#e2e8f0"}` }} />
                              ) : (
                                <div style={{ width:50, height:50, borderRadius:"50%", background:`linear-gradient(135deg,${BET_COLOR},#7c3aed)`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:16, color:"#fff", flexShrink:0, border:`2px solid ${isSelected ? BET_COLOR : "transparent"}` }}>
                                  {initials}
                                </div>
                              )}
                              {/* Infos */}
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap", marginBottom:3 }}>
                                  <span style={{ fontWeight:800, fontSize:13, color:"#0f172a" }}>{name}</span>
                                  {c.acces_bloque && <span style={{ fontSize:9, fontWeight:700, background:"#dc2626", color:"#fff", borderRadius:99, padding:"1px 7px", whiteSpace:"nowrap" }}>🔒 SUSPENDU</span>}
                                  {isCompatible && !isSelected && !c.acces_bloque && <span style={{ fontSize:10, fontWeight:700, background:"#dcfce7", color:"#15803d", borderRadius:99, padding:"1px 7px" }}>✓ {niveauClasse}</span>}

                                  {/* LA NOTE ET SA CATÉGORIE DE CONTRAT. La disponibilité
                                      dit qui PEUT prendre la classe ; la note dit à qui on
                                      souhaite la confier. */}
                                  {noteMoy != null && (
                                    <span title={`Note moyenne sur ${nbNotes} évaluation${nbNotes>1?"s":""}`}
                                      style={{ fontSize:10, fontWeight:900, background:NOTE_COUL_CAT(noteMoy), color:"#fff", borderRadius:99, padding:"1px 8px" }}>
                                      ⭐ {noteSur10(noteMoy)}
                                    </span>
                                  )}
                                  <BadgeCategorieCoach note={noteMoy} taille="xs" />

                                  {/* LA TENDANCE. Deux coachs à 3,1 n'ont pas la même
                                      trajectoire : l'un remonte, l'autre décroche. */}
                                  {dernieres.length > 0 && (
                                    <span title={`${nbNotes} note${nbNotes>1?"s":""} - de la plus ancienne à la plus récente : ${dernieres.join(" · ")}`}
                                      style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
                                      <span style={{ display:"flex", gap:2, alignItems:"flex-end", height:14 }}>
                                        {dernieres.map((n, ni) => (
                                          <span key={ni} style={{ width:4, borderRadius:2, background:NOTE_COUL_CAT(n), height:`${(n/5)*100}%`, minHeight:3, display:"block" }} />
                                        ))}
                                      </span>
                                      {/* La flèche n'a de sens qu'à partir de DEUX notes. */}
                                      {dernieres.length >= 2 && (
                                        <span style={{ fontSize:12, fontWeight:900, lineHeight:1,
                                          color: tendanceCoach === "hausse" ? "#16a34a" : tendanceCoach === "baisse" ? "#dc2626" : "#94a3b8" }}>
                                          {tendanceCoach === "hausse" ? "↗" : tendanceCoach === "baisse" ? "↘" : "→"}
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </div>
                                {/* Disponibilité */}
                                <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:4 }}>
                                  <span title={`${nbClassesActives} classe(s) active(s) assignée(s) à ce coach`}
                                    style={{ fontSize:10, fontWeight:700, background:dispo.bg, color:dispo.color, borderRadius:99, padding:"1px 8px" }}>
                                    {dispo.icon} {dispo.label} · {nbClassesActives} classe{nbClassesActives>1?"s":""}
                                  </span>
                                  {conflitPotentiel && (
                                    <span title="Ce coach a déjà un créneau sur au moins un des jours choisis pour cette classe" style={{ fontSize:10, fontWeight:700, background:"#fee2e2", color:"#b91c1c", borderRadius:99, padding:"1px 8px" }}>
                                      ⚠️ Conflit horaire possible
                                    </span>
                                  )}
                                </div>
                                {creneauxOccupes.length > 0 && (
                                  <div style={{ display:"flex", gap:3, flexWrap:"wrap", marginBottom:4 }}>
                                    {creneauxOccupes.slice(0,4).map((h,i) => (
                                      <span key={i} style={{ fontSize:9, color:"#64748b", background:"#f1f5f9", borderRadius:5, padding:"1px 6px" }}>🕐 {fmtHoraire(h)}</span>
                                    ))}
                                    {creneauxOccupes.length > 4 && <span style={{ fontSize:9, color:"#94a3b8" }}>+{creneauxOccupes.length-4}</span>}
                                  </div>
                                )}
                                <CoachDispoBlock coachId={c.id} coachDispoMap={coachDispoMap} {...getPeriodesDispo()[classeCoachDispoMois]} />
                                <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:4 }}>
                                  {c.grade && <span style={{ fontSize:10, fontWeight:700, background:BET_COLOR+"18", color:BET_COLOR, borderRadius:99, padding:"1px 8px" }}>{c.grade}</span>}
                                  {c.experience_annees && <span style={{ fontSize:10, color:"#94a3b8" }}>🏅 {c.experience_annees} ans</span>}
                                  {c.telephone && <span style={{ fontSize:10, color:"#0891b2" }}>📞 {c.telephone}</span>}
                                  {c.type_contrat && <span style={{ fontSize:10, color:"#94a3b8" }}>📄 {c.type_contrat}</span>}
                                </div>
                                {c.email && (
                                  <div style={{ fontSize:10, color:"#94a3b8", marginBottom:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>✉️ {c.email}</div>
                                )}
                                {c.biographie && (
                                  <div style={{ fontSize:11, color:"#64748b", marginBottom:5, lineHeight:1.4, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>
                                    {c.biographie}
                                  </div>
                                )}
                                {niveaux.length > 0 && (
                                  <div style={{ display:"flex", gap:3, flexWrap:"wrap", marginBottom:3 }}>
                                    {niveaux.map(n => (
                                      <span key={n} style={{ fontSize:10, fontWeight:800, padding:"1px 6px", borderRadius:4, background: CBGC[n]||"#f3f4f6", color: CCOL[n]||"#6b7280", border:`1px solid ${CCOL[n]||"#d1d5db"}` }}>{n}</span>
                                    ))}
                                  </div>
                                )}
                                {specs.length > 0 && (
                                  <div style={{ display:"flex", gap:3, flexWrap:"wrap" }}>
                                    {specs.slice(0,3).map(s => (
                                      <span key={s} style={{ fontSize:10, background:"#f1f5f9", color:"#475569", borderRadius:5, padding:"1px 6px", fontWeight:500 }}>#{s}</span>
                                    ))}
                                    {specs.length > 3 && <span style={{ fontSize:10, color:"#94a3b8" }}>+{specs.length-3}</span>}
                                  </div>
                                )}
                              </div>
                              {isSelected && (
                                <div style={{ position:"absolute", top:10, right:10, width:22, height:22, borderRadius:"50%", background:BET_COLOR, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, color:"#fff", fontWeight:800 }}>✓</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {filteredCoachs.length === 0 && coachsList.length > 0 && (
                        <div style={{ textAlign:"center", padding:16, color:"#94a3b8", fontSize:12 }}>
                          Aucun coach ne correspond
                          {classeCoachSearch ? ` à « ${classeCoachSearch} »` : ""}
                          {classeCoachCategorie ? " dans cette catégorie" : ""}.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Dates */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  <div>
                    <label style={labelSt}>Date de début</label>
                    <input type="date" value={classeForm.date_debut} onChange={e => setClasseForm(p=>({...p,date_debut:e.target.value}))} style={inputSt} />
                  </div>
                  <div>
                    <label style={labelSt}>Date de fin <span style={{ fontWeight:400, color:"#94a3b8" }}>(optionnel - coaching privé souvent sans fin fixe)</span></label>
                    <input type="date" value={classeForm.date_fin} onChange={e => setClasseForm(p=>({...p,date_fin:e.target.value}))} style={inputSt} />
                  </div>
                </div>
                {/* Horaires */}
                <div>
                  <label style={labelSt}>Horaires de la classe</label>
                  {(classeForm.horaire||[]).length > 0 && (
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:8 }}>
                      {(classeForm.horaire||[]).map((h, i) => (
                        <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"4px 10px", borderRadius:99, background:BET_COLOR+"15", color:BET_COLOR, fontSize:12, fontWeight:600, border:`1px solid ${BET_COLOR}40` }}>
                          🕐 {fmtHoraire(h)}
                          <button onClick={() => setClasseForm(p=>({ ...p, horaire: p.horaire.filter((_,j)=>j!==i) }))}
                            style={{ background:"none", border:"none", color:BET_COLOR, cursor:"pointer", fontSize:13, padding:0, lineHeight:1 }}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  {(() => {
                    const JOURS = ["Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi","Dimanche"];
                    return (
                      <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                        <select id="hor-jour-g" defaultValue="" style={{ ...selectSt, flex:"0 0 auto", width:130 }}>
                          <option value="" disabled>Jour</option>
                          {JOURS.map(j => <option key={j} value={j}>{j}</option>)}
                        </select>
                        <input id="hor-debut-g" type="time" defaultValue="08:00" style={{ ...inputSt, flex:"0 0 auto", width:110 }} />
                        <span style={{ color:"#94a3b8", fontSize:12 }}>→</span>
                        <input id="hor-fin-g" type="time" defaultValue="10:00" style={{ ...inputSt, flex:"0 0 auto", width:110 }} />
                        <button onClick={() => {
                          const jour  = document.getElementById("hor-jour-g")?.value;
                          const debut = document.getElementById("hor-debut-g")?.value;
                          const fin   = document.getElementById("hor-fin-g")?.value;
                          if (!jour) return toast.error("Sélectionne un jour");
                          setClasseForm(p => ({ ...p, horaire: [...(p.horaire||[]), `${jour} ${debut}–${fin}`] }));
                        }} style={{ padding:"8px 14px", borderRadius:7, background:BET_COLOR, color:"#fff", border:"none", fontWeight:700, fontSize:12, cursor:"pointer", whiteSpace:"nowrap" }}>
                          + Ajouter
                        </button>
                      </div>
                    );
                  })()}
                  {(classeForm.horaire||[]).length === 0 && (
                    <div style={{ fontSize:11, color:"#94a3b8", marginTop:5 }}>Aucun créneau ajouté - la classe sera sans horaire fixe</div>
                  )}
                </div>
                {/* Capacité */}
                <div>
                  <label style={labelSt}>Capacité max (apprenants)</label>
                  <input type="number" min={1} max={50} value={classeForm.capacite_max}
                    onChange={e => setClasseForm(p=>({...p,capacite_max:Number(e.target.value)}))}
                    style={{ ...inputSt, width:120 }} />
                </div>
              </div>
            </div>
            <div style={{ display:"flex", justifyContent:"flex-end", gap:10, padding:"14px 24px", borderTop:"1px solid #f1f5f9", flex:"0 0 auto", background:"#fff" }}>
              <button onClick={() => { setShowCreateClasse(false); setShowCreateClasseInFin(false); setShowCreateClasseInTransfert(false); }} style={{ ...btnSecondary, fontSize:13, padding:"9px 20px" }}>Annuler</button>
              <button onClick={saveClasse} disabled={classeSaving||!peut("classes",classeForm._id?"update":"create")} title={permBtnTitle(classeForm._id?"Mettre à jour":"Créer la classe",peut("classes",classeForm._id?"update":"create"))}
                style={{ padding:"9px 24px", background: classeSaving ? "#9ca3af" : BET_COLOR, color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer", opacity:peut("classes",classeForm._id?"update":"create")?1:0.45 }}>
                {classeSaving ? "Enregistrement…" : !peut("classes",classeForm._id?"update":"create") ? "🔒" : classeForm._id ? "Mettre à jour" : "✓ Créer la classe"}
              </button>
            </div>
        </ModaleAnimee>
      );
      })()}

      {/* ══ MODAL DÉTAIL APPRENANT (sans dossier BET) ══ */}
      {classeApprenantModal && (() => {
        const ga = classeApprenantModal;
        // Un assignation_id présent dans la note mais introuvable dans `assignations` ne veut
        // pas dire que le dossier n'existe pas : GET /api/parcours/assignations ne renvoie que
        // les dossiers de L'ASSISTANTE CONNECTÉE - un dossier réel appartenant à une collègue
        // (classes en ligne visibles par toutes, dossiers privés à chacune) donne exactement le
        // même résultat vide. Distinguer les deux évite un message qui ressemble à un bug de
        // données alors que c'est une limite de permission normale.
        let assignationIdDeNoteGa = null;
        try { assignationIdDeNoteGa = JSON.parse(ga.note || "{}").assignation_id || null; } catch {}
        const niv = CECRL_COLORS[ga.niveau] || { color:"#6b7280", bg:"#f3f4f6" };
        const initiales = ([ga.prenom_apprenant, ga.nom_apprenant].filter(Boolean).join(" ") || "?")
          .split(" ").map(p=>p[0]).slice(0,2).join("").toUpperCase();
        const STATUT_P = {
          present:{ label:"Présent",  bg:"#dcfce7", c:"#166534" },
          absent: { label:"Absent",   bg:"#fee2e2", c:"#991b1b" },
          retard: { label:"En retard",bg:"#fef3c7", c:"#92400e" },
          excuse: { label:"Excusé",   bg:"#dbeafe", c:"#1e40af" },
        };
        const nbPresent = classeAppPres.filter(p=>p.statut==="present").length;
        const tauxGA = classeAppPres.length ? Math.round(nbPresent/classeAppPres.length*100) : null;
        return (
          <ModaleAnimee ouvert
            onClicVoile={() => { setClasseApprenantModal(null); setClasseApprenantTab("infos"); setClasseAppPres([]); }}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:9500, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
            panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:560, maxHeight:"88vh", display:"flex", flexDirection:"column", boxShadow:"0 24px 64px rgba(0,0,0,.2)", overflow:"hidden" }}>
              {/* Header */}
              <div style={{ background:BET_GRADIENT, padding:"18px 22px", display:"flex", alignItems:"center", gap:14, flexShrink:0 }}>
                <div style={{ width:46, height:46, borderRadius:"50%", background:"rgba(255,255,255,0.18)", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:17, color:"#fff", flexShrink:0, overflow:"hidden" }}>
                  {ga.avatar_url ? <img src={ga.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : initiales}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:16, fontWeight:800, color:"#fff" }}>
                    {ga.prenom_apprenant ? `${ga.prenom_apprenant} ` : ""}{ga.nom_apprenant}
                  </div>
                  <div style={{ display:"flex", gap:6, marginTop:4 }}>
                    {ga.niveau && <span style={{ fontSize:10, fontWeight:800, padding:"2px 8px", borderRadius:99, background:niv.bg, color:niv.color }}>{ga.niveau}</span>}
                    <span style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:99, background: ga.statut==="actif"?"rgba(34,197,94,0.3)":"rgba(239,68,68,0.3)", color:"#fff" }}>
                      {ga.statut || "actif"}
                    </span>
                  </div>
                </div>
                <button onClick={() => { setClasseApprenantModal(null); setClasseApprenantTab("infos"); setClasseAppPres([]); }}
                  style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", width:30, height:30, borderRadius:"50%", cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
              </div>
              {/* Tabs */}
              <div style={{ display:"flex", borderBottom:"2px solid #e2e8f0", background:"#f8fafc", flexShrink:0 }}>
                {[{ id:"infos", label:"📋 Informations" }, { id:"presences", label:"✅ Présences" }].map(t => (
                  <button key={t.id} onClick={() => {
                    setClasseApprenantTab(t.id);
                    if (t.id === "presences" && classeAppPres.length === 0 && ga.groupe_id) {
                      setClasseAppPresLoading(true);
                      fetch(`${API_URL}/api/groupes/${ga.groupe_id}/presences`, { headers: authHeaders() })
                        .then(r => r.ok ? r.json() : { presences: [] })
                        .then(d => {
                          const rows = (d.presences || []).filter(p => p.ga_id === ga.id || (p.nom_apprenant===ga.nom_apprenant && p.prenom_apprenant===ga.prenom_apprenant));
                          setClasseAppPres(rows);
                        })
                        .catch(() => {})
                        .finally(() => setClasseAppPresLoading(false));
                    }
                  }}
                    style={{ padding:"11px 18px", border:"none", background:"none", cursor:"pointer", fontSize:12, fontWeight:classeApprenantTab===t.id?800:500, color:classeApprenantTab===t.id?"#7c3aed":"#64748b", borderBottom:classeApprenantTab===t.id?"2.5px solid #7c3aed":"2.5px solid transparent", marginBottom:-2, whiteSpace:"nowrap" }}>
                    {t.label}
                  </button>
                ))}
              </div>
              {/* Body */}
              <div style={{ flex:1, overflowY:"auto", padding:"18px 22px" }}>
                {classeApprenantTab === "infos" && (
                  <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                      {[
                        { label:"Email",      value: ga.email_apprenant,  icon:"✉" },
                        { label:"Téléphone",  value: ga.telephone,        icon:"📞" },
                        { label:"Date ajout", value: ga.date_ajout ? new Date(ga.date_ajout).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"}) : null, icon:"📅" },
                        { label:"Note",       value: ga.note,             icon:"📝" },
                      ].filter(f => f.value).map(f => (
                        <div key={f.label} style={{ background:"#f8fafc", borderRadius:8, padding:"10px 12px", border:"1px solid #e2e8f0" }}>
                          <div style={{ fontSize:10, fontWeight:700, color:"#64748b", marginBottom:3, textTransform:"uppercase", letterSpacing:".05em" }}>{f.icon} {f.label}</div>
                          <div style={{ fontSize:13, fontWeight:600, color:"#0f172a", wordBreak:"break-all" }}>{f.value}</div>
                        </div>
                      ))}
                    </div>
                    {assignationIdDeNoteGa ? (
                      <div style={{ background:"#eff6ff", borderRadius:8, padding:"10px 14px", border:"1px solid #bfdbfe", fontSize:12, color:"#1e40af" }}>
                        ℹ️ Ce dossier appartient à une autre assistante - vous n'avez pas accès à son détail complet. Les informations ci-dessus proviennent de la liste de la classe.
                      </div>
                    ) : (
                      <div style={{ background:"#fffbeb", borderRadius:8, padding:"10px 14px", border:"1px solid #fde68a", fontSize:12, color:"#92400e" }}>
                        ⚠️ Cet apprenant n'a pas de dossier BET lié à son email. Ses informations proviennent uniquement de la liste de la classe.
                      </div>
                    )}
                    {/* Même règle que dans la fiche apprenant : une classe en ligne est du
                        ressort de l'Onboarding. Ici le canal se lit sur la CLASSE, seule
                        information fiable quand le dossier BET n'est pas lié. */}
                    {/* Même règle que dans la fiche apprenant : le changement passe par
                        « Dossiers à réaffecter », jamais directement depuis une liste. */}
                    {selectedClasse?.capacite_max !== 1 && (
                      <div style={{ padding:"9px 12px", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:9, fontSize:11.5, color:"#64748b", textAlign:"center", lineHeight:1.6 }}>
                        {selectedClasse?.type_cours === "en_ligne"
                          ? "L'affectation et le changement de groupe sont gérés par l'Onboarding."
                          : "Le changement de groupe se fait depuis l'onglet « Dossiers à réaffecter », après la demande du coach et le test de niveau."}
                      </div>
                    )}
                  </div>
                )}
                {classeApprenantTab === "presences" && (
                  <div>
                    {classeAppPresLoading ? (
                      <div style={{ textAlign:"center", padding:"32px 0", color:"#9ca3af", fontSize:13 }}>Chargement…</div>
                    ) : classeAppPres.length === 0 ? (
                      <div style={{ textAlign:"center", padding:"32px 0", color:"#9ca3af" }}>
                        <div style={{ fontSize:28, marginBottom:8 }}>📋</div>
                        <div>Aucune feuille de présence enregistrée.</div>
                        <div style={{ fontSize:12, marginTop:4, color:"#cbd5e1" }}>Le coach renseigne les présences depuis son espace.</div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:14 }}>
                          {[
                            { l:"Séances", v:classeAppPres.length,  c:"#0f172a" },
                            { l:"Présences", v:nbPresent,           c:"#059669" },
                            { l:"Taux",    v:tauxGA!==null?`${tauxGA}%`:"-", c:"#0891b2" },
                          ].map(k => (
                            <div key={k.l} style={{ textAlign:"center", padding:"10px", borderRadius:10, background:"#f8fafc", border:"1px solid #e5e7eb" }}>
                              <div style={{ fontSize:10, color:"#9ca3af", fontWeight:600 }}>{k.l}</div>
                              <div style={{ fontSize:17, fontWeight:800, color:k.c, marginTop:3 }}>{k.v}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                          {[...classeAppPres].sort((a,b)=>b.date_seance?.localeCompare(a.date_seance||"")||0).map((p,i) => {
                            const st = STATUT_P[p.statut] || { label:p.statut, bg:"#f3f4f6", c:"#374151" };
                            return (
                              <div key={p.id||i} style={{ display:"flex", alignItems:"center", gap:12, padding:"9px 12px", borderRadius:9, background:i%2===0?"#fff":"#f8fafc", border:"1px solid #f1f5f9" }}>
                                <div style={{ fontWeight:700, color:"#0f172a", minWidth:140, fontSize:12 }}>
                                  {p.date_seance ? new Date(p.date_seance).toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short",year:"numeric"}) : "-"}
                                </div>
                                <span style={{ padding:"2px 9px", borderRadius:20, fontSize:10, fontWeight:700, background:st.bg, color:st.c, flexShrink:0 }}>{st.label}</span>
                                {p.note && <div style={{ fontSize:11, color:"#6b7280", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.note}</div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
          </ModaleAnimee>
        );
      })()}

      {/* ══ MODAL IMPORT CLASSES - étape 1 : modèle + upload ══ */}
      {showImportClassesUpload && (
        <Modal title="📤 Importer des classes" onClose={() => setShowImportClassesUpload(false)} wide>
          <p style={{ margin:"-8px 0 18px", fontSize:12.5, color:"#6b7280", lineHeight:1.6 }}>
            1. Téléchargez le modèle Excel ci-dessous.<br/>
            2. Remplissez une ligne par classe (seul le nom est obligatoire).<br/>
            3. Importez le fichier rempli - vous pourrez vérifier chaque ligne avant de valider.
          </p>
          <button onClick={telechargerModeleImportClasses} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", justifyContent:"center", padding:"12px 0", background:"#f0fdf4", color:"#16a34a", border:"1.5px solid #bbf7d0", borderRadius:10, fontSize:13, fontWeight:700, cursor:"pointer", marginBottom:14 }}>
            📄 Télécharger le modèle Excel
          </button>
          <label htmlFor="import-classes-file" style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8, width:"100%", padding:"28px 0", border:"2px dashed #ddd6fe", borderRadius:10, background:"#f5f3ff", cursor:"pointer" }}>
            <span style={{ fontSize:24 }}>📁</span>
            <span style={{ fontSize:13, fontWeight:700, color:"#7c3aed" }}>Cliquez pour choisir le fichier rempli</span>
            <span style={{ fontSize:11, color:"#94a3b8" }}>.xlsx, .xls ou .csv</span>
          </label>
          <input id="import-classes-file" ref={importClassesFileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display:"none" }} onChange={lireEtPrevisualiserClasses} />
        </Modal>
      )}

      {/* ══ MODAL IMPORT GLOBAL - classes + apprenants combinés ══ */}
      {showImportGlobalUpload && (
        <Modal title="🌐 Import global - classes + apprenants" onClose={() => { if (!importGlobalProcessing) setShowImportGlobalUpload(false); }} wide>
          <p style={{ margin:"-8px 0 18px", fontSize:12.5, color:"#6b7280", lineHeight:1.6 }}>
            Combine en un seul fichier la création des classes <strong>et</strong> l'inscription des apprenants qui y vont directement.<br/><br/>
            1. Téléchargez le modèle Excel ci-dessous.<br/>
            2. Une ligne par apprenant - répétez le même nom de classe sur toutes ses lignes (seule la 1ère ligne d'une classe a besoin de ses colonnes filière/niveau/coach/horaires, les suivantes peuvent les laisser vides).<br/>
            3. Importez le fichier - les classes manquantes sont créées automatiquement, puis vous retrouvez l'aperçu apprenants habituel pour vérifier avant de valider.
          </p>
          <button onClick={telechargerModeleImportGlobal} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", justifyContent:"center", padding:"12px 0", background:"#f0fdf4", color:"#16a34a", border:"1.5px solid #bbf7d0", borderRadius:10, fontSize:13, fontWeight:700, cursor:"pointer", marginBottom:14 }}>
            📄 Télécharger le modèle Excel
          </button>
          {importGlobalProcessing ? (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8, width:"100%", padding:"28px 0", border:"2px dashed #a5f3fc", borderRadius:10, background:"#ecfeff" }}>
              <span style={{ fontSize:24 }}>⏳</span>
              <span style={{ fontSize:13, fontWeight:700, color:"#0e7490" }}>Création des classes en cours…</span>
            </div>
          ) : (
            <label htmlFor="import-global-file" style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8, width:"100%", padding:"28px 0", border:"2px dashed #a5f3fc", borderRadius:10, background:"#ecfeff", cursor:"pointer" }}>
              <span style={{ fontSize:24 }}>📁</span>
              <span style={{ fontSize:13, fontWeight:700, color:"#0e7490" }}>Cliquez pour choisir le fichier rempli</span>
              <span style={{ fontSize:11, color:"#94a3b8" }}>.xlsx, .xls ou .csv</span>
            </label>
          )}
          <input id="import-global-file" ref={importGlobalFileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display:"none" }} onChange={lireEtPrevisualiserGlobal} disabled={importGlobalProcessing} />
        </Modal>
      )}

      {/* ══ MODAL IMPORT CLASSES - prévisualisation ══ */}
      {showImportClassesPreview && (() => {
        const nbErreurs     = importClassesPreviewRows.filter(l => l._statut === "erreur").length;
        const nbAvertiss    = importClassesPreviewRows.filter(l => l._statut === "avertissement").length;
        const nbOk          = importClassesPreviewRows.filter(l => l._statut === "ok").length;
        const nbImportables = importClassesPreviewRows.filter(l => l._statut !== "erreur").length;
        return (
          <Modal
            title="🔍 Prévisualisation - Import de classes - Vérifiez avant de valider"
            onClose={() => { if (importClassesConfirming) { toast.error("Import en cours - ne fermez pas cette fenêtre."); return; } setShowImportClassesPreview(false); setImportClassesPreviewRows([]); }}
            size="xl"
          >
            <p style={{ margin:"-8px 0 14px", fontSize:12, color:"#6b7280" }}>{importClassesPreviewRows.length} ligne(s) · {nbOk} OK · {nbAvertiss} avertissement(s) · {nbErreurs} erreur(s) bloquante(s)</p>
            <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
              {nbOk > 0 && <div style={{ background:"#dcfce7", color:"#16a34a", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>✅ {nbOk} valide{nbOk>1?"s":""}</div>}
              {nbAvertiss > 0 && <div style={{ background:"#fef3c7", color:"#92400e", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>⚠️ {nbAvertiss} avertissement{nbAvertiss>1?"s":""}</div>}
              {nbErreurs > 0 && <div style={{ background:"#fee2e2", color:"#dc2626", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>🚫 {nbErreurs} erreur{nbErreurs>1?"s":""} bloquante{nbErreurs>1?"s":""}</div>}
              <div style={{ background:"#f1f5f9", color:"#475569", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>
                📍 Toutes les classes seront créées en "{(assistanteTypeCours && assistanteTypeCours !== "les_deux") ? assistanteTypeCours : "presentiel"}"{assistanteCentreNom ? ` - ${assistanteCentreNom}` : ""}
              </div>
            </div>

            {nbErreurs > 0 && (
              <div style={{ background:"#fff1f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:12, color:"#991b1b" }}>
                ⚠️ Les lignes en rouge <strong>ne seront pas importées</strong>. Corrigez le fichier et réimportez.
              </div>
            )}

            <div style={{ overflowX:"auto", maxHeight:420, overflowY:"auto", marginBottom:16, borderRadius:10, border:"1.5px solid #e2e8f0" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead style={{ position:"sticky", top:0, zIndex:2 }}>
                  <tr style={{ background:"#f8fafc" }}>
                    {["#","Statut","Nom classe","Filière","Niveau","Date début","Date fin","Capacité","Coach","Horaires","Problèmes détectés"].map(h => (
                      <th key={h} style={{ padding:"9px 10px", textAlign:"left", fontWeight:700, fontSize:11, color:"#64748b", borderBottom:"2px solid #e2e8f0", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {importClassesPreviewRows.map((l, i) => {
                    const bgRow = l._statut==="erreur" ? "#fff1f2" : l._statut==="avertissement" ? "#fffbeb" : "#f0fdf4";
                    const allIssues = [
                      ...l._erreurs.map(e2 => ({ text:e2, type:"erreur" })),
                      ...l._avertissements.map(w => ({ text:w, type:"avertissement" })),
                    ];
                    return (
                      <tr key={i} style={{ borderBottom:"1px solid #f1f5f9", background:bgRow }}>
                        <td style={{ padding:"8px 10px", color:"#94a3b8", fontSize:11, fontWeight:600 }}>{l._idx}</td>
                        <td style={{ padding:"8px 10px", whiteSpace:"nowrap" }}>
                          {l._statut==="erreur"
                            ? <span style={{ background:"#fee2e2", color:"#dc2626", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700 }}>🚫 Erreur</span>
                            : l._statut==="avertissement"
                            ? <span style={{ background:"#fef3c7", color:"#92400e", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700 }}>⚠️ Attention</span>
                            : <span style={{ background:"#dcfce7", color:"#16a34a", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700 }}>✅ Valide</span>
                          }
                        </td>
                        <td style={{ padding:"8px 10px", fontWeight:600 }}>{l.nom || <span style={{ color:"#ef4444" }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#374151" }}>{l.filiere || <span style={{ color:"#d1d5db", fontSize:11 }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#374151" }}>{l.niveau || <span style={{ color:"#d1d5db", fontSize:11 }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#64748b", whiteSpace:"nowrap" }}>{l.date_debut || <span style={{ color:"#d1d5db", fontSize:11 }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#64748b", whiteSpace:"nowrap" }}>{l.date_fin || <span style={{ color:"#d1d5db", fontSize:11 }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#64748b" }}>{l.capacite_max}</td>
                        <td style={{ padding:"8px 10px", color:"#374151", whiteSpace:"nowrap" }}>{l.coach_nom || <span style={{ color:"#ef4444" }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#64748b", maxWidth:180 }}>{l.horaire?.length ? l.horaire.map(fmtHoraire).join(", ") : <span style={{ color:"#d1d5db", fontSize:11 }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", maxWidth:260 }}>
                          {allIssues.length === 0
                            ? <span style={{ color:"#86efac", fontSize:11 }}>Aucun problème</span>
                            : <ul style={{ margin:0, paddingLeft:14 }}>
                                {allIssues.map((iss, j) => (
                                  <li key={j} style={{ fontSize:11, color: iss.type==="erreur"?"#dc2626":"#92400e", fontWeight: iss.type==="erreur"?700:500, marginBottom:2 }}>
                                    {iss.text}
                                  </li>
                                ))}
                              </ul>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", alignItems:"center", flexWrap:"wrap" }}>
              {nbErreurs > 0 && <span style={{ fontSize:11, color:"#94a3b8", marginRight:"auto" }}>
                {nbImportables} ligne{nbImportables>1?"s":""} importable{nbImportables>1?"s":""} sur {importClassesPreviewRows.length}
              </span>}
              <button
                onClick={() => { setShowImportClassesPreview(false); setImportClassesPreviewRows([]); }}
                style={{ padding:"9px 18px", background:"#f1f5f9", border:"none", borderRadius:8, fontSize:12, color:"#374151", cursor:"pointer", fontWeight:600 }}>
                ✕ Fermer et corriger
              </button>
              {nbImportables > 0 && (
                <button
                  onClick={async () => {
                    if (await confirm(`${nbImportables} classe${nbImportables>1?"s vont être créées":" va être créée"} directement en base - vérifiez l'aperçu ci-dessus avant de continuer.`, { titre: `Importer ${nbImportables} classe${nbImportables>1?"s":""} ?`, confirmLabel: "Importer", danger: false })) confirmerImportClasses();
                  }}
                  disabled={importClassesConfirming}
                  style={{ padding:"9px 20px",
                    // Pendant l'import, le fond devient une barre de progression : la partie
                    // déjà traitée est en couleur pleine, le reste en teinte claire.
                    background: importClassesConfirming
                      ? `linear-gradient(to right, #6d28d9 ${importClassesProgress}%, #c4b5fd ${importClassesProgress}%)`
                      : (nbErreurs>0 ? "#f59e0b" : "#7c3aed"),
                    transition:"background 0.3s ease",
                    border:"none", borderRadius:8, fontSize:12, color:"#fff", cursor: importClassesConfirming ? "not-allowed" : "pointer", fontWeight:700, opacity: importClassesConfirming?0.9:1 }}>
                  {importClassesConfirming ? `⏳ Import en cours… ${importClassesProgress}%` : nbErreurs > 0
                    ? `⚠️ Importer uniquement les ${nbImportables} ligne${nbImportables>1?"s":""} valides`
                    : `✅ Valider et importer ${nbImportables} classe${nbImportables>1?"s":""}`
                  }
                </button>
              )}
            </div>
          </Modal>
        );
      })()}

      {/* ══ MODAL IMPORT CLASSES - résultat ══ */}
      {rendreModalProgressionImport()}
      {rendreModalHistoriqueImports()}

      {/* ── Motif de blocage ────────────────────────────────────────────────────
          Le texte choisi ici est EXACTEMENT celui que l'apprenant lira sur la page de
          connexion et recevra par email - d'où l'aperçu, pour qu'on ne découvre pas
          après coup ce qui lui a été dit. Les motifs viennent du serveur
          (/api/apprenants/motifs-blocage), pas d'une liste recopiée ici. */}
      {/* Confirmation de l'arret (ou de la reprise) de formation. Un modal et non un
          clic direct : c'est une decision de gestion, et le motif sert a l'historique. */}
      {arretModal && (
        <ModaleAnimee ouvert
          onClicVoile={e => { if (e.target === e.currentTarget && !arretLoading) setArretModal(null); }}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:9400, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
          panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:520, padding:24 }}>
            <h3 style={{ margin:"0 0 6px", fontSize:16, fontWeight:800, color:"#0f172a" }}>
              {arretModal.arrete ? "🎓 Arrêter la formation" : "↩ Reprendre la formation"}
            </h3>
            <p style={{ margin:"0 0 14px", fontSize:12.5, color:"#64748b", lineHeight:1.6 }}>
              {arretModal.arrete ? (
                <>
                  <strong>{`${arretModal.ga.prenom_apprenant || ""} ${arretModal.ga.nom_apprenant || ""}`.trim()}</strong> ne recevra plus les cours
                  que son coach publiera à partir d'aujourd'hui.<br />
                  Il <strong>garde l'accès aux cours suivis</strong> pendant sa formation - son historique est préservé.
                </>
              ) : (
                <>Il recevra de nouveau les cours publiés par son coach.</>
              )}
            </p>
            {/* LE MOTIF EST OBLIGATOIRE. Il était facultatif, donc presque jamais saisi :
                un arrêt sans raison laisse un dossier clos que personne ne sait relire des
                mois plus tard. Reprendre, en revanche, n'a rien à justifier. */}
            {arretModal.arrete && (
              <div style={{ marginBottom:14 }}>
                <label style={{ display:"block", fontSize:12, fontWeight:700, color:"#374151", marginBottom:5 }}>
                  Motif de l'arrêt <span style={{ color:"#dc2626" }}>*</span>
                </label>
                <input value={arretMotif} onChange={e=>setArretMotif(e.target.value)} autoFocus
                  placeholder="déménagement, fin de contrat, abandon…"
                  style={{ width:"100%", padding:"10px 12px", border:`1.5px solid ${arretMotif.trim() ? "#e2e8f0" : "#fca5a5"}`, borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                <div style={{ fontSize:11, color: arretMotif.trim() ? "#9ca3af" : "#dc2626", marginTop:4 }}>
                  {arretMotif.trim()
                    ? "Conservé dans le dossier de l'apprenant."
                    : "Requis : c'est la seule trace de la raison du départ."}
                </div>
              </div>
            )}
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button disabled={arretLoading} onClick={()=>setArretModal(null)}
                style={{ padding:"9px 18px", borderRadius:8, border:"1px solid #e2e8f0", background:"#fff", color:"#374151", fontSize:13, fontWeight:600, cursor:"pointer" }}>Annuler</button>
              <button disabled={arretLoading || (arretModal.arrete && !arretMotif.trim())}
                title={arretModal.arrete && !arretMotif.trim() ? "Indiquez le motif de l'arrêt" : undefined}
                onClick={async () => {
                  if (arretModal.arrete && !arretMotif.trim()) { toast.error("Le motif de l'arrêt est obligatoire"); return; }
                  setArretLoading(true);
                  try {
                    // `arretModal.gaId`, jamais `ga.id` : ce dernier est l'id de
                    // l'assignation dès qu'un dossier est rattaché au modal.
                    const r = await fetch(`${API_URL}/api/groupes/apprenants/${arretModal.gaId}/arret-formation`, {
                      method: "PATCH",
                      headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ arrete: arretModal.arrete, motif: arretMotif.trim() || null }),
                    });
                    const d = await r.json().catch(()=>({}));
                    if (!r.ok) { toast.error(d.error || "Erreur"); return; }
                    toast.success(d.message || "Enregistré ✓");
                    // Le bandeau lit `commClasseInfo` : sans cette reprise, il continuerait
                    // d'afficher « Formation en cours » après un arrêt réussi. Le serveur
                    // renvoie la ligne mise à jour, on la recopie plutôt que de la deviner.
                    if (d.assignation) {
                      setCommClasseInfo(prev => prev ? {
                        ...prev,
                        statut_ga:    d.assignation.statut,
                        date_retrait: d.assignation.date_retrait || null,
                      } : prev);
                    }
                    setArretModal(null);
                    // Rechargement plutot qu'une mise a jour locale : la fenetre d'acces
                    // change l'affichage a plusieurs endroits de la fiche.
                    if (typeof fetchAssignations === "function") fetchAssignations();
                  } catch { toast.error("Erreur réseau"); }
                  finally { setArretLoading(false); }
                }}
                style={{ padding:"9px 18px", borderRadius:8, border:"none",
                  background: (arretModal.arrete && !arretMotif.trim()) ? "#e2e8f0" : (arretModal.arrete?"#ea580c":"#22c55e"),
                  color: (arretModal.arrete && !arretMotif.trim()) ? "#94a3b8" : "#fff",
                  fontSize:13, fontWeight:700,
                  cursor: (arretLoading || (arretModal.arrete && !arretMotif.trim())) ? "not-allowed" : "pointer" }}>
                {arretLoading ? "…" : arretModal.arrete ? "🎓 Confirmer l'arrêt" : "↩ Confirmer la reprise"}
              </button>
            </div>
        </ModaleAnimee>
      )}

      {blocageModal && (() => {
        const choisi = motifsBlocage.find(m => m.code === motifChoisi);
        const apercu = precisionMotif.trim() || choisi?.message || "";
        const confirmer = async () => {
          setSuspensionLoading(true);
          try {
            const r = await fetch(`${API_URL}/api/apprenants/${blocageModal.appId}/suspension`, {
              method: "PATCH",
              headers: authHeaders(),
              body: JSON.stringify({
                bloque:  true,
                motif:   motifChoisi,
                message: precisionMotif.trim() || null,
              }),
            });
            if (!r.ok) { const e = await r.json().catch(()=>({})); toast.error(e.error || "Erreur"); return; }
            const rep = await r.json().catch(() => ({}));
            setCommAccesInfo(prev => prev ? { ...prev, profil: { ...prev.profil, acces_bloque: true } } : prev);
            setBlocageStatus(prev => prev ? { ...prev, acces_bloque: true } : prev);
            setBlocageModal(null);
            toast.success("🔒 Accès bloqué - l'apprenant a été prévenu par email");
            /* COACHING EN FAMILLE : le serveur a pu bloquer d'autres personnes du même
               dossier, sur un motif de non-paiement. On le DIT - bloquer trois personnes
               d'un geste sans l'annoncer ferait découvrir l'étendue de l'action par les
               appels des intéressés. */
            if (rep?.famille?.traites > 0) {
              toast(`👥 ${rep.famille.traites} autre${rep.famille.traites > 1 ? "s" : ""} personne${rep.famille.traites > 1 ? "s" : ""} de la famille également bloquée${rep.famille.traites > 1 ? "s" : ""} - le forfait est global`,
                { icon: "🔒", duration: 8000 });
            }
          } catch { toast.error("Erreur réseau"); }
          finally { setSuspensionLoading(false); }
        };
        return (
          <ModaleAnimee ouvert
            onClicVoile={e => { if (e.target === e.currentTarget && !suspensionLoading) setBlocageModal(null); }}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:9400, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
            panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:520, maxHeight:"92vh", overflowY:"auto", padding:24 }}>
              <h3 style={{ margin:"0 0 6px", fontSize:16, fontWeight:800, color:"#0f172a" }}>🔒 Bloquer l'accès</h3>
              <p style={{ margin:"0 0 18px", fontSize:12.5, color:"#6b7280", lineHeight:1.6 }}>
                Choisissez le motif. Il s'affichera lorsque l'apprenant tentera de se connecter,
                et lui sera envoyé par email.
              </p>

              {!motifsBlocage.length ? (
                <p style={{ fontSize:13, color:"#94a3b8", padding:"16px 0" }}>Chargement des motifs…</p>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
                  {motifsBlocage.map(m => {
                    const actif = motifChoisi === m.code;
                    return (
                      <button key={m.code} onClick={() => setMotifChoisi(m.code)}
                        style={{ textAlign:"left", padding:"11px 14px", borderRadius:10, cursor:"pointer",
                          border:`2px solid ${actif ? "#dc2626" : "#e5e7eb"}`,
                          background: actif ? "#fef2f2" : "#fff" }}>
                        <div style={{ fontSize:13, fontWeight:800, color: actif ? "#dc2626" : "#374151" }}>
                          {actif ? "◉" : "○"} {m.label}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <label style={{ display:"block", fontSize:12, fontWeight:700, color:"#374151", marginBottom:5 }}>
                Précision <span style={{ fontWeight:400, color:"#9ca3af" }}>(facultatif - remplace le texte type)</span>
              </label>
              <textarea value={precisionMotif} onChange={e => setPrecisionMotif(e.target.value)} rows={3}
                placeholder="Ex : 3 absences non justifiées en novembre, malgré deux relances."
                style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box", resize:"vertical", fontFamily:"inherit" }} />

              <div style={{ marginTop:14, padding:"12px 14px", borderRadius:10, background:"#f8fafc", border:"1px solid #e2e8f0" }}>
                <div style={{ fontSize:10, fontWeight:800, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".05em", marginBottom:6 }}>
                  Ce que l'apprenant verra et recevra
                </div>
                <div style={{ fontSize:12.5, color:"#0f172a", lineHeight:1.6 }}>{apercu || "-"}</div>
              </div>

              <div style={{ display:"flex", gap:10, marginTop:20 }}>
                <button onClick={() => setBlocageModal(null)} disabled={suspensionLoading}
                  style={{ flex:1, padding:"10px 0", borderRadius:8, border:"1.5px solid #e2e8f0", background:"#fff", color:"#374151", fontWeight:700, fontSize:13, cursor:suspensionLoading?"not-allowed":"pointer" }}>
                  Annuler
                </button>
                <button onClick={confirmer} disabled={suspensionLoading || !apercu}
                  style={{ flex:1, padding:"10px 0", borderRadius:8, border:"none", background:(suspensionLoading||!apercu)?"#e2e8f0":"#dc2626", color:(suspensionLoading||!apercu)?"#9ca3af":"#fff", fontWeight:800, fontSize:13, cursor:(suspensionLoading||!apercu)?"not-allowed":"pointer" }}>
                  {suspensionLoading ? "…" : "🔒 Bloquer et prévenir"}
                </button>
              </div>
          </ModaleAnimee>
        );
      })()}

      {showImportClassesResultat && importClassesResultats.length > 0 && (
        <Modal
          title="✅ Import terminé - Classes créées"
          onClose={() => setShowImportClassesResultat(false)}
          wide
        >
          <p style={{ margin:"-8px 0 14px", fontSize:12, color:"#6b7280" }}>Récapitulatif ligne par ligne.</p>
          <div style={{ overflowX:"auto", marginBottom:16 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr style={{ background:"#f8fafc" }}>
                  {["Nom classe","Statut","Détail"].map(h=>(
                    <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontWeight:700, fontSize:11, color:"#64748b", borderBottom:"2px solid #e2e8f0", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {importClassesResultats.map((r,i)=>(
                  <tr key={i} style={{ borderBottom:"1px solid #f1f5f9", background:r.statut==="ok"?"#fff":"#fff1f2" }}>
                    <td style={{ padding:"8px 12px", fontWeight:600 }}>{r.nom}</td>
                    <td style={{ padding:"8px 12px" }}>
                      {r.statut==="ok"
                        ? <span style={{ background:"#dcfce7", color:"#16a34a", borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>✓ Créée</span>
                        : <span style={{ background:"#fee2e2", color:"#dc2626", borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>✗ Erreur</span>
                      }
                    </td>
                    <td style={{ padding:"8px 12px", color:"#ef4444", fontSize:11 }}>{r.detail || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", gap:10 }}>
            {rendreBoutonAnnulerImport(() => { fetchClasses(); })}
            <button onClick={() => setShowImportClassesResultat(false)} style={{ padding:"9px 18px", background:"#7c3aed", border:"none", borderRadius:8, fontSize:12, color:"#fff", cursor:"pointer", fontWeight:700 }}>Fermer</button>
          </div>
        </Modal>
      )}

      {/* ══ MODAL IMPORT COACHING PRIVÉ - étape 1 : modèle + upload ══ */}
      {showImportPriveUpload && (
        <Modal title="🔒 Importer du coaching privé" onClose={() => setShowImportPriveUpload(false)} wide>
          <p style={{ margin:"-8px 0 18px", fontSize:12.5, color:"#6b7280", lineHeight:1.6 }}>
            Pour des relations <strong>déjà réelles</strong> (apprenant et coach déjà ensemble) - pas de nouveaux dossiers à faire valider.<br/><br/>
            1. Téléchargez le modèle Excel ci-dessous.<br/>
            2. Remplissez une ligne par coaching privé (nom apprenant et coach sont obligatoires).<br/>
            3. Importez le fichier rempli - vous pourrez vérifier chaque ligne avant de valider.
          </p>
          <button onClick={telechargerModeleImportPrive} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", justifyContent:"center", padding:"12px 0", background:"#f0fdf4", color:"#16a34a", border:"1.5px solid #bbf7d0", borderRadius:10, fontSize:13, fontWeight:700, cursor:"pointer", marginBottom:14 }}>
            📄 Télécharger le modèle Excel
          </button>
          <label htmlFor="import-prive-file" style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8, width:"100%", padding:"28px 0", border:"2px dashed #fbcfe8", borderRadius:10, background:"#fdf2f8", cursor:"pointer" }}>
            <span style={{ fontSize:24 }}>📁</span>
            <span style={{ fontSize:13, fontWeight:700, color:"#be185d" }}>Cliquez pour choisir le fichier rempli</span>
            <span style={{ fontSize:11, color:"#94a3b8" }}>.xlsx, .xls ou .csv</span>
          </label>
          <input id="import-prive-file" ref={importPriveFileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display:"none" }} onChange={lireEtPrevisualiserPrive} />
        </Modal>
      )}

      {/* ══ MODAL IMPORT COACHING PRIVÉ - prévisualisation ══ */}
      {showImportPrivePreview && (() => {
        const nbErreurs     = importPrivePreviewRows.filter(l => l._statut === "erreur").length;
        const nbAvertiss    = importPrivePreviewRows.filter(l => l._statut === "avertissement").length;
        const nbOk          = importPrivePreviewRows.filter(l => l._statut === "ok").length;
        const nbImportables = importPrivePreviewRows.filter(l => l._statut !== "erreur").length;
        return (
          <Modal
            title="🔍 Prévisualisation - Import coaching privé - Vérifiez avant de valider"
            onClose={() => { if (importPriveConfirming) { toast.error("Import en cours - ne fermez pas cette fenêtre."); return; } setShowImportPrivePreview(false); setImportPrivePreviewRows([]); }}
            size="xl"
          >
            <p style={{ margin:"-8px 0 14px", fontSize:12, color:"#6b7280" }}>{importPrivePreviewRows.length} ligne(s) · {nbOk} OK · {nbAvertiss} avertissement(s) · {nbErreurs} erreur(s) bloquante(s)</p>
            <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
              {nbOk > 0 && <div style={{ background:"#dcfce7", color:"#16a34a", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>✅ {nbOk} valide{nbOk>1?"s":""}</div>}
              {nbAvertiss > 0 && <div style={{ background:"#fef3c7", color:"#92400e", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>⚠️ {nbAvertiss} avertissement{nbAvertiss>1?"s":""}</div>}
              {nbErreurs > 0 && <div style={{ background:"#fee2e2", color:"#dc2626", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>🚫 {nbErreurs} erreur{nbErreurs>1?"s":""} bloquante{nbErreurs>1?"s":""}</div>}
              <div style={{ background:"#fdf2f8", color:"#be185d", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>
                🔒 Dossier + classe + coach créés directement à l'état final, sans passage par le PA
              </div>
            </div>

            {nbErreurs > 0 && (
              <div style={{ background:"#fff1f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:12, color:"#991b1b" }}>
                ⚠️ Les lignes en rouge <strong>ne seront pas importées</strong>. Corrigez le fichier et réimportez.
              </div>
            )}

            <div style={{ overflowX:"auto", maxHeight:420, overflowY:"auto", marginBottom:16, borderRadius:10, border:"1.5px solid #e2e8f0" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead style={{ position:"sticky", top:0, zIndex:2 }}>
                  <tr style={{ background:"#f8fafc" }}>
                    {["#","Statut","Apprenant","Email","Niveau","Lieu","Adresse","Coach","Pack","Montant payé","Disponibilités","Problèmes détectés"].map(h => (
                      <th key={h} style={{ padding:"9px 10px", textAlign:"left", fontWeight:700, fontSize:11, color:"#64748b", borderBottom:"2px solid #e2e8f0", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {importPrivePreviewRows.map((l, i) => {
                    const bgRow = l._statut==="erreur" ? "#fff1f2" : l._statut==="avertissement" ? "#fffbeb" : "#f0fdf4";
                    const allIssues = [
                      ...l._erreurs.map(e2 => ({ text:e2, type:"erreur" })),
                      ...l._avertissements.map(w => ({ text:w, type:"avertissement" })),
                    ];
                    return (
                      <tr key={i} style={{ borderBottom:"1px solid #f1f5f9", background:bgRow }}>
                        <td style={{ padding:"8px 10px", color:"#94a3b8", fontSize:11, fontWeight:600 }}>{l._idx}</td>
                        <td style={{ padding:"8px 10px", whiteSpace:"nowrap" }}>
                          {l._statut==="erreur"
                            ? <span style={{ background:"#fee2e2", color:"#dc2626", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700 }}>🚫 Erreur</span>
                            : l._statut==="avertissement"
                            ? <span style={{ background:"#fef3c7", color:"#92400e", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700 }}>⚠️ Attention</span>
                            : <span style={{ background:"#dcfce7", color:"#16a34a", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700 }}>✅ Valide</span>
                          }
                        </td>
                        <td style={{ padding:"8px 10px", fontWeight:600 }}>
                          {[l.prenom, l.nom].filter(Boolean).join(" ") || <span style={{ color:"#ef4444" }}>-</span>}
                          {l.profil_type === "enfant" && (
                            <span style={{ marginLeft:6, background:"#fef9c3", color:"#854d0e", borderRadius:20, padding:"2px 7px", fontSize:10, fontWeight:700, whiteSpace:"nowrap" }}>
                              👶 Enfant{l.nom_parent ? ` · ${l.nom_parent}` : ""}
                            </span>
                          )}
                        </td>
                        <td style={{ padding:"8px 10px", color:"#374151", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.email || <span style={{ color:"#d1d5db", fontSize:11 }}>{l.profil_type === "enfant" ? "- (parent)" : "-"}</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#374151" }}>{l.niveau || <span style={{ color:"#d1d5db", fontSize:11 }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#64748b", whiteSpace:"nowrap" }}>{l.lieu === "domicile" ? "🏠 Domicile" : "🏢 Cabinet"}</td>
                        <td style={{ padding:"8px 10px", color:"#374151", maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.lieu === "domicile" ? (l.adresse || <span style={{ color:"#dc2626", fontSize:11 }}>⚠ manquante</span>) : <span style={{ color:"#d1d5db", fontSize:11 }}>- (cabinet)</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#374151", whiteSpace:"nowrap" }}>{l.coach_nom || <span style={{ color:"#ef4444" }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#64748b", whiteSpace:"nowrap" }}>{l.pack === "intensive" ? "Intensive" : "Classique"}</td>
                        <td style={{ padding:"8px 10px", color:"#64748b", whiteSpace:"nowrap" }}>{l.montant_paye != null ? l.montant_paye.toLocaleString("fr-FR") + " F" : <span style={{ color:"#d1d5db", fontSize:11 }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#64748b", maxWidth:180 }}>{l.horaire?.length ? l.horaire.map(fmtHoraire).join(", ") : <span style={{ color:"#d1d5db", fontSize:11 }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", maxWidth:260 }}>
                          {allIssues.length === 0
                            ? <span style={{ color:"#86efac", fontSize:11 }}>Aucun problème</span>
                            : <ul style={{ margin:0, paddingLeft:14 }}>
                                {allIssues.map((iss, j) => (
                                  <li key={j} style={{ fontSize:11, color: iss.type==="erreur"?"#dc2626":"#92400e", fontWeight: iss.type==="erreur"?700:500, marginBottom:2 }}>
                                    {iss.text}
                                  </li>
                                ))}
                              </ul>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", alignItems:"center", flexWrap:"wrap" }}>
              {nbErreurs > 0 && <span style={{ fontSize:11, color:"#94a3b8", marginRight:"auto" }}>
                {nbImportables} ligne{nbImportables>1?"s":""} importable{nbImportables>1?"s":""} sur {importPrivePreviewRows.length}
              </span>}
              <button
                onClick={() => { setShowImportPrivePreview(false); setImportPrivePreviewRows([]); }}
                style={{ padding:"9px 18px", background:"#f1f5f9", border:"none", borderRadius:8, fontSize:12, color:"#374151", cursor:"pointer", fontWeight:600 }}>
                ✕ Fermer et corriger
              </button>
              {nbImportables > 0 && (
                <button
                  onClick={async () => {
                    if (await confirm(`${nbImportables} coaching${nbImportables>1?"s privés vont être créés":" privé va être créé"} directement en base (dossier + classe + coach + apprenant) - vérifiez l'aperçu ci-dessus avant de continuer.`, { titre: `Importer ${nbImportables} coaching${nbImportables>1?"s":""} privé${nbImportables>1?"s":""} ?`, confirmLabel: "Importer", danger: false })) confirmerImportPrive();
                  }}
                  disabled={importPriveConfirming}
                  style={{ padding:"9px 20px",
                    background: importPriveConfirming
                      ? `linear-gradient(to right, #9d174d ${importPriveProgress}%, #f9a8d4 ${importPriveProgress}%)`
                      : (nbErreurs>0 ? "#f59e0b" : "#be185d"),
                    transition:"background 0.3s ease",
                    border:"none", borderRadius:8, fontSize:12, color:"#fff", cursor: importPriveConfirming ? "not-allowed" : "pointer", fontWeight:700, opacity: importPriveConfirming?0.9:1 }}>
                  {importPriveConfirming ? `⏳ Import en cours… ${importPriveProgress}%` : nbErreurs > 0
                    ? `⚠️ Importer uniquement les ${nbImportables} ligne${nbImportables>1?"s":""} valides`
                    : `✅ Valider et importer ${nbImportables} coaching${nbImportables>1?"s":""} privé${nbImportables>1?"s":""}`
                  }
                </button>
              )}
            </div>
          </Modal>
        );
      })()}

      {/* ══ MODAL IMPORT COACHING PRIVÉ - résultat ══ */}
      {showImportPriveResultat && importPriveResultats.length > 0 && (
        <Modal
          title="✅ Import terminé - Coachings privés créés"
          onClose={() => setShowImportPriveResultat(false)}
          wide
        >
          <p style={{ margin:"-8px 0 14px", fontSize:12, color:"#6b7280" }}>Récapitulatif ligne par ligne.</p>
          <div style={{ overflowX:"auto", marginBottom:16 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr style={{ background:"#f8fafc" }}>
                  {["Apprenant","Statut","Détail"].map(h=>(
                    <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontWeight:700, fontSize:11, color:"#64748b", borderBottom:"2px solid #e2e8f0", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {importPriveResultats.map((r,i)=>(
                  <tr key={i} style={{ borderBottom:"1px solid #f1f5f9", background:r.statut==="ok"?"#fff":r.statut==="avertissement"?"#fffbeb":"#fff1f2" }}>
                    <td style={{ padding:"8px 12px", fontWeight:600 }}>{r.nom}</td>
                    <td style={{ padding:"8px 12px" }}>
                      {r.statut==="ok"
                        ? <span style={{ background:"#dcfce7", color:"#16a34a", borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>✓ Créé</span>
                        : r.statut==="avertissement"
                        ? <span style={{ background:"#fef3c7", color:"#b45309", borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>⚠ Créé avec avertissement</span>
                        : <span style={{ background:"#fee2e2", color:"#dc2626", borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>✗ Erreur</span>
                      }
                    </td>
                    <td style={{ padding:"8px 12px", color:r.statut==="avertissement"?"#b45309":"#ef4444", fontSize:11 }}>{r.detail || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", gap:10 }}>
            {rendreBoutonAnnulerImport(() => { fetchClasses(); })}
            <button onClick={() => setShowImportPriveResultat(false)} style={{ padding:"9px 18px", background:"#be185d", border:"none", borderRadius:8, fontSize:12, color:"#fff", cursor:"pointer", fontWeight:700 }}>Fermer</button>
          </div>
        </Modal>
      )}

      {/* ══ MODAL IMPORT APPRENANTS - étape 1 : modèle + upload ══ */}
      {showImportApprenantsUpload && (
        <Modal title="📤 Importer des apprenants dans des classes existantes" onClose={() => setShowImportApprenantsUpload(false)} wide>
          <p style={{ margin:"-8px 0 18px", fontSize:12.5, color:"#6b7280", lineHeight:1.6 }}>
            Pour des apprenants déjà dans une classe existante - aucune classe ni coach n'est créé ici.<br/><br/>
            1. Téléchargez le modèle Excel ci-dessous.<br/>
            2. Remplissez une ligne par apprenant - la colonne "Classe" est optionnelle : renseignez-la si vous
            connaissez déjà le nom exact de la classe, sinon laissez-la vide, vous choisirez une classe pour
            tout le lot juste après l'import.<br/>
            3. Importez le fichier rempli - un résumé des classes reconnues s'affiche avant le détail ligne par ligne.
          </p>
          <button onClick={telechargerModeleImportApprenants} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", justifyContent:"center", padding:"12px 0", background:"#f0fdf4", color:"#16a34a", border:"1.5px solid #bbf7d0", borderRadius:10, fontSize:13, fontWeight:700, cursor:"pointer", marginBottom:14 }}>
            📄 Télécharger le modèle Excel
          </button>
          <label htmlFor="import-apprenants-file" style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8, width:"100%", padding:"28px 0", border:"2px dashed #c4b5fd", borderRadius:10, background:"#f5f3ff", cursor:"pointer" }}>
            <span style={{ fontSize:24 }}>📁</span>
            <span style={{ fontSize:13, fontWeight:700, color:"#6d28d9" }}>Cliquez pour choisir le fichier rempli</span>
            <span style={{ fontSize:11, color:"#94a3b8" }}>.xlsx, .xls ou .csv</span>
          </label>
          <input id="import-apprenants-file" ref={importApprenantsFileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display:"none" }} onChange={lireEtPrevisualiserApprenants} />
        </Modal>
      )}

      {/* ══ MODAL IMPORT APPRENANTS - prévisualisation (résumé des classes + détail) ══ */}
      {showImportApprenantsPreview && (() => {
        const nbErreurs     = importApprenantsPreviewRows.filter(l => l._statut === "erreur").length;
        const nbAvertiss    = importApprenantsPreviewRows.filter(l => l._statut === "avertissement").length;
        const nbOk          = importApprenantsPreviewRows.filter(l => l._statut === "ok").length;
        const nbImportables = importApprenantsPreviewRows.filter(l => l._statut !== "erreur").length;

        // Résumé des classes distinctes référencées dans le fichier - visible avant le
        // détail ligne par ligne, pour repérer d'un coup d'œil une classe mal orthographiée
        // qui toucherait des centaines de lignes, plutôt que de le découvrir en scrollant.
        const parClasse = new Map(); // clé = classe_saisie normalisée (ou "" pour vide)
        importApprenantsPreviewRows.forEach(l => {
          const cle = normNomImport(l.classe_saisie || "");
          if (!parClasse.has(cle)) parClasse.set(cle, { libelle: l.classe_saisie || "", trouvee: !!l.classe_id, nomReel: l.classe_nom, count: 0 });
          parClasse.get(cle).count++;
        });
        const resumeClasses = Array.from(parClasse.values()).sort((a, b) => b.count - a.count);
        const bloqueParClasseIntrouvable = resumeClasses.some(c => c.libelle && !c.trouvee);

        return (
          <Modal
            title="🔍 Prévisualisation - Import apprenants - Vérifiez avant de valider"
            onClose={() => { if (importApprenantsConfirming) return; setShowImportApprenantsPreview(false); setImportApprenantsPreviewRows([]); }}
            size="xl"
          >
            {importApprenantsConfirming && (
              <div style={{ background:"#f5f3ff", border:"1.5px solid #c4b5fd", borderRadius:10, padding:"14px 16px", marginBottom:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:12.5, fontWeight:700, color:"#5b21b6" }}>⏳ Import en cours - {importApprenantsProgress}%</span>
                  <span style={{ fontSize:11, fontWeight:700, color:"#7c3aed" }}>⚠️ Ne fermez pas cette fenêtre</span>
                </div>
                <div style={{ height:8, background:"#ede9fe", borderRadius:99, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${importApprenantsProgress}%`, background:"#7c3aed", borderRadius:99, transition:"width .2s" }} />
                </div>
              </div>
            )}
            <p style={{ margin:"-8px 0 14px", fontSize:12, color:"#6b7280" }}>{importApprenantsPreviewRows.length} ligne(s) · {nbOk} OK · {nbAvertiss} avertissement(s) · {nbErreurs} erreur(s) bloquante(s)</p>

            <h4 style={{ margin:"0 0 8px", fontSize:13, fontWeight:800, color:"#0f172a" }}>🔎 Résumé des classes référencées</h4>
            <div style={{ overflowX:"auto", marginBottom:18, borderRadius:10, border:"1.5px solid #e2e8f0" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead>
                  <tr style={{ background:"#f8fafc" }}>
                    {["Classe référencée dans le fichier", "Lignes concernées", "Statut"].map(h => (
                      <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontWeight:700, fontSize:11, color:"#64748b", borderBottom:"2px solid #e2e8f0" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resumeClasses.map((c, i) => (
                    <tr key={i} style={{ borderBottom:"1px solid #f1f5f9" }}>
                      <td style={{ padding:"8px 12px", fontWeight:600 }}>{c.libelle ? c.nomReel || c.libelle : <span style={{ color:"#9ca3af", fontStyle:"italic" }}>(cellule vide)</span>}</td>
                      <td style={{ padding:"8px 12px" }}>{c.count}</td>
                      <td style={{ padding:"8px 12px" }}>
                        {!c.libelle
                          ? <span style={{ background:"#ede9fe", color:"#6d28d9", borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>🕓 À assigner après import</span>
                          : c.trouvee
                          ? <span style={{ background:"#dcfce7", color:"#16a34a", borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>✅ Trouvée</span>
                          : <span style={{ background:"#fee2e2", color:"#dc2626", borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>❌ Introuvable</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {bloqueParClasseIntrouvable && (
              <div style={{ background:"#fff1f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:12, color:"#991b1b" }}>
                🚫 Au moins une classe référencée est introuvable - corrigez directement la cellule "Classe" sur
                la ou les lignes concernées ci-dessous (pas besoin de réuploader le fichier), ou laissez-la vide
                pour l'assigner après l'import.
              </div>
            )}

            <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
              {nbOk > 0 && <div style={{ background:"#dcfce7", color:"#16a34a", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>✅ {nbOk} valide{nbOk>1?"s":""}</div>}
              {nbAvertiss > 0 && <div style={{ background:"#fef3c7", color:"#92400e", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>⚠️ {nbAvertiss} avertissement{nbAvertiss>1?"s":""}</div>}
              {nbErreurs > 0 && <div style={{ background:"#fee2e2", color:"#dc2626", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>🚫 {nbErreurs} erreur{nbErreurs>1?"s":""} bloquante{nbErreurs>1?"s":""}</div>}
            </div>

            {nbErreurs > 0 && (
              <div style={{ background:"#fff1f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:12, color:"#991b1b" }}>
                ⚠️ Les lignes en rouge <strong>ne seront pas importées</strong>. Corrigez-les directement dans le tableau (colonne Classe) ou dans le fichier et réimportez.
              </div>
            )}

            <div style={{ overflowX:"auto", maxHeight:420, overflowY:"auto", marginBottom:16, borderRadius:10, border:"1.5px solid #e2e8f0" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead style={{ position:"sticky", top:0, zIndex:2 }}>
                  <tr style={{ background:"#f8fafc" }}>
                    {["#","Statut","Apprenant","Email","Niveau","Classe","Début","Paiements","Problèmes détectés"].map(h => (
                      <th key={h} style={{ padding:"9px 10px", textAlign:"left", fontWeight:700, fontSize:11, color:"#64748b", borderBottom:"2px solid #e2e8f0", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {importApprenantsPreviewRows.map((l, i) => {
                    const bgRow = l._statut==="erreur" ? "#fff1f2" : l._statut==="avertissement" ? "#fffbeb" : "#f0fdf4";
                    const allIssues = [
                      ...l._erreurs.map(e2 => ({ text:e2, type:"erreur" })),
                      ...l._avertissements.map(w => ({ text:w, type:"avertissement" })),
                    ];
                    return (
                      <tr key={i} style={{ borderBottom:"1px solid #f1f5f9", background:bgRow }}>
                        <td style={{ padding:"8px 10px", color:"#94a3b8", fontSize:11, fontWeight:600 }}>{l._idx}</td>
                        <td style={{ padding:"8px 10px", whiteSpace:"nowrap" }}>
                          {l._statut==="erreur"
                            ? <span style={{ background:"#fee2e2", color:"#dc2626", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700 }}>🚫 Erreur</span>
                            : l._statut==="avertissement"
                            ? <span style={{ background:"#fef3c7", color:"#92400e", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700 }}>⚠️ Attention</span>
                            : <span style={{ background:"#dcfce7", color:"#16a34a", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700 }}>✅ Valide</span>
                          }
                        </td>
                        <td style={{ padding:"8px 10px", fontWeight:600 }}>{[l.prenom, l.nom].filter(Boolean).join(" ") || <span style={{ color:"#ef4444" }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#374151", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.email || <span style={{ color:"#d1d5db", fontSize:11 }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#374151" }}>{l.niveau || <span style={{ color:"#d1d5db", fontSize:11 }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", minWidth:200 }}>
                          <input
                            defaultValue={l.classe_saisie}
                            placeholder="(vide → après import)"
                            onBlur={ev => { if (ev.target.value !== l.classe_saisie) corrigerClasseLigneApprenant(i, ev.target.value); }}
                            style={{ width:"100%", padding:"5px 8px", borderRadius:6, border: l.classe_saisie && !l.classe_id ? "1.5px solid #ef4444" : "1px solid #e2e8f0", fontSize:11.5 }}
                          />
                        </td>
                        <td style={{ padding:"8px 10px", color:"#64748b", whiteSpace:"nowrap" }}>{l.date_debut || <span style={{ color:"#d1d5db", fontSize:11 }}>-</span>}</td>
                        <td style={{ padding:"8px 10px", color:"#64748b", whiteSpace:"nowrap" }}>
                          {l.paiements?.length
                            ? <div title={l.paiements.map(p => `${p.montant.toLocaleString("fr-FR")} F${p.date ? " · " + p.date : ""}${p.reference ? " · " + p.reference : ""}`).join("\n")}>
                                <div style={{ fontWeight:700, color:"#16a34a" }}>{l.montant_paye.toLocaleString("fr-FR")} F</div>
                                <div style={{ fontSize:10, color:"#94a3b8" }}>{l.paiements.length} versement{l.paiements.length>1?"s":""}</div>
                              </div>
                            : <span style={{ color:"#d1d5db", fontSize:11 }}>-</span>}
                        </td>
                        <td style={{ padding:"8px 10px", maxWidth:260 }}>
                          {allIssues.length === 0
                            ? <span style={{ color:"#86efac", fontSize:11 }}>Aucun problème</span>
                            : <ul style={{ margin:0, paddingLeft:14 }}>
                                {allIssues.map((iss, j) => (
                                  <li key={j} style={{ fontSize:11, color: iss.type==="erreur"?"#dc2626":"#92400e", fontWeight: iss.type==="erreur"?700:500, marginBottom:2 }}>
                                    {iss.text}
                                  </li>
                                ))}
                              </ul>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", alignItems:"center", flexWrap:"wrap" }}>
              {nbErreurs > 0 && <span style={{ fontSize:11, color:"#94a3b8", marginRight:"auto" }}>
                {nbImportables} ligne{nbImportables>1?"s":""} importable{nbImportables>1?"s":""} sur {importApprenantsPreviewRows.length}
              </span>}
              <button
                onClick={() => { setShowImportApprenantsPreview(false); setImportApprenantsPreviewRows([]); }}
                disabled={importApprenantsConfirming}
                style={{ padding:"9px 18px", background:"#f1f5f9", border:"none", borderRadius:8, fontSize:12, color:"#374151", cursor: importApprenantsConfirming ? "not-allowed" : "pointer", fontWeight:600, opacity: importApprenantsConfirming ? 0.5 : 1 }}>
                ✕ Fermer et corriger
              </button>
              {nbImportables > 0 && (
                <button
                  onClick={async () => {
                    if (await confirm(`${nbImportables} dossier${nbImportables>1?"s vont être créés":" va être créé"} - vérifiez l'aperçu ci-dessus avant de continuer.`, { titre: `Importer ${nbImportables} apprenant${nbImportables>1?"s":""} ?`, confirmLabel: "Importer", danger: false })) confirmerImportApprenants();
                  }}
                  disabled={importApprenantsConfirming}
                  style={{ padding:"9px 20px",
                    background: importApprenantsConfirming
                      ? `linear-gradient(to right, #6d28d9 ${importApprenantsProgress}%, #c4b5fd ${importApprenantsProgress}%)`
                      : (nbErreurs>0 ? "#f59e0b" : "#7c3aed"),
                    transition:"background 0.3s ease",
                    border:"none", borderRadius:8, fontSize:12, color:"#fff", cursor: importApprenantsConfirming ? "not-allowed" : "pointer", fontWeight:700, opacity: importApprenantsConfirming?0.9:1 }}>
                  {importApprenantsConfirming ? `⏳ Import en cours… ${importApprenantsProgress}%` : nbErreurs > 0
                    ? `⚠️ Importer uniquement les ${nbImportables} ligne${nbImportables>1?"s":""} valides`
                    : `✅ Valider et importer ${nbImportables} apprenant${nbImportables>1?"s":""}`
                  }
                </button>
              )}
            </div>
          </Modal>
        );
      })()}

      {/* ══ MODAL IMPORT APPRENANTS - choix groupé de la classe pour le lot "sans classe" ══ */}
      {showImportApprenantsChoixClasse && (
        <Modal title="🎯 Choisir la classe pour les apprenants restants" onClose={() => {}} wide>
          {importApprenantsConfirming && (
            <div style={{ background:"#f5f3ff", border:"1.5px solid #c4b5fd", borderRadius:10, padding:"14px 16px", marginBottom:16 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <span style={{ fontSize:12.5, fontWeight:700, color:"#5b21b6" }}>⏳ Affectation en cours - {importApprenantsProgress}%</span>
                <span style={{ fontSize:11, fontWeight:700, color:"#7c3aed" }}>⚠️ Ne fermez pas cette fenêtre</span>
              </div>
              <div style={{ height:8, background:"#ede9fe", borderRadius:99, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${importApprenantsProgress}%`, background:"#7c3aed", borderRadius:99, transition:"width .2s" }} />
              </div>
            </div>
          )}
          <p style={{ margin:"-8px 0 16px", fontSize:12.5, color:"#6b7280", lineHeight:1.6 }}>
            {importApprenantsEnAttente.length} apprenant{importApprenantsEnAttente.length>1?"s n'avaient":" n'avait"} pas de classe précisée dans le fichier - leur dossier est déjà créé. Choisissez une seule classe existante : tous seront inscrits dedans en une fois.
          </p>
          <select
            value={importApprenantsClasseChoisieId}
            onChange={e => setImportApprenantsClasseChoisieId(e.target.value)}
            disabled={importApprenantsConfirming}
            style={{ ...selectSt, width:"100%", marginBottom:18 }}
          >
            <option value="">- Choisir une classe -</option>
            {classes.filter(c => c.statut !== "terminé" && c.statut !== "cloture").map(c => (
              <option key={c.id} value={c.id}>{c.nom} {c.niveau ? `(${c.niveau})` : ""} - {c.nb_apprenants||0}/{c.capacite_max||20}</option>
            ))}
          </select>
          <div style={{ display:"flex", justifyContent:"flex-end" }}>
            <button
              onClick={async () => {
                const classeCible = classes.find(c => String(c.id) === String(importApprenantsClasseChoisieId));
                if (!classeCible) { toast.error("Choisissez une classe"); return; }
                if (await confirm(`${importApprenantsEnAttente.length} apprenant(s) vont être inscrits dans "${classeCible.nom}".`, { titre: "Confirmer l'affectation groupée", confirmLabel: "Affecter", danger: false })) confirmerChoixClasseGroupe();
              }}
              disabled={importApprenantsConfirming || !importApprenantsClasseChoisieId}
              style={{ padding:"9px 20px",
                background: importApprenantsConfirming
                  ? `linear-gradient(to right, #6d28d9 ${importApprenantsProgress}%, #c4b5fd ${importApprenantsProgress}%)`
                  : "#7c3aed",
                transition:"background 0.3s ease",
                border:"none", borderRadius:8, fontSize:12, color:"#fff", cursor: (importApprenantsConfirming || !importApprenantsClasseChoisieId) ? "not-allowed" : "pointer", fontWeight:700, opacity: importApprenantsConfirming ? 0.9 : (!importApprenantsClasseChoisieId ? 0.6 : 1) }}>
              {importApprenantsConfirming ? `⏳ Affectation en cours… ${importApprenantsProgress}%` : `✅ Affecter les ${importApprenantsEnAttente.length} apprenant${importApprenantsEnAttente.length>1?"s":""}`}
            </button>
          </div>
        </Modal>
      )}

      {/* ══ MODAL IMPORT APPRENANTS - résultat ══ */}
      {showImportApprenantsResultat && importApprenantsResultats.length > 0 && (
        <Modal
          title="✅ Import terminé - Apprenants"
          onClose={() => { setShowImportApprenantsResultat(false); setImportApprenantsResultats([]); }}
          wide
        >
          <p style={{ margin:"-8px 0 14px", fontSize:12, color:"#6b7280" }}>Récapitulatif ligne par ligne.</p>
          <div style={{ overflowX:"auto", marginBottom:16 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr style={{ background:"#f8fafc" }}>
                  {["Apprenant","Statut","Détail"].map(h=>(
                    <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontWeight:700, fontSize:11, color:"#64748b", borderBottom:"2px solid #e2e8f0", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {importApprenantsResultats.map((r,i)=>(
                  <tr key={i} style={{ borderBottom:"1px solid #f1f5f9", background:r.statut==="ok"?"#fff":r.statut==="avertissement"?"#fffbeb":"#fff1f2" }}>
                    <td style={{ padding:"8px 12px", fontWeight:600 }}>{r.nom}</td>
                    <td style={{ padding:"8px 12px" }}>
                      {r.statut==="ok"
                        ? <span style={{ background:"#dcfce7", color:"#16a34a", borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>✓ Créé</span>
                        : r.statut==="avertissement"
                        ? <span style={{ background:"#fef3c7", color:"#b45309", borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>⚠ Avertissement</span>
                        : <span style={{ background:"#fee2e2", color:"#dc2626", borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>✗ Erreur</span>
                      }
                    </td>
                    <td style={{ padding:"8px 12px", color: r.statut==="ok" ? "#16a34a" : r.statut==="avertissement" ? "#b45309" : "#ef4444", fontSize:11 }}>{r.detail || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", gap:10 }}>
            {rendreBoutonAnnulerImport(() => { fetchClasses(); })}
            <button onClick={() => { setShowImportApprenantsResultat(false); setImportApprenantsResultats([]); }} style={{ padding:"9px 18px", background:"#7c3aed", border:"none", borderRadius:8, fontSize:12, color:"#fff", cursor:"pointer", fontWeight:700 }}>Fermer</button>
          </div>
        </Modal>
      )}

      {/* ══ MODAL TRANSFERT DE GROUPE - choix de la classe cible ══ */}
      {/* Même composant que l'affectation initiale d'un apprenant à une classe
          (AssignationCentreModal) - cohérence demandée avec ce flux existant plutôt
          qu'une liste maison : mêmes colonnes, même bouton "+ Créer une classe".
          Étape "Nouveaux créneaux / motif" retirée pour le moment - le clic sur
          "Changer de groupe" ouvre directement ce sélecteur de classe. */}
      {showTransfert && (
        <AssignationCentreModal
          assignation={{ prospect_nom: transfertForm.nom_apprenant || "Apprenant", niveau_client: transfertForm.niveau }}
          /* TOUS les niveaux, pas seulement celui de l'apprenant : un transfert sert
             justement à acter une progression ou une régression. Le sélecteur remonte
             déjà les classes compatibles en premier et signale l'absence de classe au
             bon niveau - filtrer ici rendait ces repères inutiles et, surtout, empêchait
             toute réaffectation vers un autre niveau. */
          classes={classes.filter(cl => cl.id !== transfertForm.source_groupe_id)}
          /* La classe QUITTÉE, pour savoir si elle se videra — elle est justement exclue de
             la liste ci-dessus, d'où ce passage à part. */
          classeSource={classes.find(cl => cl.id === transfertForm.source_groupe_id) || null}
          coachs={coachsList}
          coachDispoMap={coachDispoMap}
          onSave={finaliserTransfert}
          onClose={() => setShowTransfert(false)}
          /* MIGRATION DE CANAL : le contexte et le tarif, en tête du même modal. L'assistante
             qui accepte ne connaît pas forcément le dossier - lui dire d'où part l'apprenant
             lui évite de le chercher ailleurs - et le tarif doit se décider ici, sans quoi il
             se perd : personne ne revient sur un écran qu'il a quitté. */
          /* LE TARIF VAUT POUR LES DEUX CIRCUITS SANS TEST, et pas seulement pour la migration
             de canal : deux CENTRES ne pratiquent pas le même prix non plus. Le champ était
             conditionné au seul `changement_coaching`, si bien qu'un transfert d'Angré à
             Yopougon partait toujours à l'ancien tarif - le serveur l'acceptait pourtant, il
             ne recevait simplement jamais la valeur.
             Le champ est FACULTATIF des deux côtés : laissé vide, la mensualité ne bouge pas. */
          enTete={(() => {
            /* LE TARIF VAUT POUR LES TROIS CIRCUITS. Il n'était proposé que sur deux - la
               migration de canal et le transfert de centre - alors qu'un changement de CLASSE
               peut lui aussi changer le prix : passer d'un groupe à un rythme plus soutenu ne
               coûte pas la même chose. Il fallait alors ressortir sur la fiche paiement après
               coup, et c'est exactement ce qu'on oublie de faire. */
            const t = demandeEnCours?.type_demande;
            if (!t) return null;
            const centre = t === "changement_centre";
            const migration = t === "changement_coaching";
            return (
              <>
                <div style={{ background: centre ? "#e0f2fe" : migration ? "#f2edfb" : "#f0fdf4",
                  border: `1px solid ${centre ? "#bae6fd" : migration ? "#ddd0f5" : "#bbf7d0"}`, borderRadius:10,
                  padding:"11px 14px", marginBottom:14, fontSize:12.5,
                  color: centre ? "#075985" : migration ? "#5b3a94" : "#15803d", lineHeight:1.6 }}>
                  {centre ? (
                    <>🏢 <strong>Transfert de centre</strong> — aucun test de niveau requis.
                    L'apprenant garde son niveau, son historique et toutes ses données :
                    seuls sa classe et son centre changent.</>
                  ) : migration ? (
                    <>🔀 <strong>Migration de canal</strong> — aucun test de niveau requis.
                    L'apprenant garde son niveau, son historique et toutes ses données :
                    seules sa classe et son canal changent.</>
                  ) : (
                    <>🔄 <strong>Changement de classe</strong> — le niveau est revenu du test.
                    L'apprenant garde son historique et toutes ses données ; ajustez la
                    mensualité ci-dessous si la nouvelle classe n'a pas le même tarif.</>
                  )}
                </div>
                <ChampTarifMigration
                  plan={planMigration}
                  valeur={nouvelleMensualite}
                  onChange={setNouvelleMensualite}
                  desLeMoisEnCours={tarifDesLeMoisEnCours}
                  onChangeDesLeMoisEnCours={setTarifDesLeMoisEnCours} />
              </>
            );
          })()}
          onCreateClasse={(niveauSuggere) => {
            fetchCoachs();
            setClasseForm({ nom:"", niveau: niveauSuggere||transfertForm.niveau||"", filiere:"", type_cours:"", coach_id:"", centre_id:"", date_debut:"", date_fin:"", capacite_max:20, horaire:[], photo_url:null, public_cible:"" });
            setClasseCoachSearch("");
            setShowCreateClasseInTransfert(true);
            setShowCreateClasse(true);
          }}
        />
      )}

      {/* ══ MODAL FINALISATION INSCRIPTION ══ */}
      {showFinalisationModal && finalisationTarget && (() => {
        const a    = finalisationTarget;
        const pmts = paiements.filter(p => p.assignationId === a.id);
        const totalDu   = pmts.length > 0 ? Math.max(...pmts.map(p => p.montantDu || 0)) : 0;
        const totalReçu = pmts.reduce((s,p)=>s+(p.montantReçu||0),0);
        const reste     = totalDu - totalReçu;
        const testLie   = tests.find(t => t.email && a.prospect_email && t.email.toLowerCase()===a.prospect_email.toLowerCase());
        const modeLabel = libelleCanal(a.type_cours, a.centre_nom);
        const coachLabel= ["groupe","standard"].includes(a.type_coaching) ? "Coaching groupe" : a.type_coaching==="prive" ? "Coaching privé" : "-";
        const offreCatalogueFinalis = matchOffreCatalogue(catalogueOffres, a.inscription_offre);

        return (
          <ModaleAnimee ouvert
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
            panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:1040, maxHeight:"94vh", overflowY:"auto", display:"flex", flexDirection:"column" }}>

              {/* Header */}
              <div style={{ background:BET_GRADIENT, padding:"20px 24px", borderRadius:"16px 16px 0 0", color:"#fff", display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexShrink:0 }}>
                <div>
                  <div style={{ fontSize:11, color:"#86efac", fontWeight:700, letterSpacing:".06em", marginBottom:4 }}>TRANSMISSION DU DOSSIER</div>
                  <h2 style={{ margin:0, fontSize:18, fontWeight:800 }}>📤 {a.prospect_nom}</h2>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.65)", marginTop:4 }}>{modeLabel} · {coachLabel}</div>
                </div>
                <button onClick={()=>{ setShowFinalisationModal(false); setFinalisationTarget(null); setDbCheckResult(null); }} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", width:30, height:30, borderRadius:"50%", cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
              </div>

              <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:16 }}>

                {/* ── CHECKLIST PRÉ-VALIDATION (vérification réelle en DB) ── */}
                {dbCheckLoading ? (
                  <div style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 16px", background:"#f8fafc", borderRadius:12, border:"1px solid #e2e8f0" }}>
                    <div style={{ width:16, height:16, border:"2px solid #7c3aed", borderTop:"2px solid transparent", borderRadius:"50%", animation:"spin 1s linear infinite", flexShrink:0 }} />
                    <span style={{ fontSize:12, color:"#6b7280" }}>Vérification des données en base de données…</span>
                  </div>
                ) : dbCheckResult?.error ? (
                  <div style={{ padding:"12px 16px", background:"#fff7ed", borderRadius:10, border:"1px solid #fed7aa", fontSize:12, color:"#c2410c" }}>
                    ⚠️ Impossible de vérifier la base de données : {dbCheckResult.error}
                  </div>
                ) : dbCheckResult ? (() => {
                  const db       = dbCheckResult.dbAss;
                  const dbPmts   = dbCheckResult.dbPaiements;
                  const dbTotal  = dbPmts.reduce((s,p)=>s+(p.montant_recu||0),0);
                  // montant_du est le montant TOTAL dû pour tout le dossier, répété sur chaque
                  // ligne de versement (pas un montant par versement) - le sommer comptait le
                  // même montant dû plusieurs fois dès qu'il y avait plus d'un versement,
                  // affichant un faux "reste à payer" alors que le dossier était soldé.
                  const dbMontantDu = dbPmts.length > 0 ? Math.max(...dbPmts.map(p=>p.montant_du||0)) : 0;
                  const dbReste  = dbMontantDu - dbTotal;

                  const checks = [
                    {
                      ok:   !!a.prospect_email,
                      label: a.prospect_email ? `Email enregistré - ${a.prospect_email}` : "Email du prospect manquant",
                      warn: false,
                    },
                    {
                      ok:   !!testLie,
                      label: testLie ? `Test de niveau effectué - ${testLie.niveau} (${testLie.score}%)` : "Test de niveau non effectué",
                      warn: true,
                    },
                    {
                      ok:   dbPmts.length > 0 && dbPmts.some(p=>(p.montant_recu||0)>0),
                      label: dbPmts.length > 0
                        ? `✓ DB - ${dbPmts.length} paiement${dbPmts.length>1?"s":""} · ${Number(dbTotal).toLocaleString("fr-FR")} FCFA confirmés en base`
                        : "❌ DB - Aucun paiement trouvé en base de données (données non sauvegardées ?)",
                      warn: false,
                    },
                    {
                      ok:   dbReste <= 0 && dbTotal > 0,
                      label: dbReste > 0
                        ? `Reste à payer (DB) : ${Number(dbReste).toLocaleString("fr-FR")} FCFA`
                        : dbTotal > 0 ? "Solde soldé en base" : "Montant non renseigné",
                      warn: true,
                    },
                    {
                      ok:   !!(db?.inscription_offre) || !!(a.inscription_offre),
                      label: db?.inscription_offre
                        ? `✓ DB - Offre enregistrée : ${db.inscription_offre}`
                        : a.inscription_offre
                          ? `✓ Offre confirmée : ${a.inscription_offre} (sera enregistrée à la transmission)`
                          : "⚠️ Offre de formation absente - à sélectionner ci-dessous",
                      warn: true,
                    },
                    {
                      ok:   !!(db?.niveau_client) || !!(a.niveau_client) || !!(testLie?.niveau),
                      label: db?.niveau_client
                        ? `✓ DB - Niveau enregistré : ${db.niveau_client}`
                        : testLie?.niveau
                          ? `✓ Niveau issu du test : ${testLie.niveau} (sera enregistré à la transmission)`
                          : a.niveau_client
                            ? `✓ Niveau confirmé : ${a.niveau_client} (sera enregistré à la transmission)`
                            : "⚠️ Niveau CECRL absent - à confirmer ci-dessous",
                      warn: true,
                    },
                  ];
                  const blockers = checks.filter(c => !c.ok && !c.warn);
                  const warnings = checks.filter(c => !c.ok && c.warn);
                  const allGood  = blockers.length === 0;
                  return (
                    <div style={{ borderRadius:12, border:`2px solid ${allGood?"#bbf7d0":"#fca5a5"}`, overflow:"hidden" }}>
                      <div style={{ padding:"10px 14px", background:allGood?"#dcfce7":"#fee2e2", display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:16 }}>{allGood ? "✅" : "⛔"}</span>
                        <span style={{ fontSize:12, fontWeight:800, color:allGood?"#15803d":"#991b1b" }}>
                          {allGood
                            ? "Dossier vérifié - prêt pour transmission"
                            : `${blockers.length} point${blockers.length>1?"s":""} bloquant${blockers.length>1?"s":""} détecté${blockers.length>1?"s":""} dans la base de données`}
                        </span>
                        {warnings.length > 0 && <span style={{ fontSize:11, color:"#92400e", marginLeft:"auto" }}>{warnings.length} avert.</span>}
                      </div>
                      <div style={{ padding:"10px 14px", display:"flex", flexDirection:"column", gap:5, background:"#fff" }}>
                        {checks.map((c, i) => (
                          <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:8, padding:"6px 10px", borderRadius:8, background: c.ok?"#f0fdf4":c.warn?"#fffbeb":"#fff5f5", border:`1px solid ${c.ok?"#bbf7d0":c.warn?"#fde68a":"#fca5a5"}` }}>
                            <span style={{ fontSize:13, flexShrink:0, marginTop:1 }}>{c.ok?"✅":c.warn?"⚠️":"❌"}</span>
                            <span style={{ fontSize:12, fontWeight:600, color:c.ok?"#15803d":c.warn?"#92400e":"#991b1b", lineHeight:1.4 }}>{c.label}</span>
                          </div>
                        ))}
                      </div>
                      {blockers.length > 0 && (
                        <div style={{ padding:"8px 14px", background:"#fff1f2", borderTop:"1px solid #fecdd3", fontSize:11, color:"#be123c", fontWeight:600 }}>
                          🔴 Résolvez les points bloquants avant de transmettre - complétez les données manquantes dans le formulaire ci-dessous.
                        </div>
                      )}
                    </div>
                  );
                })() : null}

                {/* ── Récap prospect (lecture seule) ── */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  {/* Identité */}
                  <div style={{ background:"#f8fafc", borderRadius:10, padding:"12px 14px", border:"1px solid #e2e8f0" }}>
                    <div style={{ fontSize:10, fontWeight:800, color:"#0891b2", marginBottom:8, textTransform:"uppercase", letterSpacing:".06em" }}>👤 Prospect</div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>{a.prospect_nom||"-"}</div>
                    {a.prospect_email     && <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>✉ {a.prospect_email}</div>}
                    {a.prospect_telephone && <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>📞 {a.prospect_telephone}</div>}
                  </div>
                  {/* Test */}
                  <div style={{ background:"#f8fafc", borderRadius:10, padding:"12px 14px", border:"1px solid #e2e8f0" }}>
                    <div style={{ fontSize:10, fontWeight:800, color:"#7c3aed", marginBottom:8, textTransform:"uppercase", letterSpacing:".06em" }}>📝 Test de niveau</div>
                    {testLie ? (
                      <>
                        <span style={{ fontWeight:800, fontSize:15, color:"#fff", background:{ A1:"#6b7280",A2:"#0891b2",B1:"#16a34a",B2:"#7c3aed",C1:"#d97706",C2:"#dc2626" }[testLie.niveau]||"#6b7280", borderRadius:999, padding:"2px 12px" }}>{testLie.niveau}</span>
                        <span style={{ marginLeft:8, fontSize:12, color:"#6b7280" }}>{testLie.score}%</span>
                      </>
                    ) : <div style={{ fontSize:12, color:"#94a3b8", fontStyle:"italic" }}>Non effectué</div>}
                  </div>
                </div>

                {/* Paiements */}
                <div style={{ borderRadius:10, border:`2px solid ${reste<=0?"#bbf7d0":"#fde68a"}`, overflow:"hidden" }}>
                  <div style={{ padding:"9px 14px", background:reste<=0?"#dcfce7":"#fef3c7", display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:13 }}>{reste<=0?"✅":"⚠️"}</span>
                    <span style={{ fontSize:12, fontWeight:700, color:reste<=0?"#15803d":"#92400e" }}>
                      {reste<=0 ? "Paiement intégral confirmé" : `Paiement partiel - ${Number(reste).toLocaleString("fr-FR")} FCFA restants`}
                    </span>
                    <span style={{ marginLeft:"auto", fontSize:11, color:"#64748b" }}>{pmts.length} versement{pmts.length>1?"s":""}</span>
                  </div>
                  <div style={{ display:"flex", padding:"10px 14px", gap:0 }}>
                    {[{l:"Total dû",v:totalDu,c:"#0f172a"},{l:"Reçu",v:totalReçu,c:"#22c55e"},{l:"Reste",v:reste,c:reste>0?"#ef4444":"#22c55e"}].map((s,i,arr)=>(
                      <div key={s.l} style={{ flex:1, textAlign:"center", borderRight:i<arr.length-1?"1px solid #e2e8f0":"none" }}>
                        <div style={{ fontSize:10, color:"#94a3b8", marginBottom:3 }}>{s.l}</div>
                        <div style={{ fontSize:14, fontWeight:800, color:s.c }}>{Number(Math.max(0,s.v)).toLocaleString("fr-FR")} F</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Séparateur */}
                <div style={{ borderTop:"2px dashed #e2e8f0", paddingTop:4 }}>
                  <div style={{ fontSize:12, fontWeight:800, color:"#0f172a", marginBottom:2 }}>📋 Informations de formation</div>
                  <div style={{ fontSize:11, color:"#9ca3af" }}>Complétez les détails ci-dessous pour finaliser l'inscription officielle.</div>
                </div>

                {/* ── Formule choisie par le client ────────────────────────────────────
                    L'écran de vérification listait le test, les paiements et l'offre, mais
                    pas le rythme retenu - alors que c'est précisément lui que l'Onboarding
                    lira pour affecter la classe. On le montre avant de transmettre. ── */}
                {(() => {
                  const fg = a.suivi_demarrage?.formule_groupe;
                  const packSeul = !fg && a.type_coaching !== "prive" ? a.suivi_demarrage?.pack_coaching : null;
                  if (!fg && !packSeul) return null;
                  return (
                    <div style={{ borderRadius:10, border:"2px solid #bbf7d0", overflow:"hidden" }}>
                      <div style={{ padding:"9px 14px", background:"#dcfce7", display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:13 }}>👥</span>
                        <span style={{ fontSize:12, fontWeight:700, color:"#15803d" }}>Formule choisie par le client</span>
                        {fg?.canal && (
                          <span style={{ marginLeft:"auto", fontSize:11, color:"#166534", fontWeight:700 }}>
                            {fg.canal === "presentiel" ? "🏢 Présentiel" : "💻 En ligne"}
                          </span>
                        )}
                      </div>
                      <div style={{ padding:"11px 14px", display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:14, fontWeight:800, color:"#0f172a" }}>{fg?.label || packSeul}</div>
                          {fg?.frequence && <div style={{ fontSize:11.5, color:"#64748b", marginTop:2 }}>{fg.frequence}</div>}
                          {a.suivi_demarrage?.formation_souhaitee && (
                            <div style={{ fontSize:11.5, color:"#64748b", marginTop:3 }}>📚 {a.suivi_demarrage.formation_souhaitee}</div>
                          )}
                        </div>
                        {(fg?.tarif || a.suivi_demarrage?.cout_formation) > 0 && (
                          <div style={{ textAlign:"right", flexShrink:0 }}>
                            <div style={{ fontSize:14, fontWeight:800, color:"#15803d" }}>
                              {Number(fg?.tarif || a.suivi_demarrage.cout_formation).toLocaleString("fr-FR")} F
                            </div>
                            <div style={{ fontSize:10, color:"#64748b", fontWeight:700 }}>/ mois</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Note d'information pour la commerciale */}
                <div style={{ padding:"10px 14px", background:"#eff6ff", borderRadius:10, border:"1px solid #bfdbfe", fontSize:12, color:"#1d4ed8" }}>
                  ℹ️ L'affectation au groupe (Onboarding) et l'assignation du coach (PA Advisor) seront effectuées après transmission.
                </div>

                {/* Formulaire d'inscription intégré */}
                <InscriptionForm
                  coachingPriveConfig={coachingPriveConfig}
                  initialData={{
                    client:     a.prospect_nom       || "",
                    contact:    a.prospect_nom       || "",
                    telephone:  a.prospect_telephone || "",
                    email:      a.prospect_email     || "",
                    offre:      a.inscription_offre || pmts.find(p => p.offre)?.offre || (testLie ? offreParNiveau(testLie.niveau) : ""),
                    niveauTest: testLie?.niveau || a.niveau_client || "",
                    dateDebut:  a.date_debut_formation ? a.date_debut_formation.slice(0,10) : "",
                    date:       new Date().toISOString().slice(0,10),
                    /* Le tarif de la formule prime sur le prix catalogue : c'est celui que le
                       client a vu et payé. Sans cela, la commerciale devait le ressaisir. */
                    montant:    a.suivi_demarrage?.formule_groupe?.tarif || a.suivi_demarrage?.cout_formation || offreCatalogueFinalis?.prix_num || pmts.find(p => p.offre)?.montantDu || (pmts.length === 1 ? totalDu : 0),
                    /* La formule retenue, pré-sélectionnée dans le sélecteur du formulaire et
                       reportée telle quelle dans le dossier transmis à l'Onboarding. */
                    formule_groupe:        a.suivi_demarrage?.formule_groupe?.id || "",
                    formule_groupe_detail: a.suivi_demarrage?.formule_groupe || null,
                    statut:     "confirmée",
                    apprenantConverti: false,
                    documents:  [],
                    notesAdmin: `Parcours BET · ${modeLabel} · ${coachLabel}${a.assistante_nom ? " · Assistante : "+a.assistante_nom : ""}`,
                    // Profil enfant / coordonnées parent - déjà présents sur l'assignation,
                    // à reporter ici sinon le formulaire retombe sur "Adulte" et affiche des
                    // champs enfant/parent vides malgré des données existantes.
                    profil_type:          a.profil_type          || "adulte",
                    type_client:          a.type_client          || "particulier",
                    objectif_client:      a.objectif_client      || "",
                    prenom_enfant:        a.prenom_enfant        || "",
                    nom_enfant:           a.nom_enfant           || "",
                    age_enfant:           a.age_enfant           || "",
                    niveau_classe_enfant: a.niveau_classe_enfant || "",
                    nom_parent:           a.nom_parent           || "",
                    telephone_parent:     a.telephone_parent     || "",
                    email_parent:         a.email_parent         || "",
                  }}
                  onSave={(formData) => handleFinaliserInscription(a, formData)}
                  onCancel={() => { setShowFinalisationModal(false); setFinalisationTarget(null); setDbCheckResult(null); }}
                  finalisationMode
                  catalogueOffres={catalogueOffres}
                />

              </div>
          </ModaleAnimee>
        );
      })()}
      {showPaiementModal && (
        <Modal
          title={editingItem?.id ? "Modifier le paiement" : tranchePayTarget ? `💳 Paiement tranche - ${editingItem?.client}` : editingItem?.assignationId ? `💳 Paiement - ${editingItem.client}` : "Enregistrer un paiement"}
          onClose={() => { setShowPaiementModal(false); setEditingItem(null); setTranchePayTarget(null); }}
          wide
        >
          <PaiementForm
            initialData={editingItem}
            onSave={handleSavePaiement}
            onCancel={() => { setShowPaiementModal(false); setEditingItem(null); setTranchePayTarget(null); }}
            apprenants={assignations.filter(a => a.statut === "converti")}
            catalogueOffres={catalogueOffres}
          />
        </Modal>
      )}
      {moisEcheanceTarget && (
        <Modal
          title={`✏️ Modifier ${moisEcheanceTarget.mois.label}`}
          onClose={() => setMoisEcheanceTarget(null)}
        >
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            <p style={{ fontSize:12.5, color:"#64748b", margin:0 }}>
              L'apprenant ne paie pas forcément le montant ou la date standard du plan - ajustez ce mois précis sans toucher aux autres.
            </p>
            <div>
              <label style={{ display:"block", fontSize:12, fontWeight:700, color:"#374151", marginBottom:6 }}>Montant dû (FCFA)</label>
              <input
                type="number" min="1" value={moisMontantValeur}
                onChange={e => setMoisMontantValeur(e.target.value)}
                style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:14, boxSizing:"border-box" }}
              />
            </div>
            <div>
              <label style={{ display:"block", fontSize:12, fontWeight:700, color:"#374151", marginBottom:6 }}>Date d'échéance</label>
              <input
                type="date" value={moisEcheanceValeur}
                onChange={e => setMoisEcheanceValeur(e.target.value)}
                style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:14, boxSizing:"border-box" }}
              />
            </div>

            <div style={{ borderTop:"1px solid #f1f5f9", paddingTop:14 }}>
              <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12.5, fontWeight:700, color:"#374151", cursor:"pointer" }}>
                <input type="checkbox" checked={moisReductionActive} onChange={e => setMoisReductionActive(e.target.checked)} />
                🎁 Appliquer une réduction sur ce mois
              </label>
              <p style={{ fontSize:11, color:"#94a3b8", margin:"4px 0 0 24px" }}>
                Ex : badge de fidélité "6e mois avec BET" (-10%) - à appliquer manuellement, jamais automatique.
              </p>

              {moisReductionActive && (
                <div style={{ marginTop:12, display:"flex", flexDirection:"column", gap:10, background:"#fef3c7", border:"1px solid #fde68a", borderRadius:8, padding:12 }}>
                  <div>
                    <label style={{ display:"block", fontSize:11.5, fontWeight:700, color:"#92400e", marginBottom:5 }}>Motif</label>
                    <input
                      value={moisReductionMotif} onChange={e => setMoisReductionMotif(e.target.value)}
                      placeholder="Ex : Badge fidélité 6e mois"
                      style={{ width:"100%", padding:"7px 10px", border:"1px solid #fde68a", borderRadius:6, fontSize:12.5, boxSizing:"border-box" }}
                    />
                  </div>
                  <div style={{ display:"flex", gap:8 }}>
                    <select value={moisReductionType} onChange={e => setMoisReductionType(e.target.value)} style={{ padding:"7px 8px", border:"1px solid #fde68a", borderRadius:6, fontSize:12.5, background:"#fff" }}>
                      <option value="pourcentage">%</option>
                      <option value="montant">FCFA</option>
                    </select>
                    <input
                      type="number" min="1" value={moisReductionValeur} onChange={e => setMoisReductionValeur(e.target.value)}
                      placeholder={moisReductionType === "pourcentage" ? "10" : "1000"}
                      style={{ flex:1, padding:"7px 10px", border:"1px solid #fde68a", borderRadius:6, fontSize:12.5, boxSizing:"border-box" }}
                    />
                  </div>
                  {Number(moisMontantValeur) > 0 && Number(moisReductionValeur) > 0 && (() => {
                    const base = Number(moisMontantValeur);
                    const deduit = moisReductionType === "pourcentage" ? Math.round(base * Number(moisReductionValeur) / 100) : Math.min(Number(moisReductionValeur), base);
                    return (
                      <div style={{ fontSize:12.5, color:"#92400e", fontWeight:700 }}>
                        Montant final : {(base - deduit).toLocaleString("fr-FR")} F <span style={{ fontWeight:500, color:"#b45309" }}>(-{deduit.toLocaleString("fr-FR")} F)</span>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => setMoisEcheanceTarget(null)} style={{ padding:"9px 18px", background:"#f1f5f9", border:"none", borderRadius:8, fontWeight:600, cursor:"pointer", fontSize:13 }}>Annuler</button>
              <button onClick={sauvegarderMoisEcheance} disabled={moisEcheanceSaving} style={{ padding:"9px 20px", background: moisEcheanceSaving ? "#94a3b8" : "#0891b2", color:"#fff", border:"none", borderRadius:8, fontWeight:700, cursor: moisEcheanceSaving ? "not-allowed" : "pointer", fontSize:13 }}>
                {moisEcheanceSaving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {showTestModal && (
        <Modal
          title={
            editingItem?.correction_statut === "en_attente"
              ? `✏️ Corriger le test ${editingItem?.format_test || ""} - ${editingItem?.prenom} ${editingItem?.nom}`
              : editingItem?.id
              ? "Modifier le test"
              : editingItem?.email
              ? "📝 Saisir résultat du test physique"
              : "Ajouter un test manuellement"
          }
          onClose={() => { setShowTestModal(false); setEditingItem(null); }}
          size="xxl"
        >
          <TestForm initialData={editingItem} onSave={handleSaveTest} onCancel={() => { setShowTestModal(false); setEditingItem(null); }} />
        </Modal>
      )}
      {/* ══ MODAL DÉTAIL TEST DE NIVEAU ══ */}
      {showTestDetailModal && selectedTest && (() => {
        const t = selectedTest;
        const scoreColor = t.score >= 70 ? "#22c55e" : t.score >= 50 ? "#f59e0b" : "#ef4444";
        const cats = Object.entries(t.by_category || {});
        const cefrs = Object.entries(t.by_cefr || {});
        const answers = Array.isArray(t.answers_details) ? t.answers_details : [];
        const audioAnswers = t.audio_answers || {};
        const hasAudio = Object.keys(audioAnswers).length > 0;
        const mins = Math.floor((t.time_taken_seconds||0)/60);
        const secs = (t.time_taken_seconds||0) % 60;
        const CAT_ICONS = { Grammaire:"📖", Vocabulaire:"💬", Compréhension:"🧠", Listening:"🎧", Speaking:"🎤", Writing:"✍️", Reading:"📚" };
        const CEFR_COLORS = { A1:"#94a3b8", A2:"#f59e0b", B1:"#1e3a8a", B2:"#7c3aed", C1:"#059669", C2:"#dc2626" };
        // Les autres passages du même apprenant : un transfert en ajoute un, et on doit
        // pouvoir relire l'ancien sans refermer le modal pour aller le rechercher dans la liste.
        const passages = passagesDuMemeApprenant(t);

        return (
          <Modal title={`🔍 Test de niveau - ${t.prenom} ${t.nom}`} onClose={() => { setShowTestDetailModal(false); setSelectedTest(null); }} size="xxl">

            {/* ── Les passages successifs ──────────────────────────────────────────────
                Ne s'affiche qu'à partir de deux : un seul passage n'est pas un historique,
                et une barre de sélection à un seul bouton ferait chercher un choix inexistant. */}
            {passages.length > 1 && (
              <div style={{ marginBottom:16, padding:"12px 14px", borderRadius:10, background:"#f8fafc", border:"1px solid #e2e8f0" }}>
                <div style={{ fontSize:11, fontWeight:800, color:"#64748b", textTransform:"uppercase", letterSpacing:".05em", marginBottom:9 }}>
                  📝 {passages.length} tests passés - cliquez pour voir le détail de chacun
                </div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {passages.map((p, i) => {
                    const actuel = p.id === t.id;
                    // Le plus récent est en tête du tri : c'est le niveau qui vaut aujourd'hui.
                    const dernier = i === 0;
                    const c = CEFR_COLORS[(p.niveau || "").toUpperCase()] || "#475569";
                    return (
                      <button
                        key={p.id || i}
                        onClick={() => setSelectedTest(p)}
                        style={{
                          display:"flex", flexDirection:"column", alignItems:"flex-start", gap:2,
                          padding:"8px 13px", borderRadius:9, cursor:"pointer", textAlign:"left",
                          background: actuel ? "#fff" : "transparent",
                          border: actuel ? `2px solid ${c}` : "1px solid #e2e8f0",
                          boxShadow: actuel ? "0 1px 4px rgba(0,0,0,.07)" : "none",
                        }}
                      >
                        <span style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <span style={{ fontSize:13, fontWeight:800, color:c }}>{p.niveau || "-"}</span>
                          {p.score != null && <span style={{ fontSize:11, color:"#64748b" }}>{p.score}%</span>}
                          {dernier && <span style={{ fontSize:9, fontWeight:700, color:"#16a34a", background:"#dcfce7", padding:"1px 6px", borderRadius:99 }}>ACTUEL</span>}
                        </span>
                        <span style={{ fontSize:10, color:"#94a3b8" }}>
                          {formatDate(p.date || p.submitted_at)}
                          {p.source === "oral" ? " · présentiel" : ""}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Candidat + Score global ── */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:16 }}>
              <div style={{ padding:14, borderRadius:10, background:"#f0f9ff", border:"1px solid #bae6fd" }}>
                <div style={{ fontSize:11, fontWeight:700, color:"#0369a1", marginBottom:10 }}>👤 Candidat</div>
                {[
                  ["Nom",`${t.prenom} ${t.nom}`],
                  ["Email", t.email],
                  ["Téléphone", t.telephone||"-"],
                  ["Profil", t.profil],
                  ["Date", formatDate(t.date)],
                  ["Durée", `${mins}m ${secs}s`],
                  ["Réponses", `${t.correct_answers||0} / ${t.total_questions||0} correctes`],
                ].map(([l,v])=>(
                  <div key={l} style={{ display:"flex", justifyContent:"space-between", marginBottom:5, fontSize:12 }}>
                    <span style={{ color:"#6b7280" }}>{l}</span><span style={{ fontWeight:600 }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ padding:14, borderRadius:10, background:"#f0fdf4", border:"1px solid #bbf7d0", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                <div style={{ fontSize:48, fontWeight:900, color:scoreColor, lineHeight:1 }}>{t.score}%</div>
                <div style={{ fontSize:22, fontWeight:800, color:BET_COLOR, margin:"6px 0 2px" }}>{t.niveau}</div>
                <div style={{ fontSize:11, color:"#9ca3af", marginBottom:12 }}>Niveau CECRL</div>
                <div style={{ width:"100%", height:10, background:"#e5e7eb", borderRadius:5, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${t.score}%`, background:scoreColor, borderRadius:5 }} />
                </div>
                <div style={{ fontSize:11, color:"#6b7280", marginTop:6 }}>{t.points_earned} / {t.points_total} pts</div>
              </div>
            </div>

            {/* ── Scores par catégorie ── */}
            {cats.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:8 }}>📊 Scores par compétence</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))", gap:8 }}>
                  {cats.map(([cat, val])=>{
                    // Le test public (FreeLevelTest.jsx) stocke { correct, total, pts, max } -
                    // "earned"/"total" n'ont jamais existé sous ce nom, d'où le NaN% affiché
                    // avant ce correctif : on retombe sur pts/max (points), puis correct/total.
                    const earned = val.pts ?? val.earned ?? val.correct ?? 0;
                    const totalV = val.max ?? val.total ?? 0;
                    const pct = totalV > 0 ? Math.round((earned/totalV)*100) : 0;
                    const c = pct>=70?"#22c55e":pct>=50?"#f59e0b":"#ef4444";
                    return (
                      <div key={cat} style={{ padding:"10px 12px", borderRadius:9, background:"#f8fafc", border:"1px solid #e5e7eb" }}>
                        <div style={{ fontSize:11, color:"#6b7280", marginBottom:4 }}>{CAT_ICONS[cat]||"•"} {cat}</div>
                        <div style={{ fontSize:18, fontWeight:800, color:c }}>{pct}%</div>
                        <div style={{ height:5, background:"#e5e7eb", borderRadius:3, marginTop:5 }}>
                          <div style={{ height:"100%", width:`${pct}%`, background:c, borderRadius:3 }} />
                        </div>
                        <div style={{ fontSize:10, color:"#9ca3af", marginTop:4 }}>{earned}/{totalV} pts</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Scores par niveau CEFR ── */}
            {cefrs.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:8 }}>🎯 Scores par niveau CECRL</div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {cefrs.map(([lvl, val])=>{
                    // by_cefr ne trace que { correct, total } (comptage de questions, pas de
                    // points) - jamais de champ "earned" - même correctif que pour by_category.
                    const earned = val.correct ?? val.earned ?? 0;
                    const totalV = val.total ?? 0;
                    const pct = totalV > 0 ? Math.round((earned/totalV)*100) : 0;
                    return (
                      <div key={lvl} style={{ padding:"8px 14px", borderRadius:8, background:"#f8fafc", border:`2px solid ${CEFR_COLORS[lvl]||"#e5e7eb"}`, minWidth:80, textAlign:"center" }}>
                        <div style={{ fontSize:15, fontWeight:800, color:CEFR_COLORS[lvl]||"#374151" }}>{lvl}</div>
                        <div style={{ fontSize:13, fontWeight:700, color:"#374151" }}>{pct}%</div>
                        <div style={{ fontSize:10, color:"#9ca3af" }}>{earned}/{totalV}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Enregistrements audio (Speaking) - notation directement ici, plus besoin
                de passer par "Modifier" pour évaluer chaque réponse orale ── */}
            {hasAudio && (
              <div style={{ marginBottom:16, padding:14, borderRadius:10, background:"#fdf4ff", border:"1px solid #e9d5ff" }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#7e22ce", marginBottom:2 }}>🎤 Réponses orales (Speaking)</div>
                {t.correction_statut === "en_attente" && (
                  <div style={{ fontSize:11, color:"#a855f7", marginBottom:8 }}>Note indicative par question - le score et le niveau final se choisissent plus bas.</div>
                )}
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {Object.entries(audioAnswers).map(([qId, url])=>{
                    const sn = testCorrection.speaking_notes?.[qId] || { note:"", score:"" };
                    const setSN = (patch) => setTestCorrection(c => ({ ...c, speaking_notes: { ...(c.speaking_notes||{}), [qId]: { ...sn, ...patch } } }));
                    return (
                      <div key={qId} style={{ background:"#fff", borderRadius:8, padding:"10px 14px", border:"1px solid #e9d5ff" }}>
                        <div style={{ fontSize:11, color:"#7e22ce", fontWeight:600, marginBottom:6 }}>Question #{qId}</div>
                        <audio controls src={url} style={{ width:"100%", height:36, marginBottom: t.correction_statut === "en_attente" ? 8 : 0 }} />
                        {t.correction_statut === "en_attente" && (
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 100px", gap:8 }}>
                            <input
                              placeholder="Note d'évaluation (prononciation, fluidité, vocabulaire…)"
                              value={sn.note}
                              onChange={e => setSN({ note: e.target.value })}
                              style={{ padding:"7px 10px", borderRadius:6, border:"1px solid #e9d5ff", fontSize:12 }}
                            />
                            <input
                              type="number" min={0} max={100} placeholder="Score %"
                              value={sn.score}
                              onChange={e => setSN({ score: e.target.value === "" ? "" : Number(e.target.value) })}
                              style={{ padding:"7px 10px", borderRadius:6, border:"1px solid #e9d5ff", fontSize:12 }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Correction manuelle directement ici (plus besoin du bouton Modifier) ── */}
            {t.correction_statut === "en_attente" && (
              <div style={{ marginBottom:16, padding:14, borderRadius:10, background:"#fffbeb", border:"1px solid #fde68a" }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#92400e", marginBottom:10 }}>✏️ Attribuer le niveau et le score final</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                  <div>
                    <label style={labelSt}>Niveau CECRL *</label>
                    <select style={inputSt} value={testCorrection.niveau} onChange={e=>setTestCorrection(c=>({...c,niveau:e.target.value}))}>
                      {["A1","A2","B1","B2","C1","C2"].map(n=><option key={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelSt}>Score (%)</label>
                    <input type="number" min={0} max={100} style={inputSt} value={testCorrection.score} onChange={e=>setTestCorrection(c=>({...c,score:Number(e.target.value)}))} />
                  </div>
                </div>
                {(() => {
                  const nSpeakingTotal = Object.keys(audioAnswers).length;
                  const nLibre = answers.filter(a => a.type === "libre").length;
                  const nGradable = Math.max(0, (t.total_questions || 0) - nSpeakingTotal - nLibre);
                  const scored = Object.values(testCorrection.speaking_notes || {}).filter(v => v?.score !== "" && v?.score != null && !isNaN(Number(v.score)));
                  if (scored.length === 0) return null;
                  const speakingAvg = scored.reduce((s,v)=>s+Number(v.score),0) / scored.length;
                  const qcmScore = Number(t.score) || 0;
                  const totalWeight = nGradable + scored.length;
                  if (totalWeight === 0) return null;
                  const suggested = Math.round((qcmScore * nGradable + speakingAvg * scored.length) / totalWeight);
                  return (
                    <div style={{ fontSize:11, color:"#0891b2", marginBottom:10, background:"#ecfeff", border:"1px solid #a5f3fc", borderRadius:6, padding:"6px 10px" }}>
                      💡 Score suggéré : <strong>{suggested}%</strong> (QCM {qcmScore}% sur {nGradable} question{nGradable>1?"s":""} + speaking {Math.round(speakingAvg)}% sur {scored.length} notée{scored.length>1?"s":""}) - n'écrase pas le champ Score, à ajuster toi-même si tu es d'accord.
                    </div>
                  );
                })()}
                <div>
                  <label style={labelSt}>Observations de correction</label>
                  <textarea style={{ ...inputSt, height:60 }} value={testCorrection.notes} onChange={e=>setTestCorrection(c=>({...c,notes:e.target.value}))} placeholder="Ex : bonne compréhension, difficultés à l'expression écrite…" />
                </div>
              </div>
            )}

            {/* ── Détail des réponses par question ── */}
            {answers.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:8 }}>📋 Détail question par question</div>
                <div style={{ maxHeight:280, overflowY:"auto", border:"1px solid #e5e7eb", borderRadius:10 }}>
                  {answers.map((a, idx)=>{
                    const isAudio = a.user_answer === "[audio]";
                    const audioUrl = isAudio ? audioAnswers[a.question_id] : null;
                    return (
                      <div key={idx} style={{ padding:"10px 14px", borderBottom:"1px solid #f1f5f9", background: a.is_correct ? "#f0fdf4" : "#fff8f8" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
                          <div style={{ flex:1 }}>
                            <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:4, flexWrap:"wrap" }}>
                              <span style={{ fontSize:10, fontWeight:700, color:"#6b7280" }}>Q{a.question_id}</span>
                              <span style={{ fontSize:10, padding:"1px 7px", borderRadius:99, background:"#f0f9ff", color:"#0369a1", fontWeight:600 }}>{CAT_ICONS[a.category]||""} {a.category}</span>
                              <span style={{ fontSize:10, padding:"1px 7px", borderRadius:99, background:"#f8fafc", color:CEFR_COLORS[a.cefr]||"#6b7280", fontWeight:700, border:`1px solid ${CEFR_COLORS[a.cefr]||"#e5e7eb"}` }}>{a.cefr}</span>
                            </div>
                            {isAudio && audioUrl ? (
                              <audio controls src={audioUrl} style={{ width:"100%", height:32, marginTop:4 }} />
                            ) : (
                              <div style={{ fontSize:12 }}>
                                <span style={{ color:"#6b7280" }}>Réponse : </span>
                                <span style={{ fontWeight:600, color: a.is_correct ? "#15803d" : "#dc2626" }}>{a.user_answer || "-"}</span>
                                {!a.is_correct && a.correct_answer && (
                                  <span style={{ color:"#6b7280", marginLeft:8 }}>→ <span style={{ fontWeight:600, color:"#15803d" }}>{a.correct_answer}</span></span>
                                )}
                              </div>
                            )}
                          </div>
                          <span style={{ fontSize:18, flexShrink:0 }}>{a.is_correct ? "✅" : "❌"}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Offre recommandée ── */}
            <div style={{ padding:12, borderRadius:9, background:"#fef9ee", border:"1px solid #fde68a", marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:700, color:"#92400e", marginBottom:6 }}>💡 Offre conseillée</div>
              <div style={{ fontSize:13, fontWeight:700, color:"#374151" }}>📚 {t.offreRecommandee||"à déterminer"}</div>
            </div>

            {t.notes && <div style={{ padding:"10px 14px", borderRadius:8, background:"#f8fafc", border:"1px solid #e5e7eb", fontSize:12, color:"#374151", marginBottom:14 }}>📝 {t.notes}</div>}

            <div style={{ display:"flex", gap:10 }}>
              {t.correction_statut === "en_attente" ? (
                <button onClick={handleValiderCorrectionDepuisDetail} disabled={savingCorrectionDetail || !testCorrection.niveau}
                  style={{ ...btnPrimary, background:"#d97706", opacity: savingCorrectionDetail ? .7 : 1, cursor: savingCorrectionDetail ? "not-allowed" : "pointer" }}>
                  {savingCorrectionDetail ? "⏳ Enregistrement…" : "✅ Valider la correction"}
                </button>
              ) : (
                <button onClick={() => { setEditingItem(t); setShowTestModal(true); setShowTestDetailModal(false); }} style={btnSecondary}>✏️ Modifier</button>
              )}
              <button onClick={() => setShowTestDetailModal(false)} style={btnSecondary}>Fermer</button>
            </div>
          </Modal>
        );
      })()}

      {/* ══ MODAL DÉTAIL INSCRIPTION ══ */}
      {showInscDetailModal && selectedInscription && (() => {
        const exporterDossierPDF = () => {
          try {
            const num = `BET-DOS-${String(selectedInscription.id||"").slice(0,8).toUpperCase()}`;
            const now = new Date().toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" });
            const row = (lbl, val) => val==null || val==="" ? "" : `<tr><td class="lbl">${lbl}</td><td class="val">${val}</td></tr>`;
            const section = (icon, title, rows) => {
              const body = rows.filter(Boolean).join("");
              if (!body) return "";
              return `<section><div class="section-title">${icon} ${title}</div><table>${body}</table></section>`;
            };
            const w = window.open("","_blank","width=760,height=920");
            w.document.write(`<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Dossier inscription - ${selectedInscription.client}</title>
            <style>
              *{box-sizing:border-box}
              body{font-family:Arial,sans-serif;margin:0;padding:40px;color:#1e293b;background:#fff}
              .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #0891b2;padding-bottom:20px;margin-bottom:28px}
              .logo{font-size:28px;font-weight:900;color:#0891b2;letter-spacing:-1pxdisplay:flex;align-items:center;gap:11px}.logo::before{content:'';width:40px;height:40px;flex-shrink:0;background:#fff url('https://res.cloudinary.com/nvp7nxqp/image/upload/v1788473005/bet/branding/bet-logo.png') center / 28px 28px no-repeat;border-radius:9px;box-shadow:0 2px 7px rgba(0,0,0,.14)}
              .logo span{color:#0f172a}
              .logo-sub{font-size:11px;color:#6b7280;margin-top:4px;margin-left:51px}
              h2{margin:0 0 4px;font-size:20px;font-weight:800;color:#0f172a}
              .num{font-size:11px;color:#6b7280;margin-top:2px}
              section{margin-bottom:20px}
              .section-title{font-size:11px;font-weight:800;color:#0891b2;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;border-bottom:1px solid #e0f2fe;padding-bottom:4px}
              table{width:100%;border-collapse:collapse}
              td{padding:9px 12px;border-bottom:1px solid #f1f5f9;font-size:13px;vertical-align:top}
              .lbl{color:#6b7280;font-weight:500;width:42%}
              .val{font-weight:600;color:#0f172a}
              .footer{margin-top:36px;padding-top:14px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af;text-align:center;line-height:1.7}
              @media print{body{padding:20px}*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}}
            </style></head><body>
            <div class="header">
              <div>
                <div class="logo">BET<span>Languages</span></div>
                <div class="logo-sub">Centre de langues - Abidjan, Côte d'Ivoire</div>
                <div class="logo-sub" style="margin-top:2px">📧 contact@binniesenglish.com · 📞 +225 27 22 40 71 99</div>
              </div>
              <div style="text-align:right">
                <h2>DOSSIER INSCRIPTION</h2>
                <div class="num">N° ${num}</div>
                <div class="num">Émis le ${now}</div>
              </div>
            </div>

            ${section("👤","Coordonnées client", [
              row("Client/Entreprise", selectedInscription.client),
              row("Contact", selectedInscription.contact),
              row("Téléphone", selectedInscription.telephone),
              row("Email", selectedInscription.email),
            ])}

            ${section("🎓","Formation", [
              row("Offre", selectedInscription.offre),
              row("Niveau testé", selectedInscription.niveauTest),
              row("Date début", selectedInscription.dateDebut ? formatDate(selectedInscription.dateDebut) : null),
              row("Montant", formatMoney(selectedInscription.montant)),
              row("Statut", selectedInscription.assigne ? "Assigné" : selectedInscription.statut === "confirmée" ? "Confirmée" : "En attente"),
            ])}

            ${selectedInscription.notesAdmin ? section("📝","Notes", [row("Notes", selectedInscription.notesAdmin)]) : ""}

            <div class="footer">
              Ce dossier est généré automatiquement par le système BET Languages. Conservez ce document pour vos archives.<br/>
              BET Languages · Centre de langues agréé · Abidjan, Côte d'Ivoire<br/>
              contact@binniesenglish.com · +225 27 22 40 71 99
            </div>
            <script>window.onload=()=>window.print();</script>
            </body></html>`);
            w.document.close();
            toast.success("Dossier exporté en PDF");
          } catch (e) { toast.error("Erreur lors de l'export PDF"); }
        };
        return (
        <Modal title={`📋 Dossier inscription - ${selectedInscription.client}`} onClose={() => { setShowInscDetailModal(false); setSelectedInscription(null); }} wide
          headerExtra={<button onClick={exporterDossierPDF} style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", background:"#0891b2", color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontSize:12, fontWeight:700, whiteSpace:"nowrap" }}>📄 Export PDF</button>}
        >
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:16 }}>
            <div style={{ padding:14, borderRadius:10, background:"#f0f9ff", border:"1px solid #bae6fd" }}>
              <div style={{ fontSize:11, fontWeight:700, color:"#0369a1", marginBottom:10 }}>👤 Coordonnées client</div>
              {[["Client/Entreprise",selectedInscription.client],["Contact",selectedInscription.contact||"-"],["Téléphone",selectedInscription.telephone||"-"],["Email",selectedInscription.email||"-"]].map(([l,v])=>(
                <div key={l} style={{ display:"flex", justifyContent:"space-between", marginBottom:6, fontSize:12 }}><span style={{ color:"#6b7280" }}>{l}</span><span style={{ fontWeight:600 }}>{v}</span></div>
              ))}
            </div>
            <div style={{ padding:14, borderRadius:10, background:"#f0fdf4", border:"1px solid #bbf7d0" }}>
              <div style={{ fontSize:11, fontWeight:700, color:"#15803d", marginBottom:10 }}>📚 Formation</div>
              {[["Offre",selectedInscription.offre],["Niveau testé",selectedInscription.niveauTest||"-"],["Date début",selectedInscription.dateDebut?formatDate(selectedInscription.dateDebut):"-"],["Montant",formatMoney(selectedInscription.montant)],["Statut",selectedInscription.assigne ? "🏫 Assigné" : selectedInscription.statut==="confirmée" ? "Confirmée" : "En attente"]].map(([l,v])=>(
                <div key={l} style={{ display:"flex", justifyContent:"space-between", marginBottom:6, fontSize:12 }}><span style={{ color:"#6b7280" }}>{l}</span><span style={{ fontWeight:600 }}>{v}</span></div>
              ))}
            </div>
          </div>
          {/* Documents */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:8 }}>📎 Documents du dossier</div>
            {selectedInscription.documents && selectedInscription.documents.length > 0 ? (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:8 }}>
                {selectedInscription.documents.map((doc,di)=>{
                  const typeIcons = { devis:"📄", bc:"🧾", id:"🪪", photo:"📷", fiche:"📋" };
                  const ic = typeIcons[doc.type]||"📎";
                  return (
                    <div key={di} style={{ padding:"10px 12px", borderRadius:8, background:"#f8fafc", border:"1px solid #e2e8f0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                        <span style={{ fontSize:20 }}>{ic}</span>
                        <span style={{ fontSize:11, fontWeight:600, color:"#374151" }}>{doc.nom}</span>
                      </div>
                      <button onClick={() => telechargerDocument(doc, selectedInscription.client)} style={{ padding:"3px 8px", background:"#0891b2", color:"#fff", border:"none", borderRadius:4, cursor:"pointer", fontSize:10 }}>⬇️</button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding:12, borderRadius:8, background:"#fef9ee", border:"1px dashed #fde68a", fontSize:12, color:"#92400e", textAlign:"center" }}>Aucun document joint - modifiez le dossier pour en ajouter</div>
            )}
          </div>
          {selectedInscription.notesAdmin && <div style={{ padding:"10px 14px", borderRadius:8, background:"#f8fafc", border:"1px solid #e5e7eb", fontSize:12, marginBottom:14 }}>📝 {selectedInscription.notesAdmin}</div>}
          <div style={{ display:"flex", gap:10 }}>
            {!selectedInscription.apprenantConverti && selectedInscription.statut==="confirmée" && (
              <button onClick={() => { convertirEnApprenant(selectedInscription); setShowInscDetailModal(false); }} style={{ ...btnPrimary, background:"#7c3aed" }}>🎓 Convertir → Apprenant BET</button>
            )}
            {selectedInscription.apprenantConverti && <span style={{ padding:"9px 14px", background:"#f3e8ff", color:"#7c3aed", borderRadius:6, fontSize:12, fontWeight:700 }}>✅ Déjà converti en Apprenant BET</span>}
            <button onClick={() => setShowInscDetailModal(false)} style={btnSecondary}>Fermer</button>
          </div>
        </Modal>
        );
      })()}

      {showPlanifModal && (
        <Modal title="Planifier l'envoi automatique de rapports" onClose={() => setShowPlanifModal(false)}>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div><label style={labelSt}>Fréquence</label>
              <select value={planifConfig.frequence} onChange={e=>setPlanifConfig({...planifConfig,frequence:e.target.value})} style={inputSt}>
                <option value="hebdomadaire">Hebdomadaire</option><option value="mensuel">Mensuel</option>
              </select>
            </div>
            <div><label style={labelSt}>Emails destinataires (séparés par virgules)</label>
              <input value={planifConfig.emails} onChange={e=>setPlanifConfig({...planifConfig,emails:e.target.value})} placeholder="manager@bet.com, direction@bet.ci" style={inputSt} />
            </div>
            <div><label style={labelSt}>Format</label>
              <select value={planifConfig.format} onChange={e=>setPlanifConfig({...planifConfig,format:e.target.value})} style={inputSt}>
                <option value="pdf">PDF</option><option value="excel">Excel (CSV)</option>
              </select>
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:10 }}>
              <button onClick={()=>setShowPlanifModal(false)} style={btnSecondary}>Annuler</button>
              <button onClick={handlePlanifierEnvoi} style={btnPrimary}>Enregistrer</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ══ PANNEAU CHAT PROSPECT (flottant) ══ */}
      {chatAssignation && (
        <ProspectChatPanel
          assignationId={chatAssignation.id}
          profil={profil}
          assignation={chatAssignation}
          onClose={() => setChatAssignation(null)}
        />
      )}

      {/* ══ MODAL ROUTING POST-FINALISATION ══ */}
      {showRoutingModal && routingTarget && (() => {
        const a = routingTarget;
        const isPrivate = a.type_coaching === "prive" || isOffreCoachingPrive(a.inscription_offre || "");
        const modeLabel = a.type_cours === "en_ligne" ? "💻 En ligne" : a.type_cours === "domicile" ? "🏠 À domicile" : `🏢 Présentiel${a.centre_nom ? " · " + a.centre_nom : ""}`;

        return (
          <div style={modalOverlay}>
            <div style={{ ...modalBox, width:520, maxHeight:"90vh", overflowY:"auto" }}>
              {/* Header */}
              <div style={{ background: isPrivate ? "linear-gradient(135deg,#7c3aed,#6d28d9)" : "linear-gradient(135deg,#0891b2,#0e7490)", borderRadius:"10px 10px 0 0", margin:"-24px -24px 20px", padding:"20px 24px" }}>
                <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.7)", textTransform:"uppercase", letterSpacing:".08em", marginBottom:4 }}>
                  {isPrivate ? "📚 Cours privé - Affectation PA Advisor" : "📥 Onboarding - Transmission automatique"}
                </div>
                <div style={{ fontSize:17, fontWeight:800, color:"#fff" }}>{a.prospect_nom}</div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.75)", marginTop:3 }}>{modeLabel} · {isPrivate ? "Coaching privé" : ["groupe","standard"].includes(a.type_coaching) ? "Coaching groupe" : "Formation"}{a.inscription_offre ? ` · ${a.inscription_offre}` : ""}</div>
              </div>

              {isPrivate ? (
                /* ── CAS 1 : Cours privé → choisir PA Advisor ── */
                <div>
                  <p style={{ fontSize:13, color:"#374151", marginBottom:16, lineHeight:1.6 }}>
                    Ce cours privé doit être géré par un <strong>PA Advisor</strong> qui affectera le coach approprié.
                    Sélectionnez le PA Advisor habilité :
                  </p>

                  {paAdvisors.length === 0 ? (
                    <div style={{ textAlign:"center", padding:"24px", background:"#fef9c3", borderRadius:10, color:"#854d0e", fontSize:12 }}>
                      ⚠️ Aucun PA Advisor actif trouvé. Vérifiez la configuration des comptes.
                    </div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:20 }}>
                      {paAdvisors.map(pa => (
                        <button key={pa.id} onClick={() => setSelectedPaId(pa.id)}
                          style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", borderRadius:10, border:`2px solid ${selectedPaId === pa.id ? "#7c3aed" : "#e2e8f0"}`, background:selectedPaId === pa.id ? "#f5f3ff" : "#fff", cursor:"pointer", textAlign:"left", transition:"all .15s" }}>
                          <div style={{ width:38, height:38, borderRadius:"50%", background: selectedPaId === pa.id ? "#7c3aed" : "#e0f2fe", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:14, color: selectedPaId === pa.id ? "#fff" : "#0891b2", flexShrink:0 }}>
                            {pa.prenom?.[0]}{pa.nom?.[0]}
                          </div>
                          <div>
                            <div style={{ fontSize:13, fontWeight:700, color: selectedPaId === pa.id ? "#7c3aed" : "#0f172a" }}>{pa.prenom} {pa.nom}</div>
                            <div style={{ fontSize:11, color:"#9ca3af" }}>{pa.email}</div>
                          </div>
                          {selectedPaId === pa.id && <span style={{ marginLeft:"auto", color:"#7c3aed", fontSize:18 }}>✓</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* ── CAS 2 : Cours non-privé → Onboarding automatique ── */
                <div>
                  <div style={{ background:"#f0fdf4", borderRadius:10, padding:"14px 16px", border:"1px solid #bbf7d0", marginBottom:20 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                      <span style={{ fontSize:20 }}>🎯</span>
                      <div>
                        <div style={{ fontSize:12, fontWeight:700, color:"#15803d" }}>Transmission automatique à l'Onboarding</div>
                        <div style={{ fontSize:13, fontWeight:800, color:"#0f172a" }}>{myOnboardingNom || "Votre assistante onboarding"}</div>
                      </div>
                    </div>
                    <p style={{ margin:0, fontSize:12, color:"#374151", lineHeight:1.6 }}>
                      L'apprenant sera immédiatement visible dans le tableau de bord de l'Onboarding pour :
                      affectation au groupe · suivi de démarrage · programmation des cours.
                    </p>
                  </div>
                </div>
              )}

              <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                <button onClick={handleConfirmRouting} disabled={routingLoading || (isPrivate && !selectedPaId)}
                  style={{ padding:"9px 20px", background: isPrivate ? "#7c3aed" : BET_COLOR, color:"#fff", border:"none", borderRadius:7, cursor:(routingLoading || (isPrivate && !selectedPaId)) ? "not-allowed" : "pointer", fontWeight:700, fontSize:12, opacity:(isPrivate && !selectedPaId) ? 0.5 : 1 }}>
                  {routingLoading ? "⏳ Transmission…" : isPrivate ? "✅ Affecter au PA Advisor" : "✅ Transmettre à l'Onboarding"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <FooterSaaS couleurTheme={BET_COLOR} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   FORMULAIRES CRUD
═══════════════════════════════════════════════════════ */
// const BET_COLOR_F = "#0891b2";
const OFFRES_TARIFS = [
  { label:"Anglais Pro B2",           prix: 150_000 },
  { label:"Anglais Pro B1",           prix: 120_000 },
  { label:"Anglais Essentiel A2",     prix:  90_000 },
  { label:"Business English",         prix: 200_000 },
  { label:"Certification TOEIC",      prix: 300_000 },
  { label:"Anglais Enfant",           prix:  80_000 },
  { label:"Formation Entreprise",     prix: 250_000 },
  { label:"Coaching de groupe",       prix:  60_000 },
  { label:"Coaching privé",           prix: 180_000 },
  { label:"Anglais Expert C2",        prix: 220_000 },
  { label:"Cours débutant / Alpha",   prix:  70_000 },
  { label:"Formation Personnalisée",  prix:       0 },
];
const OFFRE_LIST_F = OFFRES_TARIFS.map(o => o.label);


const DOC_TYPES = [
  { key:"devis",  label:"Devis signé",          icon:"📄" },
  { key:"bc",     label:"Bon de commande",       icon:"🧾" },
  { key:"id",     label:"Pièce d'identité",      icon:"🪪" },
  { key:"photo",  label:"Photo d'identité",      icon:"📷" },
  { key:"fiche",  label:"Fiche de renseignement",icon:"📋" },
  { key:"autre",  label:"Autre document",        icon:"📎" },
];

// Grille par défaut, utilisée tant que plateforme_config.coaching_prive_config n'a pas
// répondu - ne jamais utiliser directement ces valeurs pour un calcul de prix affiché,
// toujours passer par le state coachingPriveConfig (alimenté par ce fetch), sous peine de
// désynchronisation avec le prix réellement affiché au prospect (CourseDetail/ParcoursModal
// lisent tous les deux la même config live).
const GRILLE_PRIVE_COM_DEFAULT = {
  classique: { label:"Classique - 3 séances × 2h/sem", cabinet:150000, domicile:200000, en_ligne:100000 },
  intensive:  { label:"Intensive - 5 séances/sem",      cabinet:200000, domicile:300000, en_ligne:200000 },
};
const JOURS_COURTS_COM = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];
const InscriptionForm = ({ initialData, onSave, onCancel, finalisationMode, catalogueOffres = [], assistanteTypeCours = null, assistanteCentreId = null, assistanteCentreNom = null, commercialId = null, coachingPriveConfig = GRILLE_PRIVE_COM_DEFAULT, groupeConfig = {} }) => {
  const alerte = useAlerte();
  const isCentre  = assistanteTypeCours === "presentiel";
  const isEnLigne = assistanteTypeCours === "en_ligne";

  // Filtrer le catalogue selon le contexte : centre → présentiel, en ligne → en_ligne, les_deux → tout

  const centresList = React.useMemo(() => {
    try {
      const raw = localStorage.getItem("bet_centres_master");
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(c => c.actif !== false).map(c => ({ key: c.key, name: c.nom || c.name })) : [];
    } catch { return []; }
  }, []);
  const [form, setForm] = useState(initialData || {
    client:"", contact:"", telephone:"", email:"", offre:"", niveauTest:"",
    dateDebut:"", date:new Date().toISOString().slice(0,10), montant:0,
    statut:"confirmée", apprenantConverti:false, documents:[], notesAdmin:"",
    profil_type:"adulte", nom_parent:"", telephone_parent:"", email_parent:"",
    prenom_enfant:"", nom_enfant:"", age_enfant:"", niveau_classe_enfant:"", email_enfant:"",
    type_client:"particulier",
    objectif_client:"",
  });
  const [confirmPayload, setConfirmPayload] = useState(null); // modal confirmation avant envoi PA
  const [submitting, setSubmitting] = useState(false);

  /* ── CODE PROMO ───────────────────────────────────────────────────────────
     La mécanique existait déjà côté serveur (POST /api/parcours/assignation
     revalide le code, soustrait la réduction et incrémente le compteur d'usage),
     et le site public l'utilisait. Le formulaire de l'assistante, lui, n'avait
     aucun champ : la remise ne pouvait donc s'appliquer qu'à une inscription
     passée par le tunnel public, jamais à celle saisie au comptoir.

     Le code n'est retenu qu'une fois VÉRIFIÉ. Sans ce garde-fou, l'assistante
     annonce une remise au client, enregistre, et le serveur écarte
     silencieusement un code expiré ou hors quota - l'écart ne se découvre
     qu'au premier paiement. */
  /* ── MENSUALITÉ NÉGOCIÉE ─────────────────────────────────────────────────
     `montant` dit ce qu'on encaisse aujourd'hui ; il ne disait rien du tarif de
     la SUITE. L'échéancier étant créé plus tard - souvent par quelqu'un d'autre -
     son formulaire repartait du prix catalogue, et la remise accordée au comptoir
     disparaissait sans trace. Sur 72 dossiers dotés d'une mensualité, 4 seulement
     l'avaient égale au montant d'inscription : les deux valeurs sont bien
     distinctes, et il fallait donc un second champ plutôt qu'un raccourci. */
  /* Déclaré AVANT tout ce qui le lit : `estPriveForm`, `tarifCatalogue` et le recalage du
     pack s'évaluent pendant le rendu, et les placer au-dessus de ce useState faisait
     échouer le formulaire entier sur « Cannot access 'cpForm' before initialization ». */
  // ── Coaching privé ──
  const [cpForm, setCpForm] = useState({
    type_coaching: initialData?.cp_type_coaching || "classique",
    lieu:          initialData?.cp_lieu           || "cabinet",
    centre:        initialData?.cp_centre         || "",
    adresse:       initialData?.cp_adresse        || "",
    dispos:        initialData?.cp_dispos         || [],
    notes_pa:      initialData?.cp_notes_pa       || "",
    date_debut:    initialData?.cp_date_debut     || "",
    /* LES AUTRES PERSONNES DE LA FAMILLE — un couple ou une famille qui prend un coaching privé
       ensemble. Le client saisi en haut du formulaire reste LE PAYEUR : un seul dossier, un
       seul montant, un seul échéancier. Ces personnes-là sont des apprenants supplémentaires,
       pas des dossiers supplémentaires. */
    membres_famille: initialData?.cp_membres_famille  || [],
  });

  const estPriveForm = cpForm?._mode === "prive" || (!cpForm?._mode && isOffreCoachingPrive(form.offre));

  /* ── FORMULES DE GROUPE ───────────────────────────────────────────────────
     Une inscription en « Formation standard » se saisissait à un montant libre : rien ne
     rappelait à l'assistante les trois formules annoncées au prospect sur le site
     (3×/semaine, 2×/semaine, week-end), ni leurs tarifs. Elle pouvait donc enregistrer un
     montant qui ne correspondait à aucune offre réelle.
     Le lieu suit le canal de l'assistante : une commerciale en ligne vend au tarif en
     ligne, une commerciale de centre au tarif présentiel. */
  const formulesGroupeCom = React.useMemo(() => {
    if (estPriveForm) return [];
    /* Les formules appartiennent à L'OFFRE choisie, et son interrupteur commande :
       toutes les offres de groupe n'en ont pas. Sans offre sélectionnée, ou sans
       formules activées, la saisie du montant reste libre comme auparavant. */
    const o = matchOffreCatalogue(catalogueOffres || [], form.offre);
    if (o?.formules_actif !== true) return [];
    return (o.formules || [])
      .map(f => ({ id:f.id, label:f.label, frequence:f.frequence, tarif:Number(f.tarif) || 0 }))
      .filter(f => f.tarif > 0 && f.label);
  }, [estPriveForm, catalogueOffres, form.offre]);

  const formuleGroupeCom = formulesGroupeCom.find(f => f.id === form.formule_groupe) || null;

  /* Grille privée effective du formulaire : celle de l'offre choisie si elle en porte,
     sinon la grille globale. Elle alimente le sélecteur de formule du bloc coaching privé. */
  const grillePriveInsc = React.useMemo(
    () => grillePriveDeLOffre(catalogueOffres, form.offre, coachingPriveConfig),
    [catalogueOffres, form.offre, coachingPriveConfig]
  );

  /* Le pack retenu doit exister dans cette grille. Une offre qui définit ses propres
     formules n'a pas de clé « classique » : sans ce recalage, le sélecteur afficherait la
     première option tout en gardant « classique » en mémoire, et le tarif tomberait à 0. */
  React.useEffect(() => {
    const cles = Object.keys(grillePriveInsc || {});
    if (cles.length && !cles.includes(cpForm.type_coaching)) {
      setCpForm(prev => ({ ...prev, type_coaching: cles[0] }));
    }
  }, [grillePriveInsc]); // eslint-disable-line
  /* La référence n'est pas la même des deux côtés : en formation standard c'est le prix du
     catalogue, en coaching privé c'est le tarif de la formule (déjà mensuel par nature). */
  const tarifCatalogue = React.useMemo(() => {
    if (estPriveForm) {
      const lieuKey = isEnLigne ? "en_ligne" : (cpForm.lieu === "domicile" ? "domicile" : "cabinet");
      return coachingPriveConfig?.[cpForm.type_coaching]?.[lieuKey] || null;
    }
    return matchOffreCatalogue(catalogueOffres || [], form.offre)?.prix_num || null;
  }, [estPriveForm, isEnLigne, cpForm?.lieu, cpForm?.type_coaching, coachingPriveConfig, catalogueOffres, form.offre]);

  /* Pré-remplissage du tarif de référence, tant que personne n'y a touché.
     Le drapeau est indispensable : en coaching privé, changer de formule ou de lieu
     recalcule le tarif, et sans lui la mensualité négociée serait écrasée à chaque
     clic - la négociation disparaîtrait sous les doigts de l'assistante. */
  const [mensTouchee, setMensTouchee] = React.useState(false);
  /* LE MONTANT NÉGOCIÉ NE DOIT PAS ÊTRE ÉCRASÉ PAR LA GRILLE.
     Le tarif d'un coaching privé se recalcule automatiquement à chaque changement de
     formule ou de lieu. C'est utile tant que personne n'a rien saisi - mais sur un
     coaching en famille, le montant EST négocié : la commerciale l'entre, puis corrige le
     lieu, et son prix disparaît sans le moindre message.
     On initialise à `true` quand on ouvre un dossier qui a déjà un montant : rouvrir une
     inscription pour changer un détail ne doit pas rétablir le tarif catalogue à son insu.
     Le bouton « Tarif public » permet de revenir en arrière volontairement. */
  const [montantTouche, setMontantTouche] = React.useState(!!initialData?.montant);
  React.useEffect(() => {
    if (mensTouchee || !tarifCatalogue) return;
    setForm(f => (Number(f.mensualite_negociee) === tarifCatalogue ? f : { ...f, mensualite_negociee: tarifCatalogue }));
  }, [tarifCatalogue, mensTouchee]); // eslint-disable-line react-hooks/exhaustive-deps
  const ecartTarif = React.useMemo(() => {
    const m = Number(form.mensualite_negociee) || 0;
    if (!m || !tarifCatalogue) return null;
    const diff = m - tarifCatalogue;
    if (diff === 0) return null;
    return { diff, pct: Math.round(Math.abs(diff) / tarifCatalogue * 100) };
  }, [form.mensualite_negociee, tarifCatalogue]);

  const [promoStatut, setPromoStatut] = useState(null);   // null | "verif" | "ok" | "ko"
  const [promoInfo,   setPromoInfo]   = useState(null);   // { code, type_reduction, valeur, description }
  const [promoErreur, setPromoErreur] = useState("");

  const verifierPromo = async () => {
    const code = (form.code_promo || "").trim();
    if (!code) return;
    setPromoStatut("verif"); setPromoErreur(""); setPromoInfo(null);
    try {
      // La catégorie du catalogue, pas `type_cours` : un code restreint aux centres
      // porte « centres », quand le dossier porte « presentiel ». Le serveur fait la
      // même correspondance à l'enregistrement.
      const categorie = assistanteTypeCours === "presentiel" ? "centres" : assistanteTypeCours || undefined;
      const r = await fetch(`${API_URL}/api/codes-promo/valider`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ code, offre_type: categorie, centre_id: assistanteCentreId || undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.valide) {
        setPromoStatut("ko");
        setPromoErreur(d.error || "Code invalide");
        setForm(f => ({ ...f, code_promo_valide: false }));
        return;
      }
      setPromoInfo(d);
      setPromoStatut("ok");
      setForm(f => ({ ...f, code_promo: d.code, code_promo_valide: true }));
    } catch {
      setPromoStatut("ko");
      setPromoErreur("Vérification impossible - réseau");
      setForm(f => ({ ...f, code_promo_valide: false }));
    }
  };

  /* La remise, telle que le serveur la calculera. On l'AFFICHE sans toucher au
     montant saisi : écraser le champ ferait passer une réduction automatique pour
     une saisie de l'assistante, et le serveur recalculerait par-dessus. */
  const remisePromo = React.useMemo(() => {
    if (promoStatut !== "ok" || !promoInfo) return null;
    const base = Number(form.montant) || 0;
    if (!base) return null;
    const reduction = promoInfo.type_reduction === "pourcentage"
      ? Math.round(base * (Number(promoInfo.valeur) / 100))
      : Math.min(Number(promoInfo.valeur), base);
    return { base, reduction, final: Math.max(0, base - reduction) };
  }, [promoStatut, promoInfo, form.montant]);

  const [upType,     setUpType]     = useState("");
  const [upNom,      setUpNom]      = useState("");
  const [uploading,  setUploading]  = useState(false);
  const [uploadPct,  setUploadPct]  = useState(0);
  const fileInputRef = React.useRef(null);

  const offresFiltrees = React.useMemo(() => {
    if (!catalogueOffres.length) return [];

    // 1. Le canal : une commerciale de centre ne vend pas de cours en ligne, et inversement.
    let liste = catalogueOffres;
    if (isCentre)       liste = catalogueOffres.filter(o => o.categorie === "presentiel" || o.categorie === "certification");
    else if (isEnLigne) liste = catalogueOffres.filter(o => o.categorie === "en_ligne"   || o.categorie === "certification");

    // 2. Le TYPE DE DEMANDE - ce filtre manquait. « Formation standard » proposait quand
    //    même les coachings privés, alors que le bloc « Dossier Coaching Privé » ne s'ouvre
    //    que pour le mode privé : on pouvait donc enregistrer un coaching privé sans son
    //    dossier, ni tarif, ni contrat.
    const mode = cpForm?._mode || (isOffreCoachingPrive(form?.offre || "") ? "prive" : "standard");
    liste = mode === "prive"
      ? liste.filter(o => isOffreCoachingPrive(o.label))
      // Les certifications restent accessibles en standard : passer le TOEIC n'est pas
      // un coaching privé.
      : liste.filter(o => !isOffreCoachingPrive(o.label));

    return liste;
  }, [catalogueOffres, isCentre, isEnLigne, cpForm?._mode, form?.offre]);
  const cpToggleDispoJour = (jour) => setCpForm(p => {
    if (p.dispos.some(d => d.jour === jour)) return { ...p, dispos: p.dispos.filter(d => d.jour !== jour) };
    const next = [...p.dispos, { jour, debut:"08:00", fin:"10:00" }];
    return { ...p, dispos: next.sort((a,b) => JOURS_COURTS_COM.indexOf(a.jour) - JOURS_COURTS_COM.indexOf(b.jour)) };
  });
  const cpSetDispoDebut = (jour, debut) => setCpForm(p => ({
    ...p, dispos: p.dispos.map(d => d.jour === jour ? {...d, debut} : d)
  }));
  const cpSetDispoFin = (jour, fin) => setCpForm(p => ({
    ...p, dispos: p.dispos.map(d => d.jour === jour ? {...d, fin} : d)
  }));

  // Coaching privé : source de vérité UNIQUE pour le montant - auparavant, le prix
  // statique du catalogue (attaché au libellé "Offre choisie", ex: "150 000 FCFA")
  // et le calcul dynamique Type de coaching × Lieu pouvaient désynchroniser le champ
  // Montant sans que le libellé de l'offre choisie ne se mette à jour, donnant
  // l'impression que deux prix différents coexistaient pour la même offre.
  React.useEffect(() => {
    const isPriveActive = cpForm._mode === "prive" || (!cpForm._mode && isOffreCoachingPrive(form.offre));
    if (!isPriveActive) return;
    const lieuKey = isEnLigne ? "en_ligne" : (cpForm.lieu === "domicile" ? "domicile" : "cabinet");
    if (montantTouche) return;   // un montant négocié prime sur la grille
    const tarif = grillePriveInsc[cpForm.type_coaching]?.[lieuKey];
    if (tarif) setForm(f => (f.montant === tarif ? f : { ...f, montant: tarif }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpForm.type_coaching, cpForm.lieu, cpForm._mode, form.offre, isEnLigne, montantTouche]);

  const formatSize = (b) => {
    if (!b) return "";
    if (b < 1024) return `${b} o`;
    if (b < 1024*1024) return `${(b/1024).toFixed(0)} Ko`;
    return `${(b/(1024*1024)).toFixed(1)} Mo`;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!upType) { toast.error("Choisissez d'abord le type de document"); e.target.value = ""; return; }
    if (file.size > maxMoPour("dossier", file.type) * 1024 * 1024) { toast.error(`Fichier trop volumineux (max ${maxMoPour("dossier", file.type)} Mo)`); e.target.value = ""; return; }
    setUploading(true); setUploadPct(10);
    try {
      const def = DOC_TYPES.find(d => d.key === upType);
      const nom = upNom.trim() || def?.label || upType;
      const fd  = new FormData();
      fd.append("file", file);
      setUploadPct(40);
      const res = await fetchUpload(`${API_URL}/api/upload/dossier`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        body: fd,
      });
      const rawText = await res.text();
      if (!res.ok) {
        let msg = `Erreur ${res.status}`;
        try { msg = JSON.parse(rawText).error || msg; } catch { msg = rawText.slice(0,200); }
        throw new Error(msg);
      }
      const { file: uploaded } = JSON.parse(rawText);
      setUploadPct(90);
      setForm(f => ({ ...f, documents: [...f.documents, {
        nom, type: upType, url: uploaded.url, public_id: uploaded.public_id,
        taille: uploaded.size || file.size, mimetype: file.type,
      }]}));
      setUpType(""); setUpNom("");
      toast.success(`${def?.icon || "📎"} ${nom} ajouté`);
    } catch (err) {
      toast.error("Upload échoué : " + (err.message || "réessayez"));
    } finally {
      setUploading(false); setUploadPct(0);
      e.target.value = "";
    }
  };

  const removeDoc = (i) => {
    const doc = form.documents[i];
    if (doc.public_id) {
      const rt = doc.mimetype?.startsWith("image/") ? "image" : "raw";
      fetch(`${API_URL}/api/upload/delete?public_id=${encodeURIComponent(doc.public_id)}&resource_type=${rt}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
      }).catch(() => {});
    }
    setForm(f => ({ ...f, documents: f.documents.filter((_,j) => j !== i) }));
  };

  const rdOnly = { ...inputSt, background:"#f8fafc", color:"#64748b", cursor:"not-allowed" };

  return (
    <div>
      {/* Coordonnées - masquées pour un profil enfant : ses infos sont demandées dans
          "Informations de l'enfant" et le contact se fait via "Informations du parent /
          tuteur" ci-dessous, qui couvrent déjà tout - ce bloc doublait exactement les mêmes
          champs (nom, téléphone, email) et forçait à les saisir deux fois. */}
      {form.profil_type !== "enfant" && (
        <>
        <div style={{ fontSize:12, fontWeight:700, color:"#0369a1", marginBottom:10, padding:"8px 12px", background:"#f0f9ff", borderRadius:8, display:"flex", alignItems:"center", gap:8 }}>
          👤 Coordonnées client {finalisationMode && <span style={{ fontSize:10, color:"#94a3b8", fontWeight:400 }}>· pré-remplies depuis le profil prospect</span>}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:4 }}>
          <div>
            <label style={labelSt}>{form.type_client === "entreprise" ? "Entreprise *" : "Nom complet *"}</label>
            <div style={{ display:"flex", gap:6 }}>
              {/* LE CHOIX « ENTREPRISE » A ÉTÉ RETIRÉ de ce formulaire : les inscriptions
                  entreprise suivent le circuit corporate, pas celui-ci. On ne supprime pas
                  pour autant le champ `type_client` ni les blocs qui en dépendent - un
                  dossier entreprise déjà enregistré doit rester lisible et modifiable ici,
                  avec son libellé et son contact. Le sélecteur ne réapparaît donc que sur
                  un dossier qui l'est déjà, et sans proposer de le devenir. */}
              {form.type_client === "entreprise" && (
                <span style={{ ...inputSt, width:"auto", flexShrink:0, display:"flex", alignItems:"center",
                  fontWeight:700, color:"#0369a1", background:"#f0f9ff" }}>
                  🏢 Entreprise
                </span>
              )}
              <input style={{ ...(finalisationMode?rdOnly:inputSt), flex:1 }} value={form.client} onChange={e=>setForm({...form,client:e.target.value})} readOnly={finalisationMode} required placeholder={form.type_client === "entreprise" ? "Nom de l'entreprise" : "Prénom Nom"} />
            </div>
          </div>
          {form.type_client === "entreprise" && <div><label style={labelSt}>Nom du contact / Responsable</label><input style={finalisationMode?rdOnly:inputSt} value={form.contact} onChange={e=>setForm({...form,contact:e.target.value})} readOnly={finalisationMode} placeholder="Prénom Nom du responsable" /></div>}
          <div>
            <label style={labelSt}>Téléphone</label>
            <input style={finalisationMode?rdOnly:inputSt} value={form.telephone} onChange={e=>setForm({...form,telephone:e.target.value})} placeholder="+225 07 XX XX XX" readOnly={finalisationMode} />
          </div>
          <div>
            <label style={labelSt}>Email</label>
            <input type="email" style={finalisationMode?rdOnly:inputSt} value={form.email} onChange={e=>setForm({...form,email:e.target.value})} readOnly={finalisationMode} />
          </div>
        </div>
        </>
      )}

      {/* ── Profil apprenant ── */}
      <div style={{ margin:"14px 0 12px" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:8 }}>Profil de l'apprenant *</div>
        <div style={{ display:"flex", gap:8 }}>
          {PROFILS_LIST.map(p => {
            const active = (form.profil_type || "adulte") === p.id;
            return (
              <button key={p.id} type="button"
                onClick={() => setForm(f => ({ ...f, profil_type: p.id }))}
                style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"10px 12px", borderRadius:10, border:`2px solid ${active ? p.color+"55" : "#e2e8f0"}`, background: active ? p.bg : "#f8fafc", cursor:"pointer", transition:"all .15s" }}>
                <span style={{ fontSize:18 }}>{p.emoji}</span>
                <span style={{ fontSize:12, fontWeight:800, color: active ? p.color : "#374151" }}>{p.label}</span>
                {active && <div style={{ marginLeft:"auto", width:14, height:14, borderRadius:"50%", background:p.color, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><svg width="8" height="6" viewBox="0 0 9 7" fill="none"><path d="M1 3.5l2.5 2.5 4.5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Champs enfant - visibles seulement si profil_type === "enfant" */}
      {form.profil_type === "enfant" && (
        <div style={{ margin:"0 0 10px", borderRadius:10, border:"1.5px solid #bfdbfe", background:"#eff6ff", padding:"14px 16px" }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#1d4ed8", marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
            <span>👶</span> Informations de l'enfant
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div>
              <label style={labelSt}>Prénom de l'enfant *</label>
              <input style={{ ...inputSt, border:"1.5px solid #93c5fd" }}
                placeholder="Ex : Kouamé"
                value={form.prenom_enfant}
                onChange={e => setForm(f => ({ ...f, prenom_enfant: e.target.value }))} />
            </div>
            <div>
              <label style={labelSt}>Nom de l'enfant *</label>
              <input style={{ ...inputSt, border:"1.5px solid #93c5fd" }}
                placeholder="Ex : Traoré"
                value={form.nom_enfant}
                onChange={e => setForm(f => ({ ...f, nom_enfant: e.target.value }))} />
            </div>
            <div>
              <label style={labelSt}>Âge</label>
              <input style={{ ...inputSt, border:"1.5px solid #93c5fd" }}
                placeholder="Ex : 10 ans"
                value={form.age_enfant}
                onChange={e => setForm(f => ({ ...f, age_enfant: e.target.value }))} />
            </div>
            <div>
              <label style={labelSt}>Niveau de classe</label>
              <select style={{ ...inputSt, border:"1.5px solid #93c5fd" }}
                value={form.niveau_classe_enfant}
                onChange={e => setForm(f => ({ ...f, niveau_classe_enfant: e.target.value }))}>
                <option value="">- Choisir -</option>
                {["CP","CE1","CE2","CM1","CM2","6ème","5ème","4ème","3ème","2nde","1ère","Terminale"].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            <div style={{ gridColumn:"1/-1" }}>
              <label style={labelSt}>
                Email de l'enfant <span style={{ color:"#94a3b8", fontWeight:400 }}>(optionnel)</span>
              </label>
              <input style={{ ...inputSt, border:"1.5px solid #93c5fd" }}
                type="email" placeholder="enfant@email.com - laisser vide pour un identifiant généré"
                value={form.email_enfant || ""}
                onChange={e => setForm(f => ({ ...f, email_enfant: e.target.value }))} />
              {/* Certains parents créent une vraie adresse pour leur enfant ; sans ce champ
                  elle était perdue, et un identifiant technique était généré à la place -
                  que l'enfant ne reconnaissait pas au moment de se connecter. */}
              <div style={{ fontSize:11, color:"#64748b", marginTop:4 }}>
                Sert d'identifiant de connexion. Laissé vide, un identifiant est généré
                automatiquement. L'e-mail du parent n'est jamais réutilisé pour l'enfant.
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Champs parent - visibles seulement si profil_type === "enfant" */}
      {form.profil_type === "enfant" && (
        <div style={{ margin:"0 0 14px", borderRadius:10, border:"1.5px solid #fde68a", background:"#fffbeb", padding:"14px 16px" }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#92400e", marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
            <span>👨‍👩‍👧</span> Informations du parent / tuteur
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div style={{ gridColumn:"1/-1" }}>
              <label style={labelSt}>Nom complet du parent *</label>
              <input style={{ ...inputSt, border:"1.5px solid #fbbf24" }}
                placeholder="Ex : Koné Marie-Claire"
                value={form.nom_parent}
                onChange={e => setForm(f => ({ ...f, nom_parent: e.target.value }))} />
            </div>
            <div>
              <label style={labelSt}>Téléphone du parent *</label>
              <input style={{ ...inputSt, border:"1.5px solid #fbbf24" }}
                placeholder="+225 07 XX XX XX"
                value={form.telephone_parent}
                onChange={e => setForm(f => ({ ...f, telephone_parent: e.target.value }))} />
            </div>
            <div>
              <label style={labelSt}>Email du parent</label>
              <input type="email" style={{ ...inputSt, border:"1.5px solid #fbbf24" }}
                placeholder="parent@email.com"
                value={form.email_parent}
                onChange={e => setForm(f => ({ ...f, email_parent: e.target.value }))} />
            </div>
          </div>
        </div>
      )}

      {/* ── Toggle type de demande ── */}
      {!finalisationMode && (
        <div style={{ margin:"14px 0 12px" }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:8 }}>Type de demande *</div>
          <div style={{ display:"flex", gap:8 }}>
            {[
              { val:"standard", icon:"📚", label:"Formation standard", desc: isCentre ? "Cours en groupe dans nos locaux" : isEnLigne ? "Cours en groupe en ligne" : "Cours en groupe, en ligne ou présentiel", color:"#0369a1", bg:"#eff6ff", border:"#bfdbfe" },
              { val:"prive",    icon:"🎯", label:"Coaching Privé",      desc:"Cours particuliers · Classique ou Intensif",  color:"#6d28d9", bg:"#faf5ff", border:"#c4b5fd" },
            ].map(t => {
              const sel = (isOffreCoachingPrive(form.offre) ? "prive" : "standard") === t.val;
              // on utilise cpForm.modeType comme driver
              const active = cpForm._mode === t.val || (!cpForm._mode && t.val === "standard" && !isOffreCoachingPrive(form.offre)) || (!cpForm._mode && t.val === "prive" && isOffreCoachingPrive(form.offre));
              const isActive = cpForm._mode ? cpForm._mode === t.val : t.val === "standard";
              return (
                <button key={t.val} type="button"
                  onClick={() => {
                    setCpForm(p => ({ ...p, _mode: t.val }));
                    if (t.val === "prive" && !isOffreCoachingPrive(form.offre)) {
                      setForm(f => ({ ...f, offre: "Coaching privé", montant: coachingPriveConfig.classique.cabinet }));
                    }
                    if (t.val === "standard" && isOffreCoachingPrive(form.offre)) {
                      setForm(f => ({ ...f, offre: "" }));
                    }
                  }}
                  style={{ flex:1, display:"flex", alignItems:"flex-start", gap:10, padding:"12px 14px", borderRadius:10, border:`2px solid ${isActive ? t.border : "#e2e8f0"}`, background: isActive ? t.bg : "#f8fafc", cursor:"pointer", textAlign:"left", transition:"all .15s" }}>
                  <span style={{ fontSize:20, marginTop:1 }}>{t.icon}</span>
                  <div>
                    <div style={{ fontSize:12, fontWeight:800, color: isActive ? t.color : "#374151" }}>{t.label}</div>
                    <div style={{ fontSize:10, color: isActive ? t.color+"aa" : "#94a3b8", marginTop:1 }}>{t.desc}</div>
                  </div>
                  {isActive && <div style={{ marginLeft:"auto", width:16, height:16, borderRadius:"50%", background:t.color, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><svg width="8" height="6" viewBox="0 0 9 7" fill="none"><path d="M1 3.5l2.5 2.5 4.5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ fontSize:12, fontWeight:700, color:"#15803d", margin:"14px 0 10px", padding:"8px 12px", background:"#f0fdf4", borderRadius:8 }}>📚 Formation</div>

      {/* En mode finalisation : offre + montant issus du paiement → lecture seule */}
      {finalisationMode ? (
        <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:14 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div style={{ background:"#f8fafc", borderRadius:8, border:"1px solid #e2e8f0", padding:"10px 12px" }}>
              <div style={{ fontSize:9, fontWeight:700, color:"#94a3b8", marginBottom:4, textTransform:"uppercase", letterSpacing:".06em" }}>Formation</div>
              <div style={{ fontSize:13, fontWeight:800, color:"#0f172a" }}>{form.offre || <span style={{ color:"#94a3b8", fontStyle:"italic" }}>Non définie</span>}</div>
            </div>
            <div style={{ background:"#f0fdf4", borderRadius:8, border:"1px solid #bbf7d0", padding:"10px 12px" }}>
              <div style={{ fontSize:9, fontWeight:700, color:"#15803d", marginBottom:4, textTransform:"uppercase", letterSpacing:".06em" }}>Montant total</div>
              <div style={{ fontSize:13, fontWeight:800, color:"#15803d" }}>{Number(form.montant||0).toLocaleString("fr-FR")} FCFA</div>
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div><label style={labelSt}>Niveau CECRL</label>
              <select style={inputSt} value={form.niveauTest} onChange={e=>setForm({...form,niveauTest:e.target.value})}>
                <option value="">- Non évalué -</option>
                {["A1","A2","B1","B2","C1","C2"].map(n=><option key={n}>{n}</option>)}
              </select>
            </div>
            <div><label style={labelSt}>Date d'inscription</label><input type="date" style={inputSt} value={form.date} onChange={e=>setForm({...form,date:e.target.value})} /></div>
          </div>
          {!isOffreCoachingPrive(form.offre) && <div><label style={{ ...labelSt, color:"#0369a1", fontWeight:800 }}>Date de début formation *</label>
            <input type="date" style={{ ...inputSt, border:"2px solid #0891b2" }} value={form.dateDebut} onChange={e=>setForm({...form,dateDebut:e.target.value})} />
          </div>}
          {!form.offre && (
            <div><label style={{ ...labelSt, color:"#dc2626" }}>⚠ Offre choisie * (non saisie lors du paiement)</label>
              <select style={{ ...inputSt, borderColor:"#fca5a5" }} value={form.offre} onChange={e=>setForm({...form,offre:e.target.value})}>
                <option value="">– Choisir –</option>
                {offresFiltrees.length > 0
                  ? Object.entries(offresFiltrees.reduce((acc,o) => { (acc[o.categorie]=[...(acc[o.categorie]||[]),o]); return acc; }, {}))
                      .map(([cat, items]) => (
                        <optgroup key={cat} label={CAT_LABELS[cat] || cat}>
                          {items.map(o => <option key={o.id} value={o.label}>{o.icon} {o.label}{o.prix_num>0?` · ${o.prix_num.toLocaleString("fr-FR")} FCFA`:""}</option>)}
                        </optgroup>
                      ))
                  : OFFRE_LIST_F.map(o=><option key={o}>{o}</option>)
                }
              </select>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:4 }}>
          <div><label style={labelSt}>Offre choisie *</label>
            <select style={inputSt} value={form.offre} onChange={e=>{
              const sel = offresFiltrees.find(o=>o.label===e.target.value);
              setForm({...form, offre:e.target.value, montant: sel?.prix_num || form.montant});
            }}>
              <option value="">– Choisir –</option>
              {offresFiltrees.length > 0
                ? Object.entries(offresFiltrees.reduce((acc,o)=>{ (acc[o.categorie]=[...(acc[o.categorie]||[]),o]); return acc; }, {}))
                    .map(([cat, items]) => (
                      <optgroup key={cat} label={CAT_LABELS?.[cat] || cat}>
                        {items.map(o => <option key={o.id} value={o.label}>{o.icon} {o.label}{o.prix_num>0?` · ${o.prix_num.toLocaleString("fr-FR")} FCFA`:""}</option>)}
                      </optgroup>
                    ))
                : OFFRE_LIST_F.map(o=><option key={o}>{o}</option>)
              }
            </select>
          </div>
          <div><label style={labelSt}>Niveau CECRL</label>
            <select style={inputSt} value={form.niveauTest} onChange={e=>setForm({...form,niveauTest:e.target.value})}>
              <option value="">- Non évalué -</option>
              {["A1","A2","B1","B2","C1","C2"].map(n=><option key={n}>{n}</option>)}
            </select>
          </div>
          <div><label style={labelSt}>Date d'inscription</label><input type="date" style={inputSt} value={form.date} onChange={e=>setForm({...form,date:e.target.value})} /></div>
          {!(cpForm._mode === "prive" || (!cpForm._mode && isOffreCoachingPrive(form.offre))) && <div><label style={labelSt}>Date de début formation *</label><input type="date" style={inputSt} value={form.dateDebut} onChange={e=>setForm({...form,dateDebut:e.target.value})} /></div>}
          <div>
            <label style={labelSt}>Montant (FCFA)</label>
            <div style={{ display:"flex", gap:6 }}>
              <input type="number" style={{ ...inputSt, flex:1 }} value={form.montant}
                onChange={e => { setMontantTouche(true); setForm({ ...form, montant: Number(e.target.value) }); }} />
              {montantTouche && estPriveForm && (
                <button type="button"
                  onClick={() => {
                    setMontantTouche(false);
                    const lieuKey = isEnLigne ? "en_ligne" : (cpForm.lieu === "domicile" ? "domicile" : "cabinet");
                    const tarif = grillePriveInsc[cpForm.type_coaching]?.[lieuKey];
                    if (tarif) setForm(f => ({ ...f, montant: tarif }));
                  }}
                  title="Reprendre le tarif de la grille"
                  style={{ padding:"0 12px", borderRadius:8, border:"1px solid #cbd5e1", background:"#fff",
                    color:"#475569", fontSize:12, fontWeight:700, whiteSpace:"nowrap", cursor:"pointer" }}>
                  Tarif grille
                </button>
              )}
            </div>
            {montantTouche && estPriveForm && (
              <div style={{ fontSize:10, color:"#6d28d9", marginTop:3 }}>
                Montant négocié — il ne sera plus recalculé si vous changez de formule ou de lieu.
              </div>
            )}
          </div>

          {/* Code promo - vérifié avant d'être retenu, jamais appliqué sur parole. */}
          <div>
            <label style={labelSt}>Code promo</label>
            <div style={{ display:"flex", gap:6 }}>
              <input type="text" style={{ ...inputSt, textTransform:"uppercase", flex:1 }}
                placeholder="Ex : BETNOEL25"
                value={form.code_promo || ""}
                onChange={e => {
                  // Toute retouche invalide la vérification précédente : sans cela, on
                  // vérifie un code, on en tape un autre, et l'ancien reste « validé ».
                  setForm({ ...form, code_promo: e.target.value.toUpperCase(), code_promo_valide: false });
                  setPromoStatut(null); setPromoInfo(null); setPromoErreur("");
                }}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); verifierPromo(); } }} />
              <button type="button" onClick={verifierPromo}
                disabled={!((form.code_promo||"").trim()) || promoStatut === "verif"}
                style={{ padding:"0 14px", borderRadius:8, border:"none", fontSize:12.5, fontWeight:700, whiteSpace:"nowrap",
                  background: promoStatut === "ok" ? "#16a34a" : (form.code_promo||"").trim() ? "#0891b2" : "#e5e7eb",
                  color: (form.code_promo||"").trim() ? "#fff" : "#9ca3af",
                  cursor: (form.code_promo||"").trim() && promoStatut !== "verif" ? "pointer" : "not-allowed" }}>
                {promoStatut === "verif" ? "…" : promoStatut === "ok" ? "✓ Valide" : "Vérifier"}
              </button>
            </div>

            {promoStatut === "ko" && (
              <div style={{ fontSize:11.5, color:"#b91c1c", marginTop:4 }}>✕ {promoErreur}</div>
            )}

            {promoStatut === "ok" && !remisePromo && (
              <div style={{ fontSize:11.5, color:"#b45309", marginTop:4 }}>
                Code valide - saisissez un montant pour voir la remise.
              </div>
            )}

            {/* L'aperçu du calcul. Le montant saisi reste intact : c'est le serveur qui
                applique la remise, l'écraser ici la ferait passer pour une saisie. */}
            {remisePromo && (
              <div style={{ marginTop:6, padding:"8px 11px", borderRadius:8, background:"#f0fdf4", border:"1px solid #bbf7d0", fontSize:12, color:"#15803d", lineHeight:1.6 }}>
                🎟️ <strong>{promoInfo.code}</strong>
                {promoInfo.type_reduction === "pourcentage" ? ` · -${promoInfo.valeur} %` : ` · -${Number(promoInfo.valeur).toLocaleString("fr-FR")} F`}
                {promoInfo.description ? ` · ${promoInfo.description}` : ""}
                <div style={{ color:"#0f172a", marginTop:2 }}>
                  {remisePromo.base.toLocaleString("fr-FR")} F
                  {" - "}{remisePromo.reduction.toLocaleString("fr-FR")} F
                  {" = "}<strong>{remisePromo.final.toLocaleString("fr-FR")} F</strong>
                </div>
              </div>
            )}
          </div>

          {/* ── Formule de groupe ──
              Affichée pour la formation standard uniquement : le coaching privé a ses
              propres formules, plus bas dans le bloc violet. */}
          {!estPriveForm && formulesGroupeCom.length > 0 && (
            <div style={{ gridColumn:"1/-1" }}>
              <label style={labelSt}>
                Formule de groupe
                <span style={{ fontWeight:400, color:"#94a3b8" }}>
                  {" · "}{assistanteTypeCours === "en_ligne" ? "tarifs en ligne" : "tarifs présentiel"}
                </span>
              </label>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:8 }}>
                {formulesGroupeCom.map(f => {
                  const actif = form.formule_groupe === f.id;
                  return (
                    <button key={f.id} type="button"
                      onClick={() => {
                        /* Choisir une formule renseigne le montant ET la mensualité du
                           contrat : c'est le même tarif, et le saisir deux fois à la main
                           était l'occasion de les désaccorder. La mensualité reste
                           modifiable juste en dessous si le tarif a été négocié. */
                        setMensTouchee(false);
                        setForm(prev => ({
                          ...prev,
                          formule_groupe: actif ? "" : f.id,
                          // L'objet complet part avec le dossier : l'onboarding a besoin
                          // des jours et horaires pour affecter à une classe.
                          formule_groupe_detail: actif ? null : {
                            id: f.id, label: f.label, frequence: f.frequence, tarif: f.tarif,
                            canal: assistanteTypeCours === "en_ligne" ? "en_ligne" : "presentiel",
                          },
                          ...(actif ? {} : { montant: f.tarif, mensualite_negociee: f.tarif }),
                        }));
                      }}
                      style={{ textAlign:"left", cursor:"pointer", padding:"10px 12px", borderRadius:10,
                        border:`2px solid ${actif ? "#059669" : "#e2e8f0"}`,
                        background: actif ? "#f0fdf4" : "#fff",
                        boxShadow: actif ? "0 0 0 3px #05966920" : "none", transition:"all .18s", fontFamily:"inherit" }}>
                      <div style={{ fontWeight:800, fontSize:12.5, color: actif ? "#059669" : "#0f172a" }}>
                        {actif ? "✓ " : ""}{f.label}
                      </div>
                      {f.frequence && <div style={{ fontSize:10.5, color:"#94a3b8", marginTop:2, lineHeight:1.4 }}>{f.frequence}</div>}
                      <div style={{ fontWeight:800, fontSize:13, color: actif ? "#059669" : "#334155", marginTop:4 }}>
                        {f.tarif.toLocaleString("fr-FR")} F <span style={{ fontSize:10, fontWeight:600, color:"#94a3b8" }}>/ mois</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize:11, color:"#94a3b8", marginTop:5, lineHeight:1.5 }}>
                Facultatif. Renseigne le montant et la mensualité du contrat — tous deux
                restent modifiables ensuite.
              </div>
            </div>
          )}

          {/* Mensualité du contrat - distincte du montant encaissé aujourd'hui. */}
          <div>
            <label style={labelSt}>
              Mensualité du contrat (FCFA)
              {tarifCatalogue ? <span style={{ fontWeight:400, color:"#94a3b8" }}> · {estPriveForm ? "tarif de la formule" : "tarif catalogue"} : {tarifCatalogue.toLocaleString("fr-FR")} F</span> : null}
            </label>
            <div style={{ display:"flex", gap:6 }}>
              <input type="number" style={{ ...inputSt, flex:1 }}
                placeholder={tarifCatalogue ? String(tarifCatalogue) : "Laisser vide si non négocié"}
                value={form.mensualite_negociee ?? ""}
                onChange={e => { setMensTouchee(true); setForm({ ...form, mensualite_negociee: e.target.value === "" ? "" : Number(e.target.value) }); }} />
              {tarifCatalogue && Number(form.mensualite_negociee) !== tarifCatalogue && (
                <button type="button" onClick={()=>{ setMensTouchee(false); setForm({ ...form, mensualite_negociee: tarifCatalogue }); }}
                  title="Reprendre le tarif du catalogue"
                  style={{ padding:"0 12px", borderRadius:8, border:"1px solid #cbd5e1", background:"#fff", color:"#475569", fontSize:12, fontWeight:700, whiteSpace:"nowrap", cursor:"pointer" }}>
                  Tarif public
                </button>
              )}
            </div>
            {/* L'écart est DIT, pas seulement enregistré : une remise de 40 % saisie par
                inadvertance se remarque ici, pas trois mois plus tard. */}
            {ecartTarif && (
              <div style={{ marginTop:5, fontSize:11.5, fontWeight:600, color: ecartTarif.diff < 0 ? "#b45309" : "#15803d" }}>
                {ecartTarif.diff < 0
                  ? `↓ Remise de ${Math.abs(ecartTarif.diff).toLocaleString("fr-FR")} F (${ecartTarif.pct} %) sur le tarif catalogue`
                  : `↑ Majoration de ${ecartTarif.diff.toLocaleString("fr-FR")} F (${ecartTarif.pct} %) sur le tarif catalogue`}
              </div>
            )}
            <div style={{ fontSize:11, color:"#94a3b8", marginTop:4, lineHeight:1.5 }}>
              Ce tarif vaudra pour <strong>tout le contrat</strong>. Il sera proposé à la
              création de l'échéancier, avec votre nom. Laissé vide, le tarif catalogue s'applique.
            </div>
          </div>

          <div><label style={labelSt}>Statut</label>
            <select style={inputSt} value={form.statut} onChange={e=>setForm({...form,statut:e.target.value})}>
              <option value="confirmée">Confirmée</option><option value="en_attente">En attente</option>
            </select>
          </div>
        </div>
      )}

      {/* ── Section Coaching Privé ── */}
      {(cpForm._mode === "prive" || (!cpForm._mode && isOffreCoachingPrive(form.offre))) && (
        <div style={{ margin:"14px 0", borderRadius:12, border:"1.5px solid #7c3aed40", overflow:"hidden" }}>
          {/* Header */}
          <div style={{ background:BET_GRADIENT, padding:"10px 14px", display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:16 }}>🎯</span>
            <div>
              <div style={{ fontSize:12, fontWeight:800, color:"#fff" }}>Dossier Coaching Privé</div>
              <div style={{ fontSize:10, color:"#ddd6fe" }}>À compléter puis transmettre au PA depuis Prospects parcours</div>
            </div>
          </div>

          <div style={{ padding:"14px 16px", background:"#faf5ff", display:"flex", flexDirection:"column", gap:12 }}>

            {/* Type coaching + Lieu - coaching privé en ligne : aucun choix cabinet/domicile
                ne s'applique, l'assistante en ligne n'a jamais géré ces deux options. */}
            <div style={{ display:"grid", gridTemplateColumns: isEnLigne ? "1fr" : "1fr 1fr", gap:10 }}>
              <div>
                <label style={{ ...labelSt, color:"#6d28d9" }}>Type de coaching *</label>
                <select style={{ ...inputSt, border:"1.5px solid #c4b5fd" }}
                  value={cpForm.type_coaching}
                  onChange={e => {
                    const tc = e.target.value;
                    setCpForm(p => ({ ...p, type_coaching: tc }));
                    const lieuKey = isEnLigne ? "en_ligne" : (cpForm.lieu === "domicile" ? "domicile" : "cabinet");
                    const tarif = grillePriveInsc[tc]?.[lieuKey] || 0;
                    if (tarif) setForm(f => ({ ...f, montant: tarif, mensualite_negociee: tarif }));
                  }}>
                  {/* Autant de formules que l'offre en définit, pas seulement deux. */}
                  {Object.entries(grillePriveInsc).map(([cle, g]) => (
                    <option key={cle} value={cle}>{g.label}{g.frequence ? ` - ${g.frequence}` : ""}</option>
                  ))}
                </select>
                {cpForm.type_coaching && (
                  <div style={{ fontSize:10, color:"#7c3aed", marginTop:3, fontWeight:600 }}>
                    {(grillePriveInsc[cpForm.type_coaching]?.[isEnLigne ? "en_ligne" : (cpForm.lieu === "domicile" ? "domicile" : "cabinet")] || 0).toLocaleString("fr-FR")} FCFA/mois
                  </div>
                )}
              </div>
              {!isEnLigne && (
                <div>
                  <label style={{ ...labelSt, color:"#6d28d9" }}>Lieu des séances *</label>
                  <select style={{ ...inputSt, border:"1.5px solid #c4b5fd" }}
                    value={cpForm.lieu}
                    onChange={e => {
                      const lieu = e.target.value;
                      setCpForm(p => ({ ...p, lieu, centre: "", adresse: "" }));
                      const tarif = grillePriveInsc[cpForm.type_coaching]?.[lieu === "domicile" ? "domicile" : "cabinet"] || 0;
                      if (tarif) setForm(f => ({ ...f, montant: tarif }));
                    }}>
                    <option value="cabinet">Au cabinet BET</option>
                    <option value="domicile">À domicile</option>
                  </select>
                </div>
              )}
            </div>
            {isEnLigne && (
              <div style={{ background:"#ede9fe", borderRadius:8, padding:"9px 12px", display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:14 }}>💻</span>
                <span style={{ fontSize:12, fontWeight:700, color:"#4c1d95" }}>Séances en ligne (visio)</span>
              </div>
            )}

            {/* Centre BET */}
            {!isEnLigne && cpForm.lieu === "cabinet" && (
              isCentre && assistanteCentreNom ? (
                <div style={{ background:"#ede9fe", borderRadius:8, padding:"9px 12px", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:14 }}>📍</span>
                  <div>
                    <div style={{ fontSize:10, fontWeight:700, color:"#6d28d9", textTransform:"uppercase", letterSpacing:".06em" }}>Centre attitré</div>
                    <div style={{ fontSize:13, fontWeight:800, color:"#4c1d95" }}>{assistanteCentreNom}</div>
                  </div>
                </div>
              ) : (
                <div>
                  <label style={{ ...labelSt, color:"#6d28d9" }}>Centre BET *</label>
                  <select style={{ ...inputSt, border:"1.5px solid #c4b5fd" }}
                    value={cpForm.centre}
                    onChange={e => setCpForm(p => ({ ...p, centre: e.target.value }))}>
                    <option value="">- Sélectionner un centre -</option>
                    {(centresList.length > 0 ? centresList : [
                      { key:"angre",    name:"BET Angré"    },
                      { key:"koumassi", name:"BET Koumassi" },
                      { key:"bouake",   name:"BET Bouaké"   },
                      { key:"yopougon",  name:"BET Yopougon"  },
                    ]).map(c => <option key={c.key || c.id} value={c.key || c.id}>{c.name || c.nom}</option>)}
                  </select>
                </div>
              )
            )}

            {/* Adresse (si domicile) */}
            {cpForm.lieu === "domicile" && (
              <div>
                <label style={{ ...labelSt, color:"#6d28d9" }}>Quartier / Zone / Adresse *</label>
                <input style={{ ...inputSt, border:"1.5px solid #c4b5fd" }}
                  placeholder="Ex : Cocody Riviera 2, près du carrefour..."
                  value={cpForm.adresse}
                  onChange={e => setCpForm(p => ({ ...p, adresse: e.target.value }))} />
              </div>
            )}

            {/* Disponibilités */}
            <div>
              <label style={{ ...labelSt, color:"#6d28d9" }}>
                Disponibilités de l'apprenant
                {cpForm.dispos.length > 0 && <span style={{ marginLeft:6, fontSize:10, background:"#7c3aed", color:"#fff", borderRadius:99, padding:"1px 7px" }}>{cpForm.dispos.length}</span>}
              </label>
              {/* Chips jours */}
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:cpForm.dispos.length>0?8:0 }}>
                {JOURS_COURTS_COM.map(jour => {
                  const active = cpForm.dispos.some(d => d.jour === jour);
                  return (
                    <button key={jour} type="button" onClick={() => cpToggleDispoJour(jour)}
                      style={{ padding:"6px 14px", borderRadius:20, border:`2px solid ${active?"#7c3aed":"#e2e8f0"}`,
                        background:active?"#ede9fe":"#f8fafc", color:active?"#4c1d95":"#64748b",
                        fontWeight:700, fontSize:11, cursor:"pointer", transition:"all .15s" }}>
                      {jour}
                    </button>
                  );
                })}
              </div>
              {/* Lignes heure début */}
              {cpForm.dispos.length > 0 && (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {cpForm.dispos.map(row => (
                    <div key={row.jour} style={{ display:"flex", alignItems:"center", gap:8, background:"#f5f3ff", borderRadius:9, padding:"7px 11px", border:"1.5px solid #c4b5fd" }}>
                      <span style={{ fontWeight:800, fontSize:11, color:"#4c1d95", minWidth:32 }}>{row.jour}</span>
                      <span style={{ fontSize:10, color:"#9ca3af", flexShrink:0 }}>de</span>
                      <input type="time" value={row.debut}
                        onChange={e => cpSetDispoDebut(row.jour, e.target.value)}
                        style={{ padding:"4px 7px", borderRadius:7, border:"1px solid #c4b5fd", fontSize:11, outline:"none", cursor:"pointer", background:"#fff", color:"#0f172a" }} />
                      <span style={{ fontSize:10, color:"#9ca3af", flexShrink:0 }}>à</span>
                      <input type="time" value={row.fin || ""}
                        onChange={e => cpSetDispoFin(row.jour, e.target.value)}
                        style={{ padding:"4px 7px", borderRadius:7, border:"1px solid #c4b5fd", fontSize:11, outline:"none", cursor:"pointer", background:"#fff", color:"#0f172a" }} />
                      <button type="button" onClick={() => setCpForm(p=>({...p, dispos:p.dispos.filter(d=>d.jour!==row.jour)}))}
                        style={{ marginLeft:"auto", background:"none", border:"none", color:"#ef4444", cursor:"pointer", fontSize:13, lineHeight:1, flexShrink:0 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {cpForm.dispos.length === 0 && (
                <p style={{ fontSize:10, color:"#94a3b8", margin:0 }}>Cliquez sur un jour pour ajouter un créneau</p>
              )}
            </div>

            {/* Date de début */}
            <div>
              <label style={{ ...labelSt, color:"#6d28d9" }}>Date de début de la formation</label>
              <input type="date"
                style={{ ...inputSt, border:"1.5px solid #c4b5fd" }}
                value={cpForm.date_debut}
                onChange={e => setCpForm(p => ({ ...p, date_debut: e.target.value }))} />
            </div>

            {/* ── AUTRES PERSONNES DE LA FAMILLE ──────────────────────────────────────────
                Un couple ou une famille qui s'inscrit ensemble en coaching privé. Le client
                saisi en haut du formulaire reste LE PAYEUR ; les personnes ajoutées ici sont
                des apprenants supplémentaires, pas des dossiers supplémentaires.

                POURQUOI ICI, sous les disponibilités et la date : une famille partage ses
                créneaux - c'est une seule séance pour tout le monde, pas trois séances à
                caler. Et ce bloc ne s'affiche que pour le coaching privé, parce qu'une
                formation en groupe n'a pas de famille : chacun s'y inscrit pour soi.

                LE TARIF RESTE GLOBAL et ne se multiplie pas par le nombre de personnes :
                c'est un forfait négocié, saisi librement plus haut. */}
            <div style={{ border:"1.5px dashed #c4b5fd", borderRadius:10, padding:"12px 14px", background:"#faf9ff" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, marginBottom: cpForm.membres_famille.length ? 10 : 6 }}>
                <label style={{ ...labelSt, color:"#6d28d9", margin:0 }}>
                  👥 Autres personnes de la famille
                  {cpForm.membres_famille.length > 0 && (
                    <span style={{ marginLeft:8, fontSize:10, fontWeight:800, padding:"1px 8px", borderRadius:999, background:"#ede9fe", color:"#6d28d9" }}>
                      {cpForm.membres_famille.length}
                    </span>
                  )}
                </label>
                <button type="button"
                  onClick={() => setCpForm(p => ({ ...p, membres_famille: [...p.membres_famille, { prenom:"", nom:"", telephone:"", email:"", lien:"", niveau:"" }] }))}
                  style={{ padding:"5px 12px", borderRadius:8, border:"1.5px solid #c4b5fd", background:"#fff",
                    color:"#6d28d9", fontWeight:700, fontSize:11.5, cursor:"pointer", flexShrink:0 }}>
                  + Ajouter une personne
                </button>
              </div>

              {cpForm.membres_famille.length === 0 ? (
                <p style={{ fontSize:10.5, color:"#94a3b8", margin:0, lineHeight:1.5 }}>
                  Laissez vide pour un coaching individuel. Ajoutez une ligne par personne
                  supplémentaire si un couple ou une famille suit la formation ensemble —
                  le tarif saisi plus haut reste global pour la famille.
                </p>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                  {cpForm.membres_famille.map((m, i) => {
                    /* UNE FICHE PAR PERSONNE, pas une ligne. Ces personnes deviendront des
                       apprenants à part entière : elles auront un espace personnel, des
                       présences, des ressources. Il leur faut donc de quoi créer un accès -
                       un e-mail ou un téléphone - au même titre que le payeur.
                       Sans coordonnées, l'Onboarding ne peut pas leur ouvrir de compte et
                       devrait rappeler le client pour les réclamer. */
                    const majMembre = (cle, val) => setCpForm(p => ({ ...p,
                      membres_famille: p.membres_famille.map((x, j) => j === i ? { ...x, [cle]: val } : x) }));
                    const champ = (cle, ph, type = "text") => (
                      <input type={type} placeholder={ph} value={m[cle] || ""}
                        onChange={e => majMembre(cle, e.target.value)}
                        style={{ width:"100%", padding:"6px 9px", borderRadius:7,
                          border:"1px solid #c4b5fd", fontSize:11.5, outline:"none" }} />
                    );
                    return (
                      <div key={i} style={{ border:"1px solid #ddd6fe", borderRadius:9, padding:"9px 10px", background:"#fff" }}>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:7 }}>
                          <span style={{ fontSize:10, fontWeight:800, color:"#6d28d9", letterSpacing:".04em" }}>
                            PERSONNE {i + 1}
                          </span>
                          <button type="button"
                            onClick={() => setCpForm(p => ({ ...p, membres_famille: p.membres_famille.filter((_, j) => j !== i) }))}
                            title="Retirer cette personne"
                            style={{ background:"none", border:"none", color:"#ef4444", cursor:"pointer",
                              fontSize:14, lineHeight:1, padding:"0 2px" }}>×</button>
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6, marginBottom:6 }}>
                          {champ("prenom", "Prénom *")}
                          {champ("nom", "Nom")}
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6, marginBottom:6 }}>
                          {champ("telephone", "Téléphone", "tel")}
                          {champ("email", "Email", "email")}
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                          <select value={m.lien || ""} onChange={e => majMembre("lien", e.target.value)}
                            style={{ width:"100%", padding:"6px 8px", borderRadius:7, border:"1px solid #c4b5fd",
                              fontSize:11.5, outline:"none", background:"#fff", cursor:"pointer" }}>
                            <option value="">Lien avec le client</option>
                            {["Conjoint(e)","Enfant","Parent","Frère / Sœur","Autre"].map(l => <option key={l} value={l}>{l}</option>)}
                          </select>
                          <select value={m.niveau || ""} onChange={e => majMembre("niveau", e.target.value)}
                            style={{ width:"100%", padding:"6px 8px", borderRadius:7, border:"1px solid #c4b5fd",
                              fontSize:11.5, outline:"none", background:"#fff", cursor:"pointer" }}>
                            <option value="">Niveau CECRL</option>
                            {["A1","A2","B1","B2","C1","C2"].map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                  <p style={{ fontSize:10, color:"#94a3b8", margin:"2px 0 0", lineHeight:1.5 }}>
                    Le prénom suffit pour enregistrer une personne, mais un e-mail ou un téléphone
                    est nécessaire pour lui ouvrir un espace apprenant. La classe sera créée pour{" "}
                    {(() => {
                      const n = 1 + cpForm.membres_famille.filter(m => (m.prenom||"").trim() || (m.nom||"").trim()).length;
                      return <strong style={{ color:"#6d28d9" }}>{n} apprenant{n > 1 ? "s" : ""}</strong>;
                    })()}, et le coach pointera leurs présences séparément.
                  </p>
                </div>
              )}
            </div>

            {/* CHAMP « Notes pour le PA Advisor » RETIRÉ.
                Il demandait « objectifs, contraintes, préférences du prospect » - c'est-à-dire
                exactement ce que le champ « Objectif du client » recueille plus bas, de façon
                explicite et affichée jusque chez le coach. Deux cases pour la même information
                en laissent toujours une vide, et le PA ne sait plus laquelle lire.

                `cpForm.notes_pa` reste dans l'état du formulaire et continue d'alimenter
                `notes` à l'enregistrement : vidé de saisie, il vaut "" et part donc à null.
                Le supprimer demanderait de toucher au corps de la requête pour rien. */}

            {/* Tarif recap */}
            {cpForm.type_coaching && (() => {
              const lieuKey   = isEnLigne ? "en_ligne" : (cpForm.lieu === "domicile" ? "domicile" : "cabinet");
              const lieuLabel = isEnLigne ? "En ligne" : (cpForm.lieu === "domicile" ? "À domicile" : "Au cabinet");
              return (
                <div style={{ background:"#ede9fe", borderRadius:8, padding:"9px 12px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:11, color:"#6d28d9", fontWeight:600 }}>
                    {grillePriveInsc[cpForm.type_coaching]?.label} · {lieuLabel}
                  </span>
                  <span style={{ fontSize:14, fontWeight:800, color:"#4c1d95" }}>
                    {(grillePriveInsc[cpForm.type_coaching]?.[lieuKey] || 0).toLocaleString("fr-FR")} FCFA/mois
                  </span>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <div style={{ fontSize:12, fontWeight:700, color:"#7c3aed", margin:"14px 0 10px", padding:"8px 12px", background:"#faf5ff", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <span>📎 Documents du dossier</span>
        <span style={{ fontSize:10, fontWeight:400, color:"#9ca3af" }}>{form.documents.length} fichier{form.documents.length!==1?"s":""} · max 20 Mo</span>
      </div>

      {/* Liste des fichiers uploadés */}
      {form.documents.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:10 }}>
          {form.documents.map((doc, i) => {
            const def = DOC_TYPES.find(d => d.key === doc.type);
            return (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:8, background:"#faf5ff", borderRadius:8, border:"1px solid #e9d5ff", padding:"8px 10px" }}>
                <span style={{ fontSize:18, flexShrink:0 }}>{def?.icon || "📎"}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#4c1d95", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{doc.nom}</div>
                  <div style={{ fontSize:10, color:"#9ca3af" }}>{def?.label || doc.type}{doc.taille ? ` · ${formatSize(doc.taille)}` : ""}</div>
                </div>
                {doc.url && (
                  <a href={doc.url} target="_blank" rel="noopener noreferrer"
                    style={{ background:"#ede9fe", color:"#6d28d9", border:"none", borderRadius:6, padding:"4px 8px", fontSize:10, fontWeight:700, textDecoration:"none", flexShrink:0 }}>
                    👁 Voir
                  </a>
                )}
                <button onClick={() => removeDoc(i)}
                  style={{ background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:6, padding:"4px 8px", cursor:"pointer", fontSize:10, fontWeight:700, flexShrink:0 }}>
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}
      {form.documents.length === 0 && (
        <div style={{ textAlign:"center", padding:"14px 0", color:"#9ca3af", fontSize:11, fontStyle:"italic", marginBottom:8 }}>
          Aucun document - ajoutez des fichiers ci-dessous
        </div>
      )}

      {/* Zone d'ajout */}
      <div style={{ background:"#f5f3ff", borderRadius:10, border:"1.5px dashed #c4b5fd", padding:"12px 14px", marginBottom:14 }}>
        <div style={{ display:"flex", gap:8, marginBottom:8 }}>
          <select value={upType} onChange={e => setUpType(e.target.value)} style={{ ...inputSt, flex:1 }}>
            <option value="">- Type de document -</option>
            {DOC_TYPES.map(d => <option key={d.key} value={d.key}>{d.icon} {d.label}</option>)}
          </select>
          <input value={upNom} onChange={e => setUpNom(e.target.value)}
            style={{ ...inputSt, flex:1 }} placeholder="Nom personnalisé (optionnel)" />
        </div>
        <input ref={fileInputRef} type="file"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.xls,.xlsx"
          style={{ display:"none" }} onChange={handleFileSelect} />
        <button
          onClick={() => { if (!upType) { toast.error("Sélectionnez d'abord le type de document"); return; } fileInputRef.current?.click(); }}
          disabled={uploading}
          style={{ width:"100%", background: uploading ? "#ddd6fe" : "linear-gradient(135deg,#7c3aed,#6d28d9)", color:"#fff", border:"none", borderRadius:8, padding:"9px 0", fontWeight:700, fontSize:12, cursor: uploading ? "not-allowed" : "pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
          {uploading ? (
            <><span style={{ width:14, height:14, border:"2px solid rgba(255,255,255,.3)", borderTop:"2px solid #fff", borderRadius:"50%", display:"inline-block", animation:"spin 1s linear infinite" }} />{uploadPct}% - Envoi en cours…</>
          ) : "📤 Choisir un fichier et uploader"}
        </button>
      </div>

      {/* L'OBJECTIF DU CLIENT - le champ que le conseiller pédagogique lira pour choisir
          le coach. Il existait en base et dans l'API depuis toujours, mais aucun formulaire
          ne le proposait : les 85 dossiers étaient tous vides. Placé en fin de bloc
          Formation, parce qu'il se remplit après avoir parlé du besoin, pas avant.
          NON OBLIGATOIRE à dessein : une inscription prise au téléphone dans l'urgence ne
          doit pas être bloquée par lui - il se complète ensuite depuis la fiche. */}
      <div style={{ marginBottom:14 }}>
        <label style={{ ...labelSt, display:"flex", alignItems:"center", gap:7, flexWrap:"wrap" }}>
          🎯 Objectif du client
          <span style={{ fontWeight:400, color:"#94a3b8" }}>· ce que le PA lira pour choisir le coach</span>
        </label>
        <textarea style={{ ...inputSt, height:64 }} value={form.objectif_client || ""}
          onChange={e=>setForm({...form, objectif_client:e.target.value})}
          placeholder="Ex. Préparer un entretien d'embauche en anglais chez Orange · IELTS 7.0 avant mars · Reprendre les bases pour voyager" />
        <div style={{ fontSize:11, color:"#94a3b8", marginTop:4 }}>
          Ce texte suit le dossier jusqu'au coach. Plus il est précis, plus l'affectation sera juste.
        </div>
      </div>

      {/* LE CHAMP « NOTES ADMINISTRATIVES » A ÉTÉ RETIRÉ de ce formulaire. Sur 85 dossiers,
          les 8 notes existantes étaient TOUTES générées par le système (« Parcours BET ·
          En ligne · … ») : aucune assistante n'en avait jamais écrit une. L'objectif du
          client, juste au-dessus, recueille désormais ce qu'on avait à dire d'un dossier,
          et il va, lui, jusqu'au coach.
          La colonne `notes` et son AFFICHAGE sont conservés : l'export PDF et la fiche les
          lisent, et les textes automatiques doivent rester consultables. */}

      {(() => {
        // Dossier "complet" = toutes les infos qu'Onboarding a besoin pour traiter le dossier
        // sans repasser par la commerciale (offre, date de début, niveau, un moyen de
        // contact). Uniquement dans ce cas le bouton retrouve son comportement d'origine
        // (vérification + transmission immédiate à l'Onboarding) - sinon il ne fait
        // qu'enregistrer, la transmission se faisant plus tard depuis Prospects parcours une
        // fois le dossier réellement complété.
        const isDossierComplet = !!form.offre && !!form.dateDebut && !!form.niveauTest && !!(form.email || form.telephone);
        const _isPriveBtn = cpForm._mode === "prive" || (!cpForm._mode && isOffreCoachingPrive(form.offre));
        const envoiImmediat = !_isPriveBtn && isEnLigne && isDossierComplet;
        return (
      <div style={{ display:"flex", gap:10, justifyContent:"flex-end", position:"sticky", bottom:-1, background:"#fff", padding:"12px 0 2px", marginTop:6, borderTop:"1px solid #f1f5f9" }}>
        <button onClick={onCancel} style={btnSecondary}>Annuler</button>
        <button
          disabled={submitting}
          onClick={async () => {
            if (submitting) return;
            if (!form.offre) { await alerte("Veuillez choisir une offre de formation.", { titre: "Offre requise", danger: true }); return; }
            const _isPrive = cpForm._mode === "prive" || (!cpForm._mode && isOffreCoachingPrive(form.offre));
            if (!_isPrive && !form.dateDebut) { await alerte("Veuillez indiquer la date de début de la formation.", { titre: "Date requise", danger: true }); return; }
            if (_isPrive && !cpForm.date_debut) { await alerte("Veuillez indiquer la date de début de la formation (section Coaching Privé).", { titre: "Date requise", danger: true }); return; }
            const payload = { ...form };
            // Coaching privé → simple enregistrement, jamais de transmission immédiate au PA
            // (dossier_statut reste vide à la création - la transmission est désormais un
            // clic délibéré depuis "Besoin du client" une fois le dossier complété, cf.
            // PATCH /assignations/:id/transmettre-pa). On garde quand même une confirmation
            // avant d'enregistrer (récap des infos), juste sans prétendre "envoyer au PA".
            if (_isPrive) {
              payload.coachingPriveData = { ...cpForm };
              setConfirmPayload(payload);
              return;
            }
            /* FINALISATION : ce clic n'enregistre pas, il TRANSMET. Il enchaîne trois
               écritures sans retour possible - le dossier est complété, passé en
               « converti », puis poussé chez l'Onboarding. Il partait pourtant sans aucune
               confirmation, là où la simple création en avait une : le déclencheur
               existant repose sur `assistanteTypeCours`, une prop que le mode finalisation
               ne reçoit pas. On ne pose PAS `envoyerOnboardingImmediat` ici : ce drapeau
               sert à la création et serait un champ parasite dans un dossier finalisé. */
            if (finalisationMode) {
              setConfirmPayload(payload);
              return;
            }
            // En ligne + dossier complet → même confirmation qu'avant, avec transmission
            // immédiate à l'Onboarding (routing_statut posé dès la création côté backend).
            if (envoiImmediat) {
              payload.envoyerOnboardingImmediat = true;
              setConfirmPayload(payload);
              return;
            }
            // Sinon (en ligne incomplet, ou centre) → enregistrement direct, sans routing_statut
            // - la transmission se fera plus tard depuis Prospects parcours.
            setSubmitting(true);
            try { await onSave(payload); } finally { setSubmitting(false); }
          }}
          style={finalisationMode
            ? { ...btnPrimary, background:"linear-gradient(135deg,#15803d,#22c55e)", padding:"10px 22px", fontSize:13 }
            : btnPrimary
          }
        >
          {finalisationMode
            ? "📤 Transmettre le dossier"
            : _isPriveBtn
              ? "💾 Enregistrer le dossier coaching privé"
              : envoiImmediat
                ? "Vérifier & envoyer à l'Onboarding →"
                : "Enregistrer l'inscription"}
        </button>
      </div>
        );
      })()}

      {/* ── Modal de confirmation (adapté selon le contexte) ── */}
      {confirmPayload && (() => {
        const isPrive   = !!confirmPayload.coachingPriveData;
        const headerBg  = isPrive ? "linear-gradient(135deg,#7c3aed,#6d28d9)" : "linear-gradient(135deg,#0369a1,#0891b2)";
        const btnBg     = isPrive ? "linear-gradient(135deg,#7c3aed,#6d28d9)"  : "linear-gradient(135deg,#0369a1,#0891b2)";
        // Coaching privé : simple enregistrement - la transmission au PA reste un clic
        // délibéré séparé (bouton "Transmettre au PA" dans Besoin du client), jamais
        // annoncée ici pour ne pas prétendre à une action qui n'a pas vraiment lieu.
        const dest      = isPrive ? "" : "à l'Onboarding";
        const destBtn   = isPrive ? "✅ Confirmer et enregistrer le dossier" : "✅ Confirmer et envoyer à l'Onboarding";
        const baseRows = [
          { label:"Client",       value: confirmPayload.client || confirmPayload.contact },
          { label:"Profil",       value: PROFILS_LIST.find(p=>p.id===(confirmPayload.profil_type||"adulte"))?.label || "Adulte" },
          ...(confirmPayload.profil_type === "enfant" ? [{ label:"Parent / Tuteur", value: confirmPayload.nom_parent || "-" }] : []),
          { label:"Téléphone",    value: confirmPayload.telephone || (confirmPayload.profil_type==="enfant" ? confirmPayload.telephone_parent : "") || "-" },
          { label:"Email",        value: confirmPayload.email || (confirmPayload.profil_type==="enfant" ? confirmPayload.email_parent : "") || "-" },
          { label:"Formation",    value: confirmPayload.offre },
          /* Le rythme retenu : c'est lui que l'Onboarding lira pour choisir la classe, et
             lui qui fixe le tarif. Le relire avant de transmettre coûte une ligne. */
          ...(confirmPayload.formule_groupe_detail ? [{
            label: "Formule",
            value: `${confirmPayload.formule_groupe_detail.label}`
              + (confirmPayload.formule_groupe_detail.tarif ? ` · ${Number(confirmPayload.formule_groupe_detail.tarif).toLocaleString("fr-FR")} F/mois` : ""),
          }] : []),
          { label:"Niveau",       value: confirmPayload.niveauTest || "-" },
          { label:"Date de début",value: (confirmPayload.dateDebut || confirmPayload.coachingPriveData?.date_debut) ? new Date(confirmPayload.dateDebut || confirmPayload.coachingPriveData.date_debut).toLocaleDateString("fr-FR") : "-" },
        ];
        const priveRows = isPrive ? [
          { label:"Type coaching", value: confirmPayload.coachingPriveData?.type_coaching === "intensive" ? "Intensive - 5 séances/sem" : "Classique - 3 séances × 2h/sem" },
          { label:"Lieu",          value: assistanteTypeCours === "en_ligne" ? "En ligne" : (confirmPayload.coachingPriveData?.lieu === "domicile" ? "À domicile" : "Au cabinet") },
          ...(assistanteTypeCours !== "en_ligne" && confirmPayload.coachingPriveData?.lieu !== "domicile" && confirmPayload.coachingPriveData?.centre ? [{ label:"Centre", value: confirmPayload.coachingPriveData.centre }] : []),
          ...(assistanteTypeCours !== "en_ligne" && confirmPayload.coachingPriveData?.lieu === "domicile" && confirmPayload.coachingPriveData?.adresse ? [{ label:"Adresse", value: confirmPayload.coachingPriveData.adresse }] : []),
        ] : [];
        /* LE MONTANT MANQUAIT au récapitulatif, alors que c'est la ligne qu'on relit en
           premier avant d'enregistrer - surtout sur un forfait famille négocié à la main. */
        const montantRows = [
          ...(confirmPayload.montant ? [{ label:"Montant", value:`${Number(confirmPayload.montant).toLocaleString("fr-FR")} FCFA` }] : []),
          ...(confirmPayload.mensualite_negociee ? [{ label:"Mensualité du contrat", value:`${Number(confirmPayload.mensualite_negociee).toLocaleString("fr-FR")} FCFA` }] : []),
        ];
        const rows = [...baseRows, ...priveRows, ...montantRows];
        const membresRecap = (confirmPayload.coachingPriveData?.membres_famille || [])
          .filter(m => (m?.prenom || "").trim() || (m?.nom || "").trim());
        const objectifRecap = (confirmPayload.objectif_client || "").trim();
        return (
          <ModaleAnimee ouvert
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:10000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
            panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:480, boxShadow:"0 20px 60px rgba(0,0,0,0.25)", overflow:"hidden" }}>
              <div style={{ background:headerBg, padding:"20px 24px", color:"#fff" }}>
                <div style={{ fontSize:20, marginBottom:4 }}>📋 {isPrive ? "Confirmation d'enregistrement" : "Confirmation d'envoi"}</div>
                <div style={{ fontSize:12, opacity:0.85 }}>
                  {isPrive
                    ? <>Vérifiez les informations avant d'enregistrer ce dossier coaching privé.<br/>Vous pourrez ensuite le compléter et le transmettre au PA depuis "Besoin du client".</>
                    : <>Vérifiez les informations avant d'envoyer {dest}.<br/>⚠️ Cette action est <strong>irréversible</strong>.</>}
                </div>
              </div>
              <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:10 }}>
                {rows.map(row => (
                  <div key={row.label} style={{ display:"flex", justifyContent:"space-between", fontSize:13, borderBottom:"1px solid #f1f5f9", paddingBottom:6 }}>
                    <span style={{ color:"#6b7280", fontWeight:600 }}>{row.label}</span>
                    <span style={{ color:"#0f172a", fontWeight:700, textAlign:"right", maxWidth:"60%" }}>{row.value}</span>
                  </div>
                ))}

                {/* L'OBJECTIF, en pleine largeur : c'est une phrase, et la seule information
                    du dossier qui dise POURQUOI le client vient. Une ligne de tableau à deux
                    colonnes l'aurait tronquée. */}
                {objectifRecap && (
                  <div style={{ background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:8, padding:"9px 12px" }}>
                    <div style={{ fontSize:10, fontWeight:800, color:"#6d28d9", textTransform:"uppercase", letterSpacing:".04em", marginBottom:4 }}>
                      🎯 Objectif du client
                    </div>
                    <div style={{ fontSize:12.5, lineHeight:1.55, color:"#0f172a", whiteSpace:"pre-wrap" }}>{objectifRecap}</div>
                  </div>
                )}

                {/* LA FAMILLE. Enregistrer un dossier à trois apprenants sans que le
                    récapitulatif ne les montre, c'est demander une confirmation sur ce qu'on
                    ne voit pas - la commerciale ne pouvait pas vérifier une saisie qu'elle
                    venait pourtant de faire. */}
                {membresRecap.length > 0 && (
                  <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:"9px 12px" }}>
                    <div style={{ fontSize:10, fontWeight:800, color:"#1d4ed8", textTransform:"uppercase", letterSpacing:".04em", marginBottom:6 }}>
                      👥 Coaching en famille · {1 + membresRecap.length} apprenants
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:"#1e40af" }}>
                        {confirmPayload.client || confirmPayload.contact} <span style={{ fontWeight:600, color:"#64748b" }}>· payeur</span>
                      </div>
                      {membresRecap.map((m, i) => (
                        <div key={i} style={{ fontSize:12, color:"#0f172a" }}>
                          {[m.prenom, m.nom].filter(Boolean).join(" ")}
                          <span style={{ color:"#64748b" }}>
                            {m.lien ? ` · ${m.lien}` : ""}
                            {m.niveau ? ` · ${m.niveau}` : " · niveau à évaluer"}
                            {(m.telephone || m.email) ? ` · ${m.telephone || m.email}` : " · sans coordonnées"}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize:10, color:"#64748b", marginTop:6, lineHeight:1.5 }}>
                      Le montant est global pour la famille. Les accès seront créés à l'affectation de la classe.
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display:"flex", gap:10, padding:"0 24px 20px" }}>
                <button onClick={() => setConfirmPayload(null)}
                  style={{ flex:1, padding:"10px", border:"1.5px solid #e2e8f0", borderRadius:9, background:"#f8fafc", color:"#374151", fontWeight:700, fontSize:13, cursor:"pointer" }}>
                  ← Modifier
                </button>
                <button disabled={submitting} onClick={async () => { if (submitting) return; setSubmitting(true); try { await onSave(confirmPayload); } finally { setSubmitting(false); } setConfirmPayload(null); }}
                  style={{ flex:2, padding:"10px", background:btnBg, color:"#fff", border:"none", borderRadius:9, fontWeight:700, fontSize:13, cursor:submitting?"not-allowed":"pointer", opacity:submitting?0.7:1 }}>
                  {submitting ? "⏳ Envoi en cours…" : destBtn}
                </button>
              </div>
          </ModaleAnimee>
        );
      })()}
    </div>
  );
};

const PROOF_MODES = new Set(["Chèque","RIA","MoneyGram","Western Union","Virement bancaire"]);

const CAT_LABELS = { en_ligne:"💻 En ligne", presentiel:"🏢 Présentiel / Centres", certification:"📜 Certifications" };

const PaiementForm = ({ initialData, onSave, onCancel, apprenants = [], catalogueOffres = [] }) => {
  const alerte = useAlerte();
  const [form, setForm] = useState(initialData || { client:"",email:"",telephone:"",offre:"",inscription:"",montantDu:0,montantReçu:0,date:new Date().toISOString().slice(0,10),mode:"Mobile Money",statut:"en_attente",notes:"",refTransaction:"",assignationId:null,preuveImage:null,dateDebutFormation:"" });
  const [submitting, setSubmitting] = useState(false);
  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  // Montants déjà reçus sur d'autres versements pour ce même apprenant
  const dejaReçu = initialData?.dejaReçu || 0;

  // Fallback statique si le catalogue n'est pas encore chargé
  const offres = catalogueOffres.length > 0 ? catalogueOffres : OFFRES_TARIFS.map(o => ({ id:o.label, label:o.label, prix_str:`${o.prix.toLocaleString("fr-FR")} FCFA`, prix_num:o.prix, categorie:"en_ligne", icon:"📚" }));

  const selectOffre = (label) => {
    const found = offres.find(o => o.label === label);
    setForm(prev => ({
      ...prev,
      offre:     label,
      montantDu: found?.prix_num ?? prev.montantDu,
    }));
  };

  // Grouper par catégorie pour le select
  const grouped = offres.reduce((acc, o) => {
    const cat = o.categorie || "autre";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(o);
    return acc;
  }, {});

  const handleProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { await alerte("Fichier trop volumineux (max 8 Mo)", { titre: "Fichier trop volumineux", danger: true }); return; }
    const reader = new FileReader();
    reader.onload = (ev) => f("preuveImage", ev.target.result);
    reader.readAsDataURL(file);
  };

  const isFromProspect = !!form.assignationId;
  const [apprenantSearch, setApprenantSearch] = useState("");

  const selectApprenant = (id) => {
    if (!id) { setForm(prev => ({ ...prev, assignationId:null, client:"", email:"", telephone:"", inscription:"", offre:"", montantDu:0 })); return; }
    const a = apprenants.find(x => x.id === id);
    if (!a) return;
    const ml = libelleCanal(a.type_cours, a.centre_nom);
    const tl = ["groupe","standard"].includes(a.type_coaching)?"Coaching groupe":a.type_coaching==="prive"?"Coaching privé":"Formation";
    // Reprend l'offre déjà connue du dossier - sinon la sélection d'apprenant ne remplissait
    // jamais la Formation, obligeant à la re-choisir manuellement alors qu'elle était déjà là.
    const offreConnue = a.inscription_offre || "";
    const offreCatalogue = matchOffreCatalogue(offres, offreConnue);
    setForm(prev => ({
      ...prev, assignationId:a.id, client:a.prospect_nom||"", email:a.prospect_email||"", telephone:a.prospect_telephone||"",
      inscription:`Parcours BET · ${ml} · ${tl}`,
      offre: offreConnue,
      // Priorité au prix RÉELLEMENT choisi par le client (pack Classique/Intensive ×
      // lieu, capturé à la soumission dans suivi_demarrage.cout_formation) - le prix
      // générique du catalogue ne reflète pas le pack effectivement sélectionné pour
      // un coaching privé.
      montantDu: a.suivi_demarrage?.cout_formation || offreCatalogue?.prix_num || prev.montantDu,
      // Détail affiché juste en dessous - sans ça, un coaching privé Intensive se
      // présentait au paiement sans distinction visible du Classique (même libellé
      // d'offre générique), malgré un montant différent.
      packCoachingLabel: a.type_coaching === "prive"
        ? `${a.suivi_demarrage?.type_coaching === "intensive" ? "Intensive - 5 séances/sem" : "Classique - 3 séances × 2h/sem"}${a.suivi_demarrage?.lieu_coaching ? " · " + (a.suivi_demarrage.lieu_coaching === "domicile" ? "À domicile" : a.suivi_demarrage.lieu_coaching === "en_ligne" ? "En ligne" : "Au cabinet") : ""}`
        : null,
      formuleGroupe: a.suivi_demarrage?.formule_groupe || null,
    }));
  };

  return (
    <div>
      {/* Sélecteur apprenant - uniquement pour un nouveau paiement sans apprenant pré-lié.
          Remplace un simple <select> texte par des cartes montrant photo, coordonnées,
          formation, type de parcours et niveau - pour retrouver le bon apprenant sans devoir
          deviner à partir du seul nom dans une liste déroulante. */}
      {!initialData?.assignationId && apprenants.length > 0 && (
        <div style={{ background:"#f0fdf4", border:"1.5px solid #bbf7d0", borderRadius:10, padding:"10px 14px", marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#15803d" }}>🎓 Lier à un apprenant (optionnel)</div>
            {form.assignationId && (
              <button type="button" onClick={() => selectApprenant(null)} style={{ fontSize:10, fontWeight:700, color:"#dc2626", background:"none", border:"none", cursor:"pointer" }}>✕ Retirer le lien</button>
            )}
          </div>
          <input
            value={apprenantSearch}
            onChange={e => setApprenantSearch(e.target.value)}
            placeholder="🔍 Rechercher par nom, email, téléphone…"
            style={{ ...inputSt, margin:0, marginBottom:8, width:"100%", boxSizing:"border-box" }}
          />
          <div style={{ display:"flex", flexDirection:"column", gap:6, maxHeight:260, overflowY:"auto" }}>
            {apprenants
              .filter(a => {
                const q = apprenantSearch.toLowerCase();
                if (!q) return true;
                return `${a.prospect_nom||""} ${a.prospect_email||""} ${a.prospect_telephone||""}`.toLowerCase().includes(q);
              })
              .map(a => {
                const isSel = form.assignationId === a.id;
                const initiales = (a.prospect_nom||"?").split(" ").map(p=>p[0]).filter(Boolean).slice(0,2).join("").toUpperCase();
                const modeLabel = a.type_cours==="en_ligne" ? "💻 En ligne" : `🏢 Présentiel${a.centre_nom?" · "+a.centre_nom:""}`;
                const coachingLabel = ["groupe","standard"].includes(a.type_coaching) ? "👥 Groupe" : a.type_coaching==="prive" ? "👤 Privé" : null;
                return (
                  <div key={a.id} onClick={() => selectApprenant(a.id)}
                    style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:9, border:`1.5px solid ${isSel?"#16a34a":"#e2e8f0"}`, background:isSel?"#dcfce7":"#fff", cursor:"pointer", transition:"all .15s" }}>
                    <div style={{ width:34, height:34, borderRadius:"50%", background:isSel?"#16a34a":"#e2e8f0", color:isSel?"#fff":"#6b7280", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:12, flexShrink:0, overflow:"hidden" }}>
                      {a.avatar_url ? <img src={a.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : initiales}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                        <span style={{ fontWeight:700, fontSize:12.5, color:"#0f172a" }}>{a.prospect_nom}</span>
                        {a.niveau_client && <span style={{ fontSize:9, fontWeight:800, padding:"1px 6px", borderRadius:99, background:"#ede9fe", color:"#6d28d9" }}>{a.niveau_client}</span>}
                      </div>
                      <div style={{ fontSize:10.5, color:"#9ca3af", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {[a.prospect_email, a.prospect_telephone].filter(Boolean).join(" · ") || "-"}
                      </div>
                      <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginTop:3 }}>
                        <span style={{ fontSize:9.5, fontWeight:600, padding:"1px 6px", borderRadius:99, background:"#eff6ff", color:"#1d4ed8" }}>{modeLabel}</span>
                        {coachingLabel && <span style={{ fontSize:9.5, fontWeight:600, padding:"1px 6px", borderRadius:99, background:"#f5f3ff", color:"#7c3aed" }}>{coachingLabel}</span>}
                        {a.inscription_offre && <span style={{ fontSize:9.5, fontWeight:600, padding:"1px 6px", borderRadius:99, background:"#f0fdf4", color:"#15803d", overflow:"hidden", textOverflow:"ellipsis", maxWidth:160, whiteSpace:"nowrap" }}>📚 {a.inscription_offre}</span>}
                      </div>
                    </div>
                    {isSel && <span style={{ color:"#16a34a", fontWeight:900, fontSize:16, flexShrink:0 }}>✓</span>}
                  </div>
                );
              })}
            {apprenants.filter(a => {
              const q = apprenantSearch.toLowerCase();
              if (!q) return true;
              return `${a.prospect_nom||""} ${a.prospect_email||""} ${a.prospect_telephone||""}`.toLowerCase().includes(q);
            }).length === 0 && (
              <div style={{ textAlign:"center", padding:"14px 0", color:"#94a3b8", fontSize:12 }}>Aucun apprenant trouvé.</div>
            )}
          </div>
        </div>
      )}

      {/* Bloc apprenant/prospect lié */}
      {isFromProspect && (
        <div style={{ background:"#fffbeb", border:"1.5px solid #fde68a", borderRadius:10, padding:"10px 14px", marginBottom:16, display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:"1.3rem" }}>🎯</span>
          <div>
            <div style={{ fontWeight:700, fontSize:12, color:"#92400e" }}>Paiement lié à un apprenant parcours</div>
            <div style={{ fontSize:11, color:"#b45309", marginTop:2 }}>
              {form.client}{form.email ? ` · ${form.email}` : ""}
            </div>
          </div>
        </div>
      )}

      {/* Infos complètes du paiement en ligne (lecture seule) - ce paiement vient de
          TouchPoint, pas d'une saisie manuelle : montrer tout ce qu'on sait déjà au lieu
          de laisser la commerciale re-remplir des champs déjà connus. */}
      {initialData?.id && initialData?.mode === "TouchPoint" && (
        <div style={{ background:"#f0fdf4", border:"1.5px solid #bbf7d0", borderRadius:10, padding:"12px 16px", marginBottom:16 }}>
          <div style={{ fontWeight:700, fontSize:12, color:"#15803d", marginBottom:8 }}>🌐 Paiement en ligne TouchPoint - détails complets</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, fontSize:12 }}>
            <div><span style={{ color:"#6b7280" }}>Offre : </span><strong>{form.offre || "-"}</strong></div>
            <div><span style={{ color:"#6b7280" }}>Statut : </span><strong>{form.statut || "-"}</strong></div>
            <div><span style={{ color:"#6b7280" }}>Montant dû : </span><strong>{Number(form.montantDu||0).toLocaleString("fr-FR")} FCFA</strong></div>
            <div><span style={{ color:"#6b7280" }}>Montant reçu : </span><strong style={{ color:"#16a34a" }}>{Number(form.montantReçu||0).toLocaleString("fr-FR")} FCFA</strong></div>
            <div><span style={{ color:"#6b7280" }}>Date : </span><strong>{form.date ? new Date(form.date).toLocaleDateString("fr-FR") : "-"}</strong></div>
            <div><span style={{ color:"#6b7280" }}>Référence : </span><strong style={{ fontFamily:"monospace" }}>{form.refTransaction || "-"}</strong></div>
            {form.operateur && <div><span style={{ color:"#6b7280" }}>Opérateur : </span><strong>{form.operateur}</strong></div>}
            {form.paymentToken && <div><span style={{ color:"#6b7280" }}>Token TouchPoint : </span><strong style={{ fontFamily:"monospace", fontSize:11 }}>{form.paymentToken}</strong></div>}
            {form.niveau && <div><span style={{ color:"#6b7280" }}>Niveau : </span><strong>{form.niveau}</strong></div>}
            {form.assignationId && <div style={{ gridColumn:"1 / -1" }}><span style={{ color:"#6b7280" }}>Dossier lié : </span><strong style={{ fontFamily:"monospace", fontSize:11 }}>{form.assignationId}</strong></div>}
            {form.notes && <div style={{ gridColumn:"1 / -1" }}><span style={{ color:"#6b7280" }}>Notes : </span>{form.notes}</div>}
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        <div style={{ marginBottom:12 }}><label style={labelSt}>Client *</label><input style={isFromProspect ? { ...inputSt, background:"#f1f5f9", color:"#94a3b8", cursor:"not-allowed" } : inputSt} value={form.client} onChange={e=>{ if (!isFromProspect) f("client",e.target.value); }} readOnly={isFromProspect} required /></div>
        <div style={{ marginBottom:12 }}><label style={labelSt}>Téléphone</label><input style={isFromProspect ? { ...inputSt, background:"#f1f5f9", color:"#94a3b8", cursor:"not-allowed" } : inputSt} value={form.telephone||""} onChange={e=>{ if (!isFromProspect) f("telephone",e.target.value); }} readOnly={isFromProspect} placeholder="+225 07 XX XX XX" /></div>
      </div>

      <div style={{ marginBottom:12 }}><label style={labelSt}>Email client</label><input type="email" style={isFromProspect ? { ...inputSt, background:"#f1f5f9", color:"#94a3b8", cursor:"not-allowed" } : inputSt} value={form.email} onChange={e=>{ if (!isFromProspect) f("email",e.target.value); }} readOnly={isFromProspect} placeholder="exemple@email.com" /></div>

      {/* Formation - sélection manuelle toujours possible : le catalogue existait déjà
          (selectOffre/grouped) mais n'était jamais branché à un contrôle visible, donc ce
          bloc restait un simple affichage sans aucun moyen de choisir une formation quand le
          modal s'ouvrait depuis le bouton générique "+ Nouveau paiement" (pas d'apprenant
          pré-lié avec une offre déjà connue). */}
      <div style={{ background:"#f0fdf4", border:"1.5px solid #bbf7d0", borderRadius:10, padding:"12px 14px", marginBottom:14 }}>
        <div style={{ fontSize:11, fontWeight:700, color:"#15803d", marginBottom:8 }}>📚 Formation</div>
        <select
          style={{ ...inputSt, margin:0, marginBottom: form.offre ? 8 : 0 }}
          value={form.offre || ""}
          onChange={e => selectOffre(e.target.value)}
        >
          <option value="">- Choisir une formation -</option>
          {Object.entries(grouped).map(([cat, list]) => (
            <optgroup key={cat} label={cat === "en_ligne" ? "En ligne" : cat === "presentiel" ? "Présentiel" : cat === "certification" ? "Certification" : cat}>
              {list.map(o => <option key={o.label} value={o.label}>{o.icon ? `${o.icon} ` : ""}{o.label} - {o.prix_str}</option>)}
            </optgroup>
          ))}
        </select>
        {form.offre && (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 12px", background:"#dcfce7", borderRadius:8, border:"1px solid #86efac" }}>
            <span style={{ fontSize:13, fontWeight:700, color:"#166534" }}>📋 {form.offre}</span>
            {form.montantDu > 0 && (
              <span style={{ fontSize:12, fontWeight:800, color:"#15803d", background:"#fff", borderRadius:999, padding:"2px 10px", border:"1px solid #86efac", whiteSpace:"nowrap", marginLeft:8 }}>
                {Number(form.montantDu).toLocaleString("fr-FR")} FCFA
              </span>
            )}
          </div>
        )}
      </div>

      {form.packCoachingLabel && (
        <div style={{ display:"flex", alignItems:"center", gap:8, background:"#f5f3ff", border:"1.5px solid #ddd6fe", borderRadius:10, padding:"10px 14px", marginBottom:14 }}>
          <span style={{ fontSize:16 }}>🎯</span>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:"#7c3aed", textTransform:"uppercase", letterSpacing:".05em" }}>Coaching privé</div>
            <div style={{ fontSize:12.5, fontWeight:700, color:"#4c1d95", marginTop:1 }}>{form.packCoachingLabel}</div>
          </div>
        </div>
      )}

      {/* ── Formule de groupe retenue ────────────────────────────────────────
          Le coaching privé avait son bloc de détail (violet, juste au-dessus) mais pas le
          coaching de groupe : le modal n'affichait que l'offre générique et son montant,
          si bien qu'on encaissait sans voir le rythme facturé. C'est pourtant lui qui
          justifie le tarif - deux formules de la même offre n'ont pas le même prix. ── */}
      {form.formuleGroupe && (
        <div style={{ display:"flex", alignItems:"center", gap:10, background:"#f0fdf4", border:"1.5px solid #bbf7d0", borderRadius:10, padding:"10px 14px", marginBottom:14 }}>
          <span style={{ fontSize:16 }}>👥</span>
          <div style={{ minWidth:0, flex:1 }}>
            <div style={{ fontSize:10, fontWeight:700, color:"#15803d", textTransform:"uppercase", letterSpacing:".05em" }}>
              Formule de groupe choisie par le client
            </div>
            <div style={{ fontSize:12.5, fontWeight:700, color:"#14532d", marginTop:1 }}>
              {form.formuleGroupe.label}
              {form.formuleGroupe.canal === "presentiel" ? " · 🏢 Présentiel" : form.formuleGroupe.canal === "en_ligne" ? " · 💻 En ligne" : ""}
            </div>
            {form.formuleGroupe.frequence && (
              <div style={{ fontSize:11.5, color:"#475569", marginTop:2 }}>{form.formuleGroupe.frequence}</div>
            )}
          </div>
          {form.formuleGroupe.tarif > 0 && (
            <div style={{ textAlign:"right", flexShrink:0 }}>
              <div style={{ fontSize:13, fontWeight:800, color:"#15803d" }}>
                {Number(form.formuleGroupe.tarif).toLocaleString("fr-FR")} F
              </div>
              <div style={{ fontSize:10, color:"#64748b", fontWeight:700 }}>/ mois</div>
            </div>
          )}
        </div>
      )}

      {/* Montants */}
      <div style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:10, padding:"12px 14px", marginBottom:14 }}>
        <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:10 }}>💰 Montants</div>

        {/* Récap des versements précédents */}
        {dejaReçu > 0 && (
          <div style={{ display:"flex", gap:0, background:"#fff", borderRadius:8, border:"1px solid #e2e8f0", overflow:"hidden", marginBottom:10 }}>
            {[
              { l:"Total formation",   v: form.montantDu,              c:"#0f172a" },
              { l:"Déjà encaissé",     v: dejaReçu,                    c:"#16a34a" },
              { l:"Reste avant ce versement", v: Math.max(0, form.montantDu - dejaReçu), c: form.montantDu - dejaReçu > 0 ? "#d97706" : "#16a34a" },
            ].map((s, i, arr) => (
              <div key={s.l} style={{ flex:1, textAlign:"center", padding:"8px 6px", borderRight: i < arr.length-1 ? "1px solid #e2e8f0" : "none" }}>
                <div style={{ fontSize:9, color:"#94a3b8", marginBottom:2 }}>{s.l}</div>
                <div style={{ fontSize:12, fontWeight:800, color:s.c }}>{Number(s.v).toLocaleString("fr-FR")} F</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
          {/* Montant dû - auto-rempli, modifiable */}
          <div>
            <label style={{ ...labelSt, display:"flex", alignItems:"center", gap:6 }}>
              Montant total dû (FCFA)
              {form.offre && <span style={{ fontSize:9, background:"#dcfce7", color:"#15803d", padding:"1px 6px", borderRadius:999, fontWeight:700 }}>auto</span>}
            </label>
            <input type="number" style={inputSt} value={form.montantDu} onChange={e=>f("montantDu",Number(e.target.value))} min={0} />
          </div>
          {/* Montant reçu - principal champ de saisie */}
          <div>
            <label style={{ ...labelSt, color:"#0369a1", fontWeight:800 }}>
              Montant reçu ce versement (FCFA) *
            </label>
            <div style={{ display:"flex", gap:6 }}>
              <input
                type="number"
                style={{ ...inputSt, border:"2px solid #0891b2", fontWeight:700, fontSize:14, flex:1 }}
                value={form.montantReçu}
                onChange={e=>f("montantReçu",Number(e.target.value))}
                min={0}
                autoFocus={!!form.assignationId}
                placeholder={dejaReçu > 0 ? `Solde restant : ${Number(Math.max(0,form.montantDu-dejaReçu)).toLocaleString("fr-FR")} F` : "0"}
              />
              <button type="button"
                disabled={!(form.montantDu - dejaReçu > 0)}
                onClick={() => f("montantReçu", Math.max(0, form.montantDu - dejaReçu))}
                title={dejaReçu > 0 ? "Renseigner le solde restant (paiement intégral)" : "Renseigner le montant reçu = montant dû (paiement intégral)"}
                style={{ flexShrink:0, padding:"0 12px", borderRadius:8, border:"1.5px solid #0891b2", background: (form.montantDu - dejaReçu > 0) ? "#e0f2fe" : "#f1f5f9", color: (form.montantDu - dejaReçu > 0) ? "#0369a1" : "#94a3b8", fontWeight:800, fontSize:11.5, cursor: (form.montantDu - dejaReçu > 0) ? "pointer" : "not-allowed", whiteSpace:"nowrap" }}>
                💰 {dejaReçu > 0 ? "Solder" : "Total"}
              </button>
            </div>
          </div>
        </div>

        {/* Bilan global (avec versements précédents) */}
        {(form.montantDu > 0 || form.montantReçu > 0) && (() => {
          const totalEncaissé = dejaReçu + form.montantReçu;
          const vraiReste     = form.montantDu - totalEncaissé;
          const estSoldé      = vraiReste <= 0;
          return (
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {dejaReçu > 0 && (
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 10px", background:"#f0fdf4", borderRadius:7, fontSize:11, color:"#15803d", fontWeight:600 }}>
                  <span>✅ Total encaissé après ce versement</span>
                  <span style={{ fontWeight:800 }}>{Number(totalEncaissé).toLocaleString("fr-FR")} FCFA</span>
                </div>
              )}
              <div style={{ padding:"8px 12px", background: estSoldé ? "#f0fdf4" : "#fef3c7", borderRadius:8, fontSize:12, fontWeight:700, color: estSoldé ? "#15803d" : "#d97706" }}>
                {estSoldé
                  ? `✅ Solde intégralement réglé`
                  : `⏳ Reste à payer après ce versement : ${Number(vraiReste).toLocaleString("fr-FR")} FCFA`
                }
              </div>
            </div>
          );
        })()}
      </div>

      {/* Détails du paiement */}
      <div style={{ background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:10, padding:"12px 14px", marginBottom:14 }}>
        <div style={{ fontSize:11, fontWeight:700, color:"#0369a1", marginBottom:10 }}>📱 Détails du paiement</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <div>
            <label style={labelSt}>Mode de paiement</label>
            <select style={inputSt} value={form.mode} onChange={e=>f("mode",e.target.value)}>
              <option>Mobile Money (MTN)</option>
              <option>Orange Money</option>
              <option>Wave</option>
              <option>CinetPay</option>
              <option>Espèces</option>
              <option>Carte bancaire</option>
              <option>Chèque</option>
              <option>Virement bancaire</option>
              <option>RIA</option>
              <option>MoneyGram</option>
              <option>Western Union</option>
            </select>
          </div>
          <div>
            <label style={labelSt}>Date de réception</label>
            <input type="date" style={inputSt} value={form.date} onChange={e=>f("date",e.target.value)} />
          </div>
        </div>
        <div style={{ marginTop:10 }}>
          <label style={labelSt}>🔖 Référence de transaction</label>
          <input
            style={{ ...inputSt, fontFamily:"monospace", letterSpacing:".05em" }}
            value={form.refTransaction}
            onChange={e=>f("refTransaction",e.target.value)}
            placeholder="Ex : TXN-123456789, OCI-20240511-0099…"
          />
          <div style={{ fontSize:10, color:"#64748b", marginTop:4 }}>Numéro fourni par Orange Money, Wave, CinetPay, etc.</div>
        </div>

        {/* Preuve de paiement - optionnelle, disponible pour tous les modes de paiement */}
        <div style={{ marginTop:12, background:"#fffbeb", border:"1.5px solid #fde68a", borderRadius:8, padding:"10px 12px" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#92400e", marginBottom:8 }}>
              📸 Preuve de paiement <span style={{ fontWeight:400, color:"#b45309" }}>(optionnel)</span>
            </div>
            {form.preuveImage ? (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {form.preuveImage.startsWith("data:application/pdf") ? (
                  <a href={form.preuveImage} target="_blank" rel="noopener noreferrer"
                    style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 12px", background:"#fff", borderRadius:6, border:"1px solid #fde68a", color:"#92400e", fontWeight:700, fontSize:12, textDecoration:"none" }}>
                    📄 Ouvrir le PDF →
                  </a>
                ) : (
                  <img
                    src={form.preuveImage}
                    alt="Preuve de paiement"
                    style={{ maxWidth:"100%", maxHeight:200, objectFit:"contain", borderRadius:6, border:"1px solid #fde68a", background:"#fff" }}
                  />
                )}
                <div style={{ display:"flex", gap:8 }}>
                  <span style={{ fontSize:10, color:"#15803d", fontWeight:700 }}>✅ Document chargé</span>
                  <button
                    type="button"
                    onClick={() => f("preuveImage", null)}
                    style={{ fontSize:10, color:"#dc2626", background:"none", border:"none", cursor:"pointer", fontWeight:700, padding:0 }}
                  >✕ Supprimer</button>
                </div>
              </div>
            ) : (
              <div>
                <label style={{ display:"block", cursor:"pointer", background:"#fff", border:"1.5px dashed #fcd34d", borderRadius:6, padding:"12px", textAlign:"center" }}>
                  <input type="file" accept="image/*,.pdf" style={{ display:"none" }} onChange={handleProofUpload} />
                  <div style={{ fontSize:22, marginBottom:4 }}>📎</div>
                  <div style={{ fontSize:11, color:"#d97706", fontWeight:700 }}>Cliquer pour uploader la photo du reçu</div>
                  <div style={{ fontSize:10, color:"#94a3b8", marginTop:3 }}>JPG, PNG, PDF - max 8 Mo</div>
                </label>
              </div>
            )}
        </div>
      </div>

      {isFromProspect && !form._isApprenant && (
        <div style={{ marginBottom:12, background:"#eff6ff", border:"1.5px solid #bfdbfe", borderRadius:10, padding:"10px 14px" }}>
          <label style={{ ...labelSt, color:"#1d4ed8", fontWeight:800 }}>📅 Date de début de formation *</label>
          <input
            type="date"
            style={{ ...inputSt, border:"2px solid #3b82f6", marginBottom:0 }}
            value={form.dateDebutFormation || ""}
            onChange={e => f("dateDebutFormation", e.target.value)}
          />
          <div style={{ fontSize:10, color:"#3b82f6", marginTop:4 }}>Cette date sera enregistrée sur le dossier du prospect.</div>
        </div>
      )}

      <div style={{ marginBottom:12 }}>
        <label style={labelSt}>Statut</label>
        <select style={inputSt} value={form.statut} onChange={e=>f("statut",e.target.value)}>
          <option value="en_attente">⏳ En attente de confirmation</option>
          <option value="partiel">🔄 Paiement partiel</option>
          <option value="reçu">✅ Paiement reçu / confirmé</option>
          <option value="remboursé">↩️ Remboursé</option>
        </select>
      </div>

      <div style={{ marginBottom:12 }}>
        <label style={labelSt}>Notes internes</label>
        <textarea style={{ ...inputSt, height:52 }} value={form.notes} onChange={e=>f("notes",e.target.value)} placeholder="Remarques, informations complémentaires…" />
      </div>

      <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
        <button onClick={onCancel} style={btnSecondary}>Annuler</button>
        <button disabled={submitting} onClick={async () => { if (submitting) return; setSubmitting(true); try { await onSave(form); } finally { setSubmitting(false); } }} style={{ ...btnPrimary, opacity:submitting?0.7:1, cursor:submitting?"not-allowed":"pointer" }}>{submitting ? "⏳ Enregistrement…" : "💾 Enregistrer le paiement"}</button>
      </div>
    </div>
  );
};

const DocDossierModal = ({ assignation: a, onSave, onClose }) => {
  const [docs,      setDocs]      = useState(a.documents_dossier ? [...a.documents_dossier] : []);
  const [upType,    setUpType]    = useState("");
  const [upNom,     setUpNom]     = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [saving,    setSaving]    = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!upType) { toast.error("Choisissez d'abord le type de document"); e.target.value = ""; return; }
    if (file.size > maxMoPour("dossier", file.type) * 1024 * 1024) { toast.error(`Fichier trop volumineux (max ${maxMoPour("dossier", file.type)} Mo)`); e.target.value = ""; return; }

    setUploading(true); setUploadPct(10);
    try {
      const def = DOC_TYPES.find(d => d.key === upType);
      const nom = upNom.trim() || def?.label || upType;

      const formData = new FormData();
      formData.append("file", file);

      setUploadPct(40);
      const res = await fetchUpload(`${API_URL}/api/upload/dossier`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        body: formData,
      });
      const rawText = await res.text();
      if (!res.ok) {
        let msg = `Erreur ${res.status}`;
        try { msg = JSON.parse(rawText).error || msg; } catch { msg = rawText.slice(0, 200); }
        throw new Error(msg);
      }
      const { file: uploaded } = JSON.parse(rawText);
      setUploadPct(90);

      setDocs(prev => [...prev, {
        nom,
        type:       upType,
        url:        uploaded.url,
        public_id:  uploaded.public_id,
        taille:     uploaded.size || file.size,
        mimetype:   file.type,
      }]);
      setUpType(""); setUpNom("");
      toast.success(`${def?.icon || "📎"} ${nom} ajouté`);
    } catch (err) {
      toast.error("Erreur lors de l'upload : " + (err.message || "réessayez"));
    } finally {
      setUploading(false); setUploadPct(0);
      e.target.value = "";
    }
  };

  const removeDoc = async (i) => {
    const doc = docs[i];
    if (doc.public_id) {
      const rt = doc.mimetype?.startsWith("image/") ? "image" : "raw";
      fetch(`${API_URL}/api/upload/delete?public_id=${encodeURIComponent(doc.public_id)}&resource_type=${rt}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
      }).catch(() => {});
    }
    setDocs(prev => prev.filter((_, j) => j !== i));
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    await onSave(docs);
    setSaving(false);
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes/1024).toFixed(0)} Ko`;
    return `${(bytes/(1024*1024)).toFixed(1)} Mo`;
  };

  return (
    <ModaleAnimee ouvert
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:9300, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:560, display:"flex", flexDirection:"column", maxHeight:"92vh", overflowY:"auto" }}>

        {/* Header */}
        <div style={{ background:BET_GRADIENT, padding:"18px 22px", borderRadius:"16px 16px 0 0", color:"#fff", display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexShrink:0 }}>
          <div>
            <div style={{ fontSize:10, color:"#ddd6fe", fontWeight:700, letterSpacing:".06em", marginBottom:3 }}>DOSSIER APPRENANT</div>
            <h3 style={{ margin:0, fontSize:16, fontWeight:800 }}>📎 Documents - {a.prospect_nom}</h3>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.6)", marginTop:3 }}>{docs.length} document{docs.length !== 1 ? "s" : ""} dans le dossier</div>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", width:28, height:28, borderRadius:"50%", cursor:"pointer", fontSize:15, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
        </div>

        <div style={{ padding:"18px 22px", display:"flex", flexDirection:"column", gap:14 }}>

          {/* Liste des documents */}
          {docs.length === 0 ? (
            <div style={{ textAlign:"center", padding:"28px 0", color:"#94a3b8", fontSize:12, fontStyle:"italic" }}>
              Aucun document dans le dossier.<br/>Ajoutez des fichiers ci-dessous.
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {docs.map((doc, i) => {
                const def = DOC_TYPES.find(d => d.key === doc.type);
                const isImage = doc.mimetype?.startsWith("image/");
                return (
                  <div key={i} style={{ background:"#faf5ff", borderRadius:10, border:"1px solid #e9d5ff", overflow:"hidden" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px" }}>
                      <span style={{ fontSize:22, flexShrink:0 }}>{def?.icon || "📎"}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:"#4c1d95", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{doc.nom}</div>
                        <div style={{ fontSize:10, color:"#9ca3af", marginTop:1 }}>
                          {def?.label || doc.type}{doc.taille ? ` · ${formatSize(doc.taille)}` : ""}
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                        {doc.url && (
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:6, border:"1px solid #ddd6fe", background:"#ede9fe", color:"#6d28d9", textDecoration:"none", display:"flex", alignItems:"center", gap:4 }}
                          >
                            {isImage ? "🔍 Voir" : "⬇️ Télécharger"}
                          </a>
                        )}
                        <button
                          onClick={() => removeDoc(i)}
                          style={{ fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:6, border:"1px solid #fecaca", background:"#fee2e2", color:"#dc2626", cursor:"pointer" }}
                        >✕ Retirer</button>
                      </div>
                    </div>
                    {/* Aperçu image inline */}
                    {isImage && doc.url && (
                      <div style={{ borderTop:"1px solid #e9d5ff", padding:"0 12px 10px" }}>
                        <img
                          src={doc.url}
                          alt={doc.nom}
                          style={{ maxWidth:"100%", maxHeight:120, objectFit:"contain", borderRadius:6, marginTop:8, background:"#fff", border:"1px solid #e9d5ff" }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Zone d'upload */}
          <div style={{ background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0", padding:"14px 16px" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:12 }}>➕ Ajouter un fichier</div>

            {/* Type + nom personnalisé */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
              <div>
                <label style={{ fontSize:10, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 }}>Type de document *</label>
                <select value={upType} onChange={e => setUpType(e.target.value)} style={{ ...inputSt, margin:0 }}>
                  <option value="">- Choisir -</option>
                  {DOC_TYPES.map(d => <option key={d.key} value={d.key}>{d.icon} {d.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:10, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 }}>Nom personnalisé</label>
                <input
                  value={upNom}
                  onChange={e => setUpNom(e.target.value)}
                  placeholder="Ex : CNI de Jean Koné"
                  style={{ ...inputSt, margin:0 }}
                />
              </div>
            </div>

            {/* Zone de sélection fichier */}
            {uploading ? (
              <div style={{ textAlign:"center", padding:"16px 0" }}>
                <div style={{ fontSize:12, color:"#7c3aed", fontWeight:700, marginBottom:8 }}>Upload en cours… {uploadPct}%</div>
                <div style={{ height:6, background:"#e9d5ff", borderRadius:999, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${uploadPct}%`, background:"#7c3aed", borderRadius:999, transition:"width .3s" }} />
                </div>
              </div>
            ) : (
              <label style={{
                display:"block", cursor: upType ? "pointer" : "not-allowed",
                background: upType ? "#fff" : "#f1f5f9",
                border:`2px dashed ${upType ? "#a78bfa" : "#cbd5e1"}`,
                borderRadius:8, padding:"18px", textAlign:"center",
              }}>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                  style={{ display:"none" }}
                  disabled={!upType || uploading}
                  onChange={handleFileSelect}
                />
                <div style={{ fontSize:28, marginBottom:6 }}>📂</div>
                <div style={{ fontSize:12, fontWeight:700, color: upType ? "#7c3aed" : "#94a3b8" }}>
                  {upType ? "Cliquer pour choisir un fichier" : "Choisissez d'abord le type de document"}
                </div>
                <div style={{ fontSize:10, color:"#9ca3af", marginTop:4 }}>PDF · JPG · PNG · Word · Excel - max {maxMoPour("dossier","application/pdf")} Mo</div>
              </label>
            )}
          </div>

          {/* Actions */}
          <div style={{ display:"flex", gap:10, justifyContent:"flex-end", borderTop:"1px solid #f1f5f9", paddingTop:14 }}>
            <button onClick={onClose} style={btnSecondary}>Fermer sans sauvegarder</button>
            <button
              onClick={handleSave}
              disabled={saving || uploading}
              style={{ ...btnPrimary, background:BET_GRADIENT, opacity: (saving || uploading) ? 0.7 : 1 }}
            >
              {saving ? "Enregistrement…" : "💾 Sauvegarder le dossier"}
            </button>
          </div>

        </div>
    </ModaleAnimee>
  );
};

/* ═══════════════════════════════════════════════════════
   SUIVI DÉMARRAGE MODAL
═══════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════
   ASSIGNATION CLASSE & COACH - CENTRES PRÉSENTIEL
═══════════════════════════════════════════════════════ */
/* `enTete` : du contenu que l'appelant insère en haut du corps. Sert à la migration de
   canal, qui emprunte CE modal - celui de l'affectation initiale - plutôt que d'en
   imposer un autre pour le même geste, et qui doit y ajouter son contexte et son tarif. */
const AssignationCentreModal = ({ assignation: a, classes, coachs, coachDispoMap, onSave, onClose, onCreateClasse, enTete, classeSource }) => {
  const alerte = useAlerte();
  const confirm = useConfirm();
  const [classeId, setClasseId] = useState(a.suivi_demarrage?.classe_id || "");
  const [coachId,  setCoachId]  = useState(a.suivi_demarrage?.coach_id  || "");
  const [saving,   setSaving]   = useState(false);
  const [step,     setStep]     = useState("classe"); // "classe" | "coach"
  const [coachDispoMois, setCoachDispoMois] = useState("courant"); // "courant" | "suivant"

  /* ── CLÔTURER LA CLASSE QUITTÉE SI ELLE SE VIDE ──
     Une classe de coaching privé n'existe que pour ses apprenants : le dernier parti, elle
     n'a plus d'objet. Rien ne la fermait, et elle restait « active » indéfiniment — comptée
     dans la charge de son coach et proposée dans les sélecteurs de classe.
     LE CRITÈRE EST LE VIDE, PAS LE PRIVÉ : une classe privée peut accueillir une FAMILLE, et
     tant qu'un frère ou une sœur y suit ses cours elle doit vivre. La case n'est donc cochée
     d'avance que si cet apprenant est le dernier. Le serveur recompte de son côté et refuse
     de clôturer s'il reste quelqu'un : l'écran propose, il ne décide pas. */
  const sourcePrivee  = !!classeSource && (classeSource.is_prive === true
    || classeSource.type_cours === "prive" || Number(classeSource.capacite_max) === 1);
  const resteApres    = Math.max(0, (Number(classeSource?.nb_apprenants) || 0) - 1);
  const [cloturerSource, setCloturerSource] = useState(false);
  useEffect(() => { setCloturerSource(sourcePrivee && resteApres === 0); }, [sourcePrivee, resteApres]);

  const classeChoisie = classes.find(cl => cl.id === classeId);
  const coachChoisi   = coachs.find(c  => c.id  === coachId);

  const handleSelectClasse = (cl) => {
    setClasseId(cl.id);
    if (cl.coach_id) setCoachId(cl.coach_id);
    setStep("coach");
  };

  const handleSave = async () => {
    if (saving) return;
    if (!classeId) { await alerte("Veuillez sélectionner une classe.", { titre: "Classe requise", danger: true }); return; }
    /* LE NOM DU COACH, SANS DÉPENDRE DE LA LISTE. `coachs` n'est chargé que depuis l'onglet
       « Mes classes » : ouvert depuis « Dossiers à réaffecter », ce modal le reçoit vide, et
       cette recherche ne rendait rien. La confirmation annonçait alors « (sans coach assigné) »
       sur une classe qui en a un — l'assistante annulait, croyant à une erreur, alors que
       `coachId` portait bien le bon identifiant et que l'enregistrement aurait été correct.
       La classe choisie porte déjà `coach_nom` : c'est lui qu'affiche l'étape précédente. */
    const coachTrouve = coachs.find(c => c.id === coachId);
    const coachNom = (coachTrouve ? `${coachTrouve.prenom || ""} ${coachTrouve.nom || ""}`.trim() : "")
      || (coachId && coachId === classeChoisie?.coach_id ? (classeChoisie.coach_nom || "") : "");
    const veut = await confirm(
      `Assigner ${a.prospect_nom || "cet apprenant"} à la classe "${classeChoisie?.nom || "-"}"${coachNom ? ` avec ${coachNom} comme coach` : " (sans coach assigné)"} ?`,
      { titre: "Confirmer l'assignation", confirmLabel: "✅ Confirmer", danger: false }
    );
    if (!veut) return;
    setSaving(true);
    await onSave({ classeId, classeNom: classeChoisie?.nom || "", coachId, coachNom, cloturerSource });
    setSaving(false);
  };

  const niveauClient = a.niveau_client || "";

  return (
    <ModaleAnimee ouvert
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9300, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      panneauStyle={{ background:"#fff", borderRadius:18, width:"90vw", maxWidth:"90vw", height:"90vh", maxHeight:"90vh", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,.25)" }}>

        {/* Header */}
        <div style={{ background:BET_GRADIENT, padding:"18px 24px", display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexShrink:0 }}>
          <div>
            <div style={{ color:"rgba(255,255,255,0.65)", fontSize:10, fontWeight:700, letterSpacing:".07em", marginBottom:4 }}>ASSIGNATION CENTRE PRÉSENTIEL</div>
            <div style={{ color:"#fff", fontWeight:800, fontSize:17 }}>🏫 {a.prospect_nom}</div>
            <div style={{ display:"flex", gap:10, marginTop:6, flexWrap:"wrap" }}>
              {a.inscription_offre && <span style={{ background:"rgba(255,255,255,.15)", color:"#fff", borderRadius:99, padding:"2px 10px", fontSize:11, fontWeight:600 }}>📚 {a.inscription_offre}</span>}
              {niveauClient && (() => { const niv = CECRL_COLORS[niveauClient] || {}; return <span style={{ background: niv.bg || "rgba(255,255,255,.15)", color: niv.color || "#fff", borderRadius:99, padding:"2px 10px", fontSize:11, fontWeight:800, border:`1px solid ${niv.color || "rgba(255,255,255,.3)"}` }}>{niveauClient}</span>; })()}
              {a.date_debut_formation && <span style={{ background:"rgba(255,255,255,.12)", color:"rgba(255,255,255,.85)", borderRadius:99, padding:"2px 10px", fontSize:11 }}>📅 {new Date(a.date_debut_formation).toLocaleDateString("fr-FR")}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,.15)", border:"none", color:"#fff", width:32, height:32, borderRadius:"50%", cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>✕</button>
        </div>

        {/* Stepper */}
        <div style={{ display:"flex", borderBottom:"1px solid #e2e8f0", flexShrink:0 }}>
          {[{ id:"classe", label:"1. Choisir la classe", icon:"📚" }, { id:"coach", label:"2. Choisir le coach", icon:"👩‍🏫" }].map(s => (
            <button key={s.id} onClick={() => setStep(s.id)}
              style={{ flex:1, padding:"12px 8px", border:"none", borderBottom: step===s.id ? "3px solid #7c3aed" : "3px solid transparent", background:"none", cursor:"pointer", fontSize:13, fontWeight: step===s.id ? 800 : 500, color: step===s.id ? "#7c3aed" : "#94a3b8", transition:"all .2s", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
              {s.icon} {s.label}
              {s.id==="classe" && classeChoisie && <span style={{ background:"#7c3aed", color:"#fff", borderRadius:"50%", width:16, height:16, fontSize:9, fontWeight:900, display:"flex", alignItems:"center", justifyContent:"center" }}>✓</span>}
              {s.id==="coach"  && coachChoisi   && <span style={{ background:"#7c3aed", color:"#fff", borderRadius:"50%", width:16, height:16, fontSize:9, fontWeight:900, display:"flex", alignItems:"center", justifyContent:"center" }}>✓</span>}
            </button>
          ))}
        </div>

        {/* Contenu scrollable */}
        <div style={{ flex:1, overflowY:"auto", padding:"18px 22px" }}>
          {enTete}

          {/* ── ÉTAPE 1 : CLASSES ── */}
          {step === "classe" && (() => {
            const classesActives = [...classes]
              .filter(cl => cl.statut !== "terminé" && cl.statut !== "cloture")
              .sort((a, b) => {
                if (niveauClient) { const aM = a.niveau===niveauClient; const bM = b.niveau===niveauClient; if (aM && !bM) return -1; if (!aM && bM) return 1; }
                return 0;
              });
            return (
            <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, flexShrink:0 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#374151" }}>
                  {classesActives.length === 0 ? "Aucune classe active" : `${classesActives.length} classe${classesActives.length > 1 ? "s" : ""} active${classesActives.length > 1 ? "s" : ""}`}
                  {niveauClient && classesActives.some(cl => cl.niveau === niveauClient) && (
                    <span style={{ marginLeft:8, fontSize:11, color:"#7c3aed", fontWeight:600 }}>· Compatibles {niveauClient} en premier</span>
                  )}
                  {niveauClient && classesActives.length > 0 && classesActives.every(cl => cl.niveau && cl.niveau !== niveauClient) && (
                    <span style={{ marginLeft:8, fontSize:11, color:"#dc2626", fontWeight:600 }}>· Aucune classe de niveau {niveauClient}</span>
                  )}
                </div>
                <button onClick={() => onCreateClasse(niveauClient)}
                  style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", background:"linear-gradient(135deg,#7c3aed,#a855f7)", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
                  + Créer une classe
                </button>
              </div>

              {classesActives.length === 0 ? (
                <div style={{ textAlign:"center", padding:"40px 20px", background:"#faf5ff", borderRadius:12, border:"2px dashed #ddd6fe" }}>
                  <div style={{ fontSize:"2.5rem", marginBottom:10 }}>🏫</div>
                  <div style={{ fontSize:13, fontWeight:700, color:"#7c3aed", marginBottom:6 }}>Aucune classe active pour ce centre</div>
                  <div style={{ fontSize:12, color:"#94a3b8", marginBottom:16 }}>Créez une classe pour pouvoir y inscrire cet apprenant.</div>
                  <button onClick={() => onCreateClasse(niveauClient)} style={{ padding:"9px 22px", background:"linear-gradient(135deg,#7c3aed,#a855f7)", color:"#fff", border:"none", borderRadius:8, fontWeight:800, fontSize:13, cursor:"pointer" }}>
                    + Créer la première classe
                  </button>
                </div>
              ) : (
                <div style={{ overflowX:"auto", borderRadius:10, border:"1px solid #e2e8f0" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                    <thead>
                      <tr style={{ background:"#f8fafc", borderBottom:"2px solid #e2e8f0" }}>
                        <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, color:"#64748b", fontSize:11, textTransform:"uppercase", letterSpacing:".05em", whiteSpace:"nowrap" }}>Classe</th>
                        <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, color:"#64748b", fontSize:11, textTransform:"uppercase", letterSpacing:".05em", whiteSpace:"nowrap" }}>Niveau</th>
                        <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, color:"#64748b", fontSize:11, textTransform:"uppercase", letterSpacing:".05em", whiteSpace:"nowrap" }}>Coach</th>
                        <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, color:"#64748b", fontSize:11, textTransform:"uppercase", letterSpacing:".05em", whiteSpace:"nowrap" }}>Programme</th>
                        <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, color:"#64748b", fontSize:11, textTransform:"uppercase", letterSpacing:".05em", whiteSpace:"nowrap" }}>Dates</th>
                        <th style={{ padding:"10px 14px", textAlign:"center", fontWeight:700, color:"#64748b", fontSize:11, textTransform:"uppercase", letterSpacing:".05em", whiteSpace:"nowrap" }}>Effectif</th>
                        <th style={{ padding:"10px 14px", textAlign:"center", fontWeight:700, color:"#64748b", fontSize:11, textTransform:"uppercase", letterSpacing:".05em" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {classesActives.map((cl, idx) => {
                        const niv            = CECRL_COLORS[cl.niveau] || { color:"#6b7280", bg:"#f3f4f6" };
                        const fill           = cl.capacite_max ? Math.round(((cl.nb_apprenants||0)/cl.capacite_max)*100) : 0;
                        const isFull         = fill >= 100;
                        const isSel          = classeId === cl.id;
                        const match          = niveauClient && cl.niveau === niveauClient;
                        const isIncompatible = !!(niveauClient && cl.niveau && cl.niveau !== niveauClient);
                        // Un niveau différent ALERTE, il ne bloque pas : une réaffectation sert
                        // justement à acter une progression ou une régression. Seule une classe
                        // COMPLÈTE reste réellement inaccessible. Un coaching privé n'a plus
                        // « une place par construction » : depuis qu'une famille peut s'inscrire
                        // ensemble, sa capacité vaut le nombre de personnes, et il reste
                        // sélectionnable tant que la famille n'y est pas entière.
                        const isDisabled     = isFull;
                        return (
                          <tr key={cl.id}
                            onClick={() => { if (!isDisabled) handleSelectClasse(cl); }}
                            title={isFull ? "Classe complète" : isIncompatible ? `Niveau différent - l'apprenant est ${niveauClient}, cette classe est ${cl.niveau}. Sélection possible (progression ou régression).` : undefined}
                            style={{
                              borderBottom: idx < classesActives.length-1 ? "1px solid #f1f5f9" : "none",
                              background: isSel ? "#faf5ff" : isIncompatible ? "#fffbeb" : isFull ? "#f8fafc" : idx%2===0 ? "#fff" : "#fafafa",
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              // Seule une classe complète est estompée : elle est réellement
                              // inaccessible. Un niveau différent garde une opacité pleine -
                              // il se signale par sa teinte ambrée et son badge, pas en se
                              // faisant passer pour désactivé alors qu'il est sélectionnable.
                              opacity: isFull ? 0.6 : 1,
                              outline: isSel ? "2px solid #7c3aed" : "none",
                              outlineOffset: -2,
                              transition: "background .15s",
                            }}>
                            {/* Classe */}
                            <td style={{ padding:"11px 14px", verticalAlign:"middle" }}>
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                {isSel && <span style={{ color:"#7c3aed", fontWeight:900, fontSize:14 }}>✓</span>}
                                <div>
                                  <div style={{ fontWeight:700, color:"#0f172a", fontSize:13 }}>{cl.nom}</div>
                                  {cl.filiere && <div style={{ fontSize:11, color:"#94a3b8", marginTop:1 }}>{cl.filiere}</div>}
                                </div>
                              </div>
                            </td>
                            {/* Niveau */}
                            <td style={{ padding:"11px 14px", verticalAlign:"middle", whiteSpace:"nowrap" }}>
                              {cl.niveau ? (
                                <span style={{ fontSize:11, fontWeight:800, padding:"2px 9px", borderRadius:6, background:niv.bg, color:niv.color, border:`1.5px solid ${niv.color}` }}>{cl.niveau}</span>
                              ) : <span style={{ color:"#cbd5e1", fontSize:11 }}>-</span>}
                              {match          && <span style={{ display:"block", marginTop:3, fontSize:9, background:"#f5f3ff", color:"#7c3aed", borderRadius:99, padding:"1px 6px", fontWeight:700, width:"fit-content" }}>✓ Adapté</span>}
                              {/* "Incompatible" en rouge disait "interdit" - or c'est désormais
                                  permis. Le badge informe d'un écart de niveau, il n'interdit rien. */}
                              {isIncompatible && <span style={{ display:"block", marginTop:3, fontSize:9, background:"#fef3c7", color:"#92400e", borderRadius:99, padding:"1px 6px", fontWeight:700, width:"fit-content" }}>⚠️ Autre niveau</span>}
                            </td>
                            {/* Coach */}
                            <td style={{ padding:"11px 14px", verticalAlign:"middle" }}>
                              {cl.coach_nom ? (
                                <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                                  <div style={{ width:26, height:26, borderRadius:"50%", background:`linear-gradient(135deg,${BET_COLOR},#7c3aed)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:800, color:"#fff", flexShrink:0, overflow:"hidden" }}>
                                    {cl.coach_photo_url ? <img src={cl.coach_photo_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : cl.coach_nom.split(" ").map(p=>p[0]).slice(0,2).join("").toUpperCase()}
                                  </div>
                                  <span style={{ fontSize:12, fontWeight:600, color:"#374151", whiteSpace:"nowrap" }}>{cl.coach_nom}</span>
                                </div>
                              ) : <span style={{ color:"#f59e0b", fontSize:11, fontWeight:600 }}>⚠️ Non assigné</span>}
                            </td>
                            {/* Programme */}
                            <td style={{ padding:"11px 14px", verticalAlign:"middle" }}>
                              {cl.horaire?.length > 0 ? (
                                <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                                  {cl.horaire.map((h, i) => (
                                    <span key={i} style={{ padding:"2px 8px", borderRadius:99, background:"#eff6ff", color:"#1e3a8a", fontSize:10, fontWeight:700, border:"1px solid #bfdbfe", whiteSpace:"nowrap" }}>🕐 {fmtHoraire(h)}</span>
                                  ))}
                                </div>
                              ) : <span style={{ color:"#cbd5e1", fontSize:11 }}>-</span>}
                            </td>
                            {/* Dates */}
                            <td style={{ padding:"11px 14px", verticalAlign:"middle", whiteSpace:"nowrap" }}>
                              {cl.date_debut ? (
                                <div style={{ fontSize:11, color:"#475569" }}>
                                  <div>📅 {new Date(cl.date_debut).toLocaleDateString("fr-FR", { day:"numeric", month:"short", year:"numeric" })}</div>
                                  {cl.date_fin && <div style={{ marginTop:2 }}>→ {new Date(cl.date_fin).toLocaleDateString("fr-FR", { day:"numeric", month:"short", year:"numeric" })}</div>}
                                </div>
                              ) : <span style={{ color:"#cbd5e1", fontSize:11 }}>-</span>}
                            </td>
                            {/* Effectif */}
                            <td style={{ padding:"11px 14px", verticalAlign:"middle", textAlign:"center" }}>
                              <div style={{ fontSize:13, fontWeight:800, color: fill>=90 ? "#dc2626" : fill>=70 ? "#d97706" : "#7c3aed" }}>
                                {cl.nb_apprenants||0}<span style={{ fontSize:10, color:"#94a3b8", fontWeight:400 }}>/{cl.capacite_max||20}</span>
                              </div>
                              <div style={{ width:60, height:4, background:"#f1f5f9", borderRadius:99, overflow:"hidden", margin:"4px auto 0" }}>
                                <div style={{ height:"100%", width:`${Math.min(fill,100)}%`, background: fill>=90?"#dc2626":fill>=70?"#f59e0b":"#22c55e", borderRadius:99 }} />
                              </div>
                              {isFull && <div style={{ fontSize:9, color:"#dc2626", fontWeight:700, marginTop:2 }}>COMPLET</div>}
                            </td>
                            {/* Action */}
                            <td style={{ padding:"11px 14px", verticalAlign:"middle", textAlign:"center" }}>
                              {isSel ? (
                                <span style={{ fontSize:11, fontWeight:800, color:"#7c3aed" }}>✓ Sélectionné</span>
                              ) : (
                                <button
                                  disabled={isDisabled}
                                  onClick={e => { e.stopPropagation(); if (!isDisabled) handleSelectClasse(cl); }}
                                  style={{ padding:"5px 14px", background: isDisabled ? "#f1f5f9" : "linear-gradient(135deg,#7c3aed,#a855f7)", color: isDisabled ? "#94a3b8" : "#fff", border:"none", borderRadius:6, fontSize:11, fontWeight:700, cursor: isDisabled ? "not-allowed" : "pointer", whiteSpace:"nowrap" }}>
                                  Choisir →
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            );
          })()}

          {/* ── ÉTAPE 2 : COACH ── */}
          {step === "coach" && (
            <div>
              {/* QUAND LA CLASSE A SON COACH, ON LE MONTRE — on ne renvoie pas à une liste.
                  Cette étape affichait « Le coach attitré est automatiquement assigné », puis
                  juste en dessous « 0 coach disponible · Ajoutez des coachs depuis la gestion
                  des utilisateurs ». Deux phrases qui se contredisent sur un écran de
                  confirmation : l'assistante croit la classe sans coach et renonce.
                  Le nom vient de `classeChoisie`, qui le porte déjà (c'est lui qu'affiche le
                  tableau de l'étape précédente) — et non de `coachs`, dont cette étape n'a
                  aucun besoin puisque le choix est verrouillé. */}
              {classeChoisie?.coach_id ? (
                <>
                  <div style={{ background:"#f5f3ff", border:"1.5px solid #ddd6fe", borderRadius:10, padding:"10px 14px", marginBottom:14, fontSize:12, color:"#7c3aed", fontWeight:600 }}>
                    🔒 Le coach attitré de la classe <strong>{classeChoisie.nom}</strong> est automatiquement assigné. Les autres coachs ne sont pas disponibles pour cette classe.
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderRadius:12,
                    border:"2px solid #7c3aed", background:"#faf5ff", boxShadow:"0 0 0 3px #ddd6fe" }}>
                    <div style={{ width:46, height:46, borderRadius:"50%", flexShrink:0, overflow:"hidden",
                      background:`linear-gradient(135deg,${BET_COLOR},#7c3aed)`, color:"#fff", display:"flex",
                      alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:800 }}>
                      {classeChoisie.coach_photo_url
                        ? <img src={classeChoisie.coach_photo_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                        : (classeChoisie.coach_nom || "?").split(" ").map(p=>p[0]).slice(0,2).join("").toUpperCase()}
                    </div>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:10, fontWeight:800, color:"#7c3aed", textTransform:"uppercase", letterSpacing:".06em" }}>Coach de la classe</div>
                      <div style={{ fontSize:15, fontWeight:800, color:"#0f172a", marginTop:2 }}>
                        {classeChoisie.coach_nom || "Coach attitré"}
                      </div>
                      <div style={{ fontSize:11.5, color:"#64748b", marginTop:2 }}>
                        Rien à choisir ici — passez à la confirmation.
                      </div>
                    </div>
                  </div>
                </>
              ) : coachs.length === 0 ? (
                <>
                  <div style={{ background:"#fffbeb", border:"1.5px solid #fde68a", borderRadius:10, padding:"10px 14px", marginBottom:14, fontSize:12, color:"#92400e", fontWeight:600 }}>
                    ⚠️ Aucun coach attitré sur cette classe - sélectionnez-en un manuellement.
                  </div>
                  <div style={{ textAlign:"center", padding:"24px", background:"#f8fafc", borderRadius:10, color:"#94a3b8", fontSize:12 }}>
                    Aucun coach disponible. Ajoutez des coachs depuis la gestion des utilisateurs.
                  </div>
                </>
              ) : (
                <>
                <div style={{ background:"#fffbeb", border:"1.5px solid #fde68a", borderRadius:10, padding:"10px 14px", marginBottom:14, fontSize:12, color:"#92400e", fontWeight:600 }}>
                  ⚠️ Aucun coach attitré sur cette classe - sélectionnez-en un manuellement.
                </div>
                <div style={{ fontSize:13, fontWeight:700, color:"#374151", marginBottom:12 }}>
                  {coachs.length} coach{coachs.length > 1 ? "s" : ""} disponible{coachs.length > 1 ? "s" : ""}
                </div>
                <DispoMoisToggle moisSel={coachDispoMois} setMoisSel={setCoachDispoMois} periodes={getPeriodesDispo()} />
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  {coachs.map(c => {
                    const initials     = `${(c.prenom||"")[0]||""}${(c.nom||"")[0]||""}`.toUpperCase();
                    const isFromClasse = classeChoisie?.coach_id === c.id;
                    const isGrised     = classeChoisie?.coach_id && !isFromClasse;
                    const isSel        = coachId === c.id;
                    const niveaux      = Array.isArray(c.niveaux_enseignes) ? c.niveaux_enseignes : [];
                    const nbClassesActives = (classes||[]).filter(cl => cl.coach_id === c.id && cl.statut !== "terminé" && cl.statut !== "cloture").length;
                    const dispo = nbClassesActives === 0
                      ? { label:"Disponible", color:"#15803d", bg:"#dcfce7" }
                      : nbClassesActives <= 2
                        ? { label:"Peu chargé", color:"#a16207", bg:"#fef9c3" }
                        : { label:"Chargé", color:"#b91c1c", bg:"#fee2e2" };
                    return (
                      <div key={c.id}
                        onClick={() => { if (!isGrised) setCoachId(c.id); }}
                        style={{ borderRadius:12, border:`2px solid ${isSel ? "#7c3aed" : isGrised ? "#f1f5f9" : "#e2e8f0"}`, background: isSel ? "#faf5ff" : isGrised ? "#f8fafc" : "#fff", padding:"14px", cursor: isGrised ? "not-allowed" : "pointer", opacity: isGrised ? 0.4 : 1, transition:"all .18s", boxShadow: isSel ? "0 0 0 3px #ddd6fe" : "none", position:"relative" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <div style={{ width:42, height:42, borderRadius:"50%", background: isGrised ? "#e2e8f0" : `linear-gradient(135deg,${BET_COLOR},#7c3aed)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:800, color: isGrised ? "#94a3b8" : "#fff", flexShrink:0, overflow:"hidden" }}>
                            {c.photo_url ? <img src={c.photo_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : initials}
                          </div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontWeight:800, fontSize:13, color: isGrised ? "#94a3b8" : "#0f172a" }}>{c.prenom} {c.nom}</div>
                            {Array.isArray(c.specialites) && c.specialites.length > 0 && <div style={{ fontSize:11, color: isGrised ? "#cbd5e1" : "#64748b", marginTop:1 }}>{c.specialites.join(", ")}</div>}
                            {c.email && <div style={{ fontSize:10, color:"#94a3b8", marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.email}</div>}
                          </div>
                        </div>
                        {!isGrised && niveaux.length > 0 && (
                          <div style={{ display:"flex", gap:3, flexWrap:"wrap", marginTop:8 }}>
                            {niveaux.map(n => (
                              <span key={n} style={{ fontSize:9, fontWeight:800, padding:"1px 6px", borderRadius:4, background:"#f3f4f6", color:"#374151", border:"1px solid #d1d5db" }}>{n}</span>
                            ))}
                          </div>
                        )}
                        <div style={{ display:"flex", gap:6, marginTop:8, flexWrap:"wrap" }}>
                          {isFromClasse && <span style={{ fontSize:10, background:"#f5f3ff", color:"#7c3aed", borderRadius:99, padding:"1px 8px", fontWeight:700, border:"1px solid #ddd6fe" }}>✓ Coach attitré</span>}
                          {!isGrised && <span title={`${nbClassesActives} classe(s) active(s)`} style={{ fontSize:10, background:dispo.bg, color:dispo.color, borderRadius:99, padding:"1px 8px", fontWeight:700 }}>{dispo.label} · {nbClassesActives} classe{nbClassesActives>1?"s":""}</span>}
                          {!isGrised && c.type_contrat && <span style={{ fontSize:10, background:"#eff6ff", color:"#1e40af", borderRadius:99, padding:"1px 8px", fontWeight:600 }}>📄 {c.type_contrat}</span>}
                          {!isGrised && c.telephone && <span style={{ fontSize:10, background:"#f0fdf4", color:"#15803d", borderRadius:99, padding:"1px 8px", fontWeight:600 }}>📞 {c.telephone}</span>}
                          {!isGrised && c.actif !== false && <span style={{ fontSize:10, background:"#dcfce7", color:"#16a34a", borderRadius:99, padding:"1px 8px", fontWeight:700 }}>Actif</span>}
                        </div>
                        {!isGrised && <CoachDispoBlock coachId={c.id} coachDispoMap={coachDispoMap || {}} {...getPeriodesDispo()[coachDispoMois]} />}
                        {isSel && (
                          <div style={{ marginTop:8, textAlign:"center", fontSize:11, color:"#7c3aed", fontWeight:700 }}>{isFromClasse ? "✓ Assigné automatiquement" : "✓ Sélectionné"}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:"14px 22px", borderTop:"1px solid #e2e8f0", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0, background:"#fafafa" }}>
          <div style={{ fontSize:12, color:"#64748b" }}>
            {classeChoisie ? <span>📚 <strong>{classeChoisie.nom}</strong></span> : <span style={{ color:"#d97706" }}>⚠️ Classe non sélectionnée</span>}
            {coachChoisi && <span style={{ marginLeft:10 }}>👩‍🏫 <strong>{coachChoisi.prenom} {coachChoisi.nom}</strong></span>}
          </div>
          {/* La case vit dans le PIED, à côté du bouton qui déclenche tout : c'est le dernier
              endroit que l'on regarde avant de valider. Plus haut, elle se perdrait sous le
              tableau des classes. Affichée seulement pour une classe privée — pour un groupe,
              se vider est normal entre deux cohortes et ne doit rien déclencher. */}
          {sourcePrivee && step === "coach" && (
            <label style={{ display:"flex", alignItems:"flex-start", gap:9, flex:1, cursor: resteApres === 0 ? "pointer" : "not-allowed",
              padding:"9px 12px", background: resteApres === 0 ? "#fffbeb" : "#f8fafc",
              border:`1px solid ${resteApres === 0 ? "#fde68a" : "#e2e8f0"}`, borderRadius:9, marginRight:10 }}>
              <input type="checkbox" checked={cloturerSource} disabled={resteApres > 0}
                onChange={e => setCloturerSource(e.target.checked)} style={{ marginTop:2, cursor:"inherit" }} />
              <span style={{ fontSize:11.5, lineHeight:1.5, color: resteApres === 0 ? "#92400e" : "#94a3b8" }}>
                {resteApres === 0 ? (
                  <>Clôturer <strong>{classeSource.nom}</strong>, qui n'aura plus aucun apprenant.
                    <span style={{ color:"#b45309" }}> Les plans de paiement sont gelés et la classe
                    quitte la charge de son coach.</span></>
                ) : (
                  <>Clôture impossible : <strong>{resteApres} apprenant{resteApres > 1 ? "s" : ""}</strong>{" "}
                    rest{resteApres > 1 ? "ent" : "e"} dans {classeSource.nom}.</>
                )}
              </span>
            </label>
          )}
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={onClose} style={{ ...btnSecondary, padding:"10px 18px" }}>Annuler</button>
            {step === "classe" && classeChoisie && (
              <button onClick={() => setStep("coach")} style={{ padding:"10px 20px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer" }}>
                Suivant → Coach
              </button>
            )}
            {step === "coach" && (
              <button onClick={handleSave} disabled={saving || !classeId}
                style={{ padding:"10px 24px", background: (!classeId||saving) ? "#e5e7eb" : "linear-gradient(135deg,#7c3aed,#a855f7)", color: (!classeId||saving) ? "#94a3b8" : "#fff", border:"none", borderRadius:8, fontWeight:800, fontSize:13, cursor: (!classeId||saving) ? "not-allowed" : "pointer" }}>
                {saving ? "⏳ Assignation…" : "✅ Confirmer l'assignation"}
              </button>
            )}
          </div>
        </div>
    </ModaleAnimee>
  );
};

const SUIVI_STEPS_DEFAULT = [
  { id: "acces_plateforme",   label: "Accès plateforme créé et envoyé",        icon: "🔐" },
  { id: "test_niveau",        label: "Test de niveau effectué",                 icon: "📝" },
  { id: "coach_assigne",      label: "Coach / formateur assigné",               icon: "👩‍🏫" },
  { id: "planning_envoye",    label: "Planning de cours communiqué",            icon: "📅" },
  { id: "premier_cours",      label: "Premier cours effectué",                  icon: "🎓" },
  { id: "materiel_partage",   label: "Matériel pédagogique partagé",            icon: "📚" },
  { id: "contact_etabli",     label: "Contact WhatsApp / e-mail établi",        icon: "💬" },
  { id: "groupe_whatsapp",    label: "Groupe WhatsApp rejoint",                 icon: "📲" },
  { id: "dossier_complet",    label: "Documents dossier complets",              icon: "📎" },
  { id: "premier_paiement",   label: "Premier paiement reçu",                  icon: "💳" },
];

const SuiviDemarrageModal = ({ assignation: a, onSave, onClose }) => {
  const existing = a.suivi_demarrage || {};
  const initSteps = SUIVI_STEPS_DEFAULT.map(def => {
    const saved = (existing.steps || []).find(s => s.id === def.id);
    return { ...def, done: saved?.done || false, date: saved?.date || null, note: saved?.note || "" };
  });

  const [steps,   setSteps]   = useState(initSteps);
  const [notes,   setNotes]   = useState(existing.notes_generales || "");
  const [saving,  setSaving]  = useState(false);
  const [noteOpen, setNoteOpen] = useState(null);

  const toggle = (id) => setSteps(prev => prev.map(s =>
    s.id === id ? { ...s, done: !s.done, date: !s.done ? new Date().toISOString().slice(0, 10) : null } : s
  ));

  const setNote = (id, val) => setSteps(prev => prev.map(s => s.id === id ? { ...s, note: val } : s));

  const done  = steps.filter(s => s.done).length;
  const total = steps.length;
  const pct   = Math.round((done / total) * 100);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    await onSave({ steps: steps.map(s => ({ id: s.id, done: s.done, date: s.date, note: s.note })), notes_generales: notes });
    setSaving(false);
  };

  return (
    <ModaleAnimee ouvert
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:9300, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:560, maxHeight:"92vh", display:"flex", flexDirection:"column", overflow:"hidden" }}>

        {/* Header */}
        <div style={{ background:BET_GRADIENT, padding:"18px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
          <div>
            <div style={{ color:"rgba(255,255,255,0.7)", fontSize:11, marginBottom:3 }}>Suivi de démarrage</div>
            <div style={{ color:"#fff", fontWeight:800, fontSize:15 }}>🚀 {a.prospect_nom}</div>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", width:30, height:30, borderRadius:"50%", cursor:"pointer", fontSize:15, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
        </div>

        {/* Barre de progression */}
        <div style={{ padding:"14px 22px 10px", borderBottom:"1px solid #e2e8f0", flexShrink:0 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
            <span style={{ fontSize:12, fontWeight:700, color:"#374151" }}>{done}/{total} étapes complétées</span>
            <span style={{ fontSize:12, fontWeight:800, color: pct===100 ? "#15803d" : "#d97706" }}>{pct}%</span>
          </div>
          <div style={{ background:"#e5e7eb", borderRadius:999, height:8, overflow:"hidden" }}>
            <div style={{ background: pct===100 ? "#22c55e" : "#f59e0b", height:"100%", width:`${pct}%`, borderRadius:999, transition:"width .3s" }} />
          </div>
          {pct === 100 && (
            <div style={{ marginTop:6, fontSize:11, color:"#15803d", fontWeight:700 }}>🎉 Onboarding terminé !</div>
          )}
        </div>

        {/* Checklist */}
        <div style={{ flex:1, overflowY:"auto", padding:"14px 22px" }}>
          {steps.map(s => (
            <div key={s.id} style={{ marginBottom:6 }}>
              <div
                style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 13px", borderRadius:9, border:`1px solid ${s.done ? "#bbf7d0" : "#e2e8f0"}`, background: s.done ? "#f0fdf4" : "#fafafa", cursor:"pointer", transition:"all .15s" }}
                onClick={() => toggle(s.id)}
              >
                <div style={{ width:22, height:22, borderRadius:6, border:`2px solid ${s.done ? "#22c55e" : "#d1d5db"}`, background: s.done ? "#22c55e" : "#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all .15s" }}>
                  {s.done && <span style={{ color:"#fff", fontSize:13, fontWeight:900 }}>✓</span>}
                </div>
                <span style={{ fontSize:13, color:"#0f172a" }}>{s.icon} {s.label}</span>
                {s.done && s.date && (
                  <span style={{ marginLeft:"auto", fontSize:10, color:"#6b7280", flexShrink:0 }}>
                    {new Date(s.date).toLocaleDateString("fr-FR", { day:"numeric", month:"short" })}
                  </span>
                )}
                <button
                  onClick={e => { e.stopPropagation(); setNoteOpen(noteOpen === s.id ? null : s.id); }}
                  style={{ marginLeft: s.done && s.date ? 6 : "auto", background:"none", border:"none", cursor:"pointer", fontSize:14, color: s.note ? "#0891b2" : "#9ca3af", padding:0, flexShrink:0 }}
                  title="Ajouter une note"
                >💬</button>
              </div>
              {noteOpen === s.id && (
                <div style={{ padding:"6px 13px 8px", background:"#f0f9ff", border:"1px solid #bae6fd", borderTop:"none", borderRadius:"0 0 9px 9px" }}>
                  <input
                    value={s.note}
                    onChange={e => setNote(s.id, e.target.value)}
                    placeholder="Note optionnelle…"
                    style={{ width:"100%", border:"none", background:"transparent", fontSize:12, color:"#374151", outline:"none", boxSizing:"border-box" }}
                    autoFocus
                  />
                </div>
              )}
            </div>
          ))}

          {/* Notes générales */}
          <div style={{ marginTop:14 }}>
            <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>📝 Notes générales de suivi</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Observations, points d'attention, prochaines actions…"
              style={{ width:"100%", padding:"9px 11px", borderRadius:8, border:"1px solid #d1d5db", fontSize:12, resize:"vertical", minHeight:72, boxSizing:"border-box", fontFamily:"inherit" }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding:"12px 22px", borderTop:"1px solid #e2e8f0", display:"flex", gap:10, justifyContent:"flex-end", flexShrink:0 }}>
          <button onClick={onClose} style={{ padding:"8px 16px", background:"#e5e7eb", color:"#374151", border:"none", borderRadius:7, cursor:"pointer", fontWeight:600, fontSize:12 }}>
            Annuler
          </button>
          <button onClick={handleSave} disabled={saving} style={{ padding:"8px 18px", background:"#15803d", color:"#fff", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity: saving ? .7 : 1 }}>
            {saving ? "Enregistrement…" : "💾 Sauvegarder le suivi"}
          </button>
        </div>
    </ModaleAnimee>
  );
};

const FORMAT_ICONS = { mixte:"🔀", reading:"📖", writing:"✍️", speaking:"🎤", listening:"🎧", oral:"📝" };
const FORMAT_LABELS = { mixte:"Texte mixte", reading:"Reading", writing:"Writing", speaking:"Speaking", listening:"Listening", oral:"Test oral" };

const TestForm = ({ initialData, onSave, onCancel }) => {
  const alerte = useAlerte();
  const defaultForm ={ nom:"", prenom:"", telephone:"", email:"", profil:"Particulier", niveau:"A2", score:0, date:new Date().toISOString().slice(0,10), statut:"nouveau", notes:"", offreRecommandee:"" };
  const [form, setForm] = useState(initialData || defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const isFromProspect = !!(initialData?.email || initialData?.telephone);
  const isCorrection   = initialData?.correction_statut === "en_attente";
  const fmtKey = initialData?.format_test || (initialData?.source === "oral" ? "oral" : "mixte");

  return (
    <div>
      {/* Bandeau contextuel */}
      {(isFromProspect || isCorrection) && (
        <div style={{ background: isCorrection ? "#fffbeb" : "#faf5ff", border:`1px solid ${isCorrection ? "#fde68a" : "#ddd6fe"}`, borderRadius:10, padding:"10px 14px", marginBottom:14, display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:"1.4rem" }}>{FORMAT_ICONS[fmtKey]}</span>
          <div>
            <div style={{ fontWeight:700, fontSize:13, color: isCorrection ? "#92400e" : "#6d28d9" }}>
              {isCorrection
                ? `Correction du test ${FORMAT_LABELS[fmtKey]} - attribuez le score et le niveau`
                : `Test oral - résultat saisi par l'assistante`}
            </div>
            <div style={{ fontSize:11, color: isCorrection ? "#b45309" : "#7c3aed", marginTop:1 }}>
              Candidat : <strong>{form.prenom} {form.nom}</strong>{form.email ? ` · ${form.email}` : ""}
            </div>
          </div>
        </div>
      )}

      {/* Réponses écrites (Writing / questions libres d'un test mixte) à lire avant de corriger -
          basé sur la présence réelle de réponses de type "libre", pas sur le format strict du
          test : un test "mixte" peut contenir quelques questions libres au milieu du QCM. */}
      {isCorrection && (() => {
        const libres = (Array.isArray(initialData?.answers_text) ? initialData.answers_text : [])
          .filter(a => a.type ? a.type === "libre" : (fmtKey === "writing"));
        if (libres.length === 0) return null;
        return (
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:8 }}>📄 Réponses écrites du candidat</div>
            {libres.map((a, i) => (
              <div key={i} style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:8, padding:"10px 14px", marginBottom:8 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"#6b7280", marginBottom:4 }}>Question {i+1} - {a.category || ""}</div>
                <div style={{ fontSize:12, color:"#1e293b", fontStyle:"italic", marginBottom:6 }}>"{a.text || a.question_text || ""}"</div>
                <div style={{ fontSize:13, color:"#374151", background:"#fff", borderRadius:6, padding:"8px 10px", border:"1px solid #e2e8f0" }}>{a.user_answer || "-"}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Enregistrements audio (Speaking) - affiché dès qu'il y a des enregistrements,
          même sur un test "mixte" qui n'est pas 100% speaking. */}
      {isCorrection && initialData?.audio_answers && Object.keys(initialData.audio_answers).length > 0 && (
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:2 }}>🎤 Enregistrements du candidat</div>
          <div style={{ fontSize:11, color:"#9ca3af", marginBottom:8 }}>Note indicative par question - le score et le niveau final restent à choisir ci-dessous.</div>
          {Object.entries(initialData.audio_answers).map(([qId, url]) => {
            const sn = form.speaking_notes?.[qId] || { note:"", score:"" };
            const setSN = (patch) => setForm(f => ({ ...f, speaking_notes: { ...(f.speaking_notes||{}), [qId]: { ...sn, ...patch } } }));
            return (
              <div key={qId} style={{ background:"#fdf4ff", border:"1px solid #e9d5ff", borderRadius:8, padding:"10px 14px", marginBottom:8 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"#7e22ce", marginBottom:6 }}>Question #{qId}</div>
                <audio controls src={url} style={{ width:"100%", height:36, borderRadius:4, marginBottom:8 }} />
                <div style={{ display:"grid", gridTemplateColumns:"1fr 100px", gap:8 }}>
                  <input
                    placeholder="Note d'évaluation (prononciation, fluidité, vocabulaire…)"
                    value={sn.note}
                    onChange={e => setSN({ note: e.target.value })}
                    style={{ padding:"7px 10px", borderRadius:6, border:"1px solid #e9d5ff", fontSize:12 }}
                  />
                  <input
                    type="number" min={0} max={100} placeholder="Score %"
                    value={sn.score}
                    onChange={e => setSN({ score: e.target.value === "" ? "" : Number(e.target.value) })}
                    style={{ padding:"7px 10px", borderRadius:6, border:"1px solid #e9d5ff", fontSize:12 }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isCorrection && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:4 }}>
          <div>
            <label style={labelSt}>Nom *</label>
            <input style={{ ...inputSt, background: isFromProspect ? "#f8fafc" : "#fff" }} value={form.nom} onChange={e=>setForm({...form,nom:e.target.value})} readOnly={isFromProspect} />
          </div>
          <div>
            <label style={labelSt}>Prénom</label>
            <input style={{ ...inputSt, background: isFromProspect ? "#f8fafc" : "#fff" }} value={form.prenom} onChange={e=>setForm({...form,prenom:e.target.value})} readOnly={isFromProspect} />
          </div>
          <div>
            <label style={labelSt}>Téléphone</label>
            <input style={{ ...inputSt, background: isFromProspect ? "#f8fafc" : "#fff" }} value={form.telephone} onChange={e=>setForm({...form,telephone:e.target.value})} placeholder="+225 07 XX XX XX" readOnly={isFromProspect} />
          </div>
          <div>
            <label style={labelSt}>Email</label>
            <input type="email" style={{ ...inputSt, background: isFromProspect ? "#f8fafc" : "#fff" }} value={form.email} onChange={e=>setForm({...form,email:e.target.value})} readOnly={isFromProspect} />
          </div>
        </div>
      )}

      {!isFromProspect && !isCorrection && (
        <div style={{ marginBottom:12 }}><label style={labelSt}>Profil</label>
          <select style={inputSt} value={form.profil} onChange={e=>setForm({...form,profil:e.target.value})}>
            <option>Particulier</option><option>Entreprise</option><option>Étudiant</option>
          </select>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom: isCorrection ? 4 : 12 }}>
        <div><label style={labelSt}>Niveau CECRL *</label>
          <select style={inputSt} value={form.niveau} onChange={e=>setForm({...form,niveau:e.target.value})}>
            {["A1","A2","B1","B2","C1","C2"].map(n=><option key={n}>{n}</option>)}
          </select>
        </div>
        <div><label style={labelSt}>Score (%)</label>
          <input type="number" min={0} max={100} style={inputSt} value={form.score} onChange={e=>setForm({...form,score:Number(e.target.value)})} />
        </div>
      </div>

      {/* Score suggéré, purement indicatif - combine le score QCM déjà calculé (sur les
          questions auto-corrigeables uniquement) et la moyenne des scores speaking notés
          par question ci-dessus, pondérés par le nombre de questions de chaque type. Ne
          modifie jamais le champ Score : la correctrice décide seule du score final. */}
      {isCorrection && (() => {
        const nSpeakingTotal = Object.keys(initialData?.audio_answers || {}).length;
        const nLibre = (Array.isArray(initialData?.answers_text) ? initialData.answers_text : []).filter(a => a.type === "libre").length;
        const nGradable = Math.max(0, (initialData?.total_questions || 0) - nSpeakingTotal - nLibre);
        const scored = Object.values(form.speaking_notes || {}).filter(v => v?.score !== "" && v?.score != null && !isNaN(Number(v.score)));
        if (scored.length === 0) return null;
        const speakingAvg = scored.reduce((s,v)=>s+Number(v.score),0) / scored.length;
        const qcmScore = Number(initialData?.score) || 0;
        const totalWeight = nGradable + scored.length;
        if (totalWeight === 0) return null;
        const suggested = Math.round((qcmScore * nGradable + speakingAvg * scored.length) / totalWeight);
        return (
          <div style={{ fontSize:11, color:"#0891b2", marginBottom:12, background:"#ecfeff", border:"1px solid #a5f3fc", borderRadius:6, padding:"6px 10px" }}>
            💡 Score suggéré : <strong>{suggested}%</strong> (QCM {qcmScore}% sur {nGradable} question{nGradable>1?"s":""} + speaking {Math.round(speakingAvg)}% sur {scored.length} notée{scored.length>1?"s":""}) - n'écrase pas le champ Score, à ajuster toi-même si tu es d'accord.
          </div>
        );
      })()}

      {!isCorrection && (
        <div style={{ marginBottom:12 }}><label style={labelSt}>Date du test</label>
          <input type="date" style={inputSt} value={form.date} onChange={e=>setForm({...form,date:e.target.value})} />
        </div>
      )}

      {/* CE CHAMP NE SERT PLUS QU'À LA CORRECTION.
          En saisie manuelle d'un test, « Notes / Observations » restait vide : le niveau CECRL
          et le score disent déjà ce qu'il y a à dire, et l'objectif du prospect se saisit à
          l'inscription. En correction en revanche, le correcteur a de vraies observations à
          consigner sur la copie - c'est le même champ, mais pas le même usage. */}
      {isCorrection && (
      <div style={{ marginBottom:12 }}>
        <label style={labelSt}>Observations de correction</label>
        <textarea style={{ ...inputSt, height:70 }} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Ex : bonne compréhension, difficultés à l'expression écrite…" />
      </div>
      )}

      <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
        <button onClick={onCancel} style={btnSecondary}>Annuler</button>
        <button disabled={submitting} onClick={async () => { if (submitting) return; if (!isCorrection && !form.nom) { await alerte("Le nom est requis", { titre: "Nom requis", danger: true }); return; } setSubmitting(true); try { await onSave(form); } finally { setSubmitting(false); } }} style={{ ...btnPrimary, background: isCorrection ? "#d97706" : undefined, opacity:submitting?0.7:1, cursor:submitting?"not-allowed":"pointer" }}>
          {submitting ? "⏳ Enregistrement…" : isCorrection ? "✅ Valider la correction" : "✅ Enregistrer le test"}
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════ */
const BET_C   = BET_COLOR;
const BET_L   = BET_COLOR + "18";
const BET_Dk  = BET_COLOR;
const btnPrimary   = { padding:"9px 16px", background:BET_C,   color:"#fff",    border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 };
const btnSecondary = { padding:"9px 16px", background:"#e5e7eb", color:"#374151", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 };
const btnIconEdit  = { padding:"4px 8px", background:BET_L, color:BET_Dk, border:`1px solid ${BET_C}40`, borderRadius:4, cursor:"pointer", fontSize:11, fontWeight:600 };
const labelSt      = { display:"block", fontSize:12, fontWeight:600, color:"#374151", marginBottom:4 };
const inputSt      = { padding:9, borderRadius:6, border:"1px solid #d1d5db", fontSize:13, width:"100%", boxSizing:"border-box" };
const selectSt     = { padding:"6px 12px", borderRadius:6, border:"1px solid #e5e7eb", fontSize:12 };
const modalOverlay = { position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:9200 };
const modalBox     = { background:"#fff", padding:24, borderRadius:14, maxWidth:"92vw" };
const th           = { padding:"8px 10px", textAlign:"left", fontWeight:600, color:"#374151" };
const td           = { padding:"8px 10px" };